// Generated by scripts/generate_object_manifest.py from the API's OpenAPI and every concepts.yaml.
// Do not edit: declare objects and commands in concepts.yaml and mark their routes (docs/object-contract.md).
import type { ObjectManifest } from "../domain/object-ui/contract";

export const objectManifest: ObjectManifest = {
  "overview": "One is the staff system for customers, quotes, orders, purchase orders, stock and invoices. Tharstern is still the MIS for work that One's dictionary can't describe. One keeps a copy of Tharstern's data. Xero keeps the accounts. Every number in One has a prefix, one or two letters and five digits, such as ORD-K48193. The prefixes are CUS (customer), SUP (supplier), QT (quote), ORD (order), PRJ (project), PO (purchase order), INV (invoice), CN (credit note) and PF (pro-forma).\n\n## From quote to payment\n\nA quote starts as a private draft or goes straight to the customer from a basket. Publishing will fix the prices. Sharing will email the customer a link to the quote. When the customer accepts the quote, One will create the order.\n\nThe order records how the customer pays: by card, on a credit account or by pro-forma. Credit control checks a credit-account order once, when One creates the order. A billing account on stop, or over the credit limit, will put the order on a credit hold until someone decides.\n\nOne makes a proof for each corrected artwork revision. The customer approves the proof.\n\nRelease sends an order line to the supplier or to production. Release waits for payment, approved proofs, a print-ready file, complete delivery details and a usable supplier. An order line will release itself when the last check passes, unless \"Review before release\" is on. For a bought order line, Release raises the supplier's purchase order.\n\nThe sales ledger raises the invoice when the order lines fall due. The sales ledger invoices card and pro-forma orders on payment, and credit-account orders on despatch or on delivery. One pushes each invoice to Xero as a draft for finance to approve. A pro-forma asks for payment before supply. The purchase ledger checks each supplier's invoice against the purchase orders before anyone releases the payment.\n\n## Customers and contacts\n\nEach customer is mastered either in One or in Tharstern. Customer users sign in to the customer portal; supplier users to the supplier portal.\n\n## Catalogue and baskets\n\nA catalogue item is a dictionary product configured and priced for a customer. A basket holds one person's priced lines for one customer. Checkout turns the basket into an order, a quote or a draft quote.\n\n## Stock\n\nA stock item is stock that One holds for an owning customer. Pollards' own stock belongs to the customer \"Pollards (own stock)\". An order line from stock gets an allocation when One creates the order, or waits as a back order. Release turns the allocation into a pick. A replenishment proposal tops a stock item up with an order into stock.\n\n## Needs attention and support\n\nAn operational alert flags an order, an order line or a purchase order that someone should act on. Customer alerts flag conditions on customers. Customer opportunities suggest a next action with a customer. A ticket is a piece of internal work for a person or a department. An issue is a quality or operational problem under investigation.\n\n## Marketing\n\nAn audience is a saved set of rules and pinned people. A campaign sends to one audience in tranches. A lead source is a landing page whose website form sends enquiries to One. Each submission raises a ticket for the right sales rep. A brand-new contact from a submission also becomes a lead. A lead is a company that a sales rep is pursuing. Converting the lead links the lead to a customer. A prospect is a contact that the enrichment agent suggests at an existing customer.",
  "areas": [
    {
      "id": "needs_attention",
      "label": "Needs attention",
      "description": "Operational alerts on orders and purchase orders, and customer alerts and opportunities on customers, each for someone to act on.",
      "objects": [
        {
          "id": "needs_attention.alert",
          "sidebar": true
        },
        {
          "id": "needs_attention.rule",
          "sidebar": true
        },
        {
          "id": "recommendations_generation.alert",
          "sidebar": true
        },
        {
          "id": "recommendations_generation.opportunity",
          "sidebar": true
        }
      ],
      "tools": []
    },
    {
      "id": "support",
      "label": "Support",
      "description": "Internal work for a person or a department, quality problems under investigation, and the notes and reminders staff keep on records.",
      "objects": [
        {
          "id": "workflow.ticket",
          "sidebar": true
        },
        {
          "id": "comms_ingest.email",
          "sidebar": true
        },
        {
          "id": "issues.issue",
          "sidebar": true
        },
        {
          "id": "feedback.feedback_item",
          "sidebar": true
        },
        {
          "id": "workflow.note",
          "sidebar": false
        },
        {
          "id": "comms_ingest.automatic_action",
          "sidebar": false
        }
      ],
      "tools": []
    },
    {
      "id": "parties",
      "label": "Parties",
      "description": "The customers and suppliers Pollards works with, the people at each who sign in to the customer portal or the supplier portal, Pollards' own staff, and how each customer's portal looks.",
      "objects": [
        {
          "id": "customers.customer",
          "sidebar": true
        },
        {
          "id": "customer_access.customer_link",
          "sidebar": true
        },
        {
          "id": "suppliers.supplier",
          "sidebar": true
        },
        {
          "id": "supplier_access.supplier_person",
          "sidebar": true
        },
        {
          "id": "users.user",
          "sidebar": true
        },
        {
          "id": "customer_communications.communication",
          "sidebar": false
        },
        {
          "id": "portal_theme.customer_theme",
          "sidebar": false
        },
        {
          "id": "portal_theme.base_theme",
          "sidebar": false
        },
        {
          "id": "portal_theme.portal_notice",
          "sidebar": false
        }
      ],
      "tools": []
    },
    {
      "id": "selling",
      "label": "Selling",
      "description": "From a customer's quote or merchandise request to their order, with the catalogue items they reorder and the artwork files that arrive for orders.",
      "objects": [
        {
          "id": "quotes.quote",
          "sidebar": true
        },
        {
          "id": "draft_estimates.draft_estimate",
          "sidebar": true
        },
        {
          "id": "orders.order",
          "sidebar": true
        },
        {
          "id": "order_channel_brandhub.intake",
          "sidebar": true
        },
        {
          "id": "tharstern_job_check.job_check",
          "sidebar": true
        },
        {
          "id": "catalogue.catalogue_item",
          "sidebar": true
        },
        {
          "id": "catalogue.catalogue",
          "sidebar": true
        },
        {
          "id": "artwork_intake.orphan",
          "sidebar": true
        },
        {
          "id": "orders.project",
          "sidebar": false
        },
        {
          "id": "orders.tharstern_order",
          "sidebar": false
        },
        {
          "id": "quotes.tharstern_estimate",
          "sidebar": false
        },
        {
          "id": "order_channel_promo_office.held_up_item",
          "sidebar": false
        }
      ],
      "tools": []
    },
    {
      "id": "buying",
      "label": "Buying",
      "description": "What Pollards buys from suppliers and who can make it, and the stock it holds for customers, with the proposals that top stock up.",
      "objects": [
        {
          "id": "purchase_orders.purchase_order",
          "sidebar": true
        },
        {
          "id": "purchase_orders.price_increase",
          "sidebar": false
        },
        {
          "id": "supplier_quotes.supplier_quote",
          "sidebar": true
        },
        {
          "id": "suppliers.supplier_capability",
          "sidebar": true
        },
        {
          "id": "stock.stock_item",
          "sidebar": true
        },
        {
          "id": "stock_replenishment.replenishment_proposal",
          "sidebar": true
        },
        {
          "id": "fulfilment_adapter_promo_office.held_up_item",
          "sidebar": false
        }
      ],
      "tools": []
    },
    {
      "id": "money",
      "label": "Money",
      "description": "Invoices and pro-formas to customers, their billing accounts and credit, the supplier invoices checked against purchase orders before payment, and the trading companies that sell and buy as.",
      "objects": [
        {
          "id": "finance_sales_ledger.invoice",
          "sidebar": true
        },
        {
          "id": "finance_sales_ledger.proforma",
          "sidebar": true
        },
        {
          "id": "finance_credit_control.billing_account",
          "sidebar": true
        },
        {
          "id": "finance_purchase_ledger.purchase_invoice",
          "sidebar": true
        },
        {
          "id": "finance_accruals_and_prepayments.month_end",
          "sidebar": true
        },
        {
          "id": "finance_accruals_and_prepayments.prepayment",
          "sidebar": true
        },
        {
          "id": "finance_accruals_and_prepayments.recurring_accrual",
          "sidebar": true
        },
        {
          "id": "finance_purchase_ledger.auto_confirmation_rule",
          "sidebar": true
        },
        {
          "id": "trading_companies.trading_company",
          "sidebar": true
        }
      ],
      "tools": []
    },
    {
      "id": "marketing",
      "label": "Marketing",
      "description": "The audiences Pollards markets to, the website forms that bring enquiries in, and the prospects and leads that sales reps pursue.",
      "objects": [
        {
          "id": "marketing_audiences.audience",
          "sidebar": true
        },
        {
          "id": "marketing_audiences.standing_recipient",
          "sidebar": true
        },
        {
          "id": "marketing_leads.lead_source",
          "sidebar": true
        },
        {
          "id": "marketing_leads.lead_submission",
          "sidebar": false
        },
        {
          "id": "enrichment.prospect_contact",
          "sidebar": true
        },
        {
          "id": "sales_leads.lead",
          "sidebar": true
        },
        {
          "id": "marketing_triggers.trigger",
          "sidebar": false
        }
      ],
      "tools": []
    },
    {
      "id": "management",
      "label": "Management",
      "description": "CSRs' claims on customers waiting for a new CSR, for a manager to decide.",
      "objects": [
        {
          "id": "account_allocation.allocation_claim",
          "sidebar": true
        }
      ],
      "tools": []
    },
    {
      "id": "insights",
      "label": "Insights",
      "description": "Read-only questions over the business's curated metrics, such as revenue, customer health and demand, for admins and managers.",
      "objects": [],
      "tools": [
        "list_metrics_models_tool",
        "query_metrics_tool"
      ]
    }
  ],
  "objects": [
    {
      "id": "account_allocation.allocation_claim",
      "module": "account_allocation_module",
      "label": "Account allocation claim",
      "plural": "Account allocation claims",
      "description": "A CSR's claim on a reallocation-queue customer, pending until a manager approves or declines it or the CSR withdraws it.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "pending",
        "approved",
        "declined",
        "withdrawn"
      ],
      "list": {
        "path": "/views/account-allocation/claims",
        "key_field": "id",
        "title_field": "customer_name",
        "columns": [
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text"
          },
          {
            "field": "claimant_name",
            "label": "Claimant",
            "kind": "text"
          },
          {
            "field": "status",
            "label": "Status",
            "kind": "state",
            "enum_labels": {
              "approved": "Approved",
              "declined": "Declined",
              "pending": "Pending",
              "withdrawn": "Withdrawn"
            }
          },
          {
            "field": "created_at",
            "label": "Raised",
            "kind": "datetime"
          }
        ],
        "filters": [
          {
            "param": "status",
            "label": "Status",
            "kind": "multi_enum",
            "options": [
              [
                "pending",
                "Pending"
              ],
              [
                "approved",
                "Approved"
              ],
              [
                "declined",
                "Declined"
              ],
              [
                "withdrawn",
                "Withdrawn"
              ]
            ]
          },
          {
            "param": "claimant_user_id",
            "label": "Claimant",
            "kind": "entity",
            "options": [],
            "object": "users.user",
            "description": "Only this user's claims; a claimant without the manage permit sees only their own."
          }
        ],
        "sorts": [
          [
            "created_at",
            "Raised"
          ],
          [
            "decided_at",
            "Decided"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "pending",
            "label": "Pending",
            "params": {
              "status": [
                "pending"
              ]
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": null,
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "An account allocation claim is a CSR's claim on a customer in the reallocation queue. A customer is in the reallocation queue when the customer's CSR in Tharstern is no longer a current member of staff. A manager approves or declines each claim. Approving will make the claimant the customer's CSR in One and in Tharstern. A customer has at most one pending claim.",
      "state_meanings": {
        "pending": "Pending: the claim will wait for a manager to approve or decline the claim.",
        "approved": "Approved: the claimant became the customer's CSR in One and in Tharstern.",
        "declined": "Declined: a manager declined the claim.",
        "withdrawn": "Withdrawn: the CSR withdrew the claim."
      },
      "state_tones": {
        "pending": "attention",
        "approved": "done",
        "declined": "stopped",
        "withdrawn": "stopped"
      },
      "commands": [
        "account_allocation.approve_claim",
        "account_allocation.decline_claim",
        "account_allocation.withdraw_claim"
      ],
      "features": []
    },
    {
      "id": "artwork_intake.orphan",
      "module": "artwork_intake_module",
      "label": "Unmatched artwork file",
      "plural": "Unmatched artwork files",
      "description": "A received file nobody could place on a version's part, waiting for a person to match it to an order or discard it.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "received",
        "targeted",
        "handed_over",
        "orphan",
        "discarded"
      ],
      "list": {
        "path": "/artwork/orphans",
        "key_field": "id",
        "title_field": "original_filename",
        "columns": [
          {
            "field": "source",
            "label": "Source",
            "kind": "text",
            "enum_labels": {
              "email": "Emailed",
              "hot_folder": "Hot folder",
              "imported": "Imported",
              "order_channel": "From the channel",
              "reuse": "Reused from an earlier order",
              "studio": "From the studio",
              "upload": "Uploaded"
            }
          },
          {
            "field": "original_filename",
            "label": "File",
            "kind": "text"
          },
          {
            "field": "received_at",
            "label": "Received",
            "kind": "datetime"
          },
          {
            "field": "orphan_reason",
            "label": "Reason",
            "kind": "text",
            "enum_labels": {
              "no_number": "No order or quote number found in the subject or the file name",
              "not_accepting_files": "That version takes no new file now",
              "nothing_owes_files": "Nothing on that number is waiting for a file",
              "several_numbers": "More than one number found",
              "several_owing": "More than one version or part is waiting for a file",
              "tool_failed": "The prepress tool could not check the file. Match the file again to retry."
            }
          },
          {
            "field": "matched_number",
            "label": "Number found",
            "kind": "text",
            "ref": [
              "orders.order",
              "quotes.quote"
            ]
          }
        ],
        "filters": [
          {
            "param": "source",
            "label": "Source",
            "kind": "multi_enum",
            "options": [
              [
                "upload",
                "Uploaded"
              ],
              [
                "email",
                "Emailed"
              ],
              [
                "hot_folder",
                "Hot folder"
              ],
              [
                "reuse",
                "Reused from an earlier order"
              ],
              [
                "imported",
                "Imported"
              ],
              [
                "order_channel",
                "From the channel"
              ],
              [
                "studio",
                "From the studio"
              ]
            ]
          },
          {
            "param": "received_since",
            "label": "Received since",
            "kind": "date",
            "options": [],
            "description": "Received at or after this instant."
          },
          {
            "param": "matched_number",
            "label": "Number found",
            "kind": "text",
            "options": [],
            "description": "The order or quote number (ORD-…, QT-…), or the order line reference, found, exactly."
          }
        ],
        "sorts": [],
        "searchable": true,
        "queues": []
      },
      "record": {
        "path": "/artwork/orphans/{id}",
        "path_param": "id",
        "title_field": "original_filename",
        "state_fields": [
          "state"
        ],
        "state_labels": {
          "state": {
            "discarded": "Discarded",
            "handed_over": "Matched",
            "orphan": "Unmatched",
            "received": "Received",
            "targeted": "Being matched"
          }
        },
        "facts": [
          {
            "field": "source",
            "label": "Source",
            "kind": "text",
            "enum_labels": {
              "email": "Emailed",
              "hot_folder": "Hot folder",
              "imported": "Imported",
              "order_channel": "From the channel",
              "reuse": "Reused from an earlier order",
              "studio": "From the studio",
              "upload": "Uploaded"
            }
          },
          {
            "field": "received_at",
            "label": "Received",
            "kind": "datetime"
          },
          {
            "field": "orphan_reason",
            "label": "Reason",
            "kind": "text",
            "enum_labels": {
              "no_number": "No order or quote number found in the subject or the file name",
              "not_accepting_files": "That version takes no new file now",
              "nothing_owes_files": "Nothing on that number is waiting for a file",
              "several_numbers": "More than one number found",
              "several_owing": "More than one version or part is waiting for a file",
              "tool_failed": "The prepress tool could not check the file. Match the file again to retry."
            }
          },
          {
            "field": "matched_number",
            "label": "Number found",
            "kind": "text",
            "ref": [
              "orders.order",
              "quotes.quote"
            ]
          },
          {
            "field": "discarded_reason",
            "label": "Why discarded",
            "kind": "text"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "An unmatched artwork file is a received file that One couldn't place on an order or a quote. One matches an incoming file on the order or quote number (ORD-…, QT-…) in the email subject or the file name. A file is unmatched when One finds no number, more than one number or more than one part waiting for a file. A person then matches the file to an order or a quote, or discards the file. A file One can place on its own waits here until its match runs, which is usually at once. The Unmatched artwork files list isn't limited to your customers, because an unmatched artwork file belongs to no customer yet.",
      "state_meanings": {
        "received": "Received: One hasn't matched the file yet.",
        "targeted": "Being matched: One will hand the file to artwork preparation next.",
        "handed_over": "Matched: artwork preparation has the file.",
        "orphan": "Unmatched: the file will wait until someone matches or discards the file.",
        "discarded": "Discarded: a person deleted the file."
      },
      "state_tones": {
        "received": "in_progress",
        "targeted": "in_progress",
        "handed_over": "done",
        "orphan": "attention",
        "discarded": "neutral"
      },
      "commands": [
        "artwork_intake.resolve_orphan",
        "artwork_intake.discard_orphan"
      ],
      "features": [
        "orders"
      ]
    },
    {
      "id": "artwork_preparation.artwork",
      "module": "artwork_preparation_module",
      "label": "Artwork",
      "plural": "Artwork",
      "description": "The files one line version (or a repeatable catalogue item) owes and has supplied, through its revisions to approval and the release lock.",
      "identifier": {
        "kind": "uuid",
        "field": "artwork_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "files_owed",
        "checking",
        "tool_failed",
        "cant_print",
        "held",
        "checked",
        "proof_out",
        "changes_asked",
        "approved"
      ],
      "list": null,
      "record": {
        "path": "/views/artwork-360/{artwork_id}",
        "path_param": "artwork_id",
        "title_field": "title",
        "state_fields": [
          "state"
        ],
        "state_labels": {
          "state": {
            "approved": "Approved",
            "cant_print": "Can't print",
            "changes_asked": "Sent back",
            "checked": "Checked",
            "checking": "Checking",
            "files_owed": "Files owed",
            "held": "Held",
            "proof_out": "Proof out",
            "tool_failed": "Tool failed"
          }
        },
        "facts": [
          {
            "field": "order_no",
            "label": "Order",
            "kind": "identifier",
            "ref": "orders.order"
          },
          {
            "field": "quote_no",
            "label": "Quote",
            "kind": "identifier",
            "ref": "quotes.quote"
          },
          {
            "field": "line",
            "label": "Line",
            "kind": "text"
          },
          {
            "field": "version",
            "label": "Version",
            "kind": "text"
          },
          {
            "field": "revision_no",
            "label": "Revision",
            "kind": "number"
          },
          {
            "field": "status",
            "label": "Where it stands",
            "kind": "text"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [
        "orders.order",
        "quotes.quote"
      ],
      "children": [
        {
          "object": "artwork_preparation.finding",
          "label": "Check warnings",
          "field": "held_findings",
          "key_field": "id",
          "columns": [
            {
              "field": "subject",
              "label": "For",
              "kind": "text"
            },
            {
              "field": "check_name",
              "label": "Check",
              "kind": "text"
            },
            {
              "field": "detail",
              "label": "Detail",
              "kind": "text"
            },
            {
              "field": "held_since",
              "label": "Held since",
              "kind": "datetime"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "files_owed": "in_progress",
        "checking": "in_progress",
        "tool_failed": "attention",
        "cant_print": "attention",
        "held": "attention",
        "checked": "in_progress",
        "proof_out": "in_progress",
        "changes_asked": "attention",
        "approved": "done"
      },
      "commands": [
        "artwork_preparation.accept_revision_without_proof",
        "artwork_intake.receive_upload",
        "artwork_intake.receive_whole_upload",
        "artwork_preparation.share_part_file"
      ],
      "features": [
        "orders"
      ]
    },
    {
      "id": "artwork_preparation.finding",
      "module": "artwork_preparation_module",
      "label": "Check warning",
      "plural": "Check warnings",
      "description": "One thing a check found in a revision's part or an artwork check, with its tier, and the person's decision when it was held.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "artwork_preparation.artwork",
        "quotes.quote"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [
        "artwork_preparation.decide_held_finding"
      ],
      "features": []
    },
    {
      "id": "artwork_proofing.proof",
      "module": "artwork_proofing_module",
      "label": "Proof",
      "plural": "Proofs",
      "description": "What we will print, made from one revision of an order line version's or a repeatable catalogue item's artwork, for the customer to approve or send back.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "out",
        "approved",
        "rejected",
        "superseded",
        "reopened"
      ],
      "list": null,
      "record": null,
      "parents": [
        "catalogue.catalogue_item"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "out": "in_progress",
        "approved": "done",
        "rejected": "attention",
        "superseded": "neutral",
        "reopened": "attention"
      },
      "commands": [
        "artwork_proofing.decide_proof",
        "orders.email_proof_now",
        "artwork_proofing.reopen_proof"
      ],
      "features": []
    },
    {
      "id": "catalogue.catalogue",
      "module": "catalogue_module",
      "label": "Catalogue",
      "plural": "Catalogues",
      "description": "A named set of catalogue items: the full range, the core range, or a catalogue allocated to customers.",
      "identifier": {
        "kind": "field",
        "field": "catalogue_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": {
        "path": "/catalogue/manage/catalogues",
        "key_field": "catalogue_id",
        "title_field": "name",
        "columns": [
          {
            "field": "catalogue_id",
            "label": "Catalogue",
            "kind": "identifier"
          },
          {
            "field": "audience_label",
            "label": "Listed for",
            "kind": "text"
          },
          {
            "field": "private_to_code",
            "label": "Private to",
            "kind": "text"
          },
          {
            "field": "ownership",
            "label": "Ownership",
            "kind": "text"
          }
        ],
        "filters": [
          {
            "param": "audience",
            "label": "Listed for",
            "kind": "multi_enum",
            "options": [
              [
                "full_range",
                "Staff only"
              ],
              [
                "core_range",
                "Customers with nothing allocated"
              ],
              [
                "allocated",
                "Customers it is allocated to"
              ]
            ],
            "description": "Only catalogues listed so."
          },
          {
            "param": "is_private",
            "label": "Private",
            "kind": "boolean",
            "options": [],
            "description": "True: only private catalogues; false: only shared ones."
          },
          {
            "param": "owned_by",
            "label": "Ownership",
            "kind": "multi_enum",
            "options": [
              [
                "code",
                "Managed in code"
              ],
              [
                "db",
                "Managed in One"
              ]
            ],
            "description": "Only catalogues with this ownership."
          }
        ],
        "sorts": [
          [
            "name",
            "Name"
          ]
        ],
        "searchable": true,
        "queues": []
      },
      "record": {
        "path": "/catalogue/manage/catalogues/{catalogue_id}",
        "path_param": "catalogue_id",
        "title_field": "name",
        "state_fields": [],
        "state_labels": {},
        "facts": [
          {
            "field": "ownership",
            "label": "Ownership",
            "kind": "text"
          },
          {
            "field": "audience_label",
            "label": "Listed for",
            "kind": "text"
          },
          {
            "field": "private_to",
            "label": "Private to",
            "kind": "text"
          },
          {
            "field": "accounts",
            "label": "Accounts",
            "kind": "text"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A catalogue is a named set of catalogue items. The full range holds every dictionary item, for staff only. A customer sees the catalogues allocated to it (\"Set catalogues\" on its record), or the core range when nothing is allocated. A private catalogue belongs to one customer, and only that customer's accounts can have it; a catalogue holding repeatable items stays private. A catalogue managed in code changes through its definition files on every deploy, and One shows it read-only apart from VAT, the pricing group and the copy customers read. An admin uses \"Hand over to One\" so the catalogue is managed in One. The full range is always managed in code.",
      "commands": [
        "catalogue.set_catalogue_accounts",
        "catalogue.make_catalogue_private",
        "catalogue.make_catalogue_shared",
        "catalogue.restore_catalogue_version",
        "catalogue.hand_over_catalogue",
        "catalogue.export_dictionary_workbook",
        "catalogue.submit_dictionary_workbook"
      ],
      "features": [
        "catalogue"
      ]
    },
    {
      "id": "catalogue.catalogue_item",
      "module": "catalogue_module",
      "label": "Catalogue item",
      "plural": "Catalogue items",
      "description": "Something the catalogue offers to order: a product from the dictionary, configured and priced for a customer.",
      "identifier": {
        "kind": "uuid",
        "field": "item_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": {
        "path": "/catalogue/items",
        "key_field": "item_id",
        "title_field": "name",
        "columns": [
          {
            "field": "item_id",
            "label": "Item",
            "kind": "identifier"
          },
          {
            "field": "name",
            "label": "Name",
            "kind": "text"
          },
          {
            "field": "catalogue_name",
            "label": "Catalogue",
            "kind": "text"
          }
        ],
        "filters": [
          {
            "param": "ordering_customer_code",
            "label": "Ordering for customer code",
            "kind": "text",
            "options": [],
            "description": "Ordering context: omit for staff browsing; empty for the core range; code for the core range plus that customer."
          },
          {
            "param": "catalogue_id",
            "label": "Catalogue ID",
            "kind": "multi_enum",
            "options": [],
            "description": "Only items in these catalogues (repeat the param to select several)."
          },
          {
            "param": "favourites_only",
            "label": "My favourites",
            "kind": "boolean",
            "options": [],
            "description": "Only items the requesting user has favourited."
          }
        ],
        "sorts": [],
        "searchable": true,
        "queues": [
          {
            "id": "favourites",
            "label": "My favourites",
            "params": {
              "favourites_only": true
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/catalogue/items/{item_id}",
        "path_param": "item_id",
        "title_field": "name",
        "state_fields": [],
        "state_labels": {},
        "facts": [
          {
            "field": "catalogue_name",
            "label": "Catalogue",
            "kind": "text"
          },
          {
            "field": "customer_product_ref",
            "label": "Customer's product code",
            "kind": "text"
          },
          {
            "field": "made_from",
            "label": "Made from",
            "kind": "text",
            "ref": "orders.order_line"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "artwork_proofing.proof",
          "label": "Proofs",
          "field": "proofs",
          "key_field": "id",
          "columns": [
            {
              "field": "label",
              "label": "Proof",
              "kind": "text"
            },
            {
              "field": "revision",
              "label": "Revision",
              "kind": "text"
            },
            {
              "field": "state",
              "label": "State",
              "kind": "state",
              "enum_labels": {
                "approved": "Approved",
                "out": "Awaiting decision",
                "rejected": "Sent back",
                "superseded": "Replaced"
              }
            },
            {
              "field": "made_at",
              "label": "Made",
              "kind": "datetime"
            },
            {
              "field": "decided_at",
              "label": "Decided",
              "kind": "datetime"
            },
            {
              "field": "decided_by",
              "label": "Decided by",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [],
      "lifecycle": [],
      "about": "A catalogue item is a dictionary product that the catalogue offers, configured and priced for a customer. Every catalogue item derives from one dictionary item or from another catalogue item. A repeatable catalogue item has a fixed specification and one standing artwork. Staff order a catalogue item with \"Order now\" or \"Quote now\", or add it to a draft quote with \"Add to draft quote\". Withdrawing a catalogue item will take the catalogue item out of browse, baskets and checkout.",
      "commands": [
        "catalogue.quote_catalogue_item_now",
        "catalogue.add_to_draft_quote",
        "catalogue.order_catalogue_item_now"
      ],
      "features": [
        "catalogue"
      ]
    },
    {
      "id": "comms_ingest.automatic_action",
      "module": "comms_ingest_module",
      "label": "Automatic action",
      "plural": "Automatic actions",
      "description": "A person's ruling that one handler's proposals of one command run on their own, for one supplier or any.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "automatic",
        "stopped"
      ],
      "list": {
        "path": "/comms-ingest/automatic-actions",
        "key_field": "id",
        "title_field": "title",
        "columns": [
          {
            "field": "title",
            "label": "Automatic action",
            "kind": "text"
          },
          {
            "field": "command_label",
            "label": "Command",
            "kind": "text"
          },
          {
            "field": "handler_label",
            "label": "Handler",
            "kind": "text"
          },
          {
            "field": "supplier_label",
            "label": "Supplier",
            "kind": "text"
          },
          {
            "field": "state",
            "label": "State",
            "kind": "state",
            "enum_labels": {
              "automatic": "Automatic",
              "stopped": "Stopped"
            }
          },
          {
            "field": "made_automatic_by",
            "label": "Made automatic by",
            "kind": "text"
          },
          {
            "field": "when",
            "label": "When",
            "kind": "datetime"
          },
          {
            "field": "note",
            "label": "Why it was made automatic",
            "kind": "text"
          },
          {
            "field": "stopped",
            "label": "Stopped",
            "kind": "text"
          }
        ],
        "filters": [
          {
            "param": "state",
            "label": "State",
            "kind": "multi_enum",
            "options": [
              [
                "automatic",
                "Automatic"
              ],
              [
                "stopped",
                "Stopped"
              ]
            ],
            "description": "Only automatic actions in these states."
          },
          {
            "param": "handler",
            "label": "Handler",
            "kind": "text",
            "options": [],
            "description": "Only one handler's, by its name."
          }
        ],
        "sorts": [
          [
            "changed_at",
            "Changed at"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "automatic",
            "label": "Automatic",
            "params": {
              "state": [
                "automatic"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "stopped",
            "label": "Stopped",
            "params": {
              "state": [
                "stopped"
              ]
            },
            "count": false,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/comms-ingest/automatic-actions/{id}",
        "path_param": "id",
        "title_field": "title",
        "state_fields": [
          "state"
        ],
        "state_labels": {
          "state": {
            "automatic": "Automatic",
            "stopped": "Stopped"
          }
        },
        "facts": [
          {
            "field": "command_label",
            "label": "Command",
            "kind": "text"
          },
          {
            "field": "handler_label",
            "label": "Handler",
            "kind": "text"
          },
          {
            "field": "supplier_label",
            "label": "Supplier",
            "kind": "text"
          },
          {
            "field": "made_automatic_by",
            "label": "Made automatic by",
            "kind": "text"
          },
          {
            "field": "when",
            "label": "When",
            "kind": "datetime"
          },
          {
            "field": "note",
            "label": "Why it was made automatic",
            "kind": "text"
          },
          {
            "field": "stopped",
            "label": "Stopped",
            "kind": "text"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "An automatic action lets One do a proposed action on its own, without waiting for a person: one handler's proposals of one command, for one supplier or any. An admin or a manager makes it with Make automatic… on a confirmed proposed action or here, and stops it with Stop automatic…. Asks to colleagues run on their own unless stopped; asks to outsiders start stopped; a command that decides money or a commitment never runs on its own.",
      "state_meanings": {
        "automatic": "Automatic: One will do these proposed actions on its own, without waiting for a person.",
        "stopped": "Stopped: these proposed actions will wait for a person until someone makes them automatic again."
      },
      "state_tones": {
        "automatic": "done",
        "stopped": "neutral"
      },
      "commands": [
        "comms_ingest.stop_automatic",
        "comms_ingest.make_automatic"
      ],
      "features": [
        "one_mailbox"
      ]
    },
    {
      "id": "comms_ingest.email",
      "module": "comms_ingest_module",
      "label": "Email",
      "plural": "Emails",
      "description": "An email One sent or received, kept as it was, tagged to the records it is about, with one state saying what One did with it.",
      "identifier": {
        "kind": "cis_number",
        "field": "email_no",
        "prefix": "EM",
        "pattern": "^EM-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}$",
        "line_pattern": "^EM-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}\\.[0-9]{2,}$"
      },
      "states": [
        "being_processed",
        "actions_waiting",
        "needs_a_person",
        "nothing_to_do",
        "done",
        "sent"
      ],
      "list": {
        "path": "/comms-ingest/emails",
        "key_field": "email_no",
        "title_field": "subject",
        "columns": [
          {
            "field": "email_no",
            "label": "Email",
            "kind": "identifier"
          },
          {
            "field": "at",
            "label": "Received or sent",
            "kind": "datetime"
          },
          {
            "field": "from_address",
            "label": "From",
            "kind": "text",
            "untrusted": true
          },
          {
            "field": "to",
            "label": "To",
            "kind": "text",
            "untrusted": true
          },
          {
            "field": "subject",
            "label": "Subject",
            "kind": "text",
            "untrusted": true
          },
          {
            "field": "tag_labels",
            "label": "Tags",
            "kind": "text",
            "ref": [
              "purchase_orders.purchase_order",
              "orders.order",
              "quotes.quote",
              "finance_sales_ledger.invoice",
              "suppliers.supplier",
              "customers.customer",
              "supplier_quotes.supplier_quote",
              "finance_purchase_ledger.purchase_invoice"
            ],
            "ref_id_field": "tag_ids",
            "ref_object_field": "tag_objects"
          },
          {
            "field": "state",
            "label": "State",
            "kind": "state",
            "enum_labels": {
              "actions_waiting": "Actions waiting",
              "being_processed": "Being processed",
              "done": "Done",
              "needs_a_person": "Needs a person",
              "nothing_to_do": "Nothing to do",
              "sent": "Sent"
            }
          }
        ],
        "filters": [
          {
            "param": "state",
            "label": "State",
            "kind": "multi_enum",
            "options": [
              [
                "being_processed",
                "Being processed"
              ],
              [
                "actions_waiting",
                "Actions waiting"
              ],
              [
                "needs_a_person",
                "Needs a person"
              ],
              [
                "nothing_to_do",
                "Nothing to do"
              ],
              [
                "done",
                "Done"
              ],
              [
                "sent",
                "Sent"
              ]
            ],
            "description": "Only emails in these states. To do is the Actions waiting and Needs a person states."
          },
          {
            "param": "tagged_to",
            "label": "Tagged",
            "kind": "enum",
            "options": [
              [
                "purchase_orders.purchase_order",
                "Purchase order"
              ],
              [
                "orders.order",
                "Order"
              ],
              [
                "quotes.quote",
                "Quote"
              ],
              [
                "finance_sales_ledger.invoice",
                "Invoice"
              ],
              [
                "suppliers.supplier",
                "Supplier"
              ],
              [
                "customers.customer",
                "Customer"
              ],
              [
                "supplier_quotes.supplier_quote",
                "Supplier quote"
              ],
              [
                "finance_purchase_ledger.purchase_invoice",
                "Purchase invoice"
              ]
            ],
            "description": "Only emails tagged to a record of this kind."
          },
          {
            "param": "supplier_no",
            "label": "Supplier",
            "kind": "entity",
            "options": [],
            "object": "suppliers.supplier",
            "description": "Only emails tagged to this supplier."
          },
          {
            "param": "customer_code",
            "label": "Customer",
            "kind": "entity",
            "options": [],
            "object": "customers.customer",
            "description": "Only emails tagged to this customer."
          },
          {
            "param": "tag_object",
            "label": "Tagged record's kind",
            "kind": "enum",
            "options": [
              [
                "purchase_orders.purchase_order",
                "Purchase order"
              ],
              [
                "orders.order",
                "Order"
              ],
              [
                "quotes.quote",
                "Quote"
              ],
              [
                "finance_sales_ledger.invoice",
                "Invoice"
              ],
              [
                "suppliers.supplier",
                "Supplier"
              ],
              [
                "customers.customer",
                "Customer"
              ],
              [
                "supplier_quotes.supplier_quote",
                "Supplier quote"
              ],
              [
                "finance_purchase_ledger.purchase_invoice",
                "Purchase invoice"
              ]
            ],
            "description": "With the tagged record's number: only the emails tagged to that one record (a record's Emails list)."
          },
          {
            "param": "tag_key",
            "label": "Tagged record's number or code",
            "kind": "text",
            "options": [],
            "description": "The tagged record's number, given with the tagged record's kind."
          }
        ],
        "sorts": [
          [
            "at",
            "Received or sent"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "to_do",
            "label": "To do",
            "params": {
              "state": [
                "actions_waiting",
                "needs_a_person"
              ]
            },
            "count": true,
            "default": true
          },
          {
            "id": "actions_waiting",
            "label": "Actions waiting",
            "params": {
              "state": [
                "actions_waiting"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "needs_a_person",
            "label": "Needs a person",
            "params": {
              "state": [
                "needs_a_person"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "done",
            "label": "Done",
            "params": {
              "state": [
                "done"
              ]
            },
            "count": false,
            "default": false
          },
          {
            "id": "nothing_to_do",
            "label": "Nothing to do",
            "params": {
              "state": [
                "nothing_to_do"
              ]
            },
            "count": false,
            "default": false
          },
          {
            "id": "sent",
            "label": "Sent",
            "params": {
              "state": [
                "sent"
              ]
            },
            "count": false,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/comms-ingest/emails/{email_no}",
        "path_param": "email_no",
        "title_field": "subject",
        "state_fields": [
          "state"
        ],
        "state_labels": {
          "state": {
            "actions_waiting": "Actions waiting",
            "being_processed": "Being processed",
            "done": "Done",
            "needs_a_person": "Needs a person",
            "nothing_to_do": "Nothing to do",
            "sent": "Sent"
          }
        },
        "facts": [
          {
            "field": "needs_reason",
            "label": "Needs a person",
            "kind": "text",
            "untrusted": true
          },
          {
            "field": "done_note",
            "label": "Marked done",
            "kind": "text"
          },
          {
            "field": "from_address",
            "label": "From",
            "kind": "text",
            "untrusted": true
          },
          {
            "field": "to",
            "label": "To",
            "kind": "text",
            "untrusted": true
          },
          {
            "field": "cc",
            "label": "Cc",
            "kind": "text",
            "untrusted": true
          },
          {
            "field": "received_at",
            "label": "Received",
            "kind": "datetime"
          },
          {
            "field": "sent_at",
            "label": "Sent",
            "kind": "datetime"
          },
          {
            "field": "mailbox",
            "label": "Mailbox",
            "kind": "text"
          },
          {
            "field": "sent_by",
            "label": "Sent by",
            "kind": "text"
          },
          {
            "field": "sent_because",
            "label": "Sent because",
            "kind": "text"
          },
          {
            "field": "redirected_to",
            "label": "Redirected in testing to",
            "kind": "text"
          },
          {
            "field": "blocked_in_testing",
            "label": "Blocked in testing",
            "kind": "text"
          },
          {
            "field": "in_reply_to",
            "label": "In reply to",
            "kind": "text",
            "ref": "comms_ingest.email"
          },
          {
            "field": "subject",
            "label": "Subject",
            "kind": "text",
            "untrusted": true
          },
          {
            "field": "email_text",
            "label": "The email",
            "kind": "text",
            "untrusted": true
          },
          {
            "field": "attachment_files",
            "label": "Attachments",
            "kind": "file",
            "untrusted": true
          },
          {
            "field": "attachments_not_kept",
            "label": "Not kept",
            "kind": "text",
            "untrusted": true
          },
          {
            "field": "tag_labels",
            "label": "On",
            "kind": "text",
            "ref": [
              "purchase_orders.purchase_order",
              "orders.order",
              "quotes.quote",
              "finance_sales_ledger.invoice",
              "suppliers.supplier",
              "customers.customer",
              "supplier_quotes.supplier_quote",
              "finance_purchase_ledger.purchase_invoice"
            ],
            "ref_id_field": "tag_ids",
            "ref_object_field": "tag_objects"
          }
        ],
        "fact_groups": [
          {
            "label": null,
            "fields": [
              "needs_reason"
            ],
            "section": "Needs a person"
          },
          {
            "label": null,
            "fields": [
              "done_note"
            ],
            "section": "Marked done"
          },
          {
            "label": null,
            "fields": [
              "from_address",
              "to",
              "cc"
            ],
            "section": "The email",
            "section_heading": false
          },
          {
            "label": null,
            "fields": [
              "received_at",
              "sent_at",
              "mailbox"
            ],
            "section": "The email"
          },
          {
            "label": null,
            "fields": [
              "sent_by",
              "sent_because",
              "redirected_to",
              "blocked_in_testing"
            ],
            "section": "The email"
          },
          {
            "label": null,
            "fields": [
              "in_reply_to"
            ],
            "section": "The email"
          },
          {
            "label": null,
            "fields": [
              "subject"
            ],
            "section": "The email",
            "style": "title"
          },
          {
            "label": null,
            "fields": [
              "email_text"
            ],
            "section": "The email"
          },
          {
            "label": null,
            "fields": [
              "attachment_files"
            ],
            "section": "The email"
          },
          {
            "label": null,
            "fields": [
              "attachments_not_kept"
            ],
            "section": "The email"
          },
          {
            "label": null,
            "fields": [
              "tag_labels"
            ],
            "section": "Tags"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "comms_ingest.proposed_action",
          "label": "Proposed actions",
          "field": "proposed_actions",
          "key_field": "id",
          "columns": [
            {
              "field": "step",
              "label": "Step",
              "kind": "number"
            },
            {
              "field": "action_label",
              "label": "Action",
              "kind": "text"
            },
            {
              "field": "acts_on_label",
              "label": "On",
              "kind": "text",
              "ref": [
                "purchase_orders.purchase_order",
                "orders.order",
                "quotes.quote",
                "finance_sales_ledger.invoice",
                "suppliers.supplier",
                "customers.customer",
                "supplier_quotes.supplier_quote",
                "finance_purchase_ledger.purchase_invoice"
              ],
              "ref_id_field": "acts_on_id",
              "ref_object_field": "acts_on_object"
            },
            {
              "field": "summary",
              "label": "What it will do",
              "kind": "text",
              "untrusted": true
            },
            {
              "field": "state",
              "label": "State",
              "kind": "state",
              "enum_labels": {
                "confirmed": "Confirmed",
                "done_automatically": "Done automatically",
                "for_a_person": "For a person",
                "nothing_to_do": "Nothing to do",
                "rejected": "Rejected",
                "waiting": "Waiting",
                "withdrawn": "Withdrawn"
              }
            },
            {
              "field": "will_happen_text",
              "label": "What will happen",
              "kind": "text",
              "untrusted": true
            },
            {
              "field": "why",
              "label": "Why",
              "kind": "text",
              "untrusted": true
            },
            {
              "field": "automatic_attempt_text",
              "label": "Tried automatically",
              "kind": "text",
              "untrusted": true
            },
            {
              "field": "decided",
              "label": "Decided",
              "kind": "text",
              "untrusted": true
            }
          ],
          "paged": false,
          "parent_filter": null,
          "stack": {
            "heading_fields": [
              "step",
              "action_label",
              "acts_on_label",
              "summary"
            ],
            "lines": [
              {
                "label": "What will happen",
                "field": "will_happen_text"
              },
              {
                "label": null,
                "field": "automatic_attempt_text"
              },
              {
                "label": "Why",
                "field": "why",
                "items_field": "evidence",
                "when_folded": true
              },
              {
                "label": "Not chosen",
                "items_field": "not_chosen",
                "when_folded": true
              }
            ],
            "folded_states": [
              "confirmed",
              "done_automatically",
              "rejected",
              "for_a_person",
              "withdrawn"
            ],
            "folded_detail_field": "decided"
          }
        }
      ],
      "related": [],
      "lifecycle": [],
      "about": "An email is one that One sent or received, kept as it was, with what One did with it: the actions it proposed, or why there was nothing to do, or why it needs a person. A tag says which record the email is about, and an email has any number.",
      "state_meanings": {
        "being_processed": "Being processed: One will tag the email and propose any actions in a moment.",
        "actions_waiting": "Actions waiting: the proposed actions below will wait until someone confirms or rejects them.",
        "needs_a_person": "Needs a person: One couldn't deal with the email on its own. The reason says what someone needs to do.",
        "nothing_to_do": "Nothing to do: One found nothing to record from the email. The reason is below.",
        "done": "Done: everything the email needed has been dealt with, by One or by a person.",
        "sent": "Sent: One sent the email and tagged it to the records it was sent about. A reply will be tagged to the same records."
      },
      "state_tones": {
        "being_processed": "in_progress",
        "actions_waiting": "attention",
        "needs_a_person": "attention",
        "nothing_to_do": "neutral",
        "done": "done",
        "sent": "done"
      },
      "commands": [
        "comms_ingest.confirm_all_proposed_actions",
        "comms_ingest.mark_email_done",
        "comms_ingest.add_email_tag",
        "comms_ingest.remove_email_tag",
        "comms_ingest.send_email_again"
      ],
      "features": [
        "one_mailbox"
      ]
    },
    {
      "id": "comms_ingest.proposed_action",
      "module": "comms_ingest_module",
      "label": "Proposed action",
      "plural": "Proposed actions",
      "description": "One of One's commands proposed from a received email, with what will happen, why and its evidence, or a step for a person, or why there is nothing to do.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "waiting",
        "confirmed",
        "done_automatically",
        "withdrawn",
        "rejected",
        "for_a_person",
        "nothing_to_do"
      ],
      "list": null,
      "record": null,
      "parents": [
        "comms_ingest.email"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A proposed action is one of One's commands that One proposes from a received email, with what will happen, why and its evidence. A person confirms the proposed action or rejects it. Confirming it runs the command as that person, with the dry run asked again first.",
      "state_meanings": {
        "waiting": "Waiting: nothing will happen until someone confirms or rejects the proposed action.",
        "confirmed": "Confirmed: a person confirmed the proposed action. The change shows on the record the action acted on.",
        "done_automatically": "Done automatically: One ran the proposed action without waiting for a person.",
        "withdrawn": "Withdrawn: One withdrew the proposed action because the record changed, so nothing will happen.",
        "rejected": "Rejected: a person rejected the proposed action, so nothing was done.",
        "for_a_person": "For a person: One never takes this step. Someone takes the step in the app once the earlier steps are done.",
        "nothing_to_do": "Nothing to do: One found nothing to record from the email. The reason is below."
      },
      "state_tones": {
        "waiting": "attention",
        "confirmed": "done",
        "done_automatically": "done",
        "withdrawn": "stopped",
        "rejected": "stopped",
        "for_a_person": "attention",
        "nothing_to_do": "neutral"
      },
      "commands": [
        "comms_ingest.confirm_proposed_action",
        "comms_ingest.reject_proposed_action",
        "comms_ingest.make_automatic_from_proposed_action",
        "comms_ingest.stop_automatic_from_proposed_action"
      ],
      "features": []
    },
    {
      "id": "customer_access.customer_link",
      "module": "customer_access_module",
      "label": "Customer user",
      "plural": "Customer users",
      "description": "A person's standing at one customer in the customer portal, basic until staff add extras; removed, never deleted.",
      "identifier": {
        "kind": "uuid",
        "field": "link_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "active",
        "revoked",
        "deactivated"
      ],
      "list": {
        "path": "/views/customer-users",
        "key_field": "link_id",
        "title_field": "name",
        "columns": [
          {
            "field": "name",
            "label": "Name",
            "kind": "text"
          },
          {
            "field": "email",
            "label": "Email address",
            "kind": "text"
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text",
            "ref": "customers.customer",
            "ref_id_field": "customer_code"
          },
          {
            "field": "customer_code",
            "label": "Customer code",
            "kind": "identifier",
            "ref": "customers.customer"
          },
          {
            "field": "status",
            "label": "Status",
            "kind": "state",
            "enum_labels": {
              "active": "Active",
              "deactivated": "Deactivated",
              "revoked": "Removed"
            }
          },
          {
            "field": "has_signed_in",
            "label": "Signed in",
            "kind": "boolean"
          },
          {
            "field": "added_at",
            "label": "Added",
            "kind": "datetime"
          }
        ],
        "filters": [
          {
            "param": "customer_id",
            "label": "Customer ID",
            "kind": "multi_enum",
            "options": [],
            "description": "Only the people at these customers (repeat the param for several; at most 20). Each is the customer's id, as on the customer's record, not the customer number (CUS-…)."
          },
          {
            "param": "status",
            "label": "Status",
            "kind": "enum",
            "options": [
              [
                "active",
                "Active"
              ],
              [
                "revoked",
                "Removed"
              ],
              [
                "deactivated",
                "Deactivated"
              ]
            ]
          }
        ],
        "sorts": [
          [
            "name",
            "Name"
          ],
          [
            "email",
            "Email address"
          ],
          [
            "customer_name",
            "Customer"
          ],
          [
            "status",
            "Status"
          ],
          [
            "added_at",
            "Added"
          ]
        ],
        "searchable": true,
        "queues": []
      },
      "record": {
        "path": "/customer-access/customer-users/{link_id}",
        "path_param": "link_id",
        "title_field": "name",
        "state_fields": [
          "status"
        ],
        "state_labels": {
          "status": {
            "active": "Active",
            "deactivated": "Deactivated",
            "revoked": "Removed"
          }
        },
        "facts": [
          {
            "field": "email",
            "label": "Email address",
            "kind": "text"
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text",
            "ref": "customers.customer",
            "ref_id_field": "customer_code"
          },
          {
            "field": "customer_code",
            "label": "Customer code",
            "kind": "identifier",
            "ref": "customers.customer"
          },
          {
            "field": "access",
            "label": "Access",
            "kind": "text"
          },
          {
            "field": "has_signed_in",
            "label": "Signed in",
            "kind": "boolean"
          },
          {
            "field": "added_at",
            "label": "Added",
            "kind": "datetime"
          },
          {
            "field": "removed_at",
            "label": "Removed",
            "kind": "datetime"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [
        "customers.customer"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A customer user is one person's standing at one customer in the customer portal. The people feed adds the people in Tharstern's contacts. Staff add others with \"Add customer user…\" or \"Link customer user…\" on the customer. \"Send portal invite\" emails the person an invite to the portal. A customer user starts with the person's own orders and quotes only. \"Set portal access…\" adds extras, such as seeing all of the customer's work. \"Remove customer user\" ends the person's standing at this customer only.",
      "state_meanings": {
        "active": "Active: the person can use the portal at this customer while the customer's portal is open.",
        "revoked": "Removed: the person can't use the portal at this customer until staff link the person again.",
        "deactivated": "Deactivated: the person can't use the portal at any customer. The person stays on this customer's list until staff remove the customer user."
      },
      "state_tones": {
        "active": "done",
        "revoked": "neutral",
        "deactivated": "neutral"
      },
      "commands": [
        "customer_access.send_portal_invite",
        "customer_access.set_link_extras",
        "customer_access.unlink_person_from_customer"
      ],
      "features": []
    },
    {
      "id": "customer_access.customer_team",
      "module": "customer_access_module",
      "label": "Team",
      "plural": "Teams",
      "description": "A named set of one customer's people, for a second brand under one customer and for team notices; grants nothing, retired never deleted.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "active",
        "retired"
      ],
      "list": null,
      "record": null,
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A team is a named set of one customer's people. Staff make one with \"Create team…\" on the customer, for a second brand under one customer or for notices to some of its people, and put the customer's own users in it with \"Add to team…\". A team changes nobody's access. A person may be in several teams, and leaves every team at the customer when they are removed from it. \"Retire team\" ends a team for good, and its name can then be used again.",
      "state_meanings": {
        "active": "Active: the team counts its people wherever teams are read.",
        "retired": "Retired: the team will count nobody and can't be renamed, changed or brought back."
      },
      "state_tones": {
        "active": "done",
        "retired": "neutral"
      },
      "commands": [
        "customer_access.rename_customer_team",
        "customer_access.add_person_to_customer_team",
        "customer_access.remove_person_from_customer_team",
        "customer_access.retire_customer_team"
      ],
      "features": []
    },
    {
      "id": "customer_communications.communication",
      "module": "customer_communications_module",
      "label": "Communication",
      "plural": "Communications",
      "description": "Meeting notes, call notes or a call transcript a colleague shared about customers and their people; shown on each customer's timeline. Not a note, which is an internal memo.",
      "identifier": {
        "kind": "uuid",
        "field": "communication_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": {
        "path": "/views/communication/{communication_id}",
        "path_param": "communication_id",
        "title_field": "title",
        "state_fields": [],
        "state_labels": {},
        "facts": [
          {
            "field": "kind_label",
            "label": "Kind",
            "kind": "text"
          },
          {
            "field": "occurred_at",
            "label": "When it happened",
            "kind": "datetime"
          },
          {
            "field": "source",
            "label": "Source",
            "kind": "text"
          },
          {
            "field": "shared_by",
            "label": "Shared by",
            "kind": "text"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [
        "customer_communications.update_communication",
        "customer_communications.remove_communication"
      ],
      "features": []
    },
    {
      "id": "customers.address",
      "module": "customers_module",
      "label": "Address",
      "plural": "Addresses",
      "description": "A place belonging to one customer, for delivery, invoices or both; open until closed.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "active",
        "closed"
      ],
      "list": {
        "path": "/cis-customers/addresses",
        "key_field": "id",
        "title_field": "name",
        "columns": [
          {
            "field": "customer_code",
            "label": "Customer code",
            "kind": "identifier",
            "ref": "customers.customer"
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text",
            "ref": "customers.customer",
            "ref_id_field": "customer_code"
          },
          {
            "field": "name",
            "label": "Name",
            "kind": "text"
          },
          {
            "field": "address",
            "label": "Address",
            "kind": "text"
          },
          {
            "field": "used_for",
            "label": "Used for",
            "kind": "text"
          },
          {
            "field": "mastered_in",
            "label": "Edited in",
            "kind": "text",
            "enum_labels": {
              "cis": "One",
              "tharstern": "Tharstern"
            }
          },
          {
            "field": "status",
            "label": "Status",
            "kind": "state",
            "enum_labels": {
              "active": "Active",
              "closed": "Closed"
            }
          }
        ],
        "filters": [
          {
            "param": "customer_id",
            "label": "Customer ID",
            "kind": "text",
            "options": [],
            "description": "Only this customer's addresses, by the customer record's customer_id."
          },
          {
            "param": "is_active",
            "label": "Active",
            "kind": "boolean",
            "options": [],
            "description": "Only open (true) or closed (false) addresses."
          },
          {
            "param": "is_delivery",
            "label": "Delivery address",
            "kind": "boolean",
            "options": [],
            "description": "Only delivery addresses."
          },
          {
            "param": "is_invoice",
            "label": "Invoice address",
            "kind": "boolean",
            "options": [],
            "description": "Only invoice addresses."
          },
          {
            "param": "mastered_in",
            "label": "Edited in",
            "kind": "enum",
            "options": [
              [
                "tharstern",
                "Tharstern"
              ],
              [
                "cis",
                "One"
              ]
            ],
            "description": "``tharstern``: fed from the replica and read-only in CIS. ``cis``: edited in CIS only.\n\nMastery only ever moves from ``tharstern`` to ``cis`` (the switch, or an unlink)."
          },
          {
            "param": "is_country_unknown",
            "label": "Country unknown",
            "kind": "boolean",
            "options": [],
            "description": "Only addresses with (true) or without (false) a country."
          }
        ],
        "sorts": [
          [
            "customer_name",
            "Customer"
          ],
          [
            "name",
            "Name"
          ],
          [
            "postcode",
            "Postcode"
          ],
          [
            "is_default_delivery",
            "Is Default Delivery"
          ]
        ],
        "searchable": true,
        "queues": []
      },
      "record": null,
      "parents": [
        "customers.customer"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "active": "done",
        "closed": "neutral"
      },
      "commands": [
        "customers.create_address",
        "customers.update_address",
        "customers.reopen_address",
        "customers.close_address"
      ],
      "features": []
    },
    {
      "id": "customers.customer",
      "module": "customers_module",
      "label": "Customer",
      "plural": "Customers",
      "description": "A customer One sells to, created in One or fed from Tharstern and linked to it; open until closed.",
      "identifier": {
        "kind": "cis_number",
        "field": "customer_code",
        "prefix": "CUS",
        "pattern": "^CUS-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}$",
        "line_pattern": "^CUS-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}\\.[0-9]{2,}$"
      },
      "states": [
        "active",
        "closed"
      ],
      "list": {
        "path": "/cis-customers",
        "key_field": "customer_code",
        "title_field": "name",
        "columns": [
          {
            "field": "customer_code",
            "label": "Customer",
            "kind": "identifier"
          },
          {
            "field": "name",
            "label": "Name",
            "kind": "text"
          },
          {
            "field": "market",
            "label": "Market",
            "kind": "text"
          },
          {
            "field": "status",
            "label": "Status",
            "kind": "state",
            "enum_labels": {
              "active": "Active",
              "closed": "Closed"
            }
          },
          {
            "field": "tharstern_customer_code",
            "label": "Tharstern code",
            "kind": "text"
          },
          {
            "field": "default_trading_company",
            "label": "Trading company",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "default_trading_company_code"
          }
        ],
        "filters": [
          {
            "param": "is_active",
            "label": "Active",
            "kind": "boolean",
            "options": [],
            "description": "Only open (true) or closed (false) customers."
          },
          {
            "param": "is_linked",
            "label": "Linked to Tharstern",
            "kind": "boolean",
            "options": [],
            "description": "Only customers with (true) or without (false) a Tharstern link."
          },
          {
            "param": "mastered_in",
            "label": "Edited in",
            "kind": "enum",
            "options": [
              [
                "tharstern",
                "Tharstern"
              ],
              [
                "cis",
                "One"
              ]
            ],
            "description": "Only customers edited in this system."
          },
          {
            "param": "default_trading_company_code",
            "label": "Trading company",
            "kind": "entity",
            "options": [],
            "object": "trading_companies.trading_company",
            "description": "Only customers whose default trading company has this code, such as POL: what *Stop trading…* asks to change first."
          }
        ],
        "sorts": [
          [
            "name",
            "Name"
          ],
          [
            "customer_code",
            "Customer"
          ],
          [
            "created_at",
            "Created at"
          ]
        ],
        "searchable": true,
        "queues": []
      },
      "record": {
        "path": "/views/cis-customer/{customer_code}",
        "path_param": "customer_code",
        "title_field": "name",
        "state_fields": [
          "status"
        ],
        "state_labels": {
          "status": {
            "active": "Active",
            "closed": "Closed"
          }
        },
        "facts": [
          {
            "field": "market",
            "label": "Market",
            "kind": "text"
          },
          {
            "field": "default_trading_company",
            "label": "Default trading company",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "default_trading_company_code"
          },
          {
            "field": "mastered_in",
            "label": "Edited in",
            "kind": "text"
          },
          {
            "field": "tharstern_customer_code",
            "label": "Tharstern code",
            "kind": "text"
          },
          {
            "field": "sales_rep_name",
            "label": "Sales rep",
            "kind": "text"
          },
          {
            "field": "csr_name",
            "label": "CSR",
            "kind": "text"
          },
          {
            "field": "default_order_contact_name",
            "label": "Default order contact",
            "kind": "text"
          },
          {
            "field": "portal_access",
            "label": "Customer portal",
            "kind": "text"
          },
          {
            "field": "portal_view_href",
            "label": "Portal view",
            "kind": "link",
            "link_text": "View portal"
          },
          {
            "field": "spend_reporting",
            "label": "Spend reporting",
            "kind": "text"
          },
          {
            "field": "health_tier",
            "label": "Health tier",
            "kind": "text"
          },
          {
            "field": "health_tier_override",
            "label": "Health tier override",
            "kind": "text"
          },
          {
            "field": "key_people",
            "label": "Key people",
            "kind": "text"
          },
          {
            "field": "catalogues",
            "label": "Catalogues",
            "kind": "text"
          },
          {
            "field": "show_core_range",
            "label": "Show core range",
            "kind": "text"
          },
          {
            "field": "delivery_updates_to",
            "label": "Delivery updates go to",
            "kind": "text"
          },
          {
            "field": "delivery_problems",
            "label": "Delivery problems",
            "kind": "text"
          }
        ],
        "timeline_path": "/views/cis-customer/{customer_code}/timeline",
        "on_open": null,
        "include": [],
        "timeline_search": "Searches email subjects and the start of each email, meeting titles, and shared notes and transcripts.",
        "timeline_filters": [
          {
            "param": "kind",
            "label": "Kind",
            "kind": "multi_enum",
            "options": [
              [
                "email",
                "Email"
              ],
              [
                "meeting",
                "Meeting"
              ],
              [
                "shared",
                "Shared communication"
              ]
            ]
          },
          {
            "param": "date_from",
            "label": "From",
            "kind": "date",
            "options": [],
            "description": "The first day to show, included."
          },
          {
            "param": "date_to",
            "label": "To",
            "kind": "date",
            "options": [],
            "description": "The last day to show, included."
          }
        ]
      },
      "parents": [],
      "children": [
        {
          "object": "customers.address",
          "label": "Addresses",
          "field": "addresses",
          "key_field": "id",
          "columns": [
            {
              "field": "name",
              "label": "Name",
              "kind": "text"
            },
            {
              "field": "address",
              "label": "Address",
              "kind": "text"
            },
            {
              "field": "used_for",
              "label": "Used for",
              "kind": "text"
            },
            {
              "field": "mastered_in",
              "label": "Edited in",
              "kind": "text"
            },
            {
              "field": "status",
              "label": "Status",
              "kind": "state",
              "enum_labels": {
                "active": "Active",
                "closed": "Closed"
              }
            }
          ],
          "paged": true,
          "parent_filter": "customer_id"
        },
        {
          "object": "customer_access.customer_link",
          "label": "Customer users",
          "field": "portal_people",
          "key_field": "link_id",
          "columns": [
            {
              "field": "name",
              "label": "Name",
              "kind": "text"
            },
            {
              "field": "email",
              "label": "Email address",
              "kind": "text"
            },
            {
              "field": "access",
              "label": "Access",
              "kind": "text"
            },
            {
              "field": "signed_in",
              "label": "Signed in",
              "kind": "text"
            }
          ],
          "paged": true,
          "parent_filter": "customer_id"
        }
      ],
      "related": [
        {
          "object": "orders.order",
          "label": "Orders",
          "params": {
            "customer_id": "{customer_id}"
          }
        },
        {
          "object": "quotes.quote",
          "label": "Quotes",
          "params": {
            "customer_id": "{customer_id}"
          }
        },
        {
          "object": "recommendations_generation.alert",
          "label": "Customer alerts",
          "params": {
            "customer_id": "{tharstern_customer_code}"
          },
          "secondary": true
        },
        {
          "object": "recommendations_generation.opportunity",
          "label": "Customer opportunities",
          "params": {
            "customer_id": "{tharstern_customer_code}"
          },
          "secondary": true
        },
        {
          "object": "comms_ingest.email",
          "label": "Emails",
          "params": {
            "tag_object": "customers.customer",
            "tag_key": "{customer_code}"
          },
          "secondary": true
        }
      ],
      "lifecycle": [],
      "about": "A customer is a business that buys from us. Each customer is edited either in One or in Tharstern. One takes the changes to a customer edited in Tharstern every five minutes. Staff create a customer in One with \"Create customer\". Staff edit a customer that comes from Tharstern in Tharstern.",
      "state_meanings": {
        "active": "Active: the customer record is open.",
        "closed": "Closed: staff can't add addresses to the customer until someone reopens the customer."
      },
      "state_tones": {
        "active": "done",
        "closed": "neutral"
      },
      "commands": [
        "customer_communications.add_communication",
        "customers.create_customer",
        "customers.update_customer",
        "account_allocation.allocate_customer",
        "customer_access.set_default_order_contact",
        "workflow.set_customer_key_person",
        "catalogue.set_customer_catalogues",
        "catalogue.show_customer_core_range",
        "catalogue.hide_customer_core_range",
        "orders.set_customer_delivery_updates",
        "finance_credit_control.set_billed_to",
        "customers.change_default_trading_company",
        "customer_access.find_or_create_person_at_customer",
        "customer_access.link_person_to_customer",
        "customer_access.create_customer_team",
        "customer_access.enable_customer_spend_reporting",
        "customer_access.disable_customer_spend_reporting",
        "workflow.override_customer_health_tier",
        "workflow.end_customer_health_tier_override",
        "journey_testing.sign_in_as_customer",
        "customers.link_tharstern_customer",
        "customers.unlink_tharstern_customer",
        "customer_access.unblock_customer_portal",
        "customer_access.block_customer_portal",
        "customers.reopen_customer",
        "customers.close_customer"
      ],
      "features": []
    },
    {
      "id": "delivery_tracking.tracking_event",
      "module": "delivery_tracking_module",
      "label": "Courier tracking event",
      "plural": "Courier tracking",
      "description": "One scan a courier reported on one of an order's consignments, or the note that it has reported none yet.",
      "identifier": {
        "kind": "field",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "orders.order"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": []
    },
    {
      "id": "draft_estimates.draft_estimate",
      "module": "draft_estimates_module",
      "label": "Merchandise request",
      "plural": "Merchandise requests",
      "description": "One bespoke merchandise request, from submission until the merchandise team's Promo Office quote prices it.",
      "identifier": {
        "kind": "cis_number",
        "field": "reference",
        "prefix": "MCH",
        "pattern": "^MCH-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}$",
        "line_pattern": "^MCH-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}\\.[0-9]{2,}$"
      },
      "states": [
        "awaiting_quote",
        "priced",
        "cost_changed",
        "withdrawn"
      ],
      "list": {
        "path": "/draft-estimates/list",
        "key_field": "reference",
        "title_field": "project_name",
        "columns": [
          {
            "field": "reference",
            "label": "Reference",
            "kind": "identifier"
          },
          {
            "field": "project_name",
            "label": "Project name",
            "kind": "text"
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text"
          },
          {
            "field": "state",
            "label": "State",
            "kind": "state",
            "enum_labels": {
              "awaiting_quote": "Awaiting quote",
              "cost_changed": "Cost changed",
              "priced": "Priced",
              "withdrawn": "Withdrawn"
            }
          },
          {
            "field": "request_line_count",
            "label": "Request lines",
            "kind": "number"
          },
          {
            "field": "deadline",
            "label": "Deadline",
            "kind": "date"
          },
          {
            "field": "created_at",
            "label": "Created",
            "kind": "datetime"
          }
        ],
        "filters": [
          {
            "param": "state",
            "label": "State",
            "kind": "enum",
            "options": [
              [
                "awaiting_quote",
                "Awaiting quote"
              ],
              [
                "priced",
                "Priced"
              ],
              [
                "cost_changed",
                "Cost changed"
              ],
              [
                "withdrawn",
                "Withdrawn"
              ]
            ],
            "description": "Only merchandise requests in this state."
          }
        ],
        "sorts": [
          [
            "created_at",
            "Created"
          ],
          [
            "reference",
            "Reference"
          ],
          [
            "customer_code",
            "Customer code"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "awaiting_quote",
            "label": "Awaiting quote",
            "params": {
              "state": "awaiting_quote"
            },
            "count": true,
            "default": false
          },
          {
            "id": "cost_changed",
            "label": "Cost changed",
            "params": {
              "state": "cost_changed"
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/views/draft-estimate/{reference}",
        "path_param": "reference",
        "title_field": "project_name",
        "state_fields": [
          "state"
        ],
        "state_labels": {
          "state": {
            "awaiting_quote": "Awaiting quote",
            "cost_changed": "Cost changed",
            "priced": "Priced",
            "withdrawn": "Withdrawn"
          }
        },
        "facts": [
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text",
            "ref": "customers.customer",
            "ref_id_field": "cis_customer_code"
          },
          {
            "field": "deadline",
            "label": "Deadline",
            "kind": "date"
          },
          {
            "field": "delivery",
            "label": "Delivery",
            "kind": "text"
          },
          {
            "field": "notes",
            "label": "Notes",
            "kind": "text"
          },
          {
            "field": "merchandise_team_ticket",
            "label": "Merchandise team ticket",
            "kind": "text"
          },
          {
            "field": "created_by_name",
            "label": "Requested by",
            "kind": "text"
          },
          {
            "field": "created_at",
            "label": "Requested",
            "kind": "datetime"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "draft_estimates.draft_estimate_request_line",
          "label": "Request lines",
          "field": "request_lines",
          "key_field": "id",
          "columns": [
            {
              "field": "line_no",
              "label": "Line",
              "kind": "number"
            },
            {
              "field": "item_type",
              "label": "Item",
              "kind": "text"
            },
            {
              "field": "quantity",
              "label": "Quantity",
              "kind": "text"
            },
            {
              "field": "logo_colours",
              "label": "Logo colours",
              "kind": "text"
            },
            {
              "field": "budget",
              "label": "Budget",
              "kind": "text"
            },
            {
              "field": "sourcing_city_code",
              "label": "Sourcing City code",
              "kind": "text"
            },
            {
              "field": "logos",
              "label": "Logos",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [
        {
          "object": "workflow.ticket",
          "label": "Tickets",
          "params": {
            "id": "{merchandise_team_ticket_id}"
          },
          "secondary": true
        }
      ],
      "lifecycle": [],
      "about": "A Merchandise request asks the merchandise team to price bespoke merchandise for a customer. A CSR makes the request from the customer record with \"Request merchandise quote\". One raises a ticket for the merchandise team. The merchandise team types the merchandise request number (MCH-…) on the Promo Office quote. One will then price the request at the merchandise team's selling total.",
      "state_meanings": {
        "awaiting_quote": "Awaiting quote: the request will wait until the merchandise team attaches a Promo Office quote.",
        "priced": "Priced: the request carries the merchandise team's price.",
        "cost_changed": "Cost changed: the request will be held until the CSR re-prices or withdraws the request.",
        "withdrawn": "Withdrawn: the request will go no further."
      },
      "state_tones": {
        "awaiting_quote": "in_progress",
        "priced": "done",
        "cost_changed": "attention",
        "withdrawn": "stopped"
      },
      "commands": [
        "draft_estimates.create_draft_estimate",
        "draft_estimates.attach_draft_estimate_file"
      ],
      "features": [
        "merchandise_draft_estimates"
      ]
    },
    {
      "id": "draft_estimates.draft_estimate_request_line",
      "module": "draft_estimates_module",
      "label": "Request line",
      "plural": "Request lines",
      "description": "One item the customer asked the merchandise team to quote, with the logos added to it.",
      "identifier": {
        "kind": "field",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "draft_estimates.draft_estimate"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": []
    },
    {
      "id": "enrichment.prospect_contact",
      "module": "enrichment_module",
      "label": "Prospect",
      "plural": "Prospects",
      "description": "A contact the enrichment agent suggests at an existing customer, with its rationale and confidence.",
      "identifier": {
        "kind": "field",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": {
        "path": "/enrichment/prospects",
        "key_field": "id",
        "title_field": "name",
        "columns": [
          {
            "field": "created_at",
            "label": "Suggested",
            "kind": "datetime"
          },
          {
            "field": "name",
            "label": "Name",
            "kind": "text"
          },
          {
            "field": "likely_job_title",
            "label": "Likely job title",
            "kind": "text"
          },
          {
            "field": "likely_function",
            "label": "Likely function",
            "kind": "text"
          },
          {
            "field": "linkedin_url",
            "label": "LinkedIn",
            "kind": "link"
          },
          {
            "field": "rationale",
            "label": "Rationale",
            "kind": "text"
          },
          {
            "field": "confidence",
            "label": "Confidence",
            "kind": "text",
            "enum_labels": {
              "high": "High",
              "low": "Low",
              "medium": "Medium"
            }
          }
        ],
        "filters": [
          {
            "param": "customer_id",
            "label": "Customer",
            "kind": "entity",
            "options": [],
            "object": "customers.customer",
            "entity_value": "tharstern_customer_code",
            "description": "Only this customer's prospects, chosen by the customer's Tharstern code, not the customer number. Prospects are kept against the Tharstern code, so a customer with no Tharstern code has no prospects."
          }
        ],
        "sorts": [
          [
            "confidence",
            "Confidence"
          ],
          [
            "name",
            "Name"
          ],
          [
            "created_at",
            "Suggested"
          ]
        ],
        "searchable": true,
        "queues": []
      },
      "record": null,
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A prospect is a contact that the enrichment agent suggests at an existing customer. Each prospect carries a likely job title, a confidence and the agent's rationale. A customer refresh runs the enrichment agent and saves new prospects. Suppressing a prospect will stop the agent suggesting the name for that customer again.",
      "commands": [
        "enrichment.suppress_prospect"
      ],
      "features": []
    },
    {
      "id": "feedback.feedback_image",
      "module": "feedback_module",
      "label": "Image",
      "plural": "Images",
      "description": "A screenshot or pasted image sent with a quick note or a Still wrong report.",
      "identifier": {
        "kind": "uuid",
        "field": "image_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "feedback.feedback_item"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": []
    },
    {
      "id": "feedback.feedback_item",
      "module": "feedback_module",
      "label": "Feedback",
      "plural": "Feedback",
      "description": "A point someone raised about One or Classic, from sending through triage and build to the raiser's re-test.",
      "identifier": {
        "kind": "uuid",
        "field": "feedback_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "new",
        "awaiting_ruling",
        "ready_to_plan",
        "in_build",
        "ready_to_retest",
        "closed",
        "duplicate"
      ],
      "list": {
        "path": "/feedback/items",
        "key_field": "feedback_id",
        "title_field": "title",
        "columns": [
          {
            "field": "created_at",
            "label": "Raised",
            "kind": "datetime"
          },
          {
            "field": "page_path",
            "label": "Page",
            "kind": "text"
          },
          {
            "field": "feedback_id",
            "label": "Feedback",
            "kind": "identifier"
          },
          {
            "field": "title",
            "label": "Feedback",
            "kind": "text"
          },
          {
            "field": "state",
            "label": "State",
            "kind": "state",
            "enum_labels": {
              "awaiting_ruling": "Awaiting ruling",
              "closed": "Closed",
              "duplicate": "Duplicate",
              "in_build": "In build",
              "new": "New",
              "ready_to_plan": "Ready to plan",
              "ready_to_retest": "Ready to re-test"
            }
          },
          {
            "field": "group",
            "label": "Group",
            "kind": "state",
            "enum_labels": {
              "bug": "Bug",
              "declaration": "Declaration",
              "environment": "Environment set-up",
              "explain": "Explain",
              "not_a_fact": "Not a fact",
              "not_for_one": "Not for One",
              "other_stream": "Other stream",
              "platform": "Platform",
              "product_decision": "Product decision",
              "watch": "Potential gap to watch"
            }
          },
          {
            "field": "stream",
            "label": "Stream",
            "kind": "text"
          },
          {
            "field": "package",
            "label": "Package",
            "kind": "text"
          },
          {
            "field": "user_name",
            "label": "Raised by",
            "kind": "text"
          },
          {
            "field": "environment",
            "label": "Environment",
            "kind": "text"
          }
        ],
        "filters": [
          {
            "param": "state",
            "label": "State",
            "kind": "multi_enum",
            "options": [
              [
                "new",
                "New"
              ],
              [
                "awaiting_ruling",
                "Awaiting ruling"
              ],
              [
                "ready_to_plan",
                "Ready to plan"
              ],
              [
                "in_build",
                "In build"
              ],
              [
                "ready_to_retest",
                "Ready to re-test"
              ],
              [
                "closed",
                "Closed"
              ],
              [
                "duplicate",
                "Duplicate"
              ]
            ],
            "description": "Only feedback in these states."
          },
          {
            "param": "group",
            "label": "Group",
            "kind": "multi_enum",
            "options": [
              [
                "bug",
                "Bug"
              ],
              [
                "platform",
                "Platform"
              ],
              [
                "declaration",
                "Declaration"
              ],
              [
                "product_decision",
                "Product decision"
              ],
              [
                "explain",
                "Explain"
              ],
              [
                "not_a_fact",
                "Not a fact"
              ],
              [
                "other_stream",
                "Other stream"
              ],
              [
                "not_for_one",
                "Not for One"
              ],
              [
                "watch",
                "Potential gap to watch"
              ],
              [
                "environment",
                "Environment set-up"
              ]
            ],
            "description": "Only feedback triaged into these groups."
          },
          {
            "param": "stream",
            "label": "Stream",
            "kind": "text",
            "options": []
          },
          {
            "param": "package",
            "label": "Package",
            "kind": "text",
            "options": []
          },
          {
            "param": "user_id",
            "label": "Raised by",
            "kind": "entity",
            "options": [],
            "object": "users.user"
          },
          {
            "param": "mine",
            "label": "Raised by me",
            "kind": "boolean",
            "options": [],
            "description": "True: only feedback you raised."
          },
          {
            "param": "environment",
            "label": "Environment",
            "kind": "text",
            "options": [],
            "description": "e.g. staging, production."
          },
          {
            "param": "object_id",
            "label": "Object ID",
            "kind": "text",
            "options": [],
            "description": "The One object the feedback was raised on, e.g. orders.order."
          },
          {
            "param": "mode",
            "label": "Kind",
            "kind": "multi_enum",
            "options": [
              [
                "feedback",
                "Conversation"
              ],
              [
                "brain_sucker",
                "Brain sucker"
              ],
              [
                "quick_note",
                "Quick note"
              ]
            ],
            "description": "Conversations and brain-sucker sessions are listed only for people who read every kind."
          },
          {
            "param": "reopened",
            "label": "Reopened",
            "kind": "boolean",
            "options": [],
            "description": "True: only feedback reported still wrong."
          },
          {
            "param": "duplicate_of",
            "label": "Duplicate of",
            "kind": "text",
            "options": [],
            "description": "Only the duplicates of this item."
          }
        ],
        "sorts": [
          [
            "created_at",
            "Raised"
          ],
          [
            "user_name",
            "Raised by"
          ],
          [
            "state",
            "State"
          ],
          [
            "group",
            "Group"
          ],
          [
            "stream",
            "Stream"
          ],
          [
            "package",
            "Package"
          ],
          [
            "environment",
            "Environment"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "mine",
            "label": "My feedback",
            "params": {
              "mine": true
            },
            "count": false,
            "default": true
          },
          {
            "id": "new",
            "label": "New",
            "params": {
              "state": [
                "new"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "awaiting_ruling",
            "label": "Awaiting ruling",
            "params": {
              "state": [
                "awaiting_ruling"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "ready_to_plan",
            "label": "Ready to plan",
            "params": {
              "state": [
                "ready_to_plan"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "in_build",
            "label": "In build",
            "params": {
              "state": [
                "in_build"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "ready_to_retest",
            "label": "Ready to re-test",
            "params": {
              "state": [
                "ready_to_retest"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "reopened",
            "label": "Reopened",
            "params": {
              "state": [
                "new"
              ],
              "reopened": true
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/feedback/items/{feedback_id}",
        "path_param": "feedback_id",
        "title_field": "title",
        "state_fields": [
          "state",
          "group"
        ],
        "state_labels": {
          "state": {
            "awaiting_ruling": "Awaiting ruling",
            "closed": "Closed",
            "duplicate": "Duplicate",
            "in_build": "In build",
            "new": "New",
            "ready_to_plan": "Ready to plan",
            "ready_to_retest": "Ready to re-test"
          },
          "group": {
            "bug": "Bug",
            "declaration": "Declaration",
            "environment": "Environment set-up",
            "explain": "Explain",
            "not_a_fact": "Not a fact",
            "not_for_one": "Not for One",
            "other_stream": "Other stream",
            "platform": "Platform",
            "product_decision": "Product decision",
            "watch": "Potential gap to watch"
          }
        },
        "facts": [
          {
            "field": "note",
            "label": "Note",
            "kind": "text"
          },
          {
            "field": "reply",
            "label": "Reply",
            "kind": "text"
          },
          {
            "field": "ruling",
            "label": "Ruling",
            "kind": "text"
          },
          {
            "field": "lead_title",
            "label": "Duplicate of",
            "kind": "text",
            "ref": "feedback.feedback_item",
            "ref_id_field": "duplicate_of"
          },
          {
            "field": "lead_state",
            "label": "Lead's state",
            "kind": "text",
            "enum_labels": {
              "awaiting_ruling": "Awaiting ruling",
              "closed": "Closed",
              "duplicate": "Duplicate",
              "in_build": "In build",
              "new": "New",
              "ready_to_plan": "Ready to plan",
              "ready_to_retest": "Ready to re-test"
            }
          },
          {
            "field": "lead_reply",
            "label": "Lead's reply",
            "kind": "text"
          },
          {
            "field": "stream",
            "label": "Stream",
            "kind": "text"
          },
          {
            "field": "package",
            "label": "Package",
            "kind": "text"
          },
          {
            "field": "user_name",
            "label": "Raised by",
            "kind": "text"
          },
          {
            "field": "created_at",
            "label": "Raised",
            "kind": "datetime"
          },
          {
            "field": "reopened",
            "label": "Reopened",
            "kind": "boolean"
          },
          {
            "field": "environment",
            "label": "Environment",
            "kind": "text"
          },
          {
            "field": "page_path",
            "label": "Page",
            "kind": "text"
          },
          {
            "field": "object_id",
            "label": "Object",
            "kind": "text"
          },
          {
            "field": "record_key",
            "label": "Record",
            "kind": "text"
          },
          {
            "field": "build_sha",
            "label": "Build",
            "kind": "text"
          },
          {
            "field": "preview_id",
            "label": "Preview",
            "kind": "text"
          },
          {
            "field": "viewport",
            "label": "Viewport",
            "kind": "text"
          },
          {
            "field": "user_agent",
            "label": "Browser",
            "kind": "text"
          }
        ],
        "timeline_path": "/feedback/items/{feedback_id}/timeline",
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "feedback.feedback_recent_error",
          "label": "Recent errors",
          "field": "recent_errors",
          "key_field": "error_no",
          "columns": [
            {
              "field": "error_no",
              "label": "Error",
              "kind": "identifier"
            },
            {
              "field": "kind",
              "label": "Kind",
              "kind": "text",
              "enum_labels": {
                "api": "API call",
                "console": "Console"
              }
            },
            {
              "field": "request",
              "label": "Request",
              "kind": "text"
            },
            {
              "field": "http_status",
              "label": "Status",
              "kind": "number"
            },
            {
              "field": "message",
              "label": "Message",
              "kind": "text"
            },
            {
              "field": "trace_id",
              "label": "Trace",
              "kind": "text"
            },
            {
              "field": "trace_href",
              "label": "Trace in Grafana",
              "kind": "link"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "feedback.feedback_pull_request",
          "label": "Pull requests",
          "field": "linked_pull_requests",
          "key_field": "pull_request",
          "columns": [
            {
              "field": "pull_request",
              "label": "Pull request",
              "kind": "identifier"
            },
            {
              "field": "href",
              "label": "On GitHub",
              "kind": "link"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [
        {
          "object": "feedback.feedback_item",
          "label": "Feedback",
          "params": {
            "duplicate_of": "{feedback_id}"
          },
          "secondary": true
        }
      ],
      "lifecycle": [],
      "about": "Feedback is a point someone raised about One or Classic: a quick note sent from the Feedback button with the page and a screenshot, or a longer conversation. Anyone signed in can send a quick note with \"Send quick note\" from any environment, and production keeps every quick note. Admins and engineers triage new feedback, record Ben's rulings, put feedback in packages and link the pull requests that fix it. \"Record feedback for…\" enters a point raised outside One as the chosen person's own. The person who raised the feedback then re-tests on staging and uses \"Confirm fixed\" or \"Still wrong…\". Nobody is notified: people look in One.",
      "state_meanings": {
        "new": "New: the feedback will wait for an admin or engineer to triage the feedback.",
        "awaiting_ruling": "Awaiting ruling: the feedback will wait for Ben's ruling on the product decision.",
        "ready_to_plan": "Ready to plan: the feedback will wait to be put in a package and built.",
        "in_build": "In build: the feedback will be ready to re-test once the linked pull request reaches staging.",
        "ready_to_retest": "Ready to re-test: the person who raised the feedback will check the change on staging.",
        "closed": "Closed: nothing more will happen unless the person who raised the feedback reports the problem still there.",
        "duplicate": "Duplicate: the feedback will follow its lead feedback, whose state and reply show on the record."
      },
      "state_tones": {
        "new": "attention",
        "awaiting_ruling": "in_progress",
        "ready_to_plan": "in_progress",
        "in_build": "in_progress",
        "ready_to_retest": "attention",
        "closed": "done",
        "duplicate": "neutral"
      },
      "commands": [
        "feedback.triage_feedback",
        "feedback.send_quick_note",
        "feedback.record_feedback_for",
        "feedback.rule_on_feedback",
        "feedback.put_feedback_in_package",
        "feedback.link_feedback_pr",
        "feedback.mark_feedback_ready_to_retest",
        "feedback.confirm_feedback_fixed",
        "feedback.report_feedback_still_wrong",
        "feedback.reply_and_close_feedback",
        "feedback.mark_feedback_duplicate"
      ],
      "features": []
    },
    {
      "id": "feedback.feedback_pull_request",
      "module": "feedback_module",
      "label": "Pull request",
      "plural": "Pull requests",
      "description": "A pull request linked to the feedback as its fix, opening on GitHub.",
      "identifier": {
        "kind": "field",
        "field": "pull_request",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "feedback.feedback_item"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": []
    },
    {
      "id": "feedback.feedback_recent_error",
      "module": "feedback_module",
      "label": "Recent error",
      "plural": "Recent errors",
      "description": "A failed API call or console error the quick-capture panel saw just before the note was sent.",
      "identifier": {
        "kind": "field",
        "field": "error_no",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "feedback.feedback_item"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": []
    },
    {
      "id": "finance_accruals_and_prepayments.control_account",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Control account check",
      "plural": "Reconciliation",
      "description": "One control account in Xero against the month end's schedule, to the penny.",
      "identifier": {
        "kind": "field",
        "field": "control_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "agrees",
        "explained",
        "unexplained"
      ],
      "list": null,
      "record": null,
      "parents": [
        "finance_accruals_and_prepayments.month_end"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A control account check sets one control account in Xero against the month end's schedule, to the penny. A difference is fine only when it is explained. The check is kept with its time; it is made again after a correction in Xero.",
      "state_meanings": {
        "agrees": "Agrees: Xero and the schedule agree to the penny.",
        "explained": "Explained: they differ, and every penny of the difference is explained below.",
        "unexplained": "Unexplained: they differ with nothing to explain it: the month can't close."
      },
      "state_tones": {
        "agrees": "done",
        "explained": "done",
        "unexplained": "attention"
      },
      "commands": [],
      "features": []
    },
    {
      "id": "finance_accruals_and_prepayments.estimate_change",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Estimate change",
      "plural": "Estimate changes",
      "description": "A change to a recurring accrual's annual estimate, with why.",
      "identifier": {
        "kind": "field",
        "field": "change_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "finance_accruals_and_prepayments.recurring_accrual"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": []
    },
    {
      "id": "finance_accruals_and_prepayments.journal",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Journal",
      "plural": "Journals",
      "description": "One manual journal a month end posts to Xero: an accrual or deferral and its reversal, or a release that does not reverse.",
      "identifier": {
        "kind": "field",
        "field": "journal_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "pending",
        "posting",
        "posted",
        "failed"
      ],
      "list": null,
      "record": {
        "path": "/accruals-and-prepayments/journals/{journal_id}",
        "path_param": "journal_id",
        "title_field": "heading",
        "state_fields": [
          "posting"
        ],
        "state_labels": {
          "posting": {
            "failed": "Failed",
            "pending": "Ready",
            "posted": "Posted",
            "posting": "Posting"
          }
        },
        "facts": [
          {
            "field": "month_end",
            "label": "Month end",
            "kind": "text",
            "ref": "finance_accruals_and_prepayments.month_end",
            "ref_id_field": "month_end_id"
          },
          {
            "field": "narration",
            "label": "Narration",
            "kind": "text"
          },
          {
            "field": "kind",
            "label": "Kind",
            "kind": "text"
          },
          {
            "field": "dated",
            "label": "Dated",
            "kind": "date"
          },
          {
            "field": "reverses",
            "label": "Reverses",
            "kind": "text"
          },
          {
            "field": "reversal",
            "label": "Reversal",
            "kind": "text",
            "detail": {
              "field": "reversal_detail",
              "label": "Why",
              "kind": "text"
            }
          },
          {
            "field": "xero_journal",
            "label": "Xero journal",
            "kind": "text"
          },
          {
            "field": "error",
            "label": "Error",
            "kind": "text",
            "untrusted": true
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [
        "finance_accruals_and_prepayments.month_end"
      ],
      "children": [
        {
          "object": "finance_accruals_and_prepayments.journal_line",
          "label": "Journal lines",
          "field": "lines",
          "key_field": "line_number",
          "columns": [
            {
              "field": "account",
              "label": "Account",
              "kind": "text"
            },
            {
              "field": "description",
              "label": "Description",
              "kind": "text",
              "detail": {
                "field": "lines_behind",
                "label": "Lines behind it",
                "kind": "text"
              }
            },
            {
              "field": "debit_pence",
              "label": "Debit",
              "kind": "money"
            },
            {
              "field": "credit_pence",
              "label": "Credit",
              "kind": "money"
            }
          ],
          "paged": false,
          "parent_filter": null,
          "totals": {
            "debit_pence": {
              "field": "debit_total_pence",
              "label": "Debit Total Pence",
              "kind": "number"
            },
            "credit_pence": {
              "field": "credit_total_pence",
              "label": "Credit Total Pence",
              "kind": "number"
            }
          }
        }
      ],
      "related": [],
      "lifecycle": [],
      "about": "A journal is one manual journal a month end posts to its trading company's Xero organisation: an accrual or deferral and its reversal on the first of the next month, or a release, which does not reverse. Its lines are netted per account; each line lists the month end lines behind it.",
      "state_meanings": {
        "pending": "Ready: it is posted with the month's journals, in order.",
        "posting": "Posting: it is being sent to Xero now.",
        "posted": "Posted: it is in Xero.",
        "failed": "Failed: Xero refused it or did not answer. Retrying sends it again."
      },
      "state_tones": {
        "pending": "neutral",
        "posting": "in_progress",
        "posted": "done",
        "failed": "stopped"
      },
      "commands": [],
      "features": [
        "accruals_and_prepayments"
      ]
    },
    {
      "id": "finance_accruals_and_prepayments.journal_line",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Journal line",
      "plural": "Journal lines",
      "description": "One account's line of a journal, netted across the month end lines it covers.",
      "identifier": {
        "kind": "field",
        "field": "line_number",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "finance_accruals_and_prepayments.journal"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": []
    },
    {
      "id": "finance_accruals_and_prepayments.month_end",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Month end",
      "plural": "Month ends",
      "description": "One trading company's close of one month: its schedules, the journals posted to its Xero organisation and the check of each control account against Xero.",
      "identifier": {
        "kind": "field",
        "field": "month_end_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "open",
        "proposed",
        "reviewed",
        "posting",
        "posting_failed",
        "posted",
        "closed"
      ],
      "list": {
        "path": "/accruals-and-prepayments/month-ends",
        "key_field": "month_end_id",
        "title_field": "heading",
        "columns": [
          {
            "field": "trading_company",
            "label": "Trading company",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "trading_company_code"
          },
          {
            "field": "month_label",
            "label": "Month",
            "kind": "text"
          },
          {
            "field": "stage",
            "label": "Stage",
            "kind": "state",
            "enum_labels": {
              "closed": "Closed",
              "open": "Not yet proposed",
              "posted": "Posted, reconciling",
              "posting": "Posting to Xero",
              "posting_failed": "Posting failed",
              "proposed": "Review in progress",
              "reviewed": "Reviewed, not posted"
            }
          },
          {
            "field": "lines_to_decide",
            "label": "Lines to decide",
            "kind": "number"
          },
          {
            "field": "closed",
            "label": "Closed",
            "kind": "text"
          }
        ],
        "filters": [
          {
            "param": "trading_company_code",
            "label": "Trading company",
            "kind": "entity",
            "options": [],
            "object": "trading_companies.trading_company",
            "entity_value": "code",
            "description": "Only this trading company's, by its code (POL, BCQ). Omit for every company."
          },
          {
            "param": "stage",
            "label": "Stage",
            "kind": "multi_enum",
            "options": [
              [
                "open",
                "Not yet proposed"
              ],
              [
                "proposed",
                "Review in progress"
              ],
              [
                "reviewed",
                "Reviewed, not posted"
              ],
              [
                "posting",
                "Posting to Xero"
              ],
              [
                "posting_failed",
                "Posting failed"
              ],
              [
                "posted",
                "Posted, reconciling"
              ],
              [
                "closed",
                "Closed"
              ]
            ],
            "description": "Only month ends at any of these stages (repeat the param)."
          }
        ],
        "sorts": [
          [
            "month",
            "Month (YYYY-MM)"
          ],
          [
            "trading_company_code",
            "Trading company code"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "to_review",
            "label": "To review",
            "params": {
              "stage": [
                "open",
                "proposed"
              ]
            },
            "count": false,
            "default": false
          },
          {
            "id": "to_post",
            "label": "To post",
            "params": {
              "stage": [
                "reviewed",
                "posting_failed"
              ]
            },
            "count": false,
            "default": false
          },
          {
            "id": "to_close",
            "label": "To close",
            "params": {
              "stage": [
                "posted"
              ]
            },
            "count": false,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/accruals-and-prepayments/month-ends/{month_end_id}",
        "path_param": "month_end_id",
        "title_field": "heading",
        "state_fields": [
          "stage"
        ],
        "state_labels": {
          "stage": {
            "closed": "Closed",
            "open": "Not yet proposed",
            "posted": "Posted, reconciling",
            "posting": "Posting to Xero",
            "posting_failed": "Posting failed",
            "proposed": "Review in progress",
            "reviewed": "Reviewed, not posted"
          }
        },
        "facts": [
          {
            "field": "trading_company",
            "label": "Trading company",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "trading_company_code"
          },
          {
            "field": "month_label",
            "label": "Month",
            "kind": "text"
          },
          {
            "field": "lines_to_decide",
            "label": "Lines to decide",
            "kind": "number"
          },
          {
            "field": "sales_data_as_at",
            "label": "Sales data as at",
            "kind": "text"
          },
          {
            "field": "closed",
            "label": "Closed",
            "kind": "text"
          },
          {
            "field": "reconciled_with_xero",
            "label": "Reconciled with Xero",
            "kind": "text"
          },
          {
            "field": "effect_sales",
            "label": "Sales",
            "kind": "text",
            "detail": {
              "field": "effect_sales_detail",
              "label": "Made up of",
              "kind": "text"
            }
          },
          {
            "field": "effect_cost_of_sales",
            "label": "Cost of sales",
            "kind": "text"
          },
          {
            "field": "effect_overheads",
            "label": "Overheads",
            "kind": "text"
          },
          {
            "field": "effect_to_stock",
            "label": "To stock",
            "kind": "text"
          },
          {
            "field": "prepayments_opening_pence",
            "label": "Opening",
            "kind": "money"
          },
          {
            "field": "prepayments_additions_pence",
            "label": "Additions",
            "kind": "money"
          },
          {
            "field": "prepayments_released_pence",
            "label": "Released",
            "kind": "money"
          },
          {
            "field": "prepayments_closing_pence",
            "label": "Closing",
            "kind": "money"
          }
        ],
        "fact_groups": [
          {
            "label": "Month end",
            "fields": [
              "trading_company",
              "month_label",
              "lines_to_decide",
              "sales_data_as_at",
              "closed",
              "reconciled_with_xero"
            ]
          },
          {
            "label": "Effect on the month's P&L",
            "fields": [
              "effect_sales",
              "effect_cost_of_sales",
              "effect_overheads",
              "effect_to_stock"
            ]
          },
          {
            "label": "Prepayments",
            "fields": [
              "prepayments_opening_pence",
              "prepayments_additions_pence",
              "prepayments_released_pence",
              "prepayments_closing_pence"
            ]
          }
        ],
        "timeline_path": "/accruals-and-prepayments/month-ends/{month_end_id}/timeline",
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "finance_accruals_and_prepayments.month_end_schedule",
          "label": "Schedules",
          "field": "schedules",
          "key_field": "schedule_id",
          "columns": [
            {
              "field": "schedule",
              "label": "Schedule",
              "kind": "text"
            },
            {
              "field": "included_total_pence",
              "label": "Included",
              "kind": "money"
            },
            {
              "field": "lines_included",
              "label": "Lines included",
              "kind": "text"
            },
            {
              "field": "to_decide",
              "label": "To decide",
              "kind": "number"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "finance_accruals_and_prepayments.journal",
          "label": "Journals",
          "field": "journals",
          "key_field": "journal_id",
          "columns": [
            {
              "field": "journal",
              "label": "Journal",
              "kind": "text"
            },
            {
              "field": "dated",
              "label": "Dated",
              "kind": "date"
            },
            {
              "field": "control_account",
              "label": "Control account",
              "kind": "text"
            },
            {
              "field": "total_pence",
              "label": "Total",
              "kind": "money",
              "detail": {
                "field": "against_last_month",
                "label": "Against last month",
                "kind": "text"
              }
            },
            {
              "field": "posting",
              "label": "Posting",
              "kind": "state",
              "enum_labels": {
                "failed": "Failed",
                "pending": "Ready",
                "posted": "Posted",
                "posting": "Posting"
              }
            },
            {
              "field": "xero_journal",
              "label": "Xero journal",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "finance_accruals_and_prepayments.control_account",
          "label": "Reconciliation",
          "field": "reconciliation",
          "key_field": "control_id",
          "columns": [
            {
              "field": "control_account",
              "label": "Control account",
              "kind": "text",
              "detail": {
                "field": "quadrant",
                "label": "For",
                "kind": "text"
              }
            },
            {
              "field": "ours_pence",
              "label": "Ours",
              "kind": "money"
            },
            {
              "field": "xero_pence",
              "label": "Xero",
              "kind": "money"
            },
            {
              "field": "difference_pence",
              "label": "Difference",
              "kind": "money"
            },
            {
              "field": "status",
              "label": "Status",
              "kind": "state",
              "enum_labels": {
                "agrees": "Agrees",
                "explained": "Explained",
                "unexplained": "Unexplained"
              }
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "finance_accruals_and_prepayments.reconciling_item",
          "label": "Reconciling items",
          "field": "reconciling_items",
          "key_field": "item_id",
          "columns": [
            {
              "field": "control_account",
              "label": "Control account",
              "kind": "text"
            },
            {
              "field": "amount_pence",
              "label": "Amount",
              "kind": "money"
            },
            {
              "field": "what",
              "label": "What",
              "kind": "text"
            },
            {
              "field": "reference",
              "label": "Reference",
              "kind": "text",
              "ref": "purchase_orders.purchase_order"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [
        {
          "object": "finance_accruals_and_prepayments.prepayment",
          "label": "Prepayments",
          "params": {
            "trading_company_code": "{trading_company_code}"
          },
          "secondary": true
        },
        {
          "object": "finance_accruals_and_prepayments.recurring_accrual",
          "label": "Recurring accruals",
          "params": {
            "trading_company_code": "{trading_company_code}"
          },
          "secondary": true
        }
      ],
      "lifecycle": [
        {
          "id": "finance_accruals_and_prepayments.review",
          "label": "Review",
          "module": "finance_accruals_and_prepayments_module",
          "description": "Every line proposed for the month decided, included or left out, then marked reviewed."
        },
        {
          "id": "finance_accruals_and_prepayments.post",
          "label": "Post",
          "module": "finance_accruals_and_prepayments_module",
          "description": "The month's journals posted to the trading company's Xero organisation."
        },
        {
          "id": "finance_accruals_and_prepayments.reconcile",
          "label": "Reconcile",
          "module": "finance_accruals_and_prepayments_module",
          "description": "Each control account checked against Xero, to the penny."
        },
        {
          "id": "finance_accruals_and_prepayments.close",
          "label": "Close",
          "module": "finance_accruals_and_prepayments_module",
          "description": "The month locked, with its posting, decisions and reconciliation kept as its record."
        }
      ],
      "about": "A month end is one trading company's close for one month: the lines proposed for it, the journals posted to its Xero organisation, and the check of each control account against Xero before it closes. The proposal runs itself on the first morning after the month ends, and can be run sooner. Every change from what the rules proposed carries a reason.",
      "state_meanings": {
        "open": "Not yet proposed: the proposal runs itself on the first morning after the month ends; it can be run sooner.",
        "proposed": "Review in progress: lines are being reviewed. Every change from what the rules proposed carries a reason.",
        "reviewed": "Reviewed, not posted: the review is done; the journals are ready to post to Xero.",
        "posting": "Posting to Xero: the journals are being posted one by one.",
        "posting_failed": "Posting failed: posting stopped at one journal. Retrying posts only the ones not yet in Xero.",
        "posted": "Posted, reconciling: the journals are in Xero. Each control account is checked against Xero before the month closes.",
        "closed": "Closed: the month is locked: its posting, decisions and reconciliation are kept as its record."
      },
      "state_tones": {
        "open": "neutral",
        "proposed": "in_progress",
        "reviewed": "attention",
        "posting": "in_progress",
        "posting_failed": "stopped",
        "posted": "in_progress",
        "closed": "done"
      },
      "commands": [
        "finance_accruals_and_prepayments.run_proposal",
        "finance_accruals_and_prepayments.mark_reviewed",
        "finance_accruals_and_prepayments.refresh_proposal",
        "finance_accruals_and_prepayments.add_manual_line",
        "finance_accruals_and_prepayments.post_to_xero",
        "finance_accruals_and_prepayments.retry_posting",
        "finance_accruals_and_prepayments.reconcile_with_xero",
        "finance_accruals_and_prepayments.close_month",
        "finance_accruals_and_prepayments.abandon_posting",
        "finance_accruals_and_prepayments.reopen_month"
      ],
      "features": [
        "accruals_and_prepayments"
      ]
    },
    {
      "id": "finance_accruals_and_prepayments.month_end_line",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Month end line",
      "plural": "Lines",
      "description": "One line a month end proposed: what it is, why the rules proposed it and the entry it books.",
      "identifier": {
        "kind": "field",
        "field": "line_key",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "needs_decision",
        "changed",
        "included",
        "left_out"
      ],
      "list": {
        "path": "/accruals-and-prepayments/month-end-lines",
        "key_field": "line_key",
        "title_field": "source",
        "columns": [
          {
            "field": "review",
            "label": "Review",
            "kind": "state",
            "enum_labels": {
              "changed": "Changed by reviewer",
              "included": "Included",
              "left_out": "Left out",
              "needs_decision": "Needs a decision"
            },
            "detail": {
              "field": "review_detail",
              "label": "Decided",
              "kind": "text"
            }
          },
          {
            "field": "source",
            "label": "Source",
            "kind": "text",
            "ref": [
              "purchase_orders.purchase_order",
              "orders.order",
              "finance_purchase_ledger.purchase_invoice",
              "stock.stock_item",
              "finance_accruals_and_prepayments.recurring_accrual",
              "finance_accruals_and_prepayments.prepayment"
            ],
            "ref_id_field": "source_id",
            "ref_object_field": "source_object",
            "detail": {
              "field": "origin",
              "label": "Origin",
              "kind": "text"
            }
          },
          {
            "field": "counterparty",
            "label": "Supplier or customer",
            "kind": "text",
            "detail": {
              "field": "counterparty_ref",
              "label": "Number",
              "kind": "text"
            }
          },
          {
            "field": "description",
            "label": "Description",
            "kind": "text",
            "detail": {
              "field": "description_detail",
              "label": "Evidence and account",
              "kind": "text"
            }
          },
          {
            "field": "why",
            "label": "Why",
            "kind": "text",
            "detail": {
              "field": "entry",
              "label": "Books",
              "kind": "text"
            }
          },
          {
            "field": "amount_pence",
            "label": "Amount",
            "kind": "money"
          }
        ],
        "filters": [
          {
            "param": "month_end_id",
            "label": "Month end",
            "kind": "entity",
            "options": [],
            "object": "finance_accruals_and_prepayments.month_end",
            "description": "Only this month end's lines (POL-2026-09). Omit for every month end's."
          },
          {
            "param": "schedule_id",
            "label": "Schedule ID",
            "kind": "text",
            "options": [],
            "description": "Only this schedule's lines (POL-2026-09.accrued_expense); it names its month end too."
          },
          {
            "param": "review",
            "label": "Review",
            "kind": "multi_enum",
            "options": [
              [
                "needs_decision",
                "Needs a decision"
              ],
              [
                "changed",
                "Changed by reviewer"
              ],
              [
                "included",
                "Included"
              ],
              [
                "left_out",
                "Left out"
              ]
            ],
            "description": "Only lines in any of these review states (repeat the param)."
          },
          {
            "param": "counterparty",
            "label": "Supplier or customer",
            "kind": "multi_enum",
            "options": [],
            "description": "Only these suppliers' or customers' names (repeat the param)."
          },
          {
            "param": "account",
            "label": "Account",
            "kind": "multi_enum",
            "options": [],
            "description": "Only lines on these Xero account codes (repeat the param)."
          },
          {
            "param": "origin",
            "label": "Origin",
            "kind": "multi_enum",
            "options": [
              [
                "goods_received",
                "Goods received, not yet billed"
              ],
              [
                "usage_not_billed",
                "Usage not yet billed"
              ],
              [
                "late_purchase_invoice",
                "Late purchase invoice"
              ],
              [
                "unconfirmed_purchase_invoice",
                "Unconfirmed purchase invoice"
              ],
              [
                "recurring",
                "Recurring accrual"
              ],
              [
                "prepaid_release",
                "Prepaid release"
              ],
              [
                "manual",
                "Manual line"
              ],
              [
                "accrued_income",
                "Earned, not yet invoiced"
              ],
              [
                "deferred_income",
                "Invoiced, not yet earned"
              ],
              [
                "stock_released",
                "Stock called off"
              ],
              [
                "stock_written_off",
                "Stock written off"
              ],
              [
                "recharge_purchase_order_line",
                "Purchase order line"
              ],
              [
                "recharge_bill_other_company",
                "Bill posted to the other company"
              ],
              [
                "recharge_usage",
                "Supplier usage"
              ],
              [
                "recharge_stock",
                "Stock picked"
              ]
            ],
            "description": "Only lines from these sources (repeat the param)."
          }
        ],
        "sorts": [
          [
            "source",
            "Source"
          ],
          [
            "counterparty",
            "Supplier or customer"
          ],
          [
            "evidence_date",
            "Evidence date"
          ],
          [
            "amount_pence",
            "Amount"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "needs_decision",
            "label": "Needs a decision",
            "params": {
              "review": [
                "needs_decision"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "included",
            "label": "Included",
            "params": {
              "review": [
                "included"
              ]
            },
            "count": false,
            "default": false
          },
          {
            "id": "left_out",
            "label": "Left out",
            "params": {
              "review": [
                "left_out"
              ]
            },
            "count": false,
            "default": false
          },
          {
            "id": "changed",
            "label": "Changed by reviewer",
            "params": {
              "review": [
                "changed"
              ]
            },
            "count": false,
            "default": false
          }
        ]
      },
      "record": null,
      "parents": [
        "finance_accruals_and_prepayments.month_end_schedule"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A month end line is one candidate entry: a purchase order line received and not billed, a late or unconfirmed purchase invoice, usage not yet billed, a recurring accrual, a prepaid release, income earned or invoiced out of step, stock released, a cost recharged to another trading company (a purchase order line, a bill posted to the other company, supplier usage or house stock, each moved across at cost), or a line added by hand. Every change from what the rules proposed carries a reason. A recharge received from another company is read-only: the month end that paid it decides it.",
      "state_meanings": {
        "needs_decision": "Needs a decision: the rules could not decide: include it, or keep it out with a reason.",
        "changed": "Changed by reviewer: a person changed what the rules proposed, with a reason.",
        "included": "Included: it will be posted with the month's journals.",
        "left_out": "Left out: it will not be posted this month."
      },
      "state_tones": {
        "needs_decision": "attention",
        "changed": "done",
        "included": "done",
        "left_out": "neutral"
      },
      "commands": [
        "finance_accruals_and_prepayments.keep_out_month_end_line",
        "finance_accruals_and_prepayments.include_month_end_line",
        "finance_accruals_and_prepayments.leave_out_month_end_line",
        "finance_accruals_and_prepayments.edit_month_end_line_amount",
        "finance_accruals_and_prepayments.edit_manual_month_end_line",
        "finance_accruals_and_prepayments.remove_manual_month_end_line"
      ],
      "features": [
        "accruals_and_prepayments"
      ]
    },
    {
      "id": "finance_accruals_and_prepayments.month_end_schedule",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Schedule",
      "plural": "Schedules",
      "description": "One of a month end's schedules: the lines behind one kind of entry, reviewed and posted with the month.",
      "identifier": {
        "kind": "field",
        "field": "schedule_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": {
        "path": "/accruals-and-prepayments/month-end-schedules/{schedule_id}",
        "path_param": "schedule_id",
        "title_field": "heading",
        "state_fields": [],
        "state_labels": {},
        "facts": [
          {
            "field": "month_end",
            "label": "Month end",
            "kind": "text",
            "ref": "finance_accruals_and_prepayments.month_end",
            "ref_id_field": "month_end_id"
          },
          {
            "field": "question",
            "label": "Each line answers",
            "kind": "text"
          },
          {
            "field": "included_total_pence",
            "label": "Included",
            "kind": "money"
          },
          {
            "field": "lines_included",
            "label": "Lines included",
            "kind": "text"
          },
          {
            "field": "to_decide",
            "label": "To decide",
            "kind": "number"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [
        "finance_accruals_and_prepayments.month_end"
      ],
      "children": [
        {
          "object": "finance_accruals_and_prepayments.month_end_line",
          "label": "Lines",
          "field": "lines",
          "key_field": "line_key",
          "columns": [
            {
              "field": "review",
              "label": "Review",
              "kind": "state",
              "enum_labels": {
                "changed": "Changed by reviewer",
                "included": "Included",
                "left_out": "Left out",
                "needs_decision": "Needs a decision"
              },
              "detail": {
                "field": "review_detail",
                "label": "Decided",
                "kind": "text"
              }
            },
            {
              "field": "source",
              "label": "Source",
              "kind": "text",
              "ref": [
                "purchase_orders.purchase_order",
                "orders.order",
                "finance_purchase_ledger.purchase_invoice",
                "stock.stock_item",
                "finance_accruals_and_prepayments.recurring_accrual",
                "finance_accruals_and_prepayments.prepayment"
              ],
              "ref_id_field": "source_id",
              "ref_object_field": "source_object",
              "detail": {
                "field": "origin",
                "label": "Origin",
                "kind": "text"
              }
            },
            {
              "field": "counterparty",
              "label": "Supplier or customer",
              "kind": "text",
              "detail": {
                "field": "counterparty_ref",
                "label": "Number",
                "kind": "text"
              }
            },
            {
              "field": "description",
              "label": "Description",
              "kind": "text",
              "detail": {
                "field": "description_detail",
                "label": "Evidence and account",
                "kind": "text"
              }
            },
            {
              "field": "why",
              "label": "Why",
              "kind": "text",
              "detail": {
                "field": "entry",
                "label": "Books",
                "kind": "text"
              }
            },
            {
              "field": "amount_pence",
              "label": "Amount",
              "kind": "money"
            }
          ],
          "paged": false,
          "parent_filter": null,
          "totals": {
            "amount_pence": {
              "field": "included_total_pence",
              "label": "Included",
              "kind": "money"
            }
          }
        }
      ],
      "related": [
        {
          "object": "finance_accruals_and_prepayments.recurring_accrual",
          "label": "Recurring accruals",
          "params": {
            "trading_company_code": "{recurring_trading_company_code}"
          },
          "secondary": true
        },
        {
          "object": "finance_accruals_and_prepayments.prepayment",
          "label": "Prepayments",
          "params": {
            "trading_company_code": "{prepaid_trading_company_code}"
          },
          "secondary": true
        }
      ],
      "lifecycle": [],
      "about": "A schedule is one kind of entry on a month end: accrued expense, recurring, accrued income, deferred income, prepaid releases, stock released, or the recharges between trading companies. It holds the lines behind that entry, each included or left out, and what its included lines come to. The word is the one accountants use. Recharged out holds the costs this company paid for another's orders, decided here; Recharged in shows the same lines on the company they were for, read-only, because the company that paid decides them.",
      "commands": [],
      "features": [
        "accruals_and_prepayments"
      ]
    },
    {
      "id": "finance_accruals_and_prepayments.prepaid_release",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Release",
      "plural": "Releases",
      "description": "One month of a prepayment's release.",
      "identifier": {
        "kind": "field",
        "field": "release_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "released",
        "this_month",
        "planned"
      ],
      "list": null,
      "record": null,
      "parents": [
        "finance_accruals_and_prepayments.prepayment"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A release is one month of a prepayment's release. Months before its purchase invoice arrived catch up in the month its purchase invoice is released, because a closed month can't be posted to.",
      "state_meanings": {
        "released": "Released: it has been posted.",
        "this_month": "This month: it is proposed at the month end now being closed.",
        "planned": "Planned: it will be proposed at its own month end."
      },
      "state_tones": {
        "released": "done",
        "this_month": "in_progress",
        "planned": "neutral"
      },
      "commands": [],
      "features": []
    },
    {
      "id": "finance_accruals_and_prepayments.prepayment",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Prepayment",
      "plural": "Prepayments",
      "description": "A purchase invoice line spread over the period it covers, released day by day across it.",
      "identifier": {
        "kind": "field",
        "field": "prepayment_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "releasing",
        "final_release",
        "added",
        "ended_early",
        "fully_released"
      ],
      "list": {
        "path": "/accruals-and-prepayments/prepayments",
        "key_field": "prepayment_id",
        "title_field": "heading",
        "columns": [
          {
            "field": "trading_company",
            "label": "Trading company",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "trading_company_code"
          },
          {
            "field": "supplier",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier",
            "ref_id_field": "supplier_no"
          },
          {
            "field": "description",
            "label": "Description",
            "kind": "text"
          },
          {
            "field": "purchase_invoice",
            "label": "Purchase invoice",
            "kind": "text",
            "ref": "finance_purchase_ledger.purchase_invoice",
            "ref_id_field": "purchase_invoice_id",
            "detail": {
              "field": "purchase_invoice_detail",
              "label": "Net",
              "kind": "text"
            }
          },
          {
            "field": "account",
            "label": "Account",
            "kind": "text"
          },
          {
            "field": "period_covered",
            "label": "Period covered",
            "kind": "text",
            "detail": {
              "field": "period_detail",
              "label": "Period",
              "kind": "text"
            }
          },
          {
            "field": "month",
            "label": "Month",
            "kind": "text"
          },
          {
            "field": "opening_pence",
            "label": "Opening",
            "kind": "money"
          },
          {
            "field": "additions_pence",
            "label": "Additions",
            "kind": "money"
          },
          {
            "field": "released_pence",
            "label": "Released",
            "kind": "money"
          },
          {
            "field": "closing_pence",
            "label": "Closing",
            "kind": "money"
          },
          {
            "field": "months_left",
            "label": "Months left",
            "kind": "number"
          },
          {
            "field": "status",
            "label": "Status",
            "kind": "state",
            "enum_labels": {
              "added": "Added this month",
              "ended_early": "Ended early",
              "final_release": "Final release this month",
              "fully_released": "Fully released",
              "releasing": "Releasing"
            }
          }
        ],
        "filters": [
          {
            "param": "trading_company_code",
            "label": "Trading company",
            "kind": "entity",
            "options": [],
            "object": "trading_companies.trading_company",
            "entity_value": "code",
            "description": "Only this trading company's, by its code (POL, BCQ). Omit for every company."
          },
          {
            "param": "month",
            "label": "Month",
            "kind": "text",
            "options": [],
            "description": "The month the roll-forward is for, YYYY-MM. Omit for the closing month (last month)."
          },
          {
            "param": "supplier_no",
            "label": "Supplier",
            "kind": "entity",
            "options": [],
            "object": "suppliers.supplier",
            "entity_value": "supplier_no",
            "description": "Only this supplier's prepayments, by supplier number (SUP-...)."
          },
          {
            "param": "account",
            "label": "Account",
            "kind": "multi_enum",
            "options": [],
            "description": "Only prepayments on these Xero account codes (repeat the param)."
          },
          {
            "param": "status",
            "label": "Status",
            "kind": "multi_enum",
            "options": [
              [
                "releasing",
                "Releasing"
              ],
              [
                "final_release",
                "Final release this month"
              ],
              [
                "added",
                "Added this month"
              ],
              [
                "ended_early",
                "Ended early"
              ],
              [
                "fully_released",
                "Fully released"
              ]
            ],
            "description": "Only prepayments with any of these statuses in the month (repeat the param)."
          }
        ],
        "sorts": [
          [
            "supplier",
            "Supplier"
          ],
          [
            "period_to",
            "Period covered to"
          ],
          [
            "released_pence",
            "Released"
          ],
          [
            "closing_pence",
            "Closing"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "on_balance_sheet",
            "label": "On the balance sheet",
            "params": {
              "status": [
                "releasing",
                "final_release",
                "added"
              ]
            },
            "count": false,
            "default": true
          },
          {
            "id": "final",
            "label": "Final release this month",
            "params": {
              "status": [
                "final_release"
              ]
            },
            "count": false,
            "default": false
          },
          {
            "id": "added",
            "label": "Added this month",
            "params": {
              "status": [
                "added"
              ]
            },
            "count": false,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/accruals-and-prepayments/prepayments/{prepayment_id}",
        "path_param": "prepayment_id",
        "title_field": "heading",
        "state_fields": [
          "status"
        ],
        "state_labels": {
          "status": {
            "added": "Added this month",
            "ended_early": "Ended early",
            "final_release": "Final release this month",
            "fully_released": "Fully released",
            "releasing": "Releasing"
          }
        },
        "facts": [
          {
            "field": "trading_company",
            "label": "Trading company",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "trading_company_code"
          },
          {
            "field": "supplier",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier",
            "ref_id_field": "supplier_no"
          },
          {
            "field": "purchase_invoice",
            "label": "Purchase invoice",
            "kind": "text",
            "ref": "finance_purchase_ledger.purchase_invoice",
            "ref_id_field": "purchase_invoice_id",
            "detail": {
              "field": "purchase_invoice_detail",
              "label": "Net",
              "kind": "text"
            }
          },
          {
            "field": "account",
            "label": "Account",
            "kind": "text"
          },
          {
            "field": "period_covered",
            "label": "Period covered",
            "kind": "text",
            "detail": {
              "field": "period_detail",
              "label": "Period",
              "kind": "text"
            }
          },
          {
            "field": "month",
            "label": "Month",
            "kind": "text"
          },
          {
            "field": "opening_pence",
            "label": "Opening",
            "kind": "money"
          },
          {
            "field": "released_pence",
            "label": "Released",
            "kind": "money"
          },
          {
            "field": "closing_pence",
            "label": "Closing",
            "kind": "money"
          },
          {
            "field": "months_left",
            "label": "Months left",
            "kind": "number"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "finance_accruals_and_prepayments.prepaid_release",
          "label": "Releases",
          "field": "releases",
          "key_field": "release_id",
          "columns": [
            {
              "field": "month",
              "label": "Month",
              "kind": "text"
            },
            {
              "field": "days",
              "label": "Days",
              "kind": "number"
            },
            {
              "field": "amount_pence",
              "label": "Amount",
              "kind": "money"
            },
            {
              "field": "state",
              "label": "State",
              "kind": "state",
              "enum_labels": {
                "planned": "Planned",
                "released": "Released",
                "this_month": "This month"
              }
            },
            {
              "field": "posted_in",
              "label": "Posted in",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [],
      "lifecycle": [],
      "about": "A prepayment is a purchase invoice line spread over the period it covers, released day by day across it. It joins the schedule when its purchase invoice is released; each month end proposes that month's release. The figures are for the closing month.",
      "state_meanings": {
        "releasing": "Releasing: it releases each month, weighted by days, over the period it covers.",
        "final_release": "Final release this month: what is left releases at this month end.",
        "added": "Added this month: its purchase invoice was confirmed as prepaid this month.",
        "ended_early": "Ended early: whatever was left released in full in the month chosen.",
        "fully_released": "Fully released: nothing is left on the prepayments account."
      },
      "state_tones": {
        "releasing": "in_progress",
        "final_release": "in_progress",
        "added": "in_progress",
        "ended_early": "neutral",
        "fully_released": "done"
      },
      "commands": [
        "finance_accruals_and_prepayments.end_prepayment_early"
      ],
      "features": [
        "accruals_and_prepayments"
      ]
    },
    {
      "id": "finance_accruals_and_prepayments.reconciling_item",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Reconciling item",
      "plural": "Reconciling items",
      "description": "Something that explains part of a control account's difference from Xero.",
      "identifier": {
        "kind": "field",
        "field": "item_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "finance_accruals_and_prepayments.month_end"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": []
    },
    {
      "id": "finance_accruals_and_prepayments.recurring_accrual",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Recurring accrual",
      "plural": "Recurring accruals",
      "description": "A regular cost accrued before its purchase invoice arrives: months elapsed over twelve of the annual estimate, each month end.",
      "identifier": {
        "kind": "field",
        "field": "recurring_accrual_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "not_started",
        "active",
        "stopped"
      ],
      "list": {
        "path": "/accruals-and-prepayments/recurring-accruals",
        "key_field": "recurring_accrual_id",
        "title_field": "name",
        "columns": [
          {
            "field": "name",
            "label": "Recurring accrual",
            "kind": "text"
          },
          {
            "field": "trading_company",
            "label": "Trading company",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "trading_company_code"
          },
          {
            "field": "account",
            "label": "Account",
            "kind": "text"
          },
          {
            "field": "first_month",
            "label": "From",
            "kind": "text"
          },
          {
            "field": "stops_on",
            "label": "Stops on",
            "kind": "text"
          },
          {
            "field": "annual_estimate_pence",
            "label": "Annual estimate",
            "kind": "money",
            "detail": {
              "field": "estimate_detail",
              "label": "Changed",
              "kind": "text"
            }
          },
          {
            "field": "closing_month_pence",
            "label": "Closing month",
            "kind": "money",
            "detail": {
              "field": "closing_month_detail",
              "label": "Months",
              "kind": "text"
            }
          },
          {
            "field": "stopped",
            "label": "Stopped",
            "kind": "text"
          },
          {
            "field": "status",
            "label": "Status",
            "kind": "state",
            "enum_labels": {
              "active": "Active",
              "not_started": "Not started",
              "stopped": "Stopped"
            }
          }
        ],
        "filters": [
          {
            "param": "trading_company_code",
            "label": "Trading company",
            "kind": "entity",
            "options": [],
            "object": "trading_companies.trading_company",
            "entity_value": "code",
            "description": "Only this trading company's, by its code (POL, BCQ). Omit for every company."
          },
          {
            "param": "status",
            "label": "Status",
            "kind": "multi_enum",
            "options": [
              [
                "not_started",
                "Not started"
              ],
              [
                "active",
                "Active"
              ],
              [
                "stopped",
                "Stopped"
              ]
            ],
            "description": "Only recurring accruals in any of these states in the closing month (repeat the param)."
          },
          {
            "param": "supplier_no",
            "label": "Stops on a purchase invoice from",
            "kind": "entity",
            "options": [],
            "object": "suppliers.supplier",
            "entity_value": "supplier_no",
            "description": "Only recurring accruals that stop when a purchase invoice arrives from this supplier."
          }
        ],
        "sorts": [
          [
            "name",
            "Recurring accrual"
          ],
          [
            "start_month",
            "First month (YYYY-MM)"
          ],
          [
            "annual_estimate_pence",
            "Annual estimate"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "active",
            "label": "Active",
            "params": {
              "status": [
                "active"
              ]
            },
            "count": false,
            "default": false
          },
          {
            "id": "stopped",
            "label": "Stopped",
            "params": {
              "status": [
                "stopped"
              ]
            },
            "count": false,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/accruals-and-prepayments/recurring-accruals/{recurring_accrual_id}",
        "path_param": "recurring_accrual_id",
        "title_field": "name",
        "state_fields": [
          "status"
        ],
        "state_labels": {
          "status": {
            "active": "Active",
            "not_started": "Not started",
            "stopped": "Stopped"
          }
        },
        "facts": [
          {
            "field": "trading_company",
            "label": "Trading company",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "trading_company_code"
          },
          {
            "field": "account",
            "label": "Account",
            "kind": "text"
          },
          {
            "field": "annual_estimate_pence",
            "label": "Annual estimate",
            "kind": "money",
            "detail": {
              "field": "estimate_detail",
              "label": "Changed",
              "kind": "text"
            }
          },
          {
            "field": "first_month",
            "label": "From",
            "kind": "text"
          },
          {
            "field": "stops_on",
            "label": "Stops on",
            "kind": "text"
          },
          {
            "field": "closing_month_pence",
            "label": "Closing month",
            "kind": "money",
            "detail": {
              "field": "closing_month_detail",
              "label": "Months",
              "kind": "text"
            }
          },
          {
            "field": "stopped",
            "label": "Stopped",
            "kind": "text"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "finance_accruals_and_prepayments.estimate_change",
          "label": "Estimate changes",
          "field": "estimate_changes",
          "key_field": "change_id",
          "columns": [
            {
              "field": "from_pence",
              "label": "From",
              "kind": "money"
            },
            {
              "field": "to_pence",
              "label": "To",
              "kind": "money"
            },
            {
              "field": "reason",
              "label": "Why",
              "kind": "text"
            },
            {
              "field": "by",
              "label": "By",
              "kind": "text"
            },
            {
              "field": "at",
              "label": "When",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [],
      "lifecycle": [],
      "about": "A recurring accrual is a regular cost accrued before its purchase invoice arrives. Each month end accrues months elapsed ÷ 12 of the annual estimate and reverses it on the first of the next month, so the accrual grows a month at a time. One linked to a supplier stops itself when that supplier's purchase invoice is confirmed.",
      "state_meanings": {
        "not_started": "Not started: it starts accruing at the month end of its first month.",
        "active": "Active: each month end accrues months elapsed ÷ 12 of the annual estimate, reversing on the first of the next month.",
        "stopped": "Stopped: it accrues no more. A supplier-linked one stops itself when that supplier's purchase invoice is confirmed."
      },
      "state_tones": {
        "not_started": "neutral",
        "active": "in_progress",
        "stopped": "neutral"
      },
      "commands": [
        "finance_accruals_and_prepayments.change_recurring_estimate",
        "finance_accruals_and_prepayments.stop_recurring_accrual",
        "finance_accruals_and_prepayments.create_recurring_accrual"
      ],
      "features": [
        "accruals_and_prepayments"
      ]
    },
    {
      "id": "finance_credit_control.billing_account",
      "module": "finance_credit_control_module",
      "label": "Billing account",
      "plural": "Billing accounts",
      "description": "The payer of one or more customers' orders, with how it pays and whether it is on stop.",
      "identifier": {
        "kind": "field",
        "field": "ref",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": {
        "path": "/credit-control/billing-accounts",
        "key_field": "ref",
        "title_field": "name",
        "columns": [
          {
            "field": "ref",
            "label": "Billing account",
            "kind": "identifier"
          },
          {
            "field": "name",
            "label": "Name",
            "kind": "text"
          },
          {
            "field": "methods_label",
            "label": "Pays by",
            "kind": "text"
          },
          {
            "field": "terms_label",
            "label": "Terms",
            "kind": "text"
          },
          {
            "field": "stop_reason",
            "label": "On stop",
            "kind": "state",
            "enum_labels": {
              "final_letter": "Final letter sent",
              "insolvency": "Insolvency",
              "insufficient_information": "Insufficient information",
              "limit_exceeded": "Limit exceeded",
              "other": "Other",
              "overdue": "Overdue",
              "poor_history": "Poor payment history"
            }
          },
          {
            "field": "pending_hold_count",
            "label": "Orders waiting",
            "kind": "number"
          }
        ],
        "filters": [
          {
            "param": "ledger",
            "label": "Ledger",
            "kind": "enum",
            "options": [
              [
                "POL",
                "POL"
              ],
              [
                "BCQ",
                "BCQ"
              ]
            ],
            "description": "The ledger of the account's Sage cross-reference."
          },
          {
            "param": "payment_method",
            "label": "Pays by",
            "kind": "enum",
            "options": [
              [
                "card",
                "Card"
              ],
              [
                "proforma",
                "Bank transfer (pro-forma)"
              ],
              [
                "credit_account",
                "Credit account"
              ]
            ],
            "description": "Accounts on which this method is enabled."
          },
          {
            "param": "on_stop",
            "label": "On stop",
            "kind": "boolean",
            "options": []
          },
          {
            "param": "has_pending_holds",
            "label": "Orders waiting",
            "kind": "boolean",
            "options": [],
            "description": "Accounts with orders waiting for a decision."
          }
        ],
        "sorts": [
          [
            "name",
            "Name"
          ],
          [
            "ref",
            "Billing account"
          ],
          [
            "updated_at",
            "Updated At"
          ],
          [
            "pending_hold_count",
            "Orders waiting"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "on_stop",
            "label": "On stop",
            "params": {
              "on_stop": true
            },
            "count": true,
            "default": false
          },
          {
            "id": "orders_waiting",
            "label": "Orders waiting",
            "params": {
              "has_pending_holds": true
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/views/billing-account/{ref}",
        "path_param": "ref",
        "title_field": "name",
        "state_fields": [
          "stop_reason"
        ],
        "state_labels": {
          "stop_reason": {
            "final_letter": "Final letter sent",
            "insolvency": "Insolvency",
            "insufficient_information": "Insufficient information",
            "limit_exceeded": "Limit exceeded",
            "other": "Other",
            "overdue": "Overdue",
            "poor_history": "Poor payment history"
          }
        },
        "facts": [
          {
            "field": "customer_code",
            "label": "Customer",
            "kind": "identifier",
            "ref": "customers.customer"
          },
          {
            "field": "summary_label",
            "label": "Pays by",
            "kind": "text"
          },
          {
            "field": "credit_limit_pence",
            "label": "Credit limit",
            "kind": "money"
          },
          {
            "field": "receivables_pence",
            "label": "Invoiced, unpaid",
            "kind": "money"
          },
          {
            "field": "committed_pence",
            "label": "Ordered, not invoiced yet",
            "kind": "money"
          },
          {
            "field": "headroom_pence",
            "label": "Left to order",
            "kind": "money"
          },
          {
            "field": "exposure_note",
            "label": "Exposure",
            "kind": "text"
          },
          {
            "field": "pending_hold_count",
            "label": "Orders waiting",
            "kind": "number"
          },
          {
            "field": "credit_limit_covers",
            "label": "Credit limit covers",
            "kind": "text"
          },
          {
            "field": "xero_contacts",
            "label": "Xero contacts",
            "kind": "text"
          },
          {
            "field": "made_to_order_invoice_trigger",
            "label": "Made-to-order work is invoiced",
            "kind": "text"
          },
          {
            "field": "postage_invoicing",
            "label": "Postage",
            "kind": "text"
          },
          {
            "field": "stock_billing",
            "label": "Stock billing",
            "kind": "text"
          },
          {
            "field": "posts_on_own_carrier_account",
            "label": "Posts on its own carrier account (no postage line)",
            "kind": "text"
          },
          {
            "field": "invoice_grouping",
            "label": "Invoice grouping",
            "kind": "text"
          },
          {
            "field": "invoice_cut_off",
            "label": "Invoice cut-off",
            "kind": "text"
          }
        ],
        "fact_groups": [
          {
            "label": null,
            "fields": [
              "credit_limit_pence",
              "receivables_pence",
              "committed_pence",
              "headroom_pence"
            ]
          }
        ],
        "timeline_path": "/views/billing-account/{ref}/timeline",
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "finance_credit_control.hold",
          "label": "Credit holds",
          "field": "holds",
          "key_field": "id",
          "columns": [
            {
              "field": "subject_title",
              "label": "Order",
              "kind": "text"
            },
            {
              "field": "subject_label",
              "label": "What",
              "kind": "text"
            },
            {
              "field": "amount_pence",
              "label": "Ex VAT",
              "kind": "money"
            },
            {
              "field": "reason_label",
              "label": "Held because",
              "kind": "text"
            },
            {
              "field": "status",
              "label": "State",
              "kind": "state",
              "enum_labels": {
                "held": "Waiting for a decision",
                "refused": "Refused",
                "release_to_proof_only": "Proof only",
                "released": "Released",
                "released_without_credit": "Released without credit"
              }
            },
            {
              "field": "created_at",
              "label": "Waiting since",
              "kind": "datetime"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [
        {
          "object": "finance_sales_ledger.invoice",
          "label": "Invoices",
          "params": {
            "billing_account_ref": "{ref}"
          }
        }
      ],
      "lifecycle": [],
      "about": "A billing account is the payer for one or more customers' orders. The billing account holds the payment methods, the terms, the PO rule and the credit limit. A customer without a billing account gets a card- only billing account on the customer's first order in One. Credit control can put the billing account on stop with \"Put on stop\". A stop will hold each new credit-account order until someone decides on the order.",
      "commands": [
        "finance_credit_control.resolve_credit_holds",
        "finance_credit_control.create_billing_account",
        "finance_sales_ledger.create_period_charge",
        "finance_credit_control.update_payment_settings",
        "finance_sales_ledger.update_invoicing_settings",
        "finance_credit_control.import_billing_accounts",
        "finance_credit_control.lift_stop",
        "finance_credit_control.stop_account"
      ],
      "features": []
    },
    {
      "id": "finance_credit_control.dispute",
      "module": "finance_credit_control_module",
      "label": "Dispute",
      "plural": "Disputes",
      "description": "A customer's challenge to an invoice, recorded by credit control; open until finance resolves it.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "open",
        "resolved"
      ],
      "list": null,
      "record": null,
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "open": "attention",
        "resolved": "done"
      },
      "commands": [],
      "features": []
    },
    {
      "id": "finance_credit_control.hold",
      "module": "finance_credit_control_module",
      "label": "Credit hold",
      "plural": "Credit holds",
      "description": "Credit control's decision record for one order (or extra) waiting on credit.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "held",
        "release_to_proof_only",
        "released",
        "released_without_credit",
        "refused"
      ],
      "list": null,
      "record": null,
      "parents": [
        "finance_credit_control.billing_account"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "held": "attention",
        "release_to_proof_only": "in_progress",
        "released": "done",
        "released_without_credit": "done",
        "refused": "stopped"
      },
      "commands": [],
      "features": []
    },
    {
      "id": "finance_purchase_ledger.auto_confirmation_rule",
      "module": "finance_purchase_ledger_module",
      "label": "Auto-confirmation rule",
      "plural": "Auto-confirmation rules",
      "description": "Lets one supplier's purchase invoices release themselves when they pass every check and match their orders.",
      "identifier": {
        "kind": "uuid",
        "field": "rule_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "active",
        "revoked"
      ],
      "list": {
        "path": "/views/auto-confirmation-rule-list",
        "key_field": "rule_id",
        "title_field": "name",
        "columns": [
          {
            "field": "rule_id",
            "label": "Rule",
            "kind": "identifier"
          },
          {
            "field": "name",
            "label": "Name",
            "kind": "text"
          },
          {
            "field": "status",
            "label": "Status",
            "kind": "state",
            "enum_labels": {
              "active": "Active",
              "revoked": "Revoked"
            }
          },
          {
            "field": "supplier_name",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier",
            "ref_id_field": "supplier_no"
          },
          {
            "field": "max_shortfall_pence",
            "label": "Allowed shortfall",
            "kind": "money"
          },
          {
            "field": "released_count",
            "label": "Released",
            "kind": "number"
          },
          {
            "field": "released_gross_pence",
            "label": "Released (gross)",
            "kind": "money"
          },
          {
            "field": "last_released_at",
            "label": "Last released",
            "kind": "datetime"
          },
          {
            "field": "last_changed_at",
            "label": "Last changed",
            "kind": "datetime"
          }
        ],
        "filters": [
          {
            "param": "status",
            "label": "Status",
            "kind": "multi_enum",
            "options": [
              [
                "active",
                "Active"
              ],
              [
                "revoked",
                "Revoked"
              ]
            ],
            "description": "Only rules in these states; none: every rule."
          },
          {
            "param": "supplier_id",
            "label": "Supplier ID",
            "kind": "text",
            "options": [],
            "description": "Only this supplier's rules (its id)."
          }
        ],
        "sorts": [],
        "searchable": true,
        "queues": [
          {
            "id": "active",
            "label": "Active",
            "params": {
              "status": [
                "active"
              ]
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/views/auto-confirmation-rule-detail/{rule_id}",
        "path_param": "rule_id",
        "title_field": "name",
        "state_fields": [
          "status"
        ],
        "state_labels": {
          "status": {
            "active": "Active",
            "revoked": "Revoked"
          }
        },
        "facts": [
          {
            "field": "supplier_name",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier",
            "ref_id_field": "supplier_no"
          },
          {
            "field": "max_shortfall_pence",
            "label": "Allowed shortfall",
            "kind": "money"
          },
          {
            "field": "released_count",
            "label": "Released",
            "kind": "number"
          },
          {
            "field": "last_released_at",
            "label": "Last released",
            "kind": "datetime"
          }
        ],
        "timeline_path": "/views/auto-confirmation-rule-detail/{rule_id}/timeline",
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [
        {
          "object": "finance_purchase_ledger.purchase_invoice",
          "label": "Purchase invoices",
          "params": {
            "auto_confirmation_rule_id": "{rule_id}"
          },
          "secondary": true
        }
      ],
      "lifecycle": [],
      "about": "An auto-confirmation rule lets one supplier's purchase invoices release themselves. The rule releases a purchase invoice only when every check passes and the purchase invoice matches the purchase orders, or falls short by no more than the allowed shortfall. Credit notes and purchase invoices without a purchase order always need a person. Each supplier has at most one active rule.",
      "state_meanings": {
        "active": "Active: the rule will release the supplier's clean purchase invoices.",
        "revoked": "Revoked: the supplier's purchase invoices will need a person again."
      },
      "state_tones": {
        "active": "done",
        "revoked": "neutral"
      },
      "commands": [
        "finance_purchase_ledger.write_auto_confirmation_rule",
        "finance_purchase_ledger.change_auto_confirmation_rule",
        "finance_purchase_ledger.revoke_auto_confirmation_rule"
      ],
      "features": [
        "purchase_invoice_matching"
      ]
    },
    {
      "id": "finance_purchase_ledger.match_group",
      "module": "finance_purchase_ledger_module",
      "label": "Match group",
      "plural": "Match groups",
      "description": "The purchase invoices and purchase orders of one supplier that settle each other, released together.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "open",
        "confirmed",
        "closed"
      ],
      "list": null,
      "record": null,
      "parents": [
        "finance_purchase_ledger.purchase_invoice"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "open": "in_progress",
        "confirmed": "done",
        "closed": "neutral"
      },
      "commands": [],
      "features": []
    },
    {
      "id": "finance_purchase_ledger.purchase_invoice",
      "module": "finance_purchase_ledger_module",
      "label": "Purchase invoice",
      "plural": "Purchase invoices",
      "description": "A supplier's invoice or credit note, from receipt to its release for payment and its posting to Xero.",
      "identifier": {
        "kind": "uuid",
        "field": "purchase_invoice_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "needs_a_look",
        "ready_to_release",
        "in_query",
        "released",
        "posted",
        "paid",
        "dismissed"
      ],
      "list": {
        "path": "/views/purchase-invoices",
        "key_field": "purchase_invoice_id",
        "title_field": "supplier_document_no",
        "columns": [
          {
            "field": "purchase_invoice_id",
            "label": "Purchase invoice",
            "kind": "identifier"
          },
          {
            "field": "stage_label",
            "label": "Stage",
            "kind": "text"
          },
          {
            "field": "supplier_name",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier",
            "ref_id_field": "supplier.supplier_no"
          },
          {
            "field": "supplier_document_no",
            "label": "Supplier's number",
            "kind": "text"
          },
          {
            "field": "invoice_date",
            "label": "Invoice date",
            "kind": "date"
          },
          {
            "field": "received_at",
            "label": "Arrived",
            "kind": "datetime"
          },
          {
            "field": "net_pence",
            "label": "Net",
            "kind": "money"
          }
        ],
        "filters": [
          {
            "param": "stage",
            "label": "Stage",
            "kind": "multi_enum",
            "options": [
              [
                "needs_a_look",
                "Needs a look"
              ],
              [
                "ready_to_release",
                "Ready to release"
              ],
              [
                "in_query",
                "In query"
              ],
              [
                "released",
                "Released"
              ],
              [
                "posted",
                "Posted to Xero"
              ],
              [
                "paid",
                "Paid"
              ],
              [
                "dismissed",
                "Dismissed"
              ]
            ],
            "description": "Only purchase invoices at these stages."
          },
          {
            "param": "supplier_no",
            "label": "Supplier number",
            "kind": "text",
            "options": [],
            "description": "Only this supplier's purchase invoices (SUP-H40234)."
          },
          {
            "param": "document_type",
            "label": "Type",
            "kind": "enum",
            "options": [
              [
                "purchase_invoice",
                "Purchase invoice"
              ],
              [
                "credit_note",
                "Credit note"
              ]
            ]
          },
          {
            "param": "path",
            "label": "Purchase order",
            "kind": "enum",
            "options": [
              [
                "po",
                "Against purchase orders"
              ],
              [
                "non_po",
                "No purchase order"
              ],
              [
                "usage",
                "Usage bill"
              ]
            ],
            "description": "Only purchase invoices matched to purchase orders (or waiting to be), spend with no purchase order, or usage bills."
          },
          {
            "param": "invoice_date_from",
            "label": "Invoice date from",
            "kind": "date",
            "options": [],
            "description": "Invoice date on or after."
          },
          {
            "param": "invoice_date_to",
            "label": "Invoice date to",
            "kind": "date",
            "options": [],
            "description": "Invoice date on or before."
          },
          {
            "param": "auto_confirmation_rule_id",
            "label": "Released by rule ID",
            "kind": "text",
            "options": [],
            "description": "Only purchase invoices this auto-confirmation rule released."
          },
          {
            "param": "purchase_order_no",
            "label": "Purchase order number",
            "kind": "text",
            "options": [],
            "description": "Only purchase invoices matched to this purchase order, e.g. PO-K20417."
          }
        ],
        "sorts": [
          [
            "received_at",
            "Arrived"
          ],
          [
            "invoice_date",
            "Invoice date"
          ],
          [
            "net_pence",
            "Net"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "needs_a_look",
            "label": "Needs a look",
            "params": {
              "stage": [
                "needs_a_look"
              ]
            },
            "count": true,
            "default": true
          },
          {
            "id": "ready_to_release",
            "label": "Ready to release",
            "params": {
              "stage": [
                "ready_to_release"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "in_query",
            "label": "In query",
            "params": {
              "stage": [
                "in_query"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "released",
            "label": "Released",
            "params": {
              "stage": [
                "released"
              ]
            },
            "count": false,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/views/purchase-invoice-review/{purchase_invoice_id}",
        "path_param": "purchase_invoice_id",
        "title_field": "supplier_document_no",
        "state_fields": [
          "stage"
        ],
        "state_labels": {
          "stage": {
            "dismissed": "Dismissed",
            "in_query": "In query",
            "needs_a_look": "Needs a look",
            "paid": "Paid",
            "posted": "Posted to Xero",
            "ready_to_release": "Ready to release",
            "released": "Released"
          }
        },
        "facts": [
          {
            "field": "supplier_name",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier",
            "ref_id_field": "supplier_no"
          },
          {
            "field": "trading_company",
            "label": "Trading company",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "trading_company_code"
          },
          {
            "field": "document_type_label",
            "label": "Kind",
            "kind": "text"
          },
          {
            "field": "invoice_date",
            "label": "Invoice date",
            "kind": "date"
          },
          {
            "field": "due_date",
            "label": "Due",
            "kind": "date"
          },
          {
            "field": "received_at",
            "label": "Arrived",
            "kind": "datetime"
          },
          {
            "field": "purchase_order_numbers",
            "label": "Purchase orders",
            "kind": "text",
            "ref": "purchase_orders.purchase_order"
          },
          {
            "field": "customer_order_numbers",
            "label": "For",
            "kind": "text",
            "ref": "orders.order"
          },
          {
            "field": "document",
            "label": "Document",
            "kind": "file"
          },
          {
            "field": "earlier_documents",
            "label": "Earlier documents",
            "kind": "file"
          },
          {
            "field": "net_pence",
            "label": "Net",
            "kind": "money"
          },
          {
            "field": "vat_pence",
            "label": "VAT",
            "kind": "money"
          },
          {
            "field": "gross_pence",
            "label": "Gross",
            "kind": "money"
          },
          {
            "field": "usage_period_label",
            "label": "Period",
            "kind": "text"
          },
          {
            "field": "usage_expected_pence",
            "label": "Our usage",
            "kind": "money"
          },
          {
            "field": "usage_over_pence",
            "label": "Over our usage",
            "kind": "money"
          },
          {
            "field": "posting_date",
            "label": "Posts on",
            "kind": "date"
          },
          {
            "field": "released_label",
            "label": "Released",
            "kind": "text"
          },
          {
            "field": "posted_label",
            "label": "Posted to Xero",
            "kind": "text"
          },
          {
            "field": "paid_label",
            "label": "Paid",
            "kind": "text"
          },
          {
            "field": "dismissed_label",
            "label": "Dismissed",
            "kind": "text"
          },
          {
            "field": "query_with",
            "label": "With",
            "kind": "text"
          },
          {
            "field": "query_raised",
            "label": "Raised",
            "kind": "text"
          },
          {
            "field": "query_ticket",
            "label": "Ticket",
            "kind": "text",
            "ref": "workflow.ticket",
            "ref_id_field": "query_ticket_id"
          },
          {
            "field": "query_question",
            "label": "Question",
            "kind": "text"
          },
          {
            "field": "last_query",
            "label": "Last query",
            "kind": "text",
            "untrusted": true
          },
          {
            "field": "last_query_ticket",
            "label": "Ticket",
            "kind": "text",
            "ref": "workflow.ticket",
            "ref_id_field": "last_query_ticket_id"
          }
        ],
        "fact_groups": [
          {
            "label": null,
            "fields": [
              "net_pence",
              "vat_pence",
              "gross_pence"
            ]
          },
          {
            "label": null,
            "fields": [
              "usage_period_label",
              "usage_expected_pence",
              "usage_over_pence"
            ]
          },
          {
            "label": null,
            "fields": [
              "query_with",
              "query_raised",
              "query_ticket"
            ],
            "section": "In query"
          },
          {
            "label": null,
            "fields": [
              "query_question"
            ],
            "section": "In query"
          },
          {
            "label": null,
            "fields": [
              "last_query",
              "last_query_ticket"
            ],
            "section": "Last query"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "finance_purchase_ledger.purchase_invoice_check",
          "label": "Checks",
          "field": "checks",
          "key_field": "key",
          "columns": [
            {
              "field": "label",
              "label": "Check",
              "kind": "text"
            },
            {
              "field": "outcome",
              "label": "Result",
              "kind": "state",
              "enum_labels": {
                "accepted": "Accepted",
                "fail": "Failed",
                "pass": "Passed",
                "warn": "Warning"
              }
            },
            {
              "field": "invoice_says",
              "label": "Invoice says",
              "kind": "text"
            },
            {
              "field": "one_holds",
              "label": "One holds",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "finance_purchase_ledger.purchase_invoice_line",
          "label": "Purchase invoice lines",
          "field": "lines",
          "key_field": "line_number",
          "columns": [
            {
              "field": "line_number",
              "label": "Line",
              "kind": "number"
            },
            {
              "field": "description",
              "label": "Description",
              "kind": "text"
            },
            {
              "field": "quantity",
              "label": "Quantity",
              "kind": "text"
            },
            {
              "field": "net_pence",
              "label": "Net",
              "kind": "money"
            },
            {
              "field": "vat_treatment_label",
              "label": "VAT",
              "kind": "text"
            },
            {
              "field": "account_label",
              "label": "Account",
              "kind": "text"
            },
            {
              "field": "spread_over_label",
              "label": "Spread over",
              "kind": "text"
            },
            {
              "field": "purchase_order_line_reference",
              "label": "Order line",
              "kind": "text",
              "ref": "purchase_orders.purchase_order"
            }
          ],
          "paged": true,
          "parent_filter": "purchase_invoice_id",
          "totals": {
            "net_pence": {
              "field": "lines_net_pence",
              "label": "Total",
              "kind": "money"
            }
          }
        },
        {
          "object": "purchase_orders.supplier_usage_record",
          "label": "Usage records",
          "field": "usage_records",
          "key_field": "usage_record_id",
          "columns": [
            {
              "field": "consignment",
              "label": "Consignment",
              "kind": "text"
            },
            {
              "field": "used_on",
              "label": "Used on",
              "kind": "date"
            },
            {
              "field": "order_lines",
              "label": "For",
              "kind": "text",
              "ref": "orders.order"
            },
            {
              "field": "service",
              "label": "Service",
              "kind": "text"
            },
            {
              "field": "expected_pence",
              "label": "Expected",
              "kind": "money"
            },
            {
              "field": "price_note",
              "label": "Note",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null,
          "totals": {
            "expected_pence": {
              "field": "usage_expected_pence",
              "label": "Our usage",
              "kind": "money"
            }
          }
        },
        {
          "object": "finance_purchase_ledger.supplier_message",
          "label": "Supplier messages",
          "field": "supplier_thread",
          "key_field": "id",
          "columns": [],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [
        {
          "object": "comms_ingest.email",
          "label": "Emails",
          "params": {
            "tag_object": "finance_purchase_ledger.purchase_invoice",
            "tag_key": "{purchase_invoice_id}"
          },
          "secondary": true
        }
      ],
      "lifecycle": [
        {
          "id": "finance_purchase_ledger.received",
          "label": "Received",
          "module": "finance_purchase_ledger_module",
          "description": "The purchase invoice arrived, by email, the supplier portal or by hand."
        },
        {
          "id": "finance_purchase_ledger.matched",
          "label": "Matched",
          "module": "finance_purchase_ledger_module",
          "description": "Matched to its purchase orders, to our usage, or classed as spend with no purchase order."
        },
        {
          "id": "finance_purchase_ledger.checked",
          "label": "Checked",
          "module": "finance_purchase_ledger_module",
          "description": "The checks on its numbers, its supplier and its bank details pass, or are accepted."
        },
        {
          "id": "finance_purchase_ledger.confirmed",
          "label": "Confirmed",
          "module": "finance_purchase_ledger_module",
          "description": "Released for payment by a person, or by an auto-confirmation rule."
        },
        {
          "id": "finance_purchase_ledger.posted",
          "label": "Posted to Xero",
          "module": "finance_purchase_ledger_module",
          "description": "Posted to Xero as a bill, with its document attached."
        },
        {
          "id": "finance_purchase_ledger.dismissed",
          "label": "Dismissed",
          "module": "finance_purchase_ledger_module",
          "description": "Dismissed as a duplicate, not an invoice, or not for this ledger."
        }
      ],
      "about": "A purchase invoice is a supplier's invoice or credit note, from receipt to payment. One reads most purchase invoices from supplier emails. Staff can enter a purchase invoice by hand with \"Add purchase invoice\". One checks each purchase invoice against the purchase orders. A purchase invoice reaches Xero only after a person or an auto-confirmation rule releases the payment.",
      "state_meanings": {
        "needs_a_look": "Needs a look: something needs a person before the payment can be released.",
        "ready_to_release": "Ready to release: a person with Release payments can release the payment now.",
        "in_query": "In query: the purchase invoice will wait until the query is resolved.",
        "released": "Released: One will post the bill to Xero.",
        "posted": "Posted to Xero: the bill is in Xero and waits to be paid there.",
        "paid": "Paid: Xero says the bill is paid.",
        "dismissed": "Dismissed: the purchase invoice will never be paid."
      },
      "state_tones": {
        "needs_a_look": "attention",
        "ready_to_release": "in_progress",
        "in_query": "in_progress",
        "released": "in_progress",
        "posted": "in_progress",
        "paid": "done",
        "dismissed": "neutral"
      },
      "commands": [
        "finance_purchase_ledger.reshape_match_group",
        "finance_purchase_ledger.accept_variance",
        "finance_purchase_ledger.put_purchase_invoice_in_query",
        "finance_purchase_ledger.check_bank_details",
        "finance_purchase_ledger.choose_purchase_invoice_trading_company",
        "finance_purchase_ledger.accept_purchase_invoice_as_ordering_company",
        "finance_purchase_ledger.accept_purchase_invoice_as_billed_company",
        "finance_purchase_ledger.set_usage_period",
        "finance_purchase_ledger.accept_usage_difference",
        "finance_purchase_ledger.confirm_match_group",
        "finance_purchase_ledger.approve_non_po_purchase_invoice",
        "finance_purchase_ledger.confirm_usage_purchase_invoice",
        "finance_purchase_ledger.record_purchase_invoice_second_approval",
        "finance_purchase_ledger.resolve_purchase_invoice_query",
        "finance_purchase_ledger.ask_supplier_about_purchase_invoice",
        "finance_purchase_ledger.amend_purchase_invoice",
        "finance_purchase_ledger.replace_purchase_invoice_document",
        "finance_purchase_ledger.classify_purchase_invoice_as_non_po",
        "finance_purchase_ledger.classify_purchase_invoice_as_po",
        "finance_purchase_ledger.dismiss_purchase_invoice",
        "finance_purchase_ledger.create_purchase_invoice_manually"
      ],
      "features": [
        "purchase_invoice_matching"
      ]
    },
    {
      "id": "finance_purchase_ledger.purchase_invoice_check",
      "module": "finance_purchase_ledger_module",
      "label": "Check",
      "plural": "Checks",
      "description": "One check the ledger ran on a purchase invoice, with what the invoice says beside what One holds.",
      "identifier": {
        "kind": "field",
        "field": "key",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "pass",
        "warn",
        "fail",
        "accepted"
      ],
      "list": null,
      "record": null,
      "parents": [
        "finance_purchase_ledger.purchase_invoice"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "pass": "done",
        "warn": "attention",
        "fail": "stopped",
        "accepted": "done"
      },
      "commands": [],
      "features": []
    },
    {
      "id": "finance_purchase_ledger.purchase_invoice_line",
      "module": "finance_purchase_ledger_module",
      "label": "Purchase invoice line",
      "plural": "Purchase invoice lines",
      "description": "One line of a purchase invoice, with the Xero account, VAT treatment and the period it is spread over.",
      "identifier": {
        "kind": "field",
        "field": "line_number",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": {
        "path": "/views/purchase-invoice-lines",
        "key_field": "line_number",
        "title_field": "description",
        "columns": [
          {
            "field": "line_number",
            "label": "Line",
            "kind": "number"
          },
          {
            "field": "description",
            "label": "Description",
            "kind": "text"
          },
          {
            "field": "quantity",
            "label": "Quantity",
            "kind": "text"
          },
          {
            "field": "net_pence",
            "label": "Net",
            "kind": "money"
          },
          {
            "field": "vat_treatment_label",
            "label": "VAT",
            "kind": "text"
          },
          {
            "field": "account_label",
            "label": "Account",
            "kind": "text"
          },
          {
            "field": "spread_over_label",
            "label": "Spread over",
            "kind": "text"
          },
          {
            "field": "purchase_order_line_reference",
            "label": "Order line",
            "kind": "text",
            "ref": "purchase_orders.purchase_order"
          }
        ],
        "filters": [
          {
            "param": "purchase_invoice_id",
            "label": "Purchase invoice ID",
            "kind": "text",
            "options": [],
            "description": "The purchase invoice whose lines these are: a line's number is unique only within its invoice."
          }
        ],
        "sorts": [
          [
            "line_number",
            "Line"
          ]
        ],
        "searchable": true,
        "queues": []
      },
      "record": null,
      "parents": [
        "finance_purchase_ledger.purchase_invoice"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": [
        "purchase_invoice_matching"
      ]
    },
    {
      "id": "finance_purchase_ledger.supplier_message",
      "module": "finance_purchase_ledger_module",
      "label": "Supplier message",
      "plural": "Supplier messages",
      "description": "A question finance asked the supplier about a purchase invoice, or the supplier's reply, on the supplier portal.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "finance_purchase_ledger.purchase_invoice"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": []
    },
    {
      "id": "finance_sales_ledger.credit_note",
      "module": "finance_sales_ledger_module",
      "label": "Credit note",
      "plural": "Credit notes",
      "description": "A credit against an issued invoice, in full or in part, with the refund that goes with it.",
      "identifier": {
        "kind": "cis_number",
        "field": "credit_note_no",
        "prefix": "CN",
        "pattern": "^CN-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}$",
        "line_pattern": "^CN-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}\\.[0-9]{2,}$"
      },
      "states": [
        "issued",
        "draft_in_xero",
        "approved_in_xero",
        "applied",
        "refunded",
        "refund_pending",
        "refund_failed",
        "voided_in_xero"
      ],
      "list": null,
      "record": {
        "path": "/views/credit-note/{credit_note_no}",
        "path_param": "credit_note_no",
        "title_field": "customer_name",
        "state_fields": [
          "state"
        ],
        "state_labels": {},
        "facts": [
          {
            "field": "invoice_no",
            "label": "Invoice",
            "kind": "identifier",
            "ref": "finance_sales_ledger.invoice"
          },
          {
            "field": "refund_label",
            "label": "Refund",
            "kind": "text"
          },
          {
            "field": "gross_pence",
            "label": "Gross",
            "kind": "money"
          },
          {
            "field": "credit_note_date",
            "label": "Issued",
            "kind": "date"
          },
          {
            "field": "trading_company",
            "label": "Trading company",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "trading_company_code"
          },
          {
            "field": "issued_legal_name",
            "label": "Issued as",
            "kind": "text"
          },
          {
            "field": "issued_company_number",
            "label": "Company number",
            "kind": "text"
          },
          {
            "field": "issued_registered_office",
            "label": "Registered office",
            "kind": "text"
          },
          {
            "field": "issued_vat_number",
            "label": "VAT number",
            "kind": "text"
          },
          {
            "field": "issued_bank_account",
            "label": "Pay into",
            "kind": "text"
          }
        ],
        "fact_groups": [
          {
            "label": "Issued as",
            "fields": [
              "trading_company",
              "issued_legal_name",
              "issued_company_number",
              "issued_registered_office",
              "issued_vat_number",
              "issued_bank_account"
            ]
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [
        "finance_sales_ledger.invoice"
      ],
      "children": [
        {
          "object": "finance_sales_ledger.credit_note_refund",
          "label": "Refunds",
          "field": "refunds",
          "key_field": "id",
          "columns": [
            {
              "field": "method_label",
              "label": "Refund",
              "kind": "text"
            },
            {
              "field": "amount_pence",
              "label": "Amount",
              "kind": "money"
            },
            {
              "field": "status_label",
              "label": "State",
              "kind": "text"
            },
            {
              "field": "reference",
              "label": "Reference",
              "kind": "text"
            },
            {
              "field": "refunded_on",
              "label": "Refunded on",
              "kind": "date"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "issued": "in_progress",
        "draft_in_xero": "in_progress",
        "approved_in_xero": "in_progress",
        "applied": "done",
        "refunded": "done",
        "refund_pending": "in_progress",
        "refund_failed": "stopped",
        "voided_in_xero": "stopped"
      },
      "commands": [],
      "features": [
        "sales_ledger"
      ]
    },
    {
      "id": "finance_sales_ledger.credit_note_refund",
      "module": "finance_sales_ledger_module",
      "label": "Refund",
      "plural": "Refunds",
      "description": "The money a credit note gives back, to the card or by bank transfer.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "pending",
        "made",
        "failed"
      ],
      "list": null,
      "record": null,
      "parents": [
        "finance_sales_ledger.credit_note"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "pending": "in_progress",
        "made": "done",
        "failed": "stopped"
      },
      "commands": [],
      "features": []
    },
    {
      "id": "finance_sales_ledger.invoice",
      "module": "finance_sales_ledger_module",
      "label": "Invoice",
      "plural": "Invoices",
      "description": "A VAT invoice for one order (or one payer's period), one payer, one payment method; immutable once issued.",
      "identifier": {
        "kind": "cis_number",
        "field": "invoice_no",
        "prefix": "INV",
        "pattern": "^INV-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}$",
        "line_pattern": "^INV-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}\\.[0-9]{2,}$"
      },
      "states": [
        "issued",
        "held",
        "draft_in_xero",
        "awaiting_payment",
        "paid",
        "voided_in_xero"
      ],
      "list": {
        "path": "/views/invoice-list",
        "key_field": "invoice_no",
        "title_field": "customer_name",
        "columns": [
          {
            "field": "invoice_no",
            "label": "Invoice",
            "kind": "identifier"
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text",
            "ref": "customers.customer",
            "ref_id_field": "cis_customer_code"
          },
          {
            "field": "payer_name",
            "label": "Billing account",
            "kind": "text"
          },
          {
            "field": "order_no",
            "label": "Order",
            "kind": "identifier",
            "ref": "orders.order"
          },
          {
            "field": "invoice_date",
            "label": "Issued",
            "kind": "date"
          },
          {
            "field": "due_date",
            "label": "Due",
            "kind": "date"
          },
          {
            "field": "gross_pence",
            "label": "Gross",
            "kind": "money"
          },
          {
            "field": "state",
            "label": "State",
            "kind": "state",
            "enum_labels": {
              "awaiting_payment": "Approved in Xero, awaiting payment",
              "draft_in_xero": "Draft in Xero, awaiting finance",
              "held": "Held",
              "issued": "Issued, going to Xero as a draft",
              "paid": "Paid",
              "voided_in_xero": "Voided in Xero"
            }
          }
        ],
        "filters": [
          {
            "param": "state",
            "label": "State",
            "kind": "multi_enum",
            "options": [
              [
                "issued",
                "Issued, going to Xero as a draft"
              ],
              [
                "held",
                "Held"
              ],
              [
                "draft_in_xero",
                "Draft in Xero, awaiting finance"
              ],
              [
                "awaiting_payment",
                "Approved in Xero, awaiting payment"
              ],
              [
                "paid",
                "Paid"
              ],
              [
                "voided_in_xero",
                "Voided in Xero"
              ]
            ],
            "description": "Only invoices in these states."
          },
          {
            "param": "customer_code",
            "label": "Customer code",
            "kind": "text",
            "options": [],
            "description": "Only this customer's invoices."
          },
          {
            "param": "billing_account_ref",
            "label": "Billing account reference",
            "kind": "text",
            "options": [],
            "description": "Only invoices this billing account pays."
          },
          {
            "param": "order_no",
            "label": "Order number",
            "kind": "text",
            "options": [],
            "description": "Only this order's invoices."
          }
        ],
        "sorts": [
          [
            "invoice_date",
            "Issued"
          ],
          [
            "due_date",
            "Due"
          ],
          [
            "invoice_no",
            "Invoice"
          ],
          [
            "gross_pence",
            "Gross"
          ],
          [
            "customer_name",
            "Customer"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "held",
            "label": "Held",
            "params": {
              "state": [
                "held"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "awaiting_payment",
            "label": "Awaiting payment",
            "params": {
              "state": [
                "awaiting_payment"
              ]
            },
            "count": false,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/views/invoice/{invoice_no}",
        "path_param": "invoice_no",
        "title_field": "customer_name",
        "state_fields": [
          "state"
        ],
        "state_labels": {},
        "facts": [
          {
            "field": "order_no",
            "label": "Order",
            "kind": "identifier",
            "ref": "orders.order"
          },
          {
            "field": "invoice_date",
            "label": "Issued",
            "kind": "date"
          },
          {
            "field": "due_date",
            "label": "Due",
            "kind": "date"
          },
          {
            "field": "net_pence",
            "label": "Net",
            "kind": "money"
          },
          {
            "field": "vat_pence",
            "label": "VAT",
            "kind": "money"
          },
          {
            "field": "gross_pence",
            "label": "Gross",
            "kind": "money"
          },
          {
            "field": "trading_company",
            "label": "Trading company",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "trading_company_code"
          },
          {
            "field": "issued_legal_name",
            "label": "Issued as",
            "kind": "text"
          },
          {
            "field": "issued_company_number",
            "label": "Company number",
            "kind": "text"
          },
          {
            "field": "issued_registered_office",
            "label": "Registered office",
            "kind": "text"
          },
          {
            "field": "issued_vat_number",
            "label": "VAT number",
            "kind": "text"
          },
          {
            "field": "issued_bank_account",
            "label": "Pay into",
            "kind": "text"
          }
        ],
        "fact_groups": [
          {
            "label": null,
            "fields": [
              "net_pence",
              "vat_pence",
              "gross_pence"
            ]
          },
          {
            "label": "Issued as",
            "fields": [
              "trading_company",
              "issued_legal_name",
              "issued_company_number",
              "issued_registered_office",
              "issued_vat_number",
              "issued_bank_account"
            ]
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "finance_sales_ledger.invoice_line",
          "label": "Invoice lines",
          "field": "lines",
          "key_field": "invoice_line_id",
          "columns": [
            {
              "field": "line_no",
              "label": "Line",
              "kind": "text"
            },
            {
              "field": "description",
              "label": "Description",
              "kind": "text"
            },
            {
              "field": "quantity",
              "label": "Quantity",
              "kind": "number"
            },
            {
              "field": "net_pence",
              "label": "Net",
              "kind": "money"
            }
          ],
          "paged": false,
          "parent_filter": null,
          "totals": {
            "net_pence": {
              "field": "net_pence",
              "label": "Net",
              "kind": "money"
            }
          }
        },
        {
          "object": "finance_sales_ledger.invoice_order",
          "label": "Orders",
          "field": "orders",
          "key_field": "order_no",
          "columns": [
            {
              "field": "order_no",
              "label": "Order",
              "kind": "identifier",
              "ref": "orders.order"
            },
            {
              "field": "net_pence",
              "label": "Net on this invoice",
              "kind": "money"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "finance_sales_ledger.credit_note",
          "label": "Credit notes",
          "field": "credit_notes",
          "key_field": "credit_note_no",
          "columns": [
            {
              "field": "credit_note_no",
              "label": "Credit note",
              "kind": "identifier"
            },
            {
              "field": "gross_pence",
              "label": "Gross",
              "kind": "money"
            },
            {
              "field": "state_label",
              "label": "State",
              "kind": "text"
            },
            {
              "field": "refund_label",
              "label": "Refund",
              "kind": "text"
            },
            {
              "field": "issued_on",
              "label": "Issued",
              "kind": "date"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "finance_sales_ledger.payment_received",
          "label": "Payments",
          "field": "payments",
          "key_field": "id",
          "columns": [
            {
              "field": "method_label",
              "label": "Method",
              "kind": "text"
            },
            {
              "field": "amount_pence",
              "label": "Amount",
              "kind": "money"
            },
            {
              "field": "received_on",
              "label": "Received",
              "kind": "date"
            },
            {
              "field": "reference",
              "label": "Reference",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [
        {
          "object": "comms_ingest.email",
          "label": "Emails",
          "params": {
            "tag_object": "finance_sales_ledger.invoice",
            "tag_key": "{invoice_no}"
          },
          "secondary": true
        }
      ],
      "lifecycle": [],
      "about": "An invoice is a VAT invoice for one order, one payer and one payment method, or, when the payer is invoiced at a weekly or monthly cut-off, for the lines of several orders. The sales ledger raises the invoice when the order lines fall due, or at the payer's cut-off. The sales ledger invoices card and pro-forma orders on payment. The sales ledger invoices a credit-account order on despatch or on delivery, as the billing account sets. A credit note corrects an issued invoice.",
      "state_meanings": {
        "issued": "Issued: One will send the invoice to Xero as a draft.",
        "held": "Held: the invoice will stay out of Xero until the Xero refusal or the payer's Xero contact is resolved.",
        "draft_in_xero": "Draft in Xero: finance will approve the invoice in Xero.",
        "awaiting_payment": "Awaiting payment: the customer hasn't paid the approved invoice yet.",
        "paid": "Paid: the customer has paid the invoice.",
        "voided_in_xero": "Voided in Xero: finance voided or deleted the invoice in Xero."
      },
      "state_tones": {
        "issued": "in_progress",
        "held": "attention",
        "draft_in_xero": "in_progress",
        "awaiting_payment": "in_progress",
        "paid": "done",
        "voided_in_xero": "stopped"
      },
      "commands": [
        "finance_credit_control.record_dispute",
        "finance_credit_control.resolve_invoice_dispute",
        "finance_sales_ledger.raise_credit_note"
      ],
      "features": [
        "sales_ledger"
      ]
    },
    {
      "id": "finance_sales_ledger.invoice_line",
      "module": "finance_sales_ledger_module",
      "label": "Invoice line",
      "plural": "Invoice lines",
      "description": "What one order line (or charge) bills, from pending to due, invoiced or withdrawn.",
      "identifier": {
        "kind": "field",
        "field": "invoice_line_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "pending",
        "due",
        "invoiced",
        "withdrawn",
        "not_billable"
      ],
      "list": null,
      "record": null,
      "parents": [
        "finance_sales_ledger.invoice"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "pending": "neutral",
        "due": "in_progress",
        "invoiced": "done",
        "withdrawn": "stopped",
        "not_billable": "neutral"
      },
      "commands": [],
      "features": []
    },
    {
      "id": "finance_sales_ledger.invoice_order",
      "module": "finance_sales_ledger_module",
      "label": "Order",
      "plural": "Orders",
      "description": "An order whose lines are on a consolidated invoice, with the net they add to it.",
      "identifier": {
        "kind": "field",
        "field": "order_no",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "finance_sales_ledger.invoice"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": []
    },
    {
      "id": "finance_sales_ledger.payment_received",
      "module": "finance_sales_ledger_module",
      "label": "Payment",
      "plural": "Payments",
      "description": "Money received against an order, a pro-forma or an invoice raised on payment.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "finance_sales_ledger.invoice"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": []
    },
    {
      "id": "finance_sales_ledger.period_charge",
      "module": "finance_sales_ledger_module",
      "label": "Period charge",
      "plural": "Period charges",
      "description": "What a payer is billed on a schedule with no order: storage, a fulfilment service or a licence fee.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [
        "finance_sales_ledger.record_period_usage",
        "finance_sales_ledger.end_period_charge",
        "finance_sales_ledger.withdraw_period_charge"
      ],
      "features": []
    },
    {
      "id": "finance_sales_ledger.period_usage",
      "module": "finance_sales_ledger_module",
      "label": "Period usage",
      "plural": "Period usages",
      "description": "The quantity recorded for one ended period of a metered or cost-plus charge: what the period then bills.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "finance_sales_ledger.period_charge"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": []
    },
    {
      "id": "finance_sales_ledger.proforma",
      "module": "finance_sales_ledger_module",
      "label": "Pro-forma",
      "plural": "Pro-formas",
      "description": "A request for payment before supply, for the order lines it covers; never a VAT invoice.",
      "identifier": {
        "kind": "cis_number",
        "field": "proforma_no",
        "prefix": "PF",
        "pattern": "^PF-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}$",
        "line_pattern": "^PF-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}\\.[0-9]{2,}$"
      },
      "states": [
        "issued",
        "part_paid",
        "paid",
        "superseded",
        "expired",
        "voided"
      ],
      "list": {
        "path": "/views/proforma-list",
        "key_field": "proforma_no",
        "title_field": "customer_name",
        "columns": [
          {
            "field": "proforma_no",
            "label": "Pro-forma",
            "kind": "identifier"
          },
          {
            "field": "order_no",
            "label": "Order",
            "kind": "identifier",
            "ref": "orders.order"
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text",
            "ref": "customers.customer",
            "ref_id_field": "cis_customer_code"
          },
          {
            "field": "payer_name",
            "label": "Billing account",
            "kind": "text"
          },
          {
            "field": "state",
            "label": "State",
            "kind": "state",
            "enum_labels": {
              "expired": "Expired unpaid",
              "issued": "Awaiting payment",
              "paid": "Paid",
              "part_paid": "Part paid",
              "superseded": "Replaced by a new pro-forma",
              "voided": "Voided: the order was cancelled"
            }
          },
          {
            "field": "gross_pence",
            "label": "Gross",
            "kind": "money"
          },
          {
            "field": "outstanding_pence",
            "label": "Still owed",
            "kind": "money"
          },
          {
            "field": "issued_on",
            "label": "Issued",
            "kind": "date"
          },
          {
            "field": "valid_until",
            "label": "Valid until",
            "kind": "date"
          }
        ],
        "filters": [
          {
            "param": "state",
            "label": "State",
            "kind": "multi_enum",
            "options": [
              [
                "issued",
                "Awaiting payment"
              ],
              [
                "part_paid",
                "Part paid"
              ],
              [
                "paid",
                "Paid"
              ],
              [
                "superseded",
                "Replaced by a new pro-forma"
              ],
              [
                "expired",
                "Expired unpaid"
              ],
              [
                "voided",
                "Voided: the order was cancelled"
              ]
            ],
            "description": "Only pro-formas in these states (default: all)."
          }
        ],
        "sorts": [
          [
            "valid_until",
            "Valid until"
          ],
          [
            "issued_at",
            "Issued at"
          ],
          [
            "proforma_no",
            "Pro-forma"
          ],
          [
            "order_no",
            "Order"
          ],
          [
            "customer_name",
            "Customer"
          ],
          [
            "outstanding_pence",
            "Still owed"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "awaiting_payment",
            "label": "Awaiting payment",
            "params": {
              "state": [
                "issued",
                "part_paid"
              ]
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/views/proforma/{proforma_no}",
        "path_param": "proforma_no",
        "title_field": "customer_name",
        "state_fields": [
          "state"
        ],
        "state_labels": {},
        "facts": [
          {
            "field": "order_no",
            "label": "Order",
            "kind": "identifier",
            "ref": "orders.order"
          },
          {
            "field": "gross_pence",
            "label": "Gross",
            "kind": "money"
          },
          {
            "field": "outstanding_pence",
            "label": "Still owed",
            "kind": "money"
          },
          {
            "field": "valid_until",
            "label": "Valid until",
            "kind": "date"
          },
          {
            "field": "trading_company",
            "label": "Trading company",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "trading_company_code"
          },
          {
            "field": "issued_legal_name",
            "label": "Issued as",
            "kind": "text"
          },
          {
            "field": "issued_company_number",
            "label": "Company number",
            "kind": "text"
          },
          {
            "field": "issued_registered_office",
            "label": "Registered office",
            "kind": "text"
          },
          {
            "field": "issued_vat_number",
            "label": "VAT number",
            "kind": "text"
          },
          {
            "field": "issued_bank_account",
            "label": "Pay into",
            "kind": "text"
          }
        ],
        "fact_groups": [
          {
            "label": "Issued as",
            "fields": [
              "trading_company",
              "issued_legal_name",
              "issued_company_number",
              "issued_registered_office",
              "issued_vat_number",
              "issued_bank_account"
            ]
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [
        "orders.order"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A pro-forma asks the customer to pay for order lines before supply. A pro-forma is never a VAT invoice. One issues a pro-forma when the payer pays the order by pro-forma. The PF number is the payment reference finance matches on the bank feed. Once the customer pays the pro-forma in full, the sales ledger will raise the invoice.",
      "state_meanings": {
        "issued": "Awaiting payment: the pro-forma will wait for a bank transfer or a card payment.",
        "part_paid": "Part paid: finance will chase the shortfall.",
        "paid": "Paid: the customer has paid the pro-forma in full.",
        "superseded": "Replaced by a new pro-forma: the order changed before payment.",
        "expired": "Expired unpaid: nobody paid the pro-forma within 60 days.",
        "voided": "Voided: the order lines were cancelled before any money arrived."
      },
      "state_tones": {
        "issued": "in_progress",
        "part_paid": "in_progress",
        "paid": "done",
        "superseded": "neutral",
        "expired": "stopped",
        "voided": "stopped"
      },
      "commands": [
        "finance_sales_ledger.record_proforma_payment",
        "finance_sales_ledger.mark_overpayment_refunded"
      ],
      "features": [
        "sales_ledger"
      ]
    },
    {
      "id": "fulfilment_adapter_promo_office.held_up_item",
      "module": "fulfilment_adapter_promo_office_module",
      "label": "Held up item",
      "plural": "Held up items",
      "description": "A Promo Office PO the broker could not match or follow, with why, the fix and who owns it.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "open",
        "resolved",
        "dismissed"
      ],
      "list": null,
      "record": {
        "path": "/fulfilment-adapter-promo-office/held-up-items/{id}",
        "path_param": "id",
        "title_field": "summary",
        "state_fields": [
          "state"
        ],
        "state_labels": {
          "state": {
            "dismissed": "Dismissed",
            "open": "Open",
            "resolved": "Resolved"
          }
        },
        "facts": [
          {
            "field": "document_number",
            "label": "Promo Office PO",
            "kind": "text"
          },
          {
            "field": "owner",
            "label": "Owner",
            "kind": "text"
          },
          {
            "field": "held_up_since",
            "label": "Held up since",
            "kind": "datetime"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "open": "attention",
        "resolved": "done",
        "dismissed": "neutral"
      },
      "commands": [
        "fulfilment_adapter_promo_office.resolve_held_up_item"
      ],
      "features": [
        "promo_office_channel"
      ]
    },
    {
      "id": "fulfilment_adapter_supplier_portal.supplier_proof",
      "module": "fulfilment_adapter_supplier_portal_module",
      "label": "Supplier proof",
      "plural": "Supplier proofs",
      "description": "A proof a supplier uploaded for one purchase order line, our check of it and the decision on it.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "purchase_orders.purchase_order"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [
        "fulfilment_adapter_supplier_portal.decide_supplier_proof"
      ],
      "features": []
    },
    {
      "id": "fulfilment_adapters.supplier_connection",
      "module": "fulfilment_adapters_module",
      "label": "Supplier connection",
      "plural": "Connections",
      "description": "How one area of work (pricing, purchase order, artwork, despatch, warehouse, courier, invoices, set-up) travels between us and one supplier, and whether that is the default or set for them.",
      "identifier": {
        "kind": "field",
        "field": "connection_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": {
        "path": "/views/supplier-connection/{connection_id}",
        "path_param": "connection_id",
        "title_field": "title",
        "state_fields": [],
        "state_labels": {},
        "facts": [
          {
            "field": "supplier_name",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier",
            "ref_id_field": "supplier_no"
          },
          {
            "field": "connected_through",
            "label": "Connected through",
            "kind": "text"
          },
          {
            "field": "our_admin",
            "label": "Our admin",
            "kind": "text"
          },
          {
            "field": "origin",
            "label": "Default or Set",
            "kind": "text"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [
        "suppliers.supplier"
      ],
      "children": [
        {
          "object": "fulfilment_adapters.supplier_interaction",
          "label": "Interactions",
          "field": "interactions",
          "key_field": "interaction_id",
          "columns": [
            {
              "field": "interaction",
              "label": "Interaction",
              "kind": "text"
            },
            {
              "field": "started_by",
              "label": "Started by",
              "kind": "text"
            },
            {
              "field": "request",
              "label": "Request",
              "kind": "text",
              "detail": {
                "field": "request_about",
                "label": "What happens",
                "kind": "text"
              }
            },
            {
              "field": "response",
              "label": "Response",
              "kind": "text",
              "detail": {
                "field": "response_about",
                "label": "What happens",
                "kind": "text"
              }
            },
            {
              "field": "our_admin",
              "label": "Our admin",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [
        {
          "object": "finance_purchase_ledger.purchase_invoice",
          "label": "Purchase invoices",
          "params": {
            "supplier_no": "{supplier_no}"
          },
          "secondary": true
        }
      ],
      "lifecycle": [],
      "about": "A supplier connection says how one area of work travels between Pollards and the supplier. Each area is made of interactions (one request and its response about one thing), and each interaction has a channel for the request and a channel for the response. \"Set\" means the supplier's integration profile names the area; \"Default\" means the rule decides. Where the supplier portal is on, a supplier with a purchase order email gets its orders through our supplier portal by default. A profile change is a reviewed code change.",
      "commands": [],
      "features": [
        "purchase_invoice_matching"
      ]
    },
    {
      "id": "fulfilment_adapters.supplier_interaction",
      "module": "fulfilment_adapters_module",
      "label": "Interaction",
      "plural": "Interactions",
      "description": "One request and its response between us and one supplier about one thing, with the channel each travels by and the admin it leaves us.",
      "identifier": {
        "kind": "field",
        "field": "interaction_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": {
        "path": "/views/supplier-interaction/{interaction_id}",
        "path_param": "interaction_id",
        "title_field": "interaction",
        "state_fields": [],
        "state_labels": {},
        "facts": [
          {
            "field": "connection_title",
            "label": "Connection",
            "kind": "text",
            "ref": "fulfilment_adapters.supplier_connection",
            "ref_id_field": "connection_id"
          },
          {
            "field": "started_by",
            "label": "Started by",
            "kind": "text"
          },
          {
            "field": "request",
            "label": "Request",
            "kind": "text"
          },
          {
            "field": "request_about",
            "label": "What happens with the request",
            "kind": "text"
          },
          {
            "field": "response",
            "label": "Response",
            "kind": "text"
          },
          {
            "field": "response_about",
            "label": "What happens with the response",
            "kind": "text"
          },
          {
            "field": "our_admin",
            "label": "Our admin",
            "kind": "text"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [
        "fulfilment_adapters.supplier_connection"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "An interaction is one request and its response between Pollards and a supplier about one thing, such as placing a purchase order or sending an invoice. Each has a channel for the request and a channel for the response, and says what admin it leaves on our side. Hover over a channel to see what happens.",
      "commands": [],
      "features": [
        "purchase_invoice_matching"
      ]
    },
    {
      "id": "fulfilment_adapters.supplier_website_order",
      "module": "fulfilment_adapters_module",
      "label": "Supplier website order",
      "plural": "Supplier website orders",
      "description": "A purchase order as its supplier's web shop needs it ordered, with the shop's own options and product page for each line, the price expected, where each goes and when the order must leave.",
      "identifier": {
        "kind": "field",
        "field": "purchase_order_no",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": {
        "path": "/views/supplier-website-order/{purchase_order_no}",
        "path_param": "purchase_order_no",
        "title_field": "title",
        "state_fields": [],
        "state_labels": {},
        "facts": [
          {
            "field": "purchase_order",
            "label": "Purchase order",
            "kind": "text",
            "ref": "purchase_orders.purchase_order"
          },
          {
            "field": "supplier_name",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier",
            "ref_id_field": "supplier_no"
          },
          {
            "field": "status",
            "label": "Purchase order status",
            "kind": "text"
          },
          {
            "field": "ordering_site",
            "label": "Supplier's site",
            "kind": "link"
          },
          {
            "field": "our_reference",
            "label": "Our reference",
            "kind": "text"
          },
          {
            "field": "our_reference_goes_in",
            "label": "Our reference goes in",
            "kind": "text"
          },
          {
            "field": "must_leave_on",
            "label": "Must leave the supplier by",
            "kind": "date"
          },
          {
            "field": "turnaround",
            "label": "Turnaround",
            "kind": "text"
          },
          {
            "field": "cut_off",
            "label": "Cut-off",
            "kind": "text"
          },
          {
            "field": "placing_steps",
            "label": "Steps for a person",
            "kind": "formatted_text"
          },
          {
            "field": "read_back",
            "label": "Read back and compare",
            "kind": "text"
          },
          {
            "field": "earlier_attempt_pages",
            "label": "Search for an earlier attempt in",
            "kind": "text"
          },
          {
            "field": "after_order",
            "label": "After checkout",
            "kind": "text"
          },
          {
            "field": "guide_to_complete",
            "label": "Guide still to complete from the first order",
            "kind": "text"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [
        "purchase_orders.purchase_order"
      ],
      "children": [
        {
          "object": "fulfilment_adapters.supplier_website_order_line",
          "label": "Supplier website order lines",
          "field": "lines",
          "key_field": "line_reference",
          "columns": [
            {
              "field": "line_reference",
              "label": "Line",
              "kind": "identifier"
            },
            {
              "field": "ordered_for_ref",
              "label": "For order line",
              "kind": "text",
              "ref": "orders.order"
            },
            {
              "field": "customer_order_number",
              "label": "Customer's PO number",
              "kind": "text",
              "untrusted": true
            },
            {
              "field": "quantity",
              "label": "Quantity",
              "kind": "number"
            },
            {
              "field": "lead_days",
              "label": "Lead time in working days",
              "kind": "number"
            },
            {
              "field": "expected_cost_pence",
              "label": "Expected cost",
              "kind": "money"
            },
            {
              "field": "drift_limit_pence",
              "label": "Drift limit",
              "kind": "money"
            },
            {
              "field": "artwork",
              "label": "Artwork",
              "kind": "text"
            },
            {
              "field": "product_code",
              "label": "Supplier's product",
              "kind": "text"
            },
            {
              "field": "product_page",
              "label": "Product page",
              "kind": "link"
            },
            {
              "field": "not_orderable_reason",
              "label": "Can't be ordered from the website because",
              "kind": "text"
            },
            {
              "field": "choices",
              "label": "Choices on the site",
              "kind": "text"
            },
            {
              "field": "delivering_to",
              "label": "Delivering to",
              "kind": "text",
              "untrusted": true
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [],
      "lifecycle": [],
      "about": "A supplier website order is a purchase order described in the words of the supplier's web shop, for the buyer's Claude to build the basket from. It carries the supplier's site guide: where our reference goes, what to read back, and the pages to search for an earlier attempt. Each order line shows the choices to make on the supplier's site, the expected cost and drift limit, where it goes and whether its artwork is ready. A line that has no website value for one of its options is placed by a person. The cut-off is the time the supplier holds the turnaround to.",
      "commands": [],
      "features": [
        "orders"
      ]
    },
    {
      "id": "fulfilment_adapters.supplier_website_order_line",
      "module": "fulfilment_adapters_module",
      "label": "Supplier website order line",
      "plural": "Supplier website order lines",
      "description": "One open purchase order line as the supplier's web shop takes it, ready to build into its basket.",
      "identifier": {
        "kind": "field",
        "field": "line_reference",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "fulfilment_adapters.supplier_website_order"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": []
    },
    {
      "id": "issues.issue",
      "module": "issues_module",
      "label": "Issue",
      "plural": "Issues",
      "description": "A quality or operational problem staff raised, numbered ISS-YYYY-NNNN, from raising through investigation to its resolution.",
      "identifier": {
        "kind": "uuid",
        "field": "issue_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "open",
        "investigating",
        "resolved"
      ],
      "list": {
        "path": "/issues",
        "key_field": "issue_id",
        "title_field": "reference",
        "columns": [
          {
            "field": "issue_id",
            "label": "Issue",
            "kind": "identifier"
          },
          {
            "field": "reference",
            "label": "Reference",
            "kind": "text"
          },
          {
            "field": "status",
            "label": "Status",
            "kind": "state",
            "enum_labels": {
              "investigating": "Investigating",
              "open": "Open",
              "resolved": "Resolved"
            }
          },
          {
            "field": "supplier_no",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier"
          },
          {
            "field": "title",
            "label": "Title",
            "kind": "text"
          },
          {
            "field": "raised_at",
            "label": "Raised",
            "kind": "datetime"
          }
        ],
        "filters": [
          {
            "param": "status",
            "label": "Status",
            "kind": "multi_enum",
            "options": [
              [
                "open",
                "Open"
              ],
              [
                "investigating",
                "Investigating"
              ],
              [
                "resolved",
                "Resolved"
              ]
            ],
            "description": "Only issues in these states."
          },
          {
            "param": "type_id",
            "label": "Type ID",
            "kind": "multi_enum",
            "options": []
          },
          {
            "param": "customer_id",
            "label": "Customer ID",
            "kind": "text",
            "options": []
          },
          {
            "param": "supplier_no",
            "label": "Supplier",
            "kind": "entity",
            "options": [],
            "object": "suppliers.supplier",
            "description": "Only issues about this supplier (the supplier number, SUP-…)."
          },
          {
            "param": "assignee_id",
            "label": "Assignee",
            "kind": "entity",
            "options": [],
            "object": "users.user",
            "description": "Only issues assigned to this user."
          },
          {
            "param": "mine",
            "label": "Assigned to me",
            "kind": "boolean",
            "options": [],
            "description": "True: only issues assigned to you."
          },
          {
            "param": "raised_after",
            "label": "Raised After",
            "kind": "date",
            "options": []
          },
          {
            "param": "raised_before",
            "label": "Raised Before",
            "kind": "date",
            "options": []
          }
        ],
        "sorts": [
          [
            "raised_at",
            "Raised"
          ],
          [
            "status",
            "Status"
          ],
          [
            "reference",
            "Reference"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "open",
            "label": "Open",
            "params": {
              "status": [
                "open"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "investigating",
            "label": "Investigating",
            "params": {
              "status": [
                "investigating"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "mine",
            "label": "Assigned to me",
            "params": {
              "mine": true,
              "status": [
                "open",
                "investigating"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "resolved",
            "label": "Resolved",
            "params": {
              "status": [
                "resolved"
              ]
            },
            "count": false,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/issues/{issue_id}",
        "path_param": "issue_id",
        "title_field": "reference",
        "state_fields": [
          "status"
        ],
        "state_labels": {
          "status": {
            "investigating": "Investigating",
            "open": "Open",
            "resolved": "Resolved"
          }
        },
        "facts": [
          {
            "field": "title",
            "label": "Title",
            "kind": "text"
          },
          {
            "field": "type_label",
            "label": "Type",
            "kind": "text"
          },
          {
            "field": "supplier_name",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier",
            "ref_id_field": "supplier_no"
          },
          {
            "field": "assignee_name",
            "label": "Assignee",
            "kind": "text"
          },
          {
            "field": "raised_at",
            "label": "Raised",
            "kind": "datetime"
          }
        ],
        "timeline_path": "/issues/{issue_id}/timeline",
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "issues.issue_attachment",
          "label": "Attachments",
          "field": "attachments",
          "key_field": "attachment_id",
          "columns": [
            {
              "field": "attachment_id",
              "label": "Attachment",
              "kind": "identifier"
            },
            {
              "field": "filename",
              "label": "File",
              "kind": "text"
            },
            {
              "field": "uploaded_at",
              "label": "Added",
              "kind": "datetime"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [],
      "lifecycle": [],
      "about": "An issue is a quality or operational problem that staff raised, numbered ISS-YYYY-NNNN. Staff raise an issue with \"Raise issue\". An investigator then starts the investigation with \"Start investigation\". Only investigators can start, save and resolve an investigation. An issue can't be resolved without a corrective action.",
      "state_meanings": {
        "open": "Open: the issue will wait for an investigator to start the investigation.",
        "investigating": "Investigating: the issue will stay open until an investigator resolves the issue with a corrective action.",
        "resolved": "Resolved: an investigator can reopen the issue into investigation, with a reason."
      },
      "state_tones": {
        "open": "attention",
        "investigating": "in_progress",
        "resolved": "done"
      },
      "commands": [
        "issues.resolve_issue",
        "issues.raise_issue",
        "issues.add_note",
        "issues.assign_issue",
        "issues.update_issue",
        "issues.upload_attachment",
        "issues.start_investigation",
        "issues.save_investigation",
        "issues.write_investigation_job_note",
        "issues.reopen_issue"
      ],
      "features": []
    },
    {
      "id": "issues.issue_attachment",
      "module": "issues_module",
      "label": "Attachment",
      "plural": "Attachments",
      "description": "A file attached to an issue (a photo, a PDF, a Word document).",
      "identifier": {
        "kind": "uuid",
        "field": "attachment_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "issues.issue"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [
        "issues.delete_attachment"
      ],
      "features": []
    },
    {
      "id": "marketing_audiences.audience",
      "module": "marketing_audiences_module",
      "label": "Audience",
      "plural": "Audiences",
      "description": "A named, saved set of include and exclude rules, plus pinned people, that a campaign sends to.",
      "identifier": {
        "kind": "field",
        "field": "audience_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": {
        "path": "/marketing/audiences",
        "key_field": "audience_id",
        "title_field": "name",
        "columns": [
          {
            "field": "audience_id",
            "label": "Audience",
            "kind": "text"
          },
          {
            "field": "name",
            "label": "Name",
            "kind": "text"
          },
          {
            "field": "description",
            "label": "Description",
            "kind": "text"
          },
          {
            "field": "updated_at",
            "label": "Updated",
            "kind": "datetime"
          },
          {
            "field": "rule_count",
            "label": "Rules",
            "kind": "number"
          }
        ],
        "filters": [],
        "sorts": [
          [
            "name",
            "Name"
          ],
          [
            "created_at",
            "Created At"
          ],
          [
            "updated_at",
            "Updated"
          ]
        ],
        "searchable": true,
        "queues": []
      },
      "record": {
        "path": "/marketing/audiences/{audience_id}",
        "path_param": "audience_id",
        "title_field": "name",
        "state_fields": [],
        "state_labels": {},
        "facts": [
          {
            "field": "description",
            "label": "Description",
            "kind": "text"
          },
          {
            "field": "exclude_repeat_bouncers",
            "label": "Leaves out repeat bouncers",
            "kind": "boolean"
          },
          {
            "field": "updated_at",
            "label": "Updated",
            "kind": "datetime"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "marketing_audiences.audience_rule",
          "label": "Rules",
          "field": "rules",
          "key_field": "id",
          "columns": [
            {
              "field": "id",
              "label": "Rule",
              "kind": "text"
            },
            {
              "field": "rule_type",
              "label": "Include or exclude",
              "kind": "text",
              "enum_labels": {
                "exclude": "Exclude",
                "include": "Include"
              }
            },
            {
              "field": "attribute_path",
              "label": "Attribute",
              "kind": "text"
            },
            {
              "field": "operator",
              "label": "Operator",
              "kind": "text"
            },
            {
              "field": "value",
              "label": "Value",
              "kind": "text"
            },
            {
              "field": "position",
              "label": "Position",
              "kind": "number"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "marketing_audiences.audience_manual_override",
          "label": "Pins",
          "field": "pins",
          "key_field": "person_id",
          "columns": [
            {
              "field": "person_id",
              "label": "Person id",
              "kind": "identifier"
            },
            {
              "field": "person_name",
              "label": "Person",
              "kind": "text"
            },
            {
              "field": "override_type",
              "label": "Pin",
              "kind": "text",
              "enum_labels": {
                "pin_in": "Pin in",
                "pin_out": "Pin out"
              }
            },
            {
              "field": "reason",
              "label": "Reason",
              "kind": "text"
            },
            {
              "field": "set_at",
              "label": "Pinned",
              "kind": "datetime"
            }
          ],
          "paged": true,
          "parent_filter": "audience_id"
        }
      ],
      "related": [],
      "lifecycle": [],
      "about": "An audience is a saved set of rules and pinned people that a campaign sends to. Staff make an audience with \"New audience\". Pins override the include and exclude rules. Email consent always wins over a pin.",
      "commands": [
        "marketing_audiences.create_audience",
        "marketing_audiences.update_audience",
        "marketing_audiences.set_audience_rules",
        "marketing_audiences.add_manual_override",
        "marketing_audiences.bulk_add_overrides",
        "marketing_audiences.bulk_add_overrides_by_tag",
        "marketing_audiences.bulk_add_overrides_by_emails"
      ],
      "features": []
    },
    {
      "id": "marketing_audiences.audience_manual_override",
      "module": "marketing_audiences_module",
      "label": "Pin",
      "plural": "Pins",
      "description": "A person pinned into or out of an audience, whatever its rules say.",
      "identifier": {
        "kind": "field",
        "field": "person_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": {
        "path": "/marketing/audiences/pins",
        "key_field": "person_id",
        "title_field": "person_name",
        "columns": [
          {
            "field": "person_id",
            "label": "Person id",
            "kind": "identifier"
          },
          {
            "field": "person_name",
            "label": "Person",
            "kind": "text"
          },
          {
            "field": "override_type",
            "label": "Pin",
            "kind": "text",
            "enum_labels": {
              "pin_in": "Pin in",
              "pin_out": "Pin out"
            }
          },
          {
            "field": "reason",
            "label": "Reason",
            "kind": "text"
          },
          {
            "field": "set_at",
            "label": "Pinned",
            "kind": "datetime"
          }
        ],
        "filters": [
          {
            "param": "audience_id",
            "label": "Audience ID",
            "kind": "text",
            "options": [],
            "description": "Only this audience's pins, by the audience's id."
          }
        ],
        "sorts": [
          [
            "set_at",
            "Pinned"
          ],
          [
            "override_type",
            "Pin"
          ]
        ],
        "searchable": true,
        "queues": []
      },
      "record": null,
      "parents": [
        "marketing_audiences.audience"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [
        "marketing_audiences.remove_manual_override"
      ],
      "features": []
    },
    {
      "id": "marketing_audiences.audience_rule",
      "module": "marketing_audiences_module",
      "label": "Rule",
      "plural": "Rules",
      "description": "One include or exclude rule of an audience, an attribute, an operator and a value, in order.",
      "identifier": {
        "kind": "field",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "marketing_audiences.audience"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": []
    },
    {
      "id": "marketing_audiences.standing_recipient",
      "module": "marketing_audiences_module",
      "label": "Standing recipient",
      "plural": "Standing recipients",
      "description": "A contact pinned into every audience, on top of each audience's own rules.",
      "identifier": {
        "kind": "field",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": {
        "path": "/marketing/audiences/standing-recipients",
        "key_field": "id",
        "title_field": "email",
        "columns": [
          {
            "field": "name",
            "label": "Name",
            "kind": "text"
          },
          {
            "field": "email",
            "label": "Email",
            "kind": "text"
          },
          {
            "field": "note",
            "label": "Note",
            "kind": "text"
          },
          {
            "field": "added_at",
            "label": "Added",
            "kind": "datetime"
          }
        ],
        "filters": [],
        "sorts": [
          [
            "added_at",
            "Added"
          ],
          [
            "email",
            "Email"
          ]
        ],
        "searchable": true,
        "queues": []
      },
      "record": {
        "path": "/marketing/audiences/standing-recipients/{id}",
        "path_param": "id",
        "title_field": "email",
        "state_fields": [],
        "state_labels": {},
        "facts": [
          {
            "field": "name",
            "label": "Name",
            "kind": "text"
          },
          {
            "field": "note",
            "label": "Note",
            "kind": "text"
          },
          {
            "field": "added_at",
            "label": "Added",
            "kind": "datetime"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A standing recipient is a contact pinned into every audience, on top of each audience's own rules. Staff make one with \"Add standing recipient…\": the contact is then pinned into every existing audience, and into each audience made afterwards. \"Remove…\" stops new audiences pinning the contact in. The pins already on existing audiences stay until someone unpins them. Email consent still wins over a pin.",
      "commands": [
        "marketing_audiences.add_standing_recipient",
        "marketing_audiences.remove_standing_recipient"
      ],
      "features": []
    },
    {
      "id": "marketing_leads.lead_source",
      "module": "marketing_leads_module",
      "label": "Lead source",
      "plural": "Lead sources",
      "description": "A landing page or lead magnet whose public form One takes submissions from, by its slug.",
      "identifier": {
        "kind": "field",
        "field": "lead_source_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": {
        "path": "/leads/sources",
        "key_field": "lead_source_id",
        "title_field": "name",
        "columns": [
          {
            "field": "lead_source_id",
            "label": "Lead source",
            "kind": "text"
          },
          {
            "field": "slug",
            "label": "Slug",
            "kind": "identifier"
          },
          {
            "field": "name",
            "label": "Name",
            "kind": "text"
          },
          {
            "field": "is_active",
            "label": "Active",
            "kind": "boolean"
          },
          {
            "field": "launched_at",
            "label": "Went live",
            "kind": "datetime"
          }
        ],
        "filters": [
          {
            "param": "is_active",
            "label": "Active",
            "kind": "boolean",
            "options": []
          }
        ],
        "sorts": [
          [
            "name",
            "Name"
          ],
          [
            "created_at",
            "Created At"
          ],
          [
            "updated_at",
            "Updated At"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "active",
            "label": "Active",
            "params": {
              "is_active": true
            },
            "count": false,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/leads/sources/{lead_source_id}",
        "path_param": "lead_source_id",
        "title_field": "name",
        "state_fields": [],
        "state_labels": {},
        "facts": [
          {
            "field": "slug",
            "label": "Slug",
            "kind": "identifier"
          },
          {
            "field": "is_active",
            "label": "Active",
            "kind": "boolean"
          },
          {
            "field": "launched_at",
            "label": "Went live",
            "kind": "datetime"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [
        {
          "object": "marketing_leads.lead_submission",
          "label": "Submissions",
          "params": {
            "lead_source_id": "{lead_source_id}"
          }
        }
      ],
      "lifecycle": [],
      "about": "A lead source is a landing page or lead magnet whose website form sends enquiries to One. Staff add a lead source with \"New lead source\". Each submission to the lead source raises a ticket for the right sales rep. An inactive lead source turns the website form away, so the enquiries are lost.",
      "commands": [
        "marketing_leads.update_lead_source",
        "marketing_leads.create_lead_source",
        "marketing_leads.reactivate_lead_source",
        "marketing_leads.deactivate_lead_source"
      ],
      "features": []
    },
    {
      "id": "marketing_leads.lead_submission",
      "module": "marketing_leads_module",
      "label": "Submission",
      "plural": "Submissions",
      "description": "One form submission to a lead source, what it resolved to and the ticket it raised. List-only.",
      "identifier": {
        "kind": "field",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": {
        "path": "/leads/submissions",
        "key_field": "id",
        "title_field": "email",
        "columns": [
          {
            "field": "first_name",
            "label": "First name",
            "kind": "text"
          },
          {
            "field": "last_name",
            "label": "Last name",
            "kind": "text"
          },
          {
            "field": "email",
            "label": "Email",
            "kind": "text"
          },
          {
            "field": "phone",
            "label": "Phone",
            "kind": "text"
          },
          {
            "field": "company_name",
            "label": "Company",
            "kind": "text"
          },
          {
            "field": "submitted_at",
            "label": "Submitted",
            "kind": "datetime"
          }
        ],
        "filters": [
          {
            "param": "lead_source_id",
            "label": "Lead source ID",
            "kind": "text",
            "options": []
          }
        ],
        "sorts": [
          [
            "submitted_at",
            "Submitted"
          ],
          [
            "first_name",
            "First name"
          ],
          [
            "email",
            "Email"
          ]
        ],
        "searchable": true,
        "queues": []
      },
      "record": null,
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A submission is one form submission to a lead source. The company website sends each submission to One. One then routes the submission to a ticket for the right sales rep. A brand-new contact also becomes a lead. One stores the submission before routing, so a routing failure never loses the submission.",
      "commands": [],
      "features": []
    },
    {
      "id": "marketing_triggers.trigger",
      "module": "marketing_triggers_module",
      "label": "Trigger",
      "plural": "Triggers",
      "description": "A rule that emails a contact once when something happens to them or their account.",
      "identifier": {
        "kind": "field",
        "field": "trigger_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "live",
        "paused"
      ],
      "list": {
        "path": "/marketing/triggers",
        "key_field": "trigger_id",
        "title_field": "name",
        "columns": [
          {
            "field": "trigger_id",
            "label": "Trigger",
            "kind": "identifier"
          },
          {
            "field": "name",
            "label": "Name",
            "kind": "text"
          },
          {
            "field": "description",
            "label": "Rule",
            "kind": "text"
          },
          {
            "field": "status",
            "label": "Status",
            "kind": "text",
            "enum_labels": {
              "live": "Live",
              "paused": "Paused"
            }
          },
          {
            "field": "check_before_sending",
            "label": "Check before sending",
            "kind": "boolean"
          },
          {
            "field": "updated_at",
            "label": "Updated",
            "kind": "datetime"
          }
        ],
        "filters": [
          {
            "param": "status",
            "label": "Status",
            "kind": "multi_enum",
            "options": [
              [
                "live",
                "Live"
              ],
              [
                "paused",
                "Paused"
              ]
            ]
          },
          {
            "param": "has_to_check",
            "label": "Has people to check",
            "kind": "boolean",
            "options": []
          }
        ],
        "sorts": [
          [
            "name",
            "Name"
          ],
          [
            "status",
            "Status"
          ],
          [
            "updated_at",
            "Updated"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "to_check",
            "label": "To check",
            "params": {
              "has_to_check": true
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/marketing/triggers/{trigger_id}",
        "path_param": "trigger_id",
        "title_field": "name",
        "state_fields": [
          "status"
        ],
        "state_labels": {
          "status": {
            "live": "Live",
            "paused": "Paused"
          }
        },
        "facts": [
          {
            "field": "check_before_sending",
            "label": "Check before sending",
            "kind": "boolean"
          },
          {
            "field": "from_name",
            "label": "From",
            "kind": "text"
          },
          {
            "field": "mailchimp_tag",
            "label": "Mailchimp tag",
            "kind": "text"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "marketing_triggers.trigger_firing",
          "label": "Triggered emails",
          "field": "today",
          "key_field": "id",
          "columns": [
            {
              "field": "id",
              "label": "Row",
              "kind": "identifier"
            },
            {
              "field": "account_code",
              "label": "Account",
              "kind": "text"
            },
            {
              "field": "person_id",
              "label": "Person",
              "kind": "identifier"
            },
            {
              "field": "suggested_person_id",
              "label": "Pre-selected contact",
              "kind": "identifier"
            },
            {
              "field": "state",
              "label": "State",
              "kind": "text",
              "enum_labels": {
                "left_out": "Left out",
                "needs_you": "Needs you",
                "not_sent": "Not sent",
                "removed": "Removed",
                "sent": "Sent",
                "to_send": "To send"
              }
            },
            {
              "field": "left_out_reason",
              "label": "Why left out",
              "kind": "text",
              "enum_labels": {
                "already_had_it": "Already had this email",
                "bounced_before": "Bounced before",
                "mailchimp_rejected": "Mailchimp refused the address",
                "marked_inactive": "Marked inactive",
                "no_email": "No email address",
                "opted_out": "Opted out of email",
                "public_site_shopper": "Can't be emailed (public-site shopper)",
                "suppressed": "Suppressed from marketing"
              }
            },
            {
              "field": "campaign_cap_note",
              "label": "3+ campaigns in 30 days",
              "kind": "boolean"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "live": "done",
        "paused": "neutral"
      },
      "commands": [
        "marketing_triggers.approve_trigger_list",
        "marketing_triggers.update_trigger",
        "marketing_triggers.set_trigger_status"
      ],
      "features": []
    },
    {
      "id": "marketing_triggers.trigger_firing",
      "module": "marketing_triggers_module",
      "label": "Triggered email",
      "plural": "Triggered emails",
      "description": "One person (or account) a trigger picked, and what happened.",
      "identifier": {
        "kind": "field",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "to_send",
        "needs_you",
        "left_out",
        "removed",
        "sent",
        "not_sent"
      ],
      "list": null,
      "record": null,
      "parents": [
        "marketing_triggers.trigger"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "to_send": "in_progress",
        "needs_you": "attention",
        "left_out": "neutral",
        "removed": "neutral",
        "sent": "done",
        "not_sent": "stopped"
      },
      "commands": [],
      "features": []
    },
    {
      "id": "needs_attention.alert",
      "module": "needs_attention_module",
      "label": "Operational alert",
      "plural": "Operational alerts",
      "description": "A condition on an order, one of its lines, a purchase order or a customer's stock price that a person should act on.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "open",
        "acknowledged",
        "cleared"
      ],
      "list": {
        "path": "/views/needs-attention",
        "key_field": "id",
        "title_field": "headline",
        "columns": [
          {
            "field": "headline",
            "label": "What",
            "kind": "text"
          },
          {
            "field": "state",
            "label": "State",
            "kind": "text",
            "enum_labels": {
              "acknowledged": "Acknowledged",
              "cleared": "Cleared",
              "open": "Open"
            }
          },
          {
            "field": "group",
            "label": "Kind",
            "kind": "text",
            "enum_labels": {
              "credit": "Credit",
              "delivery": "Delivery",
              "made_line": "Made in-house",
              "payment": "Payment",
              "placement": "Placing with the supplier",
              "price_drift": "Price approval",
              "release": "Release",
              "stock_prices": "Stock prices",
              "supplier": "The supplier",
              "time": "Dates",
              "waiting_on_customer": "Waiting on the customer"
            }
          },
          {
            "field": "subject_ref",
            "label": "About",
            "kind": "text",
            "ref": [
              "orders.order",
              "purchase_orders.purchase_order"
            ]
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text"
          },
          {
            "field": "owner_name",
            "label": "Owner",
            "kind": "text"
          },
          {
            "field": "ticket_with",
            "label": "Ticket with",
            "kind": "text"
          },
          {
            "field": "owner_kind",
            "label": "Goes to",
            "kind": "text",
            "enum_labels": {
              "customer_csr": "Customer's CSR",
              "department": "Department"
            }
          },
          {
            "field": "due_at",
            "label": "Due",
            "kind": "datetime"
          },
          {
            "field": "is_overdue",
            "label": "Past due",
            "kind": "boolean"
          },
          {
            "field": "priority",
            "label": "Priority",
            "kind": "text",
            "enum_labels": {
              "high": "High",
              "low": "Low",
              "normal": "Normal",
              "urgent": "Urgent"
            }
          },
          {
            "field": "detected_at",
            "label": "Detected",
            "kind": "datetime"
          }
        ],
        "filters": [
          {
            "param": "state",
            "label": "State",
            "kind": "multi_enum",
            "options": [
              [
                "open",
                "Open"
              ],
              [
                "acknowledged",
                "Acknowledged"
              ],
              [
                "cleared",
                "Cleared"
              ]
            ],
            "description": "Open and acknowledged when not given."
          },
          {
            "param": "kind",
            "label": "Kind",
            "kind": "enum",
            "options": [
              [
                "waiting_on_customer",
                "Waiting on the customer"
              ],
              [
                "release",
                "Release"
              ],
              [
                "purchase_orders",
                "Purchase orders"
              ],
              [
                "delivery",
                "Delivery"
              ],
              [
                "payment_and_credit",
                "Payment and credit"
              ],
              [
                "made_line",
                "Made in-house"
              ],
              [
                "stock_prices",
                "Stock prices"
              ]
            ]
          },
          {
            "param": "rule_code",
            "label": "Rule code",
            "kind": "text",
            "options": []
          },
          {
            "param": "mine",
            "label": "Mine",
            "kind": "boolean",
            "options": [],
            "description": "Routed to me, covered by me, or on my ticket."
          },
          {
            "param": "my_departments",
            "label": "My departments",
            "kind": "boolean",
            "options": [],
            "description": "Owned by one of my departments, with no person on it."
          },
          {
            "param": "is_overdue",
            "label": "Past due",
            "kind": "boolean",
            "options": []
          },
          {
            "param": "order_no",
            "label": "Order number",
            "kind": "text",
            "options": [],
            "description": "An order's alerts: on it, its lines and its purchase orders."
          },
          {
            "param": "purchase_order_no",
            "label": "Purchase order number",
            "kind": "text",
            "options": []
          }
        ],
        "sorts": [],
        "searchable": true,
        "queues": [
          {
            "id": "mine",
            "label": "Mine",
            "params": {
              "mine": true
            },
            "count": true,
            "default": false
          },
          {
            "id": "my_departments",
            "label": "My departments",
            "params": {
              "my_departments": true
            },
            "count": true,
            "default": false
          },
          {
            "id": "past_due",
            "label": "Past due",
            "params": {
              "is_overdue": true
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/views/needs-attention/{id}",
        "path_param": "id",
        "title_field": "headline",
        "state_fields": [
          "state"
        ],
        "state_labels": {
          "state": {
            "acknowledged": "Acknowledged",
            "cleared": "Cleared",
            "open": "Open"
          }
        },
        "facts": [
          {
            "field": "subject_ref",
            "label": "About",
            "kind": "text",
            "ref": [
              "orders.order",
              "purchase_orders.purchase_order"
            ]
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text"
          },
          {
            "field": "owner_name",
            "label": "Owner",
            "kind": "text"
          },
          {
            "field": "ticket_with",
            "label": "Ticket with",
            "kind": "text"
          },
          {
            "field": "due_at",
            "label": "Due",
            "kind": "datetime"
          },
          {
            "field": "sentence",
            "label": "Why",
            "kind": "text"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "An operational alert flags an order, an order line, a purchase order or a customer's stock price that a person should act on. Every 10 minutes, One checks each operational alert rule that is on. Each new alert raises a ticket for the customer's CSR or for the right department. Most alerts clear when the condition goes. \"Mark done\" is only for an alert that a person has to clear.",
      "state_meanings": {
        "open": "Open: nobody has acknowledged the alert yet.",
        "acknowledged": "Acknowledged: the alert will stay until the condition clears or someone marks the alert done.",
        "cleared": "Cleared: the condition has gone, someone marked the alert done, the rule was turned off or the order was closed."
      },
      "state_tones": {
        "open": "attention",
        "acknowledged": "in_progress",
        "cleared": "done"
      },
      "commands": [
        "needs_attention.acknowledge_alert",
        "needs_attention.mark_alert_done"
      ],
      "features": [
        "orders"
      ]
    },
    {
      "id": "needs_attention.rule",
      "module": "needs_attention_module",
      "label": "Operational alert rule",
      "plural": "Operational alert rules",
      "description": "One condition worth a person's attention, its threshold and who it goes to; admin-editable.",
      "identifier": {
        "kind": "field",
        "field": "code",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "on",
        "off"
      ],
      "list": {
        "path": "/needs-attention/rules",
        "key_field": "code",
        "title_field": "label",
        "columns": [
          {
            "field": "code",
            "label": "Code",
            "kind": "text"
          },
          {
            "field": "label",
            "label": "Rule",
            "kind": "text"
          },
          {
            "field": "group",
            "label": "Group",
            "kind": "text",
            "enum_labels": {
              "credit": "Credit",
              "delivery": "Delivery",
              "made_line": "Made in-house",
              "payment": "Payment",
              "placement": "Placing with the supplier",
              "price_drift": "Price approval",
              "release": "Release",
              "stock_prices": "Stock prices",
              "supplier": "The supplier",
              "time": "Dates",
              "waiting_on_customer": "Waiting on the customer"
            }
          },
          {
            "field": "state",
            "label": "On",
            "kind": "text",
            "enum_labels": {
              "off": "Off",
              "on": "On"
            }
          },
          {
            "field": "owner_label",
            "label": "Goes to",
            "kind": "text"
          },
          {
            "field": "due_working_days",
            "label": "Due, in working days",
            "kind": "number"
          },
          {
            "field": "due_set_by_time",
            "label": "Due set by Time",
            "kind": "boolean"
          },
          {
            "field": "due_label",
            "label": "Due",
            "kind": "text"
          },
          {
            "field": "priority",
            "label": "Priority",
            "kind": "text",
            "enum_labels": {
              "high": "High",
              "low": "Low",
              "normal": "Normal",
              "urgent": "Urgent"
            }
          },
          {
            "field": "threshold_label",
            "label": "After",
            "kind": "text"
          }
        ],
        "filters": [
          {
            "param": "group",
            "label": "Group",
            "kind": "enum",
            "options": [
              [
                "waiting_on_customer",
                "Waiting on the customer"
              ],
              [
                "release",
                "Release"
              ],
              [
                "placement",
                "Placing with the supplier"
              ],
              [
                "supplier",
                "The supplier"
              ],
              [
                "delivery",
                "Delivery"
              ],
              [
                "payment",
                "Payment"
              ],
              [
                "price_drift",
                "Price approval"
              ],
              [
                "credit",
                "Credit"
              ],
              [
                "made_line",
                "Made in-house"
              ],
              [
                "time",
                "Dates"
              ],
              [
                "stock_prices",
                "Stock prices"
              ]
            ],
            "description": "The groups the queue filters by (spec §4): the assessment's eight detectors, E's ninth, then Time's."
          },
          {
            "param": "is_enabled",
            "label": "On",
            "kind": "boolean",
            "options": []
          }
        ],
        "sorts": [],
        "searchable": true,
        "queues": []
      },
      "record": {
        "path": "/needs-attention/rules/{code}",
        "path_param": "code",
        "title_field": "label",
        "state_fields": [
          "state"
        ],
        "state_labels": {
          "state": {
            "off": "Off",
            "on": "On"
          }
        },
        "facts": [
          {
            "field": "group",
            "label": "Group",
            "kind": "text",
            "enum_labels": {
              "credit": "Credit",
              "delivery": "Delivery",
              "made_line": "Made in-house",
              "payment": "Payment",
              "placement": "Placing with the supplier",
              "price_drift": "Price approval",
              "release": "Release",
              "stock_prices": "Stock prices",
              "supplier": "The supplier",
              "time": "Dates",
              "waiting_on_customer": "Waiting on the customer"
            }
          },
          {
            "field": "owner_label",
            "label": "Goes to",
            "kind": "text"
          },
          {
            "field": "due_working_days",
            "label": "Due, in working days",
            "kind": "number"
          },
          {
            "field": "priority",
            "label": "Priority",
            "kind": "text",
            "enum_labels": {
              "high": "High",
              "low": "Low",
              "normal": "Normal",
              "urgent": "Urgent"
            }
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "An operational alert rule is one condition worth a person's attention, with the threshold and who the alert goes to. One adds any missing rules when One is updated, without overwriting an admin's edit. Admins change a rule with \"Edit rule\".",
      "state_meanings": {
        "on": "On: the rule will raise alerts on every sweep.",
        "off": "Off: the rule's open alerts will clear."
      },
      "state_tones": {
        "on": "done",
        "off": "neutral"
      },
      "commands": [
        "needs_attention.update_attention_rule"
      ],
      "features": [
        "orders"
      ]
    },
    {
      "id": "order_channel_brandhub.intake",
      "module": "order_channel_brandhub_module",
      "label": "BrandHub intake",
      "plural": "BrandHub intake",
      "description": "One BrandHub order as One received it, with what One decided and why.",
      "identifier": {
        "kind": "field",
        "field": "channel_ref",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "order_created",
        "would_accept",
        "declined"
      ],
      "list": {
        "path": "/views/brandhub-intakes",
        "key_field": "channel_ref",
        "title_field": "customer_name",
        "columns": [
          {
            "field": "channel_ref",
            "label": "BrandHub order",
            "kind": "identifier"
          },
          {
            "field": "received_at",
            "label": "Received",
            "kind": "datetime"
          },
          {
            "field": "checked_at",
            "label": "Checked",
            "kind": "datetime"
          },
          {
            "field": "checked_by",
            "label": "Checked by",
            "kind": "text"
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text",
            "ref": "customers.customer",
            "ref_id_field": "cis_customer_code"
          },
          {
            "field": "line_count",
            "label": "Lines",
            "kind": "number"
          },
          {
            "field": "outcome",
            "label": "Decision",
            "kind": "state",
            "enum_labels": {
              "declined": "Declined",
              "order_created": "Order created",
              "would_accept": "Would accept"
            }
          },
          {
            "field": "reason",
            "label": "Reason",
            "kind": "text",
            "ref": "orders.order"
          }
        ],
        "filters": [
          {
            "param": "customer",
            "label": "Customer",
            "kind": "text",
            "options": [],
            "description": "Only this customer's intakes: part of the customer's name, the customer number (CUS-…) or BrandHub's customer code."
          },
          {
            "param": "outcome",
            "label": "Decision",
            "kind": "enum",
            "options": [
              [
                "order_created",
                "Order created"
              ],
              [
                "would_accept",
                "Would accept"
              ],
              [
                "declined",
                "Declined"
              ]
            ],
            "description": "Only intakes with this decision: an order created, one One would accept, or one declined."
          },
          {
            "param": "reason",
            "label": "Reason",
            "kind": "multi_enum",
            "options": [
              [
                "customer_unknown",
                "Customer not in One"
              ],
              [
                "customer_not_enabled",
                "Customer not enabled"
              ],
              [
                "customer_not_live",
                "Customer not live"
              ],
              [
                "card_payment_not_supported_yet",
                "Paid by card"
              ],
              [
                "no_delivery_address",
                "No delivery address"
              ],
              [
                "orders_refused",
                "Order rules refused it"
              ],
              [
                "internal_deadline",
                "Took too long"
              ],
              [
                "template_type_unknown",
                "Unknown item type"
              ],
              [
                "stock_shadow_not_started",
                "Stock shadow not started"
              ],
              [
                "stock_not_cut_over",
                "Stock still in Tharstern"
              ],
              [
                "no_stock_item",
                "No stock item"
              ],
              [
                "stock_item_not_linked_to_item",
                "Stock not linked to the item"
              ],
              [
                "print_file_not_from_chili",
                "Print file not from CHILI"
              ],
              [
                "existing_estimate_not_supported_yet",
                "Priced from a Tharstern estimate"
              ],
              [
                "no_catalogue_item",
                "Needs a catalogue item"
              ],
              [
                "express_answers_need_guidance",
                "Express answers need guidance"
              ],
              [
                "catalogue_item_not_reviewed",
                "Catalogue item in review"
              ],
              [
                "spec_body_invalid",
                "Estimate request unreadable"
              ],
              [
                "spec_pt_retired",
                "Product type retired"
              ],
              [
                "order_spec_differs_from_catalogue_item",
                "Specification differs"
              ],
              [
                "vat_rate_not_allowed",
                "VAT rate not allowed"
              ],
              [
                "exceeds_line_cap",
                "Over the order line limit"
              ],
              [
                "no_supplier_option",
                "No supplier can make it"
              ]
            ],
            "description": "Only declined intakes whose first problem is any of these reasons."
          },
          {
            "param": "line_kind",
            "label": "Line kind",
            "kind": "enum",
            "options": [
              [
                "template",
                "Template"
              ],
              [
                "existing_estimate",
                "Existing estimate"
              ],
              [
                "upload",
                "Upload"
              ],
              [
                "express",
                "Express"
              ],
              [
                "unknown",
                "Unknown"
              ]
            ],
            "description": "Only intakes with at least one line of this kind."
          },
          {
            "param": "order_kind",
            "label": "Order kind",
            "kind": "enum",
            "options": [
              [
                "from_stock",
                "From stock"
              ],
              [
                "print_on_demand",
                "Print on demand"
              ],
              [
                "express",
                "Express"
              ]
            ],
            "description": "Only intakes with at least one line of this kind: held stock called off (a Stock template not printed on demand), an Express request, or anything else, printed for the order."
          },
          {
            "param": "since_arrival",
            "label": "Since it arrived",
            "kind": "enum",
            "options": [
              [
                "changed",
                "Changed since it arrived"
              ],
              [
                "now_accepted",
                "Now accepted"
              ],
              [
                "now_declined",
                "Now declined"
              ]
            ],
            "description": "Only intakes whose shown decision differs from the one made when the order arrived: any change, declined then and accepted now, or accepted then and declined now. Would accept and order created both accept."
          },
          {
            "param": "mode",
            "label": "Mode",
            "kind": "enum",
            "options": [
              [
                "live",
                "Live"
              ],
              [
                "shadow",
                "Shadow"
              ]
            ],
            "description": "Only intakes decided live, or only those decided as a shadow copy. Absent: both."
          },
          {
            "param": "checked_from",
            "label": "Checked from",
            "kind": "date",
            "options": [],
            "description": "Intakes whose shown decision was made on or after this day (YYYY-MM-DD)."
          },
          {
            "param": "checked_to",
            "label": "Checked to",
            "kind": "date",
            "options": [],
            "description": "Intakes whose shown decision was made on or before this day (YYYY-MM-DD)."
          },
          {
            "param": "received_from",
            "label": "Received from",
            "kind": "date",
            "options": [],
            "description": "Intakes received on or after this day (YYYY-MM-DD)."
          },
          {
            "param": "received_to",
            "label": "Received to",
            "kind": "date",
            "options": [],
            "description": "Intakes received on or before this day (YYYY-MM-DD)."
          }
        ],
        "sorts": [
          [
            "received_at",
            "Received"
          ],
          [
            "checked_at",
            "Checked"
          ],
          [
            "channel_ref",
            "BrandHub order"
          ],
          [
            "customer_name",
            "Customer"
          ],
          [
            "line_count",
            "Lines"
          ],
          [
            "outcome",
            "Decision"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "declined",
            "label": "Declined",
            "params": {
              "outcome": "declined"
            },
            "count": true,
            "default": false
          },
          {
            "id": "order_created",
            "label": "Order created",
            "params": {
              "outcome": "order_created"
            },
            "count": true,
            "default": false
          },
          {
            "id": "needs_catalogue_item",
            "label": "Needs a catalogue item",
            "params": {
              "reason": [
                "no_catalogue_item",
                "catalogue_item_not_reviewed",
                "order_spec_differs_from_catalogue_item",
                "express_answers_need_guidance"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "no_supplier",
            "label": "No supplier",
            "params": {
              "reason": [
                "no_supplier_option"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "stock_shadow_not_started",
            "label": "Stock shadow not started",
            "params": {
              "reason": [
                "stock_shadow_not_started"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "stock_in_tharstern",
            "label": "Stock still in Tharstern",
            "params": {
              "reason": [
                "stock_not_cut_over"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "changed_since_arrival",
            "label": "Changed since it arrived",
            "params": {
              "since_arrival": "changed"
            },
            "count": true,
            "default": false
          },
          {
            "id": "now_accepted",
            "label": "Now accepted",
            "params": {
              "since_arrival": "now_accepted"
            },
            "count": true,
            "default": false
          },
          {
            "id": "now_declined",
            "label": "Now declined",
            "params": {
              "since_arrival": "now_declined"
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/views/brandhub-intake/{channel_ref}",
        "path_param": "channel_ref",
        "title_field": "customer_name",
        "state_fields": [
          "outcome",
          "mode"
        ],
        "state_labels": {
          "outcome": {
            "declined": "Declined",
            "order_created": "Order created",
            "would_accept": "Would accept"
          },
          "mode": {
            "live": "Live",
            "shadow": "Shadow"
          }
        },
        "facts": [
          {
            "field": "reason",
            "label": "Reason",
            "kind": "text"
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text",
            "ref": "customers.customer",
            "ref_id_field": "cis_customer_code"
          },
          {
            "field": "received_at",
            "label": "Received",
            "kind": "datetime"
          },
          {
            "field": "checked_at",
            "label": "Checked",
            "kind": "datetime"
          },
          {
            "field": "checked_by",
            "label": "Checked by",
            "kind": "text"
          },
          {
            "field": "brandhub_placed_at",
            "label": "BrandHub placed",
            "kind": "datetime"
          },
          {
            "field": "payment_method",
            "label": "Payment",
            "kind": "text"
          },
          {
            "field": "order_no",
            "label": "Order",
            "kind": "text",
            "ref": "orders.order"
          },
          {
            "field": "order_on_arrival",
            "label": "Order made on arrival",
            "kind": "text"
          },
          {
            "field": "placed_for_real_at",
            "label": "Placed for real",
            "kind": "datetime"
          },
          {
            "field": "lines_summary",
            "label": "Lines",
            "kind": "text"
          }
        ],
        "timeline_path": "/views/brandhub-intake/{channel_ref}/timeline",
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "order_channel_brandhub.intake_line",
          "label": "Lines",
          "field": "lines",
          "key_field": "line_no",
          "columns": [
            {
              "field": "line_no",
              "label": "Line",
              "kind": "number"
            },
            {
              "field": "product_code",
              "label": "Product code",
              "kind": "text"
            },
            {
              "field": "item_category",
              "label": "Kind",
              "kind": "state",
              "enum_labels": {
                "existing_estimate": "Existing estimate",
                "express": "Express",
                "template": "Template",
                "unknown": "Unknown",
                "upload": "Upload"
              }
            },
            {
              "field": "quantity",
              "label": "Quantity",
              "kind": "number"
            },
            {
              "field": "item_name",
              "label": "Catalogue item or item read",
              "kind": "text",
              "ref": "catalogue.catalogue_item",
              "ref_id_field": "catalogue_item_id"
            },
            {
              "field": "supplier_name",
              "label": "Supplier",
              "kind": "text",
              "ref": "suppliers.supplier",
              "ref_id_field": "supplier_no"
            },
            {
              "field": "cost_pence",
              "label": "Cost",
              "kind": "money"
            },
            {
              "field": "outcome",
              "label": "Outcome",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [],
      "lifecycle": [],
      "about": "A BrandHub intake is one BrandHub order as One received it, with what One decided and why. One decides each order against reviewed catalogue items, stock and suppliers. A declined order stays with Tharstern. Where One made an order, the intake links to it. In shadow the intake shows One's latest decision on each order, from when it arrived or a later replay or \"Check again\"; Checked says when. Earlier decisions stay on record. \"Check again\" decides the stored order again and records the result; it never creates an order. \"Place for real…\" is temporary, removed when BrandHub orders go live: it lets one shadow order's purchase orders go to the supplier when its lines are released.",
      "state_meanings": {
        "order_created": "Order created: the order lines will follow the usual order steps.",
        "would_accept": "Would accept: One would create the order, but this environment isn't creating orders."
      },
      "state_tones": {
        "order_created": "done",
        "would_accept": "done",
        "declined": "stopped"
      },
      "commands": [
        "order_channel_brandhub.check_brandhub_intake",
        "order_channel_brandhub.place_brandhub_shadow_order_for_real"
      ],
      "features": [
        "brandhub_channel"
      ]
    },
    {
      "id": "order_channel_brandhub.intake_line",
      "module": "order_channel_brandhub_module",
      "label": "Line",
      "plural": "Lines",
      "description": "One BrandHub item on an intake, with its catalogue item, the supplier and cost One chose, and what became of it.",
      "identifier": {
        "kind": "field",
        "field": "line_no",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "order_channel_brandhub.intake"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": []
    },
    {
      "id": "order_channel_promo_office.held_up_item",
      "module": "order_channel_promo_office_module",
      "label": "Held up item",
      "plural": "Held up items",
      "description": "A Promo Office tender or Indent the channel could not take, with why, the fix and who owns it.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "open",
        "resolved",
        "dismissed"
      ],
      "list": null,
      "record": {
        "path": "/order-channel-promo-office/held-up-items/{id}",
        "path_param": "id",
        "title_field": "summary",
        "state_fields": [
          "state"
        ],
        "state_labels": {
          "state": {
            "dismissed": "Dismissed",
            "open": "Open",
            "resolved": "Resolved"
          }
        },
        "facts": [
          {
            "field": "document_number",
            "label": "Promo Office number",
            "kind": "text"
          },
          {
            "field": "owner",
            "label": "Owner",
            "kind": "text"
          },
          {
            "field": "held_up_since",
            "label": "Held up since",
            "kind": "datetime"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "open": "attention",
        "resolved": "done",
        "dismissed": "neutral"
      },
      "commands": [
        "order_channel_promo_office.resolve_held_up_item"
      ],
      "features": [
        "promo_office_channel"
      ]
    },
    {
      "id": "orders.delivery_line",
      "module": "orders_module",
      "label": "Delivery line",
      "plural": "Delivery lines",
      "description": "How many of a line's version go to one place, an address or one of our warehouses.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "orders.order"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [
        "orders.record_delivery_line_delivered",
        "orders.record_delivery_line_received",
        "orders.tell_customer_delivery_problem"
      ],
      "features": []
    },
    {
      "id": "orders.order",
      "module": "orders_module",
      "label": "Order",
      "plural": "Orders",
      "description": "A customer's order, its lines, deliveries and payment, from acceptance to delivery.",
      "identifier": {
        "kind": "cis_number",
        "field": "order_no",
        "prefix": "ORD",
        "pattern": "^ORD-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}$",
        "line_pattern": "^ORD-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}\\.[0-9]{2,}$"
      },
      "states": [
        "ordered",
        "in_production",
        "despatched",
        "delivered",
        "received_into_stock",
        "cancelled"
      ],
      "list": {
        "path": "/views/order-list",
        "key_field": "order_no",
        "title_field": "title",
        "columns": [
          {
            "field": "order_no",
            "label": "Order",
            "kind": "identifier"
          },
          {
            "field": "kind",
            "label": "Kind",
            "kind": "state",
            "enum_labels": {
              "cis": "One",
              "tharstern": "Tharstern"
            }
          },
          {
            "field": "tharstern_sales_order_no",
            "label": "Tharstern sales order number",
            "kind": "identifier"
          },
          {
            "field": "tharstern_job_no",
            "label": "Tharstern job number",
            "kind": "identifier"
          },
          {
            "field": "title",
            "label": "What",
            "kind": "text"
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text",
            "ref": "customers.customer",
            "ref_id_field": "cis_customer_code"
          },
          {
            "field": "trading_company",
            "label": "Trading company",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "trading_company_code"
          },
          {
            "field": "status",
            "label": "Status",
            "kind": "state",
            "enum_labels": {
              "cancelled": "Cancelled",
              "delivered": "Delivered",
              "despatched": "Despatched",
              "in_production": "In production",
              "invoiced": "Invoiced",
              "on_order": "On order",
              "ordered": "Ordered",
              "received_into_stock": "Received into stock",
              "supplied_from_stock": "Supplied from stock"
            }
          },
          {
            "field": "health",
            "label": "Health",
            "kind": "state",
            "enum_labels": {
              "at_risk": "At risk",
              "late": "Late"
            }
          },
          {
            "field": "required_on",
            "label": "Needed by",
            "kind": "date"
          },
          {
            "field": "value_pence",
            "label": "Value",
            "kind": "money"
          },
          {
            "field": "ordered_at",
            "label": "Placed",
            "kind": "datetime"
          }
        ],
        "filters": [
          {
            "param": "customer_id",
            "label": "Customer",
            "kind": "entity",
            "options": [],
            "object": "customers.customer",
            "entity_value": "id",
            "description": "Only this customer's orders, by the customer's id (the id on the customer's Customers list row and record): the Orders list on the customer record. Both kinds."
          },
          {
            "param": "channel",
            "label": "Channel",
            "kind": "enum",
            "options": [
              [
                "internal_portal",
                "Internal portal"
              ],
              [
                "customer_portal",
                "Customer portal"
              ],
              [
                "brandhub",
                "BrandHub"
              ],
              [
                "email_agent",
                "Email agent"
              ],
              [
                "whatsapp_agent",
                "WhatsApp agent"
              ],
              [
                "mcp_agent",
                "MCP agent"
              ],
              [
                "legacy_xml",
                "Legacy XML"
              ],
              [
                "replenishment",
                "Replenishment"
              ],
              [
                "promo_office",
                "Promo Office"
              ]
            ],
            "description": "Only orders that came in through this channel: the staff portal, the customer portal, BrandHub, Promo Office, an agent, legacy XML or replenishment. One orders only: setting this filter will leave Tharstern orders out.",
            "own_kind_only": true
          },
          {
            "param": "trading_company_code",
            "label": "Trading company",
            "kind": "entity",
            "options": [],
            "object": "trading_companies.trading_company",
            "description": "Only orders that belong to the trading company with this code, such as POL. One orders only: setting this filter will leave Tharstern orders out.",
            "own_kind_only": true
          },
          {
            "param": "kind",
            "label": "Kind",
            "kind": "enum",
            "options": [
              [
                "cis",
                "One"
              ],
              [
                "tharstern",
                "Tharstern"
              ]
            ],
            "description": "Which orders: 'cis' for One's own orders, 'tharstern' for Tharstern orders (read-only history since 3 June 2025, shown while the Tharstern history feature is on). Absent: both."
          },
          {
            "param": "ordered_from",
            "label": "Placed from",
            "kind": "date",
            "options": [],
            "description": "Orders placed on or after this day (YYYY-MM-DD); both kinds."
          },
          {
            "param": "ordered_to",
            "label": "Placed to",
            "kind": "date",
            "options": [],
            "description": "Orders placed on or before this day (YYYY-MM-DD); both kinds."
          },
          {
            "param": "route",
            "label": "Route",
            "kind": "multi_enum",
            "options": [
              [
                "buy",
                "buy"
              ],
              [
                "make",
                "make"
              ],
              [
                "from_stock",
                "from_stock"
              ]
            ],
            "description": "Orders with at least one line on any of these routes. One orders only: setting this filter will leave Tharstern orders out.",
            "own_kind_only": true
          },
          {
            "param": "supplier_id",
            "label": "Supplier ID",
            "kind": "multi_enum",
            "options": [],
            "description": "Orders with at least one bought line placed with, or priced on, any of these CIS suppliers (the live purchase order's supplier, else the line's). One orders only: setting this filter will leave Tharstern orders out.",
            "own_kind_only": true
          },
          {
            "param": "status",
            "label": "Status",
            "kind": "enum",
            "options": [
              [
                "ordered",
                "Ordered"
              ],
              [
                "in_production",
                "In production"
              ],
              [
                "despatched",
                "Despatched"
              ],
              [
                "delivered",
                "Delivered"
              ],
              [
                "received_into_stock",
                "Received into stock"
              ],
              [
                "cancelled",
                "Cancelled"
              ]
            ],
            "description": "Only orders with this status. One orders only: setting this filter will leave Tharstern orders out.",
            "own_kind_only": true
          },
          {
            "param": "health",
            "label": "Health",
            "kind": "enum",
            "options": [
              [
                "late",
                "Late"
              ],
              [
                "at_risk",
                "At risk"
              ]
            ],
            "description": "Only orders with this health. One orders only: setting this filter will leave Tharstern orders out.",
            "own_kind_only": true
          },
          {
            "param": "files_owed",
            "label": "Files owed",
            "kind": "boolean",
            "options": [],
            "description": "True: orders with an order line version still owing artwork files. False: orders with none. One orders only: setting this filter will leave Tharstern orders out.",
            "own_kind_only": true
          },
          {
            "param": "proofs_waiting",
            "label": "Proofs waiting",
            "kind": "boolean",
            "options": [],
            "description": "True: orders with a proof waiting for the customer's decision. False: orders with none. One orders only: setting this filter will leave Tharstern orders out.",
            "own_kind_only": true
          },
          {
            "param": "stock_call_offs",
            "label": "Stock call-offs",
            "kind": "enum",
            "options": [
              [
                "include",
                "Include"
              ],
              [
                "leave_out",
                "Leave out"
              ]
            ],
            "description": "Tharstern orders only: Leave out will drop every Tharstern order whose every line was stock called off. One orders stay. Absent means Include.",
            "default": "include",
            "included_kind_only": true
          }
        ],
        "sorts": [
          [
            "ordered_at",
            "Placed"
          ],
          [
            "order_no",
            "Order"
          ],
          [
            "required_on",
            "Needed by"
          ],
          [
            "value_pence",
            "Value"
          ],
          [
            "customer_name",
            "Customer"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "late",
            "label": "Late",
            "params": {
              "health": "late"
            },
            "count": true,
            "default": false
          },
          {
            "id": "at_risk",
            "label": "At risk",
            "params": {
              "health": "at_risk"
            },
            "count": true,
            "default": false
          },
          {
            "id": "files_owed",
            "label": "Files owed",
            "params": {
              "files_owed": true
            },
            "count": true,
            "default": false
          },
          {
            "id": "proofs_waiting",
            "label": "Proofs waiting",
            "params": {
              "proofs_waiting": true
            },
            "count": true,
            "default": false
          }
        ],
        "includes": {
          "object": "orders.tharstern_order",
          "kind": "tharstern",
          "own_kind": "cis"
        }
      },
      "record": {
        "path": "/views/order-360/{order_no}",
        "path_param": "order_no",
        "title_field": "title",
        "state_fields": [
          "status",
          "health"
        ],
        "state_labels": {
          "status": {
            "cancelled": "Cancelled",
            "delivered": "Delivered",
            "despatched": "Despatched",
            "in_production": "In production",
            "ordered": "Ordered",
            "received_into_stock": "Received into stock"
          },
          "health": {
            "at_risk": "At risk",
            "late": "Late"
          }
        },
        "facts": [
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text",
            "ref": "customers.customer",
            "ref_id_field": "cis_customer_code"
          },
          {
            "field": "trading_company",
            "label": "Trading company",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "trading_company_code"
          },
          {
            "field": "customer_order_number",
            "label": "Customer PO",
            "kind": "text"
          },
          {
            "field": "contact_name",
            "label": "Contact",
            "kind": "text"
          },
          {
            "field": "project_no",
            "label": "Project",
            "kind": "identifier"
          },
          {
            "field": "review_before_release",
            "label": "Review before release",
            "kind": "boolean"
          },
          {
            "field": "part_deliveries",
            "label": "Part deliveries",
            "kind": "text",
            "enum_labels": {
              "accept": "Accepted",
              "do_not_accept": "Not accepted"
            },
            "detail": {
              "field": "part_deliveries_note",
              "label": "Part deliveries note",
              "kind": "text"
            }
          },
          {
            "field": "delivery_updates_to",
            "label": "Delivery updates go to",
            "kind": "text"
          },
          {
            "field": "deliver_together",
            "label": "Deliver together",
            "kind": "boolean"
          },
          {
            "field": "value_pence",
            "label": "Net",
            "kind": "money"
          },
          {
            "field": "vat_pence",
            "label": "VAT",
            "kind": "money"
          },
          {
            "field": "gross_pence",
            "label": "Gross",
            "kind": "money"
          },
          {
            "field": "required_on",
            "label": "Needed by",
            "kind": "date"
          },
          {
            "field": "came_in_through",
            "label": "Came in through",
            "kind": "text",
            "ref": "order_channel_brandhub.intake",
            "ref_id_field": "brandhub_order_ref"
          }
        ],
        "fact_groups": [
          {
            "label": null,
            "fields": [
              "value_pence",
              "vat_pence",
              "gross_pence"
            ]
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [
          "tracking_events"
        ],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "orders.order_line",
          "label": "Order lines",
          "field": "lines",
          "key_field": "line_ref",
          "columns": [
            {
              "field": "line_no",
              "label": "Line",
              "kind": "text"
            },
            {
              "field": "title",
              "label": "Description",
              "kind": "text"
            },
            {
              "field": "quantity",
              "label": "Quantity",
              "kind": "number"
            },
            {
              "field": "price_pence",
              "label": "Net",
              "kind": "money"
            },
            {
              "field": "vat_amount_label",
              "label": "VAT",
              "kind": "text",
              "detail": {
                "field": "vat_rate_label",
                "label": "VAT rate",
                "kind": "text"
              }
            },
            {
              "field": "gross_pence",
              "label": "Gross",
              "kind": "money"
            },
            {
              "field": "purchase_order_no",
              "label": "Purchase order",
              "kind": "text",
              "ref": "purchase_orders.purchase_order"
            },
            {
              "field": "stock_parts_label",
              "label": "Stock",
              "kind": "text"
            },
            {
              "field": "start_number_label",
              "label": "Start number",
              "kind": "text"
            },
            {
              "field": "number_range",
              "label": "Numbers",
              "kind": "text"
            },
            {
              "field": "start_number_set_by",
              "label": "Set by",
              "kind": "text"
            },
            {
              "field": "versions_split",
              "label": "Versions",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null,
          "totals": {
            "price_pence": {
              "field": "value_pence",
              "label": "Net",
              "kind": "money"
            },
            "vat_amount_label": {
              "field": "vat_pence",
              "label": "VAT",
              "kind": "money"
            },
            "gross_pence": {
              "field": "gross_pence",
              "label": "Gross",
              "kind": "money"
            }
          }
        },
        {
          "object": "orders.delivery_line",
          "label": "Delivery lines",
          "field": "delivery_lines",
          "key_field": "id",
          "columns": [
            {
              "field": "line",
              "label": "Line",
              "kind": "text"
            },
            {
              "field": "destination",
              "label": "Going to",
              "kind": "text"
            },
            {
              "field": "quantity",
              "label": "Quantity",
              "kind": "number"
            },
            {
              "field": "status",
              "label": "Status",
              "kind": "text"
            },
            {
              "field": "arriving",
              "label": "Arriving",
              "kind": "text"
            },
            {
              "field": "predicted",
              "label": "Predicted",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null,
          "title_field": "heading"
        },
        {
          "object": "finance_sales_ledger.proforma",
          "label": "Pro-formas",
          "field": "proformas",
          "key_field": "proforma_no",
          "columns": [
            {
              "field": "proforma_no",
              "label": "Pro-forma",
              "kind": "identifier"
            },
            {
              "field": "state",
              "label": "State",
              "kind": "state",
              "enum_labels": {
                "expired": "Expired unpaid",
                "issued": "Awaiting payment",
                "paid": "Paid",
                "part_paid": "Part paid",
                "superseded": "Replaced by a new pro-forma",
                "voided": "Voided: the order was cancelled"
              }
            },
            {
              "field": "gross_pence",
              "label": "Gross",
              "kind": "money"
            },
            {
              "field": "outstanding_pence",
              "label": "Still owed",
              "kind": "money"
            },
            {
              "field": "valid_until",
              "label": "Valid until",
              "kind": "date"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "delivery_tracking.tracking_event",
          "label": "Courier tracking",
          "field": "tracking_events",
          "key_field": "id",
          "columns": [
            {
              "field": "consignment",
              "label": "Consignment",
              "kind": "text"
            },
            {
              "field": "status",
              "label": "Status",
              "kind": "text"
            },
            {
              "field": "occurred_at",
              "label": "When",
              "kind": "datetime"
            },
            {
              "field": "location",
              "label": "Where",
              "kind": "text"
            },
            {
              "field": "detail",
              "label": "Details",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "artwork_preparation.artwork",
          "label": "Artwork",
          "field": "artworks",
          "key_field": "artwork_id",
          "columns": [
            {
              "field": "title",
              "label": "Artwork",
              "kind": "text"
            },
            {
              "field": "state",
              "label": "Stage",
              "kind": "state",
              "enum_labels": {
                "approved": "Approved",
                "cant_print": "Can't print",
                "changes_asked": "Sent back",
                "checked": "Checked",
                "checking": "Checking",
                "files_owed": "Files owed",
                "held": "Held",
                "proof_out": "Proof out",
                "tool_failed": "Tool failed"
              }
            },
            {
              "field": "status",
              "label": "Status",
              "kind": "text"
            },
            {
              "field": "revision_no",
              "label": "Rev",
              "kind": "number"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [
        {
          "object": "purchase_orders.purchase_order",
          "label": "Purchase orders",
          "params": {
            "order_no": "{order_no}"
          }
        },
        {
          "object": "finance_sales_ledger.invoice",
          "label": "Invoices",
          "params": {
            "order_no": "{order_no}"
          }
        },
        {
          "object": "comms_ingest.email",
          "label": "Emails",
          "params": {
            "tag_object": "orders.order",
            "tag_key": "{order_no}"
          },
          "secondary": true
        }
      ],
      "lifecycle": [
        {
          "id": "quotes.quoted",
          "label": "Quote",
          "module": "quotes_module",
          "description": "On an order made from a quote, that quote, accepted, or the draft quote placed as the order."
        },
        {
          "id": "orders.order_placed",
          "label": "Order",
          "module": "orders_module",
          "description": "The order was placed, through which channel and by whom."
        },
        {
          "id": "artwork_preparation.order_artwork",
          "label": "Artwork",
          "module": "artwork_preparation_module",
          "description": "Each order line version's files supplied, checked and approved, by proof or on the customer's word."
        },
        {
          "id": "orders.payment",
          "label": "Payment",
          "module": "orders_module",
          "description": "The order's payment requirement, by card, account or pro-forma, and its approve-by date."
        },
        {
          "id": "orders.release",
          "label": "Release",
          "module": "orders_module",
          "description": "Each line released to its supplier, to production or to stock."
        },
        {
          "id": "orders.production",
          "label": "Production",
          "module": "orders_module",
          "description": "Each line made, bought or picked."
        },
        {
          "id": "orders.delivery",
          "label": "Delivery",
          "module": "orders_module",
          "description": "Each delivery line despatched and delivered, or received into stock."
        },
        {
          "id": "finance_sales_ledger.order_invoice",
          "label": "Invoice",
          "module": "finance_sales_ledger_module",
          "description": "The order's lines invoiced as each falls due, or held while finance clears what blocks one."
        }
      ],
      "about": "An order is what the customer has agreed to buy, from acceptance to delivery. One creates the order when the customer accepts a quote, or at checkout with \"Place order\". Each order line waits for payment, approved proofs and a print-ready file before Release. Release sends the order line to the supplier or to production. The sales ledger will invoice the order lines when the order lines fall due. Each order line shows its net, VAT and gross, and the order shows their totals, cancelled order lines left out.",
      "state_meanings": {
        "ordered": "Ordered: no order line is on a purchase order yet.",
        "in_production": "In production: at least one order line is on a live purchase order.",
        "despatched": "Despatched: every delivery line is on the way or has arrived.",
        "delivered": "Delivered: every delivery line has arrived.",
        "received_into_stock": "Received into stock: every delivery line went into one of our warehouses.",
        "cancelled": "Cancelled: every order line is cancelled."
      },
      "state_tones": {
        "ordered": "in_progress",
        "in_production": "in_progress",
        "despatched": "in_progress",
        "delivered": "done",
        "received_into_stock": "done",
        "cancelled": "stopped",
        "at_risk": "attention",
        "late": "stopped"
      },
      "commands": [
        "orders.release_order_lines",
        "catalogue.add_catalogue_item_to_order",
        "catalogue.add_bespoke_line_to_order",
        "catalogue.add_service_extra_to_order",
        "orders.add_kits_to_order",
        "orders.log_order_contact",
        "orders.set_order_contact",
        "orders.record_customer_order_number",
        "orders.set_order_review_before_release",
        "orders.change_order_payment_method",
        "orders.change_order_trading_company",
        "orders.start_card_payment",
        "orders.agree_delivery_price",
        "finance_sales_ledger.set_invoice_line_vat_treatment",
        "orders.set_order_part_deliveries",
        "orders.set_order_delivery_updates",
        "orders.set_order_deliver_together",
        "orders.book_courier_despatch",
        "orders.record_despatched_together",
        "catalogue.change_supplier_for_order_lines",
        "orders.add_order_to_project",
        "catalogue.order_again_to_draft_quote",
        "orders.resend_order_email",
        "orders.cancel_order"
      ],
      "features": [
        "orders"
      ]
    },
    {
      "id": "orders.order_line",
      "module": "orders_module",
      "label": "Order line",
      "plural": "Order lines",
      "description": "One product or service on an order, with its versions and where each goes.",
      "identifier": {
        "kind": "field",
        "field": "line_ref",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "orders.order"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [
        "orders.hold_order_line_release",
        "orders.place_held_line_now",
        "catalogue.change_order_line_quantity",
        "catalogue.change_order_line_deliveries",
        "orders.confirm_order_line_date",
        "orders.repromise_order_line",
        "orders.record_made_line_despatched",
        "orders.record_kit_drops_despatched",
        "orders.set_line_packing_note",
        "orders.set_line_artwork_source",
        "orders.set_order_version_labels",
        "catalogue.set_order_line_start_number",
        "catalogue.split_order_line_versions",
        "orders.record_line_tharstern_job",
        "catalogue.change_supplier_for_service_line",
        "catalogue.add_order_line_to_customer_catalogue",
        "orders.cancel_order_line"
      ],
      "features": []
    },
    {
      "id": "orders.project",
      "module": "orders_module",
      "label": "Project",
      "plural": "Projects",
      "description": "A payer's project, grouping its orders under one title.",
      "identifier": {
        "kind": "cis_number",
        "field": "project_no",
        "prefix": "PRJ",
        "pattern": "^PRJ-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}$",
        "line_pattern": "^PRJ-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}\\.[0-9]{2,}$"
      },
      "states": [],
      "list": {
        "path": "/orders/projects",
        "key_field": "project_no",
        "title_field": "title",
        "columns": [
          {
            "field": "project_no",
            "label": "Project",
            "kind": "identifier"
          },
          {
            "field": "title",
            "label": "Title",
            "kind": "text"
          },
          {
            "field": "billing_account_ref",
            "label": "Payer",
            "kind": "text"
          },
          {
            "field": "order_count",
            "label": "Orders",
            "kind": "number"
          },
          {
            "field": "created_at",
            "label": "Created",
            "kind": "datetime"
          }
        ],
        "filters": [
          {
            "param": "billing_account_ref",
            "label": "Payer billing account reference",
            "kind": "text",
            "options": [],
            "description": "Only this payer's projects, by billing account (a project belongs to one payer)."
          }
        ],
        "sorts": [
          [
            "created_at",
            "Created"
          ],
          [
            "project_no",
            "Project"
          ],
          [
            "title",
            "Title"
          ],
          [
            "order_count",
            "Orders"
          ]
        ],
        "searchable": true,
        "queues": []
      },
      "record": null,
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A project groups one payer's orders under one title. \"Add to project…\" on an order puts the order into one of the payer's projects, or into a new project. A project has no record page: its list is what \"Add to project…\" searches. Changing or removing an order's project is not supported yet.",
      "commands": [],
      "features": [
        "orders"
      ]
    },
    {
      "id": "orders.tharstern_order",
      "module": "orders_module",
      "label": "Tharstern order",
      "plural": "Tharstern orders",
      "description": "A past sales order from Tharstern, or work Tharstern booked without one; read-only history.",
      "identifier": {
        "kind": "field",
        "field": "tharstern_ref",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "on_order",
        "despatched",
        "invoiced",
        "supplied_from_stock",
        "cancelled"
      ],
      "list": null,
      "record": {
        "path": "/views/tharstern-order-360/{tharstern_ref}",
        "path_param": "tharstern_ref",
        "title_field": "title",
        "state_fields": [
          "status"
        ],
        "state_labels": {
          "status": {
            "cancelled": "Cancelled",
            "despatched": "Despatched",
            "invoiced": "Invoiced",
            "on_order": "On order",
            "supplied_from_stock": "Supplied from stock"
          }
        },
        "facts": [
          {
            "field": "tharstern_sales_order_no",
            "label": "Tharstern sales order number",
            "kind": "text"
          },
          {
            "field": "tharstern_job_nos",
            "label": "Tharstern job number",
            "kind": "text"
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text"
          },
          {
            "field": "customer_reference",
            "label": "Customer's order number",
            "kind": "text"
          },
          {
            "field": "ordered_at",
            "label": "Ordered",
            "kind": "datetime"
          },
          {
            "field": "value_pence",
            "label": "Net",
            "kind": "money"
          },
          {
            "field": "vat_pence",
            "label": "VAT",
            "kind": "money"
          },
          {
            "field": "gross_pence",
            "label": "Gross",
            "kind": "money"
          },
          {
            "field": "expected_on",
            "label": "Expected",
            "kind": "date"
          },
          {
            "field": "placed_through",
            "label": "Placed through",
            "kind": "text"
          },
          {
            "field": "tharstern_estimate_ref",
            "label": "Tharstern estimate number",
            "kind": "text",
            "ref": "quotes.tharstern_estimate"
          }
        ],
        "fact_groups": [
          {
            "label": null,
            "fields": [
              "value_pence",
              "vat_pence",
              "gross_pence"
            ]
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "orders.tharstern_order_line",
          "label": "Tharstern order lines",
          "field": "lines",
          "key_field": "tharstern_ref",
          "columns": [
            {
              "field": "job_label",
              "label": "Tharstern job number",
              "kind": "text"
            },
            {
              "field": "description",
              "label": "Description",
              "kind": "text"
            },
            {
              "field": "quantity",
              "label": "Quantity",
              "kind": "number"
            },
            {
              "field": "value_pence",
              "label": "Net",
              "kind": "money"
            },
            {
              "field": "vat_amount_label",
              "label": "VAT",
              "kind": "text",
              "detail": {
                "field": "vat_rate_label",
                "label": "VAT rate",
                "kind": "text"
              }
            },
            {
              "field": "gross_pence",
              "label": "Gross",
              "kind": "money"
            },
            {
              "field": "status",
              "label": "Status",
              "kind": "state",
              "enum_labels": {
                "cancelled": "Cancelled",
                "despatched": "Despatched",
                "invoiced": "Invoiced",
                "on_order": "On order",
                "supplied_from_stock": "Supplied from stock"
              }
            }
          ],
          "paged": false,
          "parent_filter": null,
          "totals": {
            "value_pence": {
              "field": "value_pence",
              "label": "Net",
              "kind": "money"
            },
            "vat_amount_label": {
              "field": "vat_pence",
              "label": "VAT",
              "kind": "money"
            },
            "gross_pence": {
              "field": "gross_pence",
              "label": "Gross",
              "kind": "money"
            }
          }
        }
      ],
      "related": [],
      "lifecycle": [],
      "about": "A Tharstern order is the frozen copy of a Tharstern sales order, or of a job Tharstern booked without a sales order, since the 3 June 2025 cutover. The Tharstern sales order number and the Tharstern job numbers come from Tharstern. The Tharstern order is read-only in One. The Orders list shows Tharstern orders beside One's own; the Stock call-offs filter can leave out the Tharstern orders whose every line was stock called off. \"Add to draft quote…\", \"Quote now…\" and \"Order now…\" repeat the work as new work. Archived artwork lists the files the artwork archive holds for each job. The net, VAT and gross are Tharstern's; a job booked without a sales order has a net only.",
      "state_meanings": {
        "on_order": "On order: Tharstern had not recorded the Tharstern order as despatched when One last read Tharstern.",
        "despatched": "Despatched: Tharstern recorded every line of the Tharstern order as despatched.",
        "invoiced": "Invoiced: Tharstern invoiced every line of the Tharstern order.",
        "supplied_from_stock": "Supplied from stock: Tharstern stock called off every line of the Tharstern order.",
        "cancelled": "Cancelled: Tharstern cancelled the Tharstern order."
      },
      "state_tones": {
        "on_order": "in_progress",
        "despatched": "in_progress",
        "invoiced": "done",
        "supplied_from_stock": "done",
        "cancelled": "stopped"
      },
      "commands": [
        "catalogue.add_tharstern_order_to_draft_quote",
        "catalogue.quote_tharstern_order_now",
        "catalogue.order_tharstern_order_now"
      ],
      "features": [
        "tharstern_history"
      ]
    },
    {
      "id": "orders.tharstern_order_line",
      "module": "orders_module",
      "label": "Tharstern order line",
      "plural": "Tharstern order lines",
      "description": "One line of a Tharstern order, with its Tharstern job number or \"Stock call-off\" when it has none.",
      "identifier": {
        "kind": "field",
        "field": "tharstern_ref",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "orders.tharstern_order"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "on_order": "in_progress",
        "despatched": "in_progress",
        "invoiced": "done",
        "supplied_from_stock": "done",
        "cancelled": "stopped"
      },
      "commands": [],
      "features": []
    },
    {
      "id": "portal_theme.base_theme",
      "module": "portal_theme_module",
      "label": "Base theme",
      "plural": "Base themes",
      "description": "A named look several customers' portal themes can pick, a Pollards look or a brand they share, in published versions.",
      "identifier": {
        "kind": "field",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "active",
        "retired"
      ],
      "list": {
        "path": "/portal-theme/base-themes",
        "key_field": "id",
        "title_field": "name",
        "columns": [
          {
            "field": "id",
            "label": "Base theme",
            "kind": "identifier"
          },
          {
            "field": "name",
            "label": "Name",
            "kind": "text"
          },
          {
            "field": "kind",
            "label": "Kind",
            "kind": "text",
            "enum_labels": {
              "customer_brand": "Customer brand",
              "pollards_look": "Pollards look"
            }
          },
          {
            "field": "status",
            "label": "Status",
            "kind": "text",
            "enum_labels": {
              "active": "Active",
              "retired": "Retired"
            }
          },
          {
            "field": "current_version",
            "label": "Version",
            "kind": "number"
          },
          {
            "field": "customers_using",
            "label": "Customers using it",
            "kind": "number"
          },
          {
            "field": "created_at",
            "label": "Created",
            "kind": "datetime"
          }
        ],
        "filters": [
          {
            "param": "status",
            "label": "Status",
            "kind": "multi_enum",
            "options": [
              [
                "active",
                "Active"
              ],
              [
                "retired",
                "Retired"
              ]
            ]
          },
          {
            "param": "kind",
            "label": "Kind",
            "kind": "multi_enum",
            "options": [
              [
                "pollards_look",
                "Pollards look"
              ],
              [
                "customer_brand",
                "Customer brand"
              ]
            ]
          }
        ],
        "sorts": [
          [
            "name",
            "Name"
          ],
          [
            "created_at",
            "Created"
          ],
          [
            "status",
            "Status"
          ],
          [
            "kind",
            "Kind"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "active",
            "label": "Active",
            "params": {
              "status": [
                "active"
              ]
            },
            "count": false,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/portal-theme/base-themes/{id}",
        "path_param": "id",
        "title_field": "name",
        "state_fields": [
          "status"
        ],
        "state_labels": {
          "status": {
            "active": "Active",
            "retired": "Retired"
          }
        },
        "facts": [
          {
            "field": "kind",
            "label": "Kind",
            "kind": "text",
            "enum_labels": {
              "customer_brand": "Customer brand",
              "pollards_look": "Pollards look"
            }
          },
          {
            "field": "current_version",
            "label": "Version",
            "kind": "number"
          },
          {
            "field": "customers_using",
            "label": "Customers using it",
            "kind": "number"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A base theme is a named look that customers' portal themes pick, either a Pollards look or a brand several customers share. Publishing the base theme changes the portal of every customer on it, except for settings a customer sets itself, so only staff who look after every portal can change base themes. The Pollards standard base theme is every customer's fallback and is never retired.",
      "state_meanings": {
        "active": "Active: portal themes can pick the base theme.",
        "retired": "Retired: no portal theme can pick the base theme again."
      },
      "state_tones": {
        "active": "done",
        "retired": "neutral"
      },
      "commands": [
        "portal_theme.create_base_theme",
        "portal_theme.publish_base_theme",
        "portal_theme.retire_base_theme"
      ],
      "features": [
        "portal_theme"
      ]
    },
    {
      "id": "portal_theme.customer_theme",
      "module": "portal_theme_module",
      "label": "Portal theme",
      "plural": "Portal themes",
      "description": "How one customer's portal looks, a customer look over a base theme with optional team looks, as a draft and published versions.",
      "identifier": {
        "kind": "field",
        "field": "customer_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "no_theme",
        "draft_only",
        "published",
        "published_with_draft"
      ],
      "list": null,
      "record": {
        "path": "/portal-theme/customer-themes/{customer_id}",
        "path_param": "customer_id",
        "title_field": "customer_name",
        "state_fields": [
          "status"
        ],
        "state_labels": {
          "status": {
            "draft_only": "Draft only",
            "no_theme": "No theme",
            "published": "Published",
            "published_with_draft": "Published, with a draft"
          }
        },
        "facts": [
          {
            "field": "current_version",
            "label": "Current version",
            "kind": "number"
          },
          {
            "field": "base_theme_name",
            "label": "Base theme",
            "kind": "text"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A portal theme is how one customer's portal looks: its logo, colours, banner, welcome text and support contact. The portal theme sets only what differs from its base theme, and a team look changes the portal theme for one team's people. Staff edit the portal theme as a draft, preview it and use \"Publish\" to make it what the customer sees; every publish is kept as a version that \"Restore\" brings back as a draft.",
      "state_meanings": {
        "no_theme": "No theme: the customer's portal will show the Pollards standard look until a theme is published.",
        "draft_only": "Draft only: the customer's portal will show the Pollards standard look until the draft is published.",
        "published": "Published: the customer's portal shows the current version.",
        "published_with_draft": "Published, with a draft: the portal shows the current version until the draft is published."
      },
      "state_tones": {
        "no_theme": "neutral",
        "draft_only": "neutral",
        "published": "done",
        "published_with_draft": "done"
      },
      "commands": [
        "portal_theme.publish_customer_theme",
        "portal_theme.apply_brandhub_look",
        "portal_theme.restore_customer_theme_version",
        "portal_theme.discard_customer_theme_draft"
      ],
      "features": [
        "portal_theme"
      ]
    },
    {
      "id": "portal_theme.portal_notice",
      "module": "portal_theme_module",
      "label": "Portal notice",
      "plural": "Portal notices",
      "description": "A top bar or pop-up shown in the customer portal to every customer, one customer or one team, between its dates.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "scheduled",
        "showing",
        "ended"
      ],
      "list": {
        "path": "/portal-theme/notices",
        "key_field": "id",
        "title_field": "text",
        "columns": [
          {
            "field": "id",
            "label": "Notice",
            "kind": "identifier"
          },
          {
            "field": "kind",
            "label": "Kind",
            "kind": "text",
            "enum_labels": {
              "pop_up": "Pop-up",
              "top_bar": "Top bar"
            }
          },
          {
            "field": "audience",
            "label": "Shown to",
            "kind": "text",
            "enum_labels": {
              "customer": "This customer",
              "everyone": "Every customer",
              "team": "One team"
            }
          },
          {
            "field": "text",
            "label": "Text",
            "kind": "text"
          },
          {
            "field": "status",
            "label": "Status",
            "kind": "text",
            "enum_labels": {
              "ended": "Ended",
              "scheduled": "Scheduled",
              "showing": "Showing"
            }
          },
          {
            "field": "showing_from",
            "label": "First day",
            "kind": "date"
          },
          {
            "field": "showing_until",
            "label": "Last day",
            "kind": "date"
          },
          {
            "field": "posted_at",
            "label": "Posted",
            "kind": "datetime"
          }
        ],
        "filters": [
          {
            "param": "audience",
            "label": "Shown to",
            "kind": "multi_enum",
            "options": [
              [
                "everyone",
                "Every customer"
              ],
              [
                "customer",
                "This customer"
              ],
              [
                "team",
                "One team"
              ]
            ]
          },
          {
            "param": "customer_id",
            "label": "Customer ID",
            "kind": "text",
            "options": []
          },
          {
            "param": "status",
            "label": "Status",
            "kind": "multi_enum",
            "options": [
              [
                "scheduled",
                "Scheduled"
              ],
              [
                "showing",
                "Showing"
              ],
              [
                "ended",
                "Ended"
              ]
            ]
          },
          {
            "param": "kind",
            "label": "Kind",
            "kind": "multi_enum",
            "options": [
              [
                "top_bar",
                "Top bar"
              ],
              [
                "pop_up",
                "Pop-up"
              ]
            ]
          }
        ],
        "sorts": [
          [
            "posted_at",
            "Posted"
          ],
          [
            "showing_from",
            "First day"
          ],
          [
            "showing_until",
            "Last day"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "showing",
            "label": "Showing",
            "params": {
              "status": [
                "showing"
              ]
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/portal-theme/notices/{id}",
        "path_param": "id",
        "title_field": "text",
        "state_fields": [
          "status"
        ],
        "state_labels": {
          "status": {
            "ended": "Ended",
            "scheduled": "Scheduled",
            "showing": "Showing"
          }
        },
        "facts": [
          {
            "field": "kind",
            "label": "Kind",
            "kind": "text",
            "enum_labels": {
              "pop_up": "Pop-up",
              "top_bar": "Top bar"
            }
          },
          {
            "field": "audience",
            "label": "Shown to",
            "kind": "text",
            "enum_labels": {
              "customer": "This customer",
              "everyone": "Every customer",
              "team": "One team"
            }
          },
          {
            "field": "showing_from",
            "label": "First day",
            "kind": "date"
          },
          {
            "field": "showing_until",
            "label": "Last day",
            "kind": "date"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A portal notice is a message in the customer portal: a top bar, or a pop-up each person closes once. The portal notice shows to everyone at one customer, one team there, or every customer, from its first day to its last, as soon as it is posted. \"End notice\" stops the portal notice at once.",
      "state_meanings": {
        "scheduled": "Scheduled: the notice will show from its first day.",
        "showing": "Showing: the notice is in the portal until its last day or until someone ends it.",
        "ended": "Ended: the notice will not show again."
      },
      "state_tones": {
        "scheduled": "in_progress",
        "showing": "done",
        "ended": "neutral"
      },
      "commands": [
        "portal_theme.post_portal_notice",
        "portal_theme.post_sitewide_portal_notice",
        "portal_theme.end_portal_notice"
      ],
      "features": [
        "portal_theme"
      ]
    },
    {
      "id": "purchase_orders.price_increase",
      "module": "purchase_orders_module",
      "label": "Price increase",
      "plural": "Price increases",
      "description": "A purchase order line a supplier raised when it accepted the order, counted against its price increases that month.",
      "identifier": {
        "kind": "uuid",
        "field": "price_increase_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "accepted",
        "held",
        "approved",
        "declined"
      ],
      "list": {
        "path": "/purchase-orders/price-increases",
        "key_field": "price_increase_id",
        "title_field": "purchase_order_no",
        "columns": [
          {
            "field": "price_increase_id",
            "label": "Price increase id",
            "kind": "identifier"
          },
          {
            "field": "purchase_order_no",
            "label": "Purchase order",
            "kind": "text",
            "ref": "purchase_orders.purchase_order"
          },
          {
            "field": "line_reference",
            "label": "Line",
            "kind": "text",
            "ref": "purchase_orders.purchase_order"
          },
          {
            "field": "supplier_name",
            "label": "Supplier",
            "kind": "text"
          },
          {
            "field": "from_pence",
            "label": "From",
            "kind": "money"
          },
          {
            "field": "to_pence",
            "label": "To",
            "kind": "money"
          },
          {
            "field": "increase_label",
            "label": "Increase",
            "kind": "text"
          },
          {
            "field": "raised_at",
            "label": "Raised",
            "kind": "datetime"
          },
          {
            "field": "outcome",
            "label": "Outcome",
            "kind": "text",
            "enum_labels": {
              "accepted": "Accepted",
              "approved": "Approved",
              "declined": "Declined",
              "held": "Held for review"
            }
          }
        ],
        "filters": [
          {
            "param": "supplier_id",
            "label": "Supplier ID",
            "kind": "text",
            "options": [],
            "description": "Only this supplier's price increases."
          },
          {
            "param": "month",
            "label": "Month",
            "kind": "text",
            "options": [],
            "description": "The London month the acknowledgement was decided in: 'this', or YYYY-MM."
          },
          {
            "param": "outcome",
            "label": "Outcome",
            "kind": "multi_enum",
            "options": [
              [
                "accepted",
                "accepted"
              ],
              [
                "held",
                "held"
              ],
              [
                "approved",
                "approved"
              ],
              [
                "declined",
                "declined"
              ]
            ],
            "description": "Only these outcomes."
          }
        ],
        "sorts": [],
        "searchable": true,
        "queues": []
      },
      "record": null,
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A price increase is a purchase order line that a supplier raised when it accepted the order. One acknowledgement that raises the order's cost counts as one price increase for that supplier that month, however many lines it raises. The reason on a held purchase order says which increase of the month it is. The count shows as the list's row count.",
      "state_meanings": {
        "accepted": "Accepted: the new price was taken at once.",
        "held": "Held for review: a manager decides the new price on the purchase order.",
        "approved": "Approved: a manager accepted the new price.",
        "declined": "Declined: a manager refused the new price and the line went back to the supplier."
      },
      "state_tones": {
        "accepted": "done",
        "held": "attention",
        "approved": "done",
        "declined": "stopped"
      },
      "commands": [],
      "features": [
        "orders"
      ]
    },
    {
      "id": "purchase_orders.purchase_order",
      "module": "purchase_orders_module",
      "label": "Purchase order",
      "plural": "Purchase orders",
      "description": "An order CIS places with a supplier for goods or services on a customer's order, or for stock.",
      "identifier": {
        "kind": "cis_number",
        "field": "purchase_order_no",
        "prefix": "PO",
        "pattern": "^PO-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}$",
        "line_pattern": "^PO-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}\\.[0-9]{2,}$"
      },
      "states": [
        "requested",
        "accepted",
        "on_hold",
        "despatched",
        "delivered",
        "received",
        "cancelled"
      ],
      "list": {
        "path": "/views/purchase-order-list",
        "key_field": "purchase_order_no",
        "title_field": "supplier_name",
        "columns": [
          {
            "field": "purchase_order_no",
            "label": "Purchase order",
            "kind": "identifier"
          },
          {
            "field": "supplier_name",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier",
            "ref_id_field": "supplier_no"
          },
          {
            "field": "supplier_reference",
            "label": "Supplier's reference",
            "kind": "text"
          },
          {
            "field": "trading_company",
            "label": "Placed by",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "trading_company_code"
          },
          {
            "field": "status",
            "label": "Status",
            "kind": "state",
            "enum_labels": {
              "accepted": "Placed with supplier",
              "cancelled": "Cancelled",
              "delivered": "Delivered",
              "despatched": "Despatched",
              "on_hold": "Held for approval",
              "received": "Received",
              "requested": "Requested"
            }
          },
          {
            "field": "health",
            "label": "Health",
            "kind": "text",
            "enum_labels": {
              "at_risk": "At risk",
              "late": "Late"
            }
          },
          {
            "field": "stage",
            "label": "Stage",
            "kind": "text",
            "enum_labels": {
              "arriving": "Arriving",
              "awaiting_acknowledgement": "Awaiting acknowledgement",
              "despatch_due": "Despatch due",
              "to_place": "To place"
            }
          },
          {
            "field": "is_late",
            "label": "Late",
            "kind": "boolean"
          },
          {
            "field": "is_ready_to_pay",
            "label": "Ready to pay",
            "kind": "boolean"
          },
          {
            "field": "requested_ready_on",
            "label": "Ready by",
            "kind": "date"
          },
          {
            "field": "despatch_due_on",
            "label": "Despatch due",
            "kind": "date"
          },
          {
            "field": "cost_pence",
            "label": "Cost",
            "kind": "money"
          },
          {
            "field": "raised_at",
            "label": "Raised",
            "kind": "datetime"
          },
          {
            "field": "customer_names",
            "label": "Customer",
            "kind": "text"
          },
          {
            "field": "order_nos",
            "label": "Order",
            "kind": "text",
            "ref": "orders.order"
          },
          {
            "field": "remaining_to_bill_pence",
            "label": "Left to bill",
            "kind": "money"
          }
        ],
        "filters": [
          {
            "param": "stage",
            "label": "Stage",
            "kind": "multi_enum",
            "options": [
              [
                "to_place",
                "To place"
              ],
              [
                "awaiting_acknowledgement",
                "Awaiting acknowledgement"
              ],
              [
                "despatch_due",
                "Despatch due"
              ],
              [
                "arriving",
                "Arriving"
              ]
            ],
            "description": "Only purchase orders at these stages of Purchasing's work."
          },
          {
            "param": "status",
            "label": "Status",
            "kind": "multi_enum",
            "options": [
              [
                "requested",
                "Requested"
              ],
              [
                "accepted",
                "Placed with supplier"
              ],
              [
                "on_hold",
                "Held for approval"
              ],
              [
                "despatched",
                "Despatched"
              ],
              [
                "delivered",
                "Delivered"
              ],
              [
                "received",
                "Received"
              ],
              [
                "cancelled",
                "Cancelled"
              ]
            ],
            "description": "Only purchase orders in these states."
          },
          {
            "param": "is_late",
            "label": "Late",
            "kind": "boolean",
            "options": [],
            "description": "True: the date for the step it is at (acknowledge by, despatch due, arriving) has passed."
          },
          {
            "param": "supplier_id",
            "label": "Supplier ID",
            "kind": "text",
            "options": [],
            "description": "Only this supplier's purchase orders."
          },
          {
            "param": "customer_id",
            "label": "Customer ID",
            "kind": "text",
            "options": [],
            "description": "A line was bought for this customer's order."
          },
          {
            "param": "order_no",
            "label": "Order number",
            "kind": "text",
            "options": [],
            "description": "A line was bought for this order (ORD-K48193)."
          },
          {
            "param": "delivery",
            "label": "Delivery",
            "kind": "text",
            "options": [],
            "description": "A despatch's tracking number, or a delivery's postcode or name (spaces in a postcode ignored)."
          },
          {
            "param": "raised_from",
            "label": "Raised from",
            "kind": "date",
            "options": [],
            "description": "Raised on or after."
          },
          {
            "param": "raised_to",
            "label": "Raised to",
            "kind": "date",
            "options": [],
            "description": "Raised on or before."
          },
          {
            "param": "goods_in_from",
            "label": "Goods in from",
            "kind": "date",
            "options": [],
            "description": "A line despatched, delivered or received on or after."
          },
          {
            "param": "goods_in_to",
            "label": "Goods in to",
            "kind": "date",
            "options": [],
            "description": "A line despatched, delivered or received on or before."
          },
          {
            "param": "is_open_to_bill",
            "label": "Open to bill",
            "kind": "boolean",
            "options": [],
            "description": "True: a purchase invoice can still draw on it (something left to bill)."
          },
          {
            "param": "remaining_from_pence",
            "label": "Left to bill from",
            "kind": "number",
            "options": [],
            "description": "At least this much left to bill."
          },
          {
            "param": "remaining_to_pence",
            "label": "Left to bill to",
            "kind": "number",
            "options": [],
            "description": "At most this much left to bill."
          }
        ],
        "sorts": [
          [
            "raised_at",
            "Raised"
          ],
          [
            "requested_ready_on",
            "Ready by"
          ],
          [
            "purchase_order_no",
            "Purchase order"
          ],
          [
            "cost_pence",
            "Cost"
          ],
          [
            "supplier_name",
            "Supplier"
          ],
          [
            "remaining_to_bill_pence",
            "Left to bill"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "to_place",
            "label": "To place",
            "params": {
              "stage": "to_place"
            },
            "count": true,
            "default": false
          },
          {
            "id": "awaiting_acknowledgement",
            "label": "Awaiting acknowledgement",
            "params": {
              "stage": "awaiting_acknowledgement"
            },
            "count": true,
            "default": false
          },
          {
            "id": "despatch_due",
            "label": "Despatch due",
            "params": {
              "stage": "despatch_due"
            },
            "count": false,
            "default": false
          },
          {
            "id": "arriving",
            "label": "Arriving",
            "params": {
              "stage": "arriving"
            },
            "count": false,
            "default": false
          },
          {
            "id": "late",
            "label": "Late",
            "params": {
              "is_late": true
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/views/purchase-order-360/{purchase_order_no}",
        "path_param": "purchase_order_no",
        "title_field": "supplier_name",
        "state_fields": [
          "status",
          "health"
        ],
        "state_labels": {
          "status": {
            "accepted": "Placed with supplier",
            "cancelled": "Cancelled",
            "delivered": "Delivered",
            "despatched": "Despatched",
            "on_hold": "Held for approval",
            "received": "Received",
            "requested": "Requested"
          }
        },
        "facts": [
          {
            "field": "order_nos",
            "label": "Orders",
            "kind": "text",
            "ref": "orders.order"
          },
          {
            "field": "supplier_quote_nos",
            "label": "Placed against",
            "kind": "text",
            "ref": "supplier_quotes.supplier_quote"
          },
          {
            "field": "supplier_reference",
            "label": "Supplier's reference",
            "kind": "text"
          },
          {
            "field": "cost_pence",
            "label": "Cost",
            "kind": "money"
          },
          {
            "field": "requested_ready_on",
            "label": "Ready by",
            "kind": "date"
          },
          {
            "field": "despatch_due_on",
            "label": "Despatch due",
            "kind": "date"
          },
          {
            "field": "trading_company",
            "label": "Placed by",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "trading_company_code"
          },
          {
            "field": "supplier_account_no",
            "label": "On our account",
            "kind": "text"
          },
          {
            "field": "bought_for",
            "label": "Bought for",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "bought_for_code"
          },
          {
            "field": "recharge_note",
            "label": "Recharge",
            "kind": "text"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "purchase_orders.purchase_order_line",
          "label": "Purchase order lines",
          "field": "lines",
          "key_field": "line_reference",
          "columns": [
            {
              "field": "line_reference",
              "label": "Line",
              "kind": "identifier"
            },
            {
              "field": "description",
              "label": "Description",
              "kind": "text"
            },
            {
              "field": "quantity",
              "label": "Quantity",
              "kind": "number"
            },
            {
              "field": "committed_value_pence",
              "label": "Cost",
              "kind": "money"
            },
            {
              "field": "order_line_ref",
              "label": "For order line",
              "kind": "text",
              "ref": "orders.order"
            }
          ],
          "paged": false,
          "parent_filter": null,
          "totals": {
            "committed_value_pence": {
              "field": "cost_pence",
              "label": "Cost",
              "kind": "money"
            }
          }
        },
        {
          "object": "fulfilment_adapter_supplier_portal.supplier_proof",
          "label": "Supplier proofs",
          "field": "supplier_proofs",
          "key_field": "id",
          "columns": [],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "fulfilment_adapters.supplier_website_order",
          "label": "Supplier website orders",
          "field": "supplier_website_orders",
          "key_field": "purchase_order_no",
          "columns": [
            {
              "field": "title",
              "label": "Supplier website order",
              "kind": "text"
            },
            {
              "field": "ordering_site",
              "label": "Supplier's site",
              "kind": "link"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [
        {
          "object": "finance_purchase_ledger.purchase_invoice",
          "label": "Purchase invoices",
          "params": {
            "purchase_order_no": "{purchase_order_no}"
          }
        },
        {
          "object": "comms_ingest.email",
          "label": "Emails",
          "params": {
            "tag_object": "purchase_orders.purchase_order",
            "tag_key": "{purchase_order_no}"
          },
          "secondary": true
        }
      ],
      "lifecycle": [
        {
          "id": "purchase_orders.raised",
          "label": "Raised",
          "module": "purchase_orders_module",
          "description": "The purchase order was raised, at Release or for stock."
        },
        {
          "id": "purchase_orders.placed",
          "label": "Placed",
          "module": "purchase_orders_module",
          "description": "Placed with the supplier, by hand or through its ordering adapter, or held for price approval."
        },
        {
          "id": "purchase_orders.despatched",
          "label": "Despatched",
          "module": "purchase_orders_module",
          "description": "The supplier despatched every line."
        },
        {
          "id": "purchase_orders.arrived",
          "label": "Arrived",
          "module": "purchase_orders_module",
          "description": "Every line was delivered to the customer or received into stock."
        },
        {
          "id": "finance_purchase_ledger.purchase_order_billed",
          "label": "Billed",
          "module": "finance_purchase_ledger_module",
          "description": "A purchase order's supplier has invoiced it."
        },
        {
          "id": "finance_purchase_ledger.purchase_order_matched",
          "label": "Matched",
          "module": "finance_purchase_ledger_module",
          "description": "Every purchase invoice on a purchase order is matched to it."
        }
      ],
      "about": "A purchase order is one order to one supplier for goods or services on a customer's order, or for stock. Release on an order raises the purchase order, or adds to the supplier's open purchase order for that order. One places the purchase order with a connected supplier. For other suppliers, staff place the purchase order and use \"Record placed\". When a supplier refuses a purchase order, the purchase order stays on \"To place\" with the reason. Use \"Place again\" once the reason has been put right. The purchase ledger then matches the supplier's invoice to the purchase order.",
      "state_meanings": {
        "requested": "Requested: the purchase order will be placed once the order lines are complete or someone uses \"Place now\".",
        "accepted": "Placed with supplier: the supplier has taken the purchase order.",
        "on_hold": "Held for approval: the purchase order will wait until someone uses \"Approve price\".",
        "despatched": "Despatched: every live purchase order line is on the way.",
        "delivered": "Delivered: the goods arrived at the customer's address.",
        "received": "Received: the goods arrived in one of our warehouses.",
        "cancelled": "Cancelled: the supplier confirmed the cancellation, staff recorded it cancelled with the supplier, or staff withdrew the purchase order before placing."
      },
      "state_tones": {
        "requested": "neutral",
        "accepted": "in_progress",
        "on_hold": "attention",
        "despatched": "in_progress",
        "delivered": "done",
        "received": "done",
        "cancelled": "stopped"
      },
      "commands": [
        "purchase_orders.record_purchase_order_placed",
        "purchase_orders.record_ready_to_pay",
        "purchase_orders.record_handover",
        "purchase_orders.record_supplier_lines_accepted",
        "purchase_orders.record_purchase_order_despatch",
        "purchase_orders.approve_purchase_order_price",
        "purchase_orders.decline_purchase_order_price",
        "purchase_orders.log_supplier_call",
        "purchase_orders.record_supplier_shortage_reported",
        "purchase_orders.record_supplier_status_reported",
        "fulfilment_adapter_supplier_portal.resend_purchase_order",
        "fulfilment_adapter_supplier_portal.chase_purchase_order",
        "purchase_orders.place_purchase_order_again",
        "fulfilment_adapter_supplier_portal.revoke_purchase_order_links",
        "purchase_orders.cancel_purchase_order_lines",
        "purchase_orders.cancel_purchase_order",
        "finance_purchase_ledger.record_purchase_order_billing_complete"
      ],
      "features": [
        "orders"
      ]
    },
    {
      "id": "purchase_orders.purchase_order_line",
      "module": "purchase_orders_module",
      "label": "Purchase order line",
      "plural": "Purchase order lines",
      "description": "One line of a purchase order, bought for one order line.",
      "identifier": {
        "kind": "field",
        "field": "line_reference",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "open",
        "delivered",
        "received",
        "cancelled"
      ],
      "list": null,
      "record": null,
      "parents": [
        "purchase_orders.purchase_order"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "open": "in_progress",
        "delivered": "done",
        "received": "done",
        "cancelled": "stopped"
      },
      "commands": [
        "purchase_orders.record_purchase_order_line_agreed_amount",
        "purchase_orders.issue_supplier_file_link",
        "finance_purchase_ledger.record_billing_complete",
        "finance_purchase_ledger.reopen_billing",
        "purchase_orders.propose_purchase_order_line_date_change",
        "fulfilment_adapter_supplier_portal.record_supplier_proof",
        "purchase_orders.record_purchase_order_line_date_change_acceptance"
      ],
      "features": []
    },
    {
      "id": "purchase_orders.supplier_usage_record",
      "module": "purchase_orders_module",
      "label": "Usage record",
      "plural": "Usage records",
      "description": "One use of a supplier's service we booked, such as a consignment, with what our prices expected it to cost.",
      "identifier": {
        "kind": "field",
        "field": "usage_record_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "finance_purchase_ledger.purchase_invoice"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": []
    },
    {
      "id": "quotes.quote",
      "module": "quotes_module",
      "label": "Quote",
      "plural": "Quotes",
      "description": "A priced offer to a customer: a private draft, then published, then accepted into an order, declined, expired or withdrawn.",
      "identifier": {
        "kind": "cis_number",
        "field": "quote_no",
        "prefix": "QT",
        "pattern": "^QT-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}$",
        "line_pattern": "^QT-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}\\.[0-9]{2,}$"
      },
      "states": [
        "draft",
        "published",
        "accepted",
        "declined",
        "expired",
        "withdrawn",
        "ordered"
      ],
      "list": {
        "path": "/views/quote-list",
        "key_field": "quote_no",
        "title_field": "title",
        "columns": [
          {
            "field": "quote_no",
            "label": "Quote",
            "kind": "identifier"
          },
          {
            "field": "kind",
            "label": "Kind",
            "kind": "state",
            "enum_labels": {
              "cis": "One",
              "tharstern": "Tharstern"
            }
          },
          {
            "field": "tharstern_ref",
            "label": "Tharstern estimate number",
            "kind": "identifier"
          },
          {
            "field": "title",
            "label": "Title",
            "kind": "text"
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text",
            "ref": "customers.customer",
            "ref_id_field": "customer.customer_code"
          },
          {
            "field": "status",
            "label": "Status",
            "kind": "state",
            "enum_labels": {
              "accepted": "Accepted",
              "declined": "Declined",
              "draft": "Draft",
              "expired": "Expired",
              "lost": "Lost",
              "open": "Open",
              "ordered": "Ordered",
              "published": "Published",
              "stale": "Stale",
              "superseded": "Superseded",
              "tender": "Tender",
              "withdrawn": "Withdrawn",
              "won": "Ordered"
            }
          },
          {
            "field": "total_pence",
            "label": "Total",
            "kind": "money"
          },
          {
            "field": "published_at",
            "label": "Published",
            "kind": "datetime"
          },
          {
            "field": "expires_at",
            "label": "Expires",
            "kind": "datetime"
          },
          {
            "field": "origin",
            "label": "Origin",
            "kind": "state",
            "enum_labels": {
              "channel": "Promo Office",
              "claude": "Prepared by Claude",
              "customer": "Customer's request",
              "staff": "Staff"
            }
          },
          {
            "field": "owner_name",
            "label": "Owner",
            "kind": "text"
          }
        ],
        "filters": [
          {
            "param": "kind",
            "label": "Kind",
            "kind": "enum",
            "options": [
              [
                "cis",
                "One"
              ],
              [
                "tharstern",
                "Tharstern"
              ]
            ],
            "description": "Which quotes: 'cis' for One's own quotes, 'tharstern' for Tharstern estimates (read-only history since 3 June 2025, shown while the Tharstern history feature is on). Absent: both."
          },
          {
            "param": "published_from",
            "label": "Published from",
            "kind": "date",
            "options": [],
            "description": "Quotes published, and Tharstern estimates dated, on or after this day (YYYY-MM-DD); a draft counts from the day it was made. Both kinds."
          },
          {
            "param": "published_to",
            "label": "Published to",
            "kind": "date",
            "options": [],
            "description": "Quotes published, and Tharstern estimates dated, on or before this day (YYYY-MM-DD); a draft counts from the day it was made. Both kinds."
          },
          {
            "param": "status",
            "label": "Status",
            "kind": "multi_enum",
            "options": [
              [
                "draft",
                "Draft"
              ],
              [
                "published",
                "Published"
              ],
              [
                "accepted",
                "Accepted"
              ],
              [
                "declined",
                "Declined"
              ],
              [
                "expired",
                "Expired"
              ],
              [
                "withdrawn",
                "Withdrawn"
              ],
              [
                "ordered",
                "Ordered"
              ]
            ],
            "description": "Quotes in any of these statuses. One quotes only: setting this filter will leave Tharstern estimates out.",
            "own_kind_only": true
          },
          {
            "param": "origin",
            "label": "Origin",
            "kind": "multi_enum",
            "options": [
              [
                "staff",
                "Staff"
              ],
              [
                "channel",
                "Promo Office"
              ],
              [
                "claude",
                "Prepared by Claude"
              ],
              [
                "customer",
                "Customer's request"
              ]
            ],
            "description": "Quotes made by any of these: staff in the app, a channel (Promo Office), or Claude acting for staff. One quotes only: setting this filter will leave Tharstern estimates out.",
            "own_kind_only": true
          },
          {
            "param": "owner_user_id",
            "label": "Owner",
            "kind": "entity",
            "options": [],
            "object": "users.user",
            "description": "Only the quotes this user owns: a draft quote's owner stays its owner after publishing. One quotes only: setting this filter will leave Tharstern estimates out.",
            "own_kind_only": true
          },
          {
            "param": "mine",
            "label": "Mine",
            "kind": "boolean",
            "options": [],
            "description": "Only the quotes the signed-in user owns: 'my drafts' for a person's Claude, without their user id. One quotes only: setting this filter will leave Tharstern estimates out.",
            "own_kind_only": true
          },
          {
            "param": "is_shared",
            "label": "Shared",
            "kind": "boolean",
            "options": [],
            "description": "Only quotes whose link has gone to the customer (true), or never has (false): the queues Awaiting decision and Not shared. One quotes only: setting this filter will leave Tharstern estimates out.",
            "own_kind_only": true
          },
          {
            "param": "customer_id",
            "label": "Customer",
            "kind": "entity",
            "options": [],
            "object": "customers.customer",
            "entity_value": "id",
            "description": "Only this customer's quotes, by the customer's id (the id on the customer's Customers list row and record): the Quotes list on the customer record. Both kinds."
          },
          {
            "param": "published_by_user_id",
            "label": "Published by",
            "kind": "entity",
            "options": [],
            "object": "users.user",
            "description": "Only the quotes this user sent. One quotes only: setting this filter will leave Tharstern estimates out.",
            "own_kind_only": true
          },
          {
            "param": "latest_version_only",
            "label": "Latest version only",
            "kind": "boolean",
            "options": [],
            "description": "Tharstern estimates only: true (the default) will show only the latest version of each Tharstern estimate; false will show every version. One quotes stay.",
            "default": true,
            "included_kind_only": true
          }
        ],
        "sorts": [
          [
            "published_at",
            "Published"
          ],
          [
            "expires_at",
            "Expires"
          ],
          [
            "quote_no",
            "Quote"
          ],
          [
            "total_pence",
            "Total"
          ],
          [
            "customer_name",
            "Customer"
          ],
          [
            "status",
            "Status"
          ],
          [
            "owned_by_me",
            "Owned by me"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "drafts",
            "label": "Drafts",
            "params": {
              "status": [
                "draft"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "my_drafts",
            "label": "My drafts",
            "params": {
              "status": [
                "draft"
              ],
              "mine": true
            },
            "count": true,
            "default": false
          },
          {
            "id": "awaiting_decision",
            "label": "Awaiting decision",
            "params": {
              "status": [
                "published"
              ],
              "is_shared": true
            },
            "count": true,
            "default": false
          },
          {
            "id": "not_shared",
            "label": "Not shared",
            "params": {
              "status": [
                "published"
              ],
              "is_shared": false
            },
            "count": true,
            "default": false
          }
        ],
        "includes": {
          "object": "quotes.tharstern_estimate",
          "kind": "tharstern",
          "own_kind": "cis"
        }
      },
      "record": {
        "path": "/views/quote-360/{quote_no}",
        "path_param": "quote_no",
        "title_field": "title",
        "state_fields": [
          "status"
        ],
        "state_labels": {
          "status": {
            "accepted": "Accepted",
            "declined": "Declined",
            "draft": "Draft",
            "expired": "Expired",
            "ordered": "Ordered",
            "published": "Published",
            "withdrawn": "Withdrawn"
          }
        },
        "facts": [
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text",
            "ref": "customers.customer",
            "ref_id_field": "customer.customer_code"
          },
          {
            "field": "contact_name",
            "label": "Contact",
            "kind": "text"
          },
          {
            "field": "delivery_address",
            "label": "Delivery address",
            "kind": "text"
          },
          {
            "field": "owner_name",
            "label": "Owner",
            "kind": "text"
          },
          {
            "field": "total_pence",
            "label": "Total",
            "kind": "money"
          },
          {
            "field": "expires_at",
            "label": "Expires",
            "kind": "datetime"
          },
          {
            "field": "last_shared_at",
            "label": "Last shared",
            "kind": "datetime"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "quotes.quote_line",
          "label": "Quote lines",
          "field": "lines",
          "key_field": "line_ref",
          "columns": [
            {
              "field": "line_ref",
              "label": "Line",
              "kind": "text"
            },
            {
              "field": "name_full",
              "label": "Description",
              "kind": "text"
            },
            {
              "field": "quantities_offered",
              "label": "Quantity",
              "kind": "text",
              "fallback": {
                "field": "quantity",
                "label": "Quantity",
                "kind": "number"
              }
            },
            {
              "field": "prices_offered",
              "label": "Price",
              "kind": "text",
              "fallback": {
                "field": "price_pence",
                "label": "Price",
                "kind": "money"
              }
            },
            {
              "field": "price_source_label",
              "label": "Price source",
              "kind": "text"
            },
            {
              "field": "supplier",
              "label": "Supplier",
              "kind": "text"
            },
            {
              "field": "speed",
              "label": "Speed",
              "kind": "text"
            },
            {
              "field": "arrives",
              "label": "Arrives",
              "kind": "text"
            },
            {
              "field": "is_optional",
              "label": "Optional extra",
              "kind": "boolean"
            },
            {
              "field": "versions_split",
              "label": "Versions",
              "kind": "text"
            },
            {
              "field": "specification",
              "label": "Specification for the supplier",
              "kind": "formatted_text"
            },
            {
              "field": "wanted_by",
              "label": "Wanted by",
              "kind": "date"
            },
            {
              "field": "supplier_files",
              "label": "Files for the supplier",
              "kind": "file"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "supplier_quotes.supplier_quote",
          "label": "Supplier quotes",
          "field": "supplier_quotes",
          "key_field": "supplier_quote_no",
          "columns": [
            {
              "field": "line_ref",
              "label": "Line",
              "kind": "text"
            },
            {
              "field": "supplier_name",
              "label": "Supplier",
              "kind": "text",
              "ref": "suppliers.supplier",
              "ref_id_field": "supplier_no"
            },
            {
              "field": "state",
              "label": "State",
              "kind": "state",
              "enum_labels": {
                "asked": "Asked",
                "chosen": "Chosen",
                "declined": "Declined",
                "out_of_date": "Out of date",
                "quote_expired": "Expired",
                "quoted": "Quoted",
                "withdrawn": "Withdrawn"
              }
            },
            {
              "field": "waiting_for",
              "label": "Waiting for",
              "kind": "text"
            },
            {
              "field": "valid_until",
              "label": "Valid until",
              "kind": "date"
            },
            {
              "field": "through",
              "label": "Through",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": "quote_no"
        },
        {
          "object": "supplier_quotes.supplier_price",
          "label": "Supplier prices",
          "field": "supplier_prices",
          "key_field": "id",
          "columns": [
            {
              "field": "line_ref",
              "label": "Line",
              "kind": "text"
            },
            {
              "field": "quantity",
              "label": "Quantity",
              "kind": "number"
            },
            {
              "field": "supplier_name",
              "label": "Supplier",
              "kind": "text",
              "ref": "suppliers.supplier",
              "ref_id_field": "supplier_no"
            },
            {
              "field": "cost_pence",
              "label": "Their delivered cost",
              "kind": "money"
            },
            {
              "field": "valid_until",
              "label": "Valid until",
              "kind": "date"
            },
            {
              "field": "state",
              "label": "State",
              "kind": "state",
              "enum_labels": {
                "asked": "Asked",
                "chosen": "Chosen",
                "not_needed": "Not needed",
                "not_quoted": "Not quoted",
                "price_out_of_date": "Out of date",
                "priced": "Priced"
              }
            }
          ],
          "paged": false,
          "parent_filter": null,
          "grid": {
            "row_field": "supplier_quote_no",
            "row_label_field": "grid_row_label",
            "column_field": "quantity",
            "cell_field": "cost_pence",
            "group_field": "line_ref",
            "state_field": "grid_state",
            "highlight_field": "is_lowest"
          }
        },
        {
          "object": "artwork_preparation.artwork",
          "label": "Artwork",
          "field": "artworks",
          "key_field": "artwork_id",
          "columns": [
            {
              "field": "title",
              "label": "Artwork",
              "kind": "text"
            },
            {
              "field": "state",
              "label": "Stage",
              "kind": "state",
              "enum_labels": {
                "approved": "Approved",
                "cant_print": "Can't print",
                "changes_asked": "Sent back",
                "checked": "Checked",
                "checking": "Checking",
                "files_owed": "Files owed",
                "held": "Held",
                "proof_out": "Proof out",
                "tool_failed": "Tool failed"
              }
            },
            {
              "field": "status",
              "label": "Status",
              "kind": "text"
            },
            {
              "field": "revision_no",
              "label": "Rev",
              "kind": "number"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "artwork_preparation.finding",
          "label": "Check warnings",
          "field": "held_findings",
          "key_field": "id",
          "columns": [
            {
              "field": "subject",
              "label": "For",
              "kind": "text"
            },
            {
              "field": "check_name",
              "label": "Check",
              "kind": "text"
            },
            {
              "field": "detail",
              "label": "Detail",
              "kind": "text"
            },
            {
              "field": "held_since",
              "label": "Held since",
              "kind": "datetime"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [
        {
          "object": "comms_ingest.email",
          "label": "Emails",
          "params": {
            "tag_object": "quotes.quote",
            "tag_key": "{quote_no}"
          },
          "secondary": true
        }
      ],
      "lifecycle": [
        {
          "id": "quotes.draft",
          "label": "Draft",
          "module": "quotes_module",
          "description": "The quote is private to staff while it is made and edited, until it is published, placed as an order or discarded."
        },
        {
          "id": "quotes.published",
          "label": "Published",
          "module": "quotes_module",
          "description": "Published with its prices fixed, shown in the customer's portal, and whether it has been shared."
        },
        {
          "id": "quotes.decision",
          "label": "Customer decision",
          "module": "quotes_module",
          "description": "The customer deciding, then accepted, declined, expired, withdrawn or replaced by a revision. Skipped for a draft quote placed as an order."
        },
        {
          "id": "quotes.order",
          "label": "Order",
          "module": "quotes_module",
          "description": "The order an accepted quote, or a draft quote placed as an order, became."
        }
      ],
      "about": "A quote is a priced proposal of catalogue lines for a customer. Staff, Claude or a channel make a draft quote with \"New draft quote…\", from a Promo Office tender, or with \"Revise quote…\". Publishing will fix the prices and start the expiry, 14 days by default. Sharing will email the customer's contact a link to the quote. When the customer accepts the quote, One will create the order. \"Place order…\" will turn a draft quote into an order without publishing the draft quote.",
      "state_meanings": {
        "draft": "Draft: the customer won't see the quote until you publish or share the quote.",
        "published": "Published: the customer can accept or decline the quote until the quote expires.",
        "accepted": "Accepted: One created an order from the quote.",
        "declined": "Declined: the customer declined the quote, which you can still revise.",
        "expired": "Expired: the customer can no longer accept the quote, which you can still revise.",
        "withdrawn": "Withdrawn: staff withdrew or discarded the quote, or a revised quote replaced the quote.",
        "ordered": "Ordered: One created an order from the draft quote without publishing the draft quote, so the customer will never see the quote."
      },
      "state_tones": {
        "draft": "neutral",
        "published": "in_progress",
        "accepted": "done",
        "declined": "stopped",
        "expired": "stopped",
        "withdrawn": "stopped",
        "ordered": "done"
      },
      "commands": [
        "quotes.publish_quote",
        "catalogue.add_catalogue_item_to_draft_quote",
        "catalogue.add_bespoke_line_to_draft_quote",
        "catalogue.reprice_draft_quote",
        "quotes.set_quote_contact",
        "quotes.set_quote_delivery_address",
        "quotes.change_quote_owner",
        "quotes.share_quote",
        "quotes.place_draft_quote",
        "quotes.accept_quote_on_behalf",
        "quotes.revise_quote",
        "quotes.create_draft_quote",
        "quotes.take_over_draft_quote_editing",
        "quotes.withdraw_quote",
        "quotes.discard_draft_quote",
        "order_channel_promo_office.request_merch_quote"
      ],
      "features": [
        "quotes"
      ]
    },
    {
      "id": "quotes.quote_line",
      "module": "quotes_module",
      "label": "Quote line",
      "plural": "Quote lines",
      "description": "One product or service on a quote, with its versions and any quantity options.",
      "identifier": {
        "kind": "field",
        "field": "line_ref",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "quotes.quote"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [
        "catalogue.set_draft_quote_line_quantity_options",
        "catalogue.change_bespoke_specification",
        "catalogue.request_supplier_quotes_for_draft_quote_line",
        "catalogue.add_supplier_quote_you_have",
        "catalogue.change_draft_quote_line_supplier",
        "quotes.resolve_possible_alternative",
        "quotes.set_quote_version_labels",
        "catalogue.set_draft_quote_line_optional",
        "catalogue.remove_draft_quote_line"
      ],
      "features": []
    },
    {
      "id": "quotes.tharstern_estimate",
      "module": "quotes_module",
      "label": "Tharstern estimate",
      "plural": "Tharstern estimates",
      "description": "A price Tharstern gave a customer, with the other versions of its revision chain; read-only history.",
      "identifier": {
        "kind": "field",
        "field": "tharstern_ref",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "open",
        "won",
        "lost",
        "superseded",
        "tender",
        "stale"
      ],
      "list": null,
      "record": {
        "path": "/views/tharstern-estimate-360/{tharstern_ref}",
        "path_param": "tharstern_ref",
        "title_field": "description",
        "state_fields": [
          "status"
        ],
        "state_labels": {
          "status": {
            "lost": "Lost",
            "open": "Open",
            "stale": "Stale",
            "superseded": "Superseded",
            "tender": "Tender",
            "won": "Ordered"
          }
        },
        "facts": [
          {
            "field": "tharstern_ref",
            "label": "Tharstern estimate number",
            "kind": "text"
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text"
          },
          {
            "field": "estimate_date",
            "label": "Date",
            "kind": "date"
          },
          {
            "field": "quantity",
            "label": "Quantity",
            "kind": "number"
          },
          {
            "field": "value_pence",
            "label": "Value",
            "kind": "money"
          },
          {
            "field": "customer_contact",
            "label": "Customer contact",
            "kind": "text"
          },
          {
            "field": "sales_rep",
            "label": "Sales rep",
            "kind": "text"
          },
          {
            "field": "estimator",
            "label": "Estimator",
            "kind": "text"
          },
          {
            "field": "tharstern_status",
            "label": "Tharstern status",
            "kind": "text"
          },
          {
            "field": "product_type_name",
            "label": "Product type",
            "kind": "text"
          },
          {
            "field": "revised_from_ref",
            "label": "Revised from Tharstern estimate number",
            "kind": "text",
            "ref": "quotes.tharstern_estimate"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "quotes.tharstern_estimate_version",
          "label": "Other versions",
          "field": "versions",
          "key_field": "tharstern_ref",
          "columns": [
            {
              "field": "tharstern_ref",
              "label": "Tharstern estimate number",
              "kind": "text"
            },
            {
              "field": "estimate_date",
              "label": "Date",
              "kind": "date"
            },
            {
              "field": "value_pence",
              "label": "Value",
              "kind": "money"
            },
            {
              "field": "status",
              "label": "Status",
              "kind": "state",
              "enum_labels": {
                "lost": "Lost",
                "open": "Open",
                "stale": "Stale",
                "superseded": "Superseded",
                "tender": "Tender",
                "won": "Ordered"
              }
            }
          ],
          "paged": false,
          "parent_filter": null,
          "opens": "quotes.tharstern_estimate"
        }
      ],
      "related": [],
      "lifecycle": [],
      "about": "A Tharstern estimate is the frozen copy of a price Tharstern gave a customer, since the 3 June 2025 cutover. The Tharstern estimate number comes from Tharstern, and each version of the Tharstern estimate keeps its own Tharstern estimate number. The Tharstern estimate is read-only in One: nothing in One will change the Tharstern estimate. The Quotes list shows the latest version of each Tharstern estimate beside One's own quotes; the Latest version only filter, when turned off, will show every version. \"Add to draft quote…\", \"Quote now…\" and \"Order now…\" turn the Tharstern estimate into a draft quote line, a quote or an order, priced today.",
      "state_meanings": {
        "open": "Open: Tharstern recorded no decision, and the Tharstern estimate is under 90 days old.",
        "won": "Ordered: Tharstern recorded the Tharstern estimate as ordered.",
        "lost": "Lost: Tharstern recorded the Tharstern estimate as lost; Tharstern status gives the reason.",
        "superseded": "Superseded: a revision replaced the Tharstern estimate.",
        "tender": "Tender: the Tharstern estimate priced a tender.",
        "stale": "Stale: Tharstern recorded no decision, and the Tharstern estimate is over 90 days old."
      },
      "state_tones": {
        "open": "in_progress",
        "won": "done",
        "lost": "stopped",
        "superseded": "neutral",
        "tender": "in_progress",
        "stale": "neutral"
      },
      "commands": [
        "catalogue.add_tharstern_estimate_to_draft_quote",
        "catalogue.quote_tharstern_estimate_now",
        "catalogue.order_tharstern_estimate_now"
      ],
      "features": [
        "tharstern_history"
      ]
    },
    {
      "id": "quotes.tharstern_estimate_version",
      "module": "quotes_module",
      "label": "Other version",
      "plural": "Other versions",
      "description": "Another version of a Tharstern estimate, from the same revision chain.",
      "identifier": {
        "kind": "field",
        "field": "tharstern_ref",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "open",
        "won",
        "lost",
        "superseded",
        "tender",
        "stale"
      ],
      "list": null,
      "record": null,
      "parents": [
        "quotes.tharstern_estimate"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "open": "in_progress",
        "won": "done",
        "lost": "stopped",
        "superseded": "neutral",
        "tender": "in_progress",
        "stale": "neutral"
      },
      "commands": [],
      "features": []
    },
    {
      "id": "recommendations_generation.alert",
      "module": "recommendations_generation_module",
      "label": "Customer alert",
      "plural": "Customer alerts",
      "description": "A condition raised against a customer that requires user attention.",
      "identifier": {
        "kind": "uuid",
        "field": "alert_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "open",
        "converted_to_task",
        "dismissed",
        "snoozed"
      ],
      "list": {
        "path": "/views/alerts",
        "key_field": "alert_id",
        "title_field": "title",
        "columns": [
          {
            "field": "title",
            "label": "Alert",
            "kind": "text"
          },
          {
            "field": "severity",
            "label": "Severity",
            "kind": "state",
            "enum_labels": {
              "critical": "Critical",
              "high": "High",
              "low": "Low",
              "medium": "Medium"
            }
          },
          {
            "field": "created_at",
            "label": "Raised",
            "kind": "datetime"
          },
          {
            "field": "status",
            "label": "Status",
            "kind": "state",
            "enum_labels": {
              "converted_to_task": "Converted to ticket",
              "dismissed": "Dismissed",
              "open": "Open",
              "snoozed": "Snoozed"
            }
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text"
          },
          {
            "field": "ticket_title",
            "label": "Ticket",
            "kind": "text",
            "ref": "workflow.ticket",
            "ref_id_field": "converted_to_task_id"
          }
        ],
        "filters": [
          {
            "param": "status",
            "label": "Status",
            "kind": "enum",
            "options": [
              [
                "open",
                "Open"
              ],
              [
                "dismissed",
                "Dismissed"
              ],
              [
                "converted_to_task",
                "Converted to ticket"
              ],
              [
                "snoozed",
                "Snoozed"
              ]
            ],
            "description": "Filter by workflow status."
          },
          {
            "param": "severity",
            "label": "Severity",
            "kind": "enum",
            "options": [
              [
                "critical",
                "Critical"
              ],
              [
                "high",
                "High"
              ],
              [
                "medium",
                "Medium"
              ],
              [
                "low",
                "Low"
              ]
            ],
            "description": "Filter by severity."
          },
          {
            "param": "customer_id",
            "label": "Customer code",
            "kind": "text",
            "options": [],
            "description": "Filter to alerts linked to this customer."
          },
          {
            "param": "alert_family",
            "label": "Alert family",
            "kind": "text",
            "options": [],
            "description": "Filter by alert family (broad grouping, e.g. risk)."
          },
          {
            "param": "alert_name",
            "label": "Rule",
            "kind": "text",
            "options": [],
            "description": "Filter by specific alert type/name (e.g. 'Account softening')."
          },
          {
            "param": "ticket_id",
            "label": "Ticket ID",
            "kind": "text",
            "options": [],
            "description": "Filter to the alerts behind this ticket: the ones converted into it, and the ones it is raised from. A ticket's history keeps its alert after the pipeline stops raising it; an account-health or critical-alert ticket lists the customer's alerts that are still open."
          }
        ],
        "sorts": [
          [
            "created_at",
            "Raised"
          ],
          [
            "severity",
            "Severity"
          ],
          [
            "title",
            "Alert"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "open",
            "label": "Open",
            "params": {
              "status": "open"
            },
            "count": true,
            "default": false
          },
          {
            "id": "snoozed",
            "label": "Snoozed",
            "params": {
              "status": "snoozed"
            },
            "count": false,
            "default": false
          },
          {
            "id": "dismissed",
            "label": "Dismissed",
            "params": {
              "status": "dismissed"
            },
            "count": false,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/views/alerts/{alert_id}",
        "path_param": "alert_id",
        "title_field": "title",
        "state_fields": [
          "status",
          "severity"
        ],
        "state_labels": {
          "status": {
            "converted_to_task": "Converted to ticket",
            "dismissed": "Dismissed",
            "open": "Open",
            "snoozed": "Snoozed"
          },
          "severity": {
            "critical": "Critical",
            "high": "High",
            "low": "Low",
            "medium": "Medium"
          }
        },
        "facts": [
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text"
          },
          {
            "field": "ticket_title",
            "label": "Ticket",
            "kind": "text",
            "ref": "workflow.ticket",
            "ref_id_field": "converted_to_task_id"
          },
          {
            "field": "description",
            "label": "Description",
            "kind": "text"
          },
          {
            "field": "signal_commentary",
            "label": "Why this fired",
            "kind": "text"
          },
          {
            "field": "alert_name",
            "label": "Rule",
            "kind": "text"
          },
          {
            "field": "signal_summary",
            "label": "Driving signal",
            "kind": "text"
          },
          {
            "field": "segment_id",
            "label": "Segment",
            "kind": "text"
          },
          {
            "field": "created_at",
            "label": "Raised",
            "kind": "datetime"
          },
          {
            "field": "snoozed_until",
            "label": "Snoozed until",
            "kind": "datetime"
          },
          {
            "field": "snooze_reason",
            "label": "Snooze note",
            "kind": "text"
          },
          {
            "field": "resolved_at",
            "label": "Resolved",
            "kind": "datetime"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [
        {
          "object": "workflow.ticket",
          "label": "Tickets",
          "params": {
            "id": "{converted_to_task_id}"
          },
          "secondary": true
        }
      ],
      "lifecycle": [],
      "about": "A customer alert flags a condition on a customer that needs attention, such as a customer overdue for an order. The recommendation pipeline raises alerts from the customer's metrics and signals. You can dismiss, snooze or convert an open alert to a ticket. Dismissing will remove the alert from everyone's open alerts. Operational alerts are a separate object, about orders and purchase orders.",
      "state_meanings": {
        "open": "Open: the alert will stay in the open alerts until someone dismisses, snoozes or converts the alert.",
        "converted_to_task": "Converted to ticket: the work on the alert will continue on the ticket.",
        "dismissed": "Dismissed: the alert has left everyone's open alerts.",
        "snoozed": "Snoozed: the alert will come back to the open alerts on the snooze date."
      },
      "state_tones": {
        "open": "attention",
        "converted_to_task": "in_progress",
        "dismissed": "neutral",
        "snoozed": "neutral",
        "critical": "stopped",
        "high": "attention",
        "medium": "neutral",
        "low": "neutral"
      },
      "commands": [
        "recommendations_user_lifecycle.convert_alert_to_ticket",
        "recommendations_user_lifecycle.snooze_alert",
        "recommendations_user_lifecycle.dismiss_alert"
      ],
      "features": [
        "recommendations"
      ]
    },
    {
      "id": "recommendations_generation.opportunity",
      "module": "recommendations_generation_module",
      "label": "Customer opportunity",
      "plural": "Customer opportunities",
      "description": "A recommended next action for a user to take with a customer (formerly NBA / next-best-action).",
      "identifier": {
        "kind": "uuid",
        "field": "opportunity_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "open",
        "done",
        "converted_to_task",
        "dismissed"
      ],
      "list": {
        "path": "/views/opportunities",
        "key_field": "opportunity_id",
        "title_field": "title",
        "columns": [
          {
            "field": "title",
            "label": "Opportunity",
            "kind": "text"
          },
          {
            "field": "priority",
            "label": "Priority",
            "kind": "state",
            "enum_labels": {
              "consider": "Consider",
              "recommended": "Recommended",
              "urgent": "Urgent"
            }
          },
          {
            "field": "due_date",
            "label": "Due",
            "kind": "date"
          },
          {
            "field": "created_at",
            "label": "Raised",
            "kind": "datetime"
          },
          {
            "field": "status",
            "label": "Status",
            "kind": "state",
            "enum_labels": {
              "converted_to_task": "Converted to ticket",
              "dismissed": "Dismissed",
              "done": "Done",
              "open": "Open"
            }
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text"
          },
          {
            "field": "ticket_title",
            "label": "Ticket",
            "kind": "text",
            "ref": "workflow.ticket",
            "ref_id_field": "converted_to_task_id"
          }
        ],
        "filters": [
          {
            "param": "status",
            "label": "Status",
            "kind": "enum",
            "options": [
              [
                "open",
                "Open"
              ],
              [
                "done",
                "Done"
              ],
              [
                "dismissed",
                "Dismissed"
              ],
              [
                "converted_to_task",
                "Converted to ticket"
              ]
            ],
            "description": "Filter by workflow status."
          },
          {
            "param": "priority",
            "label": "Priority",
            "kind": "enum",
            "options": [
              [
                "urgent",
                "Urgent"
              ],
              [
                "recommended",
                "Recommended"
              ],
              [
                "consider",
                "Consider"
              ]
            ],
            "description": "Filter by priority."
          },
          {
            "param": "customer_id",
            "label": "Customer code",
            "kind": "text",
            "options": [],
            "description": "Filter to opportunities linked to this customer."
          },
          {
            "param": "opportunity_family",
            "label": "Opportunity family",
            "kind": "text",
            "options": [],
            "description": "Filter by opportunity family (broad grouping, e.g. growth)."
          },
          {
            "param": "opportunity_name",
            "label": "Rule",
            "kind": "text",
            "options": [],
            "description": "Filter by specific opportunity type/name (e.g. 'Upsell conversation')."
          },
          {
            "param": "ticket_id",
            "label": "Ticket ID",
            "kind": "text",
            "options": [],
            "description": "Filter to the opportunities converted into this ticket, including any the pipeline has since resolved: a ticket's history keeps its opportunity."
          }
        ],
        "sorts": [
          [
            "created_at",
            "Raised"
          ],
          [
            "priority",
            "Priority"
          ],
          [
            "due_date",
            "Due"
          ],
          [
            "title",
            "Opportunity"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "open",
            "label": "Open",
            "params": {
              "status": "open"
            },
            "count": true,
            "default": false
          },
          {
            "id": "urgent",
            "label": "Urgent",
            "params": {
              "status": "open",
              "priority": "urgent"
            },
            "count": true,
            "default": false
          },
          {
            "id": "dismissed",
            "label": "Dismissed",
            "params": {
              "status": "dismissed"
            },
            "count": false,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/views/opportunities/{opportunity_id}",
        "path_param": "opportunity_id",
        "title_field": "title",
        "state_fields": [
          "status",
          "priority"
        ],
        "state_labels": {
          "status": {
            "converted_to_task": "Converted to ticket",
            "dismissed": "Dismissed",
            "done": "Done",
            "open": "Open"
          },
          "priority": {
            "consider": "Consider",
            "recommended": "Recommended",
            "urgent": "Urgent"
          }
        },
        "facts": [
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text"
          },
          {
            "field": "ticket_title",
            "label": "Ticket",
            "kind": "text",
            "ref": "workflow.ticket",
            "ref_id_field": "converted_to_task_id"
          },
          {
            "field": "rationale",
            "label": "Why we're suggesting this",
            "kind": "text"
          },
          {
            "field": "suggested_message",
            "label": "Suggested message",
            "kind": "text"
          },
          {
            "field": "suggested_action_type",
            "label": "Suggested action",
            "kind": "text"
          },
          {
            "field": "due_date",
            "label": "Due",
            "kind": "date"
          },
          {
            "field": "signal_commentary",
            "label": "Why this fired",
            "kind": "text"
          },
          {
            "field": "opportunity_name",
            "label": "Rule",
            "kind": "text"
          },
          {
            "field": "created_at",
            "label": "Raised",
            "kind": "datetime"
          },
          {
            "field": "resolved_at",
            "label": "Resolved",
            "kind": "datetime"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [
        {
          "object": "workflow.ticket",
          "label": "Tickets",
          "params": {
            "id": "{converted_to_task_id}"
          },
          "secondary": true
        }
      ],
      "lifecycle": [],
      "about": "A customer opportunity is a recommended next action to take with a customer. The recommendation pipeline raises opportunities with a rationale and a suggested message. You can dismiss an open opportunity or convert the opportunity to a ticket.",
      "state_meanings": {
        "open": "Open: the opportunity will wait for someone to dismiss or convert the opportunity.",
        "done": "Done: nothing in One marks an opportunity done yet.",
        "converted_to_task": "Converted to ticket: the work on the opportunity will continue on the ticket.",
        "dismissed": "Dismissed: the opportunity has left everyone's open opportunities."
      },
      "state_tones": {
        "open": "attention",
        "done": "done",
        "converted_to_task": "in_progress",
        "dismissed": "neutral",
        "urgent": "attention",
        "recommended": "in_progress",
        "consider": "neutral"
      },
      "commands": [
        "recommendations_user_lifecycle.convert_opportunity_to_ticket",
        "recommendations_user_lifecycle.dismiss_opportunity"
      ],
      "features": [
        "recommendations"
      ]
    },
    {
      "id": "sales_leads.lead",
      "module": "sales_leads_module",
      "label": "Lead",
      "plural": "Leads",
      "description": "A company a rep is pursuing that is not yet a Tharstern customer, its stage, contacts and follow-ups.",
      "identifier": {
        "kind": "uuid",
        "field": "lead_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "cold",
        "warm",
        "hot",
        "contact_made",
        "quoted",
        "follow_up",
        "first_order"
      ],
      "list": {
        "path": "/sales-leads",
        "key_field": "lead_id",
        "title_field": "company_name",
        "columns": [
          {
            "field": "lead_id",
            "label": "Lead",
            "kind": "identifier"
          },
          {
            "field": "company_name",
            "label": "Company",
            "kind": "text"
          },
          {
            "field": "stage",
            "label": "Stage",
            "kind": "state",
            "enum_labels": {
              "cold": "Cold",
              "contact_made": "Contact made",
              "first_order": "First order",
              "follow_up": "Follow-up",
              "hot": "Hot",
              "quoted": "Quoted",
              "warm": "Warm"
            }
          },
          {
            "field": "phone",
            "label": "Phone",
            "kind": "text"
          },
          {
            "field": "website",
            "label": "Website",
            "kind": "text"
          },
          {
            "field": "converted_customer_id",
            "label": "Tharstern customer",
            "kind": "identifier"
          },
          {
            "field": "created_at",
            "label": "Logged",
            "kind": "datetime"
          },
          {
            "field": "has_pending_match",
            "label": "Match to check",
            "kind": "boolean"
          }
        ],
        "filters": [
          {
            "param": "stage",
            "label": "Stage",
            "kind": "multi_enum",
            "options": [
              [
                "cold",
                "Cold"
              ],
              [
                "warm",
                "Warm"
              ],
              [
                "hot",
                "Hot"
              ],
              [
                "contact_made",
                "Contact made"
              ],
              [
                "quoted",
                "Quoted"
              ],
              [
                "follow_up",
                "Follow-up"
              ],
              [
                "first_order",
                "First order"
              ]
            ]
          },
          {
            "param": "owner_user_id",
            "label": "Owner",
            "kind": "entity",
            "options": [],
            "object": "users.user"
          },
          {
            "param": "has_pending_match",
            "label": "Match to check",
            "kind": "boolean",
            "options": []
          }
        ],
        "sorts": [
          [
            "company_name",
            "Company"
          ],
          [
            "stage",
            "Stage"
          ],
          [
            "created_at",
            "Logged"
          ],
          [
            "updated_at",
            "Updated At"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "match_to_check",
            "label": "Match to check",
            "params": {
              "has_pending_match": true
            },
            "count": true,
            "default": false
          },
          {
            "id": "hot",
            "label": "Hot",
            "params": {
              "stage": [
                "hot"
              ]
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/sales-leads/{lead_id}",
        "path_param": "lead_id",
        "title_field": "company_name",
        "state_fields": [
          "stage"
        ],
        "state_labels": {
          "stage": {
            "cold": "Cold",
            "contact_made": "Contact made",
            "first_order": "First order",
            "follow_up": "Follow-up",
            "hot": "Hot",
            "quoted": "Quoted",
            "warm": "Warm"
          }
        },
        "facts": [
          {
            "field": "phone",
            "label": "Phone",
            "kind": "text"
          },
          {
            "field": "website",
            "label": "Website",
            "kind": "text"
          },
          {
            "field": "converted_customer_id",
            "label": "Tharstern customer",
            "kind": "identifier"
          },
          {
            "field": "created_at",
            "label": "Logged",
            "kind": "datetime"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "sales_leads.lead_contact",
          "label": "Lead contacts",
          "field": "contacts",
          "key_field": "id",
          "columns": [
            {
              "field": "name",
              "label": "Name",
              "kind": "text"
            },
            {
              "field": "job_title",
              "label": "Job title",
              "kind": "text"
            },
            {
              "field": "phone",
              "label": "Phone",
              "kind": "text"
            },
            {
              "field": "email",
              "label": "Email",
              "kind": "text"
            },
            {
              "field": "referred_by",
              "label": "Referred by",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "sales_leads.lead_note",
          "label": "Notes",
          "field": "notes",
          "key_field": "id",
          "columns": [
            {
              "field": "note_text",
              "label": "Note",
              "kind": "text"
            },
            {
              "field": "created_at",
              "label": "Added",
              "kind": "datetime"
            },
            {
              "field": "stage_change_from",
              "label": "Moved from",
              "kind": "state",
              "enum_labels": {
                "cold": "Cold",
                "contact_made": "Contact made",
                "first_order": "First order",
                "follow_up": "Follow-up",
                "hot": "Hot",
                "quoted": "Quoted",
                "warm": "Warm"
              }
            },
            {
              "field": "stage_change_to",
              "label": "Moved to",
              "kind": "state",
              "enum_labels": {
                "cold": "Cold",
                "contact_made": "Contact made",
                "first_order": "First order",
                "follow_up": "Follow-up",
                "hot": "Hot",
                "quoted": "Quoted",
                "warm": "Warm"
              }
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "sales_leads.lead_attachment",
          "label": "Attachments",
          "field": "attachments",
          "key_field": "id",
          "columns": [
            {
              "field": "filename",
              "label": "File",
              "kind": "text"
            },
            {
              "field": "kind",
              "label": "Kind",
              "kind": "text",
              "enum_labels": {
                "email": "Email",
                "other": "Other",
                "quote": "Quote"
              }
            },
            {
              "field": "uploaded_at",
              "label": "Added",
              "kind": "datetime"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [],
      "lifecycle": [],
      "about": "A lead is a company that a sales rep is pursuing and that isn't a customer yet. Staff create a lead with \"New lead\". A landing-page submission from a new contact also creates a lead. The lead's owner moves the lead through the stages with \"Move stage\". Converting the lead will link the lead to a customer.",
      "state_meanings": {
        "cold": "Cold: every new lead starts at Cold.",
        "first_order": "First order: the stage can't change again."
      },
      "state_tones": {
        "cold": "neutral",
        "warm": "in_progress",
        "hot": "attention",
        "contact_made": "in_progress",
        "quoted": "in_progress",
        "follow_up": "attention",
        "first_order": "done"
      },
      "commands": [
        "sales_leads.advance_lead_stage",
        "sales_leads.create_lead",
        "sales_leads.add_lead_note",
        "sales_leads.add_lead_contact",
        "sales_leads.create_lead_email_draft",
        "sales_leads.file_lead_ticket",
        "sales_leads.add_lead_attachment",
        "sales_leads.update_lead",
        "sales_leads.convert_lead_to_cis_customer",
        "sales_leads.convert_lead_to_existing_customer",
        "sales_leads.convert_lead_to_customer",
        "sales_leads.resolve_lead_conversion_match"
      ],
      "features": []
    },
    {
      "id": "sales_leads.lead_attachment",
      "module": "sales_leads_module",
      "label": "Attachment",
      "plural": "Attachments",
      "description": "A quote, email or other document attached to a lead (stored in workflow's document store).",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "sales_leads.lead"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [
        "sales_leads.delete_lead_attachment"
      ],
      "features": []
    },
    {
      "id": "sales_leads.lead_contact",
      "module": "sales_leads_module",
      "label": "Lead contact",
      "plural": "Lead contacts",
      "description": "A person at a lead's company, their job title and how to reach them.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "sales_leads.lead"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [
        "sales_leads.update_lead_contact"
      ],
      "features": []
    },
    {
      "id": "sales_leads.lead_note",
      "module": "sales_leads_module",
      "label": "Note",
      "plural": "Notes",
      "description": "A note about a lead's company, tagged with the stage change it explains when it was logged with one (stored in workflow's notes).",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "sales_leads.lead"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": []
    },
    {
      "id": "stock.stock_allocation",
      "module": "stock_module",
      "label": "Allocation",
      "plural": "Allocations and back orders",
      "description": "A quantity of a stock item set aside for one from-stock order line, or that line's place on back order until supply fills it. A line the warehouse can cover only in part has an allocation for what is there and one back order for the shortfall.",
      "identifier": {
        "kind": "field",
        "field": "allocation_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "allocated",
        "back_order",
        "picked",
        "cancelled"
      ],
      "list": null,
      "record": null,
      "parents": [
        "stock.stock_item"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "allocated": "in_progress",
        "back_order": "attention",
        "picked": "done",
        "cancelled": "stopped"
      },
      "commands": [
        "stock.reprioritise_back_order"
      ],
      "features": []
    },
    {
      "id": "stock.stock_batch",
      "module": "stock_module",
      "label": "Batch",
      "plural": "Batches",
      "description": "One receipt of a stock item, with the artwork revision it was printed from, and withdrawn once its stock is written off.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "stock.stock_item"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [
        "stock.withdraw_stock_batch"
      ],
      "features": []
    },
    {
      "id": "stock.stock_holding",
      "module": "stock_module",
      "label": "Stock holding",
      "plural": "Stock holdings",
      "description": "How many of a stock item (or of one batch) sit in a warehouse, and the bin they are in.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "stock.stock_item"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "commands": [],
      "features": []
    },
    {
      "id": "stock.stock_item",
      "module": "stock_module",
      "label": "Stock item",
      "plural": "Stock items",
      "description": "A line of stock CIS holds and counts for an owning customer, with its quantities, re-order level and history.",
      "identifier": {
        "kind": "uuid",
        "field": "stock_item_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": {
        "path": "/stock/items",
        "key_field": "stock_item_id",
        "title_field": "stock_code",
        "columns": [
          {
            "field": "stock_code",
            "label": "Stock code",
            "kind": "text"
          },
          {
            "field": "description",
            "label": "Description",
            "kind": "text"
          },
          {
            "field": "reorder_level",
            "label": "Re-order level",
            "kind": "number"
          },
          {
            "field": "owner_name",
            "label": "Owner",
            "kind": "text"
          },
          {
            "field": "held_for_name",
            "label": "Held for",
            "kind": "text",
            "ref": "customers.customer",
            "ref_id_field": "held_for_customer_code"
          },
          {
            "field": "on_hand",
            "label": "On hand",
            "kind": "number"
          },
          {
            "field": "pending_despatch",
            "label": "Pending despatch",
            "kind": "number"
          },
          {
            "field": "projected",
            "label": "Projected",
            "kind": "number"
          }
        ],
        "filters": [
          {
            "param": "include_archived",
            "label": "Archived too",
            "kind": "boolean",
            "options": [],
            "description": "Archived stock items are left out unless this is true."
          },
          {
            "param": "needs_reorder",
            "label": "Needs re-order",
            "kind": "boolean",
            "options": [],
            "description": "Only stock items whose projected stock is at or below the re-order level."
          },
          {
            "param": "location_id",
            "label": "Warehouse ID",
            "kind": "text",
            "options": [],
            "description": "Only stock items with stock at this warehouse. Each row will show the stock on hand there."
          },
          {
            "param": "owner_customer_id",
            "label": "Owner ID",
            "kind": "text",
            "options": [],
            "description": "Only this customer's stock items."
          },
          {
            "param": "held_for_customer_id",
            "label": "Held for",
            "kind": "entity",
            "options": [],
            "object": "customers.customer",
            "entity_value": "id",
            "description": "Only stock held for this customer: the customer's own, or Pollards' own stock kept in the customer's range."
          }
        ],
        "sorts": [
          [
            "stock_code",
            "Stock code"
          ],
          [
            "description",
            "Description"
          ],
          [
            "on_hand",
            "On hand"
          ],
          [
            "updated_at",
            "Updated At"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "needs_reorder",
            "label": "Needs re-order",
            "params": {
              "needs_reorder": true
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/views/stock-item/{stock_item_id}",
        "path_param": "stock_item_id",
        "title_field": "stock_code",
        "state_fields": [],
        "state_labels": {},
        "facts": [
          {
            "field": "description",
            "label": "Description",
            "kind": "text"
          },
          {
            "field": "owner_name",
            "label": "Owner",
            "kind": "text"
          },
          {
            "field": "held_for_name",
            "label": "Held for",
            "kind": "text",
            "ref": "customers.customer",
            "ref_id_field": "held_for_customer_code"
          },
          {
            "field": "on_hand",
            "label": "On hand",
            "kind": "number"
          },
          {
            "field": "on_order",
            "label": "On order",
            "kind": "number"
          },
          {
            "field": "pending_despatch",
            "label": "Pending despatch",
            "kind": "number"
          },
          {
            "field": "projected",
            "label": "Projected",
            "kind": "number"
          },
          {
            "field": "reorder_level",
            "label": "Re-order level",
            "kind": "number"
          },
          {
            "field": "tharstern_template_estimate_id",
            "label": "Tharstern template estimate",
            "kind": "identifier"
          }
        ],
        "fact_groups": [
          {
            "label": null,
            "fields": [
              "on_hand",
              "on_order",
              "pending_despatch",
              "projected"
            ]
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "stock.stock_batch",
          "label": "Batches",
          "field": "batches",
          "key_field": "id",
          "columns": [
            {
              "field": "batch_no",
              "label": "Batch",
              "kind": "number"
            },
            {
              "field": "artwork_revision_id",
              "label": "Artwork revision",
              "kind": "text"
            },
            {
              "field": "goods_in_ref",
              "label": "Goods-in reference",
              "kind": "text"
            },
            {
              "field": "on_hand",
              "label": "On hand",
              "kind": "number"
            },
            {
              "field": "valid_until",
              "label": "Valid until",
              "kind": "date"
            },
            {
              "field": "trading_company",
              "label": "Trading company",
              "kind": "text",
              "ref": "trading_companies.trading_company",
              "ref_id_field": "trading_company_code"
            },
            {
              "field": "created_at",
              "label": "Received",
              "kind": "datetime"
            },
            {
              "field": "withdrawn_at",
              "label": "Withdrawn",
              "kind": "datetime"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "stock.stock_holding",
          "label": "Stock holdings",
          "field": "holdings",
          "key_field": "id",
          "columns": [
            {
              "field": "location_name",
              "label": "Warehouse",
              "kind": "text"
            },
            {
              "field": "batch_no",
              "label": "Batch",
              "kind": "number"
            },
            {
              "field": "artwork_revision_id",
              "label": "Artwork revision",
              "kind": "text"
            },
            {
              "field": "position",
              "label": "Bin",
              "kind": "text"
            },
            {
              "field": "on_hand",
              "label": "On hand",
              "kind": "number"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "stock.stock_pick",
          "label": "Picks",
          "field": "open_picks",
          "key_field": "id",
          "columns": [
            {
              "field": "picked_for_ref",
              "label": "For",
              "kind": "text",
              "ref": "orders.order"
            },
            {
              "field": "location_name",
              "label": "Warehouse",
              "kind": "text"
            },
            {
              "field": "quantity",
              "label": "Quantity",
              "kind": "number"
            },
            {
              "field": "booked_out_quantity",
              "label": "Despatched so far",
              "kind": "number"
            },
            {
              "field": "state",
              "label": "State",
              "kind": "state",
              "enum_labels": {
                "booked_out": "Despatched",
                "cancelled": "Cancelled",
                "open": "Open"
              }
            },
            {
              "field": "created_at",
              "label": "Picked",
              "kind": "datetime"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "stock.stock_allocation",
          "label": "Allocations and back orders",
          "field": "claims",
          "key_field": "allocation_id",
          "columns": [
            {
              "field": "order_line_ref",
              "label": "Order line",
              "kind": "text",
              "ref": "orders.order"
            },
            {
              "field": "location_name",
              "label": "Warehouse",
              "kind": "text"
            },
            {
              "field": "quantity",
              "label": "Quantity",
              "kind": "number"
            },
            {
              "field": "state",
              "label": "State",
              "kind": "state",
              "enum_labels": {
                "allocated": "Allocated",
                "back_order": "Back order",
                "cancelled": "Cancelled",
                "picked": "Picked"
              }
            },
            {
              "field": "available_from",
              "label": "Stock there from",
              "kind": "date"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [],
      "lifecycle": [],
      "about": "A stock item is a line of stock that One holds and counts for an owning customer. Pollards' own stock belongs to the customer \"Pollards (own stock)\". The stock item is held for the customer whose range it is kept in: the owner when a customer owns it, or the customer Pollards keeps it for. Staff create a stock item with \"New stock item\" or \"New stock item from catalogue\". A new stock item starts with nothing on hand. Release turns an order line's stock allocation into a pick.",
      "commands": [
        "stock.adjust_stock_in",
        "stock.create_stock_item",
        "stock.create_stock_item_from_repeatable_item",
        "stock.adjust_stock_out",
        "stock.transfer_stock",
        "stock_replenishment.replenish_now",
        "stock_replenishment.set_replenishment_policy",
        "stock.set_stock_holding_position",
        "stock.set_stock_item_held_for",
        "stock.update_stock_item",
        "stock.change_stock_item_owner",
        "stock.restore_stock_item",
        "stock.archive_stock_item"
      ],
      "features": [
        "stock_control"
      ]
    },
    {
      "id": "stock.stock_pick",
      "module": "stock_module",
      "label": "Pick",
      "plural": "Picks",
      "description": "A quantity of a stock item claimed at one warehouse for one despatch, until it is booked out or cancelled.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "open",
        "booked_out",
        "cancelled"
      ],
      "list": null,
      "record": null,
      "parents": [
        "stock.stock_item"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "open": "in_progress",
        "booked_out": "done",
        "cancelled": "stopped"
      },
      "commands": [],
      "features": []
    },
    {
      "id": "stock.warehouse",
      "module": "stock_module",
      "label": "Warehouse",
      "plural": "Warehouses",
      "description": "A place where goods are received and stock is held, operated by a supplier.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "suppliers.supplier"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A warehouse is a place where goods are received and stock is held. A supplier operates every warehouse: Pollards (in-house) operates ours. Staff add a warehouse with \"Add warehouse…\" on the supplier that operates the warehouse, and give the warehouse a collection contact so a courier can collect from it. \"Deactivate\" stops stock being booked in there or picked from it, once the warehouse holds none.",
      "commands": [
        "stock.set_warehouse_contact",
        "stock.reactivate_warehouse",
        "stock.deactivate_warehouse"
      ],
      "features": []
    },
    {
      "id": "stock_replenishment.replenishment_proposal",
      "module": "stock_replenishment_module",
      "label": "Replenishment proposal",
      "plural": "Replenishment proposals",
      "description": "One computed need to top a stock item up, waiting for a person or raised as an order, with what became of it.",
      "identifier": {
        "kind": "uuid",
        "field": "proposal_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "open",
        "raised",
        "dismissed",
        "failed"
      ],
      "list": {
        "path": "/views/stock-replenishment/proposals",
        "key_field": "proposal_id",
        "title_field": "stock_code",
        "columns": [
          {
            "field": "stock_code",
            "label": "Stock code",
            "kind": "text"
          },
          {
            "field": "owner_name",
            "label": "Owner",
            "kind": "text"
          },
          {
            "field": "trigger",
            "label": "Trigger",
            "kind": "text",
            "enum_labels": {
              "below_level": "Below its re-order level",
              "replenish_now": "Replenish now"
            }
          },
          {
            "field": "state",
            "label": "State",
            "kind": "state",
            "enum_labels": {
              "dismissed": "Dismissed",
              "failed": "Failed",
              "open": "Open",
              "raised": "Raised"
            }
          },
          {
            "field": "proposed_quantity",
            "label": "Proposed quantity",
            "kind": "number"
          },
          {
            "field": "stage",
            "label": "Progress",
            "kind": "text",
            "enum_labels": {
              "arrived_not_in_stock": "Arrived, not in stock",
              "cancelled": "Cancelled",
              "despatched": "Despatched",
              "in_stock": "In stock",
              "placed": "Placed with the supplier",
              "to_place": "To place with the supplier",
              "waiting_on_credit": "Waiting on credit",
              "waiting_to_release": "Waiting to be released"
            }
          },
          {
            "field": "expected_arrival_on",
            "label": "Expected arrival",
            "kind": "date"
          },
          {
            "field": "created_at",
            "label": "Proposed",
            "kind": "datetime"
          }
        ],
        "filters": [
          {
            "param": "state",
            "label": "State",
            "kind": "enum",
            "options": [
              [
                "open",
                "Open"
              ],
              [
                "raised",
                "Raised"
              ],
              [
                "dismissed",
                "Dismissed"
              ],
              [
                "failed",
                "Failed"
              ]
            ]
          },
          {
            "param": "owner_customer_id",
            "label": "Owner ID",
            "kind": "text",
            "options": []
          },
          {
            "param": "mode",
            "label": "Mode at the trigger",
            "kind": "enum",
            "options": [
              [
                "automatic",
                "Automatic"
              ],
              [
                "advise",
                "Advise"
              ]
            ]
          },
          {
            "param": "stage",
            "label": "Progress",
            "kind": "enum",
            "options": [
              [
                "still_coming",
                "Still coming"
              ],
              [
                "waiting_to_place",
                "Waiting to place"
              ],
              [
                "arrived_not_in_stock",
                "Arrived, not in stock"
              ],
              [
                "done",
                "Done"
              ]
            ],
            "description": "A raised order's progress: still coming (anything left to arrive), waiting to place (not yet with the supplier), arrived but not in stock, or done (in stock, or cancelled)."
          }
        ],
        "sorts": [
          [
            "created_at",
            "Proposed"
          ],
          [
            "stock_code",
            "Stock code"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "open",
            "label": "Open",
            "params": {
              "state": "open"
            },
            "count": true,
            "default": false
          },
          {
            "id": "failed",
            "label": "Failed",
            "params": {
              "state": "failed"
            },
            "count": true,
            "default": false
          },
          {
            "id": "still_coming",
            "label": "Still coming",
            "params": {
              "stage": "still_coming"
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/views/stock-replenishment/proposals/{proposal_id}",
        "path_param": "proposal_id",
        "title_field": "stock_code",
        "state_fields": [
          "state"
        ],
        "state_labels": {
          "state": {
            "dismissed": "Dismissed",
            "failed": "Failed",
            "open": "Open",
            "raised": "Raised"
          }
        },
        "facts": [
          {
            "field": "owner_name",
            "label": "Owner",
            "kind": "text"
          },
          {
            "field": "proposed_quantity",
            "label": "Proposed quantity",
            "kind": "number"
          },
          {
            "field": "trigger",
            "label": "Trigger",
            "kind": "text",
            "enum_labels": {
              "below_level": "Below its re-order level",
              "replenish_now": "Replenish now"
            }
          },
          {
            "field": "stage",
            "label": "Progress",
            "kind": "text",
            "enum_labels": {
              "arrived_not_in_stock": "Arrived, not in stock",
              "cancelled": "Cancelled",
              "despatched": "Despatched",
              "in_stock": "In stock",
              "placed": "Placed with the supplier",
              "to_place": "To place with the supplier",
              "waiting_on_credit": "Waiting on credit",
              "waiting_to_release": "Waiting to be released"
            }
          },
          {
            "field": "expected_arrival_on",
            "label": "Expected arrival",
            "kind": "date"
          },
          {
            "field": "created_at",
            "label": "Proposed",
            "kind": "datetime"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A replenishment proposal is one computed need to top up a stock item. Every five minutes, One proposes a top-up for each stock item at or below the re-order level. \"Replenish now\" on a stock item makes a proposal straight away. One raises the proposal as an order into stock, never as a purchase order. A stock item set to Advise will wait for a person to confirm or dismiss the proposal.",
      "state_meanings": {
        "open": "Open: the proposal will wait for you to use \"Confirm & raise\" or \"Dismiss\".",
        "raised": "Raised: One raised an order into stock, or recorded the order in a dry run.",
        "dismissed": "Dismissed: the next sweep will propose again while the stock item is at or below the re-order level.",
        "failed": "Failed: the sweep will retry the proposal, unless the proposal came from \"Replenish now\"."
      },
      "state_tones": {
        "open": "attention",
        "raised": "done",
        "dismissed": "neutral",
        "failed": "stopped"
      },
      "commands": [
        "stock_replenishment.confirm_replenishment_proposal",
        "stock_replenishment.dismiss_replenishment_proposal"
      ],
      "features": [
        "stock_replenishment"
      ]
    },
    {
      "id": "supplier_access.supplier_person",
      "module": "supplier_access_module",
      "label": "Supplier user",
      "plural": "Supplier users",
      "description": "Someone at a supplier who has, or had, a login to the supplier portal, invited by staff.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "invited",
        "active",
        "deactivated"
      ],
      "list": {
        "path": "/views/supplier-users",
        "key_field": "id",
        "title_field": "name",
        "columns": [
          {
            "field": "name",
            "label": "Name",
            "kind": "text"
          },
          {
            "field": "email",
            "label": "Email address",
            "kind": "text"
          },
          {
            "field": "supplier_name",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier",
            "ref_id_field": "supplier_no"
          },
          {
            "field": "supplier_no",
            "label": "Supplier number",
            "kind": "identifier",
            "ref": "suppliers.supplier"
          },
          {
            "field": "state",
            "label": "State",
            "kind": "state",
            "enum_labels": {
              "active": "Active",
              "deactivated": "Removed",
              "invited": "Invited"
            }
          },
          {
            "field": "added_at",
            "label": "Invited",
            "kind": "datetime"
          },
          {
            "field": "first_signed_in_at",
            "label": "First signed in",
            "kind": "datetime"
          }
        ],
        "filters": [
          {
            "param": "supplier_id",
            "label": "Supplier ID",
            "kind": "text",
            "options": [],
            "description": "The supplier's id, as on the supplier's record, not the supplier number (SUP-…)."
          },
          {
            "param": "state",
            "label": "State",
            "kind": "enum",
            "options": [
              [
                "invited",
                "Invited"
              ],
              [
                "active",
                "Active"
              ],
              [
                "deactivated",
                "Removed"
              ]
            ]
          }
        ],
        "sorts": [
          [
            "name",
            "Name"
          ],
          [
            "email",
            "Email address"
          ],
          [
            "supplier_name",
            "Supplier"
          ],
          [
            "state",
            "State"
          ],
          [
            "added_at",
            "Invited"
          ],
          [
            "first_signed_in_at",
            "First signed in"
          ]
        ],
        "searchable": true,
        "queues": []
      },
      "record": {
        "path": "/supplier-access/supplier-users/{id}",
        "path_param": "id",
        "title_field": "name",
        "state_fields": [
          "state"
        ],
        "state_labels": {
          "state": {
            "active": "Active",
            "deactivated": "Removed",
            "invited": "Invited"
          }
        },
        "facts": [
          {
            "field": "email",
            "label": "Email address",
            "kind": "text"
          },
          {
            "field": "supplier_name",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier",
            "ref_id_field": "supplier_no"
          },
          {
            "field": "supplier_no",
            "label": "Supplier number",
            "kind": "identifier",
            "ref": "suppliers.supplier"
          },
          {
            "field": "added_at",
            "label": "Invited",
            "kind": "datetime"
          },
          {
            "field": "first_signed_in_at",
            "label": "First signed in",
            "kind": "datetime"
          },
          {
            "field": "deactivated_at",
            "label": "Removed",
            "kind": "datetime"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [
        "suppliers.supplier"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A supplier user is someone at a supplier with a login to the supplier portal, where the supplier acknowledges and despatches purchase orders. Staff add one with \"Invite…\" on the supplier, which emails the person a sign-in link. \"Resend invite\" sends the sign-in link again. \"Remove access…\" ends the login for good: a person who comes back is invited again.",
      "state_meanings": {
        "invited": "Invited: the person can sign in with the link in the invite email.",
        "active": "Active: the person has signed in and can use the supplier portal.",
        "deactivated": "Removed: the person can't sign in to the supplier portal again."
      },
      "state_tones": {
        "invited": "in_progress",
        "active": "done",
        "deactivated": "neutral"
      },
      "commands": [
        "supplier_access.resend_supplier_invite",
        "supplier_access.deactivate_supplier_person"
      ],
      "features": [
        "supplier_portal"
      ]
    },
    {
      "id": "supplier_quotes.supplier_price",
      "module": "supplier_quotes_module",
      "label": "Supplier price",
      "plural": "Supplier prices",
      "description": "One supplier's delivered price for one quantity of a quote line.",
      "identifier": {
        "kind": "field",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "asked",
        "priced",
        "not_quoted",
        "chosen",
        "price_out_of_date",
        "not_needed"
      ],
      "list": null,
      "record": null,
      "parents": [
        "quotes.quote",
        "supplier_quotes.supplier_quote"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A supplier price is one supplier's delivered price for one quantity a quote line offers. Each quantity is priced from the supplier price chosen for it, so a line's quantities can come from different suppliers. A supplier quote lists its own prices; the customer's quote compares a line's suppliers quantity by quantity.",
      "state_meanings": {
        "asked": "Asked: we are waiting for the supplier's price for this quantity.",
        "priced": "Priced: the supplier priced this quantity, and the price can be used.",
        "not_quoted": "Not quoted: the supplier won't quote this quantity.",
        "chosen": "Chosen: this price prices the quantity on the line.",
        "price_out_of_date": "Out of date: the line changed after the supplier priced it, so they were asked again.",
        "not_needed": "Not needed: the quantity was taken off the line, or the line was priced from other quotes."
      },
      "state_tones": {
        "asked": "in_progress",
        "priced": "done",
        "not_quoted": "stopped",
        "chosen": "done",
        "price_out_of_date": "attention",
        "not_needed": "neutral"
      },
      "commands": [
        "catalogue.use_supplier_price"
      ],
      "features": []
    },
    {
      "id": "supplier_quotes.supplier_quote",
      "module": "supplier_quotes_module",
      "label": "Supplier quote",
      "plural": "Supplier quotes",
      "description": "One supplier's offer to make one quote line, at a delivered cost per quantity, until a stated date.",
      "identifier": {
        "kind": "cis_number",
        "field": "supplier_quote_no",
        "prefix": "SQ",
        "pattern": "^SQ-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}$",
        "line_pattern": "^SQ-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}\\.[0-9]{2,}$"
      },
      "states": [
        "asked",
        "quoted",
        "declined",
        "chosen",
        "quote_expired",
        "out_of_date",
        "withdrawn"
      ],
      "list": {
        "path": "/supplier-quotes",
        "key_field": "supplier_quote_no",
        "title_field": "title",
        "columns": [
          {
            "field": "supplier_quote_no",
            "label": "Supplier quote",
            "kind": "identifier"
          },
          {
            "field": "supplier_name",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier",
            "ref_id_field": "supplier_no"
          },
          {
            "field": "what",
            "label": "For",
            "kind": "text"
          },
          {
            "field": "waiting_for",
            "label": "Waiting for",
            "kind": "text"
          },
          {
            "field": "valid_until",
            "label": "Valid until",
            "kind": "date"
          },
          {
            "field": "state",
            "label": "State",
            "kind": "state",
            "enum_labels": {
              "asked": "Asked",
              "chosen": "Chosen",
              "declined": "Declined",
              "out_of_date": "Out of date",
              "quote_expired": "Expired",
              "quoted": "Quoted",
              "withdrawn": "Withdrawn"
            }
          }
        ],
        "filters": [
          {
            "param": "state",
            "label": "State",
            "kind": "multi_enum",
            "options": [
              [
                "asked",
                "Asked"
              ],
              [
                "quoted",
                "Quoted"
              ],
              [
                "declined",
                "Declined"
              ],
              [
                "chosen",
                "Chosen"
              ],
              [
                "quote_expired",
                "Expired"
              ],
              [
                "out_of_date",
                "Out of date"
              ],
              [
                "withdrawn",
                "Withdrawn"
              ]
            ],
            "description": "Only supplier quotes in any of these states, as shown."
          },
          {
            "param": "supplier_no",
            "label": "Supplier",
            "kind": "entity",
            "options": [],
            "object": "suppliers.supplier",
            "description": "Only this supplier's quotes, by supplier number (SUP-…)."
          },
          {
            "param": "quote_no",
            "label": "Quote number",
            "kind": "text",
            "options": [],
            "description": "Only the supplier quotes for lines of this quote, by quote number (QT-…)."
          },
          {
            "param": "has_asked_price",
            "label": "Waiting for an answer",
            "kind": "boolean",
            "options": [],
            "description": "Only the quotes with a price asked (true), or with none asked (false), whatever the quote's state."
          }
        ],
        "sorts": [
          [
            "valid_until",
            "Valid until"
          ],
          [
            "supplier_name",
            "Supplier"
          ],
          [
            "supplier_quote_no",
            "Supplier quote"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "asked",
            "label": "Waiting for an answer",
            "params": {
              "has_asked_price": true
            },
            "count": true,
            "default": false
          },
          {
            "id": "quoted",
            "label": "Quoted",
            "params": {
              "state": [
                "quoted"
              ]
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/supplier-quotes/{supplier_quote_no}",
        "path_param": "supplier_quote_no",
        "title_field": "title",
        "state_fields": [
          "state"
        ],
        "state_labels": {
          "state": {
            "asked": "Asked",
            "chosen": "Chosen",
            "declined": "Declined",
            "out_of_date": "Out of date",
            "quote_expired": "Expired",
            "quoted": "Quoted",
            "withdrawn": "Withdrawn"
          }
        },
        "facts": [
          {
            "field": "supplier_name",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier",
            "ref_id_field": "supplier_no"
          },
          {
            "field": "line_ref",
            "label": "Quote line",
            "kind": "text",
            "ref": "quotes.quote"
          },
          {
            "field": "waiting_for",
            "label": "Waiting for",
            "kind": "text"
          },
          {
            "field": "through",
            "label": "Through",
            "kind": "text"
          },
          {
            "field": "reply_by",
            "label": "Reply by",
            "kind": "date"
          },
          {
            "field": "specification",
            "label": "Specification for the supplier",
            "kind": "formatted_text"
          },
          {
            "field": "supplier_files",
            "label": "Files for the supplier",
            "kind": "file"
          },
          {
            "field": "wanted_by",
            "label": "Wanted by",
            "kind": "date"
          },
          {
            "field": "their_reference",
            "label": "Their reference",
            "kind": "text"
          },
          {
            "field": "valid_until",
            "label": "Valid until",
            "kind": "date"
          },
          {
            "field": "despatch_by",
            "label": "Despatch by",
            "kind": "date"
          },
          {
            "field": "quote_document",
            "label": "Their quote",
            "kind": "file"
          },
          {
            "field": "quote_file",
            "label": "Their quote",
            "kind": "text"
          },
          {
            "field": "declined_reason",
            "label": "Why they declined",
            "kind": "text"
          },
          {
            "field": "why_not_asked",
            "label": "Why we didn't ask them through One",
            "kind": "text"
          },
          {
            "field": "purchase_order_no",
            "label": "Purchase order",
            "kind": "text",
            "ref": "purchase_orders.purchase_order"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [
        "quotes.quote"
      ],
      "children": [
        {
          "object": "supplier_quotes.supplier_price",
          "label": "Supplier prices",
          "field": "prices",
          "key_field": "id",
          "columns": [
            {
              "field": "quantity",
              "label": "Quantity",
              "kind": "number"
            },
            {
              "field": "cost_pence",
              "label": "Their delivered cost",
              "kind": "money"
            },
            {
              "field": "state",
              "label": "State",
              "kind": "state",
              "enum_labels": {
                "asked": "Asked",
                "chosen": "Chosen",
                "not_needed": "Not needed",
                "not_quoted": "Not quoted",
                "price_out_of_date": "Out of date",
                "priced": "Priced"
              }
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [
        {
          "object": "comms_ingest.email",
          "label": "Emails",
          "params": {
            "tag_object": "supplier_quotes.supplier_quote",
            "tag_key": "{supplier_quote_no}"
          },
          "secondary": true
        }
      ],
      "lifecycle": [],
      "about": "A supplier quote is a supplier's offer to make one quote line, at a stated cost, until a stated date, under their own reference. It is delivered, and covers the line's versions and deliveries. The catalogue asks for it when a line is added; the one chosen prices the line, and the purchase order is placed against it. The supplier gives its prices in our supplier portal, and can change them there until our quote is sent to the customer.",
      "state_meanings": {
        "asked": "Asked: we are waiting for the supplier's answer.",
        "quoted": "Quoted: the supplier quoted, and the quote can be used.",
        "declined": "Declined: the supplier won't quote for this.",
        "chosen": "Chosen: the quote prices its line.",
        "quote_expired": "Expired: the quote ran out. Ask the supplier again before using it.",
        "out_of_date": "Out of date: the line changed after the supplier quoted, so they were asked again.",
        "withdrawn": "Withdrawn: we stopped asking, because the line was priced from another quote, or removed."
      },
      "state_tones": {
        "asked": "in_progress",
        "quoted": "done",
        "declined": "stopped",
        "chosen": "done",
        "quote_expired": "stopped",
        "out_of_date": "attention",
        "withdrawn": "stopped"
      },
      "commands": [
        "supplier_quotes.chase_supplier_quote",
        "supplier_quotes.record_supplier_quote_answer",
        "supplier_quotes.record_supplier_quote_decline",
        "supplier_quotes.add_supplier_quote_file"
      ],
      "features": []
    },
    {
      "id": "suppliers.supplier",
      "module": "suppliers_module",
      "label": "Supplier",
      "plural": "Suppliers",
      "description": "A business we buy goods or services from, numbered SUP-, with its payment terms, payment hold and delivery details.",
      "identifier": {
        "kind": "cis_number",
        "field": "supplier_no",
        "prefix": "SUP",
        "pattern": "^SUP-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}$",
        "line_pattern": "^SUP-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}\\.[0-9]{2,}$"
      },
      "states": [
        "active",
        "on_hold",
        "closed"
      ],
      "list": {
        "path": "/views/suppliers",
        "key_field": "supplier_no",
        "title_field": "name",
        "columns": [
          {
            "field": "supplier_no",
            "label": "Number",
            "kind": "identifier"
          },
          {
            "field": "supplier_code",
            "label": "Tharstern code",
            "kind": "text"
          },
          {
            "field": "name",
            "label": "Supplier",
            "kind": "text"
          },
          {
            "field": "status",
            "label": "Status",
            "kind": "state",
            "enum_labels": {
              "active": "Active",
              "closed": "Closed",
              "on_hold": "On hold"
            }
          },
          {
            "field": "payment_terms_days",
            "label": "Payment terms (days)",
            "kind": "number"
          },
          {
            "field": "xero_contact_state_label",
            "label": "Xero contact",
            "kind": "text"
          },
          {
            "field": "relationship_stage",
            "label": "Stage",
            "kind": "state",
            "enum_labels": {
              "commercials_agreed": "Commercials agreed",
              "contacted": "Contacted",
              "contracted": "Contracted",
              "identified": "Identified",
              "live": "Live",
              "paused": "Paused",
              "sla_agreed": "SLA agreed",
              "suggested": "Suggested"
            }
          },
          {
            "field": "owner_name",
            "label": "Owner",
            "kind": "text"
          },
          {
            "field": "target_rung_label",
            "label": "Target rung",
            "kind": "text"
          },
          {
            "field": "next_action_due_on",
            "label": "Due",
            "kind": "date"
          }
        ],
        "filters": [
          {
            "param": "status",
            "label": "Status",
            "kind": "multi_enum",
            "options": [
              [
                "active",
                "Active"
              ],
              [
                "on_hold",
                "On hold"
              ],
              [
                "closed",
                "Closed"
              ]
            ]
          },
          {
            "param": "relationship_stage",
            "label": "Stage",
            "kind": "multi_enum",
            "options": [],
            "description": "The relationship's stage."
          },
          {
            "param": "owner_user_id",
            "label": "Owner",
            "kind": "entity",
            "options": [],
            "object": "users.user",
            "description": "Who owns the next move."
          },
          {
            "param": "target_rung",
            "label": "Target rung",
            "kind": "text",
            "options": []
          },
          {
            "param": "next_action_due",
            "label": "Next action due",
            "kind": "enum",
            "options": [
              [
                "overdue",
                "Overdue"
              ],
              [
                "this_week",
                "This week"
              ],
              [
                "later",
                "Later"
              ],
              [
                "none",
                "No date"
              ]
            ],
            "description": "Whether the next action's date has passed, falls this week, later, or is not set."
          },
          {
            "param": "possible_duplicate",
            "label": "Possible duplicate",
            "kind": "boolean",
            "options": [],
            "description": "True lists the suppliers that look like one Tharstern split by company (the same name, a different Tharstern company); false lists the rest. A person folds each pair into one supplier."
          }
        ],
        "sorts": [],
        "searchable": true,
        "queues": [
          {
            "id": "on_hold",
            "label": "On hold",
            "params": {
              "status": [
                "on_hold"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "possible_duplicates",
            "label": "Possible duplicates",
            "params": {
              "possible_duplicate": true
            },
            "count": true,
            "default": false
          },
          {
            "id": "suggestions_to_review",
            "label": "Suggestions to review",
            "params": {
              "relationship_stage": [
                "suggested"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "due_this_week",
            "label": "Due this week",
            "params": {
              "next_action_due": "this_week"
            },
            "count": true,
            "default": false
          },
          {
            "id": "overdue",
            "label": "Overdue",
            "params": {
              "next_action_due": "overdue"
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/views/supplier/{supplier_no}",
        "path_param": "supplier_no",
        "title_field": "name",
        "state_fields": [
          "status",
          "relationship_stage"
        ],
        "state_labels": {
          "status": {
            "active": "Active",
            "closed": "Closed",
            "on_hold": "On hold"
          },
          "relationship_stage": {
            "commercials_agreed": "Commercials agreed",
            "contacted": "Contacted",
            "contracted": "Contracted",
            "identified": "Identified",
            "live": "Live",
            "paused": "Paused",
            "sla_agreed": "SLA agreed",
            "suggested": "Suggested"
          }
        },
        "facts": [
          {
            "field": "supplier_code",
            "label": "Tharstern code",
            "kind": "text"
          },
          {
            "field": "payment_terms_days",
            "label": "Payment terms (days)",
            "kind": "number"
          },
          {
            "field": "purchase_order_email",
            "label": "Purchase orders to",
            "kind": "text"
          },
          {
            "field": "quote_request_email",
            "label": "Quote requests to",
            "kind": "text"
          },
          {
            "field": "portal_view_href",
            "label": "Portal view",
            "kind": "link",
            "link_text": "View portal"
          },
          {
            "field": "xero_contact_state_label",
            "label": "Xero contact",
            "kind": "text"
          },
          {
            "field": "possible_duplicate_of",
            "label": "Possible duplicate of",
            "kind": "text",
            "ref": "suppliers.supplier"
          },
          {
            "field": "relationship_stage",
            "label": "Stage",
            "kind": "state",
            "enum_labels": {
              "commercials_agreed": "Commercials agreed",
              "contacted": "Contacted",
              "contracted": "Contracted",
              "identified": "Identified",
              "live": "Live",
              "paused": "Paused",
              "sla_agreed": "SLA agreed",
              "suggested": "Suggested"
            }
          },
          {
            "field": "stage_reason",
            "label": "Last stage change",
            "kind": "text"
          },
          {
            "field": "owner_name",
            "label": "Owner",
            "kind": "text"
          },
          {
            "field": "next_action",
            "label": "Next action",
            "kind": "text"
          },
          {
            "field": "next_action_due_on",
            "label": "Due",
            "kind": "date"
          },
          {
            "field": "credit_limit_pence",
            "label": "Credit limit",
            "kind": "money"
          },
          {
            "field": "rung_today_label",
            "label": "Rung today",
            "kind": "text"
          },
          {
            "field": "target_rung_label",
            "label": "Target rung",
            "kind": "text"
          },
          {
            "field": "why_not_deeper",
            "label": "Why not deeper",
            "kind": "text"
          },
          {
            "field": "moves_up_when",
            "label": "Moves up when",
            "kind": "text"
          }
        ],
        "fact_groups": [
          {
            "label": "Relationship",
            "fields": [
              "relationship_stage",
              "stage_reason",
              "owner_name",
              "next_action",
              "next_action_due_on",
              "credit_limit_pence"
            ]
          },
          {
            "label": "Integration ladder",
            "fields": [
              "rung_today_label",
              "target_rung_label",
              "why_not_deeper",
              "moves_up_when"
            ]
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "supplier_access.supplier_person",
          "label": "Supplier users",
          "field": "supplier_people",
          "key_field": "id",
          "columns": [
            {
              "field": "name",
              "label": "Name",
              "kind": "text"
            },
            {
              "field": "email",
              "label": "Email",
              "kind": "text"
            },
            {
              "field": "state",
              "label": "State",
              "kind": "state",
              "enum_labels": {
                "active": "Active",
                "deactivated": "Removed",
                "invited": "Invited"
              }
            }
          ],
          "paged": true,
          "parent_filter": "supplier_id"
        },
        {
          "object": "stock.warehouse",
          "label": "Warehouses",
          "field": "warehouses",
          "key_field": "id",
          "columns": [
            {
              "field": "name",
              "label": "Warehouse",
              "kind": "text"
            },
            {
              "field": "address",
              "label": "Address",
              "kind": "text"
            },
            {
              "field": "collection_contact",
              "label": "Collection contact",
              "kind": "text"
            },
            {
              "field": "status",
              "label": "Status",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "fulfilment_adapters.supplier_connection",
          "label": "Connections",
          "field": "connections",
          "key_field": "connection_id",
          "columns": [
            {
              "field": "area_label",
              "label": "Connection",
              "kind": "text"
            },
            {
              "field": "connected_through",
              "label": "Connected through",
              "kind": "text"
            },
            {
              "field": "our_admin",
              "label": "Our admin",
              "kind": "text"
            },
            {
              "field": "origin",
              "label": "Default or Set",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "suppliers.supplier_capability",
          "label": "Capabilities",
          "field": "capabilities",
          "key_field": "capability_id",
          "columns": [
            {
              "field": "title",
              "label": "Capability",
              "kind": "text"
            },
            {
              "field": "role",
              "label": "Role",
              "kind": "state",
              "enum_labels": {
                "candidate": "Candidate",
                "out_of_scope": "Out of scope",
                "primary": "Primary",
                "secondary": "Secondary"
              }
            },
            {
              "field": "note",
              "label": "Note",
              "kind": "text"
            },
            {
              "field": "set_by_name",
              "label": "Set by",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": "supplier_no"
        },
        {
          "object": "suppliers.supplier_account",
          "label": "Supplier accounts",
          "field": "supplier_accounts",
          "key_field": "supplier_account_id",
          "columns": [
            {
              "field": "trading_company",
              "label": "Trading company",
              "kind": "text",
              "ref": "trading_companies.trading_company",
              "ref_id_field": "trading_company_code"
            },
            {
              "field": "account_no",
              "label": "Our account number",
              "kind": "text"
            },
            {
              "field": "opened_on",
              "label": "Opened",
              "kind": "date"
            },
            {
              "field": "state",
              "label": "State",
              "kind": "state",
              "enum_labels": {
                "closed": "Closed",
                "open": "Open"
              }
            }
          ],
          "paged": false,
          "parent_filter": "supplier_no"
        }
      ],
      "related": [
        {
          "object": "purchase_orders.purchase_order",
          "label": "Purchase orders",
          "params": {
            "supplier_id": "{supplier_id}"
          }
        },
        {
          "object": "finance_purchase_ledger.purchase_invoice",
          "label": "Purchase invoices",
          "params": {
            "supplier_no": "{supplier_no}"
          }
        },
        {
          "object": "issues.issue",
          "label": "Issues",
          "params": {
            "supplier_no": "{supplier_no}"
          },
          "secondary": true
        },
        {
          "object": "supplier_quotes.supplier_quote",
          "label": "Supplier quotes",
          "params": {
            "supplier_no": "{supplier_no}"
          },
          "secondary": true
        },
        {
          "object": "finance_accruals_and_prepayments.prepayment",
          "label": "Prepayments",
          "params": {
            "supplier_no": "{supplier_no}"
          },
          "secondary": true
        },
        {
          "object": "finance_accruals_and_prepayments.recurring_accrual",
          "label": "Recurring accruals",
          "params": {
            "supplier_no": "{supplier_no}"
          },
          "secondary": true
        },
        {
          "object": "purchase_orders.price_increase",
          "label": "Price increases",
          "params": {
            "supplier_id": "{supplier_id}",
            "month": "this"
          },
          "secondary": true
        },
        {
          "object": "comms_ingest.email",
          "label": "Emails",
          "params": {
            "tag_object": "suppliers.supplier",
            "tag_key": "{supplier_no}"
          },
          "secondary": true
        }
      ],
      "lifecycle": [],
      "about": "A supplier is a business that One buys goods or services from. One loads suppliers from Tharstern every hour. \"New supplier\" creates a supplier that Tharstern doesn't know. Once a supplier exists, Tharstern no longer changes the supplier's name. One never deletes a supplier. A business both our companies buy from can appear twice: \"Possible duplicates\" lists those pairs, and \"Fold into another supplier\" joins a pair into one supplier. \"Purchase orders to\" is where our purchase orders go. \"Pollards (in-house)\" is Pollards' own production: it operates our own warehouses, receives no purchase orders and can't be put on hold.",
      "state_meanings": {
        "active": "Active: One can raise purchase orders with the supplier.",
        "on_hold": "On hold: no purchase invoice from the supplier will go to Xero until the hold is released.",
        "closed": "Closed: One will raise no purchase order with the supplier."
      },
      "state_tones": {
        "active": "done",
        "on_hold": "attention",
        "closed": "neutral",
        "suggested": "neutral",
        "identified": "neutral",
        "contacted": "in_progress",
        "commercials_agreed": "in_progress",
        "sla_agreed": "in_progress",
        "contracted": "in_progress",
        "live": "done",
        "paused": "neutral"
      },
      "commands": [
        "suppliers.create_supplier",
        "suppliers.suggest_supplier",
        "supplier_access.add_supplier_person",
        "stock.create_warehouse",
        "suppliers.record_relationship_stage",
        "suppliers.set_next_action",
        "suppliers.set_relationship_owner",
        "suppliers.set_target_rung",
        "suppliers.set_credit_limit",
        "suppliers.add_supplier_capability",
        "suppliers.add_supplier_account",
        "suppliers.rename_supplier",
        "suppliers.set_supplier_delivery_details",
        "suppliers.set_supplier_purchase_order_email",
        "suppliers.set_supplier_quote_request_email",
        "journey_testing.sign_in_as_supplier",
        "suppliers.place_supplier_on_hold",
        "suppliers.release_supplier_hold",
        "suppliers.fold_supplier"
      ],
      "features": [
        "purchase_invoice_matching"
      ]
    },
    {
      "id": "suppliers.supplier_account",
      "module": "suppliers_module",
      "label": "Supplier account",
      "plural": "Supplier accounts",
      "description": "Our account with a supplier, held by one trading company, with our account number there.",
      "identifier": {
        "kind": "field",
        "field": "supplier_account_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "open",
        "closed"
      ],
      "list": {
        "path": "/views/supplier-accounts",
        "key_field": "supplier_account_id",
        "title_field": "account_label",
        "columns": [
          {
            "field": "supplier_name",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier",
            "ref_id_field": "supplier_no"
          },
          {
            "field": "trading_company",
            "label": "Trading company",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "trading_company_code"
          },
          {
            "field": "account_no",
            "label": "Our account number",
            "kind": "text"
          },
          {
            "field": "state",
            "label": "State",
            "kind": "state",
            "enum_labels": {
              "closed": "Closed",
              "open": "Open"
            }
          }
        ],
        "filters": [
          {
            "param": "trading_company_code",
            "label": "Trading company",
            "kind": "entity",
            "options": [],
            "object": "trading_companies.trading_company",
            "description": "One trading company's accounts."
          },
          {
            "param": "supplier_no",
            "label": "Supplier",
            "kind": "entity",
            "options": [],
            "object": "suppliers.supplier",
            "description": "One supplier's accounts."
          },
          {
            "param": "state",
            "label": "State",
            "kind": "multi_enum",
            "options": [
              [
                "open",
                "Open"
              ],
              [
                "closed",
                "Closed"
              ]
            ],
            "description": "Open or closed accounts."
          }
        ],
        "sorts": [
          [
            "supplier_name",
            "Supplier"
          ],
          [
            "trading_company",
            "Trading company"
          ],
          [
            "opened_on",
            "Opened on"
          ]
        ],
        "searchable": true,
        "queues": []
      },
      "record": {
        "path": "/views/supplier-accounts/{supplier_account_id}",
        "path_param": "supplier_account_id",
        "title_field": "account_label",
        "state_fields": [
          "state"
        ],
        "state_labels": {
          "state": {
            "closed": "Closed",
            "open": "Open"
          }
        },
        "facts": [
          {
            "field": "supplier_name",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier",
            "ref_id_field": "supplier_no"
          },
          {
            "field": "trading_company",
            "label": "Trading company",
            "kind": "text",
            "ref": "trading_companies.trading_company",
            "ref_id_field": "trading_company_code"
          },
          {
            "field": "account_no",
            "label": "Our account number",
            "kind": "text"
          },
          {
            "field": "opened_on",
            "label": "Opened",
            "kind": "date"
          },
          {
            "field": "open_purchase_order_nos",
            "label": "Open purchase orders",
            "kind": "text",
            "ref": "purchase_orders.purchase_order"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [
        "suppliers.supplier"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A supplier account is our account with a supplier, held by one trading company, with our account number there. A purchase order is placed through one, and the supplier's bills go to that trading company. A supplier holds at most one open account for each trading company, and no two open accounts of a supplier share a number. \"Add supplier account\" records one on the supplier, and \"Change account number\" corrects the number the supplier's bills quote.",
      "state_meanings": {
        "open": "Open: purchase orders can be placed through it.",
        "closed": "Closed: no new purchase orders go through it. Its purchase orders and bills are kept."
      },
      "state_tones": {
        "open": "done",
        "closed": "neutral"
      },
      "commands": [
        "suppliers.change_supplier_account_number",
        "suppliers.close_supplier_account"
      ],
      "features": [
        "purchase_invoice_matching"
      ]
    },
    {
      "id": "suppliers.supplier_capability",
      "module": "suppliers_module",
      "label": "Supplier capability",
      "plural": "Capabilities",
      "description": "One thing a supplier can make or do for us, in the dictionary's words (an item family or a service, optionally a production method), and the role the supplier plays for it.",
      "identifier": {
        "kind": "field",
        "field": "capability_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "primary",
        "secondary",
        "candidate",
        "out_of_scope"
      ],
      "list": {
        "path": "/views/supplier-capabilities",
        "key_field": "capability_id",
        "title_field": "title",
        "columns": [
          {
            "field": "title",
            "label": "Capability",
            "kind": "text"
          },
          {
            "field": "supplier_name",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier",
            "ref_id_field": "supplier_no"
          },
          {
            "field": "role",
            "label": "Role",
            "kind": "state",
            "enum_labels": {
              "candidate": "Candidate",
              "out_of_scope": "Out of scope",
              "primary": "Primary",
              "secondary": "Secondary"
            }
          },
          {
            "field": "supplier_stage_label",
            "label": "Stage",
            "kind": "text"
          },
          {
            "field": "rung_today_label",
            "label": "Rung today",
            "kind": "text"
          },
          {
            "field": "note",
            "label": "Note",
            "kind": "text"
          }
        ],
        "filters": [
          {
            "param": "what",
            "label": "Family or service",
            "kind": "text",
            "options": [],
            "description": "A dictionary item family code or a service kind code."
          },
          {
            "param": "production_method",
            "label": "Production method",
            "kind": "text",
            "options": []
          },
          {
            "param": "role",
            "label": "Role",
            "kind": "multi_enum",
            "options": [
              [
                "primary",
                "Primary"
              ],
              [
                "secondary",
                "Secondary"
              ],
              [
                "candidate",
                "Candidate"
              ],
              [
                "out_of_scope",
                "Out of scope"
              ]
            ]
          },
          {
            "param": "relationship_stage",
            "label": "Supplier's stage",
            "kind": "multi_enum",
            "options": []
          },
          {
            "param": "coverage",
            "label": "Coverage",
            "kind": "enum",
            "options": [
              [
                "thin",
                "Primary only"
              ],
              [
                "no_primary",
                "No primary"
              ],
              [
                "covered",
                "Covered"
              ]
            ],
            "description": "Primary only: one primary and nothing priced behind it. No primary: rows, but none leads."
          },
          {
            "param": "supplier_no",
            "label": "Supplier",
            "kind": "entity",
            "options": [],
            "object": "suppliers.supplier",
            "description": "One supplier's capabilities."
          }
        ],
        "sorts": [
          [
            "title",
            "Capability"
          ],
          [
            "supplier_name",
            "Supplier"
          ],
          [
            "role",
            "Role"
          ],
          [
            "set_at",
            "Set at"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "thin",
            "label": "Primary only",
            "params": {
              "coverage": "thin"
            },
            "count": true,
            "default": false
          },
          {
            "id": "no_primary",
            "label": "No primary",
            "params": {
              "coverage": "no_primary"
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/views/supplier-capability/{capability_id}",
        "path_param": "capability_id",
        "title_field": "title",
        "state_fields": [
          "role"
        ],
        "state_labels": {
          "role": {
            "candidate": "Candidate",
            "out_of_scope": "Out of scope",
            "primary": "Primary",
            "secondary": "Secondary"
          }
        },
        "facts": [
          {
            "field": "supplier_name",
            "label": "Supplier",
            "kind": "text",
            "ref": "suppliers.supplier",
            "ref_id_field": "supplier_no"
          },
          {
            "field": "what_label",
            "label": "Family or service",
            "kind": "text"
          },
          {
            "field": "production_method_label",
            "label": "Production method",
            "kind": "text"
          },
          {
            "field": "note",
            "label": "Note",
            "kind": "text"
          },
          {
            "field": "set_by_name",
            "label": "Set by",
            "kind": "text"
          },
          {
            "field": "set_at",
            "label": "Set on",
            "kind": "datetime"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [
        "suppliers.supplier"
      ],
      "children": [],
      "related": [
        {
          "object": "supplier_quotes.supplier_quote",
          "label": "Supplier quotes",
          "params": {
            "supplier_no": "{supplier_no}"
          },
          "secondary": true
        }
      ],
      "lifecycle": [],
      "about": "A supplier capability is one thing a supplier can make or do for us, in the dictionary's words: an item family such as Booklet, or a service such as Mailing, and where it matters the production method. The role says whether the supplier is our primary for that work, the secondary we hold behind it, a candidate not yet tested, or out of scope. Rung today and Stage are the supplier's. A family with one primary and no secondary is thin; one with rows but no primary has no route.",
      "state_meanings": {
        "primary": "Primary: the supplier we route this work to first.",
        "secondary": "Secondary: the fallback for this work, priced and ready.",
        "candidate": "Candidate: named for this work and not yet tested on it.",
        "out_of_scope": "Out of scope: we will not use the supplier for this work."
      },
      "state_tones": {
        "primary": "done",
        "secondary": "done",
        "candidate": "neutral",
        "out_of_scope": "neutral"
      },
      "commands": [
        "suppliers.change_capability_role",
        "suppliers.remove_supplier_capability"
      ],
      "features": [
        "purchase_invoice_matching"
      ]
    },
    {
      "id": "tharstern_job_check.earlier_check",
      "module": "tharstern_job_check_module",
      "label": "Earlier check",
      "plural": "Earlier checks",
      "description": "A check of the same Tharstern job made before its latest, kept as it was.",
      "identifier": {
        "kind": "field",
        "field": "check_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "cant_read",
        "not_specifiable",
        "no_catalogue_item",
        "no_supplier",
        "not_priced_yet",
        "priced"
      ],
      "list": null,
      "record": null,
      "parents": [
        "tharstern_job_check.job_check"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "state_tones": {
        "cant_read": "attention",
        "not_specifiable": "attention",
        "no_catalogue_item": "attention",
        "no_supplier": "attention",
        "not_priced_yet": "in_progress",
        "priced": "done"
      },
      "commands": [],
      "features": []
    },
    {
      "id": "tharstern_job_check.job_check",
      "module": "tharstern_job_check_module",
      "label": "Tharstern job check",
      "plural": "Tharstern job checks",
      "description": "One's latest check of one Tharstern job, with its verdict and One's price beside Tharstern's.",
      "identifier": {
        "kind": "field",
        "field": "job_no",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "cant_read",
        "not_specifiable",
        "no_catalogue_item",
        "no_supplier",
        "not_priced_yet",
        "priced"
      ],
      "list": {
        "path": "/tharstern-job-checks",
        "key_field": "job_no",
        "title_field": "job_description",
        "columns": [
          {
            "field": "job_no",
            "label": "Tharstern job number",
            "kind": "identifier"
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text",
            "ref": "customers.customer",
            "ref_id_field": "customer_code"
          },
          {
            "field": "job_description",
            "label": "Description",
            "kind": "text"
          },
          {
            "field": "quantity",
            "label": "Quantity",
            "kind": "number"
          },
          {
            "field": "booked_on",
            "label": "Booked",
            "kind": "date"
          },
          {
            "field": "verdict",
            "label": "Verdict",
            "kind": "state",
            "enum_labels": {
              "cant_read": "Can't read",
              "no_catalogue_item": "No catalogue item",
              "no_supplier": "No supplier",
              "not_priced_yet": "Not priced yet",
              "not_specifiable": "Not specifiable",
              "priced": "Priced"
            }
          },
          {
            "field": "tharstern_net_pence",
            "label": "Tharstern's price",
            "kind": "money"
          },
          {
            "field": "one_policy_price_pence",
            "label": "One's policy price",
            "kind": "money"
          },
          {
            "field": "difference",
            "label": "Difference",
            "kind": "text"
          }
        ],
        "filters": [
          {
            "param": "verdict",
            "label": "Verdict",
            "kind": "multi_enum",
            "options": [
              [
                "cant_read",
                "Can't read"
              ],
              [
                "not_specifiable",
                "Not specifiable"
              ],
              [
                "no_catalogue_item",
                "No catalogue item"
              ],
              [
                "no_supplier",
                "No supplier"
              ],
              [
                "not_priced_yet",
                "Not priced yet"
              ],
              [
                "priced",
                "Priced"
              ]
            ],
            "description": "Only checks with any of these verdicts."
          },
          {
            "param": "reason",
            "label": "Why",
            "kind": "multi_enum",
            "options": [
              [
                "no_estimate",
                "No estimate in the copy"
              ],
              [
                "before_copy",
                "Booked before the copy"
              ],
              [
                "not_in_dictionary",
                "Not in the dictionary"
              ],
              [
                "spec_unreadable",
                "The dictionary can't read the specification"
              ],
              [
                "no_catalogue_item",
                "No catalogue item offers it"
              ],
              [
                "no_route",
                "No route can make it"
              ],
              [
                "no_price_yet",
                "No price yet"
              ]
            ],
            "description": "Only checks not priced for any of these reasons."
          },
          {
            "param": "run",
            "label": "Run",
            "kind": "text",
            "options": [],
            "description": "Only checks made by this run (the run's id, as the run report gives it)."
          },
          {
            "param": "product_type",
            "label": "Tharstern product type",
            "kind": "text",
            "options": [],
            "description": "Only checks whose Tharstern product type contains this text."
          },
          {
            "param": "customer_code",
            "label": "Customer",
            "kind": "entity",
            "options": [],
            "object": "customers.customer",
            "description": "Only checks of this customer, by customer number (CUS-…). A customer One hasn't matched to a Tharstern code has none."
          },
          {
            "param": "booked_from",
            "label": "Booked from",
            "kind": "date",
            "options": [],
            "description": "Jobs booked on or after this day (YYYY-MM-DD)."
          },
          {
            "param": "booked_to",
            "label": "Booked to",
            "kind": "date",
            "options": [],
            "description": "Jobs booked on or before this day (YYYY-MM-DD)."
          },
          {
            "param": "has_spec_gaps",
            "label": "Spec not fully read",
            "kind": "boolean",
            "options": [],
            "description": "True: only checks where Tharstern specified a field One couldn't read and took a default for. False: only checks with none."
          }
        ],
        "sorts": [
          [
            "difference_pct",
            "Difference"
          ],
          [
            "tharstern_net_pence",
            "Tharstern's price"
          ],
          [
            "booked_on",
            "Booked"
          ]
        ],
        "searchable": true,
        "queues": [
          {
            "id": "not_specifiable",
            "label": "Not specifiable",
            "params": {
              "verdict": [
                "cant_read",
                "not_specifiable"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "no_catalogue_item",
            "label": "No catalogue item",
            "params": {
              "verdict": [
                "no_catalogue_item"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "no_supplier",
            "label": "No supplier",
            "params": {
              "verdict": [
                "no_supplier"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "not_priced_yet",
            "label": "Not priced yet",
            "params": {
              "verdict": [
                "not_priced_yet"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "spec_not_fully_read",
            "label": "Spec not fully read",
            "params": {
              "has_spec_gaps": true
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/tharstern-job-checks/{job_no}",
        "path_param": "job_no",
        "title_field": "job_description",
        "state_fields": [
          "verdict"
        ],
        "state_labels": {
          "verdict": {
            "cant_read": "Can't read",
            "no_catalogue_item": "No catalogue item",
            "no_supplier": "No supplier",
            "not_priced_yet": "Not priced yet",
            "not_specifiable": "Not specifiable",
            "priced": "Priced"
          }
        },
        "facts": [
          {
            "field": "reason",
            "label": "Why",
            "kind": "text"
          },
          {
            "field": "spec_gaps",
            "label": "Spec not fully read",
            "kind": "text"
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text",
            "ref": "customers.customer",
            "ref_id_field": "customer_code"
          },
          {
            "field": "job_no",
            "label": "Tharstern job number",
            "kind": "identifier"
          },
          {
            "field": "tharstern_order_ref",
            "label": "Tharstern order",
            "kind": "identifier",
            "ref": "orders.tharstern_order"
          },
          {
            "field": "product_type",
            "label": "Tharstern product type",
            "kind": "text"
          },
          {
            "field": "quantity",
            "label": "Quantity",
            "kind": "number"
          },
          {
            "field": "booked_on",
            "label": "Booked",
            "kind": "date"
          },
          {
            "field": "required_by",
            "label": "Required by",
            "kind": "date"
          },
          {
            "field": "tharstern_estimated_pence",
            "label": "Estimated",
            "kind": "money"
          },
          {
            "field": "tharstern_ordered_pence",
            "label": "Ordered",
            "kind": "money"
          },
          {
            "field": "tharstern_invoiced_pence",
            "label": "Invoiced",
            "kind": "money"
          },
          {
            "field": "tharstern_carriage_pence",
            "label": "Carriage, not compared",
            "kind": "money"
          },
          {
            "field": "one_policy_price_pence",
            "label": "One's policy price",
            "kind": "money"
          },
          {
            "field": "difference",
            "label": "Difference from Tharstern",
            "kind": "text"
          },
          {
            "field": "one_suggested_price_pence",
            "label": "One's suggested price",
            "kind": "money"
          },
          {
            "field": "suggested_from",
            "label": "Suggested from",
            "kind": "text"
          },
          {
            "field": "priced_from",
            "label": "Priced from",
            "kind": "text"
          },
          {
            "field": "checked_at",
            "label": "Checked",
            "kind": "datetime"
          },
          {
            "field": "run",
            "label": "Run",
            "kind": "text"
          }
        ],
        "fact_groups": [
          {
            "label": "The job",
            "fields": [
              "customer_name",
              "job_no",
              "tharstern_order_ref",
              "product_type",
              "quantity",
              "booked_on",
              "required_by"
            ]
          },
          {
            "label": "Tharstern's price",
            "fields": [
              "tharstern_estimated_pence",
              "tharstern_ordered_pence",
              "tharstern_invoiced_pence",
              "tharstern_carriage_pence"
            ]
          },
          {
            "label": "One's price",
            "fields": [
              "one_policy_price_pence",
              "difference",
              "one_suggested_price_pence",
              "suggested_from",
              "priced_from"
            ]
          },
          {
            "label": "This check",
            "fields": [
              "reason",
              "spec_gaps",
              "checked_at",
              "run"
            ]
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [
        {
          "object": "tharstern_job_check.job_check_line",
          "label": "Lines",
          "field": "lines",
          "key_field": "line_ref",
          "columns": [
            {
              "field": "part_name",
              "label": "Part",
              "kind": "text"
            },
            {
              "field": "verdict",
              "label": "Verdict",
              "kind": "state",
              "enum_labels": {
                "cant_read": "Can't read",
                "no_catalogue_item": "No catalogue item",
                "no_supplier": "No supplier",
                "not_priced_yet": "Not priced yet",
                "not_specifiable": "Not specifiable",
                "priced": "Priced"
              }
            },
            {
              "field": "catalogue_item_name",
              "label": "Catalogue item",
              "kind": "text",
              "ref": "catalogue.catalogue_item",
              "ref_id_field": "catalogue_item_id"
            },
            {
              "field": "configuration",
              "label": "Configuration",
              "kind": "text"
            },
            {
              "field": "assumed",
              "label": "Assumed",
              "kind": "text"
            },
            {
              "field": "quantity",
              "label": "Quantity",
              "kind": "number"
            },
            {
              "field": "speed",
              "label": "Speed",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "tharstern_job_check.supplier_price",
          "label": "Supplier prices",
          "field": "supplier_prices",
          "key_field": "price_ref",
          "columns": [
            {
              "field": "part_name",
              "label": "Part",
              "kind": "text"
            },
            {
              "field": "route_label",
              "label": "Supplier",
              "kind": "text",
              "ref": "suppliers.supplier",
              "ref_id_field": "supplier_no"
            },
            {
              "field": "service",
              "label": "Service",
              "kind": "text"
            },
            {
              "field": "arrives_on",
              "label": "Arrives",
              "kind": "date"
            },
            {
              "field": "answer",
              "label": "Answer",
              "kind": "state",
              "enum_labels": {
                "asked_now": "Asked on this check",
                "cannot_make": "Can't make it",
                "in_house_held": "In-house cost held",
                "kept": "Kept answer",
                "no_in_house_cost": "No in-house cost held",
                "no_price_held": "No price held",
                "not_asked": "Not asked",
                "waiting_to_retry": "Waiting to retry"
              }
            },
            {
              "field": "cost_pence",
              "label": "Supplier's cost",
              "kind": "money"
            },
            {
              "field": "price_pence",
              "label": "One's price from it",
              "kind": "money"
            },
            {
              "field": "used",
              "label": "Used",
              "kind": "text"
            },
            {
              "field": "why",
              "label": "Why",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "tharstern_job_check.price_step",
          "label": "Price steps",
          "field": "price_steps",
          "key_field": "step_ref",
          "columns": [
            {
              "field": "part_name",
              "label": "Part",
              "kind": "text"
            },
            {
              "field": "label",
              "label": "Step",
              "kind": "text"
            },
            {
              "field": "basis",
              "label": "Basis",
              "kind": "text"
            },
            {
              "field": "amount_pence",
              "label": "Amount",
              "kind": "money"
            }
          ],
          "paged": false,
          "parent_filter": null,
          "totals": {
            "amount_pence": {
              "field": "one_policy_price_pence",
              "label": "One's policy price",
              "kind": "money"
            }
          }
        },
        {
          "object": "tharstern_job_check.price_level",
          "label": "Price levels",
          "field": "price_levels",
          "key_field": "level_ref",
          "columns": [
            {
              "field": "part_name",
              "label": "Part",
              "kind": "text"
            },
            {
              "field": "level",
              "label": "Level",
              "kind": "text"
            },
            {
              "field": "price_pence",
              "label": "Price",
              "kind": "money"
            },
            {
              "field": "basis",
              "label": "Basis",
              "kind": "text"
            },
            {
              "field": "margin_floor",
              "label": "Margin floor",
              "kind": "text"
            },
            {
              "field": "suggested",
              "label": "Suggested",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null
        },
        {
          "object": "tharstern_job_check.earlier_check",
          "label": "Earlier checks",
          "field": "earlier_checks",
          "key_field": "check_id",
          "columns": [
            {
              "field": "checked_at",
              "label": "Checked",
              "kind": "datetime"
            },
            {
              "field": "run",
              "label": "Run",
              "kind": "text"
            },
            {
              "field": "verdict",
              "label": "Verdict",
              "kind": "state",
              "enum_labels": {
                "cant_read": "Can't read",
                "no_catalogue_item": "No catalogue item",
                "no_supplier": "No supplier",
                "not_priced_yet": "Not priced yet",
                "not_specifiable": "Not specifiable",
                "priced": "Priced"
              }
            },
            {
              "field": "reason",
              "label": "Why",
              "kind": "text"
            },
            {
              "field": "one_policy_price_pence",
              "label": "One's policy price",
              "kind": "money"
            },
            {
              "field": "difference",
              "label": "Difference",
              "kind": "text"
            }
          ],
          "paged": false,
          "parent_filter": null
        }
      ],
      "related": [],
      "lifecycle": [],
      "about": "A Tharstern job check is One's check of one job Tharstern booked: whether One could have specified it, routed it to a supplier and priced it, with One's price beside Tharstern's. A nightly run checks each new job, and a replay checks again every job since the 3 June 2025 cutover. A check never orders, quotes or asks Tharstern for a price. \"Check again\" checks the job now and keeps the new check beside the earlier ones. The checks stop when One takes over from Tharstern.",
      "state_meanings": {
        "cant_read": "Can't read: One can't read the job's specification: the Tharstern copy holds no estimate for it.",
        "not_specifiable": "Not specifiable: the dictionary doesn't cover this work, or can't read its specification.",
        "no_catalogue_item": "No catalogue item: the dictionary reads the work, but no catalogue item offers it.",
        "no_supplier": "No supplier: One could specify the work, but no supplier route can make it.",
        "not_priced_yet": "Not priced yet: a supplier can make it, but One has no price for it yet. The next run will ask again.",
        "priced": "Priced: One could specify, route and price this work."
      },
      "state_tones": {
        "cant_read": "attention",
        "not_specifiable": "attention",
        "no_catalogue_item": "attention",
        "no_supplier": "attention",
        "not_priced_yet": "in_progress",
        "priced": "done"
      },
      "commands": [
        "tharstern_job_check.check_tharstern_job_again"
      ],
      "features": [
        "tharstern_job_check"
      ]
    },
    {
      "id": "tharstern_job_check.job_check_line",
      "module": "tharstern_job_check_module",
      "label": "Line",
      "plural": "Lines",
      "description": "The whole job, or one part where Tharstern booked the job as parts, with the catalogue item and configuration One would use.",
      "identifier": {
        "kind": "field",
        "field": "line_ref",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "cant_read",
        "not_specifiable",
        "no_catalogue_item",
        "no_supplier",
        "not_priced_yet",
        "priced"
      ],
      "list": null,
      "record": {
        "path": "/tharstern-job-checks/lines/{line_ref}",
        "path_param": "line_ref",
        "title_field": "part_name",
        "state_fields": [
          "verdict"
        ],
        "state_labels": {
          "verdict": {
            "cant_read": "Can't read",
            "no_catalogue_item": "No catalogue item",
            "no_supplier": "No supplier",
            "not_priced_yet": "Not priced yet",
            "not_specifiable": "Not specifiable",
            "priced": "Priced"
          }
        },
        "facts": [
          {
            "field": "job_no",
            "label": "Tharstern job check",
            "kind": "identifier",
            "ref": "tharstern_job_check.job_check"
          },
          {
            "field": "reason",
            "label": "Why",
            "kind": "text"
          },
          {
            "field": "catalogue_item_name",
            "label": "Catalogue item",
            "kind": "text",
            "ref": "catalogue.catalogue_item",
            "ref_id_field": "catalogue_item_id"
          },
          {
            "field": "dictionary_item_name",
            "label": "Dictionary item",
            "kind": "text"
          },
          {
            "field": "configuration",
            "label": "Configuration",
            "kind": "text"
          },
          {
            "field": "assumed",
            "label": "Assumed",
            "kind": "text"
          },
          {
            "field": "quantity",
            "label": "Quantity",
            "kind": "number"
          },
          {
            "field": "speed",
            "label": "Speed",
            "kind": "text"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [
        "tharstern_job_check.job_check"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A line of a Tharstern job check is the whole job, or one part where Tharstern booked the job as parts, with the catalogue item and configuration One would use for it and the verdict for that part. A field the job didn't say shows under Assumed with what One took for it.",
      "state_tones": {
        "cant_read": "attention",
        "not_specifiable": "attention",
        "no_catalogue_item": "attention",
        "no_supplier": "attention",
        "not_priced_yet": "in_progress",
        "priced": "done"
      },
      "commands": [],
      "features": [
        "tharstern_job_check"
      ]
    },
    {
      "id": "tharstern_job_check.price_level",
      "module": "tharstern_job_check_module",
      "label": "Price level",
      "plural": "Price levels",
      "description": "One price One could charge for a part of a Tharstern job check, judged against the margin floor.",
      "identifier": {
        "kind": "field",
        "field": "level_ref",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "tharstern_job_check.job_check"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A price level is one price One could charge for a part of a Tharstern job check: the contracted rate, what the customer last paid or was last quoted before this job, a similar order (an order of the same colours and size at a nearby quantity or page count, or on another stock, at the margin it was charged on today's cost) when nothing of this spec was paid at this quantity, and the policy price, each judged against the margin floor at today's cost. One suggests the last price paid while it clears the floor, else a close similar order, else the policy price.",
      "commands": [],
      "features": []
    },
    {
      "id": "tharstern_job_check.price_step",
      "module": "tharstern_job_check_module",
      "label": "Price step",
      "plural": "Price steps",
      "description": "One step from the cost used to One's policy price for a part of a Tharstern job check.",
      "identifier": {
        "kind": "field",
        "field": "step_ref",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": null,
      "record": null,
      "parents": [
        "tharstern_job_check.job_check"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A price step is one step of how One built its policy price for a Tharstern job check, from the supplier's cost used through the price group's rules. Every rule is listed, with its basis, whether it changed the price or not.",
      "commands": [],
      "features": []
    },
    {
      "id": "tharstern_job_check.supplier_price",
      "module": "tharstern_job_check_module",
      "label": "Supplier price",
      "plural": "Supplier prices",
      "description": "What one route answered for one part of a Tharstern job check, and One's price from it.",
      "identifier": {
        "kind": "field",
        "field": "price_ref",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "asked_now",
        "kept",
        "in_house_held",
        "no_in_house_cost",
        "cannot_make",
        "not_asked",
        "no_price_held",
        "waiting_to_retry"
      ],
      "list": null,
      "record": null,
      "parents": [
        "tharstern_job_check.job_check"
      ],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A supplier price is what one route answered for one part of a Tharstern job check: the supplier's cost and the price One would charge from it, or why it has none. The route used for One's price is the cheapest that arrives by the job's required date.",
      "state_meanings": {
        "no_price_held": "No price held: the route is priced only from the nightly cost refresh, which holds no price for this yet."
      },
      "state_tones": {
        "asked_now": "done",
        "kept": "done",
        "in_house_held": "done",
        "no_in_house_cost": "attention",
        "cannot_make": "stopped",
        "not_asked": "neutral",
        "no_price_held": "attention",
        "waiting_to_retry": "in_progress"
      },
      "commands": [],
      "features": []
    },
    {
      "id": "trading_companies.trading_company",
      "module": "trading_companies_module",
      "label": "Trading company",
      "plural": "Trading companies",
      "description": "One of our legal companies that sells and buys, with its legal details, bank account and lifecycle from Setting up to Trading to Stopped.",
      "identifier": {
        "kind": "field",
        "field": "code",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "setting_up",
        "trading",
        "stopped"
      ],
      "list": {
        "path": "/views/trading-company-list",
        "key_field": "code",
        "title_field": "legal_name",
        "columns": [
          {
            "field": "name",
            "label": "Trading company",
            "kind": "text"
          },
          {
            "field": "legal_name",
            "label": "Legal name",
            "kind": "text"
          },
          {
            "field": "state",
            "label": "State",
            "kind": "state",
            "enum_labels": {
              "setting_up": "Setting up",
              "stopped": "Stopped",
              "trading": "Trading"
            }
          },
          {
            "field": "xero_organisation",
            "label": "Xero organisation",
            "kind": "text"
          },
          {
            "field": "card_payments",
            "label": "Card payments",
            "kind": "text"
          }
        ],
        "filters": [
          {
            "param": "state",
            "label": "State",
            "kind": "multi_enum",
            "options": [
              [
                "setting_up",
                "Setting up"
              ],
              [
                "trading",
                "Trading"
              ],
              [
                "stopped",
                "Stopped"
              ]
            ],
            "description": "Only trading companies in these states; none: every state."
          }
        ],
        "sorts": [
          [
            "name",
            "Trading company"
          ],
          [
            "state",
            "State"
          ]
        ],
        "searchable": true,
        "queues": []
      },
      "record": {
        "path": "/views/trading-company/{code}",
        "path_param": "code",
        "title_field": "legal_name",
        "state_fields": [
          "state"
        ],
        "state_labels": {
          "state": {
            "setting_up": "Setting up",
            "stopped": "Stopped",
            "trading": "Trading"
          }
        },
        "facts": [
          {
            "field": "legal_name",
            "label": "Legal name",
            "kind": "text"
          },
          {
            "field": "code",
            "label": "Code",
            "kind": "text"
          },
          {
            "field": "company_number",
            "label": "Company number",
            "kind": "text"
          },
          {
            "field": "registered_in",
            "label": "Registered in",
            "kind": "text"
          },
          {
            "field": "registered_office",
            "label": "Registered office",
            "kind": "text"
          },
          {
            "field": "vat_number",
            "label": "VAT number",
            "kind": "text"
          },
          {
            "field": "bank_account",
            "label": "Bank account",
            "kind": "text"
          },
          {
            "field": "card_payments",
            "label": "Card payments",
            "kind": "text"
          },
          {
            "field": "xero_organisation",
            "label": "Xero organisation",
            "kind": "text"
          },
          {
            "field": "xero_permissions",
            "label": "Permissions Xero granted",
            "kind": "text"
          },
          {
            "field": "xero_lock_date",
            "label": "Locked in Xero up to",
            "kind": "date"
          }
        ],
        "fact_groups": [
          {
            "label": "Legal details (shown on every document)",
            "fields": [
              "legal_name",
              "code",
              "company_number",
              "registered_in",
              "registered_office",
              "vat_number"
            ]
          },
          {
            "label": "Money",
            "fields": [
              "bank_account",
              "card_payments"
            ]
          },
          {
            "label": "Xero",
            "fields": [
              "xero_organisation",
              "xero_permissions",
              "xero_lock_date"
            ]
          }
        ],
        "timeline_path": "/views/trading-company/{code}/timeline",
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [
        {
          "object": "suppliers.supplier_account",
          "label": "Supplier accounts",
          "params": {
            "trading_company_code": "{code}"
          }
        },
        {
          "object": "finance_accruals_and_prepayments.month_end",
          "label": "Month ends",
          "params": {
            "trading_company_code": "{code}"
          },
          "secondary": true
        }
      ],
      "lifecycle": [],
      "about": "A trading company is one of our legal companies that sells and buys. Each order and each purchase order belongs to one, and each trading company has its own Xero organisation, bank account, card payments and month end. \"Add trading company\" creates one in Setting up, and \"Start trading\" lets it take orders once it is ready.",
      "state_meanings": {
        "setting_up": "Setting up: its details are being entered. No order or purchase order can be given to it yet.",
        "trading": "Trading: orders and purchase orders can be given to it, and its documents go to its own Xero organisation.",
        "stopped": "Stopped: it takes no new orders or purchase orders. Its documents and its month ends are kept."
      },
      "state_tones": {
        "setting_up": "in_progress",
        "trading": "done",
        "stopped": "neutral"
      },
      "commands": [
        "trading_companies.add_trading_company",
        "trading_companies.start_trading",
        "trading_companies.update_trading_company_legal_details",
        "trading_companies.update_trading_company_bank_account",
        "trading_companies.stop_trading",
        "xero_integration.connect_xero_organisation",
        "xero_integration.disconnect_xero_organisation"
      ],
      "features": []
    },
    {
      "id": "users.user",
      "module": "users_module",
      "label": "User",
      "plural": "Users",
      "description": "Application user with email, role, and optional Entra OID linkage.",
      "identifier": {
        "kind": "uuid",
        "field": "id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "active",
        "pending",
        "left"
      ],
      "list": {
        "path": "/users",
        "key_field": "id",
        "title_field": "name",
        "columns": [
          {
            "field": "name",
            "label": "Name",
            "kind": "text"
          },
          {
            "field": "role",
            "label": "Role",
            "kind": "text",
            "enum_labels": {
              "admin": "Admin",
              "commercial": "Commercial",
              "csr": "CSR/AM",
              "engineer": "Engineer",
              "finance": "Finance",
              "manager": "Manager",
              "marketing": "Marketing",
              "pending": "Pending",
              "sales_rep": "Sales rep",
              "stock_control": "Stock control"
            }
          },
          {
            "field": "department",
            "label": "Department",
            "kind": "text"
          },
          {
            "field": "state",
            "label": "State",
            "kind": "state",
            "enum_labels": {
              "active": "Active",
              "left": "Left",
              "pending": "Pending"
            }
          }
        ],
        "filters": [
          {
            "param": "state",
            "label": "State",
            "kind": "enum",
            "options": [
              [
                "active",
                "Active"
              ],
              [
                "pending",
                "Pending"
              ],
              [
                "left",
                "Left"
              ],
              [
                "all",
                "All"
              ]
            ],
            "description": "Active: a user with a role who has not left (the default). Pending: a user who has no role yet. Left: a user marked as having left the business. All: every user.",
            "default": "active",
            "picker_value": "all"
          },
          {
            "param": "role",
            "label": "Role",
            "kind": "multi_enum",
            "options": [
              [
                "admin",
                "Admin"
              ],
              [
                "manager",
                "Manager"
              ],
              [
                "sales_rep",
                "Sales rep"
              ],
              [
                "csr",
                "CSR/AM"
              ],
              [
                "engineer",
                "Engineer"
              ],
              [
                "commercial",
                "Commercial"
              ],
              [
                "finance",
                "Finance"
              ],
              [
                "marketing",
                "Marketing"
              ],
              [
                "stock_control",
                "Stock control"
              ],
              [
                "pending",
                "Pending"
              ],
              [
                "intake_service",
                "intake_service"
              ]
            ],
            "description": "Users with any of these roles."
          },
          {
            "param": "department",
            "label": "Department",
            "kind": "text",
            "options": [],
            "description": "Users in a department whose name contains this text, as a literal substring (e.g. Prepress)."
          }
        ],
        "sorts": [
          [
            "name",
            "Name"
          ],
          [
            "role",
            "Role"
          ],
          [
            "department",
            "Department"
          ],
          [
            "state",
            "State"
          ]
        ],
        "searchable": true,
        "queues": []
      },
      "record": {
        "path": "/users/{id}",
        "path_param": "id",
        "title_field": "name",
        "state_fields": [
          "state"
        ],
        "state_labels": {
          "state": {
            "active": "Active",
            "left": "Left",
            "pending": "Pending"
          }
        },
        "facts": [
          {
            "field": "role",
            "label": "Role",
            "kind": "text",
            "enum_labels": {
              "admin": "Admin",
              "commercial": "Commercial",
              "csr": "CSR/AM",
              "engineer": "Engineer",
              "finance": "Finance",
              "manager": "Manager",
              "marketing": "Marketing",
              "pending": "Pending",
              "sales_rep": "Sales rep",
              "stock_control": "Stock control"
            }
          },
          {
            "field": "department",
            "label": "Department",
            "kind": "text"
          },
          {
            "field": "email",
            "label": "Email address",
            "kind": "text"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A user is someone at Pollards who uses One. Users arrive from Tharstern and from signing in; a new user is Pending until an admin gives them a role. A user marked as having left the business is Left. The Users list is read only: roles, leavers and departments are changed by an admin outside it. Anything in One that names a person, such as a quote's owner, picks from this list.",
      "state_meanings": {
        "active": "Active: the user can sign in and be given work.",
        "pending": "Pending: the user will see nothing until an admin gives them a role.",
        "left": "Left: the user can't sign in. The user's customers will move to the House Account at the next rebuild."
      },
      "state_tones": {
        "active": "done",
        "pending": "attention",
        "left": "neutral"
      },
      "commands": [],
      "features": []
    },
    {
      "id": "workflow.note",
      "module": "workflow_module",
      "label": "Note",
      "plural": "Notes",
      "description": "A person's free-text note about a record or a customer, with an optional reminder. Shown as a related list on the records it is about; never on a ticket (tickets take comments).",
      "identifier": {
        "kind": "uuid",
        "field": "note_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [],
      "list": {
        "path": "/notes",
        "key_field": "note_id",
        "title_field": "summary",
        "columns": [
          {
            "field": "subject_label",
            "label": "About",
            "kind": "text"
          },
          {
            "field": "created_at",
            "label": "Created",
            "kind": "datetime"
          },
          {
            "field": "remind_me_at",
            "label": "Remind me",
            "kind": "datetime"
          },
          {
            "field": "note_id",
            "label": "Note",
            "kind": "identifier"
          },
          {
            "field": "summary",
            "label": "Note",
            "kind": "text"
          }
        ],
        "filters": [
          {
            "param": "customer_id",
            "label": "Customer ID",
            "kind": "text",
            "options": [],
            "description": "Filter to notes linked to this customer."
          },
          {
            "param": "user_id",
            "label": "Created by",
            "kind": "entity",
            "options": [],
            "object": "users.user",
            "description": "Filter to notes created by this user."
          },
          {
            "param": "subject_type",
            "label": "Subject Type",
            "kind": "text",
            "options": [],
            "description": "Filter to notes connected to this subject type."
          },
          {
            "param": "subject_id",
            "label": "Subject ID",
            "kind": "text",
            "options": [],
            "description": "Filter to notes connected to this subject id."
          },
          {
            "param": "mine",
            "label": "Mine",
            "kind": "boolean",
            "options": [],
            "description": "When true, only the caller's own notes."
          },
          {
            "param": "reminder_due",
            "label": "Reminder due",
            "kind": "boolean",
            "options": [],
            "description": "When true, only notes whose reminder falls by the end of today, overdue included."
          }
        ],
        "sorts": [],
        "searchable": true,
        "queues": [
          {
            "id": "my_reminders",
            "label": "My reminders",
            "params": {
              "mine": true,
              "reminder_due": true
            },
            "count": true,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/notes/{note_id}",
        "path_param": "note_id",
        "title_field": "summary",
        "state_fields": [],
        "state_labels": {},
        "facts": [
          {
            "field": "subject_label",
            "label": "About",
            "kind": "text"
          },
          {
            "field": "remind_me_at",
            "label": "Remind me",
            "kind": "datetime"
          },
          {
            "field": "created_at",
            "label": "Created",
            "kind": "datetime"
          }
        ],
        "timeline_path": null,
        "on_open": null,
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [],
      "lifecycle": [],
      "about": "A note is a person's free-text note about a record or a customer, with an optional reminder. A note shows on the record the note is about, never on a ticket. A ticket takes comments instead of notes. My reminders lists your notes with a reminder due by the end of today, overdue included. To deal with a due reminder, clear the reminder with \"Edit note\".",
      "commands": [
        "workflow.create_note",
        "workflow.update_note"
      ],
      "features": []
    },
    {
      "id": "workflow.ticket",
      "module": "workflow_module",
      "label": "Ticket",
      "plural": "Tickets",
      "description": "A piece of internal work assigned to a person or a department, optionally about a customer or another record.",
      "identifier": {
        "kind": "uuid",
        "field": "ticket_id",
        "prefix": null,
        "pattern": null,
        "line_pattern": null
      },
      "states": [
        "open",
        "in_progress",
        "waiting",
        "resolved",
        "closed"
      ],
      "list": {
        "path": "/views/tickets",
        "key_field": "ticket_id",
        "title_field": "title",
        "columns": [
          {
            "field": "ticket_id",
            "label": "Ticket",
            "kind": "identifier"
          },
          {
            "field": "title",
            "label": "Title",
            "kind": "text"
          },
          {
            "field": "assignee_user_name",
            "label": "Assignee",
            "kind": "text"
          },
          {
            "field": "due_date",
            "label": "Due",
            "kind": "text"
          },
          {
            "field": "created_at",
            "label": "Created",
            "kind": "text"
          },
          {
            "field": "status",
            "label": "Status",
            "kind": "state",
            "enum_labels": {
              "closed": "Closed",
              "in_progress": "In progress",
              "open": "Open",
              "resolved": "Resolved",
              "waiting": "Waiting"
            }
          },
          {
            "field": "priority",
            "label": "Priority",
            "kind": "state",
            "enum_labels": {
              "high": "High",
              "low": "Low",
              "normal": "Normal",
              "urgent": "Urgent"
            }
          },
          {
            "field": "first_viewed_at",
            "label": "First opened",
            "kind": "datetime"
          },
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text"
          },
          {
            "field": "category_name",
            "label": "Category",
            "kind": "text"
          }
        ],
        "filters": [
          {
            "param": "status",
            "label": "Status",
            "kind": "multi_enum",
            "options": [],
            "description": "Filter to one or more ticket statuses."
          },
          {
            "param": "mine",
            "label": "Mine",
            "kind": "boolean",
            "options": [],
            "description": "When true, only the tickets assigned to the caller (not those they cover)."
          },
          {
            "param": "action_type",
            "label": "Action Type",
            "kind": "multi_enum",
            "options": [],
            "description": "Filter to one or more action types."
          },
          {
            "param": "customer_id",
            "label": "Customer ID",
            "kind": "multi_enum",
            "options": [],
            "description": "Filter to tickets linked to one or more customers."
          },
          {
            "param": "assigned_to_user_id",
            "label": "Assigned to user ID",
            "kind": "multi_enum",
            "options": [],
            "description": "Filter to tickets assigned to one or more users."
          },
          {
            "param": "unassigned",
            "label": "Unassigned",
            "kind": "boolean",
            "options": [],
            "description": "When true, filter to tickets with no assignee at all (needs-cover queue). Independent of assigned_to_user_id — the two are not meant to be combined."
          },
          {
            "param": "needs_cover",
            "label": "Needs Cover",
            "kind": "boolean",
            "options": [],
            "description": "When true, filter to tickets whose assignee (CSR) is absent today (per work_visibility_module's daily absence snapshot) and no covering buddy has been assigned yet. Distinct from unassigned — this is 'has a CSR but they're away', not 'has no CSR at all'."
          },
          {
            "param": "priority",
            "label": "Priority",
            "kind": "multi_enum",
            "options": [],
            "description": "Filter to one or more ticket priorities (low, normal, high, urgent)."
          },
          {
            "param": "category_id",
            "label": "Category ID",
            "kind": "multi_enum",
            "options": [],
            "description": "Filter to one or more ticket categories."
          },
          {
            "param": "subject_type",
            "label": "Subject Type",
            "kind": "multi_enum",
            "options": [],
            "description": "Filter to one or more ticket subject_type values (the opaque connection back to an originating entity, e.g. 'campaign_contact', 'account_health')."
          },
          {
            "param": "subject_id",
            "label": "Subject ID",
            "kind": "text",
            "options": [],
            "description": "Filter to the tickets about one record: its opaque subject_id, read with subject_type. The old variance authorisation links find their ticket this way (purchase invoice matching PIM-6)."
          },
          {
            "param": "exclude_subject_type",
            "label": "Exclude Subject Type",
            "kind": "multi_enum",
            "options": [],
            "description": "Exclude one or more ticket subject_type values — the inverse of subject_type. Used by the main ticket list to hide campaign-raised tickets, which get their own pinned section. A ticket with a null subject_type is never excluded."
          },
          {
            "param": "system_generated",
            "label": "System Generated",
            "kind": "boolean",
            "options": [],
            "description": "When true, filter to tickets raised by an automated flow (alerts, account health, campaigns, ...); when false, to tickets a person set. Uses workflow_module's SYSTEM_GENERATED_SUBJECT_TYPES — the same classification as each item's is_system_generated, so the filter and the flag never disagree."
          },
          {
            "param": "id",
            "label": "Ticket ID",
            "kind": "multi_enum",
            "options": [],
            "description": "Filter to one or more exact ticket ids. Used by the single-ticket detail lookup (GET /views/tickets/{id}) to reuse this query's composition rather than duplicating it."
          },
          {
            "param": "campaign_id",
            "label": "Campaign ID",
            "kind": "multi_enum",
            "options": [],
            "description": "Filter to tickets raised by one or more marketing campaigns. Only matches campaign-shaped subject_types ('campaign_contact', 'campaign', 'campaign_engaged', 'campaign_bounce'), whose subject_id starts with the campaign or one of its sends ('{campaign_id}:{person_id}' for campaign_contact) — a non-campaign ticket never matches even if its opaque subject_id happens to share a prefix."
          },
          {
            "param": "actioned",
            "label": "Actioned",
            "kind": "boolean",
            "options": [],
            "description": "When true, filter to tickets with at least one human-authored activity row (a rep commented, changed status, reassigned, tagged, ...). When false, filter to tickets with none. System-authored rows (user_id NULL or 'system') never count. Used to split the campaign-tickets box into 'untouched' vs 'actioned'."
          },
          {
            "param": "department",
            "label": "Department",
            "kind": "multi_enum",
            "options": [],
            "description": "Filter to tickets owned by one or more departments, by department slug (department-ticket-routing artifact 3) — not id, matching the frontend/producer convention of addressing departments by slug. Visibility is unaffected: a department is a default view and notification target, not an access boundary, so a customerless department ticket remains visible to non-members regardless of this filter (see the scoping predicate in tickets_query.py)."
          },
          {
            "param": "escalation_due",
            "label": "Escalation Due",
            "kind": "boolean",
            "options": [],
            "description": "When true, filter to unclaimed department tickets already past their escalation threshold (owning_department_id set, assigned_to_user_id null, status == 'open', created_at at or before the working-day cutoff the escalation sweep itself uses). The 'approaching' affordance is a frontend rendering of escalates_at, not a second server-side bucket."
          }
        ],
        "sorts": [],
        "searchable": true,
        "queues": [
          {
            "id": "mine",
            "label": "My tickets",
            "params": {
              "mine": true,
              "status": [
                "open",
                "in_progress",
                "waiting"
              ]
            },
            "count": true,
            "default": true
          },
          {
            "id": "unassigned",
            "label": "Unassigned",
            "params": {
              "unassigned": true,
              "status": [
                "open",
                "in_progress",
                "waiting"
              ]
            },
            "count": true,
            "default": false
          },
          {
            "id": "needs_cover",
            "label": "Needs cover",
            "params": {
              "needs_cover": true
            },
            "count": true,
            "default": false
          },
          {
            "id": "escalation_due",
            "label": "Escalation due",
            "params": {
              "escalation_due": true
            },
            "count": true,
            "default": false
          },
          {
            "id": "waiting",
            "label": "Waiting",
            "params": {
              "status": [
                "waiting"
              ]
            },
            "count": false,
            "default": false
          },
          {
            "id": "resolved",
            "label": "Resolved",
            "params": {
              "status": [
                "resolved"
              ]
            },
            "count": false,
            "default": false
          }
        ]
      },
      "record": {
        "path": "/views/tickets/{ticket_id}",
        "path_param": "ticket_id",
        "title_field": "title",
        "state_fields": [
          "status",
          "priority"
        ],
        "state_labels": {
          "status": {
            "closed": "Closed",
            "in_progress": "In progress",
            "open": "Open",
            "resolved": "Resolved",
            "waiting": "Waiting"
          },
          "priority": {
            "high": "High",
            "low": "Low",
            "normal": "Normal",
            "urgent": "Urgent"
          }
        },
        "facts": [
          {
            "field": "customer_name",
            "label": "Customer",
            "kind": "text"
          },
          {
            "field": "assignee_user_name",
            "label": "Assignee",
            "kind": "text"
          },
          {
            "field": "due_date",
            "label": "Due",
            "kind": "text"
          },
          {
            "field": "category_name",
            "label": "Category",
            "kind": "text"
          },
          {
            "field": "merchandise_request",
            "label": "Merchandise request",
            "kind": "text",
            "ref": "draft_estimates.draft_estimate"
          },
          {
            "field": "first_viewed_at",
            "label": "First opened",
            "kind": "datetime"
          },
          {
            "field": "muted_until",
            "label": "Alerts muted until",
            "kind": "date"
          },
          {
            "field": "mute_reason",
            "label": "Mute reason",
            "kind": "text"
          },
          {
            "field": "description",
            "label": "Details",
            "kind": "text"
          }
        ],
        "timeline_path": "/views/tickets/{ticket_id}/timeline",
        "on_open": "/workflow/actions/mark-ticket-viewed",
        "include": [],
        "timeline_search": null,
        "timeline_filters": []
      },
      "parents": [],
      "children": [],
      "related": [
        {
          "object": "recommendations_generation.alert",
          "label": "Customer alerts",
          "params": {
            "ticket_id": "{id}"
          }
        },
        {
          "object": "recommendations_generation.opportunity",
          "label": "Customer opportunities",
          "params": {
            "ticket_id": "{id}"
          }
        }
      ],
      "lifecycle": [],
      "about": "A ticket is a piece of internal work for a person or a department. A ticket can be about a customer or another record. Staff raise a ticket with \"New ticket\". One also raises tickets, for example for a Needs attention alert or when someone converts an alert to a ticket. A department ticket that nobody claims within one working day will be escalated to the department lead.",
      "state_meanings": {
        "open": "Open: nobody has started the ticket yet.",
        "in_progress": "In progress: someone is working on the ticket.",
        "waiting": "Waiting: the ticket is waiting on someone else, such as a supplier or another department.",
        "resolved": "Resolved: the ticket will close after three quiet days unless someone reopens the ticket.",
        "closed": "Closed: nothing more will happen unless someone reopens the ticket."
      },
      "state_tones": {
        "open": "neutral",
        "in_progress": "in_progress",
        "waiting": "in_progress",
        "resolved": "done",
        "closed": "done",
        "urgent": "stopped",
        "high": "attention",
        "normal": "neutral",
        "low": "neutral"
      },
      "commands": [
        "workflow.create_ticket",
        "workflow.mark_ticket_in_progress",
        "workflow.mark_ticket_waiting",
        "workflow.resolve_ticket",
        "workflow.reopen_ticket",
        "workflow.add_ticket_comment",
        "workflow.add_ticket_attachment",
        "workflow.delegate_ticket",
        "workflow.claim_department_ticket",
        "work_visibility.assign_ticket_covering",
        "workflow.set_ticket_priority",
        "workflow.set_ticket_category",
        "workflow.set_ticket_customer",
        "workflow.amend_ticket",
        "workflow.add_ticket_tag",
        "workflow.remove_ticket_tag",
        "workflow.add_ticket_watcher",
        "workflow.remove_ticket_watcher",
        "recommendations_user_lifecycle.mute_ticket_alerts",
        "recommendations_user_lifecycle.unmute_ticket_alerts",
        "finance_purchase_ledger.answer_purchase_invoice_query",
        "workflow.bulk_delegate_tickets",
        "workflow.close_tickets_with_note"
      ],
      "features": []
    }
  ],
  "commands": [
    {
      "id": "account_allocation.approve_claim",
      "module": "account_allocation_module",
      "label": "Approve claim",
      "description": "Approve pending claims, writing each claimant to the customer's Ref2 in Tharstern and rebuilding the portfolios.",
      "target": "account_allocation.allocation_claim",
      "destructive": true,
      "bulk": true,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/account-allocation/claims/actions/approve",
      "dry_run_path": "/account-allocation/claims/actions/approve/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "targets": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 50,
            "minItems": 1,
            "title": "Targets",
            "description": "The ids of the claims this bulk command acts on (at most 50)."
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "targets"
        ],
        "title": "ClaimTargetsRequest",
        "description": "Approve and withdraw take the claims ticked and nothing else."
      },
      "main": true
    },
    {
      "id": "account_allocation.decline_claim",
      "module": "account_allocation_module",
      "label": "Decline claim…",
      "description": "Decline pending claims, with an optional note that managers see.",
      "target": "account_allocation.allocation_claim",
      "destructive": false,
      "bulk": true,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/account-allocation/claims/actions/decline",
      "dry_run_path": "/account-allocation/claims/actions/decline/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "targets": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 50,
            "minItems": 1,
            "title": "Targets",
            "description": "The ids of the claims this bulk command acts on (at most 50)."
          },
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "description": "Why the claims are declined.",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "targets"
        ],
        "title": "DeclineClaimsRequest"
      }
    },
    {
      "id": "account_allocation.withdraw_claim",
      "module": "account_allocation_module",
      "label": "Withdraw claim",
      "description": "Withdraw your own pending claims.",
      "target": "account_allocation.allocation_claim",
      "destructive": false,
      "bulk": true,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/account-allocation/claims/actions/withdraw",
      "dry_run_path": "/account-allocation/claims/actions/withdraw/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "targets": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 50,
            "minItems": 1,
            "title": "Targets",
            "description": "The ids of the claims this bulk command acts on (at most 50)."
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "targets"
        ],
        "title": "ClaimTargetsRequest",
        "description": "Approve and withdraw take the claims ticked and nothing else."
      }
    },
    {
      "id": "account_allocation.allocate_customer",
      "module": "account_allocation_module",
      "label": "Set sales rep and CSR…",
      "description": "Change a customer's sales rep and CSR from its Settings tab; a Tharstern-linked customer is written back to Ref1 and Ref2 first.",
      "target": "customers.customer",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/account-allocation/customers/{customer_code}/actions/allocate",
      "dry_run_path": "/account-allocation/customers/{customer_code}/actions/allocate/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "sales_rep_user_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Sales rep",
            "x-cis-field": {}
          },
          "csr_user_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "CSR",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AllocateCustomerRequest",
        "description": "*Set sales rep and CSR…*: a customer's sales rep and CSR, from its Settings tab. ``null`` or the empty string\n(what the choices send for nobody) is House Account for a customer with no Tharstern link, and \"leave it as it is\"\nfor a linked one. Both may be absent, so the first dry run reaches the choices."
      },
      "edits": {
        "fields": [
          "sales_rep_name",
          "csr_name"
        ]
      }
    },
    {
      "id": "artwork_intake.resolve_orphan",
      "module": "artwork_intake_module",
      "label": "Match to an order or quote…",
      "description": "Place an unmatched artwork file on a version's part of an order or quote and hand it to preparation like an upload.",
      "target": "artwork_intake.orphan",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/artwork/orphans/{arrival_id}/actions/resolve",
      "dry_run_path": "/artwork/orphans/{arrival_id}/actions/resolve/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "document_no": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 20
              },
              {
                "type": "null"
              }
            ],
            "title": "Order or quote",
            "x-cis-field": {}
          },
          "owner_version_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200,
                "minLength": 1
              },
              {
                "type": "null"
              }
            ],
            "title": "Version",
            "x-cis-field": {}
          },
          "part_key": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100,
                "minLength": 1
              },
              {
                "type": "null"
              }
            ],
            "title": "Part",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ResolveArtworkOrphanRequest",
        "description": "*Match to an order or quote…*: the order or quote, then its version and part waiting for a file. Prepress home\nposts the version and part alone."
      },
      "main": true,
      "proposal_class": "records"
    },
    {
      "id": "artwork_intake.discard_orphan",
      "module": "artwork_intake_module",
      "label": "Discard unmatched artwork file…",
      "description": "Discard an unmatched artwork file and delete its bytes, keeping the reason a person gave.",
      "target": "artwork_intake.orphan",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/artwork/orphans/{arrival_id}/actions/discard",
      "dry_run_path": "/artwork/orphans/{arrival_id}/actions/discard/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "DiscardOrphanRequest"
      }
    },
    {
      "id": "artwork_intake.receive_upload",
      "module": "artwork_intake_module",
      "label": "Upload a file…",
      "description": "Receive a file for a named version part, a whole version's item (one file for every part), or a new artwork check and hand it to preparation in the same request.",
      "target": "artwork_preparation.artwork",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Files can only be uploaded in the app.",
      "method": "POST",
      "path": "/artwork/versions/{owner_version_id}/actions/upload",
      "dry_run_path": "/artwork/versions/{owner_version_id}/actions/upload/dry-run",
      "content_type": "multipart",
      "request_schema": {
        "properties": {
          "part_key": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100,
                "minLength": 1
              },
              {
                "type": "null"
              }
            ],
            "title": "Part",
            "description": "The part the file is for.",
            "x-cis-field": {}
          },
          "file": {
            "anyOf": [
              {
                "type": "string",
                "contentMediaType": "application/octet-stream"
              },
              {
                "type": "null"
              }
            ],
            "title": "File",
            "description": "A PDF, or a Word, Excel or PowerPoint file (converted to PDF), of at most 500 MB.",
            "x-cis-field": {
              "widget": "file"
            }
          },
          "is_studio_file": {
            "type": "boolean",
            "title": "Our studio's file",
            "description": "The file our studio made, for a line whose artwork the studio provides (R251).",
            "default": false,
            "x-cis-field": {}
          },
          "expected_revision_no": {
            "anyOf": [
              {
                "type": "integer",
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Expected Revision No",
            "description": "The dry run's guard: another file since then is a 409 stale."
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "UploadArtworkFileForm",
        "description": "*Upload a file…*: the multipart body of ``POST /artwork/versions/{owner_version_id}/actions/upload`` and its dry\nrun. Every field is optional so the dry run can offer the parts before a file is chosen; the commit refuses a\nmissing part or file (400)."
      }
    },
    {
      "id": "artwork_intake.receive_whole_upload",
      "module": "artwork_intake_module",
      "label": "Upload one file for every part",
      "description": "Receive one file for every part of a version's item and keep it for a person to tag its pages, splitting nothing.",
      "target": "artwork_preparation.artwork",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Files can only be uploaded in the app.",
      "method": "POST",
      "path": "/artwork/versions/{owner_version_id}/actions/upload-whole",
      "dry_run_path": "/artwork/versions/{owner_version_id}/actions/upload-whole/dry-run",
      "content_type": "multipart",
      "request_schema": {
        "properties": {
          "file": {
            "anyOf": [
              {
                "type": "string",
                "contentMediaType": "application/octet-stream"
              },
              {
                "type": "null"
              }
            ],
            "title": "File",
            "description": "A PDF with every part's pages (or a Word, Excel or PowerPoint file, converted to PDF), of at most 500 MB.",
            "x-cis-field": {
              "required": true,
              "widget": "file"
            }
          },
          "is_studio_file": {
            "type": "boolean",
            "title": "Our studio's file",
            "description": "The file our studio made, for a line whose artwork the studio provides (R251).",
            "default": false,
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "UploadWholeFileForm",
        "description": "*Upload one file for every part*: the multipart body of ``POST /artwork/versions/{owner_version_id}/actions/upload-whole``\nand its dry run. The file is optional so the dry run can answer before one is chosen; the commit refuses a missing\nfile (400)."
      }
    },
    {
      "id": "artwork_preparation.accept_revision_without_proof",
      "module": "artwork_preparation_module",
      "label": "Accept for the customer…",
      "description": "Approve a revision on the customer's word with no proof, accepting each worth-knowing finding by id.",
      "target": "artwork_preparation.artwork",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/artwork/artworks/{artwork_id}/revisions/{revision_no}/actions/accept-without-proof",
      "dry_run_path": "/artwork/artworks/{artwork_id}/revisions/{revision_no}/actions/accept-without-proof/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "accepted_finding_ids": {
            "anyOf": [
              {
                "items": {
                  "type": "string"
                },
                "type": "array",
                "maxItems": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Points the customer accepted",
            "description": "Tick each worth-knowing point the customer accepted.",
            "x-cis-field": {}
          },
          "note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 10000
              },
              {
                "type": "null"
              }
            ],
            "title": "How the customer agreed",
            "description": "Kept with the approval: 'Agreed by phone with Jo Smith'.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AcceptWithoutProofRequest",
        "description": "*Accept for the customer…*: staff accept every worth-knowing point for the customer, saying how they agreed.\n``accepted_finding_ids`` is the dry run's choice (its options are the points)."
      },
      "main": true
    },
    {
      "id": "artwork_preparation.share_part_file",
      "module": "artwork_preparation_module",
      "label": "Reuse a file…",
      "description": "Put another version's current file for a part on this version's next revision.",
      "target": "artwork_preparation.artwork",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/artwork/versions/{owner_version_id}/actions/share-part",
      "dry_run_path": "/artwork/versions/{owner_version_id}/actions/share-part/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "part_key": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100
              },
              {
                "type": "null"
              }
            ],
            "title": "Part",
            "x-cis-field": {
              "required": true
            }
          },
          "from_version_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Version to reuse the file from",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SharePartRequest",
        "description": "*Reuse a file…*: reuse ``from_version_id``'s current file for ``part_key`` (another version of the same line, the\nsame customer). Both are the dry run's choices."
      }
    },
    {
      "id": "artwork_preparation.decide_held_finding",
      "module": "artwork_preparation_module",
      "label": "Decide check warning…",
      "description": "Set a held finding's tier and re-evaluate its revision's verdict.",
      "target": "artwork_preparation.finding",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "A check warning must be decided in the app by someone who has looked at the file.",
      "method": "POST",
      "path": "/artwork/findings/{finding_id}/actions/decide",
      "dry_run_path": "/artwork/findings/{finding_id}/actions/decide/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "tier": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 50
              },
              {
                "type": "null"
              }
            ],
            "title": "What the check means",
            "description": "Can't print, Worth knowing or We'll sort it.",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "DecideFindingRequest",
        "description": "A person decides a held check. ``tier`` is the dry run's choice, validated by the action (400 in the\nfield-tagged shape, field ``tier``), so a wrong value names the valid ones."
      },
      "main": true
    },
    {
      "id": "artwork_preparation.split_combined_file",
      "module": "artwork_preparation_module",
      "label": "Split into files",
      "description": "Cut a combined file into one file per part on the person's confirmed page tags, as one revision for an artwork or one artwork check per part for a basket.",
      "target": "artwork_preparation.combined_file",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Which pages are which part's is a person's call, made in the app looking at the pages.",
      "method": "POST",
      "path": "/artwork/combined-files/{combined_file_id}/actions/split",
      "dry_run_path": "/artwork/combined-files/{combined_file_id}/actions/split/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "assignment": {
            "anyOf": [
              {
                "additionalProperties": {
                  "items": {
                    "type": "integer",
                    "maximum": 1000000.0,
                    "minimum": 0.0
                  },
                  "type": "array",
                  "maxItems": 20000
                },
                "type": "object",
                "maxProperties": 50
              },
              {
                "type": "null"
              }
            ],
            "title": "Pages of each part",
            "description": "Each part's pages in file order. Every page of the file goes in one part or is left out.",
            "x-cis-field": {
              "required": true
            }
          },
          "left_out_pages": {
            "anyOf": [
              {
                "items": {
                  "type": "integer",
                  "maximum": 1000000.0,
                  "minimum": 0.0
                },
                "type": "array",
                "maxItems": 20000
              },
              {
                "type": "null"
              }
            ],
            "title": "Pages left out",
            "description": "Pages that go to no part will stay in the original file only.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SplitCombinedFileRequest",
        "description": "*Split into files*: the person's confirmed tags. ``assignment`` maps each part key to its pages (1-based, in the\norder that part's file is assembled: a reader-order cover is ``[1, 2, 35, 36]``); ``left_out_pages`` are the pages\nthey tagged \"leave out\", which stay in the original only. Every page is in exactly one of them. A page outside the\nfile, tagged twice, or not tagged is refused in the field-tagged shape (``assignment.pages``). Both are optional so\nthe dry run can answer before anything is tagged, offering the suggested tags as its prefill."
      }
    },
    {
      "id": "artwork_preparation.discard_combined_file",
      "module": "artwork_preparation_module",
      "label": "Cancel the page tags",
      "description": "Cancel the page-tagging step of a combined file that was never split, deleting its original and page pictures.",
      "target": "artwork_preparation.combined_file",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "The file can only be thrown away by the person who sent it, in the app, having seen what it is.",
      "method": "POST",
      "path": "/artwork/combined-files/{combined_file_id}/actions/discard",
      "dry_run_path": "/artwork/combined-files/{combined_file_id}/actions/discard/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "artwork_proofing.decide_proof",
      "module": "artwork_proofing_module",
      "label": "Decide for the customer…",
      "description": "Record the customer's approval of a proof, with the points they accepted, or what they sent it back for.",
      "target": "artwork_proofing.proof",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/artwork/proofs/{proof_id}/actions/decide",
      "dry_run_path": "/artwork/proofs/{proof_id}/actions/decide/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "outcome": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Decision",
            "description": "Approve or Send back.",
            "x-cis-field": {
              "required": true
            }
          },
          "accepted_finding_ids": {
            "anyOf": [
              {
                "items": {
                  "type": "string"
                },
                "type": "array",
                "maxItems": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Points the customer accepted",
            "description": "To approve, tick every worth-knowing point the customer accepted.",
            "x-cis-field": {
              "show_if": {
                "outcome": "approved"
              }
            }
          },
          "note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 10000
              },
              {
                "type": "null"
              }
            ],
            "title": "How the customer approved it",
            "description": "Kept with the proof: 'Approved by phone with Jo Smith'.",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "outcome": "approved"
              },
              "widget": "textarea"
            }
          },
          "reason_code": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Why",
            "description": "What needs changing, as the customer said.",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "outcome": "rejected"
              }
            }
          },
          "comment": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 10000
              },
              {
                "type": "null"
              }
            ],
            "title": "What needs changing",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "outcome": "rejected"
              },
              "widget": "textarea"
            }
          },
          "wording_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Wording version",
            "description": "The version of the points' wording that was shown. One fills this in.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "StaffDecisionRequest",
        "description": "*Decide for the customer…*: staff record the customer's approval (with how they approved it) or what they sent\nback. ``outcome``, ``accepted_finding_ids`` and ``reason_code`` are the dry run's choices (plain values, the\noptions its own); ``wording_version`` is its guard, copied back unchanged. ``note`` and ``comment`` show for one\noutcome each and the action requires them there, so they are marked ``required`` although every field defaults."
      },
      "main": true
    },
    {
      "id": "artwork_proofing.reopen_proof",
      "module": "artwork_proofing_module",
      "label": "Reopen proof…",
      "description": "Undo a proof's approval before release, so it awaits the customer's decision again on the same files.",
      "target": "artwork_proofing.proof",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/artwork/proofs/{proof_id}/actions/reopen",
      "dry_run_path": "/artwork/proofs/{proof_id}/actions/reopen/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 10000
              },
              {
                "type": "null"
              }
            ],
            "title": "Why",
            "description": "Why the approval is reopened (500 characters at most).",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ReopenProofRequest",
        "description": "*Reopen proof…*: undo an approval before release, saying why."
      }
    },
    {
      "id": "catalogue.set_catalogue_accounts",
      "module": "catalogue_module",
      "label": "Set accounts…",
      "description": "Set the accounts of the customer a private catalogue belongs to; only they can have it allocated.",
      "target": "catalogue.catalogue",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Privacy is changed by staff in One's screens, deliberately (Ben, 23 Oct).",
      "method": "POST",
      "path": "/catalogue/manage/catalogues/{catalogue_id}/actions/set-accounts",
      "dry_run_path": "/catalogue/manage/catalogues/{catalogue_id}/actions/set-accounts/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "account_customer_codes": {
            "anyOf": [
              {
                "items": {
                  "type": "string"
                },
                "type": "array"
              },
              {
                "type": "null"
              }
            ],
            "title": "Accounts",
            "x-cis-field": {
              "entity": "customers.customer",
              "widget": "entity"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetCatalogueAccountsRequest",
        "description": "*Set accounts…*: the whole list of the customer's accounts that may have the private catalogue allocated."
      },
      "edits": {
        "fields": [
          "accounts"
        ]
      }
    },
    {
      "id": "catalogue.make_catalogue_private",
      "module": "catalogue_module",
      "label": "Make private…",
      "description": "Make a catalogue private to one customer, so only that customer's accounts can have it allocated.",
      "target": "catalogue.catalogue",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Privacy is changed by staff in One's screens, deliberately (Ben, 23 Oct).",
      "method": "POST",
      "path": "/catalogue/manage/catalogues/{catalogue_id}/actions/make-private",
      "dry_run_path": "/catalogue/manage/catalogues/{catalogue_id}/actions/make-private/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "customer_code": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "x-cis-field": {
              "entity": "customers.customer",
              "required": true,
              "widget": "entity"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "MakeCataloguePrivateRequest",
        "description": "*Make private…*: the one customer the catalogue will belong to."
      }
    },
    {
      "id": "catalogue.make_catalogue_shared",
      "module": "catalogue_module",
      "label": "Make shared…",
      "description": "Make a private catalogue shared, so any customer can have it allocated; refused while it holds repeatable items.",
      "target": "catalogue.catalogue",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Privacy is changed by staff in One's screens, deliberately (Ben, 23 Oct).",
      "method": "POST",
      "path": "/catalogue/manage/catalogues/{catalogue_id}/actions/make-shared",
      "dry_run_path": "/catalogue/manage/catalogues/{catalogue_id}/actions/make-shared/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "MakeCatalogueSharedRequest",
        "description": "*Make shared…*: takes nothing."
      }
    },
    {
      "id": "catalogue.restore_catalogue_version",
      "module": "catalogue_module",
      "label": "Restore catalogue version…",
      "description": "Put a catalogue managed in One back as it was at an earlier version, saved as a new version.",
      "target": "catalogue.catalogue",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/manage/catalogues/{catalogue_id}/actions/restore-version",
      "dry_run_path": "/catalogue/manage/catalogues/{catalogue_id}/actions/restore-version/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "version_number": {
            "anyOf": [
              {
                "type": "integer",
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Version",
            "x-cis-field": {
              "hidden": true
            }
          },
          "confirmed": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 5,
            "title": "Confirmed",
            "description": "The dry run's confirmations ticked, by key: `withdraw_items`."
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RestoreCatalogueVersionRequest",
        "description": "*Restore catalogue version…*: the version to put back (a dry-run choice)."
      }
    },
    {
      "id": "catalogue.hand_over_catalogue",
      "module": "catalogue_module",
      "label": "Hand over to One…",
      "description": "Hand a catalogue managed in code over to One, so staff edit it in One and the deploy stops applying its definition files.",
      "target": "catalogue.catalogue",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Ownership of a catalogue changes only by a deliberate step in One's screens, and One has no way back (Ben, 25 Oct).",
      "method": "POST",
      "path": "/catalogue/manage/catalogues/{catalogue_id}/actions/hand-over",
      "dry_run_path": "/catalogue/manage/catalogues/{catalogue_id}/actions/hand-over/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "description": "Why the catalogue is handed over, kept with the handover.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "HandOverCatalogueRequest",
        "description": "*Hand over to One…*: the catalogue's definition files stop applying and staff edit it in One from then on."
      }
    },
    {
      "id": "catalogue.export_dictionary_workbook",
      "module": "catalogue_module",
      "label": "Download dictionary and catalogues…",
      "description": "Download chosen catalogues, and the dictionary's customer wording, as one Excel workbook (Dictionary and catalogues) to edit and submit back.",
      "target": "collection:catalogue.catalogue",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/workbook/export",
      "dry_run_path": "/catalogue/workbook/export/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "catalogue_ids": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 50,
            "title": "Catalogues",
            "x-cis-field": {
              "entity": "catalogue.catalogue"
            }
          },
          "wording": {
            "type": "string",
            "enum": [
              "none",
              "short",
              "full"
            ],
            "title": "Customer wording",
            "description": "The dictionary's customer wording sheets to include: the short (first-pass) list or the full list.",
            "default": "none",
            "x-cis-field": {
              "enum_labels": {
                "full": "Full list",
                "none": "None",
                "short": "Short list"
              }
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ExportDictionaryWorkbookRequest",
        "description": "*Download dictionary and catalogues…*: the catalogues to export, in this order, and/or a customer wording list."
      }
    },
    {
      "id": "catalogue.submit_dictionary_workbook",
      "module": "catalogue_module",
      "label": "Submit dictionary and catalogues…",
      "description": "Check an edited Dictionary and catalogues workbook, listing every problem by cell, and save each changed catalogue that is managed in One as a new version.",
      "target": "collection:catalogue.catalogue",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/workbook/submit",
      "dry_run_path": "/catalogue/workbook/submit/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "workbook_ref": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Workbook",
            "description": "The edited dictionary workbook (.xlsx).",
            "x-cis-field": {
              "accept": [
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              ],
              "max_bytes": 10000000,
              "upload": {
                "file_field": "file",
                "path": "/catalogue/workbook/uploads",
                "ref_field": "workbook_ref"
              }
            }
          },
          "sheets": {
            "anyOf": [
              {
                "items": {
                  "properties": {
                    "name": {
                      "type": "string",
                      "maxLength": 31,
                      "minLength": 1,
                      "title": "Sheet name",
                      "x-cis-field": {
                        "hidden": true
                      }
                    },
                    "cells": {
                      "items": {
                        "items": {
                          "anyOf": [
                            {
                              "type": "string"
                            },
                            {
                              "type": "integer"
                            },
                            {
                              "type": "number"
                            },
                            {
                              "type": "boolean"
                            },
                            {
                              "type": "null"
                            }
                          ]
                        },
                        "type": "array"
                      },
                      "type": "array",
                      "maxItems": 50000,
                      "title": "Cells",
                      "x-cis-field": {
                        "hidden": true
                      }
                    }
                  },
                  "additionalProperties": false,
                  "type": "object",
                  "required": [
                    "name"
                  ],
                  "title": "SheetGridBody",
                  "description": "One sheet of the workbook as JSON (Claude in Excel)."
                },
                "type": "array",
                "maxItems": 100
              },
              {
                "type": "null"
              }
            ],
            "title": "Sheets",
            "description": "The workbook's sheets as Excel shows them: `name`, and `cells` as rows of values from A1 (`cells[0]` is row 1, `cells[r][0]` is column A). Send the README identity table with every catalogue sheet (the `Catalogues README` sheet). A catalogue sheet is the whole catalogue: a missing row withdraws that item. Sheets not sent are not changed. The `Customer catalogues` sheet holds several catalogues, each row's `catalogue_id` saying which: send it whole, because it is the whole of every catalogue the README table puts on it. The body must stay under 256 KB: send only the sheets you changed, with `Catalogues README`.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "confirmed": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 5,
            "title": "Confirmed",
            "description": "The dry run's confirmations ticked, by key: `withdraw_items`."
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SubmitDictionaryWorkbookRequest",
        "description": "*Submit dictionary and catalogues…*: an uploaded workbook (``workbook_ref``) or its sheets (``sheets``), not both."
      }
    },
    {
      "id": "catalogue.quote_catalogue_item_now",
      "module": "catalogue_module",
      "label": "Quote now…",
      "description": "Send the customer's contact a quote for one catalogue item at once, through a one-line draft quote that is published and shared.",
      "target": "catalogue.catalogue_item",
      "destructive": true,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/items/{item_id}/actions/quote-now",
      "dry_run_path": "/catalogue/items/{item_id}/actions/quote-now/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "required_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Needed by",
            "x-cis-field": {
              "min_date": "today"
            }
          },
          "custom_note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 2000
              },
              {
                "type": "null"
              }
            ],
            "title": "Note to the customer",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "internal_notes": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 2000
              },
              {
                "type": "null"
              }
            ],
            "title": "Internal notes",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "expires_at": {
            "anyOf": [
              {
                "type": "string",
                "format": "date-time"
              },
              {
                "type": "null"
              }
            ],
            "title": "Expires",
            "x-cis-field": {}
          },
          "reminder_interval_hours": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 1440.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Remind every (hours)",
            "x-cis-field": {}
          },
          "assigned_rep_user_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Sales rep",
            "x-cis-field": {
              "entity": "users.user",
              "widget": "entity"
            }
          },
          "cc_emails": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 10,
            "title": "Copy to",
            "default": [],
            "x-cis-field": {}
          },
          "version_split": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "even",
                  "entered"
                ],
                "title": "VersionSplit",
                "description": "How a multi-version line's quantity was shared out on the catalogue item page (R18-1)."
              },
              {
                "type": "null"
              }
            ],
            "title": "Split between versions",
            "description": "How a line of two or more versions (the item's Versions count above 1) shares its quantity: `even` (the default when there are several versions: each version gets the same number of the item's steps, the remainder one step each to the first versions; refused when the quantity cannot be split that way) or `entered` (with `version_quantities`). Each version is priced at its own quantity. Leave it out for one version.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "version_quantities": {
            "anyOf": [
              {
                "items": {
                  "type": "integer"
                },
                "type": "array",
                "maxItems": 26
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity of each version",
            "description": "With `version_split` `entered` only: one whole number of at least 1 per version, in order (A, B, C…), adding up to `quantity`. Entered quantities cannot be offered as other quantities.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "customer_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 50,
                "minLength": 1
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "description": "The customer number (CUS-…), or the customer's Tharstern code.",
            "x-cis-field": {
              "entity": "customers.customer",
              "required": true
            }
          },
          "configuration": {
            "anyOf": [
              {
                "additionalProperties": true,
                "type": "object"
              },
              {
                "type": "null"
              }
            ],
            "title": "Specification",
            "description": "Each field's chosen option or custom value. The item's default is filled in to start.",
            "x-cis-field": {
              "custom_widget": "item_configuration"
            }
          },
          "quantity": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 10000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity",
            "x-cis-field": {}
          },
          "speed_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "Speed",
            "description": "The chosen Speed's code; none, or `default`, takes the default Speed.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "delivery": {
            "anyOf": [
              {
                "properties": {
                  "mode": {
                    "anyOf": [
                      {
                        "type": "string",
                        "enum": [
                          "ship",
                          "multi",
                          "collect",
                          "hold",
                          "tbc"
                        ]
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery mode",
                    "x-cis-field": {}
                  },
                  "delivery_address_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery address",
                    "x-cis-field": {}
                  },
                  "service_level": {
                    "anyOf": [
                      {
                        "type": "string",
                        "enum": [
                          "next_day",
                          "pre_noon",
                          "pre_1030",
                          "pallet",
                          "northern_ireland",
                          "collection"
                        ],
                        "title": "DeliveryServiceLevel",
                        "description": "A delivery service level (R87). ``collection`` is the customer collecting: never priced by a courier."
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery service",
                    "x-cis-field": {}
                  },
                  "courier_supplier_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Courier",
                    "x-cis-field": {}
                  },
                  "agreed_delivery_price_pence": {
                    "anyOf": [
                      {
                        "type": "integer",
                        "maximum": 100000000.0,
                        "minimum": 0.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Agreed delivery price",
                    "x-cis-field": {}
                  },
                  "agreed_delivery_reason": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 200
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Reason for the agreed price",
                    "x-cis-field": {}
                  },
                  "weight_kg": {
                    "anyOf": [
                      {
                        "type": "number",
                        "maximum": 10000.0,
                        "exclusiveMinimum": 0.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Weight (kg)",
                    "x-cis-field": {}
                  },
                  "parcels": {
                    "anyOf": [
                      {
                        "type": "integer",
                        "maximum": 500.0,
                        "minimum": 1.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Parcels",
                    "x-cis-field": {}
                  },
                  "collection_point_address_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Collection point",
                    "x-cis-field": {}
                  },
                  "recipient_name": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 100
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient name",
                    "x-cis-field": {}
                  },
                  "recipient_phone": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 40
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient phone",
                    "x-cis-field": {}
                  },
                  "recipient_email": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 254
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient email",
                    "x-cis-field": {}
                  },
                  "delivery_lines": {
                    "items": {
                      "properties": {
                        "delivery_address_id": {
                          "type": "string",
                          "maxLength": 64,
                          "minLength": 1,
                          "title": "Delivery address",
                          "x-cis-field": {}
                        },
                        "quantity": {
                          "type": "integer",
                          "maximum": 10000000.0,
                          "minimum": 1.0,
                          "title": "Quantity",
                          "x-cis-field": {}
                        },
                        "version_position": {
                          "anyOf": [
                            {
                              "type": "integer",
                              "maximum": 100.0,
                              "minimum": 1.0
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Version",
                          "x-cis-field": {}
                        },
                        "recipient_name": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 100
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient name",
                          "x-cis-field": {}
                        },
                        "recipient_phone": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 40
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient phone",
                          "x-cis-field": {}
                        },
                        "recipient_email": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 254
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient email",
                          "x-cis-field": {}
                        }
                      },
                      "additionalProperties": false,
                      "type": "object",
                      "required": [
                        "delivery_address_id",
                        "quantity"
                      ],
                      "title": "RequestedDeliveryLineSchema",
                      "description": "One row of a line split across addresses (mode ``multi``, order to invoice doc 106 §3.1): how many go to which\nof the customer's addresses, of which version on a line with several, and who receives them when that is not the\naddress's recipient."
                    },
                    "type": "array",
                    "maxItems": 20,
                    "title": "Delivery rows",
                    "x-cis-field": {}
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "title": "DeliverySelectionSchema",
                "description": "A line's delivery choice. A destination is a CIS address of the customer's (``delivery_address_id``), or, to\ncollect, one of our collection points. The level is the customer's or the CSR's; the courier, an agreed price and\nentered packages are staff-only (the portal's routes discard them)."
              },
              {
                "type": "null"
              }
            ],
            "title": "Delivery",
            "description": "Where the line's goods go.",
            "x-cis-field": {
              "custom_widget": "delivery_allocation",
              "required": true
            }
          },
          "contact_email": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 320
              },
              {
                "type": "null"
              }
            ],
            "title": "Contact",
            "x-cis-field": {
              "hidden": true
            }
          },
          "new_contact_email": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 320
              },
              {
                "type": "null"
              }
            ],
            "title": "Contact email",
            "description": "The address of a contact who is not one of the customer's people yet.",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "contact_email": "new"
              }
            }
          },
          "price_option": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100,
                "pattern": "^(default|[^:]+:[^:]+)$"
              },
              {
                "type": "null"
              }
            ],
            "title": "Price option",
            "description": "The chosen option's key, `route_code:tier`; none, or `default`, takes the sourcing policy's default.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "route_override_reason": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "earlier_date",
                  "quality_or_specialism",
                  "keep_in_house",
                  "known_problem_with_default",
                  "customer_asked",
                  "other"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Why not the default",
            "x-cis-field": {
              "hidden": true
            }
          },
          "route_override_note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Note on the reason",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "route_override_reason": [
                  "other"
                ]
              }
            }
          },
          "needed_by": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Needed by",
            "x-cis-field": {
              "hidden": true
            }
          },
          "needed_by_is_hard": {
            "type": "boolean",
            "title": "Needed by is a fixed date",
            "default": false,
            "x-cis-field": {
              "hidden": true
            }
          },
          "price_level": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "contracted",
                  "last_ordered",
                  "similar_ordered",
                  "last_quoted",
                  "policy",
                  "custom"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Price",
            "x-cis-field": {
              "hidden": true
            }
          },
          "suggested_price_level": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "contracted",
                  "last_ordered",
                  "similar_ordered",
                  "last_quoted",
                  "policy",
                  "custom"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Suggested price level",
            "x-cis-field": {
              "hidden": true
            }
          },
          "custom_price_pence": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 100000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Custom price",
            "description": "The line's price ex VAT at the custom level.",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "price_level": "custom"
              },
              "widget": "money"
            }
          },
          "price_adjustment_reason": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "contracted_rate",
                  "held_at_last_price",
                  "matched_last_quote",
                  "commercial_discretion",
                  "customer_agreement"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Why the custom price",
            "x-cis-field": {
              "hidden": true
            }
          },
          "repeat_of_tharstern_job_no": {
            "anyOf": [
              {
                "type": "string",
                "pattern": "^[0-9]{1,12}(\\.[0-9]{2,3})?$"
              },
              {
                "type": "null"
              }
            ],
            "title": "Repeats Tharstern job",
            "x-cis-field": {
              "hidden": true
            }
          },
          "wait_seconds": {
            "anyOf": [
              {
                "type": "number",
                "maximum": 20.0,
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Wait for prices",
            "description": "How long to wait for every route's price, 0 to 20 seconds; omitted is the default. In-house prices take about 6 to 10 seconds, so pass 10 when every price is needed now. A route still pricing after it is named in the refusal, with when to ask again.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "expected_total_pence": {
            "anyOf": [
              {
                "type": "integer",
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Total at preview",
            "description": "The total, ex VAT, the dry run showed (the line and the deliveries that have a price): a price that moved since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "contact_person_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Contact person",
            "description": "The person who receives the order or quote, when the caller has already made them one of the customer's people (Classic's checkout dialog does). Left out, the contact is the one `contact_email` names.",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "QuoteNowRequest",
        "description": "*Quote now…*: one line, made into a draft quote that is published and shared at once with the line's contact;\nthe basket is not touched. The line is ``BasketLineRequest``'s, with the needed-by date and the send options.\n``request_id`` keys the new draft quote: a repeat returns the quote the first request made and emails no one\nagain."
      },
      "main": true
    },
    {
      "id": "catalogue.add_to_draft_quote",
      "module": "catalogue_module",
      "label": "Add to draft quote…",
      "description": "Configure and price a catalogue item for a customer and add it, with its delivery, to an open draft quote the person picks, whoever owns it, or to a new draft quote for the customer when none is picked. A picked draft quote gives the customer when none is chosen.",
      "target": "catalogue.catalogue_item",
      "destructive": false,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/items/{item_id}/actions/add-to-draft-quote",
      "dry_run_path": "/catalogue/items/{item_id}/actions/add-to-draft-quote/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "customer_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 50,
                "minLength": 1
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "description": "The customer number (CUS-…), or the customer's Tharstern code. Left empty with a draft quote picked, the draft quote's customer.",
            "x-cis-field": {
              "entity": "customers.customer"
            }
          },
          "quote_no": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft quote",
            "description": "An open draft quote, anyone's. Left empty, a new draft quote for the customer.",
            "x-cis-field": {
              "entity": "quotes.quote"
            }
          },
          "version_split": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "even",
                  "entered"
                ],
                "title": "VersionSplit",
                "description": "How a multi-version line's quantity was shared out on the catalogue item page (R18-1)."
              },
              {
                "type": "null"
              }
            ],
            "title": "Split between versions",
            "description": "How a line of two or more versions (the item's Versions count above 1) shares its quantity: `even` (the default when there are several versions: each version gets the same number of the item's steps, the remainder one step each to the first versions; refused when the quantity cannot be split that way) or `entered` (with `version_quantities`). Each version is priced at its own quantity. Leave it out for one version.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "version_quantities": {
            "anyOf": [
              {
                "items": {
                  "type": "integer"
                },
                "type": "array",
                "maxItems": 26
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity of each version",
            "description": "With `version_split` `entered` only: one whole number of at least 1 per version, in order (A, B, C…), adding up to `quantity`. Entered quantities cannot be offered as other quantities.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "wait_seconds": {
            "anyOf": [
              {
                "type": "number",
                "maximum": 20.0,
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Wait for prices",
            "description": "How long to wait for every route's price, 0 to 20 seconds; omitted is the default. In-house prices take about 6 to 10 seconds, so pass 10 when every price is needed now. A route still pricing after it is named in the refusal, with when to ask again.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "configuration": {
            "anyOf": [
              {
                "additionalProperties": true,
                "type": "object"
              },
              {
                "type": "null"
              }
            ],
            "title": "Specification",
            "description": "Each field's chosen option or custom value. The item's default is filled in to start.",
            "x-cis-field": {
              "custom_widget": "item_configuration"
            }
          },
          "quantity": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 10000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity",
            "x-cis-field": {}
          },
          "needed_by": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Needed by",
            "description": "The Speed offered by default will be the cheapest one that arrives by this date.",
            "x-cis-field": {}
          },
          "needed_by_is_hard": {
            "type": "boolean",
            "title": "Fixed date: it must arrive by then",
            "description": "Without a Needed by date, this will make no difference.",
            "default": false,
            "x-cis-field": {}
          },
          "speed_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "Speed",
            "description": "The chosen Speed's code; none, or `default`, takes the default Speed.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "delivery": {
            "anyOf": [
              {
                "properties": {
                  "mode": {
                    "anyOf": [
                      {
                        "type": "string",
                        "enum": [
                          "ship",
                          "multi",
                          "collect",
                          "hold",
                          "tbc"
                        ]
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery mode",
                    "x-cis-field": {}
                  },
                  "delivery_address_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery address",
                    "x-cis-field": {}
                  },
                  "service_level": {
                    "anyOf": [
                      {
                        "type": "string",
                        "enum": [
                          "next_day",
                          "pre_noon",
                          "pre_1030",
                          "pallet",
                          "northern_ireland",
                          "collection"
                        ],
                        "title": "DeliveryServiceLevel",
                        "description": "A delivery service level (R87). ``collection`` is the customer collecting: never priced by a courier."
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery service",
                    "x-cis-field": {}
                  },
                  "courier_supplier_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Courier",
                    "x-cis-field": {}
                  },
                  "agreed_delivery_price_pence": {
                    "anyOf": [
                      {
                        "type": "integer",
                        "maximum": 100000000.0,
                        "minimum": 0.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Agreed delivery price",
                    "x-cis-field": {}
                  },
                  "agreed_delivery_reason": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 200
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Reason for the agreed price",
                    "x-cis-field": {}
                  },
                  "weight_kg": {
                    "anyOf": [
                      {
                        "type": "number",
                        "maximum": 10000.0,
                        "exclusiveMinimum": 0.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Weight (kg)",
                    "x-cis-field": {}
                  },
                  "parcels": {
                    "anyOf": [
                      {
                        "type": "integer",
                        "maximum": 500.0,
                        "minimum": 1.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Parcels",
                    "x-cis-field": {}
                  },
                  "collection_point_address_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Collection point",
                    "x-cis-field": {}
                  },
                  "recipient_name": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 100
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient name",
                    "x-cis-field": {}
                  },
                  "recipient_phone": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 40
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient phone",
                    "x-cis-field": {}
                  },
                  "recipient_email": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 254
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient email",
                    "x-cis-field": {}
                  },
                  "delivery_lines": {
                    "items": {
                      "properties": {
                        "delivery_address_id": {
                          "type": "string",
                          "maxLength": 64,
                          "minLength": 1,
                          "title": "Delivery address",
                          "x-cis-field": {}
                        },
                        "quantity": {
                          "type": "integer",
                          "maximum": 10000000.0,
                          "minimum": 1.0,
                          "title": "Quantity",
                          "x-cis-field": {}
                        },
                        "version_position": {
                          "anyOf": [
                            {
                              "type": "integer",
                              "maximum": 100.0,
                              "minimum": 1.0
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Version",
                          "x-cis-field": {}
                        },
                        "recipient_name": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 100
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient name",
                          "x-cis-field": {}
                        },
                        "recipient_phone": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 40
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient phone",
                          "x-cis-field": {}
                        },
                        "recipient_email": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 254
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient email",
                          "x-cis-field": {}
                        }
                      },
                      "additionalProperties": false,
                      "type": "object",
                      "required": [
                        "delivery_address_id",
                        "quantity"
                      ],
                      "title": "RequestedDeliveryLineSchema",
                      "description": "One row of a line split across addresses (mode ``multi``, order to invoice doc 106 §3.1): how many go to which\nof the customer's addresses, of which version on a line with several, and who receives them when that is not the\naddress's recipient."
                    },
                    "type": "array",
                    "maxItems": 20,
                    "title": "Delivery rows",
                    "x-cis-field": {}
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "title": "DeliverySelectionSchema",
                "description": "A line's delivery choice. A destination is a CIS address of the customer's (``delivery_address_id``), or, to\ncollect, one of our collection points. The level is the customer's or the CSR's; the courier, an agreed price and\nentered packages are staff-only (the portal's routes discard them)."
              },
              {
                "type": "null"
              }
            ],
            "title": "Delivery",
            "description": "Where the line's goods go.",
            "x-cis-field": {
              "custom_widget": "delivery_allocation",
              "required": true
            }
          },
          "price_option": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100,
                "pattern": "^(default|[^:]+:[^:]+)$"
              },
              {
                "type": "null"
              }
            ],
            "title": "Price option",
            "description": "The chosen option's key, `route_code:tier`; none, or `default`, takes the sourcing policy's default.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "route_override_reason": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "earlier_date",
                  "quality_or_specialism",
                  "keep_in_house",
                  "known_problem_with_default",
                  "customer_asked",
                  "other"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Why not the default",
            "x-cis-field": {
              "hidden": true
            }
          },
          "route_override_note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Note on the reason",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "route_override_reason": [
                  "other"
                ]
              }
            }
          },
          "price_level": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "contracted",
                  "last_ordered",
                  "similar_ordered",
                  "last_quoted",
                  "policy",
                  "custom"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Price",
            "x-cis-field": {
              "hidden": true
            }
          },
          "suggested_price_level": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "contracted",
                  "last_ordered",
                  "similar_ordered",
                  "last_quoted",
                  "policy",
                  "custom"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Suggested price level",
            "x-cis-field": {
              "hidden": true
            }
          },
          "custom_price_pence": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 100000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Custom price",
            "description": "The line's price ex VAT at the custom level.",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "price_level": "custom"
              },
              "widget": "money"
            }
          },
          "price_adjustment_reason": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "contracted_rate",
                  "held_at_last_price",
                  "matched_last_quote",
                  "commercial_discretion",
                  "customer_agreement"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Why the custom price",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddToDraftQuoteRequest",
        "description": "*Add to draft quote…*, on a catalogue item: the item's line, on the draft quote picked or on a new one."
      }
    },
    {
      "id": "catalogue.order_catalogue_item_now",
      "module": "catalogue_module",
      "label": "Order now…",
      "description": "Place one catalogue item as the customer's order at once, paid as the payer allows, through a one-line draft quote that ends Ordered.",
      "target": "catalogue.catalogue_item",
      "destructive": true,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/items/{item_id}/actions/order-now",
      "dry_run_path": "/catalogue/items/{item_id}/actions/order-now/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "version_split": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "even",
                  "entered"
                ],
                "title": "VersionSplit",
                "description": "How a multi-version line's quantity was shared out on the catalogue item page (R18-1)."
              },
              {
                "type": "null"
              }
            ],
            "title": "Split between versions",
            "description": "How a line of two or more versions (the item's Versions count above 1) shares its quantity: `even` (the default when there are several versions: each version gets the same number of the item's steps, the remainder one step each to the first versions; refused when the quantity cannot be split that way) or `entered` (with `version_quantities`). Each version is priced at its own quantity. Leave it out for one version.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "version_quantities": {
            "anyOf": [
              {
                "items": {
                  "type": "integer"
                },
                "type": "array",
                "maxItems": 26
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity of each version",
            "description": "With `version_split` `entered` only: one whole number of at least 1 per version, in order (A, B, C…), adding up to `quantity`. Entered quantities cannot be offered as other quantities.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "customer_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 50,
                "minLength": 1
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "description": "The customer number (CUS-…), or the customer's Tharstern code.",
            "x-cis-field": {
              "entity": "customers.customer",
              "required": true
            }
          },
          "configuration": {
            "anyOf": [
              {
                "additionalProperties": true,
                "type": "object"
              },
              {
                "type": "null"
              }
            ],
            "title": "Specification",
            "description": "Each field's chosen option or custom value. The item's default is filled in to start.",
            "x-cis-field": {
              "custom_widget": "item_configuration"
            }
          },
          "quantity": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 10000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity",
            "x-cis-field": {}
          },
          "speed_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "Speed",
            "description": "The chosen Speed's code; none, or `default`, takes the default Speed.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "delivery": {
            "anyOf": [
              {
                "properties": {
                  "mode": {
                    "anyOf": [
                      {
                        "type": "string",
                        "enum": [
                          "ship",
                          "multi",
                          "collect",
                          "hold",
                          "tbc"
                        ]
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery mode",
                    "x-cis-field": {}
                  },
                  "delivery_address_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery address",
                    "x-cis-field": {}
                  },
                  "service_level": {
                    "anyOf": [
                      {
                        "type": "string",
                        "enum": [
                          "next_day",
                          "pre_noon",
                          "pre_1030",
                          "pallet",
                          "northern_ireland",
                          "collection"
                        ],
                        "title": "DeliveryServiceLevel",
                        "description": "A delivery service level (R87). ``collection`` is the customer collecting: never priced by a courier."
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery service",
                    "x-cis-field": {}
                  },
                  "courier_supplier_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Courier",
                    "x-cis-field": {}
                  },
                  "agreed_delivery_price_pence": {
                    "anyOf": [
                      {
                        "type": "integer",
                        "maximum": 100000000.0,
                        "minimum": 0.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Agreed delivery price",
                    "x-cis-field": {}
                  },
                  "agreed_delivery_reason": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 200
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Reason for the agreed price",
                    "x-cis-field": {}
                  },
                  "weight_kg": {
                    "anyOf": [
                      {
                        "type": "number",
                        "maximum": 10000.0,
                        "exclusiveMinimum": 0.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Weight (kg)",
                    "x-cis-field": {}
                  },
                  "parcels": {
                    "anyOf": [
                      {
                        "type": "integer",
                        "maximum": 500.0,
                        "minimum": 1.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Parcels",
                    "x-cis-field": {}
                  },
                  "collection_point_address_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Collection point",
                    "x-cis-field": {}
                  },
                  "recipient_name": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 100
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient name",
                    "x-cis-field": {}
                  },
                  "recipient_phone": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 40
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient phone",
                    "x-cis-field": {}
                  },
                  "recipient_email": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 254
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient email",
                    "x-cis-field": {}
                  },
                  "delivery_lines": {
                    "items": {
                      "properties": {
                        "delivery_address_id": {
                          "type": "string",
                          "maxLength": 64,
                          "minLength": 1,
                          "title": "Delivery address",
                          "x-cis-field": {}
                        },
                        "quantity": {
                          "type": "integer",
                          "maximum": 10000000.0,
                          "minimum": 1.0,
                          "title": "Quantity",
                          "x-cis-field": {}
                        },
                        "version_position": {
                          "anyOf": [
                            {
                              "type": "integer",
                              "maximum": 100.0,
                              "minimum": 1.0
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Version",
                          "x-cis-field": {}
                        },
                        "recipient_name": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 100
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient name",
                          "x-cis-field": {}
                        },
                        "recipient_phone": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 40
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient phone",
                          "x-cis-field": {}
                        },
                        "recipient_email": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 254
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient email",
                          "x-cis-field": {}
                        }
                      },
                      "additionalProperties": false,
                      "type": "object",
                      "required": [
                        "delivery_address_id",
                        "quantity"
                      ],
                      "title": "RequestedDeliveryLineSchema",
                      "description": "One row of a line split across addresses (mode ``multi``, order to invoice doc 106 §3.1): how many go to which\nof the customer's addresses, of which version on a line with several, and who receives them when that is not the\naddress's recipient."
                    },
                    "type": "array",
                    "maxItems": 20,
                    "title": "Delivery rows",
                    "x-cis-field": {}
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "title": "DeliverySelectionSchema",
                "description": "A line's delivery choice. A destination is a CIS address of the customer's (``delivery_address_id``), or, to\ncollect, one of our collection points. The level is the customer's or the CSR's; the courier, an agreed price and\nentered packages are staff-only (the portal's routes discard them)."
              },
              {
                "type": "null"
              }
            ],
            "title": "Delivery",
            "description": "Where the line's goods go.",
            "x-cis-field": {
              "custom_widget": "delivery_allocation",
              "required": true
            }
          },
          "contact_email": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 320
              },
              {
                "type": "null"
              }
            ],
            "title": "Contact",
            "x-cis-field": {
              "hidden": true
            }
          },
          "new_contact_email": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 320
              },
              {
                "type": "null"
              }
            ],
            "title": "Contact email",
            "description": "The address of a contact who is not one of the customer's people yet.",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "contact_email": "new"
              }
            }
          },
          "price_option": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100,
                "pattern": "^(default|[^:]+:[^:]+)$"
              },
              {
                "type": "null"
              }
            ],
            "title": "Price option",
            "description": "The chosen option's key, `route_code:tier`; none, or `default`, takes the sourcing policy's default.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "route_override_reason": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "earlier_date",
                  "quality_or_specialism",
                  "keep_in_house",
                  "known_problem_with_default",
                  "customer_asked",
                  "other"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Why not the default",
            "x-cis-field": {
              "hidden": true
            }
          },
          "route_override_note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Note on the reason",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "route_override_reason": [
                  "other"
                ]
              }
            }
          },
          "needed_by": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Needed by",
            "x-cis-field": {
              "hidden": true
            }
          },
          "needed_by_is_hard": {
            "type": "boolean",
            "title": "Needed by is a fixed date",
            "default": false,
            "x-cis-field": {
              "hidden": true
            }
          },
          "price_level": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "contracted",
                  "last_ordered",
                  "similar_ordered",
                  "last_quoted",
                  "policy",
                  "custom"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Price",
            "x-cis-field": {
              "hidden": true
            }
          },
          "suggested_price_level": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "contracted",
                  "last_ordered",
                  "similar_ordered",
                  "last_quoted",
                  "policy",
                  "custom"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Suggested price level",
            "x-cis-field": {
              "hidden": true
            }
          },
          "custom_price_pence": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 100000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Custom price",
            "description": "The line's price ex VAT at the custom level.",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "price_level": "custom"
              },
              "widget": "money"
            }
          },
          "price_adjustment_reason": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "contracted_rate",
                  "held_at_last_price",
                  "matched_last_quote",
                  "commercial_discretion",
                  "customer_agreement"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Why the custom price",
            "x-cis-field": {
              "hidden": true
            }
          },
          "repeat_of_tharstern_job_no": {
            "anyOf": [
              {
                "type": "string",
                "pattern": "^[0-9]{1,12}(\\.[0-9]{2,3})?$"
              },
              {
                "type": "null"
              }
            ],
            "title": "Repeats Tharstern job",
            "x-cis-field": {
              "hidden": true
            }
          },
          "wait_seconds": {
            "anyOf": [
              {
                "type": "number",
                "maximum": 20.0,
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Wait for prices",
            "description": "How long to wait for every route's price, 0 to 20 seconds; omitted is the default. In-house prices take about 6 to 10 seconds, so pass 10 when every price is needed now. A route still pricing after it is named in the refusal, with when to ask again.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "payment_method": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "card",
                  "proforma",
                  "credit_account"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Payment method",
            "x-cis-field": {
              "required": true
            }
          },
          "customer_order_number": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 50
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer PO number",
            "x-cis-field": {}
          },
          "required_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Needed by",
            "x-cis-field": {
              "min_date": "today"
            }
          },
          "expected_total_pence": {
            "anyOf": [
              {
                "type": "integer",
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Total at preview",
            "description": "The total, ex VAT, the dry run showed (the line and the deliveries that have a price): a price that moved since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "review_before_release": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "Review before release",
            "description": "Review before release for this order. Left empty, the order will follow the customer's setting.",
            "x-cis-field": {}
          },
          "deliver_together": {
            "type": "boolean",
            "title": "Deliver together",
            "description": "When on, lines are timed to arrive together, as long as that keeps them on time. Otherwise each line goes to its supplier as soon as it releases.",
            "default": true,
            "x-cis-field": {}
          },
          "add_to_customer_catalogue": {
            "type": "string",
            "enum": [
              "no",
              "ordinary",
              "repeatable"
            ],
            "title": "Add to the customer's catalogue",
            "description": "Nothing is added now: One adds the item to the customer's own catalogue when the order line is released. A repeatable item also waits for the order line's artwork to be final. Needs permission to add to a customer's catalogue.",
            "default": "no",
            "x-cis-field": {
              "enum_labels": {
                "no": "No",
                "ordinary": "As an ordinary item, once the order line is released",
                "repeatable": "As a repeatable item, once the order line is released and its artwork is final"
              }
            }
          },
          "contact_person_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Contact person",
            "description": "The person who receives the order or quote, when the caller has already made them one of the customer's people (Classic's checkout dialog does). Left out, the contact is the one `contact_email` names.",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "OrderNowRequest",
        "description": "*Order now…*: one line, made into a draft quote and placed at once as an order; the customer's basket is not\ntouched. The line is ``BasketLineRequest``'s, with how the customer pays, the PO number per the payer's PO rule and\nthe needed-by date. ``request_id`` is the command's idempotency key (quotes keeps it on the draft quote): a replay\nreturns the same order."
      }
    },
    {
      "id": "catalogue.add_catalogue_item_to_draft_quote",
      "module": "catalogue_module",
      "label": "Add catalogue item…",
      "description": "Configure and price a catalogue item for the draft quote's customer and add it, with its delivery, to the draft quote as a new line.",
      "target": "quotes.quote",
      "destructive": false,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/quotes/{quote_no}/actions/add-catalogue-item",
      "dry_run_path": "/catalogue/quotes/{quote_no}/actions/add-catalogue-item/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "item_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Catalogue item",
            "description": "The catalogue item to add, from the draft quote's customer's catalogues.",
            "x-cis-field": {
              "entity": "catalogue.catalogue_item",
              "required": true
            }
          },
          "version_split": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "even",
                  "entered"
                ],
                "title": "VersionSplit",
                "description": "How a multi-version line's quantity was shared out on the catalogue item page (R18-1)."
              },
              {
                "type": "null"
              }
            ],
            "title": "Split between versions",
            "description": "How a line of two or more versions (the item's Versions count above 1) shares its quantity: `even` (the default when there are several versions: each version gets the same number of the item's steps, the remainder one step each to the first versions; refused when the quantity cannot be split that way) or `entered` (with `version_quantities`). Each version is priced at its own quantity. Leave it out for one version.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "version_quantities": {
            "anyOf": [
              {
                "items": {
                  "type": "integer"
                },
                "type": "array",
                "maxItems": 26
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity of each version",
            "description": "With `version_split` `entered` only: one whole number of at least 1 per version, in order (A, B, C…), adding up to `quantity`. Entered quantities cannot be offered as other quantities.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "wait_seconds": {
            "anyOf": [
              {
                "type": "number",
                "maximum": 20.0,
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Wait for prices",
            "description": "How long to wait for every route's price, 0 to 20 seconds; omitted is the default. In-house prices take about 6 to 10 seconds, so pass 10 when every price is needed now. A route still pricing after it is named in the refusal, with when to ask again.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "configuration": {
            "anyOf": [
              {
                "additionalProperties": true,
                "type": "object"
              },
              {
                "type": "null"
              }
            ],
            "title": "Specification",
            "description": "Each field's chosen option or custom value. The item's default is filled in to start.",
            "x-cis-field": {
              "custom_widget": "item_configuration"
            }
          },
          "quantity": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 10000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity",
            "x-cis-field": {}
          },
          "needed_by": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Needed by",
            "description": "The Speed offered by default will be the cheapest one that arrives by this date.",
            "x-cis-field": {}
          },
          "needed_by_is_hard": {
            "type": "boolean",
            "title": "Fixed date: it must arrive by then",
            "description": "Without a Needed by date, this will make no difference.",
            "default": false,
            "x-cis-field": {}
          },
          "speed_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "Speed",
            "description": "The chosen Speed's code; none, or `default`, takes the default Speed.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "delivery": {
            "anyOf": [
              {
                "properties": {
                  "mode": {
                    "anyOf": [
                      {
                        "type": "string",
                        "enum": [
                          "ship",
                          "multi",
                          "collect",
                          "hold",
                          "tbc"
                        ]
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery mode",
                    "x-cis-field": {}
                  },
                  "delivery_address_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery address",
                    "x-cis-field": {}
                  },
                  "service_level": {
                    "anyOf": [
                      {
                        "type": "string",
                        "enum": [
                          "next_day",
                          "pre_noon",
                          "pre_1030",
                          "pallet",
                          "northern_ireland",
                          "collection"
                        ],
                        "title": "DeliveryServiceLevel",
                        "description": "A delivery service level (R87). ``collection`` is the customer collecting: never priced by a courier."
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery service",
                    "x-cis-field": {}
                  },
                  "courier_supplier_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Courier",
                    "x-cis-field": {}
                  },
                  "agreed_delivery_price_pence": {
                    "anyOf": [
                      {
                        "type": "integer",
                        "maximum": 100000000.0,
                        "minimum": 0.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Agreed delivery price",
                    "x-cis-field": {}
                  },
                  "agreed_delivery_reason": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 200
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Reason for the agreed price",
                    "x-cis-field": {}
                  },
                  "weight_kg": {
                    "anyOf": [
                      {
                        "type": "number",
                        "maximum": 10000.0,
                        "exclusiveMinimum": 0.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Weight (kg)",
                    "x-cis-field": {}
                  },
                  "parcels": {
                    "anyOf": [
                      {
                        "type": "integer",
                        "maximum": 500.0,
                        "minimum": 1.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Parcels",
                    "x-cis-field": {}
                  },
                  "collection_point_address_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Collection point",
                    "x-cis-field": {}
                  },
                  "recipient_name": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 100
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient name",
                    "x-cis-field": {}
                  },
                  "recipient_phone": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 40
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient phone",
                    "x-cis-field": {}
                  },
                  "recipient_email": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 254
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient email",
                    "x-cis-field": {}
                  },
                  "delivery_lines": {
                    "items": {
                      "properties": {
                        "delivery_address_id": {
                          "type": "string",
                          "maxLength": 64,
                          "minLength": 1,
                          "title": "Delivery address",
                          "x-cis-field": {}
                        },
                        "quantity": {
                          "type": "integer",
                          "maximum": 10000000.0,
                          "minimum": 1.0,
                          "title": "Quantity",
                          "x-cis-field": {}
                        },
                        "version_position": {
                          "anyOf": [
                            {
                              "type": "integer",
                              "maximum": 100.0,
                              "minimum": 1.0
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Version",
                          "x-cis-field": {}
                        },
                        "recipient_name": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 100
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient name",
                          "x-cis-field": {}
                        },
                        "recipient_phone": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 40
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient phone",
                          "x-cis-field": {}
                        },
                        "recipient_email": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 254
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient email",
                          "x-cis-field": {}
                        }
                      },
                      "additionalProperties": false,
                      "type": "object",
                      "required": [
                        "delivery_address_id",
                        "quantity"
                      ],
                      "title": "RequestedDeliveryLineSchema",
                      "description": "One row of a line split across addresses (mode ``multi``, order to invoice doc 106 §3.1): how many go to which\nof the customer's addresses, of which version on a line with several, and who receives them when that is not the\naddress's recipient."
                    },
                    "type": "array",
                    "maxItems": 20,
                    "title": "Delivery rows",
                    "x-cis-field": {}
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "title": "DeliverySelectionSchema",
                "description": "A line's delivery choice. A destination is a CIS address of the customer's (``delivery_address_id``), or, to\ncollect, one of our collection points. The level is the customer's or the CSR's; the courier, an agreed price and\nentered packages are staff-only (the portal's routes discard them)."
              },
              {
                "type": "null"
              }
            ],
            "title": "Delivery",
            "description": "Where the line's goods go.",
            "x-cis-field": {
              "custom_widget": "delivery_allocation",
              "required": true
            }
          },
          "price_option": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100,
                "pattern": "^(default|[^:]+:[^:]+)$"
              },
              {
                "type": "null"
              }
            ],
            "title": "Price option",
            "description": "The chosen option's key, `route_code:tier`; none, or `default`, takes the sourcing policy's default.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "route_override_reason": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "earlier_date",
                  "quality_or_specialism",
                  "keep_in_house",
                  "known_problem_with_default",
                  "customer_asked",
                  "other"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Why not the default",
            "x-cis-field": {
              "hidden": true
            }
          },
          "route_override_note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Note on the reason",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "route_override_reason": [
                  "other"
                ]
              }
            }
          },
          "price_level": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "contracted",
                  "last_ordered",
                  "similar_ordered",
                  "last_quoted",
                  "policy",
                  "custom"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Price",
            "x-cis-field": {
              "hidden": true
            }
          },
          "suggested_price_level": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "contracted",
                  "last_ordered",
                  "similar_ordered",
                  "last_quoted",
                  "policy",
                  "custom"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Suggested price level",
            "x-cis-field": {
              "hidden": true
            }
          },
          "custom_price_pence": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 100000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Custom price",
            "description": "The line's price ex VAT at the custom level.",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "price_level": "custom"
              },
              "widget": "money"
            }
          },
          "price_adjustment_reason": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "contracted_rate",
                  "held_at_last_price",
                  "matched_last_quote",
                  "commercial_discretion",
                  "customer_agreement"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Why the custom price",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddCatalogueItemToDraftQuoteRequest",
        "description": "*Add catalogue item…*: the item configured and priced for the draft quote's customer. The customer is the draft\nquote's."
      }
    },
    {
      "id": "catalogue.add_bespoke_line_to_draft_quote",
      "module": "catalogue_module",
      "label": "Add bespoke line…",
      "description": "Add a line for work the dictionary can't describe to the draft quote, waiting for supplier prices; ask up to three suppliers, or record a quote already held, which prices it when it is the only one.",
      "target": "quotes.quote",
      "destructive": true,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/quotes/{quote_no}/actions/add-bespoke-line",
      "dry_run_path": "/catalogue/quotes/{quote_no}/actions/add-bespoke-line/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "title": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 240
              },
              {
                "type": "null"
              }
            ],
            "title": "Title the customer reads",
            "x-cis-field": {
              "required": true
            }
          },
          "specification": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 8000
              },
              {
                "type": "null"
              }
            ],
            "title": "Specification for the supplier",
            "description": "Formatted text: - for bullets, 1. for numbered items, **bold** and *italic*. At most 4,000 characters.",
            "x-cis-field": {
              "required": true,
              "widget": "formatted_text"
            }
          },
          "supplier_files": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 10,
            "title": "Files for the supplier",
            "x-cis-field": {
              "max_bytes": 26214400,
              "upload": {
                "file_field": "file",
                "path": "/supplier-quotes/files",
                "ref_field": "ref"
              }
            }
          },
          "wanted_by": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Wanted by",
            "x-cis-field": {
              "min_date": "today"
            }
          },
          "quantity": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 10000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity",
            "x-cis-field": {
              "required": true
            }
          },
          "vat_rate_pct": {
            "anyOf": [
              {
                "type": "integer",
                "enum": [
                  0,
                  5,
                  20
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "VAT rate",
            "x-cis-field": {
              "enum_labels": {
                "0": "0%",
                "20": "20%",
                "5": "5%"
              },
              "required": true
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 600
              },
              {
                "type": "null"
              }
            ],
            "title": "Why the dictionary can't describe it",
            "description": "It goes to the dictionary gap log.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "first_supplier": {
            "properties": {
              "supplier_no": {
                "anyOf": [
                  {
                    "type": "string",
                    "maxLength": 64
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Supplier",
                "x-cis-field": {
                  "entity": "suppliers.supplier",
                  "widget": "entity"
                }
              },
              "have_quote": {
                "type": "boolean",
                "title": "We already have their quote",
                "default": false,
                "x-cis-field": {}
              },
              "cost": {
                "anyOf": [
                  {
                    "type": "integer",
                    "maximum": 100000000.0,
                    "minimum": 1.0
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Their delivered cost for the line",
                "x-cis-field": {
                  "required": true,
                  "show_if": {
                    "first_supplier.have_quote": true
                  },
                  "widget": "money"
                }
              },
              "their_reference": {
                "anyOf": [
                  {
                    "type": "string",
                    "maxLength": 200
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Their reference",
                "description": "Their number for the quote. For Pollards' own quote, the Tharstern estimate number.",
                "x-cis-field": {
                  "required": true,
                  "show_if": {
                    "first_supplier.have_quote": true
                  }
                }
              },
              "valid_until": {
                "anyOf": [
                  {
                    "type": "string",
                    "format": "date"
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Valid until",
                "x-cis-field": {
                  "required": true,
                  "show_if": {
                    "first_supplier.have_quote": true
                  }
                }
              },
              "quote_document": {
                "anyOf": [
                  {
                    "type": "string",
                    "maxLength": 64
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Their quote",
                "description": "Their quote as a file: their PDF, a screenshot or the email saved as a file. Not needed for Pollards: the estimate in Tharstern is the evidence.",
                "x-cis-field": {
                  "accept": [
                    "application/pdf",
                    "image/png",
                    "image/jpeg",
                    "message/rfc822",
                    "application/vnd.ms-outlook",
                    ".eml",
                    ".msg"
                  ],
                  "max_bytes": 26214400,
                  "show_if": {
                    "first_supplier.have_quote": true
                  },
                  "upload": {
                    "file_field": "file",
                    "path": "/supplier-quotes/their-quote-files",
                    "ref_field": "ref"
                  }
                }
              },
              "why_not_asked": {
                "anyOf": [
                  {
                    "type": "string",
                    "maxLength": 1000
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Why you didn't ask them through One",
                "description": "Not needed for Pollards.",
                "x-cis-field": {
                  "show_if": {
                    "first_supplier.have_quote": true
                  },
                  "widget": "textarea"
                }
              }
            },
            "additionalProperties": false,
            "type": "object",
            "title": "First supplier",
            "x-cis-field": {}
          },
          "second_supplier": {
            "properties": {
              "supplier_no": {
                "anyOf": [
                  {
                    "type": "string",
                    "maxLength": 64
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Supplier",
                "x-cis-field": {
                  "entity": "suppliers.supplier",
                  "widget": "entity"
                }
              },
              "have_quote": {
                "type": "boolean",
                "title": "We already have their quote",
                "default": false,
                "x-cis-field": {}
              },
              "cost": {
                "anyOf": [
                  {
                    "type": "integer",
                    "maximum": 100000000.0,
                    "minimum": 1.0
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Their delivered cost for the line",
                "x-cis-field": {
                  "required": true,
                  "show_if": {
                    "second_supplier.have_quote": true
                  },
                  "widget": "money"
                }
              },
              "their_reference": {
                "anyOf": [
                  {
                    "type": "string",
                    "maxLength": 200
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Their reference",
                "description": "Their number for the quote. For Pollards' own quote, the Tharstern estimate number.",
                "x-cis-field": {
                  "required": true,
                  "show_if": {
                    "second_supplier.have_quote": true
                  }
                }
              },
              "valid_until": {
                "anyOf": [
                  {
                    "type": "string",
                    "format": "date"
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Valid until",
                "x-cis-field": {
                  "required": true,
                  "show_if": {
                    "second_supplier.have_quote": true
                  }
                }
              },
              "quote_document": {
                "anyOf": [
                  {
                    "type": "string",
                    "maxLength": 64
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Their quote",
                "description": "Their quote as a file: their PDF, a screenshot or the email saved as a file. Not needed for Pollards: the estimate in Tharstern is the evidence.",
                "x-cis-field": {
                  "accept": [
                    "application/pdf",
                    "image/png",
                    "image/jpeg",
                    "message/rfc822",
                    "application/vnd.ms-outlook",
                    ".eml",
                    ".msg"
                  ],
                  "max_bytes": 26214400,
                  "show_if": {
                    "second_supplier.have_quote": true
                  },
                  "upload": {
                    "file_field": "file",
                    "path": "/supplier-quotes/their-quote-files",
                    "ref_field": "ref"
                  }
                }
              },
              "why_not_asked": {
                "anyOf": [
                  {
                    "type": "string",
                    "maxLength": 1000
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Why you didn't ask them through One",
                "description": "Not needed for Pollards.",
                "x-cis-field": {
                  "show_if": {
                    "second_supplier.have_quote": true
                  },
                  "widget": "textarea"
                }
              }
            },
            "additionalProperties": false,
            "type": "object",
            "title": "Second supplier",
            "x-cis-field": {}
          },
          "third_supplier": {
            "properties": {
              "supplier_no": {
                "anyOf": [
                  {
                    "type": "string",
                    "maxLength": 64
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Supplier",
                "x-cis-field": {
                  "entity": "suppliers.supplier",
                  "widget": "entity"
                }
              },
              "have_quote": {
                "type": "boolean",
                "title": "We already have their quote",
                "default": false,
                "x-cis-field": {}
              },
              "cost": {
                "anyOf": [
                  {
                    "type": "integer",
                    "maximum": 100000000.0,
                    "minimum": 1.0
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Their delivered cost for the line",
                "x-cis-field": {
                  "required": true,
                  "show_if": {
                    "third_supplier.have_quote": true
                  },
                  "widget": "money"
                }
              },
              "their_reference": {
                "anyOf": [
                  {
                    "type": "string",
                    "maxLength": 200
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Their reference",
                "description": "Their number for the quote. For Pollards' own quote, the Tharstern estimate number.",
                "x-cis-field": {
                  "required": true,
                  "show_if": {
                    "third_supplier.have_quote": true
                  }
                }
              },
              "valid_until": {
                "anyOf": [
                  {
                    "type": "string",
                    "format": "date"
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Valid until",
                "x-cis-field": {
                  "required": true,
                  "show_if": {
                    "third_supplier.have_quote": true
                  }
                }
              },
              "quote_document": {
                "anyOf": [
                  {
                    "type": "string",
                    "maxLength": 64
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Their quote",
                "description": "Their quote as a file: their PDF, a screenshot or the email saved as a file. Not needed for Pollards: the estimate in Tharstern is the evidence.",
                "x-cis-field": {
                  "accept": [
                    "application/pdf",
                    "image/png",
                    "image/jpeg",
                    "message/rfc822",
                    "application/vnd.ms-outlook",
                    ".eml",
                    ".msg"
                  ],
                  "max_bytes": 26214400,
                  "show_if": {
                    "third_supplier.have_quote": true
                  },
                  "upload": {
                    "file_field": "file",
                    "path": "/supplier-quotes/their-quote-files",
                    "ref_field": "ref"
                  }
                }
              },
              "why_not_asked": {
                "anyOf": [
                  {
                    "type": "string",
                    "maxLength": 1000
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Why you didn't ask them through One",
                "description": "Not needed for Pollards.",
                "x-cis-field": {
                  "show_if": {
                    "third_supplier.have_quote": true
                  },
                  "widget": "textarea"
                }
              }
            },
            "additionalProperties": false,
            "type": "object",
            "title": "Third supplier",
            "x-cis-field": {}
          },
          "reply_by": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reply by",
            "description": "Needed when any supplier is asked through One.",
            "x-cis-field": {
              "min_date": "today"
            }
          },
          "delivery": {
            "anyOf": [
              {
                "properties": {
                  "mode": {
                    "anyOf": [
                      {
                        "type": "string",
                        "enum": [
                          "ship",
                          "multi",
                          "collect",
                          "hold",
                          "tbc"
                        ]
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery mode",
                    "x-cis-field": {}
                  },
                  "delivery_address_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery address",
                    "x-cis-field": {}
                  },
                  "service_level": {
                    "anyOf": [
                      {
                        "type": "string",
                        "enum": [
                          "next_day",
                          "pre_noon",
                          "pre_1030",
                          "pallet",
                          "northern_ireland",
                          "collection"
                        ],
                        "title": "DeliveryServiceLevel",
                        "description": "A delivery service level (R87). ``collection`` is the customer collecting: never priced by a courier."
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery service",
                    "x-cis-field": {}
                  },
                  "courier_supplier_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Courier",
                    "x-cis-field": {}
                  },
                  "agreed_delivery_price_pence": {
                    "anyOf": [
                      {
                        "type": "integer",
                        "maximum": 100000000.0,
                        "minimum": 0.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Agreed delivery price",
                    "x-cis-field": {}
                  },
                  "agreed_delivery_reason": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 200
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Reason for the agreed price",
                    "x-cis-field": {}
                  },
                  "weight_kg": {
                    "anyOf": [
                      {
                        "type": "number",
                        "maximum": 10000.0,
                        "exclusiveMinimum": 0.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Weight (kg)",
                    "x-cis-field": {}
                  },
                  "parcels": {
                    "anyOf": [
                      {
                        "type": "integer",
                        "maximum": 500.0,
                        "minimum": 1.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Parcels",
                    "x-cis-field": {}
                  },
                  "collection_point_address_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Collection point",
                    "x-cis-field": {}
                  },
                  "recipient_name": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 100
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient name",
                    "x-cis-field": {}
                  },
                  "recipient_phone": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 40
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient phone",
                    "x-cis-field": {}
                  },
                  "recipient_email": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 254
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient email",
                    "x-cis-field": {}
                  },
                  "delivery_lines": {
                    "items": {
                      "properties": {
                        "delivery_address_id": {
                          "type": "string",
                          "maxLength": 64,
                          "minLength": 1,
                          "title": "Delivery address",
                          "x-cis-field": {}
                        },
                        "quantity": {
                          "type": "integer",
                          "maximum": 10000000.0,
                          "minimum": 1.0,
                          "title": "Quantity",
                          "x-cis-field": {}
                        },
                        "version_position": {
                          "anyOf": [
                            {
                              "type": "integer",
                              "maximum": 100.0,
                              "minimum": 1.0
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Version",
                          "x-cis-field": {}
                        },
                        "recipient_name": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 100
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient name",
                          "x-cis-field": {}
                        },
                        "recipient_phone": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 40
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient phone",
                          "x-cis-field": {}
                        },
                        "recipient_email": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 254
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient email",
                          "x-cis-field": {}
                        }
                      },
                      "additionalProperties": false,
                      "type": "object",
                      "required": [
                        "delivery_address_id",
                        "quantity"
                      ],
                      "title": "RequestedDeliveryLineSchema",
                      "description": "One row of a line split across addresses (mode ``multi``, order to invoice doc 106 §3.1): how many go to which\nof the customer's addresses, of which version on a line with several, and who receives them when that is not the\naddress's recipient."
                    },
                    "type": "array",
                    "maxItems": 20,
                    "title": "Delivery rows",
                    "x-cis-field": {}
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "title": "DeliverySelectionSchema",
                "description": "A line's delivery choice. A destination is a CIS address of the customer's (``delivery_address_id``), or, to\ncollect, one of our collection points. The level is the customer's or the CSR's; the courier, an agreed price and\nentered packages are staff-only (the portal's routes discard them)."
              },
              {
                "type": "null"
              }
            ],
            "title": "Delivery",
            "description": "Where the line's goods go.",
            "x-cis-field": {
              "custom_widget": "delivery_allocation",
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddBespokeLineToDraftQuoteRequest",
        "description": "*Add bespoke line…*: work our dictionary can't describe, asked of up to three suppliers or priced from a quote we\nalready have. ``request_id`` is required on the commit: a retry finishes the asking a failure left undone."
      }
    },
    {
      "id": "catalogue.reprice_draft_quote",
      "module": "catalogue_module",
      "label": "Reprice",
      "description": "Price the draft quote's catalogue lines again at today's prices; never a channel's price, a supplier's quote or a held price still holding.",
      "target": "quotes.quote",
      "destructive": false,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/quotes/{quote_no}/actions/reprice",
      "dry_run_path": "/catalogue/quotes/{quote_no}/actions/reprice/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "wait_seconds": {
            "anyOf": [
              {
                "type": "number",
                "maximum": 20.0,
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Wait for prices",
            "description": "How long to wait for every route's price, 0 to 20 seconds; omitted is the default. In-house prices take about 6 to 10 seconds, so pass 10 when every price is needed now. A route still pricing after it is named in the refusal, with when to ask again.",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RepriceDraftQuoteRequest",
        "description": "*Reprice*: nothing to say beyond the guard and how long to wait for prices."
      }
    },
    {
      "id": "catalogue.set_draft_quote_line_quantity_options",
      "module": "catalogue_module",
      "label": "Change quantities…",
      "description": "Set the quantity a draft quote line is for, or several for the customer to choose from; a catalogue line's are priced at today's price, a channel line's at the prices its team gives or types, and a line priced from supplier quotes keeps the chosen price of each quantity that stays, asks its suppliers for a quantity added and sets aside the prices of one taken off.",
      "target": "quotes.quote_line",
      "destructive": false,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/quotes/{quote_no}/lines/{line_ref}/actions/quantity-options",
      "dry_run_path": "/catalogue/quotes/{quote_no}/lines/{line_ref}/actions/quantity-options/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "version_split": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "even",
                  "entered"
                ],
                "title": "VersionSplit",
                "description": "How a multi-version line's quantity was shared out on the catalogue item page (R18-1)."
              },
              {
                "type": "null"
              }
            ],
            "title": "Split between versions",
            "description": "How a line of two or more versions (the item's Versions count above 1) shares its quantity: `even` (the default when there are several versions: each version gets the same number of the item's steps, the remainder one step each to the first versions; refused when the quantity cannot be split that way) or `entered` (with `version_quantities`). Each version is priced at its own quantity. Leave it out for one version.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "version_quantities": {
            "anyOf": [
              {
                "items": {
                  "type": "integer"
                },
                "type": "array",
                "maxItems": 26
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity of each version",
            "description": "With `version_split` `entered` only: one whole number of at least 1 per version, in order (A, B, C…), adding up to `quantity`. Entered quantities cannot be offered as other quantities.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "wait_seconds": {
            "anyOf": [
              {
                "type": "number",
                "maximum": 20.0,
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Wait for prices",
            "description": "How long to wait for every route's price, 0 to 20 seconds; omitted is the default. In-house prices take about 6 to 10 seconds, so pass 10 when every price is needed now. A route still pricing after it is named in the refusal, with when to ask again.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "quantities": {
            "anyOf": [
              {
                "items": {
                  "properties": {
                    "quantity": {
                      "type": "integer",
                      "maximum": 10000000.0,
                      "minimum": 1.0,
                      "title": "Quantity",
                      "description": "A quantity to offer, e.g. 1000.",
                      "x-cis-field": {}
                    },
                    "price_pence": {
                      "anyOf": [
                        {
                          "type": "integer",
                          "maximum": 100000000.0,
                          "minimum": 0.0
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "Price, ex VAT",
                      "description": "For a channel's line only. Leave the price empty for a catalogue line.",
                      "x-cis-field": {
                        "widget": "money"
                      }
                    },
                    "cost_pence": {
                      "anyOf": [
                        {
                          "type": "integer",
                          "maximum": 100000000.0,
                          "minimum": 0.0
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "Buy price",
                      "description": "For a channel's line only. Left empty, the buy price from the team or the price list will be kept.",
                      "x-cis-field": {
                        "widget": "money"
                      }
                    }
                  },
                  "additionalProperties": false,
                  "type": "object",
                  "required": [
                    "quantity"
                  ],
                  "title": "QuantityOfferRequest",
                  "description": "One quantity a line offers. The price and the buy price are typed for a channel's line only (the team's price);\nthe catalogue prices its own lines at today's price."
                },
                "type": "array",
                "maxItems": 20
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantities offered",
            "x-cis-field": {
              "add_rows": "Add a quantity",
              "required": true,
              "widget": "table"
            }
          },
          "speed_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "Speed",
            "description": "The chosen Speed's code; none, or `default`, keeps the line's own Speed.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "price_option": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100,
                "pattern": "^(default|[^:]+:[^:]+)$"
              },
              {
                "type": "null"
              }
            ],
            "title": "Price option",
            "description": "The chosen option's key, `route_code:tier`; none, or `default`, keeps the line's own route, which is the sourcing policy's default when the line names none.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "route_override_reason": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "earlier_date",
                  "quality_or_specialism",
                  "keep_in_house",
                  "known_problem_with_default",
                  "customer_asked",
                  "other"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Why not the default",
            "x-cis-field": {
              "hidden": true
            }
          },
          "route_override_note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Note on the reason",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "route_override_reason": [
                  "other"
                ]
              }
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetDraftQuoteLineQuantityOptionsRequest",
        "description": "*Change quantities…*: every quantity the line offers, one or several, in any order: the smallest becomes the\nline's own and the customer picks one as they accept. Left out, a dry run prefills what the line offers now. The dry\nrun's choice is the Speed, offered at the line's own (smallest) quantity: the one pick prices every quantity offered.\nThe line keeps the supplier it names (*Change supplier…* changes it) while that supplier can make every quantity\noffered, else the default is the supplier. The route fields are for the one case a route is still pricing: the dry\nrun offers the best route priced so far, and choosing it sets the three route fields, which price every quantity and\nwhich the line then keeps. A line priced from supplier quotes has no Speed or route: it keeps the chosen price of\neach quantity that stays, and its suppliers are asked for each quantity added. A catalogue line of several versions\nshares each quantity between them: evenly, or, for one quantity only, at the quantities entered\n(``version_split``, ``version_quantities``)."
      },
      "main": true
    },
    {
      "id": "catalogue.change_bespoke_specification",
      "module": "catalogue_module",
      "label": "Change specification…",
      "description": "Change what the suppliers are asked to make for a line priced from supplier quotes, or the files they are given; its prices are cleared and its suppliers asked again.",
      "target": "quotes.quote_line",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/quotes/{quote_no}/lines/{line_ref}/actions/change-specification",
      "dry_run_path": "/catalogue/quotes/{quote_no}/lines/{line_ref}/actions/change-specification/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "specification": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 8000
              },
              {
                "type": "null"
              }
            ],
            "title": "Specification for the supplier",
            "description": "Formatted text: - for bullets, 1. for numbered items, **bold** and *italic*. At most 4,000 characters.",
            "x-cis-field": {
              "required": true,
              "widget": "formatted_text"
            }
          },
          "supplier_files": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 10,
            "title": "Files for the supplier (leave empty to keep the current files)",
            "x-cis-field": {
              "max_bytes": 26214400,
              "upload": {
                "file_field": "file",
                "path": "/supplier-quotes/files",
                "ref_field": "ref"
              }
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ChangeBespokeSpecificationRequest",
        "description": "*Change specification…*: what the suppliers are asked to make, or the files they are given; they are asked\nagain."
      }
    },
    {
      "id": "catalogue.change_draft_quote_line_supplier",
      "module": "catalogue_module",
      "label": "Change supplier…",
      "description": "Change the supplier of a catalogue line on the draft quote, priced again at today's price with the delivery priced with it.",
      "target": "quotes.quote_line",
      "destructive": false,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/quotes/{quote_no}/lines/{line_ref}/actions/change-supplier",
      "dry_run_path": "/catalogue/quotes/{quote_no}/lines/{line_ref}/actions/change-supplier/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "wait_seconds": {
            "anyOf": [
              {
                "type": "number",
                "maximum": 20.0,
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Wait for prices",
            "description": "How long to wait for every route's price, 0 to 20 seconds; omitted is the default. In-house prices take about 6 to 10 seconds, so pass 10 when every price is needed now. A route still pricing after it is named in the refusal, with when to ask again.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "price_option": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100,
                "pattern": "^(default|[^:]+:[^:]+)$"
              },
              {
                "type": "null"
              }
            ],
            "title": "Price option",
            "description": "The chosen option's key, `route_code:tier`, or `default` for the sourcing policy's default, which the line then follows at every quantity. Required to commit.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "route_override_reason": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "earlier_date",
                  "quality_or_specialism",
                  "keep_in_house",
                  "known_problem_with_default",
                  "customer_asked",
                  "other"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Why not the default",
            "x-cis-field": {
              "hidden": true
            }
          },
          "route_override_note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Note on the reason",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "route_override_reason": [
                  "other"
                ]
              }
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ChangeDraftQuoteLineSupplierRequest",
        "description": "*Change supplier…*: the route that will make the line, priced at today's price at the line's own quantity. The\ndry run's choices are the price option and, for a route that is not the default, why not the default. The line\nkeeps the route and the reason; `default` leaves the line naming no route, so the default follows the quantity.\nWhile a route is still pricing, the dry run offers the best route priced so far: choosing it sets the three route\nfields, which the line then keeps."
      }
    },
    {
      "id": "catalogue.request_supplier_quotes_for_draft_quote_line",
      "module": "catalogue_module",
      "label": "Request supplier quotes…",
      "description": "Request quotes from suppliers for a draft quote's line priced from supplier quotes, as the line stands now.",
      "target": "quotes.quote_line",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/quotes/{quote_no}/lines/{line_ref}/actions/request-supplier-quotes",
      "dry_run_path": "/catalogue/quotes/{quote_no}/lines/{line_ref}/actions/request-supplier-quotes/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "supplier_no": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Supplier to ask",
            "x-cis-field": {
              "entity": "suppliers.supplier",
              "required": true,
              "widget": "entity"
            }
          },
          "reply_by": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reply by",
            "x-cis-field": {
              "min_date": "today",
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RequestSupplierQuotesForDraftQuoteLineRequest",
        "description": "*Request supplier quotes…*: ask one supplier to quote for the line as it stands."
      }
    },
    {
      "id": "catalogue.add_supplier_quote_you_have",
      "module": "catalogue_module",
      "label": "Add a quote you already have…",
      "description": "Record a supplier's quote got outside One for a line priced from supplier quotes, and price the quantities it prices from it.",
      "target": "quotes.quote_line",
      "destructive": false,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/quotes/{quote_no}/lines/{line_ref}/actions/add-quote-you-have",
      "dry_run_path": "/catalogue/quotes/{quote_no}/lines/{line_ref}/actions/add-quote-you-have/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "supplier_no": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Supplier",
            "x-cis-field": {
              "entity": "suppliers.supplier",
              "required": true,
              "widget": "entity"
            }
          },
          "prices": {
            "items": {
              "properties": {
                "quantity": {
                  "anyOf": [
                    {
                      "type": "integer",
                      "exclusiveMinimum": 0.0
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Quantity",
                  "readOnly": true,
                  "x-cis-field": {
                    "widget": "number"
                  }
                },
                "cost": {
                  "anyOf": [
                    {
                      "type": "integer",
                      "maximum": 100000000.0,
                      "minimum": 1.0
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Their delivered cost",
                  "x-cis-field": {
                    "widget": "money"
                  }
                },
                "not_quoted": {
                  "type": "boolean",
                  "title": "Can't quote",
                  "default": false,
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "title": "PriceRow",
              "description": "One quantity's answer: their delivered cost, or that they can't quote it."
            },
            "type": "array",
            "maxItems": 10,
            "title": "Their prices",
            "description": "A cost for each quantity, or tick Can't quote. The dry run fills one row per quantity.",
            "x-cis-field": {
              "required": true,
              "widget": "table"
            }
          },
          "their_reference": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Their reference",
            "x-cis-field": {
              "required": true
            }
          },
          "valid_until": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Valid until",
            "x-cis-field": {
              "required": true
            }
          },
          "quote_document": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Their quote",
            "description": "Their quote as a file: their PDF, a screenshot or the email saved as a file. Required unless the supplier is Pollards.",
            "x-cis-field": {
              "accept": [
                "application/pdf",
                "image/png",
                "image/jpeg",
                "message/rfc822",
                "application/vnd.ms-outlook",
                ".eml",
                ".msg"
              ],
              "max_bytes": 26214400,
              "upload": {
                "file_field": "file",
                "path": "/supplier-quotes/their-quote-files",
                "ref_field": "ref"
              }
            }
          },
          "why_not_asked": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 1000
              },
              {
                "type": "null"
              }
            ],
            "title": "Why you didn't ask them through One",
            "description": "Required unless the supplier is Pollards.",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddSupplierQuoteYouHaveRequest",
        "description": "*Add a quote you already have…*: only for a quote got another way; suppliers normally answer in our portal."
      }
    },
    {
      "id": "catalogue.set_draft_quote_line_optional",
      "module": "catalogue_module",
      "label": "Optional extra…",
      "description": "Tick a draft quote line as an optional extra the customer chooses on the quote, or untick it.",
      "target": "quotes.quote_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/quotes/{quote_no}/lines/{line_ref}/actions/optional",
      "dry_run_path": "/catalogue/quotes/{quote_no}/lines/{line_ref}/actions/optional/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "is_optional": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "An optional extra the customer ticks",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetDraftQuoteLineOptionalRequest",
        "description": "*Optional extra…*: ticked, the customer chooses the line on the quote."
      },
      "edits": {
        "fields": [
          "is_optional"
        ]
      }
    },
    {
      "id": "catalogue.remove_draft_quote_line",
      "module": "catalogue_module",
      "label": "Remove line",
      "description": "Remove a line, and the delivery priced with it, from the draft quote; the other lines keep their references until the draft quote is published. A line priced from supplier quotes withdraws them.",
      "target": "quotes.quote_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/quotes/{quote_no}/lines/{line_ref}/actions/remove",
      "dry_run_path": "/catalogue/quotes/{quote_no}/lines/{line_ref}/actions/remove/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RemoveDraftQuoteLineRequest",
        "description": "*Remove line*: nothing to say beyond the guard."
      }
    },
    {
      "id": "catalogue.use_supplier_price",
      "module": "catalogue_module",
      "label": "Use this price",
      "description": "Price one quantity of a draft quote's line from this supplier's price, replacing any other chosen for it; suppliers still asked once every quantity is priced are told the quote is no longer needed.",
      "target": "supplier_quotes.supplier_price",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/supplier-prices/{id}/actions/use-this-price",
      "dry_run_path": "/catalogue/supplier-prices/{id}/actions/use-this-price/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "UseSupplierPriceRequest",
        "description": "*Use this price* takes only its idempotency key and the guard: the supplier price is in the path."
      }
    },
    {
      "id": "catalogue.set_customer_catalogues",
      "module": "catalogue_module",
      "label": "Set catalogues…",
      "description": "Set which catalogues a customer sees; with nothing allocated the customer sees the core range.",
      "target": "customers.customer",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/customers/{customer_code}/actions/set-catalogues",
      "dry_run_path": "/catalogue/customers/{customer_code}/actions/set-catalogues/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "catalogue_ids": {
            "anyOf": [
              {
                "items": {
                  "type": "string"
                },
                "type": "array"
              },
              {
                "type": "null"
              }
            ],
            "title": "Catalogues",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetCustomerCataloguesRequest",
        "description": "*Set catalogues…*: the whole of the customer's allocation. Absent on a first dry run, which prefills today's."
      },
      "edits": {
        "fields": [
          "catalogues"
        ]
      }
    },
    {
      "id": "catalogue.show_customer_core_range",
      "module": "catalogue_module",
      "label": "Show core range",
      "description": "Let a customer see the core range beside the catalogues allocated to it. Hidden when the customer already sees it.",
      "target": "customers.customer",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/customers/{customer_code}/actions/show-core-range",
      "dry_run_path": "/catalogue/customers/{customer_code}/actions/show-core-range/dry-run",
      "content_type": "json",
      "request_schema": null,
      "edits": {
        "fields": [
          "show_core_range"
        ],
        "value": true
      }
    },
    {
      "id": "catalogue.hide_customer_core_range",
      "module": "catalogue_module",
      "label": "Hide core range",
      "description": "Stop a customer seeing the core range, so it sees only the catalogues allocated to it. Blocked while it has no other catalogue; hidden when it does not see the core range.",
      "target": "customers.customer",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/customers/{customer_code}/actions/hide-core-range",
      "dry_run_path": "/catalogue/customers/{customer_code}/actions/hide-core-range/dry-run",
      "content_type": "json",
      "request_schema": null,
      "edits": {
        "fields": [
          "show_core_range"
        ],
        "value": false
      }
    },
    {
      "id": "catalogue.add_tharstern_order_to_draft_quote",
      "module": "catalogue_module",
      "label": "Add to draft quote…",
      "description": "Add the jobs of a Tharstern order to a draft quote of the customer's, or to a new draft quote, priced today.",
      "target": "orders.tharstern_order",
      "destructive": false,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/tharstern-orders/{tharstern_ref}/actions/add-to-draft-quote",
      "dry_run_path": "/catalogue/tharstern-orders/{tharstern_ref}/actions/add-to-draft-quote/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "job_nos": {
            "anyOf": [
              {
                "items": {
                  "type": "string"
                },
                "type": "array",
                "maxItems": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Tharstern jobs to repeat",
            "description": "The Tharstern job numbers to repeat; left out, every job the Tharstern order names.",
            "x-cis-field": {}
          },
          "archived_artwork_refs": {
            "anyOf": [
              {
                "items": {
                  "type": "string"
                },
                "type": "array",
                "maxItems": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Use archived artwork",
            "description": "The Tharstern job numbers (the estimate number, for an estimate) whose archived file becomes the artwork of the line. The dry run lists the work that can use one; left out, no archived file is used.",
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "quote_no": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft quote",
            "description": "One of your open draft quotes for the customer; left empty, a new draft quote for the customer.",
            "x-cis-field": {
              "entity": "quotes.quote"
            }
          },
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddTharsternOrderToDraftQuoteRequest",
        "description": "*Add to draft quote…* on a Tharstern order. ``expected_draft_version`` is the dry run's guard."
      }
    },
    {
      "id": "catalogue.quote_tharstern_order_now",
      "module": "catalogue_module",
      "label": "Quote now…",
      "description": "Make a new draft quote with the jobs of a Tharstern order, then publish it and email the customer's contact the link.",
      "target": "orders.tharstern_order",
      "destructive": true,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/tharstern-orders/{tharstern_ref}/actions/quote-now",
      "dry_run_path": "/catalogue/tharstern-orders/{tharstern_ref}/actions/quote-now/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "job_nos": {
            "anyOf": [
              {
                "items": {
                  "type": "string"
                },
                "type": "array",
                "maxItems": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Tharstern jobs to repeat",
            "description": "The Tharstern job numbers to repeat; left out, every job the Tharstern order names.",
            "x-cis-field": {}
          },
          "archived_artwork_refs": {
            "anyOf": [
              {
                "items": {
                  "type": "string"
                },
                "type": "array",
                "maxItems": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Use archived artwork",
            "description": "The Tharstern job numbers (the estimate number, for an estimate) whose archived file becomes the artwork of the line. The dry run lists the work that can use one; left out, no archived file is used.",
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "expected_total_pence": {
            "anyOf": [
              {
                "type": "integer",
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Total at preview",
            "description": "The total, ex VAT, the dry run showed (the lines and the deliveries that have a price): a price that moved since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "QuoteTharsternOrderNowRequest",
        "description": "*Quote now…* on a Tharstern order: a new draft quote, published and shared at once."
      }
    },
    {
      "id": "catalogue.order_tharstern_order_now",
      "module": "catalogue_module",
      "label": "Order now…",
      "description": "Make a new draft quote with the jobs of a Tharstern order, then place it as the customer's order at once, paid as the payer allows.",
      "target": "orders.tharstern_order",
      "destructive": true,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/tharstern-orders/{tharstern_ref}/actions/order-now",
      "dry_run_path": "/catalogue/tharstern-orders/{tharstern_ref}/actions/order-now/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "payment_method": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "card",
                  "proforma",
                  "credit_account"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Payment method",
            "x-cis-field": {
              "required": true
            }
          },
          "customer_order_number": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 50
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer PO number",
            "x-cis-field": {}
          },
          "review_before_release": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "Review before release",
            "description": "Review before release for this order. Left empty, the order will follow the customer's setting.",
            "x-cis-field": {}
          },
          "expected_total_pence": {
            "anyOf": [
              {
                "type": "integer",
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Total at preview",
            "description": "The total, ex VAT, the dry run showed (the lines and the deliveries that have a price): a price that moved since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "job_nos": {
            "anyOf": [
              {
                "items": {
                  "type": "string"
                },
                "type": "array",
                "maxItems": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Tharstern jobs to repeat",
            "description": "The Tharstern job numbers to repeat; left out, every job the Tharstern order names.",
            "x-cis-field": {}
          },
          "archived_artwork_refs": {
            "anyOf": [
              {
                "items": {
                  "type": "string"
                },
                "type": "array",
                "maxItems": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Use archived artwork",
            "description": "The Tharstern job numbers (the estimate number, for an estimate) whose archived file becomes the artwork of the line. The dry run lists the work that can use one; left out, no archived file is used.",
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "OrderTharsternOrderNowRequest",
        "description": "*Order now…* on a Tharstern order: a new draft quote, placed as an order at once."
      }
    },
    {
      "id": "catalogue.add_tharstern_estimate_to_draft_quote",
      "module": "catalogue_module",
      "label": "Add to draft quote…",
      "description": "Add the work of a Tharstern estimate to a draft quote of the customer's, or to a new draft quote, priced today.",
      "target": "quotes.tharstern_estimate",
      "destructive": false,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/tharstern-estimates/{tharstern_ref}/actions/add-to-draft-quote",
      "dry_run_path": "/catalogue/tharstern-estimates/{tharstern_ref}/actions/add-to-draft-quote/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "archived_artwork_refs": {
            "anyOf": [
              {
                "items": {
                  "type": "string"
                },
                "type": "array",
                "maxItems": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Use archived artwork",
            "description": "The Tharstern job numbers (the estimate number, for an estimate) whose archived file becomes the artwork of the line. The dry run lists the work that can use one; left out, no archived file is used.",
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "quote_no": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft quote",
            "description": "One of your open draft quotes for the customer; left empty, a new draft quote for the customer.",
            "x-cis-field": {
              "entity": "quotes.quote"
            }
          },
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddTharsternEstimateToDraftQuoteRequest",
        "description": "*Add to draft quote…* on a Tharstern estimate. ``expected_draft_version`` is the dry run's guard."
      }
    },
    {
      "id": "catalogue.quote_tharstern_estimate_now",
      "module": "catalogue_module",
      "label": "Quote now…",
      "description": "Make a new draft quote with the work of a Tharstern estimate, then publish it and email the customer's contact the link.",
      "target": "quotes.tharstern_estimate",
      "destructive": true,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/tharstern-estimates/{tharstern_ref}/actions/quote-now",
      "dry_run_path": "/catalogue/tharstern-estimates/{tharstern_ref}/actions/quote-now/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "archived_artwork_refs": {
            "anyOf": [
              {
                "items": {
                  "type": "string"
                },
                "type": "array",
                "maxItems": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Use archived artwork",
            "description": "The Tharstern job numbers (the estimate number, for an estimate) whose archived file becomes the artwork of the line. The dry run lists the work that can use one; left out, no archived file is used.",
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "expected_total_pence": {
            "anyOf": [
              {
                "type": "integer",
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Total at preview",
            "description": "The total, ex VAT, the dry run showed (the lines and the deliveries that have a price): a price that moved since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "QuoteTharsternEstimateNowRequest",
        "description": "*Quote now…* on a Tharstern estimate: a new draft quote, published and shared at once."
      }
    },
    {
      "id": "catalogue.order_tharstern_estimate_now",
      "module": "catalogue_module",
      "label": "Order now…",
      "description": "Make a new draft quote with the work of a Tharstern estimate, then place it as the customer's order at once, paid as the payer allows.",
      "target": "quotes.tharstern_estimate",
      "destructive": true,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/tharstern-estimates/{tharstern_ref}/actions/order-now",
      "dry_run_path": "/catalogue/tharstern-estimates/{tharstern_ref}/actions/order-now/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "payment_method": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "card",
                  "proforma",
                  "credit_account"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Payment method",
            "x-cis-field": {
              "required": true
            }
          },
          "customer_order_number": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 50
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer PO number",
            "x-cis-field": {}
          },
          "review_before_release": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "Review before release",
            "description": "Review before release for this order. Left empty, the order will follow the customer's setting.",
            "x-cis-field": {}
          },
          "expected_total_pence": {
            "anyOf": [
              {
                "type": "integer",
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Total at preview",
            "description": "The total, ex VAT, the dry run showed (the lines and the deliveries that have a price): a price that moved since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "archived_artwork_refs": {
            "anyOf": [
              {
                "items": {
                  "type": "string"
                },
                "type": "array",
                "maxItems": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Use archived artwork",
            "description": "The Tharstern job numbers (the estimate number, for an estimate) whose archived file becomes the artwork of the line. The dry run lists the work that can use one; left out, no archived file is used.",
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "OrderTharsternEstimateNowRequest",
        "description": "*Order now…* on a Tharstern estimate: a new draft quote, placed as an order at once."
      }
    },
    {
      "id": "catalogue.add_catalogue_item_to_order",
      "module": "catalogue_module",
      "label": "Add catalogue item…",
      "description": "Configure and price a catalogue item for the order's customer at today's price and add it, with its delivery, to the order as a new line; needs the tick that the customer agreed, and emails the customer the updated order.",
      "target": "orders.order",
      "destructive": true,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/orders/{order_no}/actions/add-catalogue-item",
      "dry_run_path": "/catalogue/orders/{order_no}/actions/add-catalogue-item/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "version_split": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "even",
                  "entered"
                ],
                "title": "VersionSplit",
                "description": "How a multi-version line's quantity was shared out on the catalogue item page (R18-1)."
              },
              {
                "type": "null"
              }
            ],
            "title": "Split between versions",
            "description": "How a line of two or more versions (the item's Versions count above 1) shares its quantity: `even` (the default when there are several versions: each version gets the same number of the item's steps, the remainder one step each to the first versions; refused when the quantity cannot be split that way) or `entered` (with `version_quantities`). Each version is priced at its own quantity. Leave it out for one version.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "version_quantities": {
            "anyOf": [
              {
                "items": {
                  "type": "integer"
                },
                "type": "array",
                "maxItems": 26
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity of each version",
            "description": "With `version_split` `entered` only: one whole number of at least 1 per version, in order (A, B, C…), adding up to `quantity`. Entered quantities cannot be offered as other quantities.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "expected_total_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Order total at preview",
            "description": "The order's new total, ex VAT, the dry run showed: an order that would total otherwise is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "confirmed": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 10,
            "title": "Confirmed",
            "description": "The dry run's confirmations ticked, by key: `customer_agreed` (the customer agreed to the change)."
          },
          "wait_seconds": {
            "anyOf": [
              {
                "type": "number",
                "maximum": 20.0,
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Wait for prices",
            "description": "How long to wait for every route's price, 0 to 20 seconds; omitted is the default. In-house prices take about 6 to 10 seconds, so pass 10 when every price is needed now. A route still pricing after it is named in the refusal, with when to ask again.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "item_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Catalogue item",
            "description": "The catalogue item to add, from the order's customer's catalogues.",
            "x-cis-field": {
              "entity": "catalogue.catalogue_item",
              "required": true
            }
          },
          "configuration": {
            "anyOf": [
              {
                "additionalProperties": true,
                "type": "object"
              },
              {
                "type": "null"
              }
            ],
            "title": "Specification",
            "description": "Each field's chosen option or custom value. The item's default is filled in to start.",
            "x-cis-field": {
              "custom_widget": "item_configuration"
            }
          },
          "quantity": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 10000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity",
            "x-cis-field": {}
          },
          "speed_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "Speed",
            "description": "The chosen Speed's code; none, or `default`, takes the default Speed.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "delivery": {
            "anyOf": [
              {
                "properties": {
                  "mode": {
                    "anyOf": [
                      {
                        "type": "string",
                        "enum": [
                          "ship",
                          "multi",
                          "collect",
                          "hold",
                          "tbc"
                        ]
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery mode",
                    "x-cis-field": {}
                  },
                  "delivery_address_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery address",
                    "x-cis-field": {}
                  },
                  "service_level": {
                    "anyOf": [
                      {
                        "type": "string",
                        "enum": [
                          "next_day",
                          "pre_noon",
                          "pre_1030",
                          "pallet",
                          "northern_ireland",
                          "collection"
                        ],
                        "title": "DeliveryServiceLevel",
                        "description": "A delivery service level (R87). ``collection`` is the customer collecting: never priced by a courier."
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery service",
                    "x-cis-field": {}
                  },
                  "courier_supplier_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Courier",
                    "x-cis-field": {}
                  },
                  "agreed_delivery_price_pence": {
                    "anyOf": [
                      {
                        "type": "integer",
                        "maximum": 100000000.0,
                        "minimum": 0.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Agreed delivery price",
                    "x-cis-field": {}
                  },
                  "agreed_delivery_reason": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 200
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Reason for the agreed price",
                    "x-cis-field": {}
                  },
                  "weight_kg": {
                    "anyOf": [
                      {
                        "type": "number",
                        "maximum": 10000.0,
                        "exclusiveMinimum": 0.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Weight (kg)",
                    "x-cis-field": {}
                  },
                  "parcels": {
                    "anyOf": [
                      {
                        "type": "integer",
                        "maximum": 500.0,
                        "minimum": 1.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Parcels",
                    "x-cis-field": {}
                  },
                  "collection_point_address_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Collection point",
                    "x-cis-field": {}
                  },
                  "recipient_name": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 100
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient name",
                    "x-cis-field": {}
                  },
                  "recipient_phone": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 40
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient phone",
                    "x-cis-field": {}
                  },
                  "recipient_email": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 254
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient email",
                    "x-cis-field": {}
                  },
                  "delivery_lines": {
                    "items": {
                      "properties": {
                        "delivery_address_id": {
                          "type": "string",
                          "maxLength": 64,
                          "minLength": 1,
                          "title": "Delivery address",
                          "x-cis-field": {}
                        },
                        "quantity": {
                          "type": "integer",
                          "maximum": 10000000.0,
                          "minimum": 1.0,
                          "title": "Quantity",
                          "x-cis-field": {}
                        },
                        "version_position": {
                          "anyOf": [
                            {
                              "type": "integer",
                              "maximum": 100.0,
                              "minimum": 1.0
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Version",
                          "x-cis-field": {}
                        },
                        "recipient_name": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 100
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient name",
                          "x-cis-field": {}
                        },
                        "recipient_phone": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 40
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient phone",
                          "x-cis-field": {}
                        },
                        "recipient_email": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 254
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient email",
                          "x-cis-field": {}
                        }
                      },
                      "additionalProperties": false,
                      "type": "object",
                      "required": [
                        "delivery_address_id",
                        "quantity"
                      ],
                      "title": "RequestedDeliveryLineSchema",
                      "description": "One row of a line split across addresses (mode ``multi``, order to invoice doc 106 §3.1): how many go to which\nof the customer's addresses, of which version on a line with several, and who receives them when that is not the\naddress's recipient."
                    },
                    "type": "array",
                    "maxItems": 20,
                    "title": "Delivery rows",
                    "x-cis-field": {}
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "title": "DeliverySelectionSchema",
                "description": "A line's delivery choice. A destination is a CIS address of the customer's (``delivery_address_id``), or, to\ncollect, one of our collection points. The level is the customer's or the CSR's; the courier, an agreed price and\nentered packages are staff-only (the portal's routes discard them)."
              },
              {
                "type": "null"
              }
            ],
            "title": "Delivery",
            "description": "Where the line's goods go.",
            "x-cis-field": {
              "custom_widget": "delivery_allocation",
              "required": true
            }
          },
          "price_option": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100,
                "pattern": "^(default|[^:]+:[^:]+)$"
              },
              {
                "type": "null"
              }
            ],
            "title": "Price option",
            "description": "The chosen option's key, `route_code:tier`; none, or `default`, takes the sourcing policy's default.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "route_override_reason": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "earlier_date",
                  "quality_or_specialism",
                  "keep_in_house",
                  "known_problem_with_default",
                  "customer_asked",
                  "other"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Why not the default",
            "x-cis-field": {
              "hidden": true
            }
          },
          "route_override_note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Note on the reason",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "route_override_reason": [
                  "other"
                ]
              }
            }
          },
          "needed_by": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Needed by",
            "x-cis-field": {
              "hidden": true
            }
          },
          "needed_by_is_hard": {
            "type": "boolean",
            "title": "Needed by is a fixed date",
            "default": false,
            "x-cis-field": {
              "hidden": true
            }
          },
          "price_level": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "contracted",
                  "last_ordered",
                  "similar_ordered",
                  "last_quoted",
                  "policy",
                  "custom"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Price",
            "x-cis-field": {
              "hidden": true
            }
          },
          "suggested_price_level": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "contracted",
                  "last_ordered",
                  "similar_ordered",
                  "last_quoted",
                  "policy",
                  "custom"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Suggested price level",
            "x-cis-field": {
              "hidden": true
            }
          },
          "custom_price_pence": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 100000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Custom price",
            "description": "The line's price ex VAT at the custom level.",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "price_level": "custom"
              },
              "widget": "money"
            }
          },
          "price_adjustment_reason": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "contracted_rate",
                  "held_at_last_price",
                  "matched_last_quote",
                  "commercial_discretion",
                  "customer_agreement"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Why the custom price",
            "x-cis-field": {
              "hidden": true
            }
          },
          "add_to_customer_catalogue": {
            "type": "string",
            "enum": [
              "no",
              "ordinary",
              "repeatable"
            ],
            "title": "Add to the customer's catalogue",
            "description": "Nothing is added now: One adds the item to the customer's own catalogue when the order line is released. A repeatable item also waits for the order line's artwork to be final. Needs permission to add to a customer's catalogue.",
            "default": "no",
            "x-cis-field": {
              "enum_labels": {
                "no": "No",
                "ordinary": "As an ordinary item, once the order line is released",
                "repeatable": "As a repeatable item, once the order line is released and its artwork is final"
              }
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddCatalogueItemToOrderRequest",
        "description": "*Add catalogue item…*: the item configured and priced for the order's customer. One draws the item with its picker,\n``configuration`` and ``quantity`` with the ``item_configuration`` widget and ``delivery`` with\n``delivery_allocation``. The speed, the price option and its reason, and the price level and its reason are the dry\nrun's choices: their keys are hidden, never drawn as fields. The customer is the order's, and the contact its contact.\n``request_id`` is the new line's: a repeat adds nothing more."
      }
    },
    {
      "id": "catalogue.add_bespoke_line_to_order",
      "module": "catalogue_module",
      "label": "Add bespoke line…",
      "description": "Add a line for work the dictionary can't describe to the order, priced now from the supplier's quote staff already hold, and record that quote for it; needs the tick that the customer agreed, and emails the customer the updated order.",
      "target": "orders.order",
      "destructive": true,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/orders/{order_no}/actions/add-bespoke-line",
      "dry_run_path": "/catalogue/orders/{order_no}/actions/add-bespoke-line/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "expected_total_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Order total at preview",
            "description": "The order's new total, ex VAT, the dry run showed: an order that would total otherwise is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "confirmed": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 10,
            "title": "Confirmed",
            "description": "The dry run's confirmations ticked, by key: `customer_agreed` (the customer agreed to the change)."
          },
          "title": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 240
              },
              {
                "type": "null"
              }
            ],
            "title": "Title the customer reads",
            "x-cis-field": {
              "required": true
            }
          },
          "specification": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 8000
              },
              {
                "type": "null"
              }
            ],
            "title": "Specification",
            "description": "What the supplier quoted for: it becomes the supplier quote's specification.",
            "x-cis-field": {
              "required": true,
              "widget": "formatted_text"
            }
          },
          "quantity": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 10000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity",
            "x-cis-field": {
              "required": true
            }
          },
          "vat_rate_pct": {
            "anyOf": [
              {
                "type": "integer",
                "enum": [
                  0,
                  5,
                  20
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "VAT rate",
            "x-cis-field": {
              "enum_labels": {
                "0": "0%",
                "20": "20%",
                "5": "5%"
              },
              "required": true
            }
          },
          "supplier_quote": {
            "properties": {
              "supplier_no": {
                "anyOf": [
                  {
                    "type": "string",
                    "maxLength": 64
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Supplier",
                "x-cis-field": {
                  "entity": "suppliers.supplier",
                  "required": true,
                  "widget": "entity"
                }
              },
              "cost": {
                "anyOf": [
                  {
                    "type": "integer",
                    "maximum": 100000000.0,
                    "minimum": 1.0
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Their delivered cost for the line",
                "description": "Their delivered cost for the whole quantity, ex VAT.",
                "x-cis-field": {
                  "required": true,
                  "widget": "money"
                }
              },
              "their_reference": {
                "anyOf": [
                  {
                    "type": "string",
                    "maxLength": 200
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Their reference",
                "description": "Their number for the quote. For Pollards' own quote, the Tharstern estimate number.",
                "x-cis-field": {
                  "required": true
                }
              },
              "valid_until": {
                "anyOf": [
                  {
                    "type": "string",
                    "format": "date"
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Valid until",
                "x-cis-field": {
                  "min_date": "today",
                  "required": true
                }
              },
              "quote_document": {
                "anyOf": [
                  {
                    "type": "string",
                    "maxLength": 64
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Their quote",
                "description": "Their quote as a file: their PDF, a screenshot or the email saved as a file. Not needed for Pollards: the estimate in Tharstern is the evidence.",
                "x-cis-field": {
                  "accept": [
                    "application/pdf",
                    "image/png",
                    "image/jpeg",
                    "message/rfc822",
                    "application/vnd.ms-outlook",
                    ".eml",
                    ".msg"
                  ],
                  "max_bytes": 26214400,
                  "upload": {
                    "file_field": "file",
                    "path": "/supplier-quotes/their-quote-files",
                    "ref_field": "ref"
                  }
                }
              },
              "reason": {
                "anyOf": [
                  {
                    "type": "string",
                    "maxLength": 600
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Why the dictionary can't describe it",
                "description": "It goes to the dictionary gap log.",
                "x-cis-field": {
                  "required": true,
                  "widget": "textarea"
                }
              }
            },
            "additionalProperties": false,
            "type": "object",
            "title": "Their quote",
            "description": "The supplier's quote staff already hold for the line: its delivered cost (the order line has one quantity),\ntheir reference, until when it holds, their PDF and why the dictionary can't describe the work.",
            "x-cis-field": {}
          },
          "delivery": {
            "anyOf": [
              {
                "properties": {
                  "mode": {
                    "anyOf": [
                      {
                        "type": "string",
                        "enum": [
                          "ship",
                          "multi",
                          "collect",
                          "hold",
                          "tbc"
                        ]
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery mode",
                    "x-cis-field": {}
                  },
                  "delivery_address_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery address",
                    "x-cis-field": {}
                  },
                  "service_level": {
                    "anyOf": [
                      {
                        "type": "string",
                        "enum": [
                          "next_day",
                          "pre_noon",
                          "pre_1030",
                          "pallet",
                          "northern_ireland",
                          "collection"
                        ],
                        "title": "DeliveryServiceLevel",
                        "description": "A delivery service level (R87). ``collection`` is the customer collecting: never priced by a courier."
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery service",
                    "x-cis-field": {}
                  },
                  "courier_supplier_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Courier",
                    "x-cis-field": {}
                  },
                  "agreed_delivery_price_pence": {
                    "anyOf": [
                      {
                        "type": "integer",
                        "maximum": 100000000.0,
                        "minimum": 0.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Agreed delivery price",
                    "x-cis-field": {}
                  },
                  "agreed_delivery_reason": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 200
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Reason for the agreed price",
                    "x-cis-field": {}
                  },
                  "weight_kg": {
                    "anyOf": [
                      {
                        "type": "number",
                        "maximum": 10000.0,
                        "exclusiveMinimum": 0.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Weight (kg)",
                    "x-cis-field": {}
                  },
                  "parcels": {
                    "anyOf": [
                      {
                        "type": "integer",
                        "maximum": 500.0,
                        "minimum": 1.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Parcels",
                    "x-cis-field": {}
                  },
                  "collection_point_address_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Collection point",
                    "x-cis-field": {}
                  },
                  "recipient_name": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 100
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient name",
                    "x-cis-field": {}
                  },
                  "recipient_phone": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 40
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient phone",
                    "x-cis-field": {}
                  },
                  "recipient_email": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 254
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient email",
                    "x-cis-field": {}
                  },
                  "delivery_lines": {
                    "items": {
                      "properties": {
                        "delivery_address_id": {
                          "type": "string",
                          "maxLength": 64,
                          "minLength": 1,
                          "title": "Delivery address",
                          "x-cis-field": {}
                        },
                        "quantity": {
                          "type": "integer",
                          "maximum": 10000000.0,
                          "minimum": 1.0,
                          "title": "Quantity",
                          "x-cis-field": {}
                        },
                        "version_position": {
                          "anyOf": [
                            {
                              "type": "integer",
                              "maximum": 100.0,
                              "minimum": 1.0
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Version",
                          "x-cis-field": {}
                        },
                        "recipient_name": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 100
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient name",
                          "x-cis-field": {}
                        },
                        "recipient_phone": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 40
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient phone",
                          "x-cis-field": {}
                        },
                        "recipient_email": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 254
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient email",
                          "x-cis-field": {}
                        }
                      },
                      "additionalProperties": false,
                      "type": "object",
                      "required": [
                        "delivery_address_id",
                        "quantity"
                      ],
                      "title": "RequestedDeliveryLineSchema",
                      "description": "One row of a line split across addresses (mode ``multi``, order to invoice doc 106 §3.1): how many go to which\nof the customer's addresses, of which version on a line with several, and who receives them when that is not the\naddress's recipient."
                    },
                    "type": "array",
                    "maxItems": 20,
                    "title": "Delivery rows",
                    "x-cis-field": {}
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "title": "DeliverySelectionSchema",
                "description": "A line's delivery choice. A destination is a CIS address of the customer's (``delivery_address_id``), or, to\ncollect, one of our collection points. The level is the customer's or the CSR's; the courier, an agreed price and\nentered packages are staff-only (the portal's routes discard them)."
              },
              {
                "type": "null"
              }
            ],
            "title": "Delivery",
            "description": "Where the line's goods go.",
            "x-cis-field": {
              "custom_widget": "delivery_allocation",
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddBespokeLineToOrderRequest",
        "description": "*Add bespoke line…* on an order: work the dictionary can't describe, priced now from the supplier's quote staff\nalready hold (\"We already have their quote\" only, ruling 32). Money is in pence."
      }
    },
    {
      "id": "catalogue.add_service_extra_to_order",
      "module": "catalogue_module",
      "label": "Add extra…",
      "description": "Add a service the customer agreed to an order in progress, named from the dictionary and priced by hand.",
      "target": "orders.order",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/orders/{order_no}/actions/add-extra",
      "dry_run_path": "/catalogue/orders/{order_no}/actions/add-extra/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "service_kind": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "mailing",
                  "postage",
                  "carriage",
                  "fulfilment",
                  "storage",
                  "design",
                  "installation",
                  "decoration"
                ],
                "title": "ServiceKind"
              },
              {
                "type": "null"
              }
            ],
            "title": "Service",
            "description": "A kind Add extra offers, from the dry run's choice; storage is refused.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "activities": {
            "items": {
              "type": "string",
              "pattern": "^[a-z0-9_]{1,40}$"
            },
            "type": "array",
            "maxItems": 20,
            "title": "What is done",
            "description": "Activity codes of the kind, e.g. ['enclose']; kitting is refused with a 400.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "parameters": {
            "patternProperties": {
              "^[a-z0-9_]{1,40}$": {
                "anyOf": [
                  {
                    "type": "string",
                    "pattern": "^[a-z0-9_]{1,40}$"
                  },
                  {
                    "type": "integer",
                    "maximum": 1000000.0,
                    "minimum": 0.0
                  }
                ]
              }
            },
            "type": "object",
            "maxProperties": 20,
            "title": "Details",
            "description": "Each detail's chosen option or whole number.",
            "x-cis-field": {
              "custom_widget": "service_parameters"
            }
          },
          "applies_to": {
            "anyOf": [
              {
                "properties": {
                  "kind": {
                    "anyOf": [
                      {
                        "type": "string",
                        "enum": [
                          "order",
                          "order_lines",
                          "delivery_line"
                        ]
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "For",
                    "x-cis-field": {
                      "enum_labels": {
                        "delivery_line": "A delivery",
                        "order": "The whole order",
                        "order_lines": "Order lines"
                      }
                    }
                  },
                  "line_refs": {
                    "items": {
                      "type": "string",
                      "pattern": "^ORD-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}\\.[0-9]{2,}$"
                    },
                    "type": "array",
                    "maxItems": 50,
                    "title": "Order lines",
                    "description": "Item lines on this order, for example ORD-K48193.01, when the extra is for order lines.",
                    "x-cis-field": {}
                  },
                  "delivery_line_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "format": "uuid"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery",
                    "description": "One of the order's delivery lines, when the extra is for a delivery.",
                    "x-cis-field": {}
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "title": "AppliesToBody",
                "description": "What the extra is for, flat: the kind, and the lines or the delivery that kind names (each a dry-run choice).\nClassic's three bodies (``{\"kind\": \"order\"}``, ``{\"kind\": \"order_lines\", \"line_refs\": [...]}`` and\n``{\"kind\": \"delivery_line\", \"delivery_line_id\": ...}``) are each one of these; a piece the kind needs and is not\ngiven, or one it does not take, is the action's field error."
              },
              {
                "type": "null"
              }
            ],
            "title": "For",
            "x-cis-field": {
              "hidden": true
            }
          },
          "quantity": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 10000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity",
            "description": "Left empty, a mailing or postage takes the quantity of the largest order line it is for, and any other extra takes 1.",
            "x-cis-field": {}
          },
          "price_pence": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 100000000.0,
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Price",
            "description": "The price, ex VAT.",
            "x-cis-field": {
              "required": true,
              "widget": "money"
            }
          },
          "agreement_note": {
            "type": "string",
            "maxLength": 2000,
            "title": "Customer's agreement",
            "description": "The customer's agreement to the extra, in the CSR's words.",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "supplier_no": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64,
                "minLength": 1
              },
              {
                "type": "null"
              }
            ],
            "title": "Bought from",
            "description": "The supplier number (SUP-…) of the supplier a fulfilment or installation is bought from. Left empty, the work is done in-house.",
            "x-cis-field": {
              "entity": "suppliers.supplier",
              "show_if": {
                "service_kind": [
                  "decoration",
                  "fulfilment",
                  "installation"
                ]
              },
              "widget": "entity"
            }
          },
          "cost_pence": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 100000000.0,
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Cost",
            "description": "The supplier's cost, ex VAT, when the extra is bought from a supplier.",
            "x-cis-field": {
              "show_if": {
                "service_kind": [
                  "decoration",
                  "fulfilment",
                  "installation"
                ]
              },
              "widget": "money"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddServiceExtraRequest"
      }
    },
    {
      "id": "catalogue.change_supplier_for_order_lines",
      "module": "catalogue_module",
      "label": "Change supplier…",
      "description": "Buy an order's bought item lines from another supplier at today's cost, with the reason recorded; the customer's price stays.",
      "target": "orders.order",
      "destructive": true,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/orders/{order_no}/actions/change-supplier",
      "dry_run_path": "/catalogue/orders/{order_no}/actions/change-supplier/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "lines": {
            "items": {
              "properties": {
                "line_ref": {
                  "type": "string",
                  "pattern": "^ORD-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}\\.[0-9]{2,}$",
                  "title": "Line",
                  "description": "A bought item line of the order, e.g. ORD-K48193.01.",
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "line_ref"
              ],
              "title": "SupplierChangeLineRequest"
            },
            "type": "array",
            "maxItems": 20,
            "title": "Lines",
            "description": "The order lines to move. Every bought line that can move is filled in to start.",
            "x-cis-field": {
              "required": true,
              "selectable": true,
              "widget": "table"
            }
          },
          "route_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64,
                "minLength": 1
              },
              {
                "type": "null"
              }
            ],
            "title": "Buy from",
            "description": "The chosen supplier's route, from the dry run.",
            "x-cis-field": {
              "required": true
            }
          },
          "reason": {
            "type": "string",
            "maxLength": 2000,
            "title": "Reason",
            "description": "Why the supplier is changing, in up to 500 characters.",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "expected_cost_pence": {
            "anyOf": [
              {
                "type": "integer",
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Cost shown",
            "description": "The guard: the cost the dry run showed for the supplier chosen, summed, ex VAT.",
            "x-cis-field": {
              "hidden": true,
              "widget": "money"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ChangeSupplierForOrderLinesRequest",
        "description": "*Change supplier…* on the order: its bought item lines move to the supplier on ``route_code`` (one supplier per\nroute) at today's cost; the customer's price stays."
      }
    },
    {
      "id": "catalogue.order_again_to_draft_quote",
      "module": "catalogue_module",
      "label": "Order again",
      "description": "Put the order's item lines on a draft quote, priced again today with their delivery - a new draft quote for the order's customer, or one of the customer's open draft quotes the person picks - and leave out any line that cannot be priced today.",
      "target": "orders.order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/orders/{order_no}/actions/order-again",
      "dry_run_path": "/catalogue/orders/{order_no}/actions/order-again/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "quote_no": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft quote",
            "description": "One of your open draft quotes for the customer; left empty, a new draft quote for the customer.",
            "x-cis-field": {
              "entity": "quotes.quote"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "OrderAgainToDraftQuoteRequest",
        "description": "*Order again*'s body: the draft quote to add the lines to (a new one when left empty), the guard of a picked draft\nquote, and the request id, so a repeat adds the lines once."
      }
    },
    {
      "id": "catalogue.change_order_line_quantity",
      "module": "catalogue_module",
      "label": "Change quantity…",
      "description": "Change the quantity of a catalogue line on an order, priced again at today's price, as the old line cancelled and a new line added in one write; needs the tick that the customer agreed, and emails the customer the updated order.",
      "target": "orders.order_line",
      "destructive": true,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/orders/{order_no}/lines/{line_ref}/actions/change-quantity",
      "dry_run_path": "/catalogue/orders/{order_no}/lines/{line_ref}/actions/change-quantity/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "expected_total_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Order total at preview",
            "description": "The order's new total, ex VAT, the dry run showed: an order that would total otherwise is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "confirmed": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 10,
            "title": "Confirmed",
            "description": "The dry run's confirmations ticked, by key: `customer_agreed` (the customer agreed to the change)."
          },
          "wait_seconds": {
            "anyOf": [
              {
                "type": "number",
                "maximum": 20.0,
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Wait for prices",
            "description": "How long to wait for every route's price, 0 to 20 seconds; omitted is the default. In-house prices take about 6 to 10 seconds, so pass 10 when every price is needed now. A route still pricing after it is named in the refusal, with when to ask again.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "quantity": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 10000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity",
            "x-cis-field": {
              "required": true
            }
          },
          "price_option": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100,
                "pattern": "^[^:]+:[^:]+$"
              },
              {
                "type": "null"
              }
            ],
            "title": "Price option",
            "description": "The chosen option's key, `route_code:tier`; none takes the sourcing policy's default.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "route_override_reason": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "earlier_date",
                  "quality_or_specialism",
                  "keep_in_house",
                  "known_problem_with_default",
                  "customer_asked",
                  "other"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Why not the default",
            "x-cis-field": {
              "hidden": true
            }
          },
          "route_override_note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Note on the reason",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "route_override_reason": [
                  "other"
                ]
              }
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ChangeOrderLineQuantityRequest",
        "description": "*Change quantity…*: the line's new quantity, priced at today's price. ``request_id`` is the change's: a repeat\nchanges nothing more. While a route is still pricing, the dry run offers the best route priced so far: choosing it\nsets the three route fields, which the new line then keeps."
      }
    },
    {
      "id": "catalogue.change_order_line_deliveries",
      "module": "catalogue_module",
      "label": "Change delivery…",
      "description": "Change where an order line's goods go and what each delivery charges, after the order.",
      "target": "orders.order_line",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/orders/{order_no}/lines/{line_ref}/actions/change-delivery",
      "dry_run_path": "/catalogue/orders/{order_no}/lines/{line_ref}/actions/change-delivery/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "delivery": {
            "anyOf": [
              {
                "properties": {
                  "mode": {
                    "anyOf": [
                      {
                        "type": "string",
                        "enum": [
                          "ship",
                          "multi",
                          "collect",
                          "hold",
                          "tbc"
                        ]
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery mode",
                    "x-cis-field": {}
                  },
                  "delivery_address_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery address",
                    "x-cis-field": {}
                  },
                  "service_level": {
                    "anyOf": [
                      {
                        "type": "string",
                        "enum": [
                          "next_day",
                          "pre_noon",
                          "pre_1030",
                          "pallet",
                          "northern_ireland",
                          "collection"
                        ],
                        "title": "DeliveryServiceLevel",
                        "description": "A delivery service level (R87). ``collection`` is the customer collecting: never priced by a courier."
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Delivery service",
                    "x-cis-field": {}
                  },
                  "courier_supplier_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Courier",
                    "x-cis-field": {}
                  },
                  "agreed_delivery_price_pence": {
                    "anyOf": [
                      {
                        "type": "integer",
                        "maximum": 100000000.0,
                        "minimum": 0.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Agreed delivery price",
                    "x-cis-field": {}
                  },
                  "agreed_delivery_reason": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 200
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Reason for the agreed price",
                    "x-cis-field": {}
                  },
                  "weight_kg": {
                    "anyOf": [
                      {
                        "type": "number",
                        "maximum": 10000.0,
                        "exclusiveMinimum": 0.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Weight (kg)",
                    "x-cis-field": {}
                  },
                  "parcels": {
                    "anyOf": [
                      {
                        "type": "integer",
                        "maximum": 500.0,
                        "minimum": 1.0
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Parcels",
                    "x-cis-field": {}
                  },
                  "collection_point_address_id": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 64
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Collection point",
                    "x-cis-field": {}
                  },
                  "recipient_name": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 100
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient name",
                    "x-cis-field": {}
                  },
                  "recipient_phone": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 40
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient phone",
                    "x-cis-field": {}
                  },
                  "recipient_email": {
                    "anyOf": [
                      {
                        "type": "string",
                        "maxLength": 254
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Recipient email",
                    "x-cis-field": {}
                  },
                  "delivery_lines": {
                    "items": {
                      "properties": {
                        "delivery_address_id": {
                          "type": "string",
                          "maxLength": 64,
                          "minLength": 1,
                          "title": "Delivery address",
                          "x-cis-field": {}
                        },
                        "quantity": {
                          "type": "integer",
                          "maximum": 10000000.0,
                          "minimum": 1.0,
                          "title": "Quantity",
                          "x-cis-field": {}
                        },
                        "version_position": {
                          "anyOf": [
                            {
                              "type": "integer",
                              "maximum": 100.0,
                              "minimum": 1.0
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Version",
                          "x-cis-field": {}
                        },
                        "recipient_name": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 100
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient name",
                          "x-cis-field": {}
                        },
                        "recipient_phone": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 40
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient phone",
                          "x-cis-field": {}
                        },
                        "recipient_email": {
                          "anyOf": [
                            {
                              "type": "string",
                              "maxLength": 254
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Recipient email",
                          "x-cis-field": {}
                        }
                      },
                      "additionalProperties": false,
                      "type": "object",
                      "required": [
                        "delivery_address_id",
                        "quantity"
                      ],
                      "title": "RequestedDeliveryLineSchema",
                      "description": "One row of a line split across addresses (mode ``multi``, order to invoice doc 106 §3.1): how many go to which\nof the customer's addresses, of which version on a line with several, and who receives them when that is not the\naddress's recipient."
                    },
                    "type": "array",
                    "maxItems": 20,
                    "title": "Delivery rows",
                    "x-cis-field": {}
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "title": "DeliverySelectionSchema",
                "description": "A line's delivery choice. A destination is a CIS address of the customer's (``delivery_address_id``), or, to\ncollect, one of our collection points. The level is the customer's or the CSR's; the courier, an agreed price and\nentered packages are staff-only (the portal's routes discard them)."
              },
              {
                "type": "null"
              }
            ],
            "title": "Delivery",
            "description": "Where the line's goods go.",
            "x-cis-field": {
              "custom_widget": "delivery_allocation",
              "required": true
            }
          },
          "choices": {
            "items": {
              "properties": {
                "delivery_ref": {
                  "type": "string",
                  "pattern": "^d-[0-9a-f]{16}$",
                  "title": "Delivery",
                  "x-cis-field": {}
                },
                "service_level": {
                  "anyOf": [
                    {
                      "type": "string",
                      "enum": [
                        "next_day",
                        "pre_noon",
                        "pre_1030",
                        "pallet",
                        "northern_ireland",
                        "collection"
                      ],
                      "title": "DeliveryServiceLevel",
                      "description": "A delivery service level (R87). ``collection`` is the customer collecting: never priced by a courier."
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Delivery service",
                  "x-cis-field": {}
                },
                "courier_supplier_id": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 64
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Courier",
                  "x-cis-field": {}
                },
                "collect": {
                  "type": "boolean",
                  "title": "Customer collects",
                  "default": false,
                  "x-cis-field": {}
                },
                "collection_point_address_id": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 64
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Collection point",
                  "x-cis-field": {}
                },
                "agreed_delivery_price_pence": {
                  "anyOf": [
                    {
                      "type": "integer",
                      "maximum": 100000000.0,
                      "minimum": 0.0
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Agreed delivery price",
                  "x-cis-field": {}
                },
                "agreed_delivery_reason": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 200
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Reason for the agreed price",
                  "x-cis-field": {}
                },
                "recipient_name": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 100
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Recipient name",
                  "x-cis-field": {}
                },
                "recipient_phone": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 40
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Recipient phone",
                  "x-cis-field": {}
                },
                "shown": {
                  "anyOf": [
                    {
                      "properties": {
                        "delivery_price_pence": {
                          "anyOf": [
                            {
                              "type": "integer"
                            },
                            {
                              "type": "null"
                            }
                          ],
                          "title": "Delivery price shown",
                          "description": "Ex VAT, £0 for a collection. Empty when the delivery showed Not yet priced.",
                          "x-cis-field": {}
                        }
                      },
                      "additionalProperties": false,
                      "type": "object",
                      "required": [
                        "delivery_price_pence"
                      ],
                      "title": "ShownDeliveryPriceSchema",
                      "description": "The delivery's price as the Delivery section showed it."
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Price shown",
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "delivery_ref"
              ],
              "title": "StaffDeliveryChoiceSchema",
              "description": "What staff chose for one delivery in the basket's Delivery section (order to invoice doc 104 §6, §9): a level\n(by the courier picked, else its cheapest), an agreed price with its reason, or to collect it. ``shown`` is the price the\nsection showed: checkout refuses with ``prices_changed`` when the delivery's price is no longer that."
            },
            "type": "array",
            "maxItems": 40,
            "title": "Delivery choices",
            "description": "Each delivery's choice (a level and courier, collection, or an agreed price with its reason), with the price shown for it: a price moved since is refused (409 stale).",
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ChangeDeliveryRequest",
        "description": "*Change delivery…*: where the line's goods are to go (the delivery-allocation widget, or Classic's\n``DeliverySection``), the choice made on each delivery's card with the ``shown`` price it binds to (the guard), and\nthe request id (a retry with it changes nothing more)."
      }
    },
    {
      "id": "catalogue.set_order_line_start_number",
      "module": "catalogue_module",
      "label": "Set start number…",
      "description": "Set or correct the first number a numbered NCR line prints, before it is released - the next in the customer's own item's series is suggested; the line's name follows and its price does not change.",
      "target": "orders.order_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/orders/{order_no}/lines/{line_ref}/actions/set-start-number",
      "dry_run_path": "/catalogue/orders/{order_no}/lines/{line_ref}/actions/set-start-number/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "start_number": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 12
              },
              {
                "type": "null"
              }
            ],
            "title": "Start number",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetOrderLineStartNumberRequest",
        "description": "*Set start number…*: the first number a numbered line prints, digits only (leading zeros kept). The dry run with\nno number prefills the line's own, else the next in its item's series. It changes no price, so it asks no agreement."
      }
    },
    {
      "id": "catalogue.split_order_line_versions",
      "module": "catalogue_module",
      "label": "Split between versions…",
      "description": "Give how an order line split when ordered (versions that offered several quantities) is shared between them, and change it any time before the line is released; the price does not change, the line's cost is worked out again at the split so the purchase order carries it, and the line is held at release until its split and its cost are known.",
      "target": "orders.order_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/orders/{order_no}/lines/{line_ref}/actions/split-versions",
      "dry_run_path": "/catalogue/orders/{order_no}/lines/{line_ref}/actions/split-versions/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "versions": {
            "anyOf": [
              {
                "items": {
                  "properties": {
                    "position": {
                      "type": "integer",
                      "maximum": 26.0,
                      "minimum": 1.0,
                      "title": "Version no",
                      "readOnly": true,
                      "x-cis-field": {
                        "hidden": true
                      }
                    },
                    "version": {
                      "anyOf": [
                        {
                          "type": "string",
                          "maxLength": 200
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "Version",
                      "description": "The version, e.g. 'A · Jo Bloggs'.",
                      "readOnly": true,
                      "x-cis-field": {}
                    },
                    "quantity": {
                      "anyOf": [
                        {
                          "type": "integer",
                          "maximum": 10000000.0,
                          "minimum": 1.0
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "Quantity",
                      "description": "A whole number; the versions add up to the line's.",
                      "x-cis-field": {}
                    }
                  },
                  "additionalProperties": false,
                  "type": "object",
                  "required": [
                    "position"
                  ],
                  "title": "OrderVersionQuantityRequest",
                  "description": "One version of a line split when ordered, as *Split between versions…*'s table offers it."
                },
                "type": "array",
                "maxItems": 26
              },
              {
                "type": "null"
              }
            ],
            "title": "Split between versions",
            "description": "One row per version: the quantity it takes.",
            "x-cis-field": {
              "required": true,
              "widget": "table"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SplitOrderLineVersionsRequest",
        "description": "*Split between versions…*: how the line's quantity is shared between its versions. The dry run with no versions\nprefills them (blank while the split is owed, else as they are). The price never changes; the cost is worked out\nagain at the split and the purchase order carries it."
      }
    },
    {
      "id": "catalogue.change_supplier_for_service_line",
      "module": "catalogue_module",
      "label": "Change supplier…",
      "description": "Buy a bought fulfilment or installation from another supplier, its cost entered by hand, with the reason recorded.",
      "target": "orders.order_line",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/orders/{order_no}/lines/{line_ref}/actions/change-supplier",
      "dry_run_path": "/catalogue/orders/{order_no}/lines/{line_ref}/actions/change-supplier/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "supplier_no": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64,
                "minLength": 1
              },
              {
                "type": "null"
              }
            ],
            "title": "Supplier",
            "description": "The new supplier's supplier number (SUP-…).",
            "x-cis-field": {
              "entity": "suppliers.supplier",
              "required": true,
              "widget": "entity"
            }
          },
          "cost_pence": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 100000000.0,
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Cost",
            "description": "The new supplier's cost, ex VAT.",
            "x-cis-field": {
              "required": true,
              "widget": "money"
            }
          },
          "reason": {
            "type": "string",
            "maxLength": 2000,
            "title": "Reason",
            "description": "Why the supplier is changing, in up to 500 characters.",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ChangeSupplierForServiceLineRequest",
        "description": "*Change supplier…* on a bought fulfilment or installation (supplier interface spec 12): its supplier is named and\nits cost entered by hand."
      }
    },
    {
      "id": "catalogue.add_order_line_to_customer_catalogue",
      "module": "catalogue_module",
      "label": "Add to customer's catalogue…",
      "description": "Make an order line into an item in the customer's own catalogue, ordinary or repeatable; a customer with no catalogue of its own gets one made with the item, and a repeatable item takes the order line's approved artwork. A repeatable item is made only once the order line's artwork is final; until then, and on a line not yet released, One waits and makes the item when it can.",
      "target": "orders.order_line",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/catalogue/orders/{order_no}/lines/{line_ref}/actions/add-to-customer-catalogue",
      "dry_run_path": "/catalogue/orders/{order_no}/lines/{line_ref}/actions/add-to-customer-catalogue/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "kind": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "ordinary",
                  "repeatable"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Add as",
            "x-cis-field": {
              "enum_labels": {
                "ordinary": "An ordinary item: the same specification, new artwork each order",
                "repeatable": "A repeatable item: the same specification and artwork every order"
              },
              "required": true
            }
          },
          "name": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 510
              },
              {
                "type": "null"
              }
            ],
            "title": "Item name",
            "x-cis-field": {
              "required": true
            }
          },
          "catalogue_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Catalogue",
            "x-cis-field": {
              "hidden": true
            }
          },
          "customer_product_ref": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer's product code",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddOrderLineToCustomerCatalogueRequest",
        "description": "*Add to customer's catalogue…*: how the released order line becomes an item in the customer's own catalogue, what\nit is called, and (when the customer has two or more catalogues of its own managed in One) which one. ``catalogue_id``\nis the dry run's choice, never drawn as a field. ``request_id`` is the command's: a repeat finds the item the first\nrequest made."
      }
    },
    {
      "id": "comms_ingest.confirm_all_proposed_actions",
      "module": "comms_ingest_module",
      "label": "Confirm all actions",
      "description": "Confirm every waiting action of an email in step order, each running its command as the person; a step that no longer applies is withdrawn and the first refusal stops the rest.",
      "target": "comms_ingest.email",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/comms-ingest/emails/{email_no}/actions/confirm-all",
      "dry_run_path": "/comms-ingest/emails/{email_no}/actions/confirm-all/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ConfirmAllProposedActionsRequest",
        "description": "*Confirm all actions*: nothing to fill in; each waiting step runs with its own request."
      },
      "main": true,
      "inherits_destructive": true
    },
    {
      "id": "comms_ingest.mark_email_done",
      "module": "comms_ingest_module",
      "label": "Mark done…",
      "description": "Close a received email that is Actions waiting or Needs a person, keeping what the person did and rejecting the actions still waiting with the note as their reason.",
      "target": "comms_ingest.email",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/comms-ingest/emails/{email_no}/actions/mark-done",
      "dry_run_path": "/comms-ingest/emails/{email_no}/actions/mark-done/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "What you did",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "MarkEmailDoneRequest",
        "description": "*Mark done…*: what the person did, kept with the email."
      }
    },
    {
      "id": "comms_ingest.add_email_tag",
      "module": "comms_ingest_module",
      "label": "Add tag…",
      "description": "Tag a received email that names no record to the record the person chooses, from the possible tags One offers or any record of a kind they can see.",
      "target": "comms_ingest.email",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/comms-ingest/emails/{email_no}/actions/add-tag",
      "dry_run_path": "/comms-ingest/emails/{email_no}/actions/add-tag/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "candidate": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Record to tag",
            "x-cis-field": {
              "hidden": true,
              "required": true
            }
          },
          "record_type": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Type of record",
            "x-cis-field": {
              "hidden": true
            }
          },
          "purchase_order": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Purchase order",
            "x-cis-field": {
              "entity": "purchase_orders.purchase_order",
              "required": true,
              "show_if": {
                "candidate": "other",
                "record_type": "purchase_order"
              },
              "widget": "entity"
            }
          },
          "order": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Order",
            "x-cis-field": {
              "entity": "orders.order",
              "required": true,
              "show_if": {
                "candidate": "other",
                "record_type": "order"
              },
              "widget": "entity"
            }
          },
          "quote": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Quote",
            "x-cis-field": {
              "entity": "quotes.quote",
              "required": true,
              "show_if": {
                "candidate": "other",
                "record_type": "quote"
              },
              "widget": "entity"
            }
          },
          "invoice": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Invoice",
            "x-cis-field": {
              "entity": "finance_sales_ledger.invoice",
              "required": true,
              "show_if": {
                "candidate": "other",
                "record_type": "invoice"
              },
              "widget": "entity"
            }
          },
          "supplier": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Supplier",
            "x-cis-field": {
              "entity": "suppliers.supplier",
              "required": true,
              "show_if": {
                "candidate": "other",
                "record_type": "supplier"
              },
              "widget": "entity"
            }
          },
          "customer": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "x-cis-field": {
              "entity": "customers.customer",
              "required": true,
              "show_if": {
                "candidate": "other",
                "record_type": "customer"
              },
              "widget": "entity"
            }
          },
          "supplier_quote": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Supplier quote",
            "x-cis-field": {
              "entity": "supplier_quotes.supplier_quote",
              "required": true,
              "show_if": {
                "candidate": "other",
                "record_type": "supplier_quote"
              },
              "widget": "entity"
            }
          },
          "purchase_invoice": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Purchase invoice",
            "x-cis-field": {
              "entity": "finance_purchase_ledger.purchase_invoice",
              "required": true,
              "show_if": {
                "candidate": "other",
                "record_type": "purchase_invoice"
              },
              "widget": "entity"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddEmailTagRequest",
        "description": "*Add tag…*: a possible tag One offered, or Another record… with its kind and the record itself. There is one\npicker per kind (named for it, as ``TagObject.record_type`` says), and the commit reads the one ``record_type``\nnames."
      }
    },
    {
      "id": "comms_ingest.remove_email_tag",
      "module": "comms_ingest_module",
      "label": "Remove tag…",
      "description": "Take one tag off an email, so the email no longer shows on that record's emails; the email is kept, and a tag something was done from stays.",
      "target": "comms_ingest.email",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/comms-ingest/emails/{email_no}/actions/remove-tag",
      "dry_run_path": "/comms-ingest/emails/{email_no}/actions/remove-tag/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "tag": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Tag to remove",
            "x-cis-field": {
              "hidden": true,
              "required": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RemoveEmailTagRequest",
        "description": "*Remove tag…*: the tag chosen among the email's (its id)."
      }
    },
    {
      "id": "comms_ingest.send_email_again",
      "module": "comms_ingest_module",
      "label": "Send again…",
      "description": "Send a sent email that couldn't go again, to the address the person corrects (this email only); the module that sent it rebuilds it with fresh links.",
      "target": "comms_ingest.email",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/comms-ingest/emails/{email_no}/actions/send-again",
      "dry_run_path": "/comms-ingest/emails/{email_no}/actions/send-again/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "to": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 320
              },
              {
                "type": "null"
              }
            ],
            "title": "To",
            "description": "One email address, e.g. quotes@pdq.example.",
            "x-cis-field": {
              "required": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SendEmailAgainRequest",
        "description": "*Send again…*: the address to send the email to this time (prefilled with the one it was sent to)."
      }
    },
    {
      "id": "comms_ingest.confirm_proposed_action",
      "module": "comms_ingest_module",
      "label": "Confirm action",
      "description": "Run a proposed action's command as the person confirming it, its dry run asked again first; the action is confirmed and tags the email to its records, or is withdrawn when it no longer applies.",
      "target": "comms_ingest.proposed_action",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/comms-ingest/emails/{email_no}/proposed-actions/{action_id}/actions/confirm",
      "dry_run_path": "/comms-ingest/emails/{email_no}/proposed-actions/{action_id}/actions/confirm/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ConfirmProposedActionRequest",
        "description": "*Confirm action*: nothing to fill in; the proposal carries the command's request, and its dry run is asked\nagain when confirming."
      },
      "inherits_destructive": true
    },
    {
      "id": "comms_ingest.reject_proposed_action",
      "module": "comms_ingest_module",
      "label": "Reject…",
      "description": "Reject a waiting proposed action with the person's reason, which is kept to improve how One handles emails like this; one that no longer applies is withdrawn instead.",
      "target": "comms_ingest.proposed_action",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/comms-ingest/emails/{email_no}/proposed-actions/{action_id}/actions/reject",
      "dry_run_path": "/comms-ingest/emails/{email_no}/proposed-actions/{action_id}/actions/reject/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Why it is wrong",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RejectProposedActionRequest",
        "description": "*Reject…*: why the proposed action is wrong, kept with it to improve how One handles emails like this."
      }
    },
    {
      "id": "comms_ingest.make_automatic_from_proposed_action",
      "module": "comms_ingest_module",
      "label": "Make automatic…",
      "description": "From a confirmed proposed action, rule that its handler's proposals of its command run on their own, for its supplier or any; refused for the general handler and for a command that decides.",
      "target": "comms_ingest.proposed_action",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/comms-ingest/emails/{email_no}/proposed-actions/{action_id}/actions/make-automatic",
      "dry_run_path": "/comms-ingest/emails/{email_no}/proposed-actions/{action_id}/actions/make-automatic/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "supplier": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "For",
            "x-cis-field": {
              "hidden": true,
              "required": true
            }
          },
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Why",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "MakeAutomaticFromProposedActionRequest",
        "description": "*Make automatic…* on a confirmed proposed action: its handler and command, for its supplier or any."
      }
    },
    {
      "id": "comms_ingest.stop_automatic_from_proposed_action",
      "module": "comms_ingest_module",
      "label": "Stop automatic…",
      "description": "From a proposed action One did on its own, rule that its handler's proposals of its command wait for a person from now on.",
      "target": "comms_ingest.proposed_action",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/comms-ingest/emails/{email_no}/proposed-actions/{action_id}/actions/stop-automatic",
      "dry_run_path": "/comms-ingest/emails/{email_no}/proposed-actions/{action_id}/actions/stop-automatic/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Why",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "StopAutomaticRequest",
        "description": "*Stop automatic…*: why, kept with the automatic action."
      }
    },
    {
      "id": "comms_ingest.make_automatic",
      "module": "comms_ingest_module",
      "label": "Make automatic…",
      "description": "Rule in Setup that a handler's proposals of a command run on their own, for one supplier or any, or make a stopped one automatic again; refused for the general handler and for a command that decides.",
      "target": "collection:comms_ingest.automatic_action",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/comms-ingest/automatic-actions/actions/make-automatic",
      "dry_run_path": "/comms-ingest/automatic-actions/actions/make-automatic/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "handler": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Handler",
            "x-cis-field": {
              "hidden": true,
              "required": true
            }
          },
          "command": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Command",
            "x-cis-field": {
              "hidden": true,
              "required": true
            }
          },
          "supplier_no": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Supplier",
            "description": "Leave empty for any supplier.",
            "x-cis-field": {
              "entity": "suppliers.supplier",
              "widget": "entity"
            }
          },
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Why",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "MakeAutomaticRequest",
        "description": "*Make automatic…* in Setup: which handler's proposals of which command, for one supplier or any."
      }
    },
    {
      "id": "comms_ingest.stop_automatic",
      "module": "comms_ingest_module",
      "label": "Stop automatic…",
      "description": "Stop an automatic action, keeping who made it automatic; from now on its proposals wait for a person.",
      "target": "comms_ingest.automatic_action",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/comms-ingest/automatic-actions/{id}/actions/stop",
      "dry_run_path": "/comms-ingest/automatic-actions/{id}/actions/stop/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Why",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "StopAutomaticRequest",
        "description": "*Stop automatic…*: why, kept with the automatic action."
      },
      "main": true
    },
    {
      "id": "customer_access.find_or_create_person_at_customer",
      "module": "customer_access_module",
      "label": "Add customer user…",
      "description": "Put a person at a customer at own-work access, creating the person if the email is new; emails an invite when asked and the portal is open.",
      "target": "customers.customer",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Grants or removes outside access: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/customer-access/customers/{customer_code}/people/actions/add",
      "dry_run_path": "/customer-access/customers/{customer_code}/people/actions/add/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "email": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 254
              },
              {
                "type": "null"
              }
            ],
            "title": "Email address",
            "x-cis-field": {
              "required": true
            }
          },
          "display_name": {
            "type": "string",
            "maxLength": 200,
            "title": "Name",
            "default": "",
            "x-cis-field": {}
          },
          "source": {
            "type": "string",
            "const": "typed_email",
            "title": "Where the address came from",
            "default": "typed_email",
            "x-cis-field": {
              "enum_labels": {
                "picked_contact": "A Tharstern contact",
                "typed_email": "Typed in"
              },
              "widget": "select"
            }
          },
          "send_invite": {
            "type": "boolean",
            "title": "Email an invite",
            "description": "One will email the person an invite to the portal. No invite will be sent if the person is already linked to this customer, or while the portal is not open.",
            "default": false,
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddPortalPersonRequest",
        "description": "*Add customer user…*: put a person at the customer at own-work access, creating the person if the email is\nnew. A person CIS already knows is linked, not set up twice."
      }
    },
    {
      "id": "customer_access.link_person_to_customer",
      "module": "customer_access_module",
      "label": "Link customer user…",
      "description": "Put a person CIS already knows at a customer at own-work access; emails an invite when asked and the portal is open.",
      "target": "customers.customer",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Grants or removes outside access: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/customer-access/customers/{customer_code}/people/actions/link",
      "dry_run_path": "/customer-access/customers/{customer_code}/people/actions/link/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "person_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Person",
            "description": "The id of a person One already knows, as the portal staff view's contact picker offers it.",
            "x-cis-field": {
              "required": true
            }
          },
          "send_invite": {
            "type": "boolean",
            "title": "Email an invite",
            "description": "One will email the person an invite to the portal. No invite will be sent if the person is already linked to this customer.",
            "default": false,
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "LinkPortalPersonRequest",
        "description": "*Link customer user…*: put a person CIS already knows at the customer at own-work access."
      }
    },
    {
      "id": "customer_access.set_default_order_contact",
      "module": "customer_access_module",
      "label": "Set default order contact…",
      "description": "Make one of a customer's customer users the contact a new staff basket or quote starts with, or clear it; sends nothing.",
      "target": "customers.customer",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Part of the customer users surface: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/customer-access/customers/{customer_code}/actions/set-default-order-contact",
      "dry_run_path": "/customer-access/customers/{customer_code}/actions/set-default-order-contact/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "person_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Default order contact",
            "description": "A customer user of this customer, or none.",
            "x-cis-field": {
              "entity": "customer_access.customer_link",
              "entity_value": "person_id"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetDefaultOrderContactRequest",
        "description": "*Set default order contact…*: the person a new staff basket or quote for the customer starts with as its contact.\nEmpty or null clears it. The dry run starts on the one chosen now; the picker lists only this customer's active\ncustomer users."
      },
      "edits": {
        "fields": [
          "default_order_contact_name"
        ]
      }
    },
    {
      "id": "customer_access.create_customer_team",
      "module": "customer_access_module",
      "label": "Create team…",
      "description": "Create a team at a customer, its name unique among the customer's active teams; it starts empty and grants nothing.",
      "target": "customers.customer",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Part of the customer users surface (customer_access:people): done in the app, never by Claude.",
      "method": "POST",
      "path": "/customer-access/customers/{customer_code}/teams/actions/create",
      "dry_run_path": "/customer-access/customers/{customer_code}/teams/actions/create/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "name": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Team name",
            "description": "At most 60 characters, and not the name of another of the customer's teams.",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "CreateCustomerTeamRequest",
        "description": "*Create team…*: a named set of the customer's people (portal theme spec 05). It starts empty and grants\nnothing."
      }
    },
    {
      "id": "customer_access.enable_customer_spend_reporting",
      "module": "customer_access_module",
      "label": "Turn on spend reporting",
      "description": "Turn on a customer's Spend page for the people who hold the See spend access.",
      "target": "customers.customer",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Grants or removes outside access: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/customer-access/customers/{customer_code}/actions/enable-spend-reporting",
      "dry_run_path": "/customer-access/customers/{customer_code}/actions/enable-spend-reporting/dry-run",
      "content_type": "json",
      "request_schema": null,
      "edits": {
        "fields": [
          "spend_reporting"
        ],
        "value": true
      }
    },
    {
      "id": "customer_access.disable_customer_spend_reporting",
      "module": "customer_access_module",
      "label": "Turn off spend reporting",
      "description": "Turn off a customer's Spend page for everyone there; the See spend access people hold is kept.",
      "target": "customers.customer",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Grants or removes outside access: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/customer-access/customers/{customer_code}/actions/disable-spend-reporting",
      "dry_run_path": "/customer-access/customers/{customer_code}/actions/disable-spend-reporting/dry-run",
      "content_type": "json",
      "request_schema": null,
      "edits": {
        "fields": [
          "spend_reporting"
        ],
        "value": false
      }
    },
    {
      "id": "customer_access.unblock_customer_portal",
      "module": "customer_access_module",
      "label": "Unblock customer portal",
      "description": "Lift a customer's portal block, restoring exactly the access every link had before it.",
      "target": "customers.customer",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Grants or removes outside access: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/customer-access/customers/{customer_code}/actions/unblock-portal",
      "dry_run_path": "/customer-access/customers/{customer_code}/actions/unblock-portal/dry-run",
      "content_type": "json",
      "request_schema": null,
      "edits": {
        "fields": [
          "portal_access"
        ],
        "value": true
      }
    },
    {
      "id": "customer_access.block_customer_portal",
      "module": "customer_access_module",
      "label": "Block customer portal…",
      "description": "Turn away everyone linked to a customer at once, whatever access each holds; reversible and touches no link.",
      "target": "customers.customer",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Grants or removes outside access: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/customer-access/customers/{customer_code}/actions/block-portal",
      "dry_run_path": "/customer-access/customers/{customer_code}/actions/block-portal/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "legal",
                  "credit",
                  "other"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "description": "Why the portal is blocked. Only staff will see the reason.",
            "x-cis-field": {
              "enum_labels": {
                "credit": "Credit",
                "legal": "Legal",
                "other": "Other"
              },
              "required": true,
              "widget": "select"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "BlockCustomerPortalRequest",
        "description": "*Block customer portal…*: turn away everyone at the customer, whatever access each person holds."
      },
      "edits": {
        "fields": [
          "portal_access"
        ],
        "value": false
      }
    },
    {
      "id": "customer_access.send_portal_invite",
      "module": "customer_access_module",
      "label": "Send portal invite",
      "description": "Email a customer user that they can use the portal, at most once in ten minutes.",
      "target": "customer_access.customer_link",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Grants or removes outside access: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/customer-access/customers/{customer_code}/people/{person_id}/actions/send-invite",
      "dry_run_path": "/customer-access/customers/{customer_code}/people/{person_id}/actions/send-invite/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "customer_access.set_link_extras",
      "module": "customer_access_module",
      "label": "Set portal access…",
      "description": "Replace a customer user's whole set of extras at a customer (see all of the customer's work, order on account, see spend).",
      "target": "customer_access.customer_link",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Grants or removes outside access: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/customer-access/customers/{customer_code}/people/{person_id}/actions/set-access",
      "dry_run_path": "/customer-access/customers/{customer_code}/people/{person_id}/actions/set-access/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "extras": {
            "anyOf": [
              {
                "items": {
                  "type": "string",
                  "enum": [
                    "customer_wide_view",
                    "order_on_account",
                    "spend_reporting"
                  ]
                },
                "type": "array",
                "maxItems": 3
              },
              {
                "type": "null"
              }
            ],
            "title": "Access",
            "description": "What the person can do beyond seeing the person's own orders and quotes. Each extra can be given or taken away separately.",
            "x-cis-field": {
              "enum_labels": {
                "customer_wide_view": "See all of the customer's work",
                "order_on_account": "Order on account",
                "spend_reporting": "See spend"
              }
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetPortalAccessRequest",
        "description": "*Set portal access…*: the person's whole set of extras at the customer, replacing the one they hold. Empty is\nown-work access only. The dry run starts the extras as the person's own."
      },
      "edits": {
        "fields": [
          "access"
        ]
      }
    },
    {
      "id": "customer_access.unlink_person_from_customer",
      "module": "customer_access_module",
      "label": "Remove customer user",
      "description": "Remove a customer user from a customer, clearing their extras and taking them out of its teams; the person and their other customers stay.",
      "target": "customer_access.customer_link",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Grants or removes outside access: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/customer-access/customers/{customer_code}/people/{person_id}/actions/remove",
      "dry_run_path": "/customer-access/customers/{customer_code}/people/{person_id}/actions/remove/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "customer_access.rename_customer_team",
      "module": "customer_access_module",
      "label": "Rename team…",
      "description": "Rename an active team, its new name unique among the customer's active teams.",
      "target": "customer_access.customer_team",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Part of the customer users surface (customer_access:people): done in the app, never by Claude.",
      "method": "POST",
      "path": "/customer-access/customers/{customer_code}/teams/{team_id}/actions/rename",
      "dry_run_path": "/customer-access/customers/{customer_code}/teams/{team_id}/actions/rename/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "name": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Team name",
            "description": "At most 60 characters, and not the name of another of the customer's teams.",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RenameCustomerTeamRequest",
        "description": "*Rename team…*: the team's new name. The dry run starts it as the team's own."
      }
    },
    {
      "id": "customer_access.add_person_to_customer_team",
      "module": "customer_access_module",
      "label": "Add to team…",
      "description": "Put one of the customer's active customer users in one of its active teams; grants nothing.",
      "target": "customer_access.customer_team",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Part of the customer users surface (customer_access:people): done in the app, never by Claude.",
      "method": "POST",
      "path": "/customer-access/customers/{customer_code}/teams/{team_id}/actions/add-person",
      "dry_run_path": "/customer-access/customers/{customer_code}/teams/{team_id}/actions/add-person/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "person_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Person",
            "description": "A customer user of this customer, as the dry run offers them.",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "CustomerTeamPersonRequest",
        "description": "*Add to team…* and *Remove from team…*: the person, one of the customer's own customer users."
      }
    },
    {
      "id": "customer_access.remove_person_from_customer_team",
      "module": "customer_access_module",
      "label": "Remove from team…",
      "description": "Take a person out of one of the customer's active teams; they stay a customer user.",
      "target": "customer_access.customer_team",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Part of the customer users surface (customer_access:people): done in the app, never by Claude.",
      "method": "POST",
      "path": "/customer-access/customers/{customer_code}/teams/{team_id}/actions/remove-person",
      "dry_run_path": "/customer-access/customers/{customer_code}/teams/{team_id}/actions/remove-person/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "person_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Person",
            "description": "A customer user of this customer, as the dry run offers them.",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "CustomerTeamPersonRequest",
        "description": "*Add to team…* and *Remove from team…*: the person, one of the customer's own customer users."
      }
    },
    {
      "id": "customer_access.retire_customer_team",
      "module": "customer_access_module",
      "label": "Retire team",
      "description": "Retire a team for good; its members are kept for history and stop counting, and its name is free again.",
      "target": "customer_access.customer_team",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Part of the customer users surface (customer_access:people): done in the app, never by Claude.",
      "method": "POST",
      "path": "/customer-access/customers/{customer_code}/teams/{team_id}/actions/retire",
      "dry_run_path": "/customer-access/customers/{customer_code}/teams/{team_id}/actions/retire/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "customer_communications.add_communication",
      "module": "customer_communications_module",
      "label": "Add communication…",
      "description": "Share meeting notes, call notes or a call transcript with the customers and contact people it concerns, so colleagues see it on each customer's timeline. Use for an interaction with people outside the business; an internal memo or a reminder is a note (Add note…), not a communication.",
      "target": "customers.customer",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/comms/customers/{customer_code}/actions/add-communication",
      "dry_run_path": "/comms/customers/{customer_code}/actions/add-communication/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "kind": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "meeting_notes",
                  "call_notes",
                  "call_transcript"
                ],
                "title": "CommunicationKind",
                "description": "The shared kinds. A new kind is a new member and a label here: the form, the record and the timeline are\ngeneric over the kind, so no screen changes, and the column carries no check constraint, so no migration."
              },
              {
                "type": "null"
              }
            ],
            "title": "Kind",
            "description": "Meeting notes, call notes or a call transcript.",
            "x-cis-field": {
              "enum_labels": {
                "call_notes": "Call notes",
                "call_transcript": "Call transcript",
                "meeting_notes": "Meeting notes"
              },
              "required": true,
              "widget": "radio"
            }
          },
          "occurred_at": {
            "anyOf": [
              {
                "type": "string",
                "format": "date-time"
              },
              {
                "type": "null"
              }
            ],
            "title": "When it happened",
            "description": "When the meeting or call happened. A time without an offset is UK time.",
            "x-cis-field": {
              "required": true
            }
          },
          "title": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Title",
            "x-cis-field": {
              "required": true
            }
          },
          "text": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200000
              },
              {
                "type": "null"
              }
            ],
            "title": "Text",
            "description": "The notes or the transcript, as the person or their notes app wrote them.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "customer_codes": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 20,
            "title": "Customers",
            "description": "The customer numbers (CUS-…) of every customer the meeting or call concerned. Choose at least one.",
            "x-cis-field": {
              "entity": "customers.customer",
              "required": true,
              "widget": "entity"
            }
          },
          "person_ids": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 50,
            "title": "Contact people",
            "description": "People at those customers who took part, found among the chosen customers' active customer users.",
            "x-cis-field": {
              "entity": "customer_access.customer_link",
              "entity_value": "person_id",
              "widget": "entity"
            }
          },
          "source": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100
              },
              {
                "type": "null"
              }
            ],
            "title": "Source",
            "description": "Where the notes or transcript came from, e.g. 'Teams transcript' or 'Granola notes'.",
            "x-cis-field": {
              "required": true
            }
          },
          "source_url": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 2000
              },
              {
                "type": "null"
              }
            ],
            "title": "Link",
            "description": "Optional: an https link to the original notes or recording.",
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddCommunicationRequest",
        "description": "*Add communication…*. ``request_id`` becomes the communication's id: a repeat returns the first one."
      },
      "main": true
    },
    {
      "id": "customer_communications.update_communication",
      "module": "customer_communications_module",
      "label": "Edit communication…",
      "description": "Change a communication you shared, its customers or its contact people; only its author can.",
      "target": "customer_communications.communication",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/comms/communications/{communication_id}/actions/edit",
      "dry_run_path": "/comms/communications/{communication_id}/actions/edit/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "kind": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "meeting_notes",
                  "call_notes",
                  "call_transcript"
                ],
                "title": "CommunicationKind",
                "description": "The shared kinds. A new kind is a new member and a label here: the form, the record and the timeline are\ngeneric over the kind, so no screen changes, and the column carries no check constraint, so no migration."
              },
              {
                "type": "null"
              }
            ],
            "title": "Kind",
            "description": "Meeting notes, call notes or a call transcript.",
            "x-cis-field": {
              "enum_labels": {
                "call_notes": "Call notes",
                "call_transcript": "Call transcript",
                "meeting_notes": "Meeting notes"
              },
              "required": true,
              "widget": "radio"
            }
          },
          "occurred_at": {
            "anyOf": [
              {
                "type": "string",
                "format": "date-time"
              },
              {
                "type": "null"
              }
            ],
            "title": "When it happened",
            "description": "When the meeting or call happened. A time without an offset is UK time.",
            "x-cis-field": {
              "required": true
            }
          },
          "title": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Title",
            "x-cis-field": {
              "required": true
            }
          },
          "text": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200000
              },
              {
                "type": "null"
              }
            ],
            "title": "Text",
            "description": "The notes or the transcript, as the person or their notes app wrote them.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "customer_codes": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 20,
            "title": "Customers",
            "description": "The customer numbers (CUS-…) of every customer the meeting or call concerned. Choose at least one.",
            "x-cis-field": {
              "entity": "customers.customer",
              "required": true,
              "widget": "entity"
            }
          },
          "person_ids": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 50,
            "title": "Contact people",
            "description": "People at those customers who took part, found among the chosen customers' active customer users.",
            "x-cis-field": {
              "entity": "customer_access.customer_link",
              "entity_value": "person_id",
              "widget": "entity"
            }
          },
          "source": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100
              },
              {
                "type": "null"
              }
            ],
            "title": "Source",
            "description": "Where the notes or transcript came from, e.g. 'Teams transcript' or 'Granola notes'.",
            "x-cis-field": {
              "required": true
            }
          },
          "source_url": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 2000
              },
              {
                "type": "null"
              }
            ],
            "title": "Link",
            "description": "Optional: an https link to the original notes or recording.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "UpdateCommunicationRequest",
        "description": "*Edit communication…*: the whole communication as it should now be. The dry run prefills it as it stands."
      }
    },
    {
      "id": "customer_communications.remove_communication",
      "module": "customer_communications_module",
      "label": "Remove communication",
      "description": "Take a communication you shared off every customer's timeline for good; only its author can.",
      "target": "customer_communications.communication",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/comms/communications/{communication_id}/actions/remove",
      "dry_run_path": "/comms/communications/{communication_id}/actions/remove/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "RemoveCommunicationRequest",
        "description": "*Remove communication*: nothing to fill in."
      }
    },
    {
      "id": "customers.create_customer",
      "module": "customers_module",
      "label": "Create customer…",
      "description": "Create a customer in CIS with an optional account address and its sales rep and CSR; a repeat of the same request returns the first customer.",
      "target": "collection:customers.customer",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/cis-customers/actions/create-customer",
      "dry_run_path": "/cis-customers/actions/create-customer/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "name": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Name",
            "x-cis-field": {
              "required": true
            }
          },
          "market": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Market",
            "x-cis-field": {}
          },
          "account_address": {
            "anyOf": [
              {
                "properties": {
                  "lines": {
                    "items": {
                      "type": "string"
                    },
                    "type": "array",
                    "title": "Address lines",
                    "description": "One to four lines, none of them blank.",
                    "x-cis-field": {}
                  },
                  "town": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Town",
                    "x-cis-field": {}
                  },
                  "county": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "County",
                    "x-cis-field": {}
                  },
                  "postcode": {
                    "type": "string",
                    "title": "Postcode",
                    "x-cis-field": {}
                  },
                  "country_code": {
                    "type": "string",
                    "title": "Country code",
                    "description": "The two-letter country code, such as GB.",
                    "x-cis-field": {}
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "required": [
                  "lines",
                  "postcode",
                  "country_code"
                ],
                "title": "AccountAddressRequest",
                "description": "The new customer's account address: saved as its invoice and default delivery address."
              },
              {
                "type": "null"
              }
            ],
            "title": "Account address",
            "x-cis-field": {
              "widget": "address"
            }
          },
          "owners": {
            "anyOf": [
              {
                "properties": {
                  "sales_rep_user_id": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Sales rep",
                    "x-cis-field": {}
                  },
                  "csr_user_id": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "CSR",
                    "x-cis-field": {}
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "required": [
                  "sales_rep_user_id",
                  "csr_user_id"
                ],
                "title": "CreateCustomerOwnersRequest",
                "description": "The new customer's sales rep and CSR; ``null`` or the empty string is House Account on that axis (the empty\nstring is what *Create customer…*'s choices send for it)."
              },
              {
                "type": "null"
              }
            ],
            "title": "Sales rep and CSR",
            "description": "Chosen from the dry run's choices.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "order_contact": {
            "anyOf": [
              {
                "properties": {
                  "name": {
                    "type": "string",
                    "title": "Name",
                    "default": "",
                    "x-cis-field": {}
                  },
                  "email": {
                    "type": "string",
                    "title": "Email",
                    "x-cis-field": {}
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "required": [
                  "email"
                ],
                "title": "NewCustomerOrderContactRequest",
                "description": "The optional \"Order contact\" step: the person new quotes and orders for the customer start with (D2)."
              },
              {
                "type": "null"
              }
            ],
            "title": "Order contact",
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "CreateCustomerRequest",
        "description": "*Create customer…*. Without ``owners`` the customer gets none now, and the next allocation intake puts it on\nHouse Account. ``request_id`` becomes the customer's id: a repeat returns the first customer."
      }
    },
    {
      "id": "customers.create_address",
      "module": "customers_module",
      "label": "Add address…",
      "description": "Add an address to an open customer, moving the default delivery address if the new one takes the role; a repeat of the same request returns the first address.",
      "target": "collection:customers.address",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/cis-customers/{customer_code}/addresses/actions/create-address",
      "dry_run_path": "/cis-customers/{customer_code}/addresses/actions/create-address/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "is_delivery": {
            "type": "boolean",
            "title": "Used for deliveries",
            "default": true,
            "x-cis-field": {}
          },
          "is_invoice": {
            "type": "boolean",
            "title": "Used for invoices",
            "default": false,
            "x-cis-field": {}
          },
          "name": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Name",
            "x-cis-field": {
              "required": true
            }
          },
          "lines": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "title": "Address lines",
            "description": "One to four lines, none of them blank.",
            "default": [],
            "x-cis-field": {
              "required": true
            }
          },
          "town": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Town",
            "x-cis-field": {}
          },
          "county": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "County",
            "x-cis-field": {}
          },
          "postcode": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Postcode",
            "x-cis-field": {
              "required": true
            }
          },
          "country_code": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Country code",
            "description": "The two-letter country code, such as GB.",
            "x-cis-field": {
              "required": true
            }
          },
          "recipient_name": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Recipient name",
            "x-cis-field": {}
          },
          "recipient_phone": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Recipient phone",
            "x-cis-field": {}
          },
          "recipient_email": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Recipient email",
            "x-cis-field": {}
          },
          "delivery_instructions": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Delivery instructions",
            "x-cis-field": {}
          },
          "comment": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Comment",
            "x-cis-field": {}
          },
          "is_default_delivery": {
            "type": "boolean",
            "title": "Default delivery address",
            "default": false,
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddressFieldsRequest",
        "description": "An address's fields, as a person types them. The server trims and normalises them. On *Add address…*,\n``request_id`` becomes the address's id: a repeat returns the first address."
      }
    },
    {
      "id": "customers.update_customer",
      "module": "customers_module",
      "label": "Rename customer…",
      "description": "Rename a customer CIS masters.",
      "target": "customers.customer",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/cis-customers/{customer_code}/actions/update-customer",
      "dry_run_path": "/cis-customers/{customer_code}/actions/update-customer/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "name": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Name",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "UpdateCustomerRequest",
        "description": "*Rename customer…*. The name starts as the customer's own, from the dry run."
      }
    },
    {
      "id": "customers.reopen_customer",
      "module": "customers_module",
      "label": "Reopen customer",
      "description": "Reopen a closed customer CIS masters.",
      "target": "customers.customer",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/cis-customers/{customer_code}/actions/reopen-customer",
      "dry_run_path": "/cis-customers/{customer_code}/actions/reopen-customer/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "customers.close_customer",
      "module": "customers_module",
      "label": "Close customer",
      "description": "Close a customer CIS masters, stopping staff adding addresses to it; its addresses stay as they are.",
      "target": "customers.customer",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/cis-customers/{customer_code}/actions/close-customer",
      "dry_run_path": "/cis-customers/{customer_code}/actions/close-customer/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "customers.change_default_trading_company",
      "module": "customers_module",
      "label": "Change default trading company…",
      "description": "Choose which trading company a customer's new orders and quotes go to, on a customer from Tharstern or One alike; its open orders stay with the company they were placed under.",
      "target": "customers.customer",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/cis-customers/{customer_code}/actions/change-default-trading-company",
      "dry_run_path": "/cis-customers/{customer_code}/actions/change-default-trading-company/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "trading_company_code": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Default trading company",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ChangeDefaultTradingCompanyRequest",
        "description": "*Change default trading company…*. The dry run offers the trading companies that have not stopped, starting on the\ncustomer's current default; a dry run called with ``{}`` reads as that."
      },
      "edits": {
        "fields": [
          "default_trading_company"
        ]
      }
    },
    {
      "id": "customers.link_tharstern_customer",
      "module": "customers_module",
      "label": "Link to Tharstern…",
      "description": "Link a customer CIS masters to a live Tharstern customer, so the feed brings its Tharstern addresses in.",
      "target": "customers.customer",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Links or unlinks a customer's Tharstern record, changing its identity: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/cis-customers/{customer_code}/actions/link-tharstern-customer",
      "dry_run_path": "/cis-customers/{customer_code}/actions/link-tharstern-customer/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "tharstern_customer_code": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Tharstern customer code",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "LinkTharsternCustomerRequest",
        "description": "*Link to Tharstern…*. The code may be absent, so the first dry run checks the customer alone."
      }
    },
    {
      "id": "customers.unlink_tharstern_customer",
      "module": "customers_module",
      "label": "Unlink from Tharstern",
      "description": "Clear a customer's link to its Tharstern customer, which then returns as a new customer on the next feed run.",
      "target": "customers.customer",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Links or unlinks a customer's Tharstern record, changing its identity: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/cis-customers/{customer_code}/actions/unlink-tharstern-customer",
      "dry_run_path": "/cis-customers/{customer_code}/actions/unlink-tharstern-customer/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "customers.update_address",
      "module": "customers_module",
      "label": "Edit address…",
      "description": "Replace the fields of an address CIS masters; orders keep their own snapshot.",
      "target": "customers.address",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/cis-customers/{customer_code}/addresses/{address_id}/actions/update-address",
      "dry_run_path": "/cis-customers/{customer_code}/addresses/{address_id}/actions/update-address/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "is_delivery": {
            "type": "boolean",
            "title": "Used for deliveries",
            "default": true,
            "x-cis-field": {}
          },
          "is_invoice": {
            "type": "boolean",
            "title": "Used for invoices",
            "default": false,
            "x-cis-field": {}
          },
          "name": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Name",
            "x-cis-field": {
              "required": true
            }
          },
          "lines": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "title": "Address lines",
            "description": "One to four lines, none of them blank.",
            "default": [],
            "x-cis-field": {
              "required": true
            }
          },
          "town": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Town",
            "x-cis-field": {}
          },
          "county": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "County",
            "x-cis-field": {}
          },
          "postcode": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Postcode",
            "x-cis-field": {
              "required": true
            }
          },
          "country_code": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Country code",
            "description": "The two-letter country code, such as GB.",
            "x-cis-field": {
              "required": true
            }
          },
          "recipient_name": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Recipient name",
            "x-cis-field": {}
          },
          "recipient_phone": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Recipient phone",
            "x-cis-field": {}
          },
          "recipient_email": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Recipient email",
            "x-cis-field": {}
          },
          "delivery_instructions": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Delivery instructions",
            "x-cis-field": {}
          },
          "comment": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Comment",
            "x-cis-field": {}
          },
          "is_default_delivery": {
            "type": "boolean",
            "title": "Default delivery address",
            "default": false,
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddressFieldsRequest",
        "description": "An address's fields, as a person types them. The server trims and normalises them. On *Add address…*,\n``request_id`` becomes the address's id: a repeat returns the first address."
      }
    },
    {
      "id": "customers.reopen_address",
      "module": "customers_module",
      "label": "Reopen address",
      "description": "Reopen a closed address CIS masters, re-checking the one-invoice-address rule.",
      "target": "customers.address",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/cis-customers/{customer_code}/addresses/{address_id}/actions/reopen-address",
      "dry_run_path": "/cis-customers/{customer_code}/addresses/{address_id}/actions/reopen-address/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "customers.close_address",
      "module": "customers_module",
      "label": "Close address",
      "description": "Close an address CIS masters, clearing its default-delivery flag.",
      "target": "customers.address",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/cis-customers/{customer_code}/addresses/{address_id}/actions/close-address",
      "dry_run_path": "/cis-customers/{customer_code}/addresses/{address_id}/actions/close-address/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "dictionary.expire_cached_prices",
      "module": "dictionary_module",
      "label": "Expire cached prices…",
      "description": "Expire a route's cached prices, on one dictionary item or every item, so they are asked again at next use.",
      "target": "collection:dictionary.cached_price",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "It discards up to a month of in-house prices and makes the next prices slow, so a person decides it in the app.",
      "method": "POST",
      "path": "/dictionary/admin/cached-prices/actions/expire",
      "dry_run_path": "/dictionary/admin/cached-prices/actions/expire/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "route_code": {
            "type": "string",
            "maxLength": 64,
            "title": "Route",
            "description": "The route whose cached prices will be asked for again the next time each price is used. The dry run lists the routes to choose from.",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "dictionary_item_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 128
              },
              {
                "type": "null"
              }
            ],
            "title": "Dictionary item",
            "description": "Only this dictionary item's cached prices; empty for every item on the route.",
            "x-cis-field": {}
          },
          "reason": {
            "type": "string",
            "maxLength": 500,
            "title": "Reason",
            "description": "Why, e.g. 'Tharstern rates changed on 20 Oct'. Kept on the expiry's record; never logged.",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "confirmed": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 5,
            "title": "Confirmed",
            "description": "The dry run's confirmations ticked, by key: `expire_cached_prices`."
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ExpireCachedPricesRequest",
        "description": "Which route's cached prices to expire, on one item or every item, and why."
      }
    },
    {
      "id": "draft_estimates.create_draft_estimate",
      "module": "draft_estimates_module",
      "label": "Request merchandise quote…",
      "description": "Request a merchandise quote for a customer, creating a merchandise request and raising a ticket to the merchandise team, whose Promo Office quote attaches to the merchandise request by its reference; a repeat of the same request returns the first merchandise request.",
      "target": "collection:draft_estimates.draft_estimate",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/draft-estimates/actions/request-merchandise-quote",
      "dry_run_path": "/draft-estimates/actions/request-merchandise-quote/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "customer_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "description": "The customer number, for example CUS-K48193. The customer must be linked to Tharstern.",
            "x-cis-field": {
              "entity": "customers.customer",
              "required": true,
              "widget": "entity"
            }
          },
          "project_name": {
            "type": "string",
            "maxLength": 200,
            "title": "Project name",
            "description": "The project's name, shared with Promo Office.",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "deadline": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Deadline",
            "description": "When the whole request is needed by.",
            "x-cis-field": {
              "min_date": "today",
              "required": true
            }
          },
          "notes": {
            "type": "string",
            "maxLength": 5000,
            "title": "Notes",
            "description": "Anything the merchandise team should know.",
            "default": "",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "catalogue_item_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100
              },
              {
                "type": "null"
              }
            ],
            "title": "Catalogue item",
            "description": "The quoted-to-order catalogue item the request is about. Set when the request is opened from the item; it starts the first request line with the item's name and description.",
            "x-cis-field": {
              "entity": "catalogue.catalogue_item",
              "widget": "entity"
            }
          },
          "lines": {
            "items": {
              "properties": {
                "item_type": {
                  "type": "string",
                  "maxLength": 500,
                  "title": "Item",
                  "description": "For example, ceramic mug, white, 300ml.",
                  "default": "",
                  "x-cis-field": {}
                },
                "quantity": {
                  "type": "string",
                  "maxLength": 200,
                  "title": "Quantity",
                  "description": "Enter one number, or the price breaks to quote, such as 100 / 250 / 500.",
                  "default": "",
                  "x-cis-field": {}
                },
                "logo_colours": {
                  "type": "string",
                  "maxLength": 1000,
                  "title": "Logo colours",
                  "description": "Colours or Pantone references, or \"None\".",
                  "default": "",
                  "x-cis-field": {}
                },
                "budget": {
                  "type": "string",
                  "maxLength": 200,
                  "title": "Budget (optional)",
                  "description": "For example, £2000, or under £1000.",
                  "default": "",
                  "x-cis-field": {}
                },
                "sourcing_city_code": {
                  "type": "string",
                  "maxLength": 100,
                  "title": "Sourcing City code (optional)",
                  "description": "The product ID in Promo Office's catalogue.",
                  "default": "",
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "title": "MerchandiseRequestLineRow",
              "description": "One request line of *Request merchandise quote…*: what the customer wants quoted. Blank text is accepted here\nand refused by the action as field errors (``lines.0.item_type`` and so on)."
            },
            "type": "array",
            "maxItems": 50,
            "title": "Request lines",
            "description": "One line per item the customer wants quoted.",
            "x-cis-field": {
              "add_rows": "Add request line",
              "required": true,
              "widget": "table"
            }
          },
          "delivery_mode": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "ship",
                  "collect",
                  "multi",
                  "mailing"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Delivery",
            "description": "The merchandise team quotes carriage on the destination.",
            "x-cis-field": {
              "enum_labels": {
                "collect": "Collect",
                "mailing": "Mailing",
                "multi": "Multi-destination",
                "ship": "Ship"
              },
              "widget": "select"
            }
          },
          "delivery_notes": {
            "type": "string",
            "maxLength": 2000,
            "title": "Delivery notes",
            "description": "For example, deliver to reception.",
            "default": "",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RequestMerchandiseQuoteRequest",
        "description": "*Request merchandise quote…*: the customer's request to the merchandise team, who quote it in Promo Office.\nCreates a merchandise request and raises a ticket to the merchandising department. Absent fields are refused by the\naction as field errors; the dry run answers whatever is typed so far. ``request_id`` becomes the merchandise request's id:\na repeat returns the first merchandise request and raises no second ticket."
      }
    },
    {
      "id": "draft_estimates.attach_draft_estimate_file",
      "module": "draft_estimates_module",
      "label": "Add logo…",
      "description": "Add a logo to one request line of a merchandise request, for the merchandise team to quote from; a repeat of the same request adds nothing.",
      "target": "draft_estimates.draft_estimate",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Multipart file upload: done in the app.",
      "method": "POST",
      "path": "/draft-estimates/{reference}/actions/add-logo",
      "dry_run_path": "/draft-estimates/{reference}/actions/add-logo/dry-run",
      "content_type": "multipart",
      "request_schema": {
        "properties": {
          "file": {
            "anyOf": [
              {
                "type": "string",
                "contentMediaType": "application/octet-stream"
              },
              {
                "type": "null"
              }
            ],
            "title": "Logo",
            "description": "A PDF, PostScript, TIFF, JPEG or PNG file of at most 100 MB.",
            "x-cis-field": {
              "required": true,
              "widget": "file"
            }
          },
          "request_line_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Request line",
            "description": "Chosen from the dry run's choice.",
            "x-cis-field": {
              "required": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddLogoForm",
        "description": "*Add logo…*: the multipart body of ``POST /draft-estimates/{reference}/actions/add-logo`` and its dry run.\nThe file and the request line are optional here so the dry run answers before either is chosen (it offers the\nrequest lines); the commit refuses a missing one as a field error. ``request_id`` becomes the file's id."
      }
    },
    {
      "id": "enrichment.suppress_prospect",
      "module": "enrichment_module",
      "label": "Suppress…",
      "description": "Mark suggested prospects as bad suggestions; each is removed and never suggested again for its customer.",
      "target": "enrichment.prospect_contact",
      "destructive": true,
      "bulk": true,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/enrichment/prospects/actions/suppress",
      "dry_run_path": "/enrichment/prospects/actions/suppress/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "targets": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 50,
            "minItems": 1,
            "title": "Targets",
            "description": "The prospects to suppress, by id (at most 50)."
          },
          "reason_note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Why is it a bad suggestion?",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "targets"
        ],
        "title": "SuppressProspectsRequest",
        "description": "*Suppress…*: the prospects to suppress (at most 50) and why. The commit and the dry run take this body."
      }
    },
    {
      "id": "feedback.send_quick_note",
      "module": "feedback_module",
      "label": "Send quick note",
      "description": "Send a quick note with the page it was raised on, its recent errors and images; any signed-in person. Production keeps every quick note, so an environment that is not the store refuses it and sends the person to One Feedback.",
      "target": "collection:feedback.feedback_item",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/feedback/quick-notes",
      "dry_run_path": "/feedback/quick-notes/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 10000
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "image_ids": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 10,
            "title": "Images",
            "description": "The ids the image upload answered, at most 10.",
            "default": [],
            "x-cis-field": {
              "hidden": true
            }
          },
          "environment": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 40
              },
              {
                "type": "null"
              }
            ],
            "title": "Environment",
            "x-cis-field": {
              "hidden": true
            }
          },
          "build_sha": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Build",
            "x-cis-field": {
              "hidden": true
            }
          },
          "preview_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100
              },
              {
                "type": "null"
              }
            ],
            "title": "Preview",
            "x-cis-field": {
              "hidden": true
            }
          },
          "page_path": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 1000
              },
              {
                "type": "null"
              }
            ],
            "title": "Page",
            "x-cis-field": {
              "hidden": true
            }
          },
          "object_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100
              },
              {
                "type": "null"
              }
            ],
            "title": "Object",
            "x-cis-field": {
              "hidden": true
            }
          },
          "record_key": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Record",
            "x-cis-field": {
              "hidden": true
            }
          },
          "viewport": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 40
              },
              {
                "type": "null"
              }
            ],
            "title": "Viewport",
            "x-cis-field": {
              "hidden": true
            }
          },
          "recent_errors": {
            "items": {
              "properties": {
                "kind": {
                  "type": "string",
                  "enum": [
                    "api",
                    "console"
                  ],
                  "title": "Kind",
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "method": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Method",
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "path": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 500
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Path",
                  "description": "The path template, e.g. /orders/{order_no}.",
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "http_status": {
                  "anyOf": [
                    {
                      "type": "integer",
                      "maximum": 999.0,
                      "minimum": 0.0
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Status",
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "trace_id": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 64
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Trace",
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "message": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 500
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Message",
                  "x-cis-field": {
                    "hidden": true
                  }
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "kind"
              ],
              "title": "RecentErrorSchema",
              "description": "A failed API call or a console error the panel saw before the note was sent: never a body or a query string.\nThe panel fills these; the dialog never shows them."
            },
            "type": "array",
            "title": "Recent errors",
            "default": [],
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SendQuickNoteRequest",
        "description": "Send quick note: the note, and what the quick-capture panel captured with it. The panel fills the context and\nuploads the images first (``POST /feedback/quick-notes/images``), so everything but the note is hidden."
      }
    },
    {
      "id": "feedback.record_feedback_for",
      "module": "feedback_module",
      "label": "Record feedback for…",
      "description": "Record a point raised outside One (a document, a pasted message) as the chosen person's own quick note; admins and engineers.",
      "target": "collection:feedback.feedback_item",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/feedback/items/actions/record-for",
      "dry_run_path": "/feedback/items/actions/record-for/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "user_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Raised by",
            "description": "The person who raised the point: it will show under their My feedback and they will confirm the fix.",
            "x-cis-field": {
              "entity": "users.user",
              "required": true,
              "widget": "entity"
            }
          },
          "note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 10000
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "description": "The point as the person raised it, in their words.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "source": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Where it came from",
            "description": "e.g. Sheldon's SRM review doc, 23 Oct.",
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordFeedbackForRequest",
        "description": "Record feedback for…: a point raised outside One, entered as the chosen person's own quick note."
      }
    },
    {
      "id": "feedback.triage_feedback",
      "module": "feedback_module",
      "label": "Triage…",
      "description": "Set what feedback is and its stream (and package), moving it to Ready to plan, or to Awaiting ruling for a product decision.",
      "target": "feedback.feedback_item",
      "destructive": false,
      "bulk": true,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/feedback/items/actions/triage",
      "dry_run_path": "/feedback/items/actions/triage/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "targets": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 50,
            "minItems": 1,
            "title": "Targets",
            "description": "The ids of the feedback items this command acts on (at most 50)."
          },
          "group": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "bug",
                  "platform",
                  "declaration",
                  "product_decision",
                  "explain",
                  "not_a_fact",
                  "other_stream",
                  "not_for_one",
                  "watch",
                  "environment"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Group",
            "x-cis-field": {
              "enum_labels": {
                "bug": "Bug",
                "declaration": "Declaration",
                "environment": "Environment set-up",
                "explain": "Explain",
                "not_a_fact": "Not a fact",
                "not_for_one": "Not for One",
                "other_stream": "Other stream",
                "platform": "Platform",
                "product_decision": "Product decision",
                "watch": "Potential gap to watch"
              },
              "required": true
            }
          },
          "stream": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100
              },
              {
                "type": "null"
              }
            ],
            "title": "Stream",
            "x-cis-field": {
              "required": true
            }
          },
          "package": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100
              },
              {
                "type": "null"
              }
            ],
            "title": "Package",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "targets"
        ],
        "title": "TriageFeedbackRequest",
        "description": "Triage…: what the feedback is, its stream and, optionally, its package."
      },
      "main": true
    },
    {
      "id": "feedback.rule_on_feedback",
      "module": "feedback_module",
      "label": "Rule…",
      "description": "Record Ben's ruling on feedback word for word, then plan the feedback or close it with a reply.",
      "target": "feedback.feedback_item",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/feedback/items/{feedback_id}/actions/rule",
      "dry_run_path": "/feedback/items/{feedback_id}/actions/rule/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "ruling": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 10000
              },
              {
                "type": "null"
              }
            ],
            "title": "Ruling",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "outcome": {
            "type": "string",
            "enum": [
              "ready_to_plan",
              "closed"
            ],
            "title": "Then",
            "default": "ready_to_plan",
            "x-cis-field": {
              "enum_labels": {
                "closed": "Close with a reply",
                "ready_to_plan": "Plan the feedback"
              },
              "widget": "radio"
            }
          },
          "reply": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 10000
              },
              {
                "type": "null"
              }
            ],
            "title": "Reply",
            "description": "What the person who raised the feedback reads.",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "outcome": "closed"
              },
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RuleOnFeedbackRequest",
        "description": "Rule…: the ruling word for word, then plan the item or close it with a reply."
      }
    },
    {
      "id": "feedback.put_feedback_in_package",
      "module": "feedback_module",
      "label": "Put in package…",
      "description": "Put feedback in a package, the named group of items one worker delivers together.",
      "target": "feedback.feedback_item",
      "destructive": false,
      "bulk": true,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/feedback/items/actions/put-in-package",
      "dry_run_path": "/feedback/items/actions/put-in-package/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "targets": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 50,
            "minItems": 1,
            "title": "Targets",
            "description": "The ids of the feedback items this command acts on (at most 50)."
          },
          "package": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100
              },
              {
                "type": "null"
              }
            ],
            "title": "Package",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "targets"
        ],
        "title": "PutFeedbackInPackageRequest",
        "description": "Put in package…: the package's name."
      }
    },
    {
      "id": "feedback.link_feedback_pr",
      "module": "feedback_module",
      "label": "Link PR…",
      "description": "Link the pull request that fixes feedback, moving the feedback to In build.",
      "target": "feedback.feedback_item",
      "destructive": false,
      "bulk": true,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/feedback/items/actions/link-pr",
      "dry_run_path": "/feedback/items/actions/link-pr/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "targets": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 50,
            "minItems": 1,
            "title": "Targets",
            "description": "The ids of the feedback items this command acts on (at most 50)."
          },
          "pull_request": {
            "anyOf": [
              {
                "type": "integer",
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Pull request",
            "description": "The pull request's number, e.g. 3472.",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "targets"
        ],
        "title": "LinkFeedbackPrRequest",
        "description": "Link PR…: the pull request's number."
      }
    },
    {
      "id": "feedback.mark_feedback_ready_to_retest",
      "module": "feedback_module",
      "label": "Ready to re-test",
      "description": "Mark feedback in build ready to re-test once its pull request has merged and reached staging.",
      "target": "feedback.feedback_item",
      "destructive": false,
      "bulk": true,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/feedback/items/actions/ready-to-retest",
      "dry_run_path": "/feedback/items/actions/ready-to-retest/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "targets": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 50,
            "minItems": 1,
            "title": "Targets",
            "description": "The ids of the feedback items this command acts on (at most 50)."
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "targets"
        ],
        "title": "MarkFeedbackReadyToRetestRequest"
      }
    },
    {
      "id": "feedback.confirm_feedback_fixed",
      "module": "feedback_module",
      "label": "Confirm fixed",
      "description": "Close feedback ready to re-test; only the person who raised the feedback.",
      "target": "feedback.feedback_item",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/feedback/items/{feedback_id}/actions/confirm-fixed",
      "dry_run_path": "/feedback/items/{feedback_id}/actions/confirm-fixed/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "ConfirmFeedbackFixedRequest"
      }
    },
    {
      "id": "feedback.report_feedback_still_wrong",
      "module": "feedback_module",
      "label": "Still wrong…",
      "description": "Say what is still wrong with feedback ready to re-test or closed, sending it back to New marked reopened; only the person who raised it.",
      "target": "feedback.feedback_item",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/feedback/items/{feedback_id}/actions/still-wrong",
      "dry_run_path": "/feedback/items/{feedback_id}/actions/still-wrong/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 10000
              },
              {
                "type": "null"
              }
            ],
            "title": "What is still wrong",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "image_ids": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 10,
            "title": "Images",
            "default": [],
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ReportFeedbackStillWrongRequest",
        "description": "Still wrong…: what is still wrong, and any new images (uploaded first, as a quick note's)."
      }
    },
    {
      "id": "feedback.reply_and_close_feedback",
      "module": "feedback_module",
      "label": "Reply and close…",
      "description": "Answer the person who raised feedback and close the feedback (an explanation, or a won't-do).",
      "target": "feedback.feedback_item",
      "destructive": false,
      "bulk": true,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/feedback/items/actions/reply-and-close",
      "dry_run_path": "/feedback/items/actions/reply-and-close/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "targets": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 50,
            "minItems": 1,
            "title": "Targets",
            "description": "The ids of the feedback items this command acts on (at most 50)."
          },
          "reply": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 10000
              },
              {
                "type": "null"
              }
            ],
            "title": "Reply",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "targets"
        ],
        "title": "ReplyAndCloseFeedbackRequest",
        "description": "Reply and close…: the answer the person who raised the feedback reads."
      }
    },
    {
      "id": "feedback.mark_feedback_duplicate",
      "module": "feedback_module",
      "label": "Mark duplicate…",
      "description": "Mark feedback a duplicate of a lead feedback item, whose state and reply it shows from then on.",
      "target": "feedback.feedback_item",
      "destructive": false,
      "bulk": true,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/feedback/items/actions/mark-duplicate",
      "dry_run_path": "/feedback/items/actions/mark-duplicate/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "targets": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 50,
            "minItems": 1,
            "title": "Targets",
            "description": "The ids of the feedback items this command acts on (at most 50)."
          },
          "duplicate_of": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Lead feedback",
            "x-cis-field": {
              "entity": "feedback.feedback_item",
              "required": true,
              "widget": "entity"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "targets"
        ],
        "title": "MarkFeedbackDuplicateRequest",
        "description": "Mark duplicate…: the lead feedback item these follow."
      }
    },
    {
      "id": "finance_accruals_and_prepayments.post_to_xero",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Post to Xero…",
      "description": "Post the month's journals to the trading company's Xero organisation, one by one.",
      "target": "finance_accruals_and_prepayments.month_end",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Posts to, or closes books in, Xero: done in the app, by finance.",
      "method": "POST",
      "path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/post-to-xero",
      "dry_run_path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/post-to-xero/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expected_version": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Month end version at preview",
            "description": "The month end's version the dry run showed: a change since then is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "PostToXeroRequest"
      }
    },
    {
      "id": "finance_accruals_and_prepayments.retry_posting",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Retry posting",
      "description": "Post the journals that did not reach Xero, starting with the one that failed. A journal already in Xero is never posted twice.",
      "target": "finance_accruals_and_prepayments.month_end",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Posts to, or closes books in, Xero: done in the app, by finance.",
      "method": "POST",
      "path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/retry-posting",
      "dry_run_path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/retry-posting/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "RetryPostingRequest",
        "description": "Retrying takes nothing: the month end names the posting, and a journal already in Xero is never sent again."
      }
    },
    {
      "id": "finance_accruals_and_prepayments.abandon_posting",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Abandon posting…",
      "description": "Stop a failed posting and take the month back to review, so it can be fixed and posted again. What already reached Xero stays there.",
      "target": "finance_accruals_and_prepayments.month_end",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Posts to, or closes books in, Xero: done in the app, by finance.",
      "method": "POST",
      "path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/abandon-posting",
      "dry_run_path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/abandon-posting/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "maxLength": 2000,
            "title": "Why",
            "description": "Why the posting is being abandoned.",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AbandonPostingRequest"
      }
    },
    {
      "id": "finance_accruals_and_prepayments.reconcile_with_xero",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Reconcile with Xero",
      "description": "Check each control account against the trading company's Xero organisation, to the penny, and keep the result with its time.",
      "target": "finance_accruals_and_prepayments.month_end",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Posts to, or closes books in, Xero: done in the app, by finance.",
      "method": "POST",
      "path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/reconcile-with-xero",
      "dry_run_path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/reconcile-with-xero/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "ReconcileWithXeroRequest",
        "description": "Reconciling takes nothing: the month end names the company and the month."
      }
    },
    {
      "id": "finance_accruals_and_prepayments.close_month",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Close month…",
      "description": "Lock the month once its journals are in Xero and every control account agrees or is explained.",
      "target": "finance_accruals_and_prepayments.month_end",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Posts to, or closes books in, Xero: done in the app, by finance.",
      "method": "POST",
      "path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/close-month",
      "dry_run_path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/close-month/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "CloseMonthRequest",
        "description": "Closing takes nothing: it closes on the latest reconciliation."
      }
    },
    {
      "id": "finance_accruals_and_prepayments.reopen_month",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Reopen…",
      "description": "Reopen a posted or closed month to correct it. The posting is kept; the new posting reverses it.",
      "target": "finance_accruals_and_prepayments.month_end",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Posts to, or closes books in, Xero: done in the app, by finance.",
      "method": "POST",
      "path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/reopen-month",
      "dry_run_path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/reopen-month/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "maxLength": 2000,
            "title": "Why",
            "description": "Why the month is being reopened.",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ReopenMonthRequest"
      }
    },
    {
      "id": "finance_accruals_and_prepayments.run_proposal",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Run proposal",
      "description": "Propose the month's lines now, rather than waiting for the first morning after the month ends.",
      "target": "finance_accruals_and_prepayments.month_end",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/run-proposal",
      "dry_run_path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/run-proposal/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "finance_accruals_and_prepayments.refresh_proposal",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Refresh proposal",
      "description": "Read the month's sources again. Decisions already made, and manual lines, are kept.",
      "target": "finance_accruals_and_prepayments.month_end",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/refresh-proposal",
      "dry_run_path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/refresh-proposal/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "finance_accruals_and_prepayments.mark_reviewed",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Mark reviewed…",
      "description": "Record that every line has been decided, so the journals can be posted.",
      "target": "finance_accruals_and_prepayments.month_end",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/mark-reviewed",
      "dry_run_path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/mark-reviewed/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expected_version": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Month end version",
            "description": "The version the dry run showed (its guard), echoed so a change since is refused (409 stale).",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "MarkReviewedRequest"
      }
    },
    {
      "id": "finance_accruals_and_prepayments.add_manual_line",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Add manual line…",
      "description": "Add a line for something no data knows about. It reverses on the first of the next month like every accrual.",
      "target": "finance_accruals_and_prepayments.month_end",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/add-manual-line",
      "dry_run_path": "/accruals-and-prepayments/month-ends/{month_end_id}/actions/add-manual-line/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "books_to": {
            "type": "string",
            "enum": [
              "accrued_expense",
              "accrued_income",
              "deferred_income"
            ],
            "title": "Books to",
            "description": "Which schedule the line goes on; fixed once it is added.",
            "default": "accrued_expense",
            "x-cis-field": {
              "enum_labels": {
                "accrued_expense": "Accrued expense",
                "accrued_income": "Accrued income",
                "deferred_income": "Deferred income"
              },
              "required": true
            }
          },
          "supplier": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Supplier",
            "description": "The supplier the cost is owed to, by supplier number. Leave empty for a one-off supplier.",
            "x-cis-field": {
              "entity": "suppliers.supplier",
              "entity_value": "supplier_no",
              "show_if": {
                "books_to": "accrued_expense"
              },
              "widget": "entity"
            }
          },
          "one_off_name": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "One-off supplier",
            "description": "The name of a supplier not on the supplier list (a card spend, say), when no supplier is chosen.",
            "x-cis-field": {
              "show_if": {
                "books_to": "accrued_expense"
              }
            }
          },
          "customer": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "description": "The customer the income is from.",
            "x-cis-field": {
              "entity": "customers.customer",
              "entity_value": "id",
              "show_if": {
                "books_to": [
                  "accrued_income",
                  "deferred_income"
                ]
              },
              "widget": "entity"
            }
          },
          "description": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Description",
            "description": "What the line accrues or defers.",
            "x-cis-field": {
              "required": true
            }
          },
          "account": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 20
              },
              {
                "type": "null"
              }
            ],
            "title": "Xero account",
            "description": "The Xero account the line is coded to; the dry run offers the accounts its side may code to.",
            "x-cis-field": {
              "required": true
            }
          },
          "amount_pence": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 1000000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Amount",
            "description": "The amount, greater than zero.",
            "x-cis-field": {
              "required": true,
              "widget": "money"
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 2000
              },
              {
                "type": "null"
              }
            ],
            "title": "Why",
            "description": "Every change from what the rules proposed carries a reason, kept on the month end's record.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "search": {
            "anyOf": [
              {
                "additionalProperties": {
                  "type": "string"
                },
                "type": "object"
              },
              {
                "type": "null"
              }
            ],
            "title": "Search",
            "description": "What was typed in the account search box; the dry run answers it and the commit ignores it.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddManualLineRequest"
      }
    },
    {
      "id": "finance_accruals_and_prepayments.keep_out_month_end_line",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Keep out…",
      "description": "Confirm a line the rules left out stays out of this month, with a reason. It is proposed again next month if it still qualifies.",
      "target": "finance_accruals_and_prepayments.month_end_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/accruals-and-prepayments/month-ends/{month_end_id}/lines/{line_key}/actions/keep-out",
      "dry_run_path": "/accruals-and-prepayments/month-ends/{month_end_id}/lines/{line_key}/actions/keep-out/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 2000
              },
              {
                "type": "null"
              }
            ],
            "title": "Why it stays out",
            "description": "Every change from what the rules proposed carries a reason, kept on the month end's record.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "KeepOutRequest"
      }
    },
    {
      "id": "finance_accruals_and_prepayments.include_month_end_line",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Include…",
      "description": "Post a line the rules left out, with a reason.",
      "target": "finance_accruals_and_prepayments.month_end_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/accruals-and-prepayments/month-ends/{month_end_id}/lines/{line_key}/actions/include",
      "dry_run_path": "/accruals-and-prepayments/month-ends/{month_end_id}/lines/{line_key}/actions/include/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 2000
              },
              {
                "type": "null"
              }
            ],
            "title": "Why",
            "description": "Every change from what the rules proposed carries a reason, kept on the month end's record.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "LineReasonRequest",
        "description": "Keep out…, Include…, Leave out… and Remove line…: the reason alone."
      }
    },
    {
      "id": "finance_accruals_and_prepayments.leave_out_month_end_line",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Leave out…",
      "description": "Leave out a line the rules included, with a reason.",
      "target": "finance_accruals_and_prepayments.month_end_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/accruals-and-prepayments/month-ends/{month_end_id}/lines/{line_key}/actions/leave-out",
      "dry_run_path": "/accruals-and-prepayments/month-ends/{month_end_id}/lines/{line_key}/actions/leave-out/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 2000
              },
              {
                "type": "null"
              }
            ],
            "title": "Why",
            "description": "Every change from what the rules proposed carries a reason, kept on the month end's record.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "LineReasonRequest",
        "description": "Keep out…, Include…, Leave out… and Remove line…: the reason alone."
      }
    },
    {
      "id": "finance_accruals_and_prepayments.edit_month_end_line_amount",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Edit amount…",
      "description": "Change the amount a line books, with a reason. The proposed amount is kept beside it.",
      "target": "finance_accruals_and_prepayments.month_end_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/accruals-and-prepayments/month-ends/{month_end_id}/lines/{line_key}/actions/edit-amount",
      "dry_run_path": "/accruals-and-prepayments/month-ends/{month_end_id}/lines/{line_key}/actions/edit-amount/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "amount_pence": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 1000000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Amount to book",
            "description": "The amount the line books instead of what the rules proposed.",
            "x-cis-field": {
              "required": true,
              "widget": "money"
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 2000
              },
              {
                "type": "null"
              }
            ],
            "title": "Why",
            "description": "Every change from what the rules proposed carries a reason, kept on the month end's record.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "EditLineAmountRequest"
      },
      "edits": {
        "fields": [
          "amount_pence"
        ]
      }
    },
    {
      "id": "finance_accruals_and_prepayments.edit_manual_month_end_line",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Edit line…",
      "description": "Change a manual line's supplier or customer, description, account or amount, with a reason. What changed and why stay on the month's record.",
      "target": "finance_accruals_and_prepayments.month_end_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/accruals-and-prepayments/month-ends/{month_end_id}/lines/{line_key}/actions/edit-line",
      "dry_run_path": "/accruals-and-prepayments/month-ends/{month_end_id}/lines/{line_key}/actions/edit-line/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "books_to": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "accrued_expense",
                  "accrued_income",
                  "deferred_income"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Books to",
            "description": "Which schedule the line is on, filled by the dry run so the right fields show. It cannot change.",
            "x-cis-field": {
              "enum_labels": {
                "accrued_expense": "Accrued expense",
                "accrued_income": "Accrued income",
                "deferred_income": "Deferred income"
              },
              "hidden": true
            }
          },
          "supplier": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Supplier",
            "description": "The supplier the cost is owed to, by supplier number. Leave empty for a one-off supplier.",
            "x-cis-field": {
              "entity": "suppliers.supplier",
              "entity_value": "supplier_no",
              "show_if": {
                "books_to": "accrued_expense"
              },
              "widget": "entity"
            }
          },
          "one_off_name": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "One-off supplier",
            "description": "The name of a supplier not on the supplier list, when no supplier is chosen.",
            "x-cis-field": {
              "show_if": {
                "books_to": "accrued_expense"
              }
            }
          },
          "customer": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "description": "The customer the income is from.",
            "x-cis-field": {
              "entity": "customers.customer",
              "entity_value": "id",
              "show_if": {
                "books_to": [
                  "accrued_income",
                  "deferred_income"
                ]
              },
              "widget": "entity"
            }
          },
          "description": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Description",
            "description": "What the line accrues or defers.",
            "x-cis-field": {
              "required": true
            }
          },
          "account": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 20
              },
              {
                "type": "null"
              }
            ],
            "title": "Xero account",
            "description": "The Xero account the line is coded to.",
            "x-cis-field": {
              "required": true
            }
          },
          "amount_pence": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 1000000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Amount",
            "description": "The amount, greater than zero.",
            "x-cis-field": {
              "required": true,
              "widget": "money"
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 2000
              },
              {
                "type": "null"
              }
            ],
            "title": "Why",
            "description": "Every change from what the rules proposed carries a reason, kept on the month end's record.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "search": {
            "anyOf": [
              {
                "additionalProperties": {
                  "type": "string"
                },
                "type": "object"
              },
              {
                "type": "null"
              }
            ],
            "title": "Search",
            "description": "What was typed in the account search box; the dry run answers it and the commit ignores it.",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "EditManualLineRequest",
        "description": "*Edit line…*: the line's own values are the dry run's prefill; what differs is what changes."
      }
    },
    {
      "id": "finance_accruals_and_prepayments.remove_manual_month_end_line",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Remove line…",
      "description": "Remove a manual line before it posts. The line and why it was removed stay on the month's record.",
      "target": "finance_accruals_and_prepayments.month_end_line",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/accruals-and-prepayments/month-ends/{month_end_id}/lines/{line_key}/actions/remove-line",
      "dry_run_path": "/accruals-and-prepayments/month-ends/{month_end_id}/lines/{line_key}/actions/remove-line/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 2000
              },
              {
                "type": "null"
              }
            ],
            "title": "Why",
            "description": "Every change from what the rules proposed carries a reason, kept on the month end's record.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "LineReasonRequest",
        "description": "Keep out…, Include…, Leave out… and Remove line…: the reason alone."
      }
    },
    {
      "id": "finance_accruals_and_prepayments.end_prepayment_early",
      "module": "finance_accruals_and_prepayments_module",
      "label": "End early…",
      "description": "Release whatever is left in full in a month you choose, for example when the cover is cancelled.",
      "target": "finance_accruals_and_prepayments.prepayment",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/accruals-and-prepayments/prepayments/{prepayment_id}/actions/end-early",
      "dry_run_path": "/accruals-and-prepayments/prepayments/{prepayment_id}/actions/end-early/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "ends_in": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 7
              },
              {
                "type": "null"
              }
            ],
            "title": "Ends in",
            "description": "The month whatever is left releases in full: an open month inside the cover, before its last.",
            "x-cis-field": {
              "hidden": true,
              "required": true
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 2000
              },
              {
                "type": "null"
              }
            ],
            "title": "Why",
            "description": "Every change carries a reason, kept on the record.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "EndPrepaymentEarlyRequest"
      }
    },
    {
      "id": "finance_accruals_and_prepayments.change_recurring_estimate",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Change estimate…",
      "description": "Change the annual estimate a recurring accrual accrues against, with a reason.",
      "target": "finance_accruals_and_prepayments.recurring_accrual",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/accruals-and-prepayments/recurring-accruals/{recurring_accrual_id}/actions/change-estimate",
      "dry_run_path": "/accruals-and-prepayments/recurring-accruals/{recurring_accrual_id}/actions/change-estimate/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "annual_estimate_pence": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 1000000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "New annual estimate",
            "description": "What the recurring accrual accrues against each year, in pounds.",
            "x-cis-field": {
              "required": true,
              "widget": "money"
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 2000
              },
              {
                "type": "null"
              }
            ],
            "title": "Why",
            "description": "Every change carries a reason, kept on the record.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ChangeRecurringEstimateRequest"
      },
      "edits": {
        "fields": [
          "annual_estimate_pence"
        ]
      }
    },
    {
      "id": "finance_accruals_and_prepayments.stop_recurring_accrual",
      "module": "finance_accruals_and_prepayments_module",
      "label": "Stop…",
      "description": "Stop a recurring accrual from the next month end.",
      "target": "finance_accruals_and_prepayments.recurring_accrual",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/accruals-and-prepayments/recurring-accruals/{recurring_accrual_id}/actions/stop",
      "dry_run_path": "/accruals-and-prepayments/recurring-accruals/{recurring_accrual_id}/actions/stop/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 2000
              },
              {
                "type": "null"
              }
            ],
            "title": "Why",
            "description": "Every change carries a reason, kept on the record.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "StopRecurringAccrualRequest"
      }
    },
    {
      "id": "finance_accruals_and_prepayments.create_recurring_accrual",
      "module": "finance_accruals_and_prepayments_module",
      "label": "New recurring accrual…",
      "description": "Accrue a regular cost before its purchase invoice arrives: months elapsed ÷ 12 of the annual estimate each month end.",
      "target": "collection:finance_accruals_and_prepayments.recurring_accrual",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/accruals-and-prepayments/recurring-accruals/actions/create",
      "dry_run_path": "/accruals-and-prepayments/recurring-accruals/actions/create/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "trading_company_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 3
              },
              {
                "type": "null"
              }
            ],
            "title": "Trading company",
            "description": "The trading company whose month ends it accrues in: one that is setting up or trading.",
            "x-cis-field": {
              "hidden": true,
              "required": true
            }
          },
          "name": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "What the cost is",
            "description": "The cost it accrues, e.g. Audit fee share, year to 31 March 2027.",
            "x-cis-field": {
              "required": true
            }
          },
          "account": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 20
              },
              {
                "type": "null"
              }
            ],
            "title": "Xero account",
            "description": "The Xero account the accrual is coded to; the dry run offers the trading company's cost accounts.",
            "x-cis-field": {
              "required": true
            }
          },
          "annual_estimate_pence": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 1000000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Annual estimate",
            "description": "What the cost comes to in a year, in pounds. Each month end accrues months elapsed ÷ 12 of it.",
            "x-cis-field": {
              "required": true,
              "widget": "money"
            }
          },
          "first_month": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 7
              },
              {
                "type": "null"
              }
            ],
            "title": "First month",
            "description": "The first month end it accrues at: the trading company's first open month or one of the next eleven.",
            "x-cis-field": {
              "hidden": true,
              "required": true
            }
          },
          "supplier": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Stop when a purchase invoice arrives from",
            "description": "Its confirmed purchase invoice stops the accrual. Leave empty to stop it by hand.",
            "x-cis-field": {
              "entity": "suppliers.supplier",
              "entity_value": "supplier_no",
              "widget": "entity"
            }
          },
          "search": {
            "anyOf": [
              {
                "additionalProperties": {
                  "type": "string"
                },
                "type": "object"
              },
              {
                "type": "null"
              }
            ],
            "title": "Search",
            "description": "What was typed in the account search box; the dry run answers it and the commit ignores it.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "CreateRecurringAccrualRequest"
      }
    },
    {
      "id": "finance_credit_control.resolve_credit_holds",
      "module": "finance_credit_control_module",
      "label": "Decide waiting orders…",
      "description": "Finance decides the orders waiting on a payer's credit, by releasing, proof only, releasing without credit or refusing.",
      "target": "finance_credit_control.billing_account",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/credit-control/billing-accounts/{ref}/holds/resolve",
      "dry_run_path": "/credit-control/billing-accounts/{ref}/holds/resolve/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "decisions": {
            "items": {
              "properties": {
                "hold_id": {
                  "type": "string",
                  "title": "Hold",
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "order": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10000
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Order",
                  "description": "The order waiting for a decision.",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "amount_pence": {
                  "anyOf": [
                    {
                      "type": "integer"
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "This order",
                  "description": "The order's value, excluding VAT.",
                  "readOnly": true,
                  "x-cis-field": {
                    "widget": "money"
                  }
                },
                "reason": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10000
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Held because",
                  "description": "Why the order is waiting.",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "to_status": {
                  "type": "string",
                  "enum": [
                    "release_to_proof_only",
                    "released",
                    "released_without_credit",
                    "refused"
                  ],
                  "title": "Decision",
                  "x-cis-field": {
                    "enum_labels": {
                      "refused": "Refuse",
                      "release_to_proof_only": "Proof only",
                      "released": "Release",
                      "released_without_credit": "Release without credit"
                    },
                    "widget": "select"
                  }
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "hold_id",
                "to_status"
              ],
              "title": "HoldDecisionRequest"
            },
            "type": "array",
            "maxItems": 500,
            "title": "Orders waiting",
            "x-cis-field": {
              "required": true,
              "selectable": true,
              "widget": "table"
            }
          },
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "description": "The note will be recorded with every decision.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ResolveHoldsRequest"
      }
    },
    {
      "id": "finance_credit_control.update_payment_settings",
      "module": "finance_credit_control_module",
      "label": "Change payment settings…",
      "description": "Finance changes a payer's payment methods, terms, PO rule, PO format and credit limit together, with one reason.",
      "target": "finance_credit_control.billing_account",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Sets whether a payer can order on account, with its terms and limit: payment settings are changed in the app, never by Claude.",
      "method": "POST",
      "path": "/credit-control/billing-accounts/{ref}/payment-settings",
      "dry_run_path": "/credit-control/billing-accounts/{ref}/payment-settings/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "payment_methods": {
            "items": {
              "type": "string",
              "enum": [
                "card",
                "proforma",
                "credit_account"
              ]
            },
            "type": "array",
            "title": "Pays by",
            "x-cis-field": {
              "enum_labels": {
                "card": "Card",
                "credit_account": "Credit account",
                "proforma": "Bank transfer (pro-forma)"
              },
              "required": true
            }
          },
          "terms": {
            "anyOf": [
              {
                "properties": {
                  "days": {
                    "type": "integer",
                    "title": "Days",
                    "x-cis-field": {
                      "widget": "number"
                    }
                  },
                  "basis": {
                    "type": "string",
                    "enum": [
                      "days_after_invoice_date",
                      "days_after_invoice_month_end",
                      "day_of_following_month",
                      "day_of_current_month"
                    ],
                    "title": "Counted from",
                    "description": "1:1 with Xero's DAYSAFTERBILLDATE / DAYSAFTERBILLMONTH / OFFOLLOWINGMONTH / OFCURRENTMONTH.",
                    "x-cis-field": {
                      "enum_labels": {
                        "day_of_current_month": "of the current month",
                        "day_of_following_month": "of the following month",
                        "days_after_invoice_date": "from invoice date",
                        "days_after_invoice_month_end": "from end of invoice month"
                      },
                      "widget": "select"
                    }
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "required": [
                  "days",
                  "basis"
                ],
                "title": "TermsRequest"
              },
              {
                "type": "null"
              }
            ],
            "title": "Payment terms",
            "description": "Needed when the customer pays by credit account.",
            "x-cis-field": {}
          },
          "po_rule": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "not_required",
                  "before_order",
                  "before_release"
                ],
                "title": "PoRule",
                "description": "When a payer's customer PO number must be on an order; finance sets it with credit account.\n\n``before_order``: checkout refuses an order without one. ``before_release`` (\"PO to follow\"): the\norder is accepted and its release waits for the number, a gate order to invoice owns. A later rule\n(before despatch) joins as one more value; \"before invoice\" was considered and rejected (4 Oct).\nWording is ``PO_RULE_LABELS`` in ``credit_wording``."
              },
              {
                "type": "null"
              }
            ],
            "title": "PO number",
            "description": "When the customer's PO number is needed.",
            "x-cis-field": {
              "enum_labels": {
                "before_order": "Before order",
                "before_release": "Before release (PO to follow)",
                "not_required": "Not required"
              },
              "required": true,
              "widget": "select"
            }
          },
          "po_format": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "PO format",
            "description": "What the customer's PO number must look like: # is one number, A one letter, ? one letter or number, and anything else is typed as it is (45######## is 45 then 8 numbers). Leave empty for no format. One keeps the format but won't check PO numbers against the format while no PO number is needed.",
            "x-cis-field": {
              "widget": "text"
            }
          },
          "credit_limit_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Credit limit",
            "description": "Needed when the customer pays by credit account.",
            "x-cis-field": {
              "widget": "money"
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "PaymentSettingsRequest",
        "description": "The payer's whole payment settings block, saved together."
      }
    },
    {
      "id": "finance_credit_control.lift_stop",
      "module": "finance_credit_control_module",
      "label": "Lift stop…",
      "description": "Finance lifts a payer's stop; orders already waiting stay for an explicit decision.",
      "target": "finance_credit_control.billing_account",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/credit-control/billing-accounts/{ref}/lift-stop",
      "dry_run_path": "/credit-control/billing-accounts/{ref}/lift-stop/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "description": "Why the stop is lifted. The note will be kept in the change log.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "LiftStopRequest"
      }
    },
    {
      "id": "finance_credit_control.stop_account",
      "module": "finance_credit_control_module",
      "label": "Put on stop…",
      "description": "Finance puts a payer's credit account on stop; new orders on credit account then wait for a decision.",
      "target": "finance_credit_control.billing_account",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/credit-control/billing-accounts/{ref}/stop",
      "dry_run_path": "/credit-control/billing-accounts/{ref}/stop/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "stop_reason": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "overdue",
                  "poor_history",
                  "insufficient_information",
                  "final_letter",
                  "limit_exceeded",
                  "insolvency",
                  "other"
                ],
                "title": "StopReason",
                "description": "Why a credit account is on stop or an order is held: the gate sets limit_exceeded itself, no rule sets the others."
              },
              {
                "type": "null"
              }
            ],
            "title": "Why",
            "description": "Why the account is stopped.",
            "x-cis-field": {
              "enum_labels": {
                "final_letter": "Final letter sent",
                "insolvency": "Insolvency",
                "insufficient_information": "Insufficient information",
                "limit_exceeded": "Limit exceeded",
                "other": "Other",
                "overdue": "Overdue",
                "poor_history": "Poor payment history"
              },
              "required": true,
              "widget": "select"
            }
          },
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "description": "Why the account is stopped. The note will be shown to staff and kept in the change log.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "StopAccountRequest"
      }
    },
    {
      "id": "finance_credit_control.create_billing_account",
      "module": "finance_credit_control_module",
      "label": "Add billing account…",
      "description": "Finance adds a payer's billing account, with how it pays, its terms and limit, and a reason.",
      "target": "collection:finance_credit_control.billing_account",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Sets whether a payer can order on account, with its terms and limit: payment settings are changed in the app, never by Claude.",
      "method": "POST",
      "path": "/credit-control/billing-accounts",
      "dry_run_path": "/credit-control/billing-accounts/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "customer_code": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "description": "The customer number (CUS-K48193) of the customer who pays.",
            "x-cis-field": {
              "entity": "customers.customer",
              "required": true,
              "widget": "entity"
            }
          },
          "payment_methods": {
            "items": {
              "type": "string",
              "enum": [
                "card",
                "proforma",
                "credit_account"
              ]
            },
            "type": "array",
            "title": "Pays by",
            "x-cis-field": {
              "enum_labels": {
                "card": "Card",
                "credit_account": "Credit account",
                "proforma": "Bank transfer (pro-forma)"
              },
              "required": true
            }
          },
          "terms": {
            "anyOf": [
              {
                "properties": {
                  "days": {
                    "type": "integer",
                    "title": "Days",
                    "x-cis-field": {
                      "widget": "number"
                    }
                  },
                  "basis": {
                    "type": "string",
                    "enum": [
                      "days_after_invoice_date",
                      "days_after_invoice_month_end",
                      "day_of_following_month",
                      "day_of_current_month"
                    ],
                    "title": "Counted from",
                    "description": "1:1 with Xero's DAYSAFTERBILLDATE / DAYSAFTERBILLMONTH / OFFOLLOWINGMONTH / OFCURRENTMONTH.",
                    "x-cis-field": {
                      "enum_labels": {
                        "day_of_current_month": "of the current month",
                        "day_of_following_month": "of the following month",
                        "days_after_invoice_date": "from invoice date",
                        "days_after_invoice_month_end": "from end of invoice month"
                      },
                      "widget": "select"
                    }
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "required": [
                  "days",
                  "basis"
                ],
                "title": "TermsRequest"
              },
              {
                "type": "null"
              }
            ],
            "title": "Payment terms",
            "description": "Needed when the customer pays by credit account.",
            "x-cis-field": {}
          },
          "po_rule": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "not_required",
                  "before_order",
                  "before_release"
                ],
                "title": "PoRule",
                "description": "When a payer's customer PO number must be on an order; finance sets it with credit account.\n\n``before_order``: checkout refuses an order without one. ``before_release`` (\"PO to follow\"): the\norder is accepted and its release waits for the number, a gate order to invoice owns. A later rule\n(before despatch) joins as one more value; \"before invoice\" was considered and rejected (4 Oct).\nWording is ``PO_RULE_LABELS`` in ``credit_wording``."
              },
              {
                "type": "null"
              }
            ],
            "title": "PO number",
            "description": "When the customer's PO number is needed.",
            "x-cis-field": {
              "enum_labels": {
                "before_order": "Before order",
                "before_release": "Before release (PO to follow)",
                "not_required": "Not required"
              },
              "widget": "select"
            }
          },
          "po_format": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "PO format",
            "description": "What the customer's PO number must look like: # is one number, A one letter, ? one letter or number, and anything else is typed as it is (45######## is 45 then 8 numbers). Leave empty for no format. One keeps the format but won't check PO numbers against the format while no PO number is needed.",
            "x-cis-field": {
              "widget": "text"
            }
          },
          "credit_limit_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Credit limit",
            "description": "Needed when the customer pays by credit account.",
            "x-cis-field": {
              "widget": "money"
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "CreateBillingAccountRequest"
      }
    },
    {
      "id": "finance_credit_control.import_billing_accounts",
      "module": "finance_credit_control_module",
      "label": "Import payment settings…",
      "description": "Finance sets many payers' payment settings from one CSV in the import template.",
      "target": "collection:finance_credit_control.billing_account",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Sets whether payers can order on account, with their terms and limits: payment settings are changed in the app, never by Claude.",
      "method": "POST",
      "path": "/credit-control/billing-accounts/import",
      "dry_run_path": "/credit-control/billing-accounts/import/dry-run",
      "content_type": "multipart",
      "request_schema": {
        "properties": {
          "file": {
            "type": "string",
            "contentMediaType": "application/octet-stream",
            "title": "File",
            "description": "The import template, saved as CSV (UTF-8).",
            "x-cis-field": {
              "accept": [
                "text/csv"
              ],
              "widget": "file"
            }
          },
          "label": {
            "type": "string",
            "title": "Label",
            "description": "A name for the import, such as 'Payment settings, October 2026'.",
            "x-cis-field": {
              "widget": "text"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "file",
          "label"
        ],
        "title": "ImportBillingAccountsForm",
        "description": "The payment-settings import as a form: the CSV and a label for the run."
      }
    },
    {
      "id": "finance_credit_control.set_billed_to",
      "module": "finance_credit_control_module",
      "label": "Change who pays…",
      "description": "Finance sets which customer pays for another customer's CIS orders, or lets it pay for itself again.",
      "target": "customers.customer",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/credit-control/customers/{customer_code}/billed-to",
      "dry_run_path": "/credit-control/customers/{customer_code}/billed-to/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "payer_customer_code": {
            "anyOf": [
              {
                "type": "string",
                "minLength": 1
              },
              {
                "type": "null"
              }
            ],
            "title": "Billed to",
            "description": "The customer number (CUS-K48193) of the customer who pays. Empty when the customer pays for itself.",
            "x-cis-field": {
              "entity": "customers.customer",
              "widget": "entity"
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "description": "Why the payer is changing. The reason will be kept in the customer's billed-to log.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "payer_customer_code"
        ],
        "title": "BilledToRequest",
        "description": "Who pays for the customer's CIS orders from now on; orders already placed keep their payer."
      }
    },
    {
      "id": "finance_credit_control.record_dispute",
      "module": "finance_credit_control_module",
      "label": "Record dispute…",
      "description": "Finance records that a customer is challenging an invoice, open until it is resolved.",
      "target": "finance_sales_ledger.invoice",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/credit-control/invoices/{invoice_no}/dispute",
      "dry_run_path": "/credit-control/invoices/{invoice_no}/dispute/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "What the customer says",
            "description": "What the customer is challenging, in up to 500 characters. Only staff will see the text.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordDisputeRequest"
      }
    },
    {
      "id": "finance_credit_control.resolve_invoice_dispute",
      "module": "finance_credit_control_module",
      "label": "Resolve dispute…",
      "description": "Finance resolves the open dispute on an invoice, with a note on how.",
      "target": "finance_sales_ledger.invoice",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/credit-control/invoices/{invoice_no}/dispute/resolve",
      "dry_run_path": "/credit-control/invoices/{invoice_no}/dispute/resolve/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "How the dispute was resolved",
            "description": "How the dispute was resolved, such as a credit note number, in up to 500 characters. Only staff will see the text.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ResolveDisputeRequest"
      }
    },
    {
      "id": "finance_purchase_ledger.confirm_match_group",
      "module": "finance_purchase_ledger_module",
      "label": "Release payment…",
      "description": "Release each chosen purchase invoice's match group for payment, drawing down the purchase orders they settle.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": true,
      "bulk": true,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Releases a supplier payment: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/actions/confirm-match-group",
      "dry_run_path": "/purchase-ledger/purchase-invoices/actions/confirm-match-group/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "targets": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "minItems": 1,
            "title": "Targets",
            "description": "The identifiers of the records this bulk command acts on."
          },
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "description": "Needed when goods are not received or a purchase invoice may be a duplicate. Accept a variance with Accept variance first, not here.",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "expected_amounts_hash": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Amounts checked",
            "description": "The amounts the dry run showed (its guard), echoed so a change since is refused (409 stale).",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "targets"
        ],
        "title": "ConfirmMatchGroupRequest"
      },
      "proposal_class": "decides"
    },
    {
      "id": "finance_purchase_ledger.approve_non_po_purchase_invoice",
      "module": "finance_purchase_ledger_module",
      "label": "Release non-PO payment…",
      "description": "Release a purchase invoice with no purchase order for payment.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": true,
      "bulk": true,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Releases a supplier payment: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/actions/approve-non-po",
      "dry_run_path": "/purchase-ledger/purchase-invoices/actions/approve-non-po/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "targets": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "minItems": 1,
            "title": "Targets",
            "description": "The identifiers of the records this bulk command acts on."
          },
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "description": "Required when the purchase invoice carries a warning that asks for one.",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "expected_amounts_hash": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Amounts checked",
            "description": "The amounts the dry run showed (its guard), echoed so a change since is refused (409 stale).",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "targets"
        ],
        "title": "ApproveNonPoPurchaseInvoiceRequest"
      }
    },
    {
      "id": "finance_purchase_ledger.confirm_usage_purchase_invoice",
      "module": "finance_purchase_ledger_module",
      "label": "Release usage payment…",
      "description": "Release a usage purchase invoice (or usage credit note) for payment against our usage.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": true,
      "bulk": true,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Releases a supplier payment: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/actions/confirm-usage",
      "dry_run_path": "/purchase-ledger/purchase-invoices/actions/confirm-usage/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "targets": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "minItems": 1,
            "title": "Targets",
            "description": "The identifiers of the records this bulk command acts on."
          },
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "description": "Required when the purchase invoice carries a warning that asks for one.",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "expected_amounts_hash": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Amounts checked",
            "description": "The amounts the dry run showed (its guard), echoed so a change since is refused (409 stale).",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "targets"
        ],
        "title": "ConfirmUsagePurchaseInvoiceRequest"
      }
    },
    {
      "id": "finance_purchase_ledger.record_purchase_invoice_second_approval",
      "module": "finance_purchase_ledger_module",
      "label": "Second-approve",
      "description": "Second-approve from the purchase invoice: its match group's overdraw, or a non-PO or usage purchase invoice's amounts, as someone other than the first approver and the creator.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Second approval of a supplier payment: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/second-approve",
      "dry_run_path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/second-approve/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expected_amounts_hash": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Amounts checked",
            "description": "The amounts the dry run showed (its guard), echoed so a change since is refused (409 stale).",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SecondApproveRequest"
      }
    },
    {
      "id": "finance_purchase_ledger.accept_usage_difference",
      "module": "finance_purchase_ledger_module",
      "label": "Accept difference…",
      "description": "Accept a usage purchase invoice's difference from our usage, with a reason; the first approval.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/accept-usage-difference",
      "dry_run_path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/accept-usage-difference/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "title": "Reason",
            "description": "Why the usage purchase invoice is right although it differs from our usage for its period.",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "expected_amounts_hash": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Amounts checked",
            "description": "The amounts the dry run showed (its guard), echoed so a change since is refused (409 stale).",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "reason"
        ],
        "title": "AcceptUsageDifferenceRequest"
      }
    },
    {
      "id": "finance_purchase_ledger.check_bank_details",
      "module": "finance_purchase_ledger_module",
      "label": "Check bank details…",
      "description": "Record that a person checked, by phone, bank details that differ from the supplier's on record.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "It records that a person phoned the supplier to check bank details, which Claude cannot do (coordinator, 14 Oct; with Ben).",
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/check-bank-details",
      "dry_run_path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/check-bank-details/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note": {
            "type": "string",
            "title": "Who did you speak to?",
            "description": "Who you spoke to and how you found the number (e.g. 'Jane in accounts, number in Xero'). Never a number: six or more digits in a row are refused.",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "confirmed": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 5,
            "title": "Confirmed",
            "description": "The dry run's confirmations ticked, by key: `phoned_known_number` and `confirmed_xero_account`. Both are needed."
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "note"
        ],
        "title": "CheckBankDetailsRequest"
      }
    },
    {
      "id": "finance_purchase_ledger.choose_purchase_invoice_trading_company",
      "module": "finance_purchase_ledger_module",
      "label": "Choose trading company…",
      "description": "Say which trading company a bill belongs to when One cannot tell from its purchase orders, our account number or the supplier's only account. The bill is then that company's: its Xero organisation, its period lock and its month end.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/choose-trading-company",
      "dry_run_path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/choose-trading-company/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "trading_company_code": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Trading company",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ChooseTradingCompanyRequest",
        "description": "*Choose trading company…*: the company a bill belongs to when One cannot tell from its purchase orders or our\naccount number. A dry run called with ``{}`` offers the companies a bill can be given to."
      }
    },
    {
      "id": "finance_purchase_ledger.accept_purchase_invoice_as_ordering_company",
      "module": "finance_purchase_ledger_module",
      "label": "Accept as the ordering company's…",
      "description": "Accept a bill addressed to the other trading company as the company that placed its purchase orders, recording why (the supplier printed the wrong name). Nothing is recharged.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Decides whose books a bill goes to: done in the app by finance.",
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/accept-as-ordering-company",
      "dry_run_path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/accept-as-ordering-company/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Why",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AcceptAsTradingCompanyRequest",
        "description": "*Accept as …'s…* (either): why a bill addressed to the other trading company is taken as the one named. A dry\nrun called with ``{}`` starts the reason with the usual explanation."
      }
    },
    {
      "id": "finance_purchase_ledger.accept_purchase_invoice_as_billed_company",
      "module": "finance_purchase_ledger_module",
      "label": "Accept as the billed company's…",
      "description": "Accept a bill as the trading company it is addressed to, which pays it, recording why; its cost is recharged to the company whose order it was for. Only where the supplier holds an open account with that company.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Decides whose books a bill goes to: done in the app by finance.",
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/accept-as-billed-company",
      "dry_run_path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/accept-as-billed-company/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Why",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AcceptAsTradingCompanyRequest",
        "description": "*Accept as …'s…* (either): why a bill addressed to the other trading company is taken as the one named. A dry\nrun called with ``{}`` starts the reason with the usual explanation."
      }
    },
    {
      "id": "finance_purchase_ledger.put_purchase_invoice_in_query",
      "module": "finance_purchase_ledger_module",
      "label": "Query with colleague…",
      "description": "Ask a colleague, by default the purchase order's buyer, to find out something about the purchase invoice; it waits in In query until they answer. While a variance nobody accepted stands, the buyer's yes accepts it.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/query",
      "dry_run_path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/query/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "colleague_user_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Colleague",
            "description": "Who the query ticket will be assigned to. Left out, the purchase order's buyer.",
            "x-cis-field": {
              "entity": "users.user",
              "widget": "entity"
            }
          },
          "note": {
            "type": "string",
            "title": "What is queried",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "note"
        ],
        "title": "PutPurchaseInvoiceInQueryRequest"
      },
      "proposal_class": "asks_internal"
    },
    {
      "id": "finance_purchase_ledger.ask_supplier_about_purchase_invoice",
      "module": "finance_purchase_ledger_module",
      "label": "Query with supplier…",
      "description": "Ask the supplier a question about the purchase invoice through the supplier portal; it waits in In query until they reply.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/ask-supplier",
      "dry_run_path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/ask-supplier/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "question": {
            "type": "string",
            "title": "Question for the supplier",
            "description": "Up to 2,000 characters. The supplier will see the question on the supplier portal, with line breaks kept.",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "question"
        ],
        "title": "AskSupplierRequest"
      },
      "proposal_class": "asks_external"
    },
    {
      "id": "finance_purchase_ledger.answer_purchase_invoice_query",
      "module": "finance_purchase_ledger_module",
      "label": "Answer query…",
      "description": "Answer a purchase invoice's query on its ticket, yes or no with a note: the purchase invoice goes back to finance, and the purchase order buyer's yes accepts a variance nobody accepted. Only the ticket's assignee answers.",
      "target": "workflow.ticket",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/tickets/{ticket_id}/actions/answer-query",
      "dry_run_path": "/purchase-ledger/tickets/{ticket_id}/actions/answer-query/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "answer": {
            "type": "string",
            "enum": [
              "yes",
              "no"
            ],
            "title": "Your answer",
            "description": "The colleague's answer to a query.",
            "x-cis-field": {
              "enum_labels": {
                "no": "No",
                "yes": "Yes"
              },
              "widget": "radio"
            }
          },
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "answer"
        ],
        "title": "AnswerPurchaseInvoiceQueryRequest"
      }
    },
    {
      "id": "finance_purchase_ledger.resolve_purchase_invoice_query",
      "module": "finance_purchase_ledger_module",
      "label": "Resolve query…",
      "description": "Resolve a purchase invoice's query, returning it to the queue.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/resolve-query",
      "dry_run_path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/resolve-query/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note": {
            "type": "string",
            "title": "What the answer was",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "note"
        ],
        "title": "ResolvePurchaseInvoiceQueryRequest"
      },
      "proposal_class": "records"
    },
    {
      "id": "finance_purchase_ledger.classify_purchase_invoice_as_po",
      "module": "finance_purchase_ledger_module",
      "label": "Match to purchase orders…",
      "description": "It bills purchase orders after all: take it off the no-purchase-order path, to be matched.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/classify-po",
      "dry_run_path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/classify-po/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "finance_purchase_ledger.classify_purchase_invoice_as_non_po",
      "module": "finance_purchase_ledger_module",
      "label": "Pay without a purchase order…",
      "description": "It's a real bill to pay, but nothing was bought on a purchase order: an overhead such as insurance, hosting or software.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/classify-non-po",
      "dry_run_path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/classify-non-po/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "category": {
            "type": "string",
            "enum": [
              "software",
              "hosting",
              "insurance",
              "carriage",
              "utilities",
              "other"
            ],
            "title": "Category",
            "description": "What a purchase invoice that settles no purchase order is for: the entity's own overheads.\n\nSet by intake from the document (spec 05 §4); a purchase invoice with a category and no\npurchase order is approved as a non-PO purchase invoice rather than confirmed in a group.\nThe members are the overhead classes the supplier-purchase-invoice corpus names.",
            "x-cis-field": {
              "enum_labels": {
                "carriage": "Carriage",
                "hosting": "Hosting",
                "insurance": "Insurance",
                "other": "Other overheads",
                "software": "Software",
                "utilities": "Utilities"
              }
            }
          },
          "reason": {
            "type": "string",
            "title": "Reason",
            "description": "Why the purchase invoice settles no purchase order.",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "category",
          "reason"
        ],
        "title": "ClassifyPurchaseInvoiceAsNonPoRequest"
      }
    },
    {
      "id": "finance_purchase_ledger.set_usage_period",
      "module": "finance_purchase_ledger_module",
      "label": "Set period…",
      "description": "Set the period a usage purchase invoice covers.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/set-usage-period",
      "dry_run_path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/set-usage-period/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "period_from": {
            "type": "string",
            "format": "date",
            "title": "From",
            "description": "The first day the usage purchase invoice covers.",
            "x-cis-field": {}
          },
          "period_to": {
            "type": "string",
            "format": "date",
            "title": "To",
            "description": "The last day the usage purchase invoice covers, on or after the first day.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "period_from",
          "period_to"
        ],
        "title": "SetUsagePeriodRequest"
      }
    },
    {
      "id": "finance_purchase_ledger.amend_purchase_invoice",
      "module": "finance_purchase_ledger_module",
      "label": "Amend details…",
      "description": "Correct what was read off the document: the supplier, its number, the dates, the amounts and the lines, adding or removing a line where it was misread, and each line's VAT treatment, Xero account and the period its cost is spread over.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/amend",
      "dry_run_path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/amend/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "supplier_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Supplier",
            "description": "The supplier the purchase invoice is from.",
            "x-cis-field": {
              "entity": "suppliers.supplier",
              "entity_value": "id",
              "widget": "entity"
            }
          },
          "supplier_document_no": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Supplier's number",
            "x-cis-field": {}
          },
          "invoice_date": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Invoice date",
            "x-cis-field": {}
          },
          "due_date": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Due date",
            "x-cis-field": {}
          },
          "net_amount": {
            "anyOf": [
              {
                "type": "number"
              },
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Net",
            "x-cis-field": {}
          },
          "tax_amount": {
            "anyOf": [
              {
                "type": "number"
              },
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "VAT",
            "x-cis-field": {}
          },
          "gross_amount": {
            "anyOf": [
              {
                "type": "number"
              },
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Gross",
            "x-cis-field": {}
          },
          "lines": {
            "anyOf": [
              {
                "items": {
                  "properties": {
                    "line_number": {
                      "anyOf": [
                        {
                          "type": "integer"
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "Line",
                      "description": "Empty on a line you add.",
                      "readOnly": true,
                      "x-cis-field": {}
                    },
                    "description": {
                      "anyOf": [
                        {
                          "type": "string"
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "Description",
                      "x-cis-field": {}
                    },
                    "quantity": {
                      "anyOf": [
                        {
                          "type": "number"
                        },
                        {
                          "type": "string"
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "Quantity",
                      "x-cis-field": {}
                    },
                    "unit_price": {
                      "anyOf": [
                        {
                          "type": "number"
                        },
                        {
                          "type": "string"
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "Unit price",
                      "x-cis-field": {}
                    },
                    "net_amount": {
                      "anyOf": [
                        {
                          "type": "number"
                        },
                        {
                          "type": "string"
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "Net",
                      "x-cis-field": {
                        "widget": "money"
                      }
                    },
                    "tax_amount": {
                      "anyOf": [
                        {
                          "type": "number"
                        },
                        {
                          "type": "string"
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "VAT amount",
                      "x-cis-field": {
                        "widget": "money"
                      }
                    },
                    "gross_amount": {
                      "anyOf": [
                        {
                          "type": "number"
                        },
                        {
                          "type": "string"
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "Gross",
                      "x-cis-field": {
                        "widget": "money"
                      }
                    },
                    "vat_treatment": {
                      "anyOf": [
                        {
                          "type": "string",
                          "enum": [
                            "standard",
                            "reduced",
                            "zero_rated",
                            "exempt",
                            "no_vat",
                            "reverse_charge"
                          ],
                          "title": "VatTreatment",
                          "description": "How a line's VAT is treated, which the Xero push maps to a tax type.\n\nZero-rated, exempt and no-VAT all print as nothing on a document, so a line's\ntreatment is resolved (from the document's rate, a coding rule or a reviewer)\nrather than inferred, and a confirmed purchase invoice cannot have an unresolved one."
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "VAT treatment",
                      "description": "How the line's VAT is treated. Empty until someone chooses: the payment waits for it.",
                      "x-cis-field": {
                        "enum_labels": {
                          "exempt": "Exempt",
                          "no_vat": "No VAT",
                          "reduced": "Reduced 5%",
                          "reverse_charge": "Reverse charge",
                          "standard": "Standard 20%",
                          "zero_rated": "Zero-rated"
                        }
                      }
                    },
                    "xero_account_code": {
                      "anyOf": [
                        {
                          "type": "string"
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "Account",
                      "description": "The Xero account the line posts to: 1 to 10 letters and digits. The payment waits for one.",
                      "x-cis-field": {}
                    },
                    "period_from": {
                      "anyOf": [
                        {
                          "type": "string",
                          "format": "date"
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "Spread from",
                      "description": "With Spread to: the period the line's cost is spread over. Leave both empty to post it in the invoice's month.",
                      "x-cis-field": {}
                    },
                    "period_to": {
                      "anyOf": [
                        {
                          "type": "string",
                          "format": "date"
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "Spread to",
                      "description": "With Spread from.",
                      "x-cis-field": {}
                    },
                    "purchase_order_line_reference": {
                      "anyOf": [
                        {
                          "type": "string"
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "Order line",
                      "description": "The purchase order line it is for (PO-K20417.01); set in Edit matching….",
                      "readOnly": true,
                      "x-cis-field": {}
                    }
                  },
                  "additionalProperties": false,
                  "type": "object",
                  "title": "AmendPurchaseInvoiceLineRequest",
                  "description": "One row of the lines table. With a line number it corrects that line; without one it is a line the reading\nmissed, added (description and the three amounts required). The row also carries the line's coding: the VAT\ntreatment and Xero account it posts with, and the period its cost is spread over."
                },
                "type": "array"
              },
              {
                "type": "null"
              }
            ],
            "title": "Lines",
            "description": "Every line the purchase invoice should have. Add a line the reading missed; take out one it should not have read. Choose each line's VAT treatment and account here too.",
            "x-cis-field": {
              "add_rows": "Add a line",
              "widget": "table"
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "description": "Why the document was misread. Needed when you change a line's VAT treatment, account or spread.",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AmendPurchaseInvoiceRequest",
        "description": "Only the fields sent are changed. ``due_date`` may be sent as null to clear it. ``lines``, when sent, is the whole\nset of lines after the change; not sent, the lines stay as they are."
      }
    },
    {
      "id": "finance_purchase_ledger.replace_purchase_invoice_document",
      "module": "finance_purchase_ledger_module",
      "label": "Replace document…",
      "description": "Swap the purchase invoice's document for the correct or a clearer copy, before the payment is released.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/replace-document",
      "dry_run_path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/replace-document/dry-run",
      "content_type": "multipart",
      "request_schema": {
        "properties": {
          "document": {
            "anyOf": [
              {
                "type": "string",
                "contentMediaType": "application/octet-stream"
              },
              {
                "type": "null"
              }
            ],
            "title": "New document",
            "description": "A PDF, PNG or JPEG of at most 25 MB.",
            "x-cis-field": {
              "accept": [
                "application/pdf",
                "image/png",
                "image/jpeg"
              ],
              "required": true,
              "widget": "file"
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Why you're replacing the document",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ReplacePurchaseInvoiceDocumentForm",
        "description": "*Replace document…*'s multipart body: the new document and why."
      }
    },
    {
      "id": "finance_purchase_ledger.dismiss_purchase_invoice",
      "module": "finance_purchase_ledger_module",
      "label": "Dismiss…",
      "description": "Never pay it: a duplicate, not an invoice, or Tharstern work. It's kept for the record.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/dismiss",
      "dry_run_path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/dismiss/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "enum": [
              "duplicate",
              "not_an_invoice",
              "not_for_this_ledger",
              "other"
            ],
            "title": "Why",
            "description": "Why a purchase invoice was dismissed rather than confirmed.",
            "x-cis-field": {
              "enum_labels": {
                "duplicate": "Duplicate",
                "not_an_invoice": "Not an invoice",
                "not_for_this_ledger": "Not for this ledger",
                "other": "Other"
              },
              "widget": "radio"
            }
          },
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "description": "Needed when the reason is Other.",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "reason"
        ],
        "title": "DismissPurchaseInvoiceRequest"
      }
    },
    {
      "id": "finance_purchase_ledger.create_purchase_invoice_manually",
      "module": "finance_purchase_ledger_module",
      "label": "Add purchase invoice…",
      "description": "Add a purchase invoice by hand, with its document, for one that did not arrive by email.",
      "target": "collection:finance_purchase_ledger.purchase_invoice",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices",
      "dry_run_path": "/purchase-ledger/purchase-invoices/dry-run",
      "content_type": "multipart",
      "request_schema": {
        "properties": {
          "document": {
            "type": "string",
            "contentMediaType": "application/octet-stream",
            "title": "Document",
            "description": "The purchase invoice: a PDF, PNG or JPEG of at most 25 MB.",
            "x-cis-field": {
              "widget": "file"
            }
          },
          "purchase_invoice": {
            "type": "string",
            "title": "Purchase invoice",
            "description": "CreateManualPurchaseInvoiceRequest as JSON: the header and lines.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "document",
          "purchase_invoice"
        ],
        "title": "CreateManualPurchaseInvoiceForm",
        "description": "The manual create's multipart body: the document, and the header and lines as JSON."
      }
    },
    {
      "id": "finance_purchase_ledger.add_purchase_invoice_from_email",
      "module": "finance_purchase_ledger_module",
      "label": "Add purchase invoice…",
      "description": "Add the purchase invoice or credit note an email's document shows, as an intake handler's proposed action; nothing it adds is paid until a person releases it.",
      "target": "collection:finance_purchase_ledger.purchase_invoice",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/from-email",
      "dry_run_path": "/purchase-ledger/purchase-invoices/from-email/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "document_ref": {
            "type": "string",
            "pattern": "^[0-9a-f]{32}:[0-9]{1,2}$",
            "title": "Document",
            "description": "The document the invoice is read from: the reference given for the attachment, or for the email's own text when the invoice is in the body.",
            "x-cis-field": {}
          },
          "is_credit_note": {
            "type": "boolean",
            "title": "Credit note",
            "x-cis-field": {}
          },
          "supplier": {
            "properties": {
              "name_as_printed": {
                "type": "string",
                "maxLength": 200,
                "minLength": 1,
                "title": "Supplier as printed",
                "description": "The supplier's name as the document prints it.",
                "x-cis-field": {}
              },
              "supplier_no": {
                "anyOf": [
                  {
                    "type": "string",
                    "pattern": "^SUP-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}$"
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Supplier number",
                "description": "From the supplier search; null if not found.",
                "x-cis-field": {}
              }
            },
            "additionalProperties": false,
            "type": "object",
            "required": [
              "name_as_printed"
            ],
            "title": "Supplier",
            "x-cis-field": {}
          },
          "supplier_document_no": {
            "type": "string",
            "maxLength": 60,
            "title": "Supplier's number",
            "description": "The supplier's invoice or credit note number, as printed; empty if none.",
            "x-cis-field": {}
          },
          "invoice_date": {
            "anyOf": [
              {
                "type": "string",
                "pattern": "^[0-9]{4}-[0-9]{2}-[0-9]{2}$"
              },
              {
                "type": "null"
              }
            ],
            "title": "Invoice date",
            "x-cis-field": {}
          },
          "due_date": {
            "anyOf": [
              {
                "type": "string",
                "pattern": "^[0-9]{4}-[0-9]{2}-[0-9]{2}$"
              },
              {
                "type": "null"
              }
            ],
            "title": "Due date",
            "x-cis-field": {}
          },
          "currency": {
            "type": "string",
            "pattern": "^[A-Z]{3}$",
            "title": "Currency",
            "x-cis-field": {}
          },
          "net": {
            "type": "string",
            "maxLength": 14,
            "pattern": "^-?[0-9]{1,10}(\\.[0-9]{1,2})?$",
            "title": "Net",
            "x-cis-field": {}
          },
          "vat": {
            "type": "string",
            "maxLength": 14,
            "pattern": "^-?[0-9]{1,10}(\\.[0-9]{1,2})?$",
            "title": "VAT",
            "x-cis-field": {}
          },
          "gross": {
            "type": "string",
            "maxLength": 14,
            "pattern": "^-?[0-9]{1,10}(\\.[0-9]{1,2})?$",
            "title": "Gross",
            "x-cis-field": {}
          },
          "lines": {
            "items": {
              "properties": {
                "description": {
                  "type": "string",
                  "maxLength": 300,
                  "title": "Description",
                  "x-cis-field": {}
                },
                "quantity": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 40
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Quantity",
                  "x-cis-field": {}
                },
                "net": {
                  "type": "string",
                  "maxLength": 14,
                  "pattern": "^-?[0-9]{1,10}(\\.[0-9]{1,2})?$",
                  "title": "Net",
                  "x-cis-field": {}
                },
                "vat_rate": {
                  "type": "string",
                  "enum": [
                    "20",
                    "5",
                    "0",
                    "exempt",
                    "none"
                  ],
                  "title": "VAT rate",
                  "description": "The VAT rate a document shows on a line. ``none`` is a line with no VAT shown at all.",
                  "x-cis-field": {}
                },
                "vat_amount": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 14,
                      "pattern": "^-?[0-9]{1,10}(\\.[0-9]{1,2})?$"
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "VAT",
                  "x-cis-field": {}
                },
                "purchase_order_no": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 40
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Purchase order",
                  "x-cis-field": {}
                },
                "period_from": {
                  "anyOf": [
                    {
                      "type": "string",
                      "pattern": "^[0-9]{4}-[0-9]{2}-[0-9]{2}$"
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Period from",
                  "x-cis-field": {}
                },
                "period_to": {
                  "anyOf": [
                    {
                      "type": "string",
                      "pattern": "^[0-9]{4}-[0-9]{2}-[0-9]{2}$"
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Period to",
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "description",
                "net",
                "vat_rate"
              ],
              "title": "DocumentLineRequest"
            },
            "type": "array",
            "maxItems": 200,
            "minItems": 1,
            "title": "Lines",
            "x-cis-field": {}
          },
          "claimed_purchase_order_nos": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 20,
            "title": "Purchase orders quoted",
            "x-cis-field": {}
          },
          "credited_document_nos": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 10,
            "title": "Invoices credited",
            "description": "For a credit note: the supplier's invoice numbers it says it credits, as printed.",
            "x-cis-field": {}
          },
          "supplier_order_nos": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 20,
            "title": "Supplier's order numbers",
            "description": "The supplier's own order or job numbers the document quotes, as printed (not ours).",
            "x-cis-field": {}
          },
          "non_po_category": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "software",
                  "hosting",
                  "insurance",
                  "carriage",
                  "utilities",
                  "other"
                ],
                "title": "NonPoCategory",
                "description": "What a purchase invoice that settles no purchase order is for: the entity's own overheads.\n\nSet by intake from the document (spec 05 §4); a purchase invoice with a category and no\npurchase order is approved as a non-PO purchase invoice rather than confirmed in a group.\nThe members are the overhead classes the supplier-purchase-invoice corpus names."
              },
              {
                "type": "null"
              }
            ],
            "title": "Category of spend",
            "x-cis-field": {}
          },
          "usage_period_from": {
            "anyOf": [
              {
                "type": "string",
                "pattern": "^[0-9]{4}-[0-9]{2}-[0-9]{2}$"
              },
              {
                "type": "null"
              }
            ],
            "title": "Usage period from",
            "description": "The first day of the period the invoice covers, as printed (a usage invoice); null if none.",
            "x-cis-field": {}
          },
          "usage_period_to": {
            "anyOf": [
              {
                "type": "string",
                "pattern": "^[0-9]{4}-[0-9]{2}-[0-9]{2}$"
              },
              {
                "type": "null"
              }
            ],
            "title": "Usage period to",
            "description": "The last day of the period the invoice covers, as printed.",
            "x-cis-field": {}
          },
          "supplier_account_no": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 40
              },
              {
                "type": "null"
              }
            ],
            "title": "Our account number",
            "description": "Our account number with the supplier, as printed; null if none.",
            "x-cis-field": {}
          },
          "addressed_to": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Addressed to",
            "description": "The company the document is addressed to (its 'Bill to' or 'Invoice to'), as printed: the name only, never the address; null if none is printed.",
            "x-cis-field": {}
          },
          "bank_details_on_document": {
            "type": "string",
            "enum": [
              "not_shown",
              "matches_record",
              "differs_from_record",
              "no_record_to_compare"
            ],
            "title": "Bank details on the document",
            "description": "What the agent saw of bank details on the document, against the endings on record. Never the values.",
            "x-cis-field": {}
          },
          "source_hints": {
            "items": {
              "properties": {
                "field": {
                  "type": "string",
                  "maxLength": 60,
                  "title": "Field",
                  "x-cis-field": {}
                },
                "hint": {
                  "type": "string",
                  "maxLength": 200,
                  "title": "Where it was read",
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "field",
                "hint"
              ],
              "title": "SourceHintRequest"
            },
            "type": "array",
            "maxItems": 40,
            "title": "Where each value was read",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "document_ref",
          "is_credit_note",
          "supplier",
          "supplier_document_no",
          "currency",
          "net",
          "vat",
          "gross",
          "lines",
          "bank_details_on_document"
        ],
        "title": "AddPurchaseInvoiceFromEmailRequest",
        "description": "*Add purchase invoice…* as an email's proposed action: the purchase invoice or credit note the email's document\nshows, exactly as printed, with ``document_ref`` naming the document (the handler's ``document_ref`` tool gives it). There is no field for\nbank details: only whether the document's agree with the endings on record."
      },
      "proposal_class": "records",
      "proposal_only": true
    },
    {
      "id": "finance_purchase_ledger.reshape_match_group",
      "module": "finance_purchase_ledger_module",
      "label": "Edit matching…",
      "description": "Change what the purchase invoice's group holds: take a purchase order out, add one, or bring in another of the supplier's documents.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/reshape-match-group",
      "dry_run_path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/reshape-match-group/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "purchase_invoice_ids": {
            "anyOf": [
              {
                "items": {
                  "type": "string"
                },
                "type": "array",
                "maxItems": 50
              },
              {
                "type": "null"
              }
            ],
            "title": "Invoices",
            "description": "The documents the group should hold, by id: the supplier's invoices and credit notes.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "purchase_order_nos": {
            "anyOf": [
              {
                "items": {
                  "type": "string"
                },
                "type": "array",
                "maxItems": 50
              },
              {
                "type": "null"
              }
            ],
            "title": "Purchase orders",
            "description": "The purchase orders the group should hold, by number, e.g. PO-K20417.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "search": {
            "anyOf": [
              {
                "additionalProperties": {
                  "type": "string"
                },
                "type": "object"
              },
              {
                "type": "null"
              }
            ],
            "title": "Search",
            "description": "What was typed in each list's search box, by its field name; the dry run answers it and the commit ignores it.",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ReshapeMatchGroupRequest",
        "description": "Edit matching… (the approved mock-up, Ben 28 Oct 2026): the group as it should be after the change, offered by\nthe dry run as two choices. Ticked means \"in the group after this change\"; null leaves that side as it is. This\npurchase invoice, a released document and an order already drawn on stay whatever the lists say."
      },
      "proposal_class": "records"
    },
    {
      "id": "finance_purchase_ledger.accept_variance",
      "module": "finance_purchase_ledger_module",
      "label": "Accept variance…",
      "description": "Accept that the purchase invoice bills more than its orders, and record why.",
      "target": "finance_purchase_ledger.purchase_invoice",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/accept-variance",
      "dry_run_path": "/purchase-ledger/purchase-invoices/{purchase_invoice_id}/actions/accept-variance/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "title": "Reason",
            "description": "Why the purchase invoices are right although they differ from the purchase orders.",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "expected_amounts_hash": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Amounts checked",
            "description": "The amounts the dry run showed (its guard), echoed so a change since is refused (409 stale).",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "reason"
        ],
        "title": "AcceptVarianceRequest"
      },
      "proposal_class": "decides"
    },
    {
      "id": "finance_purchase_ledger.record_billing_complete",
      "module": "finance_purchase_ledger_module",
      "label": "No more invoices expected…",
      "description": "Mark a purchase order line delivered in full as billed in full; no more purchase invoices are expected.",
      "target": "purchase_orders.purchase_order_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/purchase-order-lines/{purchase_order_line_id}/actions/billing-complete",
      "dry_run_path": "/purchase-ledger/purchase-order-lines/{purchase_order_line_id}/actions/billing-complete/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "title": "Reason",
            "description": "Why, in up to 1,000 characters.",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "reason"
        ],
        "title": "BillingMarkerRequest"
      }
    },
    {
      "id": "finance_purchase_ledger.record_purchase_order_billing_complete",
      "module": "finance_purchase_ledger_module",
      "label": "No more invoices expected…",
      "description": "Mark every line of a purchase order that is delivered in full as billed in full; no more purchase invoices are expected for them. A line not yet delivered in full stays open.",
      "target": "purchase_orders.purchase_order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/purchase-orders/{purchase_order_no}/actions/billing-complete",
      "dry_run_path": "/purchase-ledger/purchase-orders/{purchase_order_no}/actions/billing-complete/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "title": "Reason",
            "description": "Why, in up to 1,000 characters.",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "reason"
        ],
        "title": "BillingMarkerRequest"
      }
    },
    {
      "id": "finance_purchase_ledger.reopen_billing",
      "module": "finance_purchase_ledger_module",
      "label": "Expect more invoices…",
      "description": "Reopen a purchase order line's billing; another purchase invoice is expected after all.",
      "target": "purchase_orders.purchase_order_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/purchase-order-lines/{purchase_order_line_id}/actions/reopen-billing",
      "dry_run_path": "/purchase-ledger/purchase-order-lines/{purchase_order_line_id}/actions/reopen-billing/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "title": "Reason",
            "description": "Why, in up to 1,000 characters.",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "reason"
        ],
        "title": "BillingMarkerRequest"
      }
    },
    {
      "id": "finance_purchase_ledger.write_auto_confirmation_rule",
      "module": "finance_purchase_ledger_module",
      "label": "Create rule…",
      "description": "Create a rule letting one supplier's clean purchase invoices release themselves on arrival.",
      "target": "collection:finance_purchase_ledger.auto_confirmation_rule",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Sets up automatic release of supplier payments: done in the app, never by Claude.",
      "method": "POST",
      "path": "/purchase-ledger/auto-confirmation-rules",
      "dry_run_path": "/purchase-ledger/auto-confirmation-rules/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "supplier_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Supplier",
            "x-cis-field": {
              "required": true
            }
          },
          "name": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Name",
            "x-cis-field": {
              "required": true
            }
          },
          "max_shortfall_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Allowed shortfall",
            "description": "How far under its orders an invoice can be and still release itself. Never over.",
            "x-cis-field": {
              "required": true,
              "widget": "money"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "WriteAutoConfirmationRuleRequest",
        "description": "Every field may be absent so the dry run shows the supplier's evidence before the rule is named; the action\nanswers a missing one with a 400 ``required``."
      }
    },
    {
      "id": "finance_purchase_ledger.change_auto_confirmation_rule",
      "module": "finance_purchase_ledger_module",
      "label": "Change rule…",
      "description": "Change a rule's name or allowed shortfall, kept in its change log.",
      "target": "finance_purchase_ledger.auto_confirmation_rule",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Sets up automatic release of supplier payments: done in the app, never by Claude.",
      "method": "POST",
      "path": "/purchase-ledger/auto-confirmation-rules/{rule_id}/actions/change",
      "dry_run_path": "/purchase-ledger/auto-confirmation-rules/{rule_id}/actions/change/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "name": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Name",
            "x-cis-field": {}
          },
          "max_shortfall_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Allowed shortfall",
            "description": "How far under its orders an invoice can be and still release itself. Never over.",
            "x-cis-field": {
              "widget": "money"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ChangeAutoConfirmationRuleRequest",
        "description": "What to change; a field left out keeps its value."
      }
    },
    {
      "id": "finance_purchase_ledger.revoke_auto_confirmation_rule",
      "module": "finance_purchase_ledger_module",
      "label": "Revoke…",
      "description": "Revoke a rule, so the supplier's purchase invoices need a person again.",
      "target": "finance_purchase_ledger.auto_confirmation_rule",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Its route needs LEDGER_RULES_MANAGE, a high-risk permit (package C): done in the app, never by Claude.",
      "method": "POST",
      "path": "/purchase-ledger/auto-confirmation-rules/{rule_id}/actions/revoke",
      "dry_run_path": "/purchase-ledger/auto-confirmation-rules/{rule_id}/actions/revoke/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "title": "Reason",
            "description": "Why, in up to 500 characters.",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "reason"
        ],
        "title": "RevokeAutoConfirmationRuleRequest"
      }
    },
    {
      "id": "finance_sales_ledger.raise_credit_note",
      "module": "finance_sales_ledger_module",
      "label": "Raise credit note…",
      "description": "Finance credits an invoice line by line, reducing what is owed or refunding it.",
      "target": "finance_sales_ledger.invoice",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-ledger/invoices/{invoice_no}/credit-notes",
      "dry_run_path": "/sales-ledger/invoices/{invoice_no}/credit-notes/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "lines": {
            "items": {
              "properties": {
                "invoice_line_id": {
                  "type": "string",
                  "title": "Line id",
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "line": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10000
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Line",
                  "description": "The order line's reference.",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "description": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10000
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Description",
                  "description": "What the line bills.",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "net_pence": {
                  "anyOf": [
                    {
                      "type": "integer"
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Net to credit",
                  "description": "Leave empty to credit everything left.",
                  "x-cis-field": {
                    "widget": "money"
                  }
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "invoice_line_id"
              ],
              "title": "CreditLineRequest"
            },
            "type": "array",
            "maxItems": 500,
            "title": "Lines to credit",
            "x-cis-field": {
              "required": true,
              "selectable": true,
              "widget": "table"
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "description": "Why the credit note is raised, in up to 500 characters. The reason won't be printed on the credit note.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "refund": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "none",
                  "card",
                  "bank_transfer"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Where the money goes",
            "x-cis-field": {
              "enum_labels": {
                "bank_transfer": "Refund by bank transfer",
                "card": "Refund to the card",
                "none": "Reduces what is owed"
              },
              "required": true,
              "widget": "radio"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request",
            "description": "One per dialog (the command's idempotency key): a repeat returns the first's credit note.",
            "x-cis-field": {}
          },
          "bank_refund": {
            "anyOf": [
              {
                "properties": {
                  "refunded_on": {
                    "anyOf": [
                      {
                        "type": "string",
                        "format": "date"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Refunded on",
                    "description": "The day finance refunded the money. Leave empty for today.",
                    "x-cis-field": {
                      "widget": "date"
                    }
                  },
                  "reference": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Bank reference",
                    "description": "The bank's reference for the refund, if known.",
                    "x-cis-field": {
                      "widget": "text"
                    }
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "title": "BankRefundRequest"
              },
              {
                "type": "null"
              }
            ],
            "title": "Bank refund",
            "x-cis-field": {
              "show_if": {
                "refund": "bank_transfer"
              }
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RaiseCreditNoteRequest",
        "description": "Finance credits an invoice. ``refund`` says where the money goes: nowhere (it reduces what is owed), back to the\ncard, or by bank transfer."
      }
    },
    {
      "id": "finance_sales_ledger.record_proforma_payment",
      "module": "finance_sales_ledger_module",
      "label": "Record payment…",
      "description": "Finance records a bank transfer received against a pro-forma; at its gross the invoice is raised.",
      "target": "finance_sales_ledger.proforma",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-ledger/proformas/{proforma_no}/payments",
      "dry_run_path": "/sales-ledger/proformas/{proforma_no}/payments/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "amount_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Amount received",
            "description": "The amount that arrived. An amount over what is owed will be recorded as received.",
            "x-cis-field": {
              "required": true,
              "widget": "money"
            }
          },
          "received_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Received on",
            "description": "The day the money arrived; never in the future.",
            "x-cis-field": {
              "required": true,
              "widget": "date"
            }
          },
          "bank_reference": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Bank reference",
            "description": "As it reads on the bank feed; 1 to 64 characters. A repeat of the same reference, amount and day records nothing more.",
            "x-cis-field": {
              "required": true,
              "widget": "text"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordProformaPaymentRequest",
        "description": "A bank transfer received against a pro-forma, as finance reads it on Xero's bank feed."
      },
      "main": true
    },
    {
      "id": "finance_sales_ledger.mark_overpayment_refunded",
      "module": "finance_sales_ledger_module",
      "label": "Mark the excess refunded",
      "description": "Finance records that an overpaid pro-forma's excess was refunded in Xero.",
      "target": "finance_sales_ledger.proforma",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-ledger/proformas/{proforma_no}/actions/mark-overpayment-refunded",
      "dry_run_path": "/sales-ledger/proformas/{proforma_no}/actions/mark-overpayment-refunded/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "finance_sales_ledger.update_invoicing_settings",
      "module": "finance_sales_ledger_module",
      "label": "Change invoicing settings…",
      "description": "Change some of a payer's invoicing settings, with one reason.",
      "target": "finance_credit_control.billing_account",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Changes how a payer is invoiced: ledger settings are changed in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/sales-ledger/billing-accounts/{ref}/invoicing-settings",
      "dry_run_path": "/sales-ledger/billing-accounts/{ref}/invoicing-settings/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "made_to_order_invoice_trigger": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Made-to-order work is invoiced",
            "x-cis-field": {
              "enum_labels": {
                "on_delivery": "On delivery",
                "on_despatch": "On despatch"
              },
              "widget": "select"
            }
          },
          "postage_invoicing": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Postage",
            "x-cis-field": {
              "enum_labels": {
                "invoice_early": "Invoice early (when the order is accepted)",
                "invoice_early_hold_drop": "Invoice early and hold the drop until paid",
                "with_main_invoice": "With the main invoice"
              },
              "widget": "select"
            }
          },
          "stock_billing": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Stock billing",
            "x-cis-field": {
              "enum_labels": {
                "bill_and_hold": "Bill and hold",
                "bill_on_usage": "Bill on usage"
              },
              "widget": "select"
            }
          },
          "posts_on_own_carrier_account": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "Posts on its own carrier account (no postage line)",
            "x-cis-field": {
              "enum_labels": {
                "false": "No",
                "true": "Yes"
              },
              "widget": "checkbox"
            }
          },
          "invoice_grouping": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Invoice grouping",
            "x-cis-field": {
              "enum_labels": {
                "per_customer_po": "One invoice per purchase order",
                "per_order": "One invoice per order",
                "per_payer": "One invoice for everything"
              },
              "widget": "select"
            }
          },
          "invoice_cut_off": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Invoice cut-off",
            "x-cis-field": {
              "enum_labels": {
                "monthly": "Monthly",
                "weekly": "Weekly",
                "when_due": "When due"
              },
              "widget": "select"
            }
          },
          "invoice_cut_off_day": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Cut-off day",
            "x-cis-field": {
              "enum_labels": {
                "1": "1st",
                "10": "10th",
                "11": "11th",
                "12": "12th",
                "13": "13th",
                "14": "14th",
                "15": "15th",
                "16": "16th",
                "17": "17th",
                "18": "18th",
                "19": "19th",
                "2": "2nd",
                "20": "20th",
                "21": "21st",
                "22": "22nd",
                "23": "23rd",
                "24": "24th",
                "25": "25th",
                "26": "26th",
                "27": "27th",
                "28": "28th",
                "3": "3rd",
                "4": "4th",
                "5": "5th",
                "6": "6th",
                "7": "7th",
                "8": "8th",
                "9": "9th",
                "fri": "Friday",
                "last": "Last day of the month",
                "mon": "Monday",
                "sat": "Saturday",
                "sun": "Sunday",
                "thu": "Thursday",
                "tue": "Tuesday",
                "wed": "Wednesday"
              },
              "widget": "select"
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "description": "Why the settings are changing, in up to 500 characters. The reason will be kept with every setting this save changes.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "UpdateInvoicingSettingsRequest",
        "description": "The changed settings and the reason. A field left out (or null) is not being changed."
      }
    },
    {
      "id": "finance_sales_ledger.create_period_charge",
      "module": "finance_sales_ledger_module",
      "label": "Add a period charge…",
      "description": "Finance adds a period charge to a payer: what it is for, how often, how the amount is found and from when.",
      "target": "finance_credit_control.billing_account",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Changes how a payer is billed: period charges are added in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/sales-ledger/billing-accounts/{ref}/period-charges",
      "dry_run_path": "/sales-ledger/billing-accounts/{ref}/period-charges/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "kind": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "storage",
                  "fulfilment",
                  "licence_fee"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "What it is for",
            "x-cis-field": {
              "enum_labels": {
                "fulfilment": "Fulfilment",
                "licence_fee": "Licence fee",
                "storage": "Storage"
              },
              "required": true,
              "widget": "select"
            }
          },
          "title": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Title",
            "description": "What the customer reads on the invoice; at most 120 characters.",
            "x-cis-field": {
              "required": true,
              "widget": "text"
            }
          },
          "cadence": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "monthly",
                  "annual"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "How often",
            "x-cis-field": {
              "enum_labels": {
                "annual": "Every year",
                "monthly": "Every month"
              },
              "required": true,
              "widget": "radio"
            }
          },
          "timing": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "in_advance",
                  "in_arrears"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Billed",
            "x-cis-field": {
              "enum_labels": {
                "in_advance": "In advance",
                "in_arrears": "In arrears"
              },
              "required": true,
              "widget": "radio"
            }
          },
          "basis": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "fixed",
                  "metered",
                  "cost_plus"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Amount",
            "x-cis-field": {
              "enum_labels": {
                "cost_plus": "The operator's cost plus a markup (pick and pack)",
                "fixed": "A fixed amount",
                "metered": "By quantity used"
              },
              "required": true,
              "widget": "radio"
            }
          },
          "amount_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Amount",
            "description": "The net amount billed each period, for a fixed charge.",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "basis": "fixed"
              },
              "widget": "money"
            }
          },
          "unit": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Unit",
            "description": "What one unit is called, for example pallet-week: a metered charge only.",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "basis": "metered"
              },
              "widget": "text"
            }
          },
          "rate_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Rate per unit",
            "description": "The net amount per unit, for a metered charge.",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "basis": "metered"
              },
              "widget": "money"
            }
          },
          "starts_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Starts on",
            "description": "The first day of the first period; a monthly charge starts on the 1st.",
            "x-cis-field": {
              "min_date": "today",
              "required": true,
              "widget": "date"
            }
          },
          "ends_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Ends on",
            "description": "The last day the charge bills for. The period that includes the day will still be billed in full. Leave empty for no end.",
            "x-cis-field": {
              "widget": "date"
            }
          },
          "operator_supplier_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Operator",
            "description": "The supplier that runs the warehouse and bills us for this work: a metered or cost-plus charge only.",
            "x-cis-field": {
              "entity": "suppliers.supplier",
              "widget": "entity"
            }
          },
          "markup_pct": {
            "anyOf": [
              {
                "type": "number"
              },
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Markup on the operator's cost (%)",
            "description": "Left out: the standard pick and pack markup.",
            "x-cis-field": {
              "show_if": {
                "basis": "cost_plus"
              },
              "widget": "number"
            }
          },
          "activities": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 10,
            "title": "Activities",
            "description": "For a fulfilment charge, exactly one activity: pick and pack, or an ad hoc request.",
            "x-cis-field": {}
          },
          "parameters": {
            "additionalProperties": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "integer"
                }
              ]
            },
            "type": "object",
            "maxProperties": 10,
            "title": "Details",
            "description": "For a storage charge, the location unit (pallet, bay or shelf) and the period (week or month).",
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request",
            "description": "One per dialog (the command's idempotency key): a repeat returns the first's charge.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "CreatePeriodChargeRequest",
        "description": "Add a period charge to a payer. The VAT treatment is derived from the dictionary, never sent."
      }
    },
    {
      "id": "finance_sales_ledger.record_period_usage",
      "module": "finance_sales_ledger_module",
      "label": "Record usage…",
      "description": "Finance confirms the quantity of one ended period of a metered or cost-plus charge; the period is then invoiced.",
      "target": "finance_sales_ledger.period_charge",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-ledger/period-charges/{charge_id}/actions/record-usage",
      "dry_run_path": "/sales-ledger/period-charges/{charge_id}/actions/record-usage/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "period_start": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Period",
            "description": "The first day of the ended period to record usage for (the dry run offers the earliest waiting).",
            "x-cis-field": {
              "required": true,
              "widget": "date"
            }
          },
          "quantity": {
            "anyOf": [
              {
                "type": "number"
              },
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity",
            "description": "From the operator's statement; 0 closes the period without an invoice. Left to the counted picks for a pick-and-pack charge.",
            "x-cis-field": {
              "required": true,
              "widget": "number"
            }
          },
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Statement reference",
            "description": "The operator's statement or invoice reference, at most 200 characters; never a name.",
            "x-cis-field": {
              "widget": "text"
            }
          },
          "counted_quantity": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Picks counted",
            "description": "Pick and pack: the picks the dry run counted (its guard).",
            "x-cis-field": {
              "hidden": true
            }
          },
          "operator_cost_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Operator cost",
            "description": "Pick and pack: the operator's cost of them, in pence (its guard).",
            "x-cis-field": {
              "hidden": true,
              "widget": "money"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordPeriodUsageRequest",
        "description": "Finance confirms the quantity of one ended period. A storage figure may have a fraction (it is rounded up to a whole\nunit); a pick-and-pack period is not typed: its quantity is the picks counted, shown by the dry run with the operator's\ncost, which ``counted_quantity`` and ``operator_cost_pence`` echo so a pick recorded since is a 409."
      }
    },
    {
      "id": "finance_sales_ledger.end_period_charge",
      "module": "finance_sales_ledger_module",
      "label": "End…",
      "description": "Finance ends a period charge; the period holding the end date still bills in full.",
      "target": "finance_sales_ledger.period_charge",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Changes how a payer is billed: period charges are ended in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/sales-ledger/period-charges/{charge_id}/actions/end",
      "dry_run_path": "/sales-ledger/period-charges/{charge_id}/actions/end/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "ends_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Last day it bills for",
            "description": "The period that includes the day will still be billed in full. No later period will be billed.",
            "x-cis-field": {
              "min_date": "today",
              "required": true,
              "widget": "date"
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "description": "Why, in up to 500 characters. Only staff will see the reason.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "EndPeriodChargeRequest"
      }
    },
    {
      "id": "finance_sales_ledger.withdraw_period_charge",
      "module": "finance_sales_ledger_module",
      "label": "Withdraw…",
      "description": "Finance withdraws a period charge that has billed nothing, with a reason.",
      "target": "finance_sales_ledger.period_charge",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Changes how a payer is billed: period charges are withdrawn in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/sales-ledger/period-charges/{charge_id}/actions/withdraw",
      "dry_run_path": "/sales-ledger/period-charges/{charge_id}/actions/withdraw/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "description": "Why, in up to 500 characters. Only staff will see the reason.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "WithdrawPeriodChargeRequest"
      }
    },
    {
      "id": "finance_sales_ledger.set_invoice_line_vat_treatment",
      "module": "finance_sales_ledger_module",
      "label": "Set VAT…",
      "description": "Finance sets a line's VAT treatment when its dictionary item had none; the order's due invoices issue.",
      "target": "orders.order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-ledger/orders/{order_no}/actions/set-line-vat-treatment",
      "dry_run_path": "/sales-ledger/orders/{order_no}/actions/set-line-vat-treatment/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "invoice_line_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Line",
            "description": "One of the order's lines without VAT.",
            "x-cis-field": {
              "required": true
            }
          },
          "vat_treatment": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "VAT treatment",
            "description": "Standard, reduced, zero-rated, exempt or no VAT.",
            "x-cis-field": {
              "required": true
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "description": "Why the line has this VAT treatment, in up to 500 characters.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetLineVatTreatmentRequest",
        "description": "Set VAT on one of the order's lines still waiting for a treatment (object UI doc 08 §2.2 #4). Every field may\nbe left out while the dry run is filling the choices; the commit answers a missing one 400 ``required``."
      }
    },
    {
      "id": "fulfilment_adapter_promo_office.resolve_held_up_item",
      "module": "fulfilment_adapter_promo_office_module",
      "label": "Resolve…",
      "description": "Retry an open Held up item's matching, or dismiss it with a reason so the PO stays unmatched.",
      "target": "fulfilment_adapter_promo_office.held_up_item",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/fulfilment-adapter-promo-office/actions/resolve-held-up-item",
      "dry_run_path": "/fulfilment-adapter-promo-office/actions/resolve-held-up-item/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "held_up_item_id": {
            "type": "string",
            "maxLength": 64,
            "title": "Held up item",
            "x-cis-field": {}
          },
          "resolution": {
            "type": "string",
            "enum": [
              "retry",
              "dismiss"
            ],
            "title": "Resolution",
            "x-cis-field": {
              "enum_labels": {
                "dismiss": "Dismiss",
                "retry": "Retry matching"
              }
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Why dismiss it",
            "x-cis-field": {
              "show_if": {
                "resolution": "dismiss"
              },
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "held_up_item_id",
          "resolution"
        ],
        "title": "ResolveHeldUpItemRequest",
        "description": "End an open Held up item. Field names match the validation errors' ``field``."
      }
    },
    {
      "id": "fulfilment_adapter_supplier_portal.resend_purchase_order",
      "module": "fulfilment_adapter_supplier_portal_module",
      "label": "Resend…",
      "description": "Email the purchase order to its supplier again, with a new link; earlier links still work.",
      "target": "purchase_orders.purchase_order",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/actions/resend",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/actions/resend/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "StaffSendRequest",
        "description": "*Resend* and *Chase*: nothing to choose; ``request_id`` makes a repeat return the first send."
      }
    },
    {
      "id": "fulfilment_adapter_supplier_portal.chase_purchase_order",
      "module": "fulfilment_adapter_supplier_portal_module",
      "label": "Chase…",
      "description": "Remind the supplier to answer a purchase order waiting on its acknowledgement, with a new link.",
      "target": "purchase_orders.purchase_order",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/actions/chase",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/actions/chase/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "StaffSendRequest",
        "description": "*Resend* and *Chase*: nothing to choose; ``request_id`` makes a repeat return the first send."
      }
    },
    {
      "id": "fulfilment_adapter_supplier_portal.revoke_purchase_order_links",
      "module": "fulfilment_adapter_supplier_portal_module",
      "label": "Revoke link…",
      "description": "Stop every emailed link to a purchase order working; a supplier login still reaches it.",
      "target": "purchase_orders.purchase_order",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/actions/revoke-links",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/actions/revoke-links/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "fulfilment_adapter_supplier_portal.record_supplier_proof",
      "module": "fulfilment_adapter_supplier_portal_module",
      "label": "Record their proof…",
      "description": "Record a proof a supplier sent by email or in person for a purchase order line, as you; it is checked as a proof the supplier uploads itself is.",
      "target": "purchase_orders.purchase_order_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/lines/{purchase_order_line_id}/actions/record-their-proof",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/lines/{purchase_order_line_id}/actions/record-their-proof/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "proof_file": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "Their proof",
            "description": "The proof the supplier sent: a PDF, PNG or JPEG of at most 25 MB.",
            "x-cis-field": {
              "accept": [
                "application/pdf",
                "image/png",
                "image/jpeg"
              ],
              "max_bytes": 26214400,
              "required": true,
              "upload": {
                "file_field": "file",
                "path": "/purchasing/supplier-proof-files",
                "ref_field": "ref"
              }
            }
          },
          "file_name": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 255
              },
              {
                "type": "null"
              }
            ],
            "title": "File name",
            "description": "Optional. The name to show for the proof, as the supplier named it. It shows as text, never as a path.",
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordTheirProofRequest",
        "description": "*Record their proof…*: a proof a supplier sent by email or in person, for one purchase order line. The file is\nuploaded first (``PROOF_FILE_UPLOAD``) and named by its ref. ``request_id`` is the attempt's id: a repeat records\nonce. The shell mints it at commit."
      },
      "proposal_class": "records"
    },
    {
      "id": "fulfilment_adapter_supplier_portal.decide_supplier_proof",
      "module": "fulfilment_adapter_supplier_portal_module",
      "label": "Decide proof…",
      "description": "Approve a supplier's proof, or send it back for changes with a note.",
      "target": "fulfilment_adapter_supplier_portal.supplier_proof",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/proofs/{proof_id}/actions/decide",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/proofs/{proof_id}/actions/decide/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "outcome": {
            "type": "string",
            "enum": [
              "approved",
              "changes_needed"
            ],
            "title": "Decision",
            "x-cis-field": {
              "enum_labels": {
                "approved": "Approve",
                "changes_needed": "Changes needed"
              },
              "widget": "radio"
            }
          },
          "note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 4000
              },
              {
                "type": "null"
              }
            ],
            "title": "Note to the supplier",
            "description": "What the supplier needs to change. Needed when the decision is Changes needed.",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "outcome"
        ],
        "title": "DecideSupplierProofRequest",
        "description": "*Approve* or *Changes needed* on a supplier's proof waiting for a person."
      },
      "proposal_class": "decides"
    },
    {
      "id": "issues.raise_issue",
      "module": "issues_module",
      "label": "Raise issue…",
      "description": "Raise an issue of an active type, optionally against a customer in your portfolio, a job and a supplier; numbered ISS-YYYY-NNNN.",
      "target": "collection:issues.issue",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/issues",
      "dry_run_path": "/issues/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "type_id": {
            "type": "string",
            "title": "Type",
            "description": "An active issue type from GET /issues/taxonomy.",
            "x-cis-field": {}
          },
          "title": {
            "type": "string",
            "title": "Title",
            "x-cis-field": {}
          },
          "description": {
            "type": "string",
            "title": "Description",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "initial_action": {
            "type": "string",
            "title": "Initial action",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "customer_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "x-cis-field": {}
          },
          "job_no": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Tharstern job number",
            "x-cis-field": {}
          },
          "supplier_no": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Supplier",
            "description": "The supplier the issue is about, if it is about one.",
            "x-cis-field": {
              "entity": "suppliers.supplier",
              "widget": "entity"
            }
          },
          "related_reference": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reference",
            "x-cis-field": {}
          },
          "assignee_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Assignee",
            "x-cis-field": {
              "entity": "users.user",
              "widget": "entity"
            }
          },
          "identified_by_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Identified by",
            "x-cis-field": {
              "entity": "users.user",
              "widget": "entity"
            }
          },
          "initial_investigation_department": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Investigating department",
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "type_id",
          "title",
          "description",
          "initial_action"
        ],
        "title": "IssueCreate",
        "description": "Raise issue…: the body of ``POST /issues`` and its dry run."
      }
    },
    {
      "id": "issues.resolve_issue",
      "module": "issues_module",
      "label": "Resolve issue",
      "description": "Resolve an open or investigating issue once its corrective action is recorded, and tell its raiser.",
      "target": "issues.issue",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/issues/{issue_id}/actions/resolve",
      "dry_run_path": "/issues/{issue_id}/actions/resolve/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "ResolveIssueRequest"
      },
      "main": true
    },
    {
      "id": "issues.add_note",
      "module": "issues_module",
      "label": "Add note…",
      "description": "Add a note to an issue's activity log and tell its assignee.",
      "target": "issues.issue",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/issues/{issue_id}/actions/add-note",
      "dry_run_path": "/issues/{issue_id}/actions/add-note/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note_body": {
            "type": "string",
            "title": "Note",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "note_body"
        ],
        "title": "AddNoteRequest",
        "description": "Add note…"
      }
    },
    {
      "id": "issues.assign_issue",
      "module": "issues_module",
      "label": "Assign…",
      "description": "Assign an unresolved issue to someone, or unassign it; only investigators assign it to someone else.",
      "target": "issues.issue",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/issues/{issue_id}/actions/assign",
      "dry_run_path": "/issues/{issue_id}/actions/assign/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "assignee_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Assignee",
            "x-cis-field": {
              "entity": "users.user",
              "widget": "entity"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AssignIssueRequest",
        "description": "Assign…: no assignee unassigns the issue."
      },
      "edits": {
        "fields": [
          "assignee_name"
        ]
      }
    },
    {
      "id": "issues.update_issue",
      "module": "issues_module",
      "label": "Amend details…",
      "description": "Amend an issue's type, title, description, initial action, reference, job or supplier; the raiser or an investigator.",
      "target": "issues.issue",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/issues/{issue_id}/actions/update",
      "dry_run_path": "/issues/{issue_id}/actions/update/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "type_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Type",
            "x-cis-field": {}
          },
          "title": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Title",
            "x-cis-field": {}
          },
          "description": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Description",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "initial_action": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Initial action",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "related_reference": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reference",
            "x-cis-field": {}
          },
          "job_no": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Tharstern job number",
            "x-cis-field": {}
          },
          "supplier_no": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Supplier",
            "description": "The supplier the issue is about. Clear the supplier when the issue isn't about a supplier.",
            "x-cis-field": {
              "entity": "suppliers.supplier",
              "widget": "entity"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "IssueUpdate",
        "description": "Amend details…: every field optional; at least one is required (the action answers 400)."
      }
    },
    {
      "id": "issues.upload_attachment",
      "module": "issues_module",
      "label": "Add attachment…",
      "description": "Attach a photo, PDF or Word document of at most 20 MB to an unresolved issue.",
      "target": "issues.issue",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Multipart file upload: done in the app.",
      "method": "POST",
      "path": "/issues/{issue_id}/attachments",
      "dry_run_path": "/issues/{issue_id}/attachments/dry-run",
      "content_type": "multipart",
      "request_schema": {
        "properties": {
          "file": {
            "type": "string",
            "contentMediaType": "application/octet-stream",
            "title": "File",
            "description": "A photo (JPEG, PNG, WebP, GIF), PDF or Word document of at most 20 MB.",
            "x-cis-field": {
              "widget": "file"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "file"
        ],
        "title": "UploadAttachmentForm",
        "description": "Add attachment…: the multipart body of ``POST /issues/{issue_id}/attachments`` and its dry run."
      }
    },
    {
      "id": "issues.start_investigation",
      "module": "issues_module",
      "label": "Start investigation",
      "description": "Move an open issue to investigating and tell its raiser.",
      "target": "issues.issue",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/issues/{issue_id}/actions/start-investigation",
      "dry_run_path": "/issues/{issue_id}/actions/start-investigation/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "StartInvestigationRequest"
      }
    },
    {
      "id": "issues.save_investigation",
      "module": "issues_module",
      "label": "Save investigation…",
      "description": "Save an issue's investigation findings, cause, cost, corrective action and resolution plan; an open issue moves to investigating.",
      "target": "issues.issue",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/issues/{issue_id}/actions/save-investigation-fields",
      "dry_run_path": "/issues/{issue_id}/actions/save-investigation-fields/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "investigation_findings": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Findings",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "investigation_identified_cause": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Identified cause",
            "x-cis-field": {}
          },
          "investigation_root_reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Root reason",
            "x-cis-field": {}
          },
          "investigation_attribution_department": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Attributed department",
            "x-cis-field": {}
          },
          "investigation_attribution_person_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Attributed person",
            "x-cis-field": {
              "entity": "users.user",
              "widget": "entity"
            }
          },
          "investigation_cost_direct": {
            "anyOf": [
              {
                "type": "number"
              },
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Direct cost",
            "x-cis-field": {}
          },
          "investigation_cost_indirect": {
            "anyOf": [
              {
                "type": "number"
              },
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Indirect cost",
            "x-cis-field": {}
          },
          "investigation_corrective_action": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Corrective action",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "resolution_assignee_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Resolution owner",
            "x-cis-field": {
              "entity": "users.user",
              "widget": "entity"
            }
          },
          "resolution_due_date": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Resolution due",
            "x-cis-field": {}
          },
          "resolution_actions_completed": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "Actions completed",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SaveInvestigationFieldsRequest",
        "description": "Save investigation…: every field optional; at least one is required (the action answers 400)."
      }
    },
    {
      "id": "issues.write_investigation_job_note",
      "module": "issues_module",
      "label": "Write Tharstern job note…",
      "description": "Write the investigation's note on the issue's Tharstern job (or update the one written before).",
      "target": "issues.issue",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/issues/{issue_id}/actions/write-job-note",
      "dry_run_path": "/issues/{issue_id}/actions/write-job-note/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note_body": {
            "type": "string",
            "title": "Note",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "note_body"
        ],
        "title": "WriteJobNoteRequest",
        "description": "Write Tharstern job note…: the note written on the issue's Tharstern job."
      }
    },
    {
      "id": "issues.reopen_issue",
      "module": "issues_module",
      "label": "Reopen issue…",
      "description": "Reopen a resolved issue into investigating, giving the reason, and tell its assignee.",
      "target": "issues.issue",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/issues/{issue_id}/actions/reopen",
      "dry_run_path": "/issues/{issue_id}/actions/reopen/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reopen_reason": {
            "type": "string",
            "title": "Reason",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "reopen_reason"
        ],
        "title": "ReopenIssueRequest",
        "description": "Reopen issue…: why it is reopened."
      }
    },
    {
      "id": "issues.delete_attachment",
      "module": "issues_module",
      "label": "Delete",
      "description": "Delete a file attached to an issue, its stored copy first; your own file unless you investigate.",
      "target": "issues.issue_attachment",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/issues/{issue_id}/actions/delete-attachment/{attachment_id}",
      "dry_run_path": "/issues/{issue_id}/actions/delete-attachment/{attachment_id}/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "journey_testing.sign_in_as_customer",
      "module": "journey_testing_module",
      "label": "Sign in as customer…",
      "description": "Mint a one-use link, valid for 60 seconds, that signs the browser in to this testing preview's customer portal as one of the customer's users.",
      "target": "customers.customer",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Signs a browser in as a customer's person: a tester's act in their own browser, never Claude's (PROVISIONAL, P12).",
      "method": "POST",
      "path": "/journey-testing/customers/{customer_code}/actions/sign-in",
      "dry_run_path": "/journey-testing/customers/{customer_code}/actions/sign-in/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "person_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Sign in as",
            "description": "A customer user of this customer.",
            "x-cis-field": {
              "entity": "customer_access.customer_link",
              "entity_value": "person_id",
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SignInAsCustomerRequest",
        "description": "*Sign in as customer…*: the customer user to open the customer portal as. The picker lists only this customer's\nactive customer users; the dry run starts it on the only one when there is just one."
      }
    },
    {
      "id": "journey_testing.sign_in_as_supplier",
      "module": "journey_testing_module",
      "label": "Sign in as supplier…",
      "description": "Mint a one-use link, valid for 60 seconds, that signs the browser in to this testing preview's supplier portal as one of the supplier's users.",
      "target": "suppliers.supplier",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Signs a browser in as a supplier's person: a tester's act in their own browser, never Claude's (PROVISIONAL, P12).",
      "method": "POST",
      "path": "/journey-testing/suppliers/{supplier_no}/actions/sign-in",
      "dry_run_path": "/journey-testing/suppliers/{supplier_no}/actions/sign-in/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "person_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Sign in as",
            "description": "A supplier user of this supplier.",
            "x-cis-field": {
              "entity": "supplier_access.supplier_person",
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SignInAsSupplierRequest",
        "description": "*Sign in as supplier…*: the supplier user to open the supplier portal as. The picker lists only this supplier's\nusers; the dry run starts it on the only one when there is just one."
      }
    },
    {
      "id": "marketing_audiences.create_audience",
      "module": "marketing_audiences_module",
      "label": "New audience…",
      "description": "Create a named audience with no rules, its standing recipients pinned in.",
      "target": "collection:marketing_audiences.audience",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/marketing/audiences",
      "dry_run_path": "/marketing/audiences/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "name": {
            "type": "string",
            "title": "Name",
            "x-cis-field": {}
          },
          "description": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Description",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "exclude_repeat_bouncers": {
            "type": "boolean",
            "title": "Leave out repeat bouncers",
            "default": true,
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "name"
        ],
        "title": "CreateAudienceRequest"
      }
    },
    {
      "id": "marketing_audiences.update_audience",
      "module": "marketing_audiences_module",
      "label": "Amend details…",
      "description": "Amend an audience's name, description and whether it leaves out repeat bouncers.",
      "target": "marketing_audiences.audience",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/marketing/audiences/{audience_id}/actions/update",
      "dry_run_path": "/marketing/audiences/{audience_id}/actions/update/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "name": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Name",
            "x-cis-field": {}
          },
          "description": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Description",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "exclude_repeat_bouncers": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "Leave out repeat bouncers",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "UpdateAudienceRequest",
        "description": "*Amend details…*: every field optional, so a dry run called with ``{}`` reaches the preview and its prefill;\na field left out is left unchanged."
      }
    },
    {
      "id": "marketing_audiences.set_audience_rules",
      "module": "marketing_audiences_module",
      "label": "Set rules…",
      "description": "Replace the audience's whole ordered rule set. Each rule's attribute and operator are checked before anything is saved.",
      "target": "marketing_audiences.audience",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/marketing/audiences/{audience_id}/actions/set-rules",
      "dry_run_path": "/marketing/audiences/{audience_id}/actions/set-rules/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "rules": {
            "anyOf": [
              {
                "items": {
                  "properties": {
                    "rule_type": {
                      "type": "string",
                      "enum": [
                        "include",
                        "exclude"
                      ],
                      "title": "Include or exclude",
                      "description": "Include adds the people who match. Exclude takes the people who match out again.",
                      "default": "include",
                      "x-cis-field": {
                        "enum_labels": {
                          "exclude": "Exclude",
                          "include": "Include"
                        }
                      }
                    },
                    "attribute_path": {
                      "type": "string",
                      "enum": [
                        "lifecycle_stage",
                        "has_email",
                        "has_phone",
                        "has_postal_address",
                        "consent.email",
                        "consent.phone",
                        "consent.direct_mail",
                        "consent.enclosure",
                        "company.linked",
                        "company.tharstern_linked",
                        "tags",
                        "is_marketing_eligible",
                        "has_contact_attribution",
                        "account.is_lapsed",
                        "account.is_weighbridge_customer",
                        "account.top_category",
                        "account.spend_l12m_rank",
                        "account.customer_status",
                        "account.profile",
                        "account.is_b2b2c",
                        "account.is_print_management",
                        "is_suppressed",
                        "marketing_suppression_rule",
                        "marketing_override_applied",
                        "account.buys_creative_l12m",
                        "account.buys_creative_prev12",
                        "account.buys_print_l12m",
                        "account.buys_print_prev12",
                        "account.buys_display_l12m",
                        "account.buys_display_prev12",
                        "account.buys_mailing_l12m",
                        "account.buys_mailing_prev12",
                        "account.buys_merch_l12m",
                        "account.buys_merch_prev12",
                        "account.buys_clothing_l12m",
                        "account.buys_clothing_prev12"
                      ],
                      "title": "Attribute",
                      "description": "What the rule checks about each contact.",
                      "x-cis-field": {
                        "enum_labels": {
                          "account.buys_clothing_l12m": "Buys clothing (last 12 months)",
                          "account.buys_clothing_prev12": "Buys clothing (months 13–24)",
                          "account.buys_creative_l12m": "Buys creative (last 12 months)",
                          "account.buys_creative_prev12": "Buys creative (months 13–24)",
                          "account.buys_display_l12m": "Buys display (last 12 months)",
                          "account.buys_display_prev12": "Buys display (months 13–24)",
                          "account.buys_mailing_l12m": "Buys mailing (last 12 months)",
                          "account.buys_mailing_prev12": "Buys mailing (months 13–24)",
                          "account.buys_merch_l12m": "Buys merch (last 12 months)",
                          "account.buys_merch_prev12": "Buys merch (months 13–24)",
                          "account.buys_print_l12m": "Buys print (last 12 months)",
                          "account.buys_print_prev12": "Buys print (months 13–24)",
                          "account.customer_status": "Customer status",
                          "account.is_b2b2c": "Public shop account (B2B2C)",
                          "account.is_lapsed": "Lapsed account",
                          "account.is_print_management": "Print management account",
                          "account.is_weighbridge_customer": "Weighbridge customer",
                          "account.profile": "Account profile",
                          "account.spend_l12m_rank": "Spend rank (last 12 months)",
                          "account.top_category": "Account top category",
                          "company.linked": "Linked to a company",
                          "company.tharstern_linked": "Linked to a Tharstern customer",
                          "consent.direct_mail": "Direct mail consent",
                          "consent.email": "Email consent",
                          "consent.enclosure": "Enclosure consent",
                          "consent.phone": "Phone consent",
                          "has_contact_attribution": "Has contact attribution",
                          "has_email": "Has email",
                          "has_phone": "Has phone",
                          "has_postal_address": "Has postal address",
                          "is_marketing_eligible": "Marketing eligible",
                          "is_suppressed": "Suppressed",
                          "lifecycle_stage": "Lifecycle stage",
                          "marketing_override_applied": "Suppression override applied",
                          "marketing_suppression_rule": "Suppression rule",
                          "tags": "Tag"
                        },
                        "required": true
                      }
                    },
                    "operator": {
                      "type": "string",
                      "enum": [
                        "eq",
                        "neq",
                        "in",
                        "not_in",
                        "contains",
                        "not_contains",
                        "gte",
                        "lte"
                      ],
                      "title": "Operator",
                      "description": "How the rule compares the attribute with the value. A yes or no attribute takes “is”, Tag takes “contains” and Spend rank takes “is at least” or “is at most”.",
                      "default": "eq",
                      "x-cis-field": {
                        "enum_labels": {
                          "contains": "contains",
                          "eq": "is",
                          "gte": "is at least",
                          "in": "is one of",
                          "lte": "is at most",
                          "neq": "is not",
                          "not_contains": "does not contain",
                          "not_in": "is not one of"
                        },
                        "required": true
                      }
                    },
                    "value": {
                      "anyOf": [
                        {
                          "type": "string"
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "Value",
                      "description": "What the rule compares the attribute with. For a yes or no attribute, leave the value empty to mean yes. For “is one of” or “is not one of”, separate the values with commas.",
                      "x-cis-field": {}
                    }
                  },
                  "additionalProperties": false,
                  "type": "object",
                  "required": [
                    "attribute_path"
                  ],
                  "title": "AudienceRuleRequest",
                  "description": "One row of *Set rules…*. Rules are saved in the order of the rows."
                },
                "type": "array",
                "maxItems": 100
              },
              {
                "type": "null"
              }
            ],
            "title": "Rules",
            "x-cis-field": {
              "add_rows": "Add rule",
              "widget": "table"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetAudienceRulesRequest",
        "description": "*Set rules…*: the whole ordered rule set, replacing the current one. Left out, the dry run prefills the\ncurrent rules and counts nothing."
      }
    },
    {
      "id": "marketing_audiences.add_manual_override",
      "module": "marketing_audiences_module",
      "label": "Pin in or out…",
      "description": "Pin one person into or out of an audience.",
      "target": "marketing_audiences.audience",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/marketing/audiences/{audience_id}/overrides",
      "dry_run_path": "/marketing/audiences/{audience_id}/overrides/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "person_id": {
            "type": "string",
            "title": "Person",
            "x-cis-field": {}
          },
          "override_type": {
            "type": "string",
            "enum": [
              "pin_in",
              "pin_out"
            ],
            "title": "Pin",
            "x-cis-field": {
              "enum_labels": {
                "pin_in": "Pin in",
                "pin_out": "Pin out"
              }
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "person_id",
          "override_type"
        ],
        "title": "AddManualOverrideRequest"
      }
    },
    {
      "id": "marketing_audiences.bulk_add_overrides",
      "module": "marketing_audiences_module",
      "label": "Pin by company…",
      "description": "Pin every contact of a company into or out of an audience.",
      "target": "marketing_audiences.audience",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/marketing/audiences/{audience_id}/overrides/bulk",
      "dry_run_path": "/marketing/audiences/{audience_id}/overrides/bulk/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "company_id": {
            "type": "string",
            "title": "Company",
            "x-cis-field": {}
          },
          "override_type": {
            "type": "string",
            "enum": [
              "pin_in",
              "pin_out"
            ],
            "title": "Pin",
            "x-cis-field": {
              "enum_labels": {
                "pin_in": "Pin in",
                "pin_out": "Pin out"
              }
            }
          },
          "has_email_only": {
            "type": "boolean",
            "title": "Only people with an email",
            "default": true,
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "company_id",
          "override_type"
        ],
        "title": "BulkAddOverridesRequest"
      }
    },
    {
      "id": "marketing_audiences.bulk_add_overrides_by_tag",
      "module": "marketing_audiences_module",
      "label": "Pin by tag…",
      "description": "Pin every contact carrying a tag into or out of an audience.",
      "target": "marketing_audiences.audience",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/marketing/audiences/{audience_id}/overrides/bulk-by-tag",
      "dry_run_path": "/marketing/audiences/{audience_id}/overrides/bulk-by-tag/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "tag": {
            "type": "string",
            "title": "Tag",
            "x-cis-field": {}
          },
          "override_type": {
            "type": "string",
            "enum": [
              "pin_in",
              "pin_out"
            ],
            "title": "Pin",
            "x-cis-field": {
              "enum_labels": {
                "pin_in": "Pin in",
                "pin_out": "Pin out"
              }
            }
          },
          "has_email_only": {
            "type": "boolean",
            "title": "Only people with an email",
            "default": true,
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "tag",
          "override_type"
        ],
        "title": "BulkAddOverridesByTagRequest"
      }
    },
    {
      "id": "marketing_audiences.bulk_add_overrides_by_emails",
      "module": "marketing_audiences_module",
      "label": "Pin by list…",
      "description": "Pin the contacts matching a pasted list of emails into or out of an audience.",
      "target": "marketing_audiences.audience",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/marketing/audiences/{audience_id}/overrides/bulk-by-emails",
      "dry_run_path": "/marketing/audiences/{audience_id}/overrides/bulk-by-emails/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "emails": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "title": "Emails",
            "description": "One email per contact to pin.",
            "x-cis-field": {}
          },
          "override_type": {
            "type": "string",
            "enum": [
              "pin_in",
              "pin_out"
            ],
            "title": "Pin",
            "x-cis-field": {
              "enum_labels": {
                "pin_in": "Pin in",
                "pin_out": "Pin out"
              }
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "emails",
          "override_type"
        ],
        "title": "BulkAddOverridesByEmailsRequest"
      }
    },
    {
      "id": "marketing_audiences.remove_manual_override",
      "module": "marketing_audiences_module",
      "label": "Unpin",
      "description": "Remove a person's pin, so the audience's rules decide for them again.",
      "target": "marketing_audiences.audience_manual_override",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/marketing/audiences/{audience_id}/pins/{person_id}/actions/remove",
      "dry_run_path": "/marketing/audiences/{audience_id}/pins/{person_id}/actions/remove/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "marketing_audiences.add_standing_recipient",
      "module": "marketing_audiences_module",
      "label": "Add standing recipient…",
      "description": "Add a standing recipient, pinned into every existing audience and every audience made afterwards.",
      "target": "collection:marketing_audiences.standing_recipient",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/marketing/audiences/standing-recipients",
      "dry_run_path": "/marketing/audiences/standing-recipients/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "email": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Email",
            "description": "The email of the contact to pin into every audience, e.g. nicky@example.com.",
            "x-cis-field": {
              "required": true
            }
          },
          "note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "description": "Why the contact is in every audience, e.g. external reviewer.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddStandingRecipientRequest",
        "description": "*Add standing recipient…*: the commit and the dry run take this body. *Remove…* takes none: the recipient is in\nthe path."
      }
    },
    {
      "id": "marketing_audiences.remove_standing_recipient",
      "module": "marketing_audiences_module",
      "label": "Remove…",
      "description": "Stop a standing recipient being pinned into audiences made from now on. Existing pins stay.",
      "target": "marketing_audiences.standing_recipient",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/marketing/audiences/standing-recipients/{standing_recipient_id}/actions/remove",
      "dry_run_path": "/marketing/audiences/standing-recipients/{standing_recipient_id}/actions/remove/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "marketing_leads.create_lead_source",
      "module": "marketing_leads_module",
      "label": "New lead source…",
      "description": "Create a lead source for a new landing page or lead magnet, active from the start.",
      "target": "collection:marketing_leads.lead_source",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/leads/sources",
      "dry_run_path": "/leads/sources/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "slug": {
            "type": "string",
            "maxLength": 100,
            "title": "Slug",
            "description": "The short name the website form sends submissions to, e.g. summer-flyers. The slug can't be changed after the lead source is created.",
            "x-cis-field": {}
          },
          "name": {
            "type": "string",
            "maxLength": 200,
            "title": "Name",
            "x-cis-field": {}
          },
          "ticket_category_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Ticket category",
            "x-cis-field": {}
          },
          "product_keywords": {
            "anyOf": [
              {
                "items": {
                  "type": "string"
                },
                "type": "array"
              },
              {
                "type": "null"
              }
            ],
            "title": "Product keywords",
            "x-cis-field": {}
          },
          "marketing_categories": {
            "anyOf": [
              {
                "items": {
                  "type": "string",
                  "enum": [
                    "clothing",
                    "merch",
                    "print",
                    "display",
                    "mailing",
                    "creative"
                  ]
                },
                "type": "array"
              },
              {
                "type": "null"
              }
            ],
            "title": "Product categories",
            "x-cis-field": {
              "enum_labels": {
                "clothing": "Clothing & workwear",
                "creative": "Creative",
                "display": "Display",
                "mailing": "Mailing",
                "merch": "Merch",
                "other": "Other",
                "print": "Print"
              }
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "slug",
          "name"
        ],
        "title": "CreateLeadSourceRequest",
        "description": "The commit and the dry run of *New lead source…* take this body (object contract, R4)."
      }
    },
    {
      "id": "marketing_leads.update_lead_source",
      "module": "marketing_leads_module",
      "label": "Amend details…",
      "description": "Amend a lead source's name, ticket category, product scoping, default sales rep and launch date.",
      "target": "marketing_leads.lead_source",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/leads/sources/{lead_source_id}/actions/update",
      "dry_run_path": "/leads/sources/{lead_source_id}/actions/update/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "name": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Name",
            "x-cis-field": {}
          },
          "ticket_category_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Ticket category",
            "x-cis-field": {}
          },
          "product_keywords": {
            "anyOf": [
              {
                "items": {
                  "type": "string"
                },
                "type": "array"
              },
              {
                "type": "null"
              }
            ],
            "title": "Product keywords",
            "x-cis-field": {}
          },
          "marketing_categories": {
            "anyOf": [
              {
                "items": {
                  "type": "string",
                  "enum": [
                    "clothing",
                    "merch",
                    "print",
                    "display",
                    "mailing",
                    "creative"
                  ]
                },
                "type": "array"
              },
              {
                "type": "null"
              }
            ],
            "title": "Product categories",
            "x-cis-field": {
              "enum_labels": {
                "clothing": "Clothing & workwear",
                "creative": "Creative",
                "display": "Display",
                "mailing": "Mailing",
                "merch": "Merch",
                "other": "Other",
                "print": "Print"
              }
            }
          },
          "launched_at": {
            "anyOf": [
              {
                "type": "string",
                "format": "date-time"
              },
              {
                "type": "null"
              }
            ],
            "title": "Went live",
            "x-cis-field": {
              "widget": "date"
            }
          },
          "default_sales_rep_user_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Default sales rep",
            "x-cis-field": {
              "entity": "users.user",
              "widget": "entity"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "UpdateLeadSourceRequest",
        "description": "*Amend details…*: every field optional, so a dry run called with ``{}`` reaches the preview and its prefill.\nA field left out is left unchanged; a field sent as null is cleared. Whether the source takes submissions is not\nhere: *Make inactive…* and *Make active* own it."
      },
      "main": true
    },
    {
      "id": "marketing_leads.reactivate_lead_source",
      "module": "marketing_leads_module",
      "label": "Make active",
      "description": "Make an inactive lead source active again, so its submissions are taken.",
      "target": "marketing_leads.lead_source",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/leads/sources/{lead_source_id}/actions/reactivate",
      "dry_run_path": "/leads/sources/{lead_source_id}/actions/reactivate/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "marketing_leads.deactivate_lead_source",
      "module": "marketing_leads_module",
      "label": "Make inactive…",
      "description": "Make a lead source inactive, so the website form's submissions to it are refused.",
      "target": "marketing_leads.lead_source",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/leads/sources/{lead_source_id}/actions/deactivate",
      "dry_run_path": "/leads/sources/{lead_source_id}/actions/deactivate/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "marketing_triggers.approve_trigger_list",
      "module": "marketing_triggers_module",
      "label": "Approve and send…",
      "description": "Approve a trigger's daily list; the people to send to are tagged in Mailchimp and emailed by its flow.",
      "target": "marketing_triggers.trigger",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Sends customer email, which can't be taken back: approved in the app, never by an assistant.",
      "method": "POST",
      "path": "/marketing/triggers/{trigger_id}/actions/approve-list",
      "dry_run_path": "/marketing/triggers/{trigger_id}/actions/approve-list/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "removed_firing_ids": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "title": "Removed",
            "default": [],
            "x-cis-field": {}
          },
          "picked_contacts": {
            "additionalProperties": {
              "type": "string"
            },
            "type": "object",
            "title": "Picked contacts",
            "default": {},
            "x-cis-field": {}
          },
          "list_date": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "List date",
            "x-cis-field": {
              "widget": "date"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ApproveTriggerListRequest",
        "description": "*Approve and send…*: today's list, less the \"To send\" rows removed, plus each \"Needs you\" row's pick."
      }
    },
    {
      "id": "marketing_triggers.update_trigger",
      "module": "marketing_triggers_module",
      "label": "Save settings",
      "description": "Change a trigger's sender, approval and catch-up settings.",
      "target": "marketing_triggers.trigger",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/marketing/triggers/{trigger_id}/actions/update-trigger",
      "dry_run_path": "/marketing/triggers/{trigger_id}/actions/update-trigger/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "from_name": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "From",
            "x-cis-field": {}
          },
          "check_before_sending": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "Check before sending",
            "x-cis-field": {}
          },
          "catch_up_days": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Also include people who qualified in the last [ ] days",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "UpdateTriggerRequest",
        "description": "*Save settings*: every field optional; one left out is left as it is."
      }
    },
    {
      "id": "marketing_triggers.set_trigger_status",
      "module": "marketing_triggers_module",
      "label": "Pause / Switch back on…",
      "description": "Switch a trigger on, or pause it.",
      "target": "marketing_triggers.trigger",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/marketing/triggers/{trigger_id}/actions/set-status",
      "dry_run_path": "/marketing/triggers/{trigger_id}/actions/set-status/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "status": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "live",
                  "paused"
                ],
                "title": "TriggerStatus"
              },
              {
                "type": "null"
              }
            ],
            "title": "Status",
            "x-cis-field": {
              "enum_labels": {
                "live": "Live",
                "paused": "Paused"
              }
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetTriggerStatusRequest",
        "description": "*Pause / Switch back on…*."
      }
    },
    {
      "id": "needs_attention.acknowledge_alert",
      "module": "needs_attention_module",
      "label": "Acknowledge",
      "description": "Acknowledge an alert that is yours; it leaves the CSR home panel and its ticket stays open.",
      "target": "needs_attention.alert",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/needs-attention/alerts/{alert_id}/actions/acknowledge",
      "dry_run_path": "/needs-attention/alerts/{alert_id}/actions/acknowledge/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "AcknowledgeAlertRequest",
        "description": "Acknowledge takes nothing: who and when are the request's."
      },
      "main": true
    },
    {
      "id": "needs_attention.mark_alert_done",
      "module": "needs_attention_module",
      "label": "Mark done…",
      "description": "Mark done an alert that only a person can clear (a shortage, a delivery change); its ticket is resolved.",
      "target": "needs_attention.alert",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/needs-attention/alerts/{alert_id}/actions/mark-done",
      "dry_run_path": "/needs-attention/alerts/{alert_id}/actions/mark-done/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "description": "Say what you did, if it helps the next person (at most 500 characters).",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "MarkAlertDoneRequest"
      }
    },
    {
      "id": "needs_attention.update_attention_rule",
      "module": "needs_attention_module",
      "label": "Edit rule…",
      "description": "Edit a rule's label, on or off, owner and department, ticket, due working days, priority or threshold.",
      "target": "needs_attention.rule",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/needs-attention/rules/{code}/actions/update",
      "dry_run_path": "/needs-attention/rules/{code}/actions/update/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "label": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 80
              },
              {
                "type": "null"
              }
            ],
            "title": "Label",
            "x-cis-field": {}
          },
          "is_enabled": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "On",
            "x-cis-field": {}
          },
          "owner": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "customer_csr",
                  "department"
                ],
                "title": "RuleOwner",
                "description": "Who a rule's alerts go to: the customer's CSR first (the department when there is none), or the department."
              },
              {
                "type": "null"
              }
            ],
            "title": "Goes to",
            "x-cis-field": {
              "enum_labels": {
                "customer_csr": "The customer's CSR, else the department",
                "department": "The department"
              }
            }
          },
          "department_slug": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Department",
            "x-cis-field": {}
          },
          "raises_ticket": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "Raises a ticket",
            "x-cis-field": {}
          },
          "due_working_days": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Due, in working days",
            "description": "0 to 10 working days.",
            "x-cis-field": {}
          },
          "priority": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "low",
                  "normal",
                  "high",
                  "urgent"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Priority",
            "x-cis-field": {
              "enum_labels": {
                "high": "High",
                "low": "Low",
                "normal": "Normal",
                "urgent": "Urgent"
              }
            }
          },
          "threshold": {
            "anyOf": [
              {
                "oneOf": [
                  {
                    "properties": {
                      "unit": {
                        "type": "string",
                        "const": "immediately",
                        "title": "Unit",
                        "default": "immediately"
                      }
                    },
                    "additionalProperties": false,
                    "type": "object",
                    "title": "Immediately",
                    "description": "The condition alerts as soon as it holds."
                  },
                  {
                    "properties": {
                      "unit": {
                        "type": "string",
                        "const": "hours",
                        "title": "Unit",
                        "default": "hours"
                      },
                      "hours": {
                        "type": "integer",
                        "maximum": 168.0,
                        "minimum": 1.0,
                        "title": "Hours"
                      }
                    },
                    "additionalProperties": false,
                    "type": "object",
                    "required": [
                      "hours"
                    ],
                    "title": "AfterHours",
                    "description": "The condition alerts once it has held for this many hours."
                  },
                  {
                    "properties": {
                      "unit": {
                        "type": "string",
                        "const": "working_days",
                        "title": "Unit",
                        "default": "working_days"
                      },
                      "days": {
                        "type": "integer",
                        "maximum": 20.0,
                        "minimum": 1.0,
                        "title": "Days"
                      }
                    },
                    "additionalProperties": false,
                    "type": "object",
                    "required": [
                      "days"
                    ],
                    "title": "AfterWorkingDays",
                    "description": "The condition alerts once it has held for this many working days."
                  }
                ],
                "discriminator": {
                  "propertyName": "unit",
                  "mapping": {
                    "hours": "#/components/schemas/AfterHours",
                    "immediately": "#/components/schemas/Immediately",
                    "working_days": "#/components/schemas/AfterWorkingDays"
                  }
                }
              },
              {
                "type": "null"
              }
            ],
            "title": "After",
            "x-cis-field": {}
          },
          "is_on_csr_home": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "On the CSR home",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "UpdateAttentionRuleRequest",
        "description": "A partial edit: only the fields sent change."
      }
    },
    {
      "id": "order_channel_brandhub.check_brandhub_intake",
      "module": "order_channel_brandhub_module",
      "label": "Check again",
      "description": "Decide the stored BrandHub order again and show the decision One would make now beside the one on record. The preview writes nothing. Doing it records the new decision as the order's latest (shown while the customer's BrandHub orders are in shadow) and creates nothing; it may start live supplier price requests.",
      "target": "order_channel_brandhub.intake",
      "destructive": false,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/order-channel-brandhub/intakes/{channel_ref}/actions/check-again",
      "dry_run_path": "/order-channel-brandhub/intakes/{channel_ref}/actions/check-again/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "CheckBrandhubIntakeRequest",
        "description": "Check again takes nothing: the BrandHub order is in the path."
      }
    },
    {
      "id": "order_channel_brandhub.place_brandhub_shadow_order_for_real",
      "module": "order_channel_brandhub_module",
      "label": "Place for real…",
      "description": "TEMPORARY, removed when BrandHub orders go live. Let the purchase orders of one shadow-created, print on demand BrandHub order go to the supplier when its lines are released. Records who, when and why on the intake; sends nothing itself. Purchase orders already refused as shadow are placed by a person with Place again.",
      "target": "order_channel_brandhub.intake",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "It lets supplier orders go out for real, so a person decides it in the app (Ben, 23 Oct 2026).",
      "method": "POST",
      "path": "/order-channel-brandhub/intakes/{channel_ref}/actions/place-for-real",
      "dry_run_path": "/order-channel-brandhub/intakes/{channel_ref}/actions/place-for-real/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "maxLength": 2000,
            "title": "Reason",
            "description": "Why this order is being placed with its suppliers for real. The reason will stay on the intake.",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "PlaceBrandhubShadowOrderForRealRequest",
        "description": "Place for real… (temporary): the reason is kept on the intake."
      }
    },
    {
      "id": "order_channel_promo_office.resolve_held_up_item",
      "module": "order_channel_promo_office_module",
      "label": "Resolve…",
      "description": "Retry an open Held up item, link its order lines by hand, or dismiss it so the document stays out of CIS.",
      "target": "order_channel_promo_office.held_up_item",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/order-channel-promo-office/actions/resolve-held-up-item",
      "dry_run_path": "/order-channel-promo-office/actions/resolve-held-up-item/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "held_up_item_id": {
            "type": "string",
            "maxLength": 64,
            "title": "Held up item",
            "x-cis-field": {}
          },
          "resolution": {
            "type": "string",
            "enum": [
              "retry",
              "link_lines",
              "dismiss"
            ],
            "title": "Resolution",
            "x-cis-field": {
              "enum_labels": {
                "dismiss": "Dismiss",
                "link_lines": "Link the order lines",
                "retry": "Retry"
              }
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Why dismiss it",
            "x-cis-field": {
              "show_if": {
                "resolution": "dismiss"
              },
              "widget": "textarea"
            }
          },
          "line_links": {
            "anyOf": [
              {
                "additionalProperties": {
                  "type": "string"
                },
                "type": "object"
              },
              {
                "type": "null"
              }
            ],
            "title": "Order lines to link",
            "description": "When linking the order lines: each unlinked Promo Office line, and the order line (ORD-K48193.01) it matches.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "held_up_item_id",
          "resolution"
        ],
        "title": "ResolveHeldUpItemRequest",
        "description": "End an open Held up item. Field names match the validation errors' ``field``."
      }
    },
    {
      "id": "order_channel_promo_office.link_alternative_quote",
      "module": "order_channel_promo_office_module",
      "label": "Link an alternative quote…",
      "description": "Link a second draft quote to a tender as an alternative; at conversion the one the order matches is kept.",
      "target": "order_channel_promo_office.tender_link",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/order-channel-promo-office/actions/link-alternative-quote",
      "dry_run_path": "/order-channel-promo-office/actions/link-alternative-quote/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "tender_number": {
            "type": "string",
            "maxLength": 64,
            "title": "Promo Office tender",
            "x-cis-field": {}
          },
          "quote_no": {
            "type": "string",
            "maxLength": 32,
            "title": "Draft quote",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "tender_number",
          "quote_no"
        ],
        "title": "LinkAlternativeQuoteRequest",
        "description": "Link a second draft quote to a tender as an alternative (spec 12 §6)."
      }
    },
    {
      "id": "order_channel_promo_office.request_merch_quote",
      "module": "order_channel_promo_office_module",
      "label": "New merch quote…",
      "description": "Start a merch quote for a customer: an empty draft quote (its QT number is what the merch team types in the tender's Customer Ref in Promo Office), the merch team's ticket with the brief, and their email; a repeat of the same request returns the first draft quote.",
      "target": "collection:quotes.quote",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/order-channel-promo-office/actions/request-merch-quote",
      "dry_run_path": "/order-channel-promo-office/actions/request-merch-quote/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "customer_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 50
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "description": "The customer number, for example CUS-K48193, or the customer's Tharstern code.",
            "x-cis-field": {
              "entity": "customers.customer",
              "required": true,
              "widget": "entity"
            }
          },
          "contact_person_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Contact",
            "description": "One of the customer's people; left out, chosen later, before the quote is published.",
            "x-cis-field": {
              "entity": "customer_access.customer_link",
              "entity_value": "person_id",
              "widget": "entity"
            }
          },
          "project_name": {
            "type": "string",
            "maxLength": 200,
            "title": "Project name",
            "description": "The draft quote's title until the tender's project name replaces it.",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "deadline": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Deadline",
            "description": "When the customer needs the merch by.",
            "x-cis-field": {
              "min_date": "today",
              "required": true
            }
          },
          "notes": {
            "type": "string",
            "maxLength": 5000,
            "title": "Notes",
            "description": "Anything the merch team should know.",
            "default": "",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "catalogue_item_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100
              },
              {
                "type": "null"
              }
            ],
            "title": "Catalogue item",
            "description": "The quoted-to-order catalogue item the quote is about. Set when opened from the item; it starts the first line with the item's name and description.",
            "x-cis-field": {
              "entity": "catalogue.catalogue_item",
              "widget": "entity"
            }
          },
          "lines": {
            "items": {
              "properties": {
                "item_type": {
                  "type": "string",
                  "maxLength": 500,
                  "title": "Item",
                  "description": "For example, ceramic mug, white, 300ml.",
                  "default": "",
                  "x-cis-field": {}
                },
                "quantity": {
                  "type": "string",
                  "maxLength": 200,
                  "title": "Quantity",
                  "description": "Enter one number, or the price breaks to quote, such as 100 / 250 / 500.",
                  "default": "",
                  "x-cis-field": {}
                },
                "logo_colours": {
                  "type": "string",
                  "maxLength": 1000,
                  "title": "Logo colours",
                  "description": "Colours or Pantone references, or \"None\".",
                  "default": "",
                  "x-cis-field": {}
                },
                "budget": {
                  "type": "string",
                  "maxLength": 200,
                  "title": "Budget (optional)",
                  "description": "For example, £2000, or under £1000.",
                  "default": "",
                  "x-cis-field": {}
                },
                "sourcing_city_code": {
                  "type": "string",
                  "maxLength": 100,
                  "title": "Sourcing City code (optional)",
                  "description": "The product ID in Promo Office's catalogue.",
                  "default": "",
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "title": "MerchQuoteLineRow",
              "description": "One line of the brief: what the customer wants quoted. Blank text is accepted here and refused by the action."
            },
            "type": "array",
            "maxItems": 50,
            "title": "Request lines",
            "description": "One line per item the customer wants quoted.",
            "x-cis-field": {
              "add_rows": "Add request line",
              "required": true,
              "widget": "table"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RequestMerchQuoteRequest",
        "description": "*New merch quote…*: an empty draft quote for the customer (the QT number the merch team types on the tender),\nthe merch team's ticket with the brief, and their email. ``request_id`` makes a repeat return the draft quote the\nfirst request made."
      }
    },
    {
      "id": "orders.release_order_lines",
      "module": "orders_module",
      "label": "Release…",
      "description": "Release order lines to their supplier, production or stock once their checks pass.",
      "target": "orders.order",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/actions/release",
      "dry_run_path": "/orders/{order_no}/actions/release/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "lines": {
            "items": {
              "properties": {
                "line_ref": {
                  "type": "string",
                  "pattern": "^ORD-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}\\.[0-9]{2,}$",
                  "title": "Line",
                  "description": "The order line, e.g. ORD-K48193.01: the dry run names each ready line; the tick is the choice.",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "is_confirmed": {
                  "type": "boolean",
                  "title": "File and brief match the order",
                  "description": "The tick the person makes.",
                  "default": false,
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "line_ref"
              ],
              "title": "LineToReleaseRequest"
            },
            "type": "array",
            "maxItems": 100,
            "title": "Lines",
            "description": "The lines to release; the dry run prefills every ready line, unticked.",
            "x-cis-field": {
              "required": true,
              "selectable": true,
              "widget": "table"
            }
          },
          "placement": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "place_now",
                  "hold_to_place_together"
                ],
                "title": "ReleasePlacement",
                "description": "For a bought line: ask for its purchase order to be placed now, or once the order's other lines for the same\nsupplier are released (the set)."
              },
              {
                "type": "null"
              }
            ],
            "title": "Placement",
            "description": "For bought lines: place now, or hold to place together. The dry run offers the choice.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ReleaseLinesRequest"
      },
      "main": true
    },
    {
      "id": "orders.set_order_review_before_release",
      "module": "orders_module",
      "label": "Review before release…",
      "description": "Make an order's lines wait for a person to press Release, or release as soon as nothing is outstanding.",
      "target": "orders.order",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/actions/set-review-before-release",
      "dry_run_path": "/orders/{order_no}/actions/set-review-before-release/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "review_before_release": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "Review before release",
            "description": "On: lines on this order wait for someone to press Release. Off: they release as soon as nothing is outstanding. Overrides the customer's setting for this order.",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetOrderReviewBeforeReleaseRequest",
        "description": "*Review before release* on this order: on, its lines wait for someone to press Release. A dry run may leave it\nempty; the commit then answers 400 ``required``."
      },
      "edits": {
        "fields": [
          "review_before_release"
        ]
      }
    },
    {
      "id": "orders.log_order_contact",
      "module": "orders_module",
      "label": "Log a call…",
      "description": "Record a phone call with the customer about this order. It restarts the order's reminders to them.",
      "target": "orders.order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/actions/log-contact",
      "dry_run_path": "/orders/{order_no}/actions/log-contact/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "spoken_to": {
            "type": "string",
            "maxLength": 120,
            "title": "Who you spoke to",
            "description": "Who you spoke to.",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "note": {
            "type": "string",
            "maxLength": 2000,
            "title": "What they said",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "LogContactRequest",
        "description": "A call with the customer about the order (the action box's *Contact {customer}*): who, what; it restarts the\ncustomer chase. A supplier call is the purchase order's."
      }
    },
    {
      "id": "orders.set_order_contact",
      "module": "orders_module",
      "label": "Set contact…",
      "description": "Set the customer's person the order's emails and pay links go to.",
      "target": "orders.order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/actions/set-order-contact",
      "dry_run_path": "/orders/{order_no}/actions/set-order-contact/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "person_id": {
            "type": "string",
            "maxLength": 64,
            "title": "Contact",
            "description": "One of the order's customer's people.",
            "default": "",
            "x-cis-field": {
              "entity": "customer_access.customer_link",
              "entity_value": "person_id",
              "required": true,
              "widget": "entity"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetOrderContactRequest",
        "description": "*Set contact…*: one of the order's customer's people, picked by name from the Customer users list (the dry run\nscopes the picker to that customer's active users); the body carries the person's id."
      },
      "edits": {
        "fields": [
          "contact_name"
        ]
      }
    },
    {
      "id": "orders.record_customer_order_number",
      "module": "orders_module",
      "label": "Add PO number…",
      "description": "Record the customer's PO number on an order, by staff or by the customer.",
      "target": "orders.order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/actions/record-customer-order-number",
      "dry_run_path": "/orders/{order_no}/actions/record-customer-order-number/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "customer_order_number": {
            "type": "string",
            "maxLength": 200,
            "title": "PO number",
            "description": "The customer's PO number, 1–50 characters.",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordCustomerOrderNumberRequest",
        "description": "*Add PO number*: staff record the customer's PO number. Bounds once trimmed are the action's (400s)."
      },
      "edits": {
        "fields": [
          "customer_order_number"
        ]
      }
    },
    {
      "id": "orders.change_order_payment_method",
      "module": "orders_module",
      "label": "Change payment method…",
      "description": "Move an order between card, credit account and pro-forma while no money or credit is settled.",
      "target": "orders.order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/actions/change-payment-method",
      "dry_run_path": "/orders/{order_no}/actions/change-payment-method/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "payment_method": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "card",
                  "proforma",
                  "credit_account",
                  "paid_by_channel"
                ],
                "title": "PaymentMethod",
                "description": "How a payer may pay. Credit account brings terms, a limit and a PO rule."
              },
              {
                "type": "null"
              }
            ],
            "title": "Payment method",
            "x-cis-field": {
              "enum_labels": {
                "card": "Card",
                "credit_account": "Credit account",
                "paid_by_channel": "Paid by channel",
                "proforma": "Bank transfer (pro-forma)"
              },
              "required": true,
              "widget": "radio"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ChangePaymentMethodRequest"
      }
    },
    {
      "id": "orders.change_order_trading_company",
      "module": "orders_module",
      "label": "Change trading company…",
      "description": "Move an order to another trading company before an invoice, a card payment or money on a pro-forma exists under its own; an unpaid pro-forma is voided and issued again by the new company, and the order's purchase orders are bought for it.",
      "target": "orders.order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/actions/change-trading-company",
      "dry_run_path": "/orders/{order_no}/actions/change-trading-company/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "trading_company_code": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Trading company",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ChangeTradingCompanyRequest",
        "description": "*Change trading company…*. The dry run offers the trading companies that have not stopped, starting on the order's\nown; a dry run called with ``{}`` reads as that."
      }
    },
    {
      "id": "orders.start_card_payment",
      "module": "orders_module",
      "label": "Make pay link…",
      "description": "Start or resume the card checkout for a card order, or a later addition to it.",
      "target": "orders.order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/actions/start-card-payment",
      "dry_run_path": "/orders/{order_no}/actions/start-card-payment/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "added_line_ref": {
            "anyOf": [
              {
                "type": "string",
                "pattern": "^ORD-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}\\.[0-9]{2,}$"
              },
              {
                "type": "null"
              }
            ],
            "title": "Addition",
            "description": "The first line of a later addition to pay for.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "StartCardPaymentRequest",
        "description": "*Make pay link*: the card checkout for the order, or for a later addition (``added_line_ref``). Stripe returns\nthe customer to the order's own staff page."
      }
    },
    {
      "id": "orders.agree_delivery_price",
      "module": "orders_module",
      "label": "Set delivery price…",
      "description": "Set the price the customer agreed for a delivery checkout could not price, so its goods can be released.",
      "target": "orders.order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/actions/agree-delivery-price",
      "dry_run_path": "/orders/{order_no}/actions/agree-delivery-price/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "delivery": {
            "type": "string",
            "maxLength": 400,
            "title": "Delivery",
            "description": "Which of the order's unpriced deliveries: the value of the dry run's `delivery` choice.",
            "default": "",
            "x-cis-field": {
              "hidden": true
            }
          },
          "price_pence": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 100000000.0,
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Price agreed",
            "x-cis-field": {
              "required": true,
              "widget": "money"
            }
          },
          "reason": {
            "type": "string",
            "maxLength": 200,
            "title": "Why this price",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "cost_pence": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 100000000.0,
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Courier's cost",
            "description": "What the courier charges us.",
            "x-cis-field": {
              "widget": "money"
            }
          },
          "courier_supplier_no": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Courier",
            "description": "The courier booked for it, by its supplier number (SUP-…).",
            "x-cis-field": {
              "entity": "suppliers.supplier",
              "widget": "entity"
            }
          },
          "confirmed": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 5,
            "title": "Confirmed",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AgreeDeliveryPriceRequest",
        "description": "*Set delivery price…*: staff set the price the customer agreed for a delivery not yet priced (doc 104 §5.6): the\ndelivery (the dry run lists the order's own and preselects the only one), the price ex VAT, why, the courier and\nits cost when one is booked, and the ticked confirmation that the customer agreed the price."
      }
    },
    {
      "id": "orders.set_order_part_deliveries",
      "module": "orders_module",
      "label": "Part deliveries…",
      "description": "Choose whether an order's from-stock lines may be delivered in parts as stock arrives, or only whole, or follow the customer's setting.",
      "target": "orders.order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/actions/set-part-deliveries",
      "dry_run_path": "/orders/{order_no}/actions/set-part-deliveries/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "part_deliveries": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "use_customer_setting",
                  "accept",
                  "do_not_accept"
                ],
                "title": "PartDeliveriesChoice",
                "description": "What a person chooses for an order's \"Part deliveries\": follow the customer's setting, or decide for this order."
              },
              {
                "type": "null"
              }
            ],
            "title": "Part deliveries",
            "description": "Accept: a from-stock line the warehouse holds only some of is sent in parts as stock arrives. Do not accept: it waits until all of it is in stock. Use the customer's setting: this order follows its customer.",
            "x-cis-field": {
              "enum_labels": {
                "accept": "Accept",
                "do_not_accept": "Do not accept",
                "use_customer_setting": "Use the customer's setting"
              },
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetOrderPartDeliveriesRequest",
        "description": "*Part deliveries…* on this order: whether its from-stock lines may be delivered in parts as stock arrives. A dry\nrun may leave it empty; the commit then answers 400 ``required``."
      },
      "edits": {
        "fields": [
          "part_deliveries"
        ]
      }
    },
    {
      "id": "orders.set_order_delivery_updates",
      "module": "orders_module",
      "label": "Delivery updates…",
      "description": "Send an order's delivery updates as its customer's setting says, or choose who gets them for this order.",
      "target": "orders.order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/actions/set-delivery-updates",
      "dry_run_path": "/orders/{order_no}/actions/set-delivery-updates/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "follow": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "customer",
                  "own"
                ],
                "title": "DeliveryUpdatesFollow",
                "description": "What a person chooses for an order's delivery updates: as its customer's setting says, or its own."
              },
              {
                "type": "null"
              }
            ],
            "title": "Delivery updates",
            "x-cis-field": {
              "enum_labels": {
                "customer": "As the customer",
                "own": "Choose for this order"
              },
              "required": true
            }
          },
          "to_placer": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "Who placed the order",
            "x-cis-field": {
              "show_if": {
                "follow": "own"
              }
            }
          },
          "to_recipient": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "The recipient of each delivery",
            "x-cis-field": {
              "show_if": {
                "follow": "own"
              }
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetOrderDeliveryUpdatesRequest",
        "description": "*Delivery updates…* on an order: follow the customer's setting, or choose who gets them for this order. The two\nboxes are read only for \"Choose for this order\"."
      },
      "edits": {
        "fields": [
          "delivery_updates_to"
        ]
      }
    },
    {
      "id": "orders.set_customer_delivery_updates",
      "module": "orders_module",
      "label": "Delivery updates…",
      "description": "Choose who is emailed as a customer's deliveries move, and whether delivery problems go to the customer straight away.",
      "target": "customers.customer",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/customers/{customer_code}/actions/set-delivery-updates",
      "dry_run_path": "/orders/customers/{customer_code}/actions/set-delivery-updates/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "to_placer": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "Who placed the order",
            "description": "Delivery updates go to the person who placed the order.",
            "x-cis-field": {}
          },
          "to_recipient": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "The recipient of each delivery",
            "description": "Delivery updates go to each delivery's recipient, where the delivery has a recipient's email.",
            "x-cis-field": {}
          },
          "problems": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "staff_first",
                  "straight_through"
                ],
                "title": "DeliveryProblemsChoice",
                "description": "How a customer's returns and carrier exceptions are told: staff look first, or the customer is told at once."
              },
              {
                "type": "null"
              }
            ],
            "title": "Delivery problems",
            "description": "Whether a return to sender or a carrier exception goes to the customer's CSR first, or to the customer straight away. A missed delivery or a parcel left at a parcel shop is always told at once.",
            "x-cis-field": {
              "enum_labels": {
                "staff_first": "Staff look first at returns and exceptions",
                "straight_through": "Tell the customer straight away"
              },
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetCustomerDeliveryUpdatesRequest",
        "description": "*Delivery updates…* on a customer (customer delivery updates P02): who the delivery update emails go to on every\norder that follows the customer, and how its returns and exceptions are told. The dry run opens on the customer's\nsetting; a field left out of a dry run reads as that."
      },
      "edits": {
        "fields": [
          "delivery_updates_to",
          "delivery_problems"
        ]
      }
    },
    {
      "id": "orders.set_order_deliver_together",
      "module": "orders_module",
      "label": "Deliver together…",
      "description": "Send an order's lines for one supplier together while that keeps them on time, or place each as it releases.",
      "target": "orders.order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/actions/set-deliver-together",
      "dry_run_path": "/orders/{order_no}/actions/set-deliver-together/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "deliver_together": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "Deliver together",
            "description": "When on, lines are timed to arrive together, as long as that keeps them on time. Otherwise each line goes to its supplier as soon as it releases.",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetOrderDeliverTogetherRequest",
        "description": "*Deliver together* on this order. A dry run may leave it empty; the commit then answers 400 ``required``."
      },
      "edits": {
        "fields": [
          "deliver_together"
        ]
      }
    },
    {
      "id": "orders.book_courier_despatch",
      "module": "orders_module",
      "label": "Book with courier…",
      "description": "Book one consignment from our warehouse with DPD or DX, record it despatched under the courier's number and print the label.",
      "target": "orders.order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/actions/book-courier-despatch",
      "dry_run_path": "/orders/{order_no}/actions/book-courier-despatch/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "despatched_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Despatched on",
            "x-cis-field": {
              "required": true
            }
          },
          "carrier": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "dpd",
                  "dx"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Courier",
            "x-cis-field": {
              "required": true
            }
          },
          "service_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 40
              },
              {
                "type": "null"
              }
            ],
            "title": "Service",
            "x-cis-field": {}
          },
          "parcels": {
            "items": {
              "properties": {
                "weight_kg": {
                  "type": "number",
                  "maximum": 30.0,
                  "exclusiveMinimum": 0.0,
                  "title": "Weight (kg)",
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "weight_kg"
              ],
              "title": "CourierParcelRequest",
              "description": "One parcel of a courier booking, by its gross weight."
            },
            "type": "array",
            "maxItems": 99,
            "title": "Parcels",
            "x-cis-field": {
              "widget": "table"
            }
          },
          "contents": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 25
              },
              {
                "type": "null"
              }
            ],
            "title": "Contents",
            "description": "What the label says is inside; 'Printed matter' unless set.",
            "x-cis-field": {}
          },
          "items": {
            "items": {
              "properties": {
                "order_no": {
                  "type": "string",
                  "maxLength": 32,
                  "minLength": 1,
                  "title": "Order",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "line_ref": {
                  "type": "string",
                  "pattern": "^ORD-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}\\.[0-9]{2,}$",
                  "title": "Line",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "description": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10000
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "What",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "destination": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10000
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Goes to",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "delivery_line_id": {
                  "type": "string",
                  "maxLength": 64,
                  "minLength": 1,
                  "title": "Delivery line",
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "quantity": {
                  "type": "integer",
                  "maximum": 10000000.0,
                  "minimum": 1.0,
                  "title": "Quantity",
                  "x-cis-field": {
                    "fill_label": "All",
                    "max_from": "quantity_left"
                  }
                },
                "quantity_left": {
                  "anyOf": [
                    {
                      "type": "integer"
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Left to send",
                  "readOnly": true,
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "order_no",
                "line_ref",
                "delivery_line_id",
                "quantity"
              ],
              "title": "DespatchItemRequest",
              "description": "One delivery line sent in a consignment, as the table shows it: the dry run fills the display columns (the\norder, the line, what it is, where it goes, what is left to send) and the shell sends the ticked row whole. The\ncommit reads the order, the line, the delivery line and the quantity; the action refuses a delivery line that is\nnot on that line."
            },
            "type": "array",
            "maxItems": 100,
            "title": "Lines",
            "x-cis-field": {
              "required": true,
              "selectable": true,
              "widget": "table"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "BookCourierDespatchRequest",
        "description": "*Book with DPD / DX and print label* (courier chain plan 01): *Record despatched*'s consignment (this order's\ndelivery lines and, ticked, other orders' for the same address), booked with the courier ``carrier`` names on\n``service_code`` (from the dry run's ``services``) with these parcels, then recorded despatched under the\ncourier's consignment number. ``request_id`` is the booking's idempotency key: a retry with the same one never\nbooks twice. The dry run, given a carrier and items, prefills the parcels from the stock items' weights and lists\nthe courier's services with the default."
      }
    },
    {
      "id": "orders.record_despatched_together",
      "module": "orders_module",
      "label": "Record consignment despatched…",
      "description": "Record a consignment that has left our warehouse, of this order's from-stock lines and, ticked, other orders' for the same address, sent together.",
      "target": "orders.order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/actions/record-despatched-together",
      "dry_run_path": "/orders/{order_no}/actions/record-despatched-together/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "despatched_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Left on",
            "x-cis-field": {
              "required": true
            }
          },
          "carrier": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "dpd",
                  "dx",
                  "royal_mail",
                  "parcelforce",
                  "evri",
                  "ups",
                  "fedex",
                  "tnt",
                  "dhl_express",
                  "dhl_parcel_uk",
                  "yodel",
                  "inpost",
                  "apc_overnight",
                  "pall_ex",
                  "palletforce",
                  "palletways",
                  "the_pallet_network",
                  "xdp",
                  "own_van",
                  "other"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Carrier",
            "x-cis-field": {
              "enum_labels": {
                "apc_overnight": "APC Overnight",
                "dhl_express": "DHL Express",
                "dhl_parcel_uk": "DHL Parcel UK",
                "dpd": "DPD",
                "dx": "DX",
                "evri": "Evri",
                "fedex": "FedEx",
                "inpost": "InPost",
                "other": "Another carrier",
                "own_van": "Our own van",
                "pall_ex": "Pall-Ex",
                "palletforce": "Palletforce",
                "palletways": "Palletways",
                "parcelforce": "Parcelforce",
                "royal_mail": "Royal Mail",
                "the_pallet_network": "The Pallet Network",
                "tnt": "TNT",
                "ups": "UPS",
                "xdp": "XDP",
                "yodel": "Yodel"
              }
            }
          },
          "carrier_name": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100
              },
              {
                "type": "null"
              }
            ],
            "title": "Carrier's name",
            "description": "Required when the carrier is 'other'.",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "carrier": "other"
              }
            }
          },
          "tracking_number": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Tracking number",
            "description": "Never for our own van.",
            "x-cis-field": {
              "show_if": {
                "carrier": [
                  "dpd",
                  "dx",
                  "royal_mail",
                  "parcelforce",
                  "evri",
                  "ups",
                  "fedex",
                  "tnt",
                  "dhl_express",
                  "dhl_parcel_uk",
                  "yodel",
                  "inpost",
                  "apc_overnight",
                  "pall_ex",
                  "palletforce",
                  "palletways",
                  "the_pallet_network",
                  "xdp",
                  "other"
                ]
              }
            }
          },
          "items": {
            "items": {
              "properties": {
                "order_no": {
                  "type": "string",
                  "maxLength": 32,
                  "minLength": 1,
                  "title": "Order",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "line_ref": {
                  "type": "string",
                  "pattern": "^ORD-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}\\.[0-9]{2,}$",
                  "title": "Line",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "description": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10000
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "What",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "destination": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10000
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Goes to",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "delivery_line_id": {
                  "type": "string",
                  "maxLength": 64,
                  "minLength": 1,
                  "title": "Delivery line",
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "quantity": {
                  "type": "integer",
                  "maximum": 10000000.0,
                  "minimum": 1.0,
                  "title": "Quantity",
                  "x-cis-field": {
                    "fill_label": "All",
                    "max_from": "quantity_left"
                  }
                },
                "quantity_left": {
                  "anyOf": [
                    {
                      "type": "integer"
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Left to send",
                  "readOnly": true,
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "order_no",
                "line_ref",
                "delivery_line_id",
                "quantity"
              ],
              "title": "DespatchItemRequest",
              "description": "One delivery line sent in a consignment, as the table shows it: the dry run fills the display columns (the\norder, the line, what it is, where it goes, what is left to send) and the shell sends the ticked row whole. The\ncommit reads the order, the line, the delivery line and the quantity; the action refuses a delivery line that is\nnot on that line."
            },
            "type": "array",
            "maxItems": 100,
            "title": "Lines in this consignment",
            "x-cis-field": {
              "required": true,
              "selectable": true,
              "widget": "table"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordDespatchedTogetherRequest",
        "description": "*Record consignment despatched…*: a consignment that has left our warehouse (doc 102 §4.1): this order's delivery\nlines (the dry run lists those waiting to go, unticked) and, ticked, other orders' for the same address, with one\ncarrier and tracking reference. Each order keeps its own delivery charge. ``request_id`` is the consignment's\nidempotency key (each row's despatch reference is built from it): a retry with the same one records once. The\nshell mints it at commit; a commit without it is refused."
      }
    },
    {
      "id": "orders.add_kits_to_order",
      "module": "orders_module",
      "label": "Add kits…",
      "description": "Add kit lines from a drop list, identical drops folded into one line, their goods sent to the kitter.",
      "target": "orders.order",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/actions/add-kits",
      "dry_run_path": "/orders/{order_no}/actions/add-kits/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "kitted_at": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "supplier",
                  "our_warehouse"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Kitted at",
            "x-cis-field": {
              "enum_labels": {
                "our_warehouse": "Our warehouse",
                "supplier": "A supplier"
              },
              "required": true
            }
          },
          "supplier_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Supplier",
            "x-cis-field": {
              "entity": "suppliers.supplier",
              "required": true,
              "show_if": {
                "kitted_at": "supplier"
              },
              "widget": "entity"
            }
          },
          "kit_warehouse_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Warehouse",
            "x-cis-field": {}
          },
          "cost_per_kit_pence": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 10000000.0,
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Their cost per kit",
            "description": "From their quote.",
            "x-cis-field": {
              "widget": "money"
            }
          },
          "contents": {
            "items": {
              "properties": {
                "line_ref": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 32
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Line",
                  "description": "e.g. ORD-K61204.03, or .03.",
                  "x-cis-field": {}
                },
                "stock_item_ref": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 64
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Stock item",
                  "x-cis-field": {}
                },
                "version_label": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 8
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Version",
                  "description": "A line with several versions: the one every kit takes; empty when each drop names its own.",
                  "x-cis-field": {}
                },
                "per_kit": {
                  "anyOf": [
                    {
                      "type": "integer",
                      "maximum": 100000.0,
                      "minimum": 0.0
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Per kit",
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "title": "KitContentRequest",
              "description": "One thing one kit holds: a line of this order or a stock item of the customer's, and how many per kit."
            },
            "type": "array",
            "maxItems": 100,
            "title": "Contents",
            "x-cis-field": {
              "custom_widget": "add_kits",
              "required": true,
              "widget": "table"
            }
          },
          "drops": {
            "items": {
              "properties": {
                "address_ref": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 64
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Address reference",
                  "description": "The address's id or its Tharstern code.",
                  "x-cis-field": {}
                },
                "name": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 200
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Address",
                  "x-cis-field": {}
                },
                "address": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 500
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Address lines",
                  "x-cis-field": {}
                },
                "town": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 100
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Town",
                  "x-cis-field": {}
                },
                "postcode": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 16
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Postcode",
                  "x-cis-field": {}
                },
                "country_code": {
                  "anyOf": [
                    {
                      "type": "string",
                      "pattern": "^[A-Za-z]{2}$"
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Country",
                  "x-cis-field": {}
                },
                "kits": {
                  "anyOf": [
                    {
                      "type": "integer",
                      "maximum": 100000.0,
                      "minimum": 1.0
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Kits",
                  "x-cis-field": {}
                },
                "variations": {
                  "items": {
                    "properties": {
                      "content": {
                        "type": "integer",
                        "maximum": 99.0,
                        "minimum": 0.0,
                        "title": "Content",
                        "description": "The content's position in contents.",
                        "x-cis-field": {}
                      },
                      "version_label": {
                        "anyOf": [
                          {
                            "type": "string",
                            "maxLength": 8
                          },
                          {
                            "type": "null"
                          }
                        ],
                        "title": "Version",
                        "x-cis-field": {}
                      },
                      "per_kit": {
                        "anyOf": [
                          {
                            "type": "integer",
                            "maximum": 100000.0,
                            "minimum": 0.0
                          },
                          {
                            "type": "null"
                          }
                        ],
                        "title": "Per kit",
                        "x-cis-field": {}
                      }
                    },
                    "additionalProperties": false,
                    "type": "object",
                    "required": [
                      "content"
                    ],
                    "title": "DropVariationRequest",
                    "description": "How one drop's kit differs for one content: the version it takes, or how many per kit (0: none)."
                  },
                  "type": "array",
                  "maxItems": 100,
                  "title": "Differs by",
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "title": "KitDropRequest",
              "description": "One drop: a number of kits to one place, one of the customer's delivery addresses or one to create."
            },
            "type": "array",
            "maxItems": 2000,
            "title": "Drops",
            "x-cis-field": {
              "custom_widget": "add_kits",
              "widget": "table"
            }
          },
          "drops_csv": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 1000000
              },
              {
                "type": "null"
              }
            ],
            "title": "Paste or upload the drop list",
            "description": "Columns: address reference or name, address and postcode; kits; a version for any content that differs by drop.",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "copy_drops_from_order_no": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "Copy from a previous order",
            "description": "e.g. ORD-K58816.",
            "x-cis-field": {}
          },
          "price_per_kit_pence": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 10000000.0,
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Price to the customer, per kit",
            "x-cis-field": {
              "required": true,
              "widget": "money"
            }
          },
          "delivery": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "agreed",
                  "included",
                  "customer_carrier",
                  "courier_per_drop"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Delivery of the drops",
            "x-cis-field": {
              "enum_labels": {
                "agreed": "One agreed price for all drops",
                "courier_per_drop": "Courier price per drop",
                "customer_carrier": "Customer's own carrier",
                "included": "Included by the kitter"
              },
              "required": true
            }
          },
          "delivery_price_pence": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 100000000.0,
                "minimum": 0.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Agreed price",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "delivery": "agreed"
              },
              "widget": "money"
            }
          },
          "delivery_price_reason": {
            "type": "string",
            "maxLength": 200,
            "title": "Why this price",
            "default": "",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "delivery": "agreed"
              }
            }
          },
          "expected_kit_lines": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Kit lines as shown",
            "description": "The folding the dry run showed (its guard).",
            "x-cis-field": {
              "hidden": true
            }
          },
          "confirmed": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 5,
            "title": "Confirmed",
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddKitsRequest",
        "description": "*Add kits…* (V1 completion R §9): where the kits are packed, what one kit holds, the drops, the price per kit\nand how the drops are delivered. ``drops_csv`` and ``copy_drops_from_order_no`` are read by the dry run into\n``drops`` (and ``contents``) to send back; the commit adds what ``drops`` says."
      }
    },
    {
      "id": "orders.add_order_to_project",
      "module": "orders_module",
      "label": "Add to project…",
      "description": "Put an order that is in no project into an existing project of its payer, or a new one.",
      "target": "orders.order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/actions/add-to-project",
      "dry_run_path": "/orders/{order_no}/actions/add-to-project/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "project": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "existing",
                  "new"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Add to",
            "x-cis-field": {
              "enum_labels": {
                "existing": "One of the payer's projects",
                "new": "A new project"
              },
              "required": true,
              "widget": "radio"
            }
          },
          "project_no": {
            "anyOf": [
              {
                "type": "string",
                "pattern": "^PRJ-[ACDEFGHJKLMNPQRTUVWXY]{1,2}[0-9]{5}$"
              },
              {
                "type": "null"
              }
            ],
            "title": "Project",
            "description": "One of the payer's projects, e.g. PRJ-D30512.",
            "x-cis-field": {
              "entity": "orders.project",
              "required": true,
              "show_if": {
                "project": "existing"
              },
              "widget": "entity"
            }
          },
          "new_project_title": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 120
              },
              {
                "type": "null"
              }
            ],
            "title": "New project's name",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "project": "new"
              }
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddToProjectRequest",
        "description": "*Add to project…*: ``project`` says which of the two the order joins (the dialog opens on \"existing\"); then\nexactly one of an existing project of the order's payer (``project_no``, picked from the payer's projects) or the\ntitle of a new one. Without ``project`` (a caller sending the value alone) the action refuses neither and both, on\n``project_no``; with it, the action ignores the value of the other choice."
      },
      "edits": {
        "fields": [
          "project_no"
        ]
      }
    },
    {
      "id": "orders.resend_order_email",
      "module": "orders_module",
      "label": "Resend order email",
      "description": "Send the order's contact the order's email again, everything outstanding listed, with a fresh link.",
      "target": "orders.order",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/actions/resend-email",
      "dry_run_path": "/orders/{order_no}/actions/resend-email/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ResendOrderEmailRequest",
        "description": "*Resend order email*: no fields but the attempt's id (a repeat sends nothing more)."
      }
    },
    {
      "id": "orders.cancel_order",
      "module": "orders_module",
      "label": "Cancel order…",
      "description": "Cancel every line of an order not already cancelled, services before the items they serve, all or nothing, refunding or crediting what was paid.",
      "target": "orders.order",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/actions/cancel",
      "dry_run_path": "/orders/{order_no}/actions/cancel/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "maxLength": 2000,
            "title": "Reason",
            "description": "Why the order is cancelled, 1–500 characters once trimmed; kept on every line it cancels.",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "CancelOrderRequest",
        "description": "*Cancel order*: every line not already cancelled, with one reason kept on each."
      }
    },
    {
      "id": "orders.hold_order_line_release",
      "module": "orders_module",
      "label": "Hold…",
      "description": "Hold a line's release with a reason.",
      "target": "orders.order_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/lines/{line_ref}/actions/hold-release",
      "dry_run_path": "/orders/{order_no}/lines/{line_ref}/actions/hold-release/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "maxLength": 2000,
            "title": "Reason",
            "description": "Why the line is held, 1–500 characters once trimmed.",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "HoldReleaseRequest"
      },
      "main": true
    },
    {
      "id": "orders.place_held_line_now",
      "module": "orders_module",
      "label": "Place now",
      "description": "Ask for a held line's purchase order to be placed now, with the lines held on it.",
      "target": "orders.order_line",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/lines/{line_ref}/actions/place-now",
      "dry_run_path": "/orders/{order_no}/lines/{line_ref}/actions/place-now/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "PlaceHeldLineNowRequest",
        "description": "*Place now*: no fields."
      }
    },
    {
      "id": "orders.confirm_order_line_date",
      "module": "orders_module",
      "label": "Confirm date",
      "description": "Confirm the date of a line whose promise is \"check with our team\", so the line can be released.",
      "target": "orders.order_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/lines/{line_ref}/actions/confirm-date",
      "dry_run_path": "/orders/{order_no}/lines/{line_ref}/actions/confirm-date/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "ConfirmLineDateRequest",
        "description": "*Confirm date* on a line whose date is \"check with our team\". It takes nothing: it firms the date the line's\nschedule gives now."
      }
    },
    {
      "id": "orders.repromise_order_line",
      "module": "orders_module",
      "label": "Re-promise…",
      "description": "Give the customer another date for a line, with a reason, and say whether they have been told.",
      "target": "orders.order_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/lines/{line_ref}/actions/repromise",
      "dry_run_path": "/orders/{order_no}/lines/{line_ref}/actions/repromise/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "arrival_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "New date",
            "description": "The date to promise the customer.",
            "x-cis-field": {
              "required": true
            }
          },
          "reason": {
            "type": "string",
            "maxLength": 2000,
            "title": "Reason",
            "description": "Why the date is changing, 1–500 characters once trimmed. Kept on the promise.",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "customer_told": {
            "type": "boolean",
            "title": "The customer has been told",
            "description": "Tick when you have already given them this date.",
            "default": false,
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RepromiseLineRequest",
        "description": "*Re-promise* a line: the date to give the customer instead, why, and whether they have been told. A dry run may\nleave any of it empty; the commit then answers 400 with the field."
      }
    },
    {
      "id": "orders.record_made_line_despatched",
      "module": "orders_module",
      "label": "Record despatched…",
      "description": "Record part or all of a made line despatched to one of its delivery lines; its first despatch bills it.",
      "target": "orders.order_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/lines/{line_ref}/actions/record-despatched",
      "dry_run_path": "/orders/{order_no}/lines/{line_ref}/actions/record-despatched/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "despatched_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Despatched on",
            "x-cis-field": {
              "required": true
            }
          },
          "delivery_line_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64,
                "minLength": 1
              },
              {
                "type": "null"
              }
            ],
            "title": "Delivery line",
            "x-cis-field": {}
          },
          "quantity": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 10000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity",
            "x-cis-field": {}
          },
          "carrier": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "dpd",
                  "dx",
                  "royal_mail",
                  "parcelforce",
                  "evri",
                  "ups",
                  "fedex",
                  "tnt",
                  "dhl_express",
                  "dhl_parcel_uk",
                  "yodel",
                  "inpost",
                  "apc_overnight",
                  "pall_ex",
                  "palletforce",
                  "palletways",
                  "the_pallet_network",
                  "xdp",
                  "own_van",
                  "other"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Carrier",
            "x-cis-field": {}
          },
          "carrier_name": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100
              },
              {
                "type": "null"
              }
            ],
            "title": "Carrier name",
            "description": "Required when the carrier is 'other'.",
            "x-cis-field": {}
          },
          "tracking_number": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Tracking number",
            "description": "Never for our own van.",
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordMadeLineDespatchedRequest",
        "description": "Part or all of a made line sent to one of its delivery lines (V1 completion E1). ``quantity`` and\n``delivery_line_id`` may be left out when the line has one delivery line, which then goes whole. ``request_id`` is\nthe despatch's idempotency key, unique on the line: a retry with the same one records once. The shell mints it at\ncommit; a commit without it is refused."
      }
    },
    {
      "id": "orders.record_kit_drops_despatched",
      "module": "orders_module",
      "label": "Record drops despatched…",
      "description": "Record a number of an in-house kit's drops despatched at once, with the carrier and each parcel's tracking number.",
      "target": "orders.order_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/lines/{line_ref}/actions/record-drops-despatched",
      "dry_run_path": "/orders/{order_no}/lines/{line_ref}/actions/record-drops-despatched/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "despatched_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Despatched on",
            "x-cis-field": {
              "required": true
            }
          },
          "carrier": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "dpd",
                  "dx",
                  "royal_mail",
                  "parcelforce",
                  "evri",
                  "ups",
                  "fedex",
                  "tnt",
                  "dhl_express",
                  "dhl_parcel_uk",
                  "yodel",
                  "inpost",
                  "apc_overnight",
                  "pall_ex",
                  "palletforce",
                  "palletways",
                  "the_pallet_network",
                  "xdp",
                  "own_van",
                  "other"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Carrier",
            "x-cis-field": {}
          },
          "carrier_name": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100
              },
              {
                "type": "null"
              }
            ],
            "title": "Carrier name",
            "description": "Required when the carrier is 'other'.",
            "x-cis-field": {}
          },
          "drops": {
            "items": {
              "properties": {
                "drop": {
                  "type": "integer",
                  "maximum": 2000.0,
                  "minimum": 1.0,
                  "title": "Drop",
                  "description": "The drop's number on the kit line, from 1.",
                  "x-cis-field": {}
                },
                "tracking_number": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 64
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Tracking number",
                  "x-cis-field": {}
                },
                "carrier": {
                  "anyOf": [
                    {
                      "type": "string",
                      "enum": [
                        "dpd",
                        "dx",
                        "royal_mail",
                        "parcelforce",
                        "evri",
                        "ups",
                        "fedex",
                        "tnt",
                        "dhl_express",
                        "dhl_parcel_uk",
                        "yodel",
                        "inpost",
                        "apc_overnight",
                        "pall_ex",
                        "palletforce",
                        "palletways",
                        "the_pallet_network",
                        "xdp",
                        "own_van",
                        "other"
                      ]
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Carrier",
                  "description": "Left out: the despatch's carrier.",
                  "x-cis-field": {}
                },
                "carrier_name": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 100
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Carrier name",
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "drop"
              ],
              "title": "KitDropDespatchRequest",
              "description": "One drop of a kit line that has gone: its number on the kit line (as the Kits tab and the kit manifest number\nit), and the carrier and tracking number of its parcel when they differ from the despatch's."
            },
            "type": "array",
            "maxItems": 2000,
            "title": "Drops",
            "x-cis-field": {
              "custom_widget": "despatch_kit_drops",
              "widget": "table"
            }
          },
          "drops_csv": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 1000000
              },
              {
                "type": "null"
              }
            ],
            "title": "Paste or upload the courier's list",
            "description": "Columns: drop (its number), tracking number, carrier.",
            "x-cis-field": {
              "custom_widget": "despatch_kit_drops",
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordKitDropsDespatchedRequest",
        "description": "*Despatch together…* on an in-house kit (V1 completion R5): the drops that have gone, with one date and carrier\nand, for each, its tracking number. ``drops`` left empty starts from every drop still waiting; ``drops_csv`` (a\npasted or uploaded list: drop, tracking number, carrier) is read by the dry run into ``drops`` to send back; the\ncommit records what ``drops`` says. ``request_id`` makes a retry record once, per drop."
      }
    },
    {
      "id": "orders.set_line_packing_note",
      "module": "orders_module",
      "label": "Packing note…",
      "description": "Set how the warehouse packs a from-stock line.",
      "target": "orders.order_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/lines/{line_ref}/actions/set-packing-note",
      "dry_run_path": "/orders/{order_no}/lines/{line_ref}/actions/set-packing-note/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "packing_note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Packing note",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetPackingNoteRequest",
        "description": "How the warehouse should pack a from-stock line: shown to the warehouse only. Empty or null clears it."
      }
    },
    {
      "id": "orders.set_line_artwork_source",
      "module": "orders_module",
      "label": "Artwork from…",
      "description": "Choose who provides an item line's artwork, the customer or our studio (R251).",
      "target": "orders.order_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/lines/{line_ref}/actions/set-artwork-source",
      "dry_run_path": "/orders/{order_no}/lines/{line_ref}/actions/set-artwork-source/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "artwork_source": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "customer",
                  "studio"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Artwork from",
            "x-cis-field": {
              "enum_labels": {
                "customer": "The customer",
                "studio": "Our studio"
              },
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetArtworkSourceRequest",
        "description": "*Artwork from…* on an item line (R251): who provides its artwork. Our studio: the customer is asked for no file\nand never chased for one; staff upload the studio's file and the customer still approves the proof."
      }
    },
    {
      "id": "orders.set_order_version_labels",
      "module": "orders_module",
      "label": "Name versions…",
      "description": "Name the versions of a line with two or more, until a version's file is in.",
      "target": "orders.order_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/lines/{line_ref}/actions/set-version-labels",
      "dry_run_path": "/orders/{order_no}/lines/{line_ref}/actions/set-version-labels/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "versions": {
            "items": {
              "properties": {
                "position": {
                  "type": "integer",
                  "maximum": 26.0,
                  "minimum": 1.0,
                  "title": "Version",
                  "description": "The version's position on the line, from 1.",
                  "x-cis-field": {}
                },
                "label": {
                  "type": "string",
                  "maxLength": 50,
                  "title": "Label",
                  "description": "1–8 characters, unique on the line.",
                  "x-cis-field": {}
                },
                "name": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 500
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Name",
                  "description": "Optional, up to 120 characters.",
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "position",
                "label"
              ],
              "title": "OrderVersionLabelRequest"
            },
            "type": "array",
            "maxItems": 26,
            "title": "Versions",
            "description": "Every version, once; the dry run prefills them as they are.",
            "x-cis-field": {
              "required": true,
              "widget": "table"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "OrderVersionLabelsRequest"
      }
    },
    {
      "id": "orders.record_line_tharstern_job",
      "module": "orders_module",
      "label": "Record Tharstern job…",
      "description": "Record the Tharstern job raised by hand for a made line released to production.",
      "target": "orders.order_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/lines/{line_ref}/actions/record-tharstern-job",
      "dry_run_path": "/orders/{order_no}/lines/{line_ref}/actions/record-tharstern-job/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "tharstern_job_no": {
            "type": "string",
            "maxLength": 50,
            "title": "Tharstern job",
            "description": "The Tharstern job raised by hand: six digits, or with its sub-job (740715.01).",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordTharsternJobRequest"
      }
    },
    {
      "id": "orders.cancel_order_line",
      "module": "orders_module",
      "label": "Cancel line…",
      "description": "Cancel a line not yet released, or the rest of a part-sent one, refunding or crediting what was paid.",
      "target": "orders.order_line",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/lines/{line_ref}/actions/cancel",
      "dry_run_path": "/orders/{order_no}/lines/{line_ref}/actions/cancel/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "maxLength": 2000,
            "title": "Reason",
            "description": "Why the line is cancelled, 1–500 characters once trimmed.",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "quantity": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity to cancel",
            "description": "Cancel the rest (doc 98): what the dry run showed as not yet sent (its guard); required on a part-sent line, ignored otherwise. 409 stale when more has been sent since.",
            "x-cis-field": {}
          },
          "shares": {
            "anyOf": [
              {
                "items": {
                  "properties": {
                    "delivery_line_id": {
                      "type": "string",
                      "maxLength": 64,
                      "title": "Delivery line",
                      "description": "One of the line's delivery lines.",
                      "x-cis-field": {}
                    },
                    "quantity": {
                      "type": "integer",
                      "title": "Quantity",
                      "description": "What this delivery line loses: 0 up to what has not reached it.",
                      "x-cis-field": {}
                    }
                  },
                  "additionalProperties": false,
                  "type": "object",
                  "required": [
                    "delivery_line_id",
                    "quantity"
                  ],
                  "title": "CancelRestShareRequest"
                },
                "type": "array",
                "maxItems": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "By address",
            "description": "A bought line going to several addresses: what each loses, adding up to quantity. Omitted: the offered split (from the last address back).",
            "x-cis-field": {
              "widget": "table"
            }
          },
          "agreed_amount_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Agreed amount",
            "description": "A bought line: the supplier's cost agreed for what it supplied, ex VAT, in place of the proportional cut of its committed value.",
            "x-cis-field": {
              "widget": "money"
            }
          },
          "agreed_amount_reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 2000
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "description": "Why that amount was agreed: required with agreed_amount_pence.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "CancelLineRequest"
      }
    },
    {
      "id": "orders.record_delivery_line_delivered",
      "module": "orders_module",
      "label": "Record delivered…",
      "description": "Record some or all of a delivery line delivered to its customer address.",
      "target": "orders.delivery_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/delivery-lines/{delivery_line_id}/actions/record-delivered",
      "dry_run_path": "/orders/{order_no}/delivery-lines/{delivery_line_id}/actions/record-delivered/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "quantity": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 10000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity",
            "x-cis-field": {
              "required": true
            }
          },
          "delivered_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Delivered on",
            "x-cis-field": {
              "required": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordDeliveredRequest",
        "description": "``request_id`` is the attempt's id and the recording's idempotency key: a retry with the same one records once.\nThe shell mints it at commit; a commit without it is refused (the dry run needs none)."
      }
    },
    {
      "id": "orders.record_delivery_line_received",
      "module": "orders_module",
      "label": "Record received…",
      "description": "Record some or all of a delivery line received at our warehouse and book it into stock.",
      "target": "orders.delivery_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/delivery-lines/{delivery_line_id}/actions/record-received",
      "dry_run_path": "/orders/{order_no}/delivery-lines/{delivery_line_id}/actions/record-received/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "quantity": {
            "anyOf": [
              {
                "type": "integer",
                "maximum": 10000000.0,
                "minimum": 1.0
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity",
            "x-cis-field": {
              "required": true
            }
          },
          "received_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Received on",
            "x-cis-field": {
              "required": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordReceivedRequest",
        "description": "``request_id`` is the goods-in's idempotency key: a retry with the same one records once, and stock books the\ngoods in once. The shell mints it at commit; a commit without it is refused (the dry run needs none)."
      }
    },
    {
      "id": "orders.tell_customer_delivery_problem",
      "module": "orders_module",
      "label": "Tell the customer…",
      "description": "Email the customer about a delivery problem staff looked at first (a return or a carrier exception), with what we are doing about it.",
      "target": "orders.delivery_line",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/delivery-lines/{delivery_line_id}/actions/tell-customer",
      "dry_run_path": "/orders/{order_no}/delivery-lines/{delivery_line_id}/actions/tell-customer/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "what_we_are_doing": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "What we are doing about it",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "expected_update_ids": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 4000
              },
              {
                "type": "null"
              }
            ],
            "title": "Problems shown",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "TellCustomerDeliveryProblemRequest",
        "description": "*Tell the customer…* on a delivery line: the sentence the problem email carries after the carrier's. The dry run\nfills ``expected_update_ids`` (the problems it shows), the commit's stale guard."
      }
    },
    {
      "id": "orders.email_proof_now",
      "module": "orders_module",
      "label": "Resend proof",
      "description": "Send the order's email now with a proof due and a fresh link; earlier links keep working.",
      "target": "artwork_proofing.proof",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/orders/{order_no}/proofs/{proof_id}/actions/email-now",
      "dry_run_path": "/orders/{order_no}/proofs/{proof_id}/actions/email-now/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ResendProofRequest",
        "description": "*Resend proof*: no fields but the attempt's id (a repeat sends nothing more)."
      }
    },
    {
      "id": "portal_theme.publish_customer_theme",
      "module": "portal_theme_module",
      "label": "Publish…",
      "description": "Publish a customer's draft theme as its next version, which the customer's portal then shows.",
      "target": "portal_theme.customer_theme",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/portal-theme/customer-themes/{customer_id}/actions/publish",
      "dry_run_path": "/portal-theme/customer-themes/{customer_id}/actions/publish/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "expected_updated_at": {
            "anyOf": [
              {
                "type": "string",
                "format": "date-time"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft last saved",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "type": "string",
            "format": "uuid",
            "title": "Request Id",
            "description": "Client-generated id for this attempt; a repeat returns the first result."
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "request_id"
        ],
        "title": "PublishCustomerThemeRequest"
      }
    },
    {
      "id": "portal_theme.apply_brandhub_look",
      "module": "portal_theme_module",
      "label": "Copy from BrandHub…",
      "description": "Copy a BrandHub look staged for the customer into its draft theme's customer look.",
      "target": "portal_theme.customer_theme",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/portal-theme/customer-themes/{customer_id}/actions/apply-brandhub-look",
      "dry_run_path": "/portal-theme/customer-themes/{customer_id}/actions/apply-brandhub-look/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "look_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "BrandHub look",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ApplyBrandhubLookRequest"
      }
    },
    {
      "id": "portal_theme.restore_customer_theme_version",
      "module": "portal_theme_module",
      "label": "Restore a version…",
      "description": "Write an earlier published version of a customer's theme as its draft, replacing any draft.",
      "target": "portal_theme.customer_theme",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/portal-theme/customer-themes/{customer_id}/actions/restore-version",
      "dry_run_path": "/portal-theme/customer-themes/{customer_id}/actions/restore-version/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Version",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RestoreCustomerThemeVersionRequest"
      }
    },
    {
      "id": "portal_theme.discard_customer_theme_draft",
      "module": "portal_theme_module",
      "label": "Discard draft…",
      "description": "Delete a customer's draft theme; the published theme stays as it is.",
      "target": "portal_theme.customer_theme",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/portal-theme/customer-themes/{customer_id}/actions/discard-draft",
      "dry_run_path": "/portal-theme/customer-themes/{customer_id}/actions/discard-draft/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "DiscardCustomerThemeDraftRequest",
        "description": "*Discard draft…* takes nothing but the customer in the path."
      }
    },
    {
      "id": "portal_theme.create_base_theme",
      "module": "portal_theme_module",
      "label": "New base theme…",
      "description": "Create a base theme, starting as a copy of another base theme or of the Pollards standard look.",
      "target": "collection:portal_theme.base_theme",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Changes every customer's portal.",
      "method": "POST",
      "path": "/portal-theme/base-themes/actions/create",
      "dry_run_path": "/portal-theme/base-themes/actions/create/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "base_theme_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Id",
            "description": "3 to 60 lower-case letters, digits and hyphens: epwin-brand.",
            "x-cis-field": {
              "required": true
            }
          },
          "name": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Name",
            "x-cis-field": {
              "required": true
            }
          },
          "description": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Description",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "kind": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "pollards_look",
                  "customer_brand"
                ],
                "title": "BaseThemeKind"
              },
              {
                "type": "null"
              }
            ],
            "title": "Kind",
            "x-cis-field": {
              "enum_labels": {
                "customer_brand": "Customer brand",
                "pollards_look": "Pollards look"
              },
              "required": true
            }
          },
          "starts_from": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Start from",
            "description": "Another base theme's id; empty starts from the Pollards standard look.",
            "x-cis-field": {}
          },
          "request_id": {
            "type": "string",
            "format": "uuid",
            "title": "Request Id",
            "description": "Client-generated id for this attempt; a repeat returns the first result."
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "request_id"
        ],
        "title": "CreateBaseThemeRequest"
      }
    },
    {
      "id": "portal_theme.publish_base_theme",
      "module": "portal_theme_module",
      "label": "Publish…",
      "description": "Publish a base theme's next version, reaching every customer whose theme uses it.",
      "target": "portal_theme.base_theme",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Changes every customer's portal.",
      "method": "POST",
      "path": "/portal-theme/base-themes/{id}/actions/publish",
      "dry_run_path": "/portal-theme/base-themes/{id}/actions/publish/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "values": {
            "anyOf": [
              {
                "properties": {
                  "base_theme_id": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Base Theme Id"
                  },
                  "display_name": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Display Name"
                  },
                  "main_colour": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Main Colour"
                  },
                  "secondary_colour": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Secondary Colour"
                  },
                  "text_colour": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Text Colour"
                  },
                  "background_colour": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Background Colour"
                  },
                  "highlight_colour": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Highlight Colour"
                  },
                  "success_colour": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Success Colour"
                  },
                  "warning_colour": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Warning Colour"
                  },
                  "logo_image": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Logo Image"
                  },
                  "favicon_image": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Favicon Image"
                  },
                  "banner_image": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Banner Image"
                  },
                  "banner_overlay": {
                    "anyOf": [
                      {
                        "type": "boolean"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Banner Overlay"
                  },
                  "banner_text_colour": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Banner Text Colour"
                  },
                  "shortcut_position": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Shortcut Position"
                  },
                  "welcome_title": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Welcome Title"
                  },
                  "welcome_text": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Welcome Text"
                  },
                  "support_name": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Support Name"
                  },
                  "support_email": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Support Email"
                  },
                  "support_phone": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Support Phone"
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "title": "ThemeLayer",
                "description": "One layer's settings. Every field optional; what is set is ``model_fields_set``."
              },
              {
                "type": "null"
              }
            ],
            "title": "Settings",
            "x-cis-field": {
              "required": true
            }
          },
          "note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "expected_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Version checked against",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "type": "string",
            "format": "uuid",
            "title": "Request Id",
            "description": "Client-generated id for this attempt; a repeat returns the first result."
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "request_id"
        ],
        "title": "PublishBaseThemeRequest"
      }
    },
    {
      "id": "portal_theme.retire_base_theme",
      "module": "portal_theme_module",
      "label": "Retire…",
      "description": "Retire a base theme no customer's theme uses any more.",
      "target": "portal_theme.base_theme",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Changes every customer's portal.",
      "method": "POST",
      "path": "/portal-theme/base-themes/{id}/actions/retire",
      "dry_run_path": "/portal-theme/base-themes/{id}/actions/retire/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "RetireBaseThemeRequest",
        "description": "*Retire…* takes nothing but the base theme in the path."
      }
    },
    {
      "id": "portal_theme.post_portal_notice",
      "module": "portal_theme_module",
      "label": "Post notice…",
      "description": "Post a top bar or pop-up notice for one customer's people or one of its teams.",
      "target": "collection:portal_theme.portal_notice",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/portal-theme/notices/actions/post",
      "dry_run_path": "/portal-theme/notices/actions/post/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "kind": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "top_bar",
                  "pop_up"
                ],
                "title": "PortalNoticeKind"
              },
              {
                "type": "null"
              }
            ],
            "title": "Kind",
            "x-cis-field": {
              "enum_labels": {
                "pop_up": "Pop-up",
                "top_bar": "Top bar"
              },
              "required": true
            }
          },
          "audience": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "customer",
                  "team"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Shown to",
            "x-cis-field": {
              "enum_labels": {
                "customer": "Everyone at the customer",
                "team": "One team"
              },
              "required": true
            }
          },
          "customer_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "x-cis-field": {
              "required": true
            }
          },
          "team_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Team",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "audience": "team"
              }
            }
          },
          "text": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 300
              },
              {
                "type": "null"
              }
            ],
            "title": "Text",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "showing_from": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "First day",
            "description": "Empty: today.",
            "x-cis-field": {
              "widget": "date"
            }
          },
          "showing_until": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Last day",
            "description": "Empty: until ended.",
            "x-cis-field": {
              "widget": "date"
            }
          },
          "request_id": {
            "type": "string",
            "format": "uuid",
            "title": "Request Id",
            "description": "Client-generated id for this attempt; a repeat returns the first result."
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "request_id"
        ],
        "title": "PostPortalNoticeRequest"
      }
    },
    {
      "id": "portal_theme.post_sitewide_portal_notice",
      "module": "portal_theme_module",
      "label": "Post notice for every customer…",
      "description": "Post a top bar or pop-up notice in every customer's portal.",
      "target": "collection:portal_theme.portal_notice",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Changes every customer's portal.",
      "method": "POST",
      "path": "/portal-theme/notices/actions/post-sitewide",
      "dry_run_path": "/portal-theme/notices/actions/post-sitewide/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "kind": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "top_bar",
                  "pop_up"
                ],
                "title": "PortalNoticeKind"
              },
              {
                "type": "null"
              }
            ],
            "title": "Kind",
            "x-cis-field": {
              "enum_labels": {
                "pop_up": "Pop-up",
                "top_bar": "Top bar"
              },
              "required": true
            }
          },
          "text": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 300
              },
              {
                "type": "null"
              }
            ],
            "title": "Text",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "showing_from": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "First day",
            "description": "Empty: today.",
            "x-cis-field": {
              "widget": "date"
            }
          },
          "showing_until": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Last day",
            "description": "Empty: until ended.",
            "x-cis-field": {
              "widget": "date"
            }
          },
          "request_id": {
            "type": "string",
            "format": "uuid",
            "title": "Request Id",
            "description": "Client-generated id for this attempt; a repeat returns the first result."
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "request_id"
        ],
        "title": "PostSitewidePortalNoticeRequest"
      }
    },
    {
      "id": "portal_theme.end_portal_notice",
      "module": "portal_theme_module",
      "label": "End notice…",
      "description": "End a notice now, so it no longer shows in the portal.",
      "target": "portal_theme.portal_notice",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/portal-theme/notices/{id}/actions/end",
      "dry_run_path": "/portal-theme/notices/{id}/actions/end/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "EndPortalNoticeRequest",
        "description": "*End notice…* takes nothing but the notice in the path."
      }
    },
    {
      "id": "purchase_orders.record_purchase_order_placed",
      "module": "purchase_orders_module",
      "label": "Record placed…",
      "description": "Record the purchase order placed with the supplier, with its reference and each line's confirmed cost.",
      "target": "purchase_orders.purchase_order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/actions/record-placed",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/actions/record-placed/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "supplier_reference": {
            "type": "string",
            "maxLength": 200,
            "title": "Supplier's reference",
            "description": "The supplier's own reference for the order.",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "lines": {
            "items": {
              "properties": {
                "purchase_order_line_id": {
                  "type": "string",
                  "maxLength": 64,
                  "title": "Line id",
                  "description": "The line's id, as the placement page showed it.",
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "line": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10000
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Line",
                  "description": "The purchase order line's reference (filled by the dry run; ignored on commit).",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "description": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10000
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Description",
                  "description": "What the line is (filled by the dry run; ignored on commit).",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "confirmed_cost_pence": {
                  "type": "integer",
                  "title": "Confirmed cost",
                  "description": "What the supplier confirmed for the line, ex VAT.",
                  "x-cis-field": {
                    "widget": "money"
                  }
                },
                "confirmed_vat_pence": {
                  "anyOf": [
                    {
                      "type": "integer"
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Confirmed VAT",
                  "description": "The VAT the supplier confirmed. Required for a line that had no VAT when it was priced.",
                  "x-cis-field": {
                    "widget": "money"
                  }
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "purchase_order_line_id",
                "confirmed_cost_pence"
              ],
              "title": "RecordPlacedLine",
              "description": "One line as the person placing it saw it, with what the supplier confirmed."
            },
            "type": "array",
            "maxItems": 200,
            "title": "Lines",
            "description": "Every open line the placement page showed, each once. When the purchase order's open lines differ (one joined or went meanwhile) nothing is recorded: 409, reload.",
            "x-cis-field": {
              "required": true,
              "widget": "table"
            }
          },
          "paid_by_card": {
            "type": "boolean",
            "title": "Paid by card at checkout",
            "description": "Tick when the supplier's checkout took our card, so its bill goes to Xero already paid from the card account and is not paid again.",
            "default": false,
            "x-cis-field": {}
          },
          "card_amount_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Card payment",
            "description": "What the card was charged, VAT included. Leave empty for the order's confirmed costs plus VAT. Only with 'Paid by card at checkout'.",
            "x-cis-field": {
              "widget": "money"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordPurchaseOrderPlacedRequest",
        "description": "Record the purchase order placed with the supplier, once for the whole set (doc 54 §5.2, §11)."
      },
      "main": true
    },
    {
      "id": "purchase_orders.record_ready_to_pay",
      "module": "purchase_orders_module",
      "label": "Record ready to pay…",
      "description": "Record that the basket is saved on the supplier's website and only the buyer's payment is missing, with the side-by-side of ours and the site's, each order line's price check and the supplier's basket reference.",
      "target": "purchase_orders.purchase_order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/actions/record-ready-to-pay",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/actions/record-ready-to-pay/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "basket_reference": {
            "type": "string",
            "maxLength": 200,
            "title": "Basket reference",
            "description": "The supplier's own reference for the saved basket, as the site shows it.",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "lines": {
            "items": {
              "properties": {
                "purchase_order_line_id": {
                  "type": "string",
                  "maxLength": 64,
                  "title": "Line id",
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "line": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10000
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Line",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "description": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10000
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Description",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "expected_cost_pence": {
                  "anyOf": [
                    {
                      "type": "integer"
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Expected cost",
                  "description": "What the order line is expected to cost.",
                  "readOnly": true,
                  "x-cis-field": {
                    "widget": "money"
                  }
                },
                "site_price_pence": {
                  "type": "integer",
                  "title": "Price on the site",
                  "description": "The price the supplier's site shows for the order line, ex VAT.",
                  "x-cis-field": {
                    "widget": "money"
                  }
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "purchase_order_line_id",
                "site_price_pence"
              ],
              "title": "ReadyToPayLineRequest",
              "description": "One open order line of the basket: the reference, description and expected cost are what the person reads (the\ndry run prefills them); the price the site shows is the website agent's to fill."
            },
            "type": "array",
            "maxItems": 200,
            "title": "Lines",
            "description": "Every open order line, each once, with the price the site shows. When the order's open lines differ (one joined or went meanwhile) nothing is recorded: 409, reload.",
            "x-cis-field": {
              "required": true,
              "widget": "table"
            }
          },
          "side_by_side": {
            "items": {
              "properties": {
                "line": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 40
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Order line",
                  "description": "The purchase order line it is about (PO-K20417.01). Leave empty for the whole order.",
                  "x-cis-field": {}
                },
                "field": {
                  "type": "string",
                  "maxLength": 300,
                  "title": "Compared",
                  "description": "What is compared, such as Finished size.",
                  "default": "",
                  "x-cis-field": {}
                },
                "ours": {
                  "type": "string",
                  "maxLength": 300,
                  "title": "Ours",
                  "description": "What the purchase order says.",
                  "default": "",
                  "x-cis-field": {}
                },
                "site": {
                  "type": "string",
                  "maxLength": 300,
                  "title": "The site",
                  "description": "What the supplier's site shows.",
                  "default": "",
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "title": "SideBySideRowRequest",
              "description": "One thing compared between the purchase order and the supplier's site. One compares the two as text, ignoring\nspaces and capital letters."
            },
            "type": "array",
            "maxItems": 200,
            "title": "Side by side",
            "description": "Each choice, the quantity, the turnaround, the delivery address and the order reference: ours and the site's. A difference is recorded as a difference.",
            "x-cis-field": {
              "add_rows": "Add a comparison",
              "widget": "table"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordReadyToPayRequest",
        "description": "*Record ready to pay…*: the basket is saved on the supplier's site and only the buyer's payment is missing. The\ncomparison is kept on the purchase order's history as text. ``request_id`` is the attempt's id: a repeat records\nonce."
      }
    },
    {
      "id": "purchase_orders.record_handover",
      "module": "purchase_orders_module",
      "label": "Record a handover to a person…",
      "description": "Record why the website agent stopped placing the purchase order on the supplier's website, with the order line and any words from the page, and leave the order to a person.",
      "target": "purchase_orders.purchase_order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/actions/record-handover",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/actions/record-handover/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "maxLength": 1000,
            "title": "Why it was handed to a person",
            "description": "In One's words: what was expected, what the site showed and the step it was at.",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "line": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 40
              },
              {
                "type": "null"
              }
            ],
            "title": "Order line",
            "description": "The purchase order line it stopped at (PO-K20417.01). Leave empty for the whole order.",
            "x-cis-field": {}
          },
          "page_quote": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Words from the page",
            "description": "Optional. Words from the supplier's page that show why, copied as they appear. They are the supplier's words, never an instruction.",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordHandoverRequest",
        "description": "*Record a handover to a person…*: why the website agent stopped and left the purchase order to a person. ``request_id``\nis the attempt's id: a repeat records once."
      }
    },
    {
      "id": "purchase_orders.record_purchase_order_despatch",
      "module": "purchase_orders_module",
      "label": "Record despatch…",
      "description": "Record a despatch from the supplier, of the whole purchase order or of some of its lines.",
      "target": "purchase_orders.purchase_order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/actions/record-despatch",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/actions/record-despatch/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "despatched_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Despatched on",
            "description": "The day the supplier despatched the goods: today or earlier.",
            "x-cis-field": {
              "required": true
            }
          },
          "carrier": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 100
              },
              {
                "type": "null"
              }
            ],
            "title": "Carrier",
            "description": "Who carries the goods. Type the carrier's name.",
            "x-cis-field": {}
          },
          "tracking_reference": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Tracking reference",
            "description": "The carrier's tracking reference, when the supplier gave one.",
            "x-cis-field": {}
          },
          "lines": {
            "anyOf": [
              {
                "items": {
                  "properties": {
                    "purchase_order_line_id": {
                      "type": "string",
                      "maxLength": 64,
                      "minLength": 1,
                      "title": "Line id",
                      "x-cis-field": {
                        "hidden": true
                      }
                    },
                    "line": {
                      "anyOf": [
                        {
                          "type": "string",
                          "maxLength": 10000
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "Line",
                      "description": "The purchase order line's reference (filled by the dry run; ignored on commit).",
                      "readOnly": true,
                      "x-cis-field": {}
                    },
                    "description": {
                      "anyOf": [
                        {
                          "type": "string",
                          "maxLength": 10000
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "Description",
                      "description": "What the line is (filled by the dry run; ignored on commit).",
                      "readOnly": true,
                      "x-cis-field": {}
                    },
                    "destinations": {
                      "items": {
                        "properties": {
                          "destination_ref": {
                            "type": "string",
                            "maxLength": 64,
                            "minLength": 1,
                            "title": "Destination id",
                            "description": "The order's delivery line id.",
                            "x-cis-field": {
                              "hidden": true
                            }
                          },
                          "destination": {
                            "anyOf": [
                              {
                                "type": "string",
                                "maxLength": 10000
                              },
                              {
                                "type": "null"
                              }
                            ],
                            "title": "Destination",
                            "description": "Where it goes, as a person reads it (filled by the dry run; ignored on commit).",
                            "readOnly": true,
                            "x-cis-field": {}
                          },
                          "quantity": {
                            "type": "integer",
                            "maximum": 10000000.0,
                            "minimum": 0.0,
                            "title": "Quantity",
                            "x-cis-field": {}
                          }
                        },
                        "additionalProperties": false,
                        "type": "object",
                        "required": [
                          "destination_ref",
                          "quantity"
                        ],
                        "title": "DespatchDestinationRequest",
                        "description": "How many of a line went to one of its destinations (an order's delivery line)."
                      },
                      "type": "array",
                      "maxItems": 500,
                      "minItems": 1,
                      "title": "Destinations",
                      "x-cis-field": {
                        "widget": "table"
                      }
                    }
                  },
                  "additionalProperties": false,
                  "type": "object",
                  "required": [
                    "purchase_order_line_id",
                    "destinations"
                  ],
                  "title": "DespatchLineRequest",
                  "description": "One open line of the purchase order and, for a line going to several places, how many went to each."
                },
                "type": "array",
                "maxItems": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Lines despatched",
            "description": "The lines despatched, each with how many went to each of its destinations (doc 102 §3.2); only the lines named are recorded. Omit to record every open line's remainder, each going whole to its one destination.",
            "x-cis-field": {
              "selectable": true,
              "widget": "table"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordPurchaseOrderDespatchRequest",
        "description": "Record that the supplier has despatched the purchase order (BrandHub routing doc 07 §4), or part of it."
      },
      "proposal_class": "records"
    },
    {
      "id": "purchase_orders.approve_purchase_order_price",
      "module": "purchase_orders_module",
      "label": "Approve price…",
      "description": "Approve the confirmed price a purchase order is held for, placing it.",
      "target": "purchase_orders.purchase_order",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/actions/approve-price",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/actions/approve-price/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note": {
            "type": "string",
            "maxLength": 2000,
            "title": "Note",
            "description": "Why the confirmed price is accepted. The note will stay on the purchase order's history.",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "expected_cost_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Cost approved",
            "description": "The cost the dry run showed (its guard), echoed so a change since is refused (409 stale).",
            "x-cis-field": {
              "hidden": true,
              "widget": "money"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ApprovePurchaseOrderPriceRequest",
        "description": "Approve the price a purchase order is held for: the supplier's confirmed costs are accepted as what we pay (#94)."
      },
      "proposal_class": "decides"
    },
    {
      "id": "purchase_orders.decline_purchase_order_price",
      "module": "purchase_orders_module",
      "label": "Decline price…",
      "description": "Decline the price a supplier gave when it accepted the purchase order; the held lines reopen at the cost we sent and the supplier is told.",
      "target": "purchase_orders.purchase_order",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/actions/decline-price",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/actions/decline-price/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note": {
            "type": "string",
            "maxLength": 2000,
            "title": "Note",
            "description": "Why the price is declined. The note will stay on the purchase order's history; the supplier is not sent it.",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "DeclinePurchaseOrderPriceRequest",
        "description": "Decline the price a supplier gave when it accepted a purchase order: the held lines reopen at the cost we sent\nand the supplier is told (spec 30 §L2e)."
      },
      "proposal_class": "decides"
    },
    {
      "id": "purchase_orders.log_supplier_call",
      "module": "purchase_orders_module",
      "label": "Log a call…",
      "description": "Record a phone call with the supplier about this purchase order, and the date they now give.",
      "target": "purchase_orders.purchase_order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/actions/log-supplier-call",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/actions/log-supplier-call/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "spoken_to": {
            "type": "string",
            "maxLength": 120,
            "title": "Who you spoke to",
            "description": "Who at the supplier you spoke to.",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "note": {
            "type": "string",
            "maxLength": 2000,
            "title": "What they said",
            "description": "Kept on the purchase order's history.",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "expected_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Date they now give",
            "description": "Optional. The date the supplier now gives, today or later.",
            "x-cis-field": {
              "min_date": "today"
            }
          },
          "date_kind": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "arrival",
                  "despatch"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "What the date is for",
            "description": "Required with a date. The dry run offers it as a choice once a date is given, arrival first.",
            "x-cis-field": {
              "enum_labels": {
                "arrival": "Arrives at the delivery address",
                "despatch": "Despatched"
              },
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "LogSupplierCallRequest",
        "description": "*Log a call…*: a phone call with the supplier about the purchase order, and the date they now give. ``request_id``\nis the attempt's id: a repeat logs once. The shell mints it at commit."
      }
    },
    {
      "id": "purchase_orders.record_supplier_lines_accepted",
      "module": "purchase_orders_module",
      "label": "Record their acceptance…",
      "description": "Record that the supplier accepted lines of the purchase order, as it said by email or phone, at the cost we sent or at a price of its own; a price One does not accept without approval waits for Approve price… or Decline price…. The supplier's own portal records the same.",
      "target": "purchase_orders.purchase_order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/actions/record-their-acceptance",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/actions/record-their-acceptance/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "lines": {
            "items": {
              "properties": {
                "purchase_order_line_id": {
                  "type": "string",
                  "maxLength": 64,
                  "title": "Line id",
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "line": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10000
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Line",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "description": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10000
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Description",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "sent_cost_pence": {
                  "anyOf": [
                    {
                      "type": "integer"
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Cost we sent",
                  "readOnly": true,
                  "x-cis-field": {
                    "widget": "money"
                  }
                },
                "cost_pence": {
                  "anyOf": [
                    {
                      "type": "integer"
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Their price",
                  "description": "Leave empty when the supplier accepted the cost we sent. Otherwise the delivered price it gave, ex VAT.",
                  "x-cis-field": {
                    "widget": "money"
                  }
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "purchase_order_line_id"
              ],
              "title": "TheirAcceptedLineRequest",
              "description": "One line waiting for the supplier's acceptance, as the table shows it: the id is the key sent back, the reference,\ndescription and cost we sent are what the person reads (the dry run prefills them), the price is theirs to fill."
            },
            "type": "array",
            "maxItems": 500,
            "title": "Lines they accepted",
            "description": "Tick the lines the supplier accepted (the dry run prefills the lines waiting for it, unticked).",
            "x-cis-field": {
              "required": true,
              "selectable": true,
              "widget": "table"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordTheirAcceptanceRequest",
        "description": "*Record their acceptance…*: the supplier accepted these lines, as it said by email or phone, at the cost we sent or\nat a price of its own. ``request_id`` is the attempt's id: a repeat records once. The shell mints it at commit."
      },
      "proposal_class": "records"
    },
    {
      "id": "purchase_orders.record_supplier_shortage_reported",
      "module": "purchase_orders_module",
      "label": "Record their shortage…",
      "description": "Record a service supplier receiving less of the goods sent to it than expected, as the supplier said by email or phone; the supplier's own portal records the same.",
      "target": "purchase_orders.purchase_order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/actions/record-their-shortage",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/actions/record-their-shortage/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "inbound_ref": {
            "type": "string",
            "maxLength": 64,
            "title": "What arrived short",
            "description": "The delivery or purchase order line the goods came on, as the supplier names it.",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "quantity_received": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity received",
            "description": "How many the supplier received: 0 or more.",
            "x-cis-field": {
              "required": true
            }
          },
          "note": {
            "type": "string",
            "maxLength": 2000,
            "title": "What they said",
            "description": "Kept on the purchase order's history.",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordTheirShortageRequest",
        "description": "*Record their shortage…*: a service supplier received less of the goods coming to it than expected, as it said by\nemail or phone. ``request_id`` is the attempt's id: a repeat records once. The shell mints it at commit."
      },
      "proposal_class": "records"
    },
    {
      "id": "purchase_orders.record_supplier_status_reported",
      "module": "purchase_orders_module",
      "label": "Record their status…",
      "description": "Record the supplier's own status for some lines of the purchase order, in its words; a problem status makes the dates of their open lines unknown. An API supplier's follower records the same.",
      "target": "purchase_orders.purchase_order",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/actions/record-their-status",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/actions/record-their-status/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "lines": {
            "items": {
              "properties": {
                "purchase_order_line_id": {
                  "type": "string",
                  "maxLength": 64,
                  "title": "Line id",
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "line": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10000
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Line",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "description": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10000
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Description",
                  "readOnly": true,
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "purchase_order_line_id"
              ],
              "title": "SupplierStatusLineRequest",
              "description": "One open line of the purchase order, as the table shows it: the id is the key sent back, the line's reference\nand description are what the person reads (the dry run prefills them)."
            },
            "type": "array",
            "maxItems": 500,
            "title": "Lines it is about",
            "description": "Tick the lines the status is about. Tick none for the whole purchase order.",
            "x-cis-field": {
              "selectable": true,
              "widget": "table"
            }
          },
          "status": {
            "type": "string",
            "maxLength": 100,
            "title": "Their status",
            "description": "The supplier's status, in its own words.",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "message": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 1000
              },
              {
                "type": "null"
              }
            ],
            "title": "Their message",
            "description": "Optional. What the supplier said with it.",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "is_problem": {
            "type": "boolean",
            "title": "A person must look at it",
            "description": "Tick this when the status means the supplier can't be relied on for a date. The dates of the open lines it is about become unknown, and Needs attention asks a person to chase it.",
            "default": false,
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordTheirStatusRequest",
        "description": "*Record their status…*: the supplier's own status for some lines of the purchase order, in its words. No line\nticked means the whole purchase order."
      },
      "proposal_class": "records"
    },
    {
      "id": "purchase_orders.place_purchase_order_again",
      "module": "purchase_orders_module",
      "label": "Place again…",
      "description": "Send a purchase order its supplier refused to the supplier again, once the reason it was refused has been put right.",
      "target": "purchase_orders.purchase_order",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/actions/place-again",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/actions/place-again/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "PlacePurchaseOrderAgainRequest",
        "description": "Place a purchase order its supplier refused again: it needs nothing but the purchase order's number."
      }
    },
    {
      "id": "purchase_orders.cancel_purchase_order_lines",
      "module": "purchase_orders_module",
      "label": "Cancel lines…",
      "description": "Cancel some lines of a purchase order towards the supplier, which is asked to confirm each with any costs it has incurred. Ticking \"Already cancelled with the supplier\" cancels them now; the last open line cancels the purchase order.",
      "target": "purchase_orders.purchase_order",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/actions/cancel-lines",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/actions/cancel-lines/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "lines": {
            "items": {
              "properties": {
                "purchase_order_line_id": {
                  "type": "string",
                  "maxLength": 64,
                  "title": "Line id",
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "line": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10000
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Line",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "description": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 10000
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Description",
                  "readOnly": true,
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "purchase_order_line_id"
              ],
              "title": "LineToCancelRequest",
              "description": "One open line of the purchase order, as the table shows it: the id is the key sent back, the line's reference\nand description are what the person reads (the dry run prefills them)."
            },
            "type": "array",
            "maxItems": 500,
            "title": "Lines to cancel",
            "description": "The open lines to cancel (the dry run prefills every open line, unticked).",
            "x-cis-field": {
              "required": true,
              "selectable": true,
              "widget": "table"
            }
          },
          "reason": {
            "type": "string",
            "maxLength": 2000,
            "title": "Reason",
            "description": "Why the lines are cancelled. The reason will stay on the purchase order's history and is never sent to the supplier.",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "already_cancelled_with_supplier": {
            "type": "boolean",
            "title": "Already cancelled with the supplier",
            "description": "Tick this when you have already cancelled them with the supplier. They are then cancelled now and the supplier is not told.",
            "default": false,
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "CancelLinesRequest",
        "description": "*Cancel lines…*: cancel the ticked open lines towards the supplier, all or nothing; the last open line cancels\nthe purchase order too. By default the supplier is asked to confirm (spec 30 §L2b)."
      }
    },
    {
      "id": "purchase_orders.cancel_purchase_order",
      "module": "purchase_orders_module",
      "label": "Cancel order…",
      "description": "Cancel the whole purchase order towards the supplier. The supplier is asked to confirm, with any costs it has incurred, and the lines stay on the order until it does. Ticking \"Already cancelled with the supplier\" cancels it now and tells nobody; an order the supplier never had is withdrawn at once.",
      "target": "purchase_orders.purchase_order",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/actions/cancel-order",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/actions/cancel-order/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "maxLength": 2000,
            "title": "Reason",
            "description": "Why the purchase order is cancelled. The reason will stay on the purchase order's history and is never sent to the supplier.",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "already_cancelled_with_supplier": {
            "type": "boolean",
            "title": "Already cancelled with the supplier",
            "description": "Tick this when you have already cancelled it with the supplier. It is then cancelled now and the supplier is not told.",
            "default": false,
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "CancelOrderRequest",
        "description": "*Cancel order…*: cancel the whole purchase order towards its supplier. By default the supplier is asked to\nconfirm, with any costs it has incurred; ticking ``already_cancelled_with_supplier`` records it cancelled now\ninstead (supplier interface spec 30 §L2b). ``request_id`` is the attempt's id: a repeat asks once. The shell mints\nit at commit."
      }
    },
    {
      "id": "purchase_orders.propose_purchase_order_line_date_change",
      "module": "purchase_orders_module",
      "label": "Record their new date…",
      "description": "Record the date a supplier now gives for a line, as the supplier said by email or phone; it is accepted at once when it still keeps the customer's date, else it waits for Decide new date…. The supplier's own portal proposes a date the same way.",
      "target": "purchase_orders.purchase_order_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/lines/{purchase_order_line_id}/actions/record-their-new-date",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/lines/{purchase_order_line_id}/actions/record-their-new-date/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "earliest_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Date they now give",
            "description": "The earliest date the supplier can have the order line ready, today or later. A date that still meets the customer's date is accepted at once. Any other date waits for Decide new date.",
            "x-cis-field": {
              "min_date": "today",
              "required": true
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Their reason",
            "description": "Optional. Why the supplier gave this date. Kept on the purchase order's history.",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordTheirNewDateRequest",
        "description": "*Record their new date…*: the date a supplier gave for one line, by email or phone. A date that still meets the\ncustomer's date is accepted at once; any other waits for *Decide new date…* (mailbox programme PA4)."
      },
      "proposal_class": "records"
    },
    {
      "id": "purchase_orders.record_purchase_order_line_date_change_acceptance",
      "module": "purchase_orders_module",
      "label": "Decide new date…",
      "description": "Accept the supplier's later despatch date for a line, or decline it and cancel the line; the supplier is emailed our answer where its connection says so.",
      "target": "purchase_orders.purchase_order_line",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/lines/{purchase_order_line_id}/actions/record-date-change-acceptance",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/lines/{purchase_order_line_id}/actions/record-date-change-acceptance/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "outcome": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "accepted",
                  "declined"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Decision",
            "description": "Accept new date: the line will promise the supplier's date and be acknowledged at our sent cost. Move to another supplier: the line will be cancelled as \"Date not met: moved to another supplier\". Then use Change supplier on the order.",
            "x-cis-field": {
              "enum_labels": {
                "accepted": "Accept new date",
                "declined": "Move to another supplier"
              },
              "required": true,
              "widget": "radio"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "DateChangeAcceptanceRequest",
        "description": "Pollards' decision on a supplier's date change for one line (supplier interface spec 05 §3)."
      },
      "proposal_class": "decides"
    },
    {
      "id": "purchase_orders.record_purchase_order_line_agreed_amount",
      "module": "purchase_orders_module",
      "label": "Record agreed amount…",
      "description": "Record what the supplier charges for a line after the rest of it was cancelled.",
      "target": "purchase_orders.purchase_order_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/lines/{purchase_order_line_id}/actions/record-agreed-amount",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/lines/{purchase_order_line_id}/actions/record-agreed-amount/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "amount_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Agreed amount",
            "description": "The amount agreed with the supplier, ex VAT: £0 or more.",
            "x-cis-field": {
              "required": true,
              "widget": "money"
            }
          },
          "reason": {
            "type": "string",
            "maxLength": 500,
            "title": "Reason",
            "description": "The reason will go on the purchase order's history.",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordAgreedAmountRequest",
        "description": "*Record agreed amount* (order to invoice doc 98 §5.2): what the supplier charges for a line after its rest was\ncancelled. It becomes the line's committed value."
      }
    },
    {
      "id": "purchase_orders.issue_supplier_file_link",
      "module": "purchase_orders_module",
      "label": "Copy file link…",
      "description": "Issue the supplier links to a line's released print-ready files, revoking its last link.",
      "target": "purchase_orders.purchase_order_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-orders/{purchase_order_no}/lines/{purchase_order_line_id}/actions/issue-file-link",
      "dry_run_path": "/purchase-orders/{purchase_order_no}/lines/{purchase_order_line_id}/actions/issue-file-link/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "IssueSupplierFileLinkRequest",
        "description": "*Copy file link* takes nothing but the line in its path."
      }
    },
    {
      "id": "quotes.create_draft_quote",
      "module": "quotes_module",
      "label": "New draft quote…",
      "description": "Make an empty draft quote for a customer, owned by the person who made it (or the person Claude acted for), to build line by line; a repeat of the same request returns the first draft quote.",
      "target": "collection:quotes.quote",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/quotes/actions/create-draft",
      "dry_run_path": "/quotes/actions/create-draft/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "customer_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 50
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "description": "The customer number, for example CUS-K48193, or the customer's Tharstern code.",
            "x-cis-field": {
              "entity": "customers.customer",
              "required": true,
              "widget": "entity"
            }
          },
          "contact_person_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Contact",
            "description": "One of the customer's people; left out, chosen later.",
            "x-cis-field": {
              "entity": "customer_access.customer_link",
              "entity_value": "person_id",
              "widget": "entity"
            }
          },
          "title": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Title",
            "description": "Left out, the title follows the lines once there are some.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "CreateDraftQuoteRequest",
        "description": "*New draft quote…*: an empty draft quote for a customer, owned by the person. Absent fields are refused by the\naction as field errors; the dry run answers whatever is typed so far. ``request_id`` makes a repeat return the draft\nquote the first request made."
      }
    },
    {
      "id": "quotes.publish_quote",
      "module": "quotes_module",
      "label": "Publish…",
      "description": "Publish a draft, fixing its prices and showing it in the customer's portal; nothing is emailed.",
      "target": "quotes.quote",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/quotes/{quote_no}/actions/publish",
      "dry_run_path": "/quotes/{quote_no}/actions/publish/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expires_at": {
            "anyOf": [
              {
                "type": "string",
                "format": "date-time"
              },
              {
                "type": "null"
              }
            ],
            "title": "Expires",
            "description": "When the published quote expires; must be in the future.",
            "x-cis-field": {}
          },
          "expected_total_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft total at preview",
            "description": "The draft's total, ex VAT, the dry run showed: a changed draft is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "PublishQuoteRequest",
        "description": "Publish a draft: the expiry may be set then, else it is ``QUOTE_EXPIRY_DAYS`` on. The commit and its dry run\ntake this one body; ``expected_total_pence`` is the dry run's guard (the draft's total it showed)."
      },
      "main": true
    },
    {
      "id": "quotes.place_draft_quote",
      "module": "quotes_module",
      "label": "Place order…",
      "description": "Place a draft quote straight as an order, without publishing it; the draft quote ends Ordered, linked to the order, and the customer never sees the quote.",
      "target": "quotes.quote",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/quotes/{quote_no}/actions/place-order",
      "dry_run_path": "/quotes/{quote_no}/actions/place-order/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "payment_method": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "How the customer pays",
            "description": "Chosen from the dry run's choice: the payer's methods.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "customer_order_number": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 50
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer PO number",
            "x-cis-field": {}
          },
          "quantity_options": {
            "items": {
              "properties": {
                "line_no": {
                  "type": "integer",
                  "minimum": 1.0,
                  "title": "Line no",
                  "description": "The quote line, from 1.",
                  "readOnly": true,
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "position": {
                  "type": "integer",
                  "minimum": 1.0,
                  "title": "Option no",
                  "description": "The option's position on the line, from 1.",
                  "readOnly": true,
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "line": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 500
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Line",
                  "description": "The line, e.g. 'Line 2: Pull-up banners'.",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "option": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 500
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Option",
                  "description": "The quantity and its price, e.g. '1,000 × Flyers, A5: £240.00'.",
                  "readOnly": true,
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "line_no",
                "position"
              ],
              "title": "QuantityChoiceRow",
              "description": "One quantity option of a line with alternatives, as Accept for the customer's table offers it: tick the one the\ncustomer chose on each such line."
            },
            "type": "array",
            "maxItems": 500,
            "title": "Quantities chosen",
            "description": "Each option of each line with alternatives: tick the one the customer wants on each line.",
            "x-cis-field": {
              "selectable": true,
              "widget": "table"
            }
          },
          "included_extras": {
            "items": {
              "properties": {
                "line_no": {
                  "type": "integer",
                  "minimum": 1.0,
                  "title": "Line no",
                  "description": "The quote line, from 1.",
                  "readOnly": true,
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "extra": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 500
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Extra",
                  "description": "The extra and its price, e.g. 'Line 3: 1 × Delivery to Bristol: £35.00'.",
                  "readOnly": true,
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "line_no"
              ],
              "title": "IncludedExtraRow",
              "description": "One optional extra, as Accept for the customer's table offers it: tick it to include it."
            },
            "type": "array",
            "maxItems": 500,
            "title": "Extras included",
            "description": "The draft quote's optional extras: tick each one the customer wants.",
            "x-cis-field": {
              "selectable": true,
              "widget": "table"
            }
          },
          "version_splits": {
            "items": {
              "properties": {
                "line_no": {
                  "type": "integer",
                  "minimum": 1.0,
                  "title": "Line no",
                  "description": "The quote line, from 1.",
                  "readOnly": true,
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "version_position": {
                  "type": "integer",
                  "minimum": 1.0,
                  "title": "Version no",
                  "description": "The version's position on the line.",
                  "readOnly": true,
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "line": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 500
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Line",
                  "description": "The line, e.g. 'Line 2: Business cards'.",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "version": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 500
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Version",
                  "description": "The version, e.g. 'A · Jo Bloggs'.",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "quantity": {
                  "anyOf": [
                    {
                      "type": "integer",
                      "maximum": 10000000.0,
                      "minimum": 1.0
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Quantity",
                  "description": "Whole number. Leave every version of a line empty to give the split on the order.",
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "line_no",
                "version_position"
              ],
              "title": "VersionQuantityRow",
              "description": "One version of a line split when ordered, as Accept for the customer's table offers it: the quantity the customer\ngave that version, left empty to give the split on the order."
            },
            "type": "array",
            "maxItems": 500,
            "title": "Split between versions",
            "description": "Each version of a line split when ordered: the quantity the customer gave it, for the quantity chosen. Leave a line's rows empty to give the split on the order.",
            "x-cis-field": {
              "widget": "table"
            }
          },
          "review_before_release": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "Review before release",
            "description": "Hold the order's lines for a person to release; left out, the customer's own setting decides.",
            "x-cis-field": {}
          },
          "expected_total_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft total at preview",
            "description": "The draft quote's total, ex VAT, the dry run showed: a changed draft is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "PlaceDraftQuoteRequest",
        "description": "*Place order…*: place the draft quote straight as an order, with the customer's own choices. The acting staff user\ncomes from the signed-in session. The commit and its dry run take this one body; the dry run prefills both tables\nfrom the draft quote, and ``expected_total_pence`` is its guard."
      }
    },
    {
      "id": "quotes.share_quote",
      "module": "quotes_module",
      "label": "Share…",
      "description": "Email the quote's contact a link to the quote, publishing a draft first; repeatable.",
      "target": "quotes.quote",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/quotes/{quote_no}/actions/share",
      "dry_run_path": "/quotes/{quote_no}/actions/share/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expires_at": {
            "anyOf": [
              {
                "type": "string",
                "format": "date-time"
              },
              {
                "type": "null"
              }
            ],
            "title": "Expires",
            "description": "A draft's expiry when sharing publishes it; must be in the future.",
            "x-cis-field": {}
          },
          "expected_total_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft total at preview",
            "description": "A draft's total, ex VAT, the dry run showed: a changed draft is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id",
            "description": "This share's id: a repeat with it sends nothing more and returns the first result."
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ShareQuoteRequest",
        "description": "Email the quote's contact their link; a draft is published first (``expires_at`` and the guard apply then).\nThe commit and its dry run take this one body."
      }
    },
    {
      "id": "quotes.accept_quote_on_behalf",
      "module": "quotes_module",
      "label": "Accept for the customer…",
      "description": "Accept a quote for the customer, as staff on their word or a named system channel, creating the order. A Promo Office draft is published in the same step; a staff draft quote is placed with Place order… instead.",
      "target": "quotes.quote",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/quotes/{quote_no}/actions/accept-on-behalf",
      "dry_run_path": "/quotes/{quote_no}/actions/accept-on-behalf/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "on_customers_instruction",
                  "converted_in_promo_office"
                ],
                "title": "AcceptanceReason",
                "description": "Why a quote was accepted for the customer rather than by them."
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "description": "Why staff are accepting the quote instead of the customer.",
            "x-cis-field": {
              "enum_labels": {
                "converted_in_promo_office": "Converted in Promo Office",
                "on_customers_instruction": "On the customer's instruction"
              },
              "required": true
            }
          },
          "note": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 1000
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "description": "At most 500 characters.",
            "x-cis-field": {}
          },
          "source_ref": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Channel's own reference",
            "description": "The request's own reference; with the actor it makes a replay return the same acceptance.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "payment_method": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "How the customer pays",
            "description": "Chosen from the dry run's choice: the payer's methods.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "customer_order_number": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 50
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer PO number",
            "x-cis-field": {}
          },
          "quantity_options": {
            "items": {
              "properties": {
                "line_no": {
                  "type": "integer",
                  "minimum": 1.0,
                  "title": "Line no",
                  "description": "The quote line, from 1.",
                  "readOnly": true,
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "position": {
                  "type": "integer",
                  "minimum": 1.0,
                  "title": "Option no",
                  "description": "The option's position on the line, from 1.",
                  "readOnly": true,
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "line": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 500
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Line",
                  "description": "The line, e.g. 'Line 2: Pull-up banners'.",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "option": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 500
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Option",
                  "description": "The quantity and its price, e.g. '1,000 × Flyers, A5: £240.00'.",
                  "readOnly": true,
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "line_no",
                "position"
              ],
              "title": "QuantityChoiceRow",
              "description": "One quantity option of a line with alternatives, as Accept for the customer's table offers it: tick the one the\ncustomer chose on each such line."
            },
            "type": "array",
            "maxItems": 500,
            "title": "Quantities chosen",
            "description": "Each option of each line with alternatives: tick the one the customer chose on each line.",
            "x-cis-field": {
              "selectable": true,
              "widget": "table"
            }
          },
          "included_extras": {
            "items": {
              "properties": {
                "line_no": {
                  "type": "integer",
                  "minimum": 1.0,
                  "title": "Line no",
                  "description": "The quote line, from 1.",
                  "readOnly": true,
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "extra": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 500
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Extra",
                  "description": "The extra and its price, e.g. 'Line 3: 1 × Delivery to Bristol: £35.00'.",
                  "readOnly": true,
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "line_no"
              ],
              "title": "IncludedExtraRow",
              "description": "One optional extra, as Accept for the customer's table offers it: tick it to include it."
            },
            "type": "array",
            "maxItems": 500,
            "title": "Extras included",
            "description": "The quote's optional extras: tick each one the customer wants.",
            "x-cis-field": {
              "selectable": true,
              "widget": "table"
            }
          },
          "version_splits": {
            "items": {
              "properties": {
                "line_no": {
                  "type": "integer",
                  "minimum": 1.0,
                  "title": "Line no",
                  "description": "The quote line, from 1.",
                  "readOnly": true,
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "version_position": {
                  "type": "integer",
                  "minimum": 1.0,
                  "title": "Version no",
                  "description": "The version's position on the line.",
                  "readOnly": true,
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "line": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 500
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Line",
                  "description": "The line, e.g. 'Line 2: Business cards'.",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "version": {
                  "anyOf": [
                    {
                      "type": "string",
                      "maxLength": 500
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Version",
                  "description": "The version, e.g. 'A · Jo Bloggs'.",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "quantity": {
                  "anyOf": [
                    {
                      "type": "integer",
                      "maximum": 10000000.0,
                      "minimum": 1.0
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Quantity",
                  "description": "Whole number. Leave every version of a line empty to give the split on the order.",
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "line_no",
                "version_position"
              ],
              "title": "VersionQuantityRow",
              "description": "One version of a line split when ordered, as Accept for the customer's table offers it: the quantity the customer\ngave that version, left empty to give the split on the order."
            },
            "type": "array",
            "maxItems": 500,
            "title": "Split between versions",
            "description": "Each version of a line split when ordered: the quantity the customer gave it, for the quantity chosen. Leave a line's rows empty to give the split on the order.",
            "x-cis-field": {
              "widget": "table"
            }
          },
          "expected_total_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Quote total at preview",
            "description": "The quote's total, ex VAT, the dry run showed: a changed quote is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "confirmed": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 10,
            "title": "Confirmed",
            "description": "The dry run's confirmations ticked, by key: `customer_accepted` (the customer said yes)."
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AcceptOnBehalfRequest",
        "description": "Staff accept the quote for the customer (V1 completion E5): the customer's own choices, plus why. The acting\nstaff user comes from the signed-in session, never the body. The commit and its dry run take this one body; the\ndry run prefills both tables from the quote, and ``expected_total_pence`` is its guard.\n\nEvery field has a default so a dry run called with ``{}`` reaches the action and answers its prefill, its choice\nand its guard; the action and the commit route answer a missing ``reason``, ``payment_method`` or ``request_id``\nas field errors. ``payment_method`` is the dry run's choice, and ``source_ref`` and ``expected_total_pence`` are\nfilled by a channel or the shell, so the shell never draws these three."
      }
    },
    {
      "id": "quotes.revise_quote",
      "module": "quotes_module",
      "label": "Revise quote…",
      "description": "Open a new draft quote that copies a published, expired or declined quote and holds the copied prices while they last; the quote stays as it is until the new draft quote is published.",
      "target": "quotes.quote",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/quotes/{quote_no}/actions/revise",
      "dry_run_path": "/quotes/{quote_no}/actions/revise/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ReviseQuoteRequest",
        "description": "*Revise quote…*: open a new draft quote that copies the quote. ``request_id`` makes a repeat return the draft\nquote the first request opened."
      }
    },
    {
      "id": "quotes.set_quote_contact",
      "module": "quotes_module",
      "label": "Choose contact…",
      "description": "Choose the customer's person a draft quote will go to, so staff can publish and share the draft quote.",
      "target": "quotes.quote",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/quotes/{quote_no}/actions/set-contact",
      "dry_run_path": "/quotes/{quote_no}/actions/set-contact/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "contact_person_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Contact",
            "description": "One of the customer's people.",
            "x-cis-field": {
              "entity": "customer_access.customer_link",
              "entity_value": "person_id",
              "widget": "entity"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetQuoteContactRequest",
        "description": "The customer's person the draft quote will go to. Left out, the dry run prefills the contact the draft quote has."
      },
      "edits": {
        "fields": [
          "contact_name"
        ]
      }
    },
    {
      "id": "quotes.set_quote_delivery_address",
      "module": "quotes_module",
      "label": "Choose delivery address…",
      "description": "Choose which of the customer's delivery addresses a draft quote's goods will go to; every price on the draft quote stays.",
      "target": "quotes.quote",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/quotes/{quote_no}/actions/set-delivery-address",
      "dry_run_path": "/quotes/{quote_no}/actions/set-delivery-address/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "address_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Delivery address",
            "description": "One of the customer's open delivery addresses.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetQuoteDeliveryAddressRequest",
        "description": "The customer's delivery address the draft quote's goods will go to. Left out, the dry run prefills the address the\ngoods go to now."
      },
      "edits": {
        "fields": [
          "delivery_address"
        ]
      }
    },
    {
      "id": "quotes.change_quote_owner",
      "module": "quotes_module",
      "label": "Change owner…",
      "description": "Hand a draft quote to another owner, who will own the draft quote until staff publish the draft quote; a merch CSR for a Promo Office draft quote, any staff member who can manage quotes for a staff-made one.",
      "target": "quotes.quote",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/quotes/{quote_no}/actions/change-owner",
      "dry_run_path": "/quotes/{quote_no}/actions/change-owner/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "owner_user_id": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Owner",
            "description": "A merch CSR's user id.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ChangeQuoteOwnerRequest",
        "description": "The merch CSR who will own the draft quote until staff publish the draft quote. Left out, the dry run prefills the\nowner."
      },
      "edits": {
        "fields": [
          "owner_name"
        ]
      }
    },
    {
      "id": "quotes.take_over_draft_quote_editing",
      "module": "quotes_module",
      "label": "Take over editing…",
      "description": "Take over the editing of a draft quote from the colleague who holds its 10-minute lease; the colleague's unsaved change is lost and the colleague is told who took over.",
      "target": "quotes.quote",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/quotes/{quote_no}/actions/take-over-editing",
      "dry_run_path": "/quotes/{quote_no}/actions/take-over-editing/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "TakeOverDraftQuoteEditingRequest",
        "description": "*Take over editing…*: end the editing lease a colleague holds. ``request_id`` makes a repeat return the first\ntakeover; ``expected_draft_version`` is the dry run's guard."
      }
    },
    {
      "id": "quotes.withdraw_quote",
      "module": "quotes_module",
      "label": "Withdraw…",
      "description": "Withdraw a published quote; the customer's page says it was withdrawn.",
      "target": "quotes.quote",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/quotes/{quote_no}/actions/withdraw",
      "dry_run_path": "/quotes/{quote_no}/actions/withdraw/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "quotes.discard_draft_quote",
      "module": "quotes_module",
      "label": "Discard…",
      "description": "Discard a draft quote, withdrawing it without the customer ever seeing it; a discarded revision does not stop the next revision.",
      "target": "quotes.quote",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/quotes/{quote_no}/actions/discard",
      "dry_run_path": "/quotes/{quote_no}/actions/discard/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "quotes.resolve_possible_alternative",
      "module": "quotes_module",
      "label": "Decide…",
      "description": "Decide a channel draft's line that repeats an article; a quantity option of the line it repeats, an extra line, or removed.",
      "target": "quotes.quote_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/quotes/{quote_no}/lines/{line_ref}/actions/resolve-possible-alternative",
      "dry_run_path": "/quotes/{quote_no}/lines/{line_ref}/actions/resolve-possible-alternative/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "expected_draft_version": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Draft version at preview",
            "description": "The draft version the dry run showed: a draft written since is a 409 stale.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "resolution": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "make_quantity_option",
                  "keep_as_extra_line",
                  "remove"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Decision",
            "description": "A quantity option of the line it repeats, an extra line of its own, or removed.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ResolvePossibleAlternativeRequest",
        "description": "What becomes of a line that repeats an article. Left out, a dry run says only whether it can be decided."
      }
    },
    {
      "id": "quotes.set_quote_version_labels",
      "module": "quotes_module",
      "label": "Name versions…",
      "description": "Name a published quote line's versions, labels and names only.",
      "target": "quotes.quote_line",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/quotes/{quote_no}/lines/{line_ref}/actions/set-version-labels",
      "dry_run_path": "/quotes/{quote_no}/lines/{line_ref}/actions/set-version-labels/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "versions": {
            "anyOf": [
              {
                "items": {
                  "properties": {
                    "position": {
                      "type": "integer",
                      "maximum": 500.0,
                      "minimum": 1.0,
                      "title": "Version",
                      "description": "The version's position on the line, from 1.",
                      "x-cis-field": {}
                    },
                    "label": {
                      "type": "string",
                      "maxLength": 32,
                      "title": "Label",
                      "description": "For example, 'A'. At most 8 characters.",
                      "x-cis-field": {}
                    },
                    "name": {
                      "anyOf": [
                        {
                          "type": "string",
                          "maxLength": 240
                        },
                        {
                          "type": "null"
                        }
                      ],
                      "title": "Name",
                      "description": "For example, 'Bristol'.",
                      "x-cis-field": {}
                    }
                  },
                  "additionalProperties": false,
                  "type": "object",
                  "required": [
                    "position",
                    "label"
                  ],
                  "title": "VersionLabelRequest"
                },
                "type": "array",
                "maxItems": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Versions",
            "x-cis-field": {
              "required": true,
              "widget": "table"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetVersionLabelsRequest",
        "description": "Name one line's versions. Every field may be left out, so a dry run prefills the line's versions; the commit\nrefuses an empty body (400 ``versions`` required)."
      }
    },
    {
      "id": "recommendations_user_lifecycle.convert_alert_to_ticket",
      "module": "recommendations_user_lifecycle_module",
      "label": "Convert to ticket",
      "description": "Raise a ticket from an open alert for the person converting, and take the alert out of the open alerts.",
      "target": "recommendations_generation.alert",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/alerts/{alert_id}/actions/convert-to-ticket",
      "dry_run_path": "/alerts/{alert_id}/actions/convert-to-ticket/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ConvertToTicketRequest",
        "description": "Convert to ticket. ``request_id`` becomes the new ticket's id, so a repeat returns the first ticket."
      },
      "main": true
    },
    {
      "id": "recommendations_user_lifecycle.snooze_alert",
      "module": "recommendations_user_lifecycle_module",
      "label": "Snooze alert…",
      "description": "Snooze an open alert until a chosen length has passed, recording who snoozed it.",
      "target": "recommendations_generation.alert",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/alerts/{alert_id}/actions/snooze",
      "dry_run_path": "/alerts/{alert_id}/actions/snooze/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "snooze_duration": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "5d",
                  "7d",
                  "14d",
                  "1m",
                  "2m",
                  "3m"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Snooze for",
            "description": "Chosen from the dry run's choice.",
            "x-cis-field": {
              "enum_labels": {
                "14d": "14 days",
                "1m": "1 month",
                "2m": "2 months",
                "3m": "3 months",
                "5d": "5 days",
                "7d": "7 days"
              },
              "required": true
            }
          },
          "snooze_reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 1000
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SnoozeAlertRequest",
        "description": "Snooze alert…: the length is chosen from the dry run's choice; the server works out the date."
      }
    },
    {
      "id": "recommendations_user_lifecycle.dismiss_alert",
      "module": "recommendations_user_lifecycle_module",
      "label": "Dismiss alert",
      "description": "Dismiss an open alert for everyone.",
      "target": "recommendations_generation.alert",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/alerts/{alert_id}/actions/dismiss",
      "dry_run_path": "/alerts/{alert_id}/actions/dismiss/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "NoCommandFieldsRequest",
        "description": "A command that needs nothing but the alert, opportunity or ticket: dismiss, unmute."
      }
    },
    {
      "id": "recommendations_user_lifecycle.convert_opportunity_to_ticket",
      "module": "recommendations_user_lifecycle_module",
      "label": "Convert to ticket",
      "description": "Raise a ticket from an open opportunity for the person converting, and take the opportunity out of the open opportunities.",
      "target": "recommendations_generation.opportunity",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/opportunities/{opportunity_id}/actions/convert-to-ticket",
      "dry_run_path": "/opportunities/{opportunity_id}/actions/convert-to-ticket/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ConvertToTicketRequest",
        "description": "Convert to ticket. ``request_id`` becomes the new ticket's id, so a repeat returns the first ticket."
      },
      "main": true
    },
    {
      "id": "recommendations_user_lifecycle.dismiss_opportunity",
      "module": "recommendations_user_lifecycle_module",
      "label": "Dismiss opportunity",
      "description": "Dismiss an open opportunity for everyone.",
      "target": "recommendations_generation.opportunity",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/opportunities/{opportunity_id}/actions/dismiss",
      "dry_run_path": "/opportunities/{opportunity_id}/actions/dismiss/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "NoCommandFieldsRequest",
        "description": "A command that needs nothing but the alert, opportunity or ticket: dismiss, unmute."
      }
    },
    {
      "id": "recommendations_user_lifecycle.mute_ticket_alerts",
      "module": "recommendations_user_lifecycle_module",
      "label": "No need to watch…",
      "description": "No need to watch: mute the alert rules currently behind an alert ticket, for its customer, until a date. The mute itself is workflow's customer alert suppression.",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/mute-alerts",
      "dry_run_path": "/tickets/{ticket_id}/actions/mute-alerts/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 1000
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "expires_at": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Mute until",
            "x-cis-field": {
              "min_date": "today",
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "MuteTicketAlertsRequest",
        "description": "No need to watch…: the reason, and the date the mute ends on (the end of that day). Both are optional here so a\ndry run called with ``{}`` reaches the preview; the action answers a missing one with a 400 ``required``."
      }
    },
    {
      "id": "recommendations_user_lifecycle.unmute_ticket_alerts",
      "module": "recommendations_user_lifecycle_module",
      "label": "Unmute alerts",
      "description": "Clear an early mute on the alert rules behind an alert ticket, so new alerts of those types raise tickets again.",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/unmute-alerts",
      "dry_run_path": "/tickets/{ticket_id}/actions/unmute-alerts/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "NoCommandFieldsRequest",
        "description": "A command that needs nothing but the alert, opportunity or ticket: dismiss, unmute."
      }
    },
    {
      "id": "sales_leads.create_lead",
      "module": "sales_leads_module",
      "label": "New lead…",
      "description": "Log a new lead company with its first contact, in stage Cold and owned by you.",
      "target": "collection:sales_leads.lead",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-leads",
      "dry_run_path": "/sales-leads/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "company_name": {
            "type": "string",
            "title": "Company",
            "x-cis-field": {}
          },
          "contact": {
            "properties": {
              "name": {
                "type": "string",
                "title": "Name",
                "x-cis-field": {}
              },
              "job_title": {
                "anyOf": [
                  {
                    "type": "string"
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Job title",
                "x-cis-field": {}
              },
              "phone": {
                "anyOf": [
                  {
                    "type": "string"
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Phone",
                "x-cis-field": {}
              },
              "email": {
                "anyOf": [
                  {
                    "type": "string"
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Email",
                "x-cis-field": {}
              },
              "referred_by": {
                "anyOf": [
                  {
                    "type": "string"
                  },
                  {
                    "type": "null"
                  }
                ],
                "title": "Referred by",
                "x-cis-field": {}
              }
            },
            "additionalProperties": false,
            "type": "object",
            "required": [
              "name"
            ],
            "title": "First contact",
            "x-cis-field": {}
          },
          "address": {
            "anyOf": [
              {
                "properties": {
                  "address_line1": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Address line 1",
                    "x-cis-field": {}
                  },
                  "address_line2": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Address line 2",
                    "x-cis-field": {}
                  },
                  "city": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "City",
                    "x-cis-field": {}
                  },
                  "postcode": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Postcode",
                    "x-cis-field": {}
                  },
                  "country": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Country",
                    "x-cis-field": {}
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "title": "LeadAddressSchema"
              },
              {
                "type": "null"
              }
            ],
            "title": "Address",
            "x-cis-field": {}
          },
          "phone": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Phone",
            "x-cis-field": {}
          },
          "website": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Website",
            "x-cis-field": {}
          },
          "interest_tags": {
            "items": {
              "type": "string",
              "enum": [
                "print",
                "display",
                "merch",
                "clothing",
                "mailing",
                "creative",
                "other"
              ],
              "title": "MarketingCategory",
              "description": "Coarse product-family taxonomy a lead source's (or a campaign's)\npromoted products fall into — the same values dbt derives per job and\nper quote (modules_dbt/dbt/seeds/marketing_category_map.csv), reused here\nas a structured scoping axis for commercial-outcomes attribution.\nExported for marketing_campaigns_module's ``Campaign.marketing_categories``\nand sales_leads_module's ``Lead.interest_tags``.\nAn alternative to the free-text ``product_keywords`` for a source whose\nproducts share no literal keyword — e.g. \"Big Pink Book\" covers clothing/workwear/merch,\nnone of which appear verbatim in an individual garment's own job\ndescription (\"polo shirt\" doesn't contain \"uniform\")."
            },
            "type": "array",
            "title": "Interested in",
            "default": [],
            "x-cis-field": {
              "enum_labels": {
                "clothing": "Clothing & workwear",
                "creative": "Creative",
                "display": "Display",
                "mailing": "Mailing",
                "merch": "Merch",
                "other": "Other",
                "print": "Print"
              }
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "company_name",
          "contact"
        ],
        "title": "CreateLeadRequest",
        "description": "*New lead…*. ``request_id`` becomes the lead's id: a repeat returns the first lead."
      }
    },
    {
      "id": "sales_leads.advance_lead_stage",
      "module": "sales_leads_module",
      "label": "Move stage…",
      "description": "Move a lead to another stage of the funnel, with an optional note saying why.",
      "target": "sales_leads.lead",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-leads/{lead_id}/stage",
      "dry_run_path": "/sales-leads/{lead_id}/stage/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "stage": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "cold",
                  "warm",
                  "hot",
                  "contact_made",
                  "quoted",
                  "follow_up",
                  "first_order"
                ],
                "title": "LeadStage",
                "description": "The confirmed general sales funnel (product brief §16 item 3).\n\n``first_order`` is terminal and conversion-triggering: once a lead\nreaches it, ``Lead.converted_customer_id`` (a Tharstern customer) or\n``Lead.converted_cis_customer_id`` (a customer created in CIS) is set,\nand the stage may never change again (§10)."
              },
              {
                "type": "null"
              }
            ],
            "title": "Stage",
            "x-cis-field": {
              "enum_labels": {
                "cold": "Cold",
                "contact_made": "Contact made",
                "first_order": "First order",
                "follow_up": "Follow-up",
                "hot": "Hot",
                "quoted": "Quoted",
                "warm": "Warm"
              },
              "required": true
            }
          },
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason for the change",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AdvanceLeadStageRequest",
        "description": "*Move stage…*: the stage is chosen from the dry run's choice; First order is reached only by converting the\nlead to a customer."
      },
      "main": true
    },
    {
      "id": "sales_leads.add_lead_note",
      "module": "sales_leads_module",
      "label": "Add note…",
      "description": "Add a note to a lead's company notes.",
      "target": "sales_leads.lead",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-leads/{lead_id}/notes",
      "dry_run_path": "/sales-leads/{lead_id}/notes/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note_text": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddLeadNoteRequest",
        "description": "*Add note…*. ``request_id`` becomes the note's id: a repeat adds nothing."
      }
    },
    {
      "id": "sales_leads.add_lead_contact",
      "module": "sales_leads_module",
      "label": "Add contact…",
      "description": "Add a further contact to a lead.",
      "target": "sales_leads.lead",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-leads/{lead_id}/contacts",
      "dry_run_path": "/sales-leads/{lead_id}/contacts/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "name": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Name",
            "x-cis-field": {
              "required": true
            }
          },
          "job_title": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Job title",
            "x-cis-field": {}
          },
          "phone": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Phone",
            "x-cis-field": {}
          },
          "email": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Email",
            "x-cis-field": {}
          },
          "referred_by": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Referred by",
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddLeadContactRequest",
        "description": "*Add contact…*. ``request_id`` becomes the contact's id: a repeat adds nothing."
      }
    },
    {
      "id": "sales_leads.create_lead_email_draft",
      "module": "sales_leads_module",
      "label": "Draft email…",
      "description": "Save a draft email to one of the lead's contacts in your own Outlook. Nothing is sent.",
      "target": "sales_leads.lead",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-leads/{lead_id}/email-draft",
      "dry_run_path": "/sales-leads/{lead_id}/email-draft/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "contact_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "To",
            "x-cis-field": {
              "required": true
            }
          },
          "subject": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Subject",
            "x-cis-field": {
              "required": true
            }
          },
          "body": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Message",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "CreateLeadEmailDraftRequest",
        "description": "*Draft email…*: a draft in your own Outlook; nothing is sent. The contact is chosen from the dry run's\nchoice."
      }
    },
    {
      "id": "sales_leads.file_lead_ticket",
      "module": "sales_leads_module",
      "label": "New ticket…",
      "description": "Raise a follow-up ticket on a lead, assigned to the lead's owner.",
      "target": "sales_leads.lead",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-leads/{lead_id}/tickets",
      "dry_run_path": "/sales-leads/{lead_id}/tickets/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "title": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Title",
            "x-cis-field": {
              "required": true
            }
          },
          "due_date": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Due date",
            "x-cis-field": {
              "widget": "date"
            }
          },
          "priority": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "low",
                  "normal",
                  "high",
                  "urgent"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Priority",
            "x-cis-field": {
              "enum_labels": {
                "high": "High",
                "low": "Low",
                "normal": "Normal",
                "urgent": "Urgent"
              }
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "FileLeadTicketRequest",
        "description": "*New ticket…*: a follow-up ticket assigned to the lead's owner. ``request_id`` becomes the ticket's id."
      }
    },
    {
      "id": "sales_leads.add_lead_attachment",
      "module": "sales_leads_module",
      "label": "Add attachment…",
      "description": "Attach a quote, email or other document to a lead.",
      "target": "sales_leads.lead",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-leads/{lead_id}/attachments",
      "dry_run_path": "/sales-leads/{lead_id}/attachments/dry-run",
      "content_type": "multipart",
      "request_schema": {
        "properties": {
          "file": {
            "type": "string",
            "contentMediaType": "application/octet-stream",
            "title": "File",
            "description": "The quote or email to attach.",
            "x-cis-field": {
              "widget": "file"
            }
          },
          "kind": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "quote",
                  "email",
                  "other"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Kind",
            "x-cis-field": {
              "enum_labels": {
                "email": "Email",
                "other": "Other",
                "quote": "Quote"
              }
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "file"
        ],
        "title": "AddLeadAttachmentForm",
        "description": "*Add attachment…*'s multipart body. ``request_id`` becomes the attachment's id: a repeat stores nothing."
      }
    },
    {
      "id": "sales_leads.update_lead",
      "module": "sales_leads_module",
      "label": "Amend details…",
      "description": "Amend a lead's company name, address, phone, website and interests.",
      "target": "sales_leads.lead",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-leads/{lead_id}/details",
      "dry_run_path": "/sales-leads/{lead_id}/details/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "company_name": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Company",
            "x-cis-field": {}
          },
          "address": {
            "anyOf": [
              {
                "properties": {
                  "address_line1": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Address line 1",
                    "x-cis-field": {}
                  },
                  "address_line2": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Address line 2",
                    "x-cis-field": {}
                  },
                  "city": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "City",
                    "x-cis-field": {}
                  },
                  "postcode": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Postcode",
                    "x-cis-field": {}
                  },
                  "country": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Country",
                    "x-cis-field": {}
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "title": "LeadAddressSchema"
              },
              {
                "type": "null"
              }
            ],
            "title": "Address",
            "x-cis-field": {}
          },
          "phone": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Phone",
            "x-cis-field": {}
          },
          "website": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Website",
            "x-cis-field": {}
          },
          "interest_tags": {
            "anyOf": [
              {
                "items": {
                  "type": "string",
                  "enum": [
                    "print",
                    "display",
                    "merch",
                    "clothing",
                    "mailing",
                    "creative",
                    "other"
                  ],
                  "title": "MarketingCategory",
                  "description": "Coarse product-family taxonomy a lead source's (or a campaign's)\npromoted products fall into — the same values dbt derives per job and\nper quote (modules_dbt/dbt/seeds/marketing_category_map.csv), reused here\nas a structured scoping axis for commercial-outcomes attribution.\nExported for marketing_campaigns_module's ``Campaign.marketing_categories``\nand sales_leads_module's ``Lead.interest_tags``.\nAn alternative to the free-text ``product_keywords`` for a source whose\nproducts share no literal keyword — e.g. \"Big Pink Book\" covers clothing/workwear/merch,\nnone of which appear verbatim in an individual garment's own job\ndescription (\"polo shirt\" doesn't contain \"uniform\")."
                },
                "type": "array"
              },
              {
                "type": "null"
              }
            ],
            "title": "Interested in",
            "x-cis-field": {
              "enum_labels": {
                "clothing": "Clothing & workwear",
                "creative": "Creative",
                "display": "Display",
                "mailing": "Mailing",
                "merch": "Merch",
                "other": "Other",
                "print": "Print"
              }
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "UpdateLeadRequest",
        "description": "*Amend details…*: a partial update — only fields present in the request body (per ``model_fields_set``) are\nchanged, which is why every field defaults to ``None``: an omitted field means \"leave unchanged\", an explicit\n``null`` clears it (not the company name, which a lead always has)."
      }
    },
    {
      "id": "sales_leads.convert_lead_to_cis_customer",
      "module": "sales_leads_module",
      "label": "Convert to customer…",
      "description": "Won, with v1 completion on. Create the lead's company as a customer in One, with its account address and sales rep and CSR, and lock the lead at First order.",
      "target": "sales_leads.lead",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-leads/{lead_id}/convert-to-customer",
      "dry_run_path": "/sales-leads/{lead_id}/convert-to-customer/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "name": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer name",
            "x-cis-field": {
              "required": true
            }
          },
          "account_address": {
            "anyOf": [
              {
                "properties": {
                  "line1": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Address line 1",
                    "x-cis-field": {}
                  },
                  "line2": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Address line 2",
                    "x-cis-field": {}
                  },
                  "line3": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Address line 3",
                    "x-cis-field": {}
                  },
                  "line4": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Address line 4",
                    "x-cis-field": {}
                  },
                  "town": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Town",
                    "x-cis-field": {}
                  },
                  "county": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "County",
                    "x-cis-field": {}
                  },
                  "postcode": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Postcode",
                    "x-cis-field": {}
                  },
                  "country_code": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Country code",
                    "description": "Two letters, such as GB.",
                    "x-cis-field": {}
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "title": "ConvertToCustomerAddressRequest",
                "description": "The new customer's account address as the form takes it, line by line; blank lines at the end are dropped."
              },
              {
                "type": "null"
              }
            ],
            "title": "Account address",
            "x-cis-field": {}
          },
          "owners": {
            "anyOf": [
              {
                "properties": {
                  "sales_rep_user_id": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Sales rep",
                    "x-cis-field": {}
                  },
                  "csr_user_id": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "CSR",
                    "x-cis-field": {}
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "title": "ConvertToCustomerOwnersRequest",
                "description": "The new customer's sales rep and CSR: a user id, or ``house_account`` (or ``null``) for House Account."
              },
              {
                "type": "null"
              }
            ],
            "title": "Owners",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ConvertToCustomerRequest",
        "description": "*Convert to customer…*: the \"New customer\" form, filled from the lead. Every field is optional so a dry run\ncalled with ``{}`` reaches its preview and its starting values; the commit answers a missing name with a 400\n``required``, and the customers module's own rules as field errors (``account_address.postcode``). The sales rep\nand CSR are chosen from the dry run's choices; left out, each is House Account."
      }
    },
    {
      "id": "sales_leads.convert_lead_to_existing_customer",
      "module": "sales_leads_module",
      "label": "Convert to existing customer…",
      "description": "Won with v1 completion on. Link the lead to a customer One already has, chosen from the caller's own customers, and lock the lead at First order; no customer is created, and a waiting possible match is confirmed or dismissed in the same write.",
      "target": "sales_leads.lead",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-leads/{lead_id}/actions/convert-to-existing-customer",
      "dry_run_path": "/sales-leads/{lead_id}/actions/convert-to-existing-customer/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "customer_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "description": "The customer, by the customer number (CUS-…).",
            "x-cis-field": {
              "entity": "customers.customer",
              "required": true,
              "widget": "entity"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ConvertLeadToExistingCustomerRequest",
        "description": "*Convert to existing customer…*: the CIS customer the won lead is (v1 completion C5f), picked from the\ncustomers list. Absent on a dry run's first call, so the form opens; its field errors name ``customer_code``."
      }
    },
    {
      "id": "sales_leads.convert_lead_to_customer",
      "module": "sales_leads_module",
      "label": "Won: create Tharstern customer…",
      "description": "Won, with v1 completion off. Create the lead's company as a customer in Tharstern and lock the lead at First order.",
      "target": "sales_leads.lead",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-leads/{lead_id}/convert",
      "dry_run_path": "/sales-leads/{lead_id}/convert/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "ConvertLeadToCustomerRequest",
        "description": "*Won: create Tharstern customer…* takes nothing but the lead: the customer is made from its details."
      }
    },
    {
      "id": "sales_leads.resolve_lead_conversion_match",
      "module": "sales_leads_module",
      "label": "Check match…",
      "description": "Confirm a possible match (link the lead to that Tharstern customer for good) or dismiss it.",
      "target": "sales_leads.lead",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-leads/{lead_id}/conversion-match/resolve",
      "dry_run_path": "/sales-leads/{lead_id}/conversion-match/resolve/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "action": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "confirm",
                  "dismiss"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Is the Tharstern match the same company?",
            "x-cis-field": {
              "enum_labels": {
                "confirm": "Yes, this is the same company",
                "dismiss": "No, different company"
              },
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ResolveConversionMatchRequest"
      }
    },
    {
      "id": "sales_leads.update_lead_contact",
      "module": "sales_leads_module",
      "label": "Edit contact…",
      "description": "Edit a lead contact's name, job title, phone, email and who referred them.",
      "target": "sales_leads.lead_contact",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-leads/{lead_id}/contacts/{contact_id}",
      "dry_run_path": "/sales-leads/{lead_id}/contacts/{contact_id}/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "name": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Name",
            "x-cis-field": {
              "required": true
            }
          },
          "job_title": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Job title",
            "x-cis-field": {}
          },
          "phone": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Phone",
            "x-cis-field": {}
          },
          "email": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Email",
            "x-cis-field": {}
          },
          "referred_by": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Referred by",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "UpdateLeadContactRequest",
        "description": "*Edit contact…*: the contact's fields as they should be (a field left empty is cleared). Every field optional,\nso a dry run called with ``{}`` reaches the preview and its prefill."
      }
    },
    {
      "id": "sales_leads.delete_lead_attachment",
      "module": "sales_leads_module",
      "label": "Delete",
      "description": "Delete a document attached to a lead. It cannot be restored.",
      "target": "sales_leads.lead_attachment",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/sales-leads/{lead_id}/attachments/{attachment_id}/delete",
      "dry_run_path": "/sales-leads/{lead_id}/attachments/{attachment_id}/delete/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "NoLeadFieldsRequest",
        "description": "A command on a lead's row that needs nothing but the row: *Delete* an attachment."
      }
    },
    {
      "id": "stock.create_stock_item",
      "module": "stock_module",
      "label": "New stock item…",
      "description": "Create a stock item with nothing on hand; opening stock is booked in afterwards.",
      "target": "collection:stock.stock_item",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock/actions/create-stock-item",
      "dry_run_path": "/stock/actions/create-stock-item/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "stock_code": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Stock code",
            "description": "Letters, numbers and symbols such as - / . # are all fine.",
            "x-cis-field": {
              "required": true
            }
          },
          "description": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Description",
            "x-cis-field": {
              "required": true
            }
          },
          "size": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Size",
            "x-cis-field": {}
          },
          "unit_quantity": {
            "type": "integer",
            "title": "Units",
            "description": "How many make one unit sold, e.g. 1, 50 or 100.",
            "default": 1,
            "x-cis-field": {}
          },
          "pack_quantity": {
            "type": "integer",
            "title": "Quantity in pack",
            "default": 1,
            "x-cis-field": {}
          },
          "boxes_per_pallet": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "No. boxes per pallet",
            "description": "A box can hold more than one pack.",
            "x-cis-field": {}
          },
          "tracks_batches": {
            "type": "boolean",
            "title": "Track batches",
            "description": "One batch per receipt. An item linked to a catalogue item always tracks batches.",
            "default": false,
            "x-cis-field": {}
          },
          "reorder_level": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Re-order level",
            "description": "The stock item will need re-ordering when projected stock falls to this level.",
            "x-cis-field": {}
          },
          "reorder_quantity": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Re-order quantity",
            "x-cis-field": {}
          },
          "owner_kind": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "own_stock",
                  "customer"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Owner",
            "description": "Pollards' own stock, or a customer's; from the dry run's choice.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "owner_customer_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "description": "The customer that owns the stock, when a customer owns it.",
            "x-cis-field": {
              "entity": "customers.customer",
              "required": true,
              "show_if": {
                "owner_kind": "customer"
              },
              "widget": "entity"
            }
          },
          "held_for_customer_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "Held for",
            "description": "The customer Pollards holds the stock for. Leave empty when the stock is held for no customer.",
            "x-cis-field": {
              "entity": "customers.customer",
              "show_if": {
                "owner_kind": "own_stock"
              },
              "widget": "entity"
            }
          },
          "repeatable_catalogue_item_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Catalogue item",
            "description": "Optionally one repeatable catalogue item, from the dry run's choice.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "find_catalogue_item": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Find a catalogue item",
            "description": "Enter at least two letters of a repeatable catalogue item's name. The choice will then list the matches.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "CreateStockItemRequest"
      }
    },
    {
      "id": "stock.create_stock_item_from_repeatable_item",
      "module": "stock_module",
      "label": "New stock item from catalogue…",
      "description": "Create a stock item from a repeatable catalogue item, with its name, owner and link filled in.",
      "target": "collection:stock.stock_item",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock/actions/create-stock-item-from-repeatable-item",
      "dry_run_path": "/stock/actions/create-stock-item-from-repeatable-item/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "repeatable_catalogue_item_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Catalogue item",
            "description": "The repeatable catalogue item, from the dry run's choice.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "find_catalogue_item": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Find a catalogue item",
            "description": "Enter at least two letters of a repeatable catalogue item's name. The choice will then list the matches.",
            "x-cis-field": {}
          },
          "stock_code": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Stock code",
            "x-cis-field": {
              "required": true
            }
          },
          "description": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Description",
            "description": "Leave empty to use the catalogue item's name.",
            "x-cis-field": {}
          },
          "owner_kind": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "own_stock",
                  "customer"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Owner",
            "description": "Pollards' own stock, or a customer's; from the dry run's choice.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "owner_customer_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "description": "Leave empty for the catalogue item's customer.",
            "x-cis-field": {
              "entity": "customers.customer",
              "show_if": {
                "owner_kind": "customer"
              },
              "widget": "entity"
            }
          },
          "held_for_customer_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "Held for",
            "description": "The customer Pollards holds the stock for. Leave empty for the catalogue item's customer.",
            "x-cis-field": {
              "entity": "customers.customer",
              "show_if": {
                "owner_kind": "own_stock"
              },
              "widget": "entity"
            }
          },
          "reorder_level": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Re-order level",
            "x-cis-field": {}
          },
          "reorder_quantity": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Re-order quantity",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "CreateStockItemFromRepeatableItemRequest",
        "description": "Stock up a repeatable catalogue item: the description and owner are filled in from the catalogue item unless\nsent (the owner defaults to the catalogue item's customer; send the owner kind own_stock for resale stock)."
      }
    },
    {
      "id": "stock.adjust_stock_in",
      "module": "stock_module",
      "label": "Adjust in…",
      "description": "Book stock in at a warehouse with a reason, starting a new batch for a batch-tracked item when it is a receipt.",
      "target": "stock.stock_item",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock/items/{stock_item_id}/actions/adjust-in",
      "dry_run_path": "/stock/items/{stock_item_id}/actions/adjust-in/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "location_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Warehouse",
            "description": "From the dry run's choice.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "description": "Required for the reason Other.",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "batch_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Batch",
            "description": "From the dry run's choice.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "expected_on_hand": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Expected on hand",
            "description": "What the warehouse held when the dry run showed the change; a different balance now is refused.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "quantity": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity to book in",
            "x-cis-field": {
              "required": true
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "goods_received",
                  "returned",
                  "stock_count_correction",
                  "opening_balance",
                  "other"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "x-cis-field": {
              "enum_labels": {
                "goods_received": "Goods received",
                "opening_balance": "Opening balance",
                "other": "Other",
                "returned": "Returned",
                "stock_count_correction": "Stock count correction"
              },
              "required": true
            }
          },
          "new_batch": {
            "anyOf": [
              {
                "properties": {
                  "artwork_revision_id": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Artwork revision",
                    "description": "The artwork revision the stock was printed from, e.g. Rev 2 - Autumn 2026.",
                    "x-cis-field": {}
                  },
                  "order_line_id": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Order line",
                    "description": "The order line the stock was delivered against. Leave empty for an import or an adjustment.",
                    "x-cis-field": {}
                  },
                  "purchase_order_line_id": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Purchase order line",
                    "x-cis-field": {}
                  },
                  "tharstern_job_ref": {
                    "anyOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "title": "Tharstern job reference",
                    "x-cis-field": {}
                  }
                },
                "additionalProperties": false,
                "type": "object",
                "title": "NewStockBatchRequest",
                "description": "The receipt a new batch records: references only. At most one of\n``purchase_order_line_id`` (a supplier made it) and ``tharstern_job_ref``\n(made in-house), and either needs ``order_line_id``."
              },
              {
                "type": "null"
              }
            ],
            "title": "New batch",
            "description": "Where the batch came from: a receipt always starts a new batch.",
            "x-cis-field": {}
          },
          "goods_in_ref": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Goods-in reference",
            "description": "Each reference books in at most once for the item.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AdjustStockInRequest"
      },
      "main": true
    },
    {
      "id": "stock.adjust_stock_out",
      "module": "stock_module",
      "label": "Adjust out…",
      "description": "Book stock out of a warehouse with a reason, never below zero or into stock picked for despatch.",
      "target": "stock.stock_item",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock/items/{stock_item_id}/actions/adjust-out",
      "dry_run_path": "/stock/items/{stock_item_id}/actions/adjust-out/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "location_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Warehouse",
            "description": "From the dry run's choice.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "description": "Required for the reason Other.",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "batch_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Batch",
            "description": "From the dry run's choice.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "expected_on_hand": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Expected on hand",
            "description": "What the warehouse held when the dry run showed the change; a different balance now is refused.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "quantity": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity to book out",
            "x-cis-field": {
              "required": true
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "damaged",
                  "used_issued",
                  "sample",
                  "stock_count_correction",
                  "other"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "x-cis-field": {
              "enum_labels": {
                "damaged": "Damaged",
                "other": "Other",
                "sample": "Sample",
                "stock_count_correction": "Stock count correction",
                "used_issued": "Used / issued"
              },
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AdjustStockOutRequest"
      }
    },
    {
      "id": "stock.transfer_stock",
      "module": "stock_module",
      "label": "Transfer…",
      "description": "Move stock of one item from one warehouse to another; the total on hand does not change.",
      "target": "stock.stock_item",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock/items/{stock_item_id}/actions/transfer",
      "dry_run_path": "/stock/items/{stock_item_id}/actions/transfer/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "from_location_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "From",
            "description": "From the dry run's choice.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "to_location_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "To",
            "description": "From the dry run's choice.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "quantity": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity to move",
            "x-cis-field": {
              "required": true
            }
          },
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "batch_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Batch",
            "description": "From the dry run's choice.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          },
          "expected_on_hand": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Expected on hand",
            "description": "What the source warehouse held when the dry run showed the change; a different balance now is refused.",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "TransferStockRequest"
      }
    },
    {
      "id": "stock.set_stock_holding_position",
      "module": "stock_module",
      "label": "Record a bin…",
      "description": "Record where an item, or one batch of it, sits in a warehouse (its bin), creating the holding when there is none.",
      "target": "stock.stock_item",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock/items/{stock_item_id}/actions/record-bin",
      "dry_run_path": "/stock/items/{stock_item_id}/actions/record-bin/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "location_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Warehouse",
            "description": "From the dry run's choice.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "batch_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Batch",
            "description": "From the dry run's choice.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "position": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Bin",
            "description": "e.g. Aisle 3, bay 2, shelf C",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetStockHoldingPositionRequest",
        "description": "``position: null`` (or blank) clears it. Creates the holding at 0 on hand when there is none."
      }
    },
    {
      "id": "stock.set_stock_item_held_for",
      "module": "stock_module",
      "label": "Set held for…",
      "description": "Set the customer whose range Pollards' own stock is held in, or clear it so the stock is held for no customer; a customer's own stock is always held for that customer.",
      "target": "stock.stock_item",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock/items/{stock_item_id}/actions/set-held-for",
      "dry_run_path": "/stock/items/{stock_item_id}/actions/set-held-for/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "held_for_customer_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "Held for",
            "description": "The customer Pollards holds the stock for. Leave empty when the stock is held for no customer.",
            "x-cis-field": {
              "entity": "customers.customer",
              "widget": "entity"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetStockItemHeldForRequest",
        "description": "The customer Pollards' own stock is held for. Sending nothing makes the item held for no customer."
      },
      "edits": {
        "fields": [
          "held_for_name"
        ]
      }
    },
    {
      "id": "stock.update_stock_item",
      "module": "stock_module",
      "label": "Edit details…",
      "description": "Edit a stock item's details and flags; the balance and the owner change only through their own commands.",
      "target": "stock.stock_item",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock/items/{stock_item_id}/actions/update",
      "dry_run_path": "/stock/items/{stock_item_id}/actions/update/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "stock_code": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Stock code",
            "x-cis-field": {}
          },
          "description": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Description",
            "x-cis-field": {}
          },
          "size": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Size",
            "x-cis-field": {}
          },
          "unit_quantity": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Units",
            "x-cis-field": {}
          },
          "pack_quantity": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity in pack",
            "x-cis-field": {}
          },
          "boxes_per_pallet": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "No. boxes per pallet",
            "x-cis-field": {}
          },
          "reorder_level": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Re-order level",
            "x-cis-field": {}
          },
          "reorder_quantity": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Re-order quantity",
            "x-cis-field": {}
          },
          "tracks_batches": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "Track batches",
            "description": "Can't change once stock or a bin position is recorded.",
            "x-cis-field": {}
          },
          "repeatable_catalogue_item_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Catalogue item",
            "description": "The repeatable catalogue item to link, from the dry run's choice.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "find_catalogue_item": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Find a catalogue item",
            "description": "Enter at least two letters of a repeatable catalogue item's name. The choice will then list the matches.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "UpdateStockItemRequest",
        "description": "Partial update: only fields present in the body are changed. Sending ``null`` clears an optional field. The\ndry run, asked with nothing sent, prefills the current values."
      }
    },
    {
      "id": "stock.change_stock_item_owner",
      "module": "stock_module",
      "label": "Change owner…",
      "description": "Move a stock item to another owning customer, with the reason audited; billing follows the new owner.",
      "target": "stock.stock_item",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock/items/{stock_item_id}/actions/change-owner",
      "dry_run_path": "/stock/items/{stock_item_id}/actions/change-owner/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "owner_kind": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "own_stock",
                  "customer"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "New owner",
            "description": "Pollards' own stock, or a customer's; from the dry run's choice.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "owner_customer_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 32
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "description": "The customer that owns the stock from now on, when a customer owns it.",
            "x-cis-field": {
              "entity": "customers.customer",
              "required": true,
              "show_if": {
                "owner_kind": "customer"
              },
              "widget": "entity"
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ChangeStockItemOwnerRequest",
        "description": "The new owner: a customer, or Pollards' own stock. The reason is audited."
      }
    },
    {
      "id": "stock.restore_stock_item",
      "module": "stock_module",
      "label": "Restore",
      "description": "Return an archived stock item to active use.",
      "target": "stock.stock_item",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock/items/{stock_item_id}/actions/restore",
      "dry_run_path": "/stock/items/{stock_item_id}/actions/restore/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "RestoreStockItemRequest",
        "description": "*Restore*: no fields."
      }
    },
    {
      "id": "stock.archive_stock_item",
      "module": "stock_module",
      "label": "Archive",
      "description": "Hide a stock item from the default list and block further changes; the balance and history are kept.",
      "target": "stock.stock_item",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock/items/{stock_item_id}/actions/archive",
      "dry_run_path": "/stock/items/{stock_item_id}/actions/archive/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "ArchiveStockItemRequest",
        "description": "*Archive*: no fields."
      }
    },
    {
      "id": "stock.withdraw_stock_batch",
      "module": "stock_module",
      "label": "Withdraw batch…",
      "description": "Withdraw one batch by hand, writing off what is left of it for good, with a note saying why.",
      "target": "stock.stock_batch",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock/items/{stock_item_id}/batches/{batch_id}/actions/withdraw",
      "dry_run_path": "/stock/items/{stock_item_id}/batches/{batch_id}/actions/withdraw/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Why is the batch being withdrawn?",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "WithdrawStockBatchRequest",
        "description": "Withdraw one batch by hand: its remaining stock is written off for good. The note says why."
      }
    },
    {
      "id": "stock.create_warehouse",
      "module": "stock_module",
      "label": "Add warehouse…",
      "description": "Add a warehouse that the supplier operates, where goods can be received and stock held.",
      "target": "suppliers.supplier",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock/suppliers/{supplier_no}/actions/add-warehouse",
      "dry_run_path": "/stock/suppliers/{supplier_no}/actions/add-warehouse/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "name": {
            "type": "string",
            "maxLength": 200,
            "title": "Warehouse name",
            "description": "Unique, e.g. Oak House.",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "address_line1": {
            "type": "string",
            "maxLength": 200,
            "title": "Address line 1",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "address_line2": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Address line 2",
            "x-cis-field": {}
          },
          "town": {
            "type": "string",
            "maxLength": 100,
            "title": "Town",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "postcode": {
            "type": "string",
            "maxLength": 20,
            "title": "Postcode",
            "description": "e.g. EX2 7NU.",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "country": {
            "type": "string",
            "maxLength": 100,
            "title": "Country",
            "default": "United Kingdom",
            "x-cis-field": {
              "required": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddWarehouseRequest",
        "description": "*Add warehouse…* on a supplier: the warehouse it will operate."
      }
    },
    {
      "id": "stock.set_warehouse_contact",
      "module": "stock_module",
      "label": "Set collection contact…",
      "description": "Record who a courier collects from at a warehouse (the collection label's name and phone).",
      "target": "stock.warehouse",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock/warehouses/{warehouse_id}/actions/set-collection-contact",
      "dry_run_path": "/stock/warehouses/{warehouse_id}/actions/set-collection-contact/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "contact_name": {
            "type": "string",
            "title": "Contact name",
            "description": "At most 25 characters.",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "contact_phone": {
            "type": "string",
            "title": "Contact phone",
            "description": "At most 15 characters.",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetWarehouseContactRequest",
        "description": "*Set collection contact…*: who the courier's driver asks for, and the number on the collection label."
      }
    },
    {
      "id": "stock.reactivate_warehouse",
      "module": "stock_module",
      "label": "Reactivate",
      "description": "Let stock be booked in to a deactivated warehouse and picked from it again.",
      "target": "stock.warehouse",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock/warehouses/{warehouse_id}/actions/reactivate",
      "dry_run_path": "/stock/warehouses/{warehouse_id}/actions/reactivate/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "ReactivateWarehouseRequest",
        "description": "*Reactivate* takes no input."
      }
    },
    {
      "id": "stock.deactivate_warehouse",
      "module": "stock_module",
      "label": "Deactivate",
      "description": "Stop stock being booked in to a warehouse or picked from it, once it holds none and has no open pick.",
      "target": "stock.warehouse",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock/warehouses/{warehouse_id}/actions/deactivate",
      "dry_run_path": "/stock/warehouses/{warehouse_id}/actions/deactivate/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "DeactivateWarehouseRequest",
        "description": "*Deactivate* takes no input."
      }
    },
    {
      "id": "stock.reprioritise_back_order",
      "module": "stock_module",
      "label": "Move up the queue…",
      "description": "Move a back order to the front of its item's queue, so a hard-date order does not wait behind a soft one.",
      "target": "stock.stock_allocation",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock/back-orders/{allocation_id}/actions/reprioritise",
      "dry_run_path": "/stock/back-orders/{allocation_id}/actions/reprioritise/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "maxLength": 500,
            "title": "Reason",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "reason"
        ],
        "title": "ReprioritiseBackOrderRequest",
        "description": "Why a back order goes to the front of its queue (kept on the allocation)."
      }
    },
    {
      "id": "stock_replenishment.set_replenishment_policy",
      "module": "stock_replenishment_module",
      "label": "Set replenishment policy…",
      "description": "Create or change a stock item's replenishment policy (its mode, delivery warehouse, route pin and how soon a reprint is needed).",
      "target": "stock.stock_item",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock-replenishment/policies/{stock_item_id}/actions/set",
      "dry_run_path": "/stock-replenishment/policies/{stock_item_id}/actions/set/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "mode": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "automatic",
                  "advise"
                ],
                "title": "ReplenishmentMode",
                "description": "``automatic`` raises at once when everything is in place; ``advise`` waits for a person."
              },
              {
                "type": "null"
              }
            ],
            "title": "Mode",
            "description": "Automatic will raise each reprint. Advise will ask a person to confirm each reprint.",
            "x-cis-field": {
              "enum_labels": {
                "advise": "Advise",
                "automatic": "Automatic"
              }
            }
          },
          "delivery_warehouse_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Deliver into",
            "description": "From the dry run's choice.",
            "x-cis-field": {
              "hidden": true
            }
          },
          "stock_line_owner_user_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Stock-line owner",
            "description": "The person told when a reprint waits to be confirmed. Leave blank to tell the owner's CSR.",
            "x-cis-field": {
              "entity": "users.user",
              "widget": "entity"
            }
          },
          "preferred_route_code": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Preferred route",
            "description": "The supplier route to use. Leave blank for the cheapest route.",
            "x-cis-field": {}
          },
          "needed_within_days": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Needed within (working days)",
            "description": "One will pass over supplier routes slower than this. Leave blank for no limit.",
            "x-cis-field": {}
          },
          "is_tharstern_make": {
            "anyOf": [
              {
                "type": "boolean"
              },
              {
                "type": "null"
              }
            ],
            "title": "Tharstern (make)",
            "description": "Tharstern still reprints the stock item, so One will raise nothing for the stock item.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetReplenishmentPolicyRequest",
        "description": "Only the fields sent are changed; an explicit ``null`` clears a nullable one. Creating a policy needs\n``delivery_warehouse_id``; the mode then follows the owner's billing-account kind unless sent."
      }
    },
    {
      "id": "stock_replenishment.replenish_now",
      "module": "stock_replenishment_module",
      "label": "Replenish now…",
      "description": "Raise a stock item's reprint at once, as an order delivering into the item, on the owner's behalf.",
      "target": "stock.stock_item",
      "destructive": true,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock-replenishment/policies/{stock_item_id}/actions/replenish-now",
      "dry_run_path": "/stock-replenishment/policies/{stock_item_id}/actions/replenish-now/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "quantity": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity",
            "description": "The dry run prefills the re-order quantity.",
            "x-cis-field": {
              "required": true
            }
          },
          "price_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Price to bill, ex VAT",
            "description": "Only where the owner has no price group. The dry run says when.",
            "x-cis-field": {
              "widget": "money"
            }
          },
          "customer_order_number": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer order number",
            "description": "Only where the owner's account needs a customer order number. The dry run says when.",
            "x-cis-field": {}
          },
          "expected_price_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Expected price",
            "description": "The whole price, ex VAT, the dry run showed; a different price now is refused.",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ReplenishNowRequest",
        "description": "``price_pence`` is the whole line's price in pence, ex VAT, to bill when the owner has no usable price group\n(refused otherwise, and for an internal stock order);\n``customer_order_number`` is the owner's own, when their account needs one. ``expected_price_pence`` is the\nguard the dry run fills in."
      }
    },
    {
      "id": "stock_replenishment.confirm_replenishment_proposal",
      "module": "stock_replenishment_module",
      "label": "Confirm & raise…",
      "description": "Raise an open proposal as one order, with any of the owner's other nearly-due items ticked in on the same order.",
      "target": "stock_replenishment.replenishment_proposal",
      "destructive": true,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock-replenishment/proposals/{proposal_id}/actions/confirm",
      "dry_run_path": "/stock-replenishment/proposals/{proposal_id}/actions/confirm/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "quantity": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Quantity",
            "description": "The dry run prefills the proposed quantity.",
            "x-cis-field": {
              "required": true
            }
          },
          "included_items": {
            "items": {
              "properties": {
                "stock_item_id": {
                  "type": "string",
                  "title": "Stock item",
                  "description": "The nearly-due item to include.",
                  "x-cis-field": {
                    "hidden": true
                  }
                },
                "stock_code": {
                  "anyOf": [
                    {
                      "type": "string"
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Stock code",
                  "description": "Shown for the row only.",
                  "readOnly": true,
                  "x-cis-field": {}
                },
                "quantity": {
                  "anyOf": [
                    {
                      "type": "integer"
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Quantity",
                  "description": "Starts at the stock item's re-order quantity.",
                  "x-cis-field": {}
                },
                "price_pence": {
                  "anyOf": [
                    {
                      "type": "integer"
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Price to bill, ex VAT",
                  "description": "Only where the owner has no price group.",
                  "x-cis-field": {
                    "widget": "money"
                  }
                }
              },
              "additionalProperties": false,
              "type": "object",
              "required": [
                "stock_item_id"
              ],
              "title": "IncludedItemRequest"
            },
            "type": "array",
            "maxItems": 100,
            "title": "Also reprint",
            "description": "The owner's other stock items close to their re-order level. Tick the ones to raise on the same order.",
            "x-cis-field": {
              "selectable": true,
              "widget": "table"
            }
          },
          "price_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Price to bill, ex VAT",
            "description": "Only where the owner has no price group. The dry run says when.",
            "x-cis-field": {
              "widget": "money"
            }
          },
          "customer_order_number": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer order number",
            "description": "Only where the owner's account needs a customer order number. The dry run says when.",
            "x-cis-field": {}
          },
          "expected_price_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Expected price",
            "description": "The whole price, ex VAT, over every line, the dry run showed; a different price now is refused.",
            "x-cis-field": {
              "hidden": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ConfirmReplenishmentProposalRequest",
        "description": "``included_items``: the owner's nearly-due items ticked into the same confirmation (the dry run offers them,\nunticked)."
      },
      "main": true
    },
    {
      "id": "stock_replenishment.dismiss_replenishment_proposal",
      "module": "stock_replenishment_module",
      "label": "Dismiss…",
      "description": "Dismiss an open or failed proposal with a reason; the next sweep proposes again while the item is still at or below its level.",
      "target": "stock_replenishment.replenishment_proposal",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/stock-replenishment/proposals/{proposal_id}/actions/dismiss",
      "dry_run_path": "/stock-replenishment/proposals/{proposal_id}/actions/dismiss/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "description": "Why the proposal is dismissed, in at most 500 characters.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "DismissReplenishmentProposalRequest"
      }
    },
    {
      "id": "supplier_access.add_supplier_person",
      "module": "supplier_access_module",
      "label": "Invite…",
      "description": "Give someone at a supplier a login to the supplier portal and email them the sign-in link.",
      "target": "suppliers.supplier",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Grants outside access: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/suppliers/{supplier_no}/people",
      "dry_run_path": "/suppliers/{supplier_no}/people/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "name": {
            "type": "string",
            "maxLength": 500,
            "title": "Name",
            "x-cis-field": {}
          },
          "email": {
            "type": "string",
            "maxLength": 500,
            "title": "Email",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "name",
          "email"
        ],
        "title": "AddSupplierPersonRequest",
        "description": "Give someone at the supplier a login. Field names match the validation errors' ``field`` (``name``,\n``email``)."
      }
    },
    {
      "id": "supplier_access.resend_supplier_invite",
      "module": "supplier_access_module",
      "label": "Resend invite",
      "description": "Email a supplier user's sign-in link again.",
      "target": "supplier_access.supplier_person",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Grants outside access: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/suppliers/{supplier_no}/people/{person_id}/actions/resend-invite",
      "dry_run_path": "/suppliers/{supplier_no}/people/{person_id}/actions/resend-invite/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "supplier_access.deactivate_supplier_person",
      "module": "supplier_access_module",
      "label": "Remove access…",
      "description": "End a supplier user's login; their next request to the portal is refused.",
      "target": "supplier_access.supplier_person",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Removes outside access: done in the app, never by Claude (Ben, 13 Oct).",
      "method": "POST",
      "path": "/suppliers/{supplier_no}/people/{person_id}/actions/deactivate",
      "dry_run_path": "/suppliers/{supplier_no}/people/{person_id}/actions/deactivate/dry-run",
      "content_type": "json",
      "request_schema": null
    },
    {
      "id": "supplier_quotes.chase_supplier_quote",
      "module": "supplier_quotes_module",
      "label": "Chase",
      "description": "Email a supplier again for an answer that is overdue or whose request email never went.",
      "target": "supplier_quotes.supplier_quote",
      "destructive": true,
      "bulk": false,
      "external_calls": true,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/supplier-quotes/{supplier_quote_no}/actions/chase",
      "dry_run_path": "/supplier-quotes/{supplier_quote_no}/actions/chase/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ChaseSupplierQuoteRequest",
        "description": "*Chase* takes only its idempotency key: the supplier quote is in the path."
      }
    },
    {
      "id": "supplier_quotes.record_supplier_quote_answer",
      "module": "supplier_quotes_module",
      "label": "Record their answer…",
      "description": "Record a supplier's prices for the quantities still asked, or the supplier's revision while the window is open.",
      "target": "supplier_quotes.supplier_quote",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/supplier-quotes/{supplier_quote_no}/actions/record-answer",
      "dry_run_path": "/supplier-quotes/{supplier_quote_no}/actions/record-answer/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "prices": {
            "items": {
              "properties": {
                "quantity": {
                  "anyOf": [
                    {
                      "type": "integer",
                      "exclusiveMinimum": 0.0
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Quantity",
                  "readOnly": true,
                  "x-cis-field": {
                    "widget": "number"
                  }
                },
                "cost": {
                  "anyOf": [
                    {
                      "type": "integer",
                      "maximum": 100000000.0,
                      "minimum": 0.0
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "title": "Their delivered cost",
                  "x-cis-field": {
                    "widget": "money"
                  }
                },
                "not_quoted": {
                  "type": "boolean",
                  "title": "Can't quote",
                  "default": false,
                  "x-cis-field": {}
                }
              },
              "additionalProperties": false,
              "type": "object",
              "title": "PriceAnswerRow",
              "description": "One quantity's answer: its delivered cost, or that they can't quote it."
            },
            "type": "array",
            "maxItems": 10,
            "title": "Their prices",
            "description": "A cost for each quantity asked, or tick Can't quote. At least one quantity needs a cost.",
            "x-cis-field": {
              "required": true,
              "widget": "table"
            }
          },
          "despatch_by": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Despatch by",
            "description": "When they can despatch, if they say.",
            "x-cis-field": {}
          },
          "valid_until": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Valid until",
            "description": "The last day their quote holds.",
            "x-cis-field": {
              "required": true
            }
          },
          "their_reference": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 200
              },
              {
                "type": "null"
              }
            ],
            "title": "Their reference",
            "description": "Their number for the quote. For Pollards' own quote, the Tharstern estimate number.",
            "x-cis-field": {
              "required": true
            }
          },
          "quote_document": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Their quote",
            "description": "Their quote as a file, if you have it: their PDF, a screenshot or the email saved as a file. If not, add it later with Add their quote….",
            "x-cis-field": {
              "accept": [
                "application/pdf",
                "image/png",
                "image/jpeg",
                "message/rfc822",
                "application/vnd.ms-outlook",
                ".eml",
                ".msg"
              ],
              "max_bytes": 26214400,
              "upload": {
                "file_field": "file",
                "path": "/supplier-quotes/their-quote-files",
                "ref_field": "ref"
              }
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordSupplierQuoteAnswerRequest",
        "description": "*Record their answer…*: a quote the supplier gave our staff by phone or email. One row for each quantity still\nasked, which the dry run's prefill fills in."
      }
    },
    {
      "id": "supplier_quotes.record_supplier_quote_decline",
      "module": "supplier_quotes_module",
      "label": "Record they declined…",
      "description": "Record that a supplier won't quote the quantities still asked, with their reason.",
      "target": "supplier_quotes.supplier_quote",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/supplier-quotes/{supplier_quote_no}/actions/record-decline",
      "dry_run_path": "/supplier-quotes/{supplier_quote_no}/actions/record-decline/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "declined_reason": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 2000
              },
              {
                "type": "null"
              }
            ],
            "title": "Why they declined",
            "description": "Their reason, in up to 500 characters.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RecordSupplierQuoteDeclineRequest",
        "description": "*Record they declined…*: why the supplier won't quote the quantities still asked."
      }
    },
    {
      "id": "supplier_quotes.add_supplier_quote_file",
      "module": "supplier_quotes_module",
      "label": "Add their quote…",
      "description": "Keep the supplier's quote PDF with a supplier quote that was answered without it (a phoned or emailed answer).",
      "target": "supplier_quotes.supplier_quote",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Files can only be uploaded in the app.",
      "method": "POST",
      "path": "/supplier-quotes/{supplier_quote_no}/actions/add-quote-file",
      "dry_run_path": "/supplier-quotes/{supplier_quote_no}/actions/add-quote-file/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "quote_document": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 64
              },
              {
                "type": "null"
              }
            ],
            "title": "Their quote",
            "description": "Their quote as a file: their PDF, a screenshot or the email saved as a file.",
            "x-cis-field": {
              "accept": [
                "application/pdf",
                "image/png",
                "image/jpeg",
                "message/rfc822",
                "application/vnd.ms-outlook",
                ".eml",
                ".msg"
              ],
              "max_bytes": 26214400,
              "required": true,
              "upload": {
                "file_field": "file",
                "path": "/supplier-quotes/their-quote-files",
                "ref_field": "ref"
              }
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddSupplierQuoteFileRequest",
        "description": "*Add their quote…*: the PDF of an answer that was recorded without it."
      }
    },
    {
      "id": "suppliers.create_supplier",
      "module": "suppliers_module",
      "label": "New supplier…",
      "description": "Create a supplier CIS buys from that Tharstern does not know, with a new SUP number.",
      "target": "collection:suppliers.supplier",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/suppliers",
      "dry_run_path": "/purchase-ledger/suppliers/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "name": {
            "type": "string",
            "title": "Name",
            "x-cis-field": {}
          },
          "payment_terms_days": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Payment terms (days)",
            "description": "1 to 365 days. Leave blank when unknown.",
            "x-cis-field": {}
          },
          "purchase_order_email": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Send our purchase orders to",
            "description": "Leave blank if we place orders on the supplier's website.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "name"
        ],
        "title": "CreateSupplierRequest"
      }
    },
    {
      "id": "suppliers.suggest_supplier",
      "module": "suppliers_module",
      "label": "Suggest supplier…",
      "description": "Put a supplier forward for the panel at Suggested, with the reason, so the family lead can take the supplier on; any staff member may.",
      "target": "collection:suppliers.supplier",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/suppliers/suggest",
      "dry_run_path": "/purchase-ledger/suppliers/suggest/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "name": {
            "type": "string",
            "title": "Supplier name",
            "x-cis-field": {}
          },
          "what": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "What they could make or do for us",
            "description": "A dictionary item family (booklet, leaflet, ncr, banner, packaging …) or a service (mailing, fulfilment, storage, installation …), by its code.",
            "x-cis-field": {}
          },
          "production_method": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "any",
                  "digital",
                  "litho",
                  "web_offset"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Production method",
            "x-cis-field": {
              "enum_labels": {
                "any": "Any",
                "digital": "Digital",
                "litho": "Litho",
                "web_offset": "Web offset"
              }
            }
          },
          "why": {
            "type": "string",
            "title": "Why them",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "name",
          "why"
        ],
        "title": "SuggestSupplierRequest"
      }
    },
    {
      "id": "suppliers.record_relationship_stage",
      "module": "suppliers_module",
      "label": "Record stage…",
      "description": "Move the relationship to its next stage, pause it or resume it, with the reason; reaching Contracted records the date the framework agreement was signed.",
      "target": "suppliers.supplier",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/suppliers/{supplier_no}/actions/record-stage",
      "dry_run_path": "/purchase-ledger/suppliers/{supplier_no}/actions/record-stage/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "stage": {
            "type": "string",
            "enum": [
              "suggested",
              "identified",
              "contacted",
              "commercials_agreed",
              "sla_agreed",
              "contracted",
              "live",
              "paused"
            ],
            "title": "Stage",
            "x-cis-field": {
              "enum_labels": {
                "commercials_agreed": "Commercials agreed",
                "contacted": "Contacted",
                "contracted": "Contracted",
                "identified": "Identified",
                "live": "Live",
                "paused": "Paused",
                "sla_agreed": "SLA agreed",
                "suggested": "Suggested"
              }
            }
          },
          "reason": {
            "type": "string",
            "title": "Reason",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "framework_agreement_signed_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Framework agreement signed on",
            "x-cis-field": {
              "required": true,
              "show_if": {
                "stage": "contracted"
              }
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "stage",
          "reason"
        ],
        "title": "RecordRelationshipStageRequest"
      }
    },
    {
      "id": "suppliers.set_next_action",
      "module": "suppliers_module",
      "label": "Set next action…",
      "description": "Record the next thing to do with the supplier and when it is due; both blank clears them.",
      "target": "suppliers.supplier",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/suppliers/{supplier_no}/actions/set-next-action",
      "dry_run_path": "/purchase-ledger/suppliers/{supplier_no}/actions/set-next-action/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "next_action": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Next action",
            "description": "Leave both blank to clear them.",
            "x-cis-field": {}
          },
          "due_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Due",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetNextActionRequest"
      },
      "edits": {
        "fields": [
          "next_action",
          "next_action_due_on"
        ]
      }
    },
    {
      "id": "suppliers.set_relationship_owner",
      "module": "suppliers_module",
      "label": "Set owner…",
      "description": "Choose who owns the next move with the supplier.",
      "target": "suppliers.supplier",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/suppliers/{supplier_no}/actions/set-owner",
      "dry_run_path": "/purchase-ledger/suppliers/{supplier_no}/actions/set-owner/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "owner_user_id": {
            "type": "string",
            "title": "Owner",
            "x-cis-field": {
              "entity": "users.user",
              "widget": "entity"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "owner_user_id"
        ],
        "title": "SetRelationshipOwnerRequest"
      },
      "edits": {
        "fields": [
          "owner_name"
        ]
      }
    },
    {
      "id": "suppliers.set_target_rung",
      "module": "suppliers_module",
      "label": "Set target rung…",
      "description": "Record the rung on the integration ladder the supplier should reach, why the supplier is not there yet and what moves the supplier up.",
      "target": "suppliers.supplier",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/suppliers/{supplier_no}/actions/set-target-rung",
      "dry_run_path": "/purchase-ledger/suppliers/{supplier_no}/actions/set-target-rung/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "target_rung": {
            "type": "string",
            "enum": [
              "their_api",
              "pdq",
              "our_portal",
              "email_or_manual"
            ],
            "title": "Target rung",
            "x-cis-field": {
              "enum_labels": {
                "email_or_manual": "Email / manual",
                "our_portal": "Supplier portal",
                "pdq": "PDQ",
                "their_api": "Direct API"
              }
            }
          },
          "why_not_deeper": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Why not deeper",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "moves_up_when": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Moves up when",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "target_rung"
        ],
        "title": "SetTargetRungRequest"
      }
    },
    {
      "id": "suppliers.set_credit_limit",
      "module": "suppliers_module",
      "label": "Set credit limit…",
      "description": "Record the credit limit the supplier has given us, with the reason for the change; blank clears it.",
      "target": "suppliers.supplier",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/suppliers/{supplier_no}/actions/set-credit-limit",
      "dry_run_path": "/purchase-ledger/suppliers/{supplier_no}/actions/set-credit-limit/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "credit_limit_pence": {
            "anyOf": [
              {
                "type": "integer"
              },
              {
                "type": "null"
              }
            ],
            "title": "Credit limit",
            "description": "Leave blank to clear the credit limit.",
            "x-cis-field": {
              "widget": "money"
            }
          },
          "reason": {
            "type": "string",
            "title": "Reason",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "reason"
        ],
        "title": "SetCreditLimitRequest"
      },
      "edits": {
        "fields": [
          "credit_limit_pence"
        ]
      }
    },
    {
      "id": "suppliers.add_supplier_capability",
      "module": "suppliers_module",
      "label": "Add capability…",
      "description": "Record one thing the supplier can make or do for us, in the dictionary's words, and the role the supplier plays for it.",
      "target": "suppliers.supplier",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/suppliers/{supplier_no}/actions/add-capability",
      "dry_run_path": "/purchase-ledger/suppliers/{supplier_no}/actions/add-capability/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "what": {
            "type": "string",
            "title": "Family or service",
            "description": "A dictionary item family (booklet, leaflet, ncr, banner, packaging …) or a service (mailing, fulfilment, storage, installation …), by its code.",
            "x-cis-field": {}
          },
          "production_method": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "any",
                  "digital",
                  "litho",
                  "web_offset"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Production method",
            "description": "Ignored for a service.",
            "x-cis-field": {
              "enum_labels": {
                "any": "Any",
                "digital": "Digital",
                "litho": "Litho",
                "web_offset": "Web offset"
              }
            }
          },
          "role": {
            "type": "string",
            "enum": [
              "primary",
              "secondary",
              "candidate",
              "out_of_scope"
            ],
            "title": "Role",
            "x-cis-field": {
              "enum_labels": {
                "candidate": "Candidate",
                "out_of_scope": "Out of scope",
                "primary": "Primary",
                "secondary": "Secondary"
              }
            }
          },
          "note": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "what",
          "role"
        ],
        "title": "AddSupplierCapabilityRequest"
      }
    },
    {
      "id": "suppliers.add_supplier_account",
      "module": "suppliers_module",
      "label": "Add supplier account…",
      "description": "Record an account one of our trading companies holds with the supplier, with our account number there; purchase orders for that company's orders are then placed by it through the account.",
      "target": "suppliers.supplier",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/suppliers/{supplier_no}/actions/add-supplier-account",
      "dry_run_path": "/purchase-ledger/suppliers/{supplier_no}/actions/add-supplier-account/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "trading_company_code": {
            "type": "string",
            "maxLength": 3,
            "title": "Trading company",
            "description": "The trading company that holds the account: Setting up or Trading, not Stopped.",
            "x-cis-field": {
              "entity": "trading_companies.trading_company",
              "widget": "entity"
            }
          },
          "account_no": {
            "type": "string",
            "maxLength": 40,
            "title": "Our account number",
            "description": "The number the supplier's bills quote for us, up to 40 characters.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "trading_company_code",
          "account_no"
        ],
        "title": "AddSupplierAccountRequest"
      }
    },
    {
      "id": "suppliers.fold_supplier",
      "module": "suppliers_module",
      "label": "Fold into another supplier…",
      "description": "Fold a supplier Tharstern split by company into the supplier it duplicates, so the business is one supplier with an account at each trading company; the duplicate is closed for good and its purchase orders and bills stay as they were. Refused while anything is still live on the duplicate.",
      "target": "suppliers.supplier",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "A one-off review of the supplier master: done in the app.",
      "method": "POST",
      "path": "/purchase-ledger/suppliers/{supplier_no}/actions/fold",
      "dry_run_path": "/purchase-ledger/suppliers/{supplier_no}/actions/fold/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "into_supplier_no": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Into",
            "description": "The supplier that stays: any open supplier. Its possible duplicates are offered first.",
            "x-cis-field": {
              "entity": "suppliers.supplier",
              "required": true,
              "widget": "entity"
            }
          },
          "account_company_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 3
              },
              {
                "type": "null"
              }
            ],
            "title": "Its account belongs to",
            "description": "The trading company this supplier's Tharstern record is the account of.",
            "x-cis-field": {
              "entity": "trading_companies.trading_company",
              "required": true,
              "widget": "entity"
            }
          },
          "into_account_company_code": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 3
              },
              {
                "type": "null"
              }
            ],
            "title": "The other supplier's account belongs to",
            "description": "The trading company the supplier it folds into is the account of.",
            "x-cis-field": {
              "entity": "trading_companies.trading_company",
              "required": true,
              "widget": "entity"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "FoldSupplierRequest",
        "description": "*Fold into another supplier…*: the supplier to fold into, and which trading company each side's account belongs\nto. Optional in the schema so a dry run can start the three from what One knows (``required``: the commit refuses\nan empty one)."
      }
    },
    {
      "id": "suppliers.change_supplier_account_number",
      "module": "suppliers_module",
      "label": "Change account number…",
      "description": "Correct our account number with the supplier, as its bills quote it; bills that quote the new number are matched to the account.",
      "target": "suppliers.supplier_account",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/supplier-accounts/{supplier_account_id}/actions/change-account-number",
      "dry_run_path": "/purchase-ledger/supplier-accounts/{supplier_account_id}/actions/change-account-number/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "account_no": {
            "type": "string",
            "maxLength": 40,
            "title": "Our account number",
            "description": "The number the supplier's bills quote for us, up to 40 characters.",
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "account_no"
        ],
        "title": "ChangeSupplierAccountNumberRequest"
      },
      "edits": {
        "fields": [
          "account_no"
        ]
      }
    },
    {
      "id": "suppliers.close_supplier_account",
      "module": "suppliers_module",
      "label": "Close supplier account…",
      "description": "Stop placing purchase orders through the account, for example when the supplier closes it; refused while purchase orders are open on it. The account keeps its number and its history.",
      "target": "suppliers.supplier_account",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/supplier-accounts/{supplier_account_id}/actions/close",
      "dry_run_path": "/purchase-ledger/supplier-accounts/{supplier_account_id}/actions/close/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "CloseSupplierAccountRequest",
        "description": "Closing an account takes nothing: the account is in the path and the gate is its open purchase orders."
      }
    },
    {
      "id": "suppliers.rename_supplier",
      "module": "suppliers_module",
      "label": "Rename…",
      "description": "Correct a supplier's name; its SUP number never changes and Xero's contact follows on its next poll.",
      "target": "suppliers.supplier",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/suppliers/{supplier_no}/actions/rename",
      "dry_run_path": "/purchase-ledger/suppliers/{supplier_no}/actions/rename/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "name": {
            "type": "string",
            "title": "New name",
            "description": "The supplier number (SUP-…) won't change.",
            "x-cis-field": {}
          },
          "reason": {
            "type": "string",
            "title": "Reason",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "name",
          "reason"
        ],
        "title": "RenameSupplierRequest"
      }
    },
    {
      "id": "suppliers.set_supplier_delivery_details",
      "module": "suppliers_module",
      "label": "Set delivery details…",
      "description": "Record where a supplier despatches from and the delivery service level it delivers at.",
      "target": "suppliers.supplier",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/suppliers/{supplier_no}/actions/set-delivery-details",
      "dry_run_path": "/purchase-ledger/suppliers/{supplier_no}/actions/set-delivery-details/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "despatch_postcode": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Despatch postcode",
            "description": "The full UK postcode the supplier despatches from, like EX2 7NU. Leave blank to clear the postcode.",
            "x-cis-field": {}
          },
          "delivery_service_level": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "next_day",
                  "pre_noon",
                  "pre_1030",
                  "pallet",
                  "northern_ireland"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Delivery service level",
            "description": "Leave blank to clear the delivery service level.",
            "x-cis-field": {
              "enum_labels": {
                "next_day": "Next day",
                "northern_ireland": "Northern Ireland",
                "pallet": "Pallet",
                "pre_1030": "Before 10:30",
                "pre_noon": "Before noon"
              }
            }
          },
          "reason": {
            "type": "string",
            "title": "Reason",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "despatch_postcode",
          "delivery_service_level",
          "reason"
        ],
        "title": "SetSupplierDeliveryDetailsRequest"
      }
    },
    {
      "id": "suppliers.set_supplier_purchase_order_email",
      "module": "suppliers_module",
      "label": "Set purchase order email…",
      "description": "Record where our purchase orders go for a supplier; with no integration profile entry and the supplier portal on, they are emailed there with a link to our portal.",
      "target": "suppliers.supplier",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/suppliers/{supplier_no}/actions/set-purchase-order-email",
      "dry_run_path": "/purchase-ledger/suppliers/{supplier_no}/actions/set-purchase-order-email/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "purchase_order_email": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Send our purchase orders to",
            "description": "Leave blank if we place orders on the supplier's website.",
            "x-cis-field": {}
          },
          "reason": {
            "type": "string",
            "title": "Reason",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "reason"
        ],
        "title": "SetSupplierPurchaseOrderEmailRequest"
      },
      "edits": {
        "fields": [
          "purchase_order_email"
        ]
      }
    },
    {
      "id": "suppliers.set_supplier_quote_request_email",
      "module": "suppliers_module",
      "label": "Set quote request email…",
      "description": "Record where our quote requests go for a supplier; once supplier quotes are declared on the supplier portal, they are emailed there with a link to answer in our portal, and until then our staff ask the supplier and record its answer.",
      "target": "suppliers.supplier",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/suppliers/{supplier_no}/actions/set-quote-request-email",
      "dry_run_path": "/purchase-ledger/suppliers/{supplier_no}/actions/set-quote-request-email/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "quote_request_email": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Quote requests to",
            "description": "Leave it blank if our staff ask them.",
            "x-cis-field": {}
          },
          "reason": {
            "type": "string",
            "title": "Reason",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "reason"
        ],
        "title": "SetSupplierQuoteRequestEmailRequest"
      },
      "edits": {
        "fields": [
          "quote_request_email"
        ]
      }
    },
    {
      "id": "suppliers.release_supplier_hold",
      "module": "suppliers_module",
      "label": "Release hold…",
      "description": "Release a supplier's payment hold, so its confirmed purchase invoices go to Xero on the next push.",
      "target": "suppliers.supplier",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Releases a supplier's payment hold, letting its purchase invoices go to Xero: done in the app, never by Claude (Ben, 14 Oct).",
      "method": "POST",
      "path": "/purchase-ledger/suppliers/{supplier_no}/actions/release-hold",
      "dry_run_path": "/purchase-ledger/suppliers/{supplier_no}/actions/release-hold/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "title": "Reason",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "reason"
        ],
        "title": "SupplierHoldRequest"
      }
    },
    {
      "id": "suppliers.place_supplier_on_hold",
      "module": "suppliers_module",
      "label": "Put on hold…",
      "description": "Put a supplier on payment hold; its purchase invoices still match and confirm but none goes to Xero.",
      "target": "suppliers.supplier",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/suppliers/{supplier_no}/actions/hold",
      "dry_run_path": "/purchase-ledger/suppliers/{supplier_no}/actions/hold/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "title": "Reason",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "reason"
        ],
        "title": "SupplierHoldRequest"
      }
    },
    {
      "id": "suppliers.change_capability_role",
      "module": "suppliers_module",
      "label": "Change role…",
      "description": "Change the role the supplier plays for one of its capabilities, with the reason.",
      "target": "suppliers.supplier_capability",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/purchase-ledger/supplier-capabilities/{capability_id}/actions/change-role",
      "dry_run_path": "/purchase-ledger/supplier-capabilities/{capability_id}/actions/change-role/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "role": {
            "type": "string",
            "enum": [
              "primary",
              "secondary",
              "candidate",
              "out_of_scope"
            ],
            "title": "Role",
            "x-cis-field": {
              "enum_labels": {
                "candidate": "Candidate",
                "out_of_scope": "Out of scope",
                "primary": "Primary",
                "secondary": "Secondary"
              }
            }
          },
          "reason": {
            "type": "string",
            "title": "Reason",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "role",
          "reason"
        ],
        "title": "ChangeCapabilityRoleRequest"
      }
    },
    {
      "id": "suppliers.remove_supplier_capability",
      "module": "suppliers_module",
      "label": "Remove capability…",
      "description": "Take a capability off the supplier's record, with the reason; the supplier's history keeps the change.",
      "target": "suppliers.supplier_capability",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Removes a capability from a supplier's record: done in the app, never by Claude.",
      "method": "POST",
      "path": "/purchase-ledger/supplier-capabilities/{capability_id}/actions/remove",
      "dry_run_path": "/purchase-ledger/supplier-capabilities/{capability_id}/actions/remove/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "reason": {
            "type": "string",
            "title": "Reason",
            "x-cis-field": {
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "reason"
        ],
        "title": "RemoveSupplierCapabilityRequest"
      }
    },
    {
      "id": "tharstern_job_check.check_tharstern_job_again",
      "module": "tharstern_job_check_module",
      "label": "Check again",
      "description": "Check the Tharstern job again now against today's dictionary, catalogue items, suppliers and pricing, and keep the new check beside the earlier ones. Orders, quotes and asks Tharstern for nothing; it may ask suppliers for prices, within each supplier's requests for the day.",
      "target": "tharstern_job_check.job_check",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tharstern-job-checks/{job_no}/actions/check-again",
      "dry_run_path": "/tharstern-job-checks/{job_no}/actions/check-again/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "CheckTharsternJobAgainRequest",
        "description": "Check again takes only its idempotency key: the job is in the path."
      }
    },
    {
      "id": "trading_companies.add_trading_company",
      "module": "trading_companies_module",
      "label": "Add trading company…",
      "description": "Add a legal company that will sell and buy through One, such as one the group has acquired. It starts in Setting up.",
      "target": "collection:trading_companies.trading_company",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/trading-companies/actions/add",
      "dry_run_path": "/trading-companies/actions/add/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "legal_name": {
            "type": "string",
            "maxLength": 200,
            "title": "Legal name",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "name": {
            "type": "string",
            "maxLength": 60,
            "title": "Short name",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "code": {
            "type": "string",
            "maxLength": 20,
            "title": "Code (3 letters)",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "company_number": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 40
              },
              {
                "type": "null"
              }
            ],
            "title": "Company number",
            "x-cis-field": {}
          },
          "registered_office": {
            "anyOf": [
              {
                "type": "string",
                "maxLength": 500
              },
              {
                "type": "null"
              }
            ],
            "title": "Registered office",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddTradingCompanyRequest"
      }
    },
    {
      "id": "trading_companies.update_trading_company_legal_details",
      "module": "trading_companies_module",
      "label": "Update legal details…",
      "description": "Change the legal name, company number, where it is registered, registered office or VAT number shown on this trading company's documents.",
      "target": "trading_companies.trading_company",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/trading-companies/{code}/actions/update-legal-details",
      "dry_run_path": "/trading-companies/{code}/actions/update-legal-details/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "legal_name": {
            "type": "string",
            "maxLength": 200,
            "title": "Legal name",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "company_number": {
            "type": "string",
            "maxLength": 40,
            "title": "Company number",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "registered_in": {
            "type": "string",
            "maxLength": 60,
            "title": "Registered in",
            "description": "Where the company is registered, for example England and Wales or Scotland.",
            "default": "England and Wales",
            "x-cis-field": {
              "required": true
            }
          },
          "registered_office": {
            "type": "string",
            "maxLength": 500,
            "title": "Registered office",
            "default": "",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "vat_number": {
            "type": "string",
            "maxLength": 40,
            "title": "VAT number",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "UpdateTradingCompanyLegalDetailsRequest"
      }
    },
    {
      "id": "trading_companies.update_trading_company_bank_account",
      "module": "trading_companies_module",
      "label": "Update bank account…",
      "description": "Change the bank account this trading company's invoices and pro-formas ask customers to pay into.",
      "target": "trading_companies.trading_company",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "The bank account customers pay into is changed by a person in the app.",
      "method": "POST",
      "path": "/trading-companies/{code}/actions/update-bank-account",
      "dry_run_path": "/trading-companies/{code}/actions/update-bank-account/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "account_name": {
            "type": "string",
            "maxLength": 100,
            "title": "Account name",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "sort_code": {
            "type": "string",
            "maxLength": 20,
            "title": "Sort code",
            "description": "For example 40-11-62.",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          },
          "account_number": {
            "type": "string",
            "maxLength": 20,
            "title": "Account number",
            "default": "",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "UpdateTradingCompanyBankAccountRequest"
      },
      "edits": {
        "fields": [
          "bank_account"
        ]
      }
    },
    {
      "id": "trading_companies.start_trading",
      "module": "trading_companies_module",
      "label": "Start trading…",
      "description": "Let orders and purchase orders be given to this trading company, once everything it needs is in place.",
      "target": "trading_companies.trading_company",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "A person starts a trading company trading in the app.",
      "method": "POST",
      "path": "/trading-companies/{code}/actions/start-trading",
      "dry_run_path": "/trading-companies/{code}/actions/start-trading/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "StartTradingRequest",
        "description": "Starting trading takes nothing: the company is in the path and the gate is its readiness."
      }
    },
    {
      "id": "trading_companies.stop_trading",
      "module": "trading_companies_module",
      "label": "Stop trading…",
      "description": "Stop giving new orders and purchase orders to this trading company, for example once it has combined with another.",
      "target": "trading_companies.trading_company",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "A person stops a trading company trading in the app.",
      "method": "POST",
      "path": "/trading-companies/{code}/actions/stop-trading",
      "dry_run_path": "/trading-companies/{code}/actions/stop-trading/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "StopTradingRequest",
        "description": "Stopping trading takes nothing: the company is in the path and the gate is its customers."
      }
    },
    {
      "id": "work_visibility.assign_ticket_covering",
      "module": "work_visibility_module",
      "label": "Assign cover…",
      "description": "Assign one of a CSR's buddies to cover a ticket while the CSR is away.",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/assign-cover",
      "dry_run_path": "/tickets/{ticket_id}/actions/assign-cover/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "csr_user_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "CSR",
            "x-cis-field": {
              "entity": "users.user",
              "required": true,
              "widget": "entity"
            }
          },
          "covering_user_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Cover",
            "description": "One of the CSR's buddies.",
            "x-cis-field": {
              "entity": "users.user",
              "required": true,
              "widget": "entity"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AssignCoverRequest",
        "description": "Assign cover… on a ticket (object contract): Classic's body without ``ticket_id``. Both fields are optional\nhere so a dry run called with ``{}`` reaches its preview; the commit answers a missing one with a 400\n``required``."
      }
    },
    {
      "id": "workflow.create_ticket",
      "module": "workflow_module",
      "label": "New ticket…",
      "description": "Raise a ticket for a person or a department, optionally about a customer or another record.",
      "target": "collection:workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets",
      "dry_run_path": "/tickets/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "title": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Title",
            "x-cis-field": {
              "required": true
            }
          },
          "description": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Description",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "customer_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "description": "The customer's id, in your portfolio.",
            "x-cis-field": {}
          },
          "assigned_to_user_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Assignee",
            "x-cis-field": {
              "entity": "users.user",
              "widget": "entity"
            }
          },
          "department_slug": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Department",
            "description": "A department, by its slug.",
            "x-cis-field": {}
          },
          "priority": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "low",
                  "normal",
                  "high",
                  "urgent"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Priority",
            "x-cis-field": {
              "enum_labels": {
                "high": "High",
                "low": "Low",
                "normal": "Normal",
                "urgent": "Urgent"
              }
            }
          },
          "category_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Category",
            "x-cis-field": {}
          },
          "action_type": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "call",
                  "email",
                  "meeting",
                  "general"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Action type",
            "x-cis-field": {
              "enum_labels": {
                "call": "Call",
                "email": "Email",
                "general": "General",
                "meeting": "Meeting"
              }
            }
          },
          "due_date": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Due date",
            "x-cis-field": {
              "widget": "date"
            }
          },
          "subject_type": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "About (type)",
            "x-cis-field": {}
          },
          "subject_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "About (id)",
            "x-cis-field": {}
          },
          "subject_label": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "About",
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "CreateTicketRequest",
        "description": "New ticket… An absent ``assigned_to_user_id`` assigns it to you; an explicit null leaves it unassigned\n(portfolio-shared, or the department's when ``department_slug`` is set)."
      },
      "proposal_class": "asks_internal"
    },
    {
      "id": "workflow.mark_ticket_in_progress",
      "module": "workflow_module",
      "label": "Mark in progress",
      "description": "Mark a ticket in progress; the requester and watchers are told.",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/mark-in-progress",
      "dry_run_path": "/tickets/{ticket_id}/actions/mark-in-progress/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "NoTicketFieldsRequest",
        "description": "A command on a ticket that needs nothing but the ticket: mark in progress, mark waiting, resolve, reopen,\nclaim."
      }
    },
    {
      "id": "workflow.mark_ticket_waiting",
      "module": "workflow_module",
      "label": "Mark waiting",
      "description": "Mark a ticket waiting (on someone else); the requester and watchers are told.",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/mark-waiting",
      "dry_run_path": "/tickets/{ticket_id}/actions/mark-waiting/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "NoTicketFieldsRequest",
        "description": "A command on a ticket that needs nothing but the ticket: mark in progress, mark waiting, resolve, reopen,\nclaim."
      }
    },
    {
      "id": "workflow.resolve_ticket",
      "module": "workflow_module",
      "label": "Mark resolved",
      "description": "Mark a ticket resolved; the requester and watchers are told, and it closes itself after the quiet period.",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/resolve",
      "dry_run_path": "/tickets/{ticket_id}/actions/resolve/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "NoTicketFieldsRequest",
        "description": "A command on a ticket that needs nothing but the ticket: mark in progress, mark waiting, resolve, reopen,\nclaim."
      }
    },
    {
      "id": "workflow.reopen_ticket",
      "module": "workflow_module",
      "label": "Reopen",
      "description": "Reopen a resolved or closed ticket back to open.",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/reopen",
      "dry_run_path": "/tickets/{ticket_id}/actions/reopen/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "NoTicketFieldsRequest",
        "description": "A command on a ticket that needs nothing but the ticket: mark in progress, mark waiting, resolve, reopen,\nclaim."
      }
    },
    {
      "id": "workflow.add_ticket_comment",
      "module": "workflow_module",
      "label": "Add comment…",
      "description": "Add a comment to a ticket's activity log; the requester and watchers are told.",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/add-comment",
      "dry_run_path": "/tickets/{ticket_id}/actions/add-comment/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "body": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Comment",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddTicketCommentRequest"
      }
    },
    {
      "id": "workflow.add_ticket_attachment",
      "module": "workflow_module",
      "label": "Add attachment…",
      "description": "Add a file to a ticket as a comment, with the words that go with it when given; the requester and watchers are told.",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Multipart file upload: done in the app.",
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/add-attachment",
      "dry_run_path": "/tickets/{ticket_id}/actions/add-attachment/dry-run",
      "content_type": "multipart",
      "request_schema": {
        "properties": {
          "file": {
            "type": "string",
            "contentMediaType": "application/octet-stream",
            "title": "File",
            "description": "A PDF, an image (JPEG, PNG, WebP or GIF), a Word document or a spreadsheet of at most 20 MB.",
            "x-cis-field": {
              "widget": "file"
            }
          },
          "body": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Comment",
            "description": "Optional.",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "file"
        ],
        "title": "AddTicketAttachmentForm",
        "description": "Add attachment…: the multipart body of ``POST /tickets/{ticket_id}/actions/add-attachment`` and its dry run.\nThe file is added as a comment, so ``body`` is the words that go with it."
      }
    },
    {
      "id": "workflow.delegate_ticket",
      "module": "workflow_module",
      "label": "Reassign to…",
      "description": "Reassign a ticket to another person, recording the delegation; they, the requester and watchers are told.",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/reassign",
      "dry_run_path": "/tickets/{ticket_id}/actions/reassign/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "to_user_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reassign to",
            "x-cis-field": {
              "entity": "users.user",
              "required": true,
              "widget": "entity"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "ReassignTicketRequest"
      }
    },
    {
      "id": "workflow.claim_department_ticket",
      "module": "workflow_module",
      "label": "Claim",
      "description": "Claim an unassigned department ticket as one of the department's members.",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/claim",
      "dry_run_path": "/tickets/{ticket_id}/actions/claim/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "NoTicketFieldsRequest",
        "description": "A command on a ticket that needs nothing but the ticket: mark in progress, mark waiting, resolve, reopen,\nclaim."
      }
    },
    {
      "id": "workflow.set_ticket_priority",
      "module": "workflow_module",
      "label": "Set priority…",
      "description": "Set a ticket's priority (low, normal, high, urgent).",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/set-priority",
      "dry_run_path": "/tickets/{ticket_id}/actions/set-priority/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "priority": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Priority",
            "description": "Chosen from the dry run's choice.",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetTicketPriorityRequest"
      }
    },
    {
      "id": "workflow.set_ticket_category",
      "module": "workflow_module",
      "label": "Set category…",
      "description": "Set or clear a ticket's category.",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/set-category",
      "dry_run_path": "/tickets/{ticket_id}/actions/set-category/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "category_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Category",
            "description": "Chosen from the dry run's choice; an empty string clears it.",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetTicketCategoryRequest"
      },
      "edits": {
        "fields": [
          "category_name"
        ]
      }
    },
    {
      "id": "workflow.set_ticket_customer",
      "module": "workflow_module",
      "label": "Set customer…",
      "description": "Set the customer a ticket is about, one in the caller's portfolio.",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/set-customer",
      "dry_run_path": "/tickets/{ticket_id}/actions/set-customer/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "customer_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "description": "The customer's id, in your portfolio.",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetTicketCustomerRequest"
      },
      "edits": {
        "fields": [
          "customer_name"
        ]
      }
    },
    {
      "id": "workflow.amend_ticket",
      "module": "workflow_module",
      "label": "Amend details…",
      "description": "Amend a ticket's title, description, due date or action type.",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/amend",
      "dry_run_path": "/tickets/{ticket_id}/actions/amend/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "title": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Title",
            "x-cis-field": {}
          },
          "description": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Description",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "due_date": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Due date",
            "x-cis-field": {
              "widget": "date"
            }
          },
          "action_type": {
            "anyOf": [
              {
                "type": "string",
                "enum": [
                  "call",
                  "email",
                  "meeting",
                  "general"
                ]
              },
              {
                "type": "null"
              }
            ],
            "title": "Action type",
            "x-cis-field": {
              "enum_labels": {
                "call": "Call",
                "email": "Email",
                "general": "General",
                "meeting": "Meeting"
              }
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AmendTicketRequest",
        "description": "Only the fields given change; null clears all but the title."
      }
    },
    {
      "id": "workflow.add_ticket_tag",
      "module": "workflow_module",
      "label": "Add tag",
      "description": "Add a free-form tag to a ticket.",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/add-tag",
      "dry_run_path": "/tickets/{ticket_id}/actions/add-tag/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "tag_value": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Tag",
            "x-cis-field": {
              "required": true
            }
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddTicketTagRequest"
      }
    },
    {
      "id": "workflow.remove_ticket_tag",
      "module": "workflow_module",
      "label": "Remove tag…",
      "description": "Remove one of a ticket's tags.",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/remove-tag",
      "dry_run_path": "/tickets/{ticket_id}/actions/remove-tag/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "tag_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Tag",
            "description": "Chosen from the dry run's choice.",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RemoveTicketTagRequest"
      }
    },
    {
      "id": "workflow.add_ticket_watcher",
      "module": "workflow_module",
      "label": "Watch…",
      "description": "Add a person (you, by default) as a watcher on a ticket, so they are told of its activity.",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/add-watcher",
      "dry_run_path": "/tickets/{ticket_id}/actions/add-watcher/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "user_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Watcher",
            "x-cis-field": {
              "entity": "users.user",
              "required": true,
              "widget": "entity"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "AddTicketWatcherRequest"
      }
    },
    {
      "id": "workflow.remove_ticket_watcher",
      "module": "workflow_module",
      "label": "Stop watching…",
      "description": "Stop a person watching a ticket.",
      "target": "workflow.ticket",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/{ticket_id}/actions/remove-watcher",
      "dry_run_path": "/tickets/{ticket_id}/actions/remove-watcher/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "user_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Watcher",
            "description": "Chosen from the dry run's choice.",
            "x-cis-field": {
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "RemoveTicketWatcherRequest"
      }
    },
    {
      "id": "workflow.bulk_delegate_tickets",
      "module": "workflow_module",
      "label": "Reassign tickets…",
      "description": "Reassign several tickets to one person at once, each as Reassign would; each one notifies people.",
      "target": "workflow.ticket",
      "destructive": true,
      "bulk": true,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/actions/reassign",
      "dry_run_path": "/tickets/actions/reassign/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "targets": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 50,
            "minItems": 1,
            "title": "Targets",
            "description": "The ids of the tickets to reassign (at most 50)."
          },
          "to_user_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reassign to",
            "x-cis-field": {
              "entity": "users.user",
              "required": true,
              "widget": "entity"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "targets"
        ],
        "title": "ReassignTicketsRequest"
      }
    },
    {
      "id": "workflow.close_tickets_with_note",
      "module": "workflow_module",
      "label": "Close with note…",
      "description": "Close several tickets at once, leaving the same note on each as a comment; each one notifies people.",
      "target": "workflow.ticket",
      "destructive": true,
      "bulk": true,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/tickets/actions/close-with-note",
      "dry_run_path": "/tickets/actions/close-with-note/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "targets": {
            "items": {
              "type": "string"
            },
            "type": "array",
            "maxItems": 50,
            "minItems": 1,
            "title": "Targets",
            "description": "The ids of the tickets to close (at most 50)."
          },
          "note_text": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "required": [
          "targets"
        ],
        "title": "CloseTicketsWithNoteRequest"
      }
    },
    {
      "id": "workflow.create_note",
      "module": "workflow_module",
      "label": "Add note…",
      "description": "Add a note about a record or a customer, optionally with a reminder; never about a ticket.",
      "target": "collection:workflow.note",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/notes",
      "dry_run_path": "/notes/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note_text": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "note_title": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Title",
            "x-cis-field": {}
          },
          "remind_me_at": {
            "anyOf": [
              {
                "type": "string",
                "format": "date-time"
              },
              {
                "type": "null"
              }
            ],
            "title": "Remind me",
            "x-cis-field": {
              "widget": "date"
            }
          },
          "customer_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Customer",
            "description": "The customer's id, in your portfolio.",
            "x-cis-field": {}
          },
          "subject_type": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "About (type)",
            "x-cis-field": {}
          },
          "subject_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "About (id)",
            "x-cis-field": {}
          },
          "subject_label": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "About",
            "x-cis-field": {}
          },
          "request_id": {
            "anyOf": [
              {
                "type": "string",
                "format": "uuid",
                "description": "Client-generated id for this attempt; a repeat returns the first result."
              },
              {
                "type": "null"
              }
            ],
            "title": "Request Id"
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "CreateNoteRequest",
        "description": "Add note… about a record (``subject_type`` + ``subject_id``, never a ticket) or a customer."
      },
      "proposal_class": "records"
    },
    {
      "id": "workflow.update_note",
      "module": "workflow_module",
      "label": "Edit note…",
      "description": "Change a note's title, text or reminder; clearing the reminder deals with it.",
      "target": "workflow.note",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/notes/{note_id}/actions/edit",
      "dry_run_path": "/notes/{note_id}/actions/edit/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "note_text": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Note",
            "x-cis-field": {
              "widget": "textarea"
            }
          },
          "note_title": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Title",
            "x-cis-field": {}
          },
          "remind_me_at": {
            "anyOf": [
              {
                "type": "string",
                "format": "date-time"
              },
              {
                "type": "null"
              }
            ],
            "title": "Remind me",
            "x-cis-field": {
              "widget": "date"
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "UpdateNoteRequest",
        "description": "Edit note…: only the fields given change; a null ``remind_me_at`` clears the reminder."
      }
    },
    {
      "id": "workflow.override_customer_health_tier",
      "module": "workflow_module",
      "label": "Override health tier…",
      "description": "Override a customer's health tier from its record, with a reason, until a date; the customer follows the health score again after that.",
      "target": "customers.customer",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/workflow/customers/{customer_code}/actions/override-health-tier",
      "dry_run_path": "/workflow/customers/{customer_code}/actions/override-health-tier/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "override_tier": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Health tier",
            "description": "Chosen from the dry run's choice.",
            "x-cis-field": {
              "enum_labels": {
                "at_risk": "At risk",
                "healthy": "Healthy",
                "thriving": "Thriving",
                "watch": "Watch"
              },
              "required": true
            }
          },
          "reason": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Reason",
            "description": "Why the computed tier is wrong, e.g. one big deferred order.",
            "x-cis-field": {
              "required": true,
              "widget": "textarea"
            }
          },
          "expires_on": {
            "anyOf": [
              {
                "type": "string",
                "format": "date"
              },
              {
                "type": "null"
              }
            ],
            "title": "Expires on",
            "description": "The override stops at the end of this day. The customer then follows the health score again.",
            "x-cis-field": {
              "min_date": "today",
              "required": true
            }
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "OverrideHealthTierRequest",
        "description": "Override health tier…: the tier a rep believes reflects reality, with a reason and the date it stops."
      },
      "edits": {
        "fields": [
          "health_tier_override"
        ]
      }
    },
    {
      "id": "workflow.end_customer_health_tier_override",
      "module": "workflow_module",
      "label": "Clear health tier override",
      "description": "End a customer's health tier override before its date, so the customer follows the health score again.",
      "target": "customers.customer",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/workflow/customers/{customer_code}/actions/clear-health-tier-override",
      "dry_run_path": "/workflow/customers/{customer_code}/actions/clear-health-tier-override/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "NoCustomerSettingFieldsRequest",
        "description": "A command on a customer that needs nothing but the customer: Clear health tier override."
      },
      "edits": {
        "fields": [
          "health_tier_override"
        ]
      }
    },
    {
      "id": "workflow.set_customer_key_person",
      "module": "workflow_module",
      "label": "Set key person…",
      "description": "Mark one of a customer's people as a key person, or take the mark off.",
      "target": "customers.customer",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "exposed",
      "withheld_reason": null,
      "method": "POST",
      "path": "/workflow/customers/{customer_code}/actions/set-key-person",
      "dry_run_path": "/workflow/customers/{customer_code}/actions/set-key-person/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {
          "customer_person_id": {
            "anyOf": [
              {
                "type": "string"
              },
              {
                "type": "null"
              }
            ],
            "title": "Contact",
            "description": "One of the customer's people, found among the customer's active customer users.",
            "x-cis-field": {
              "entity": "customer_access.customer_link",
              "entity_value": "person_id",
              "required": true,
              "widget": "entity"
            }
          },
          "is_key_person": {
            "type": "boolean",
            "title": "Key person",
            "description": "On: mark the contact as a key person. Off: take the mark off.",
            "default": true,
            "x-cis-field": {}
          }
        },
        "additionalProperties": false,
        "type": "object",
        "title": "SetKeyPersonRequest",
        "description": "Set key person…: one of the customer's people, marked or unmarked."
      },
      "edits": {
        "fields": [
          "key_people"
        ]
      }
    },
    {
      "id": "xero_integration.connect_xero_organisation",
      "module": "xero_integration_module",
      "label": "Connect Xero organisation…",
      "description": "Sign in to Xero and choose this trading company's organisation, so its documents and month end go there.",
      "target": "trading_companies.trading_company",
      "destructive": false,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Connecting a trading company's Xero organisation needs a person to sign in to Xero, and it decides where the company's documents and month end go.",
      "method": "POST",
      "path": "/xero/trading-companies/{trading_company_code}/actions/connect-xero-organisation",
      "dry_run_path": "/xero/trading-companies/{trading_company_code}/actions/connect-xero-organisation/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "TradingCompanyXeroCommandRequest",
        "description": "Neither command asks anything: the trading company is in the path, and the organisation is chosen in Xero."
      }
    },
    {
      "id": "xero_integration.disconnect_xero_organisation",
      "module": "xero_integration_module",
      "label": "Disconnect Xero organisation…",
      "description": "Disconnect this trading company's Xero organisation, for example to connect the right one instead.",
      "target": "trading_companies.trading_company",
      "destructive": true,
      "bulk": false,
      "external_calls": false,
      "claude": "withheld",
      "withheld_reason": "Disconnecting stops a trading company's documents and month end going to its Xero organisation and gives up One's access there; a person decides it in the app.",
      "method": "POST",
      "path": "/xero/trading-companies/{trading_company_code}/actions/disconnect-xero-organisation",
      "dry_run_path": "/xero/trading-companies/{trading_company_code}/actions/disconnect-xero-organisation/dry-run",
      "content_type": "json",
      "request_schema": {
        "properties": {},
        "additionalProperties": false,
        "type": "object",
        "title": "TradingCompanyXeroCommandRequest",
        "description": "Neither command asks anything: the trading company is in the path, and the organisation is chosen in Xero."
      }
    }
  ]
};
